import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "./firebase";
import {collection, onSnapshot, doc, getDoc, getDocs, setDoc, addDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy} from "./firebase-shim";
import ScansionaBolla from "./ScansionaBolla";
import ScannerBollaFoto from "./ScannerBollaFoto";
import SicurezzaAzienda from "./SicurezzaAzienda";
import SelettoreSchede from "./SelettoreSchede";
import GeneraPos from "./GeneraPos";
import PonteggioCantiere from "./PonteggioCantiere"; // PONT_EDIFICIO_v1
import ArchivioDocumenti from "./ArchivioDocumenti";
import Cassetta from "./Cassetta"; // CASSETTA_DOC_v2
import StatisticheAzienda from "./StatisticheAzienda"; // STAT_AZIENDA_v1
import ScadenzePersone, { caricaPersone, personaDi } from "./ScadenzePersone"; // SCADENZE_PERSONE_v1
import AgganciaOreId from "./AgganciaOreId"; // LAV_BY_ID_CONV_v1
import FotoCantiereBtn, { fotoCantiereElimina } from "./FotoCantiere"; // FOTO_CANTIERE_v1

// ── Configurazione NAS Backend ─────────────────────────────────────────────
const NAS_URL = "";

// FIX_FORMATO_NUMERI_v2: formattazione italiana manuale (punto migliaia, virgola decimali)
// indipendente dal locale del browser
const fmtNumIT = (n, dec=2) => {
  const num = parseFloat(n)||0;
  const neg = num < 0 ? "-" : "";
  const abs = Math.abs(num);
  const fixed = abs.toFixed(dec);
  const parti = fixed.split(".");
  parti[0] = parti[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return neg + (dec > 0 ? parti.join(",") : parti[0]);
};

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#21253a",
  border: "#2a2e45",
  accent: "#f5a623",
  accentSoft: "#f5a62322",
  accentHover: "#e09520",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  successSoft: "#3ecf8e22",
  warning: "#f5a623",
  danger: "#f06060",
  dangerSoft: "#f0606022",
  info: "#4e9af5",
  infoSoft: "#4e9af522",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }
  
  body {
    font-family: 'DM Sans', sans-serif;
    background: #0f1117;
    color: #e8eaf2;
    min-height: 100vh;
  }

  .app { display: flex; min-height: 100vh; }

  /* SIDEBAR */
  .sidebar {
    width: 240px;
    min-height: 100vh;
    background: #1a1d27;
    border-right: 1px solid #2a2e45;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0; left: 0; bottom: 0;
    z-index: 100;
    transition: transform 0.25s ease;
  }

  /* OVERLAY mobile */
  .sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: #00000088;
    z-index: 99;
  }

  /* HAMBURGER — nascosto su desktop */
  .hamburger {
    display: none;
    background: none;
    border: 1px solid #2a2e45;
    border-radius: 8px;
    color: #e8eaf2;
    font-size: 18px;
    padding: 6px 10px;
    cursor: pointer;
    line-height: 1;
  }

  @media (max-width: 768px) {
    .sidebar {
      transform: translateX(-100%);
      width: 260px;
      box-shadow: 4px 0 24px #00000066;
    }
    .sidebar.open {
      transform: translateX(0);
    }
    .sidebar-overlay.open {
      display: block;
    }
    .hamburger {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .main {
      margin-left: 0 !important;
    }
    .topbar {
      padding: 0 16px !important;
    }
    .content {
      padding: 16px !important;
    }
    .grid-2 {
      grid-template-columns: 1fr !important;
    }
    .stats-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
    .topbar-title {
      font-size: 15px !important;
    }
  }

  .sidebar-logo {
    padding: 24px 20px 20px;
    border-bottom: 1px solid #2a2e45;
  }
  .logo-mark {
    font-family: 'Syne', sans-serif;
    font-weight: 800;
    font-size: 20px;
    color: #f5a623;
    letter-spacing: -0.5px;
  }
  .logo-sub {
    font-size: 10px;
    color: #7b82a0;
    letter-spacing: 2px;
    text-transform: uppercase;
    margin-top: 2px;
  }

  .sidebar-nav { padding: 16px 12px; flex: 1; }
  .nav-section-label {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #4a5068;
    padding: 8px 8px 6px;
    font-weight: 500;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s;
    margin-bottom: 2px;
    font-size: 14px;
    color: #7b82a0;
    font-weight: 400;
    border: 1px solid transparent;
  }
  .nav-item:hover {
    background: #21253a;
    color: #e8eaf2;
  }
  .nav-item.active {
    background: #f5a62322;
    color: #f5a623;
    border-color: #f5a62333;
    font-weight: 500;
  }
  .nav-icon { font-size: 16px; width: 20px; text-align: center; }

  .sidebar-footer {
    padding: 16px 20px;
    border-top: 1px solid #2a2e45;
    font-size: 12px;
    color: #4a5068;
  }
  .sidebar-footer strong { color: #7b82a0; display: block; margin-bottom: 2px; }

  /* MAIN */
  .main { margin-left: 240px; flex: 1; min-height: 100vh; }

  .topbar {
    background: #1a1d27;
    border-bottom: 1px solid #2a2e45;
    padding: 0 32px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    top: 0;
    z-index: 50;
  }
  .topbar-title {
    font-family: 'Syne', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: #e8eaf2;
  }
  .topbar-actions { display: flex; gap: 10px; align-items: center; }

  .btn {
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.15s;
    font-family: 'DM Sans', sans-serif;
  }
  .btn-primary {
    background: #f5a623;
    color: #111;
  }
  .btn-primary:hover { background: #e09520; }
  .btn-ghost {
    background: transparent;
    color: #7b82a0;
    border: 1px solid #2a2e45;
  }
  .btn-ghost:hover { background: #21253a; color: #e8eaf2; }
  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-danger { background: #f0606022; color: #f06060; border: 1px solid #f0606044; }

  .content { padding: 32px; }

  /* CARDS */
  .card {
    background: #1a1d27;
    border: 1px solid #2a2e45;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 20px;
  }
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .card-title {
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 15px;
    color: #e8eaf2;
  }

  /* STATS ROW */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  .stat-card {
    background: #1a1d27;
    border: 1px solid #2a2e45;
    border-radius: 12px;
    padding: 20px;
    position: relative;
    overflow: hidden;
  }
  .stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
  }
  .stat-card.accent::before { background: #f5a623; }
  .stat-card.success::before { background: #3ecf8e; }
  .stat-card.info::before { background: #4e9af5; }
  .stat-card.danger::before { background: #f06060; }
  .stat-label { font-size: 11px; text-transform: uppercase; letter-spacing: 1.5px; color: #7b82a0; margin-bottom: 8px; }
  .stat-value { font-family: 'Syne', sans-serif; font-size: 28px; font-weight: 800; color: #e8eaf2; }
  .stat-sub { font-size: 12px; color: #4a5068; margin-top: 4px; }

  /* TABLE */
  .table-wrap { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th {
    text-align: left;
    padding: 10px 14px;
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #4a5068;
    font-weight: 500;
    border-bottom: 1px solid #2a2e45;
  }
  td {
    padding: 13px 14px;
    border-bottom: 1px solid #2a2e4566;
    color: #e8eaf2;
    vertical-align: middle;
  }
  tr:last-child td { border-bottom: none; }
  tr:hover td { background: #21253a; }

  /* BADGE */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    gap: 4px;
  }
  .badge-success { background: #3ecf8e22; color: #3ecf8e; }
  .badge-warning { background: #f5a62322; color: #f5a623; }
  .badge-info { background: #4e9af522; color: #4e9af5; }
  .badge-danger { background: #f0606022; color: #f06060; }
  .badge-muted { background: #2a2e45; color: #7b82a0; }

  /* FORM */
  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .form-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-group.full { grid-column: 1 / -1; }
  label { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #7b82a0; font-weight: 500; }
  input, select, textarea {
    background: #0f1117;
    border: 1px solid #2a2e45;
    border-radius: 8px;
    color: #e8eaf2;
    padding: 10px 14px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    outline: none;
    transition: border-color 0.15s;
  }
  input:focus, select:focus, textarea:focus {
    border-color: #f5a623;
    box-shadow: 0 0 0 3px #f5a62322;
  }
  textarea { resize: vertical; min-height: 80px; }
  select option { background: #1a1d27; }

  /* SECTION DIVIDER */
  .section-divider {
    font-family: 'Syne', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #f5a623;
    text-transform: uppercase;
    letter-spacing: 2px;
    margin: 28px 0 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .section-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #2a2e45;
  }

  /* PROGRESS BAR */
  .progress-bar {
    height: 6px;
    background: #2a2e45;
    border-radius: 3px;
    overflow: hidden;
    margin-top: 6px;
  }
  .progress-fill {
    height: 100%;
    border-radius: 3px;
    background: #f5a623;
    transition: width 0.3s;
  }
  .progress-fill.success { background: #3ecf8e; }
  .progress-fill.info { background: #4e9af5; }

  /* TIMELINE */
  .timeline { display: flex; flex-direction: column; gap: 0; }
  .timeline-item {
    display: flex;
    gap: 16px;
    padding-bottom: 20px;
    position: relative;
  }
  .timeline-item::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 36px;
    bottom: 0;
    width: 1px;
    background: #2a2e45;
  }
  .timeline-item:last-child::before { display: none; }
  .timeline-dot {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;
    border: 1px solid #2a2e45;
  }
  .timeline-dot.active { background: #f5a62322; border-color: #f5a623; }
  .timeline-dot.done { background: #3ecf8e22; border-color: #3ecf8e; }
  .timeline-dot.pending { background: #1a1d27; }
  .timeline-content { flex: 1; padding-top: 8px; }
  .timeline-title { font-size: 14px; font-weight: 500; color: #e8eaf2; }
  .timeline-date { font-size: 12px; color: #4a5068; margin-top: 2px; }

  /* CHIP */
  .chip {
    display: inline-flex; align-items: center;
    gap: 6px; padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    background: #21253a;
    border: 1px solid #2a2e45;
    color: #7b82a0;
    cursor: pointer;
    transition: all 0.15s;
  }
  .chip:hover, .chip.selected {
    background: #f5a62322;
    border-color: #f5a623;
    color: #f5a623;
  }

  /* LAVORAZIONE CARD */
  .lav-card {
    background: #0f1117;
    border: 1px solid #2a2e45;
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .lav-num {
    font-family: 'Syne', sans-serif;
    font-size: 20px;
    font-weight: 800;
    color: #f5a623;
    min-width: 32px;
  }
  .lav-info { flex: 1; }
  .lav-title { font-size: 14px; font-weight: 500; color: #e8eaf2; }
  .lav-meta { font-size: 12px; color: #7b82a0; margin-top: 3px; }
  .lav-totale { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; color: #3ecf8e; }

  /* CANTIERE CARD */
  .cantiere-card {
    background: #1a1d27;
    border: 1px solid #2a2e45;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 12px;
    cursor: pointer;
    transition: border-color 0.15s;
  }
  .cantiere-card:hover { border-color: #f5a62355; }
  .cantiere-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
  .cantiere-name { font-family: 'Syne', sans-serif; font-size: 16px; font-weight: 700; }
  .cantiere-meta { font-size: 12px; color: #7b82a0; margin-top: 2px; }
  .cantiere-progress-label { display: flex; justify-content: space-between; font-size: 12px; color: #7b82a0; margin-bottom: 4px; }

  /* DIPENDENTE */
  .dip-row { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid #2a2e4544; }
  .dip-row:last-child { border-bottom: none; }
  .avatar {
    width: 34px; height: 34px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px; color: #111;
    flex-shrink: 0;
  }
  .dip-name { font-size: 14px; font-weight: 500; color: #e8eaf2; }
  .dip-role { font-size: 11px; color: #7b82a0; }
  .dip-ore { font-family: 'Syne', sans-serif; font-weight: 700; font-size: 14px; color: #4e9af5; margin-left: auto; }

  /* TAB */
  .tabs { display: flex; gap: 4px; margin-bottom: 24px; border-bottom: 1px solid #2a2e45; }
  .tab {
    padding: 10px 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    color: #7b82a0;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    transition: all 0.15s;
  }
  .tab:hover { color: #e8eaf2; }
  .tab.active { color: #f5a623; border-bottom-color: #f5a623; }

  /* CALENDARIO */
  .cal-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
  }
  .cal-day-label { text-align: center; font-size: 11px; color: #4a5068; padding: 6px 0; text-transform: uppercase; letter-spacing: 1px; }
  .cal-day {
    aspect-ratio: 1;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px;
    cursor: pointer;
    position: relative;
    transition: background 0.1s;
  }
  .cal-day:hover { background: #21253a; }
  .cal-day.today { background: #f5a62322; color: #f5a623; font-weight: 700; }
  .cal-day.has-event::after {
    content: '';
    position: absolute;
    bottom: 4px;
    width: 4px; height: 4px;
    border-radius: 50%;
    background: #f5a623;
  }
  .cal-day.busy { background: #4e9af522; color: #4e9af5; }
  .cal-day.empty { opacity: 0; pointer-events: none; }

  /* RESPONSIVE GRID */
  .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }

  /* AMOUNT */
  .amount-big { font-family: 'Syne', sans-serif; font-size: 32px; font-weight: 800; color: #3ecf8e; }
  .amount-label { font-size: 12px; color: #7b82a0; }

  /* ALERT */
  .alert {
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .alert-info { background: #4e9af522; color: #4e9af5; border: 1px solid #4e9af533; }
  .alert-warning { background: #f5a62322; color: #f5a623; border: 1px solid #f5a62333; }

  /* SCROLLBAR */
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #2a2e45; border-radius: 3px; }
`;

// ─── DATA ───────────────────────────────────────────────────────────────────

// dipendenti è ora dinamico — letto da Firebase tramite prop utentiDB
// Questo fallback viene usato solo se utentiDB non è ancora caricato
const DIPENDENTI_FALLBACK = [
  { nome: "Marco",     colore: "#f5a623", ruolo: "Titolare" },
  { nome: "Edoardo",   colore: "#e09520", ruolo: "Titolare" },
  { nome: "Ion",       colore: "#4e9af5", ruolo: "Operaio edile" },
  { nome: "Ilie",      colore: "#3ecf8e", ruolo: "Operaio edile" },
  { nome: "Mazza",     colore: "#a78bfa", ruolo: "Operaio edile" },
  { nome: "Mahamadou", colore: "#f06060", ruolo: "Operaio edile" },
  { nome: "Amir",      colore: "#34d399", ruolo: "Operaio edile" },
  { nome: "Leo",       colore: "#60c0f0", ruolo: "Operaio edile" },
];
// dipendenti viene definito come variabile globale aggiornata dall'App
let dipendenti = DIPENDENTI_FALLBACK;
// Costo orario globale — modificabile dalle Impostazioni
let COSTO_ORARIO_GLOBALE = 25;

const preventivi = [];

const cantieri = [];

const sopralluoghi = [];

// ─── BADGE STATO ────────────────────────────────────────────────────────────

// LAV_BY_ID_v1 - aggancio registrazione -> lavorazione: id, poi nome, poi posizione
const normLavTxt = s => (s||"").toString().toLowerCase().trim();
// LAV_BY_ID_v4 - ricava l'id della lavorazione da posizione + nome (solo se sicuro)
const idLavDaPos = (lavs, idx, desc) => {
  const L = lavs || [];
  const p = parseInt(idx);
  const lo = (!isNaN(p) && p >= 0 && p < L.length) ? L[p] : null;
  const nd = normLavTxt(desc);
  let scelta = null;
  if (lo && (!nd || normLavTxt(lo.desc) === nd)) scelta = lo;
  else if (nd) {
    const cand = L.filter(l => l && normLavTxt(l.desc) === nd);
    if (cand.length === 1) scelta = cand[0];
  }
  return (scelta && scelta.id != null) ? scelta.id : null;
};
const trovaIdxLav = (a, lavs) => {
  const L = lavs || [];
  if (!a) return -1;
  if (a.lavorazioneId != null && a.lavorazioneId !== "") {
    const k = String(a.lavorazioneId);
    return L.findIndex(l => l && l.id != null && String(l.id) === k);
  }
  if (a.lavorazione) {
    const n = normLavTxt(a.lavorazione);
    const i = L.findIndex(l => l && normLavTxt(l.desc) === n);
    if (i >= 0) return i;
  }
  if (a.lavorazioneIdx != null) {
    const p = parseInt(a.lavorazioneIdx);
    if (!isNaN(p) && p >= 0 && p < L.length) return p;
  }
  return -1;
};

function StatoBadge({ stato }) {
  const map = {
    accettato: ["badge-success", "✓"],
    inviato: ["badge-info", "→"],
    bozza: ["badge-muted", "◦"],
    rifiutato: ["badge-danger", "✕"],
    "preventivo creato": ["badge-success", "✓"],
    "da preventivare": ["badge-warning", "⏳"],
  };
  const [cls, icon] = map[stato] || ["badge-muted", "?"];
  return <span className={`badge ${cls}`}>{icon} {stato}</span>;
}

// ─── PAGES ──────────────────────────────────────────────────────────────────

function Dashboard({ onNavigate, cantieriDB = [], fattureDB = [], assenzeDB = [], sopralluoghiDB = [], materialiDB = [], utente = null }) {
  // F12.2: stato scadenze documenti
  const [scadenzeData, setScadenzeData] = React.useState({ scaduti: [], inScadenza: [], conteggi: { scaduti: 0, inScadenza: 0 } });
  const [showScadenzeModal, setShowScadenzeModal] = React.useState(false);

  React.useEffect(() => {
    async function caricaScadenze() {
      try {
        const token = (typeof window !== "undefined" && window.localStorage)
          ? window.localStorage.getItem("bertoc_jwt") : null;
        if (!token) return;
        const r = await fetch("/api/scadenze", { headers: { "Authorization": "Bearer " + token } });
        if (!r.ok) return;
        const j = await r.json();
        if (j && j.ok) setScadenzeData(j);
      } catch (e) { /* silenzioso */ }
    }
    caricaScadenze();
  }, []);

  const oggi = new Date();
  const oggiStr = oggi.toLocaleDateString("it-IT");
  const oggiLabel = oggi.toLocaleDateString("it-IT", { weekday:"long", day:"numeric", month:"long", year:"numeric" });
  const meseName = oggi.toLocaleDateString("it-IT", { month:"long", year:"numeric" });
  const meseCorrente = oggi.getMonth();
  const annoCorrente = oggi.getFullYear();

  // ── CANTIERI ─────────────────────────────────────────────────────────────
  const cantieriAttivi = cantieriDB.filter(c => !c.completato);

  // Avanzamento reale per ogni cantiere
  const avanzamentoCantiere = (c) => {
    const cid = c._id||c.id;
    const lavs = c.lavorazioni||[];
    if (lavs.length === 0) return c.avanzamento||0;
    const normS = s => (s||"").toLowerCase().trim();
    // Calcola ore totali effettive e previste
    const oreTotPrev = lavs.reduce((s,l) => s + ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0), 0);
    // Mappa ore per lavorazione (per nome, poi per indice)
    const orePerLav = {};
    assenzeDB.filter(a => a.tipo==="lavorato" && (a.cantiereId===cid || normS(a.cantiere)===normS(c.nome))).forEach(a => {
      let idx = trovaIdxLav(a, lavs); // LAV_BY_ID_v1
      if (idx >= 0) orePerLav[idx] = (orePerLav[idx]||0) + (parseFloat(a.ore)||0);
      else orePerLav["_tot"] = (orePerLav["_tot"]||0) + (parseFloat(a.ore)||0);
    });
    const oreTotEff = Object.values(orePerLav).reduce((s,v) => s+v, 0);
    if (oreTotPrev > 0 && oreTotEff > 0) return Math.min(100, Math.round((oreTotEff/oreTotPrev)*100));
    // Fallback media pesata
    let somma = 0, totQ = 0;
    lavs.forEach((l,i) => {
      const orePrev = ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0);
      const oreEff = orePerLav[i]||0;
      const perc = orePrev>0?Math.min(100,Math.round((oreEff/orePrev)*100)):(oreEff>0?100:0);
      const q = parseFloat(l.qta)||1;
      somma += perc*q; totQ += q;
    });
    const calc = totQ>0?Math.round(somma/totQ):0;
    return calc > 0 ? calc : (c.avanzamento||0);
  };

  const cantieriConAvanz = cantieriAttivi.map(c => {
    const fineDate = (() => { const p=(c.fine||"").split("/"); return p.length===3?new Date(`${p[2]}-${p[1]}-${p[0]}`):null; })();
    const giorniRim = fineDate ? Math.ceil((fineDate-oggi)/(1000*60*60*24)) : null;
    const rischio = giorniRim===null?"basso":giorniRim<=3?"alto":giorniRim<=10?"medio":"basso";
    const avanz = avanzamentoCantiere(c);
    return { ...c, giorniRim, rischio, avanz };
  }).sort((a,b) => (a.giorniRim||999)-(b.giorniRim||999));

  // ── CHI LAVORA OGGI ───────────────────────────────────────────────────────
  const oreOggi = assenzeDB.filter(a => a.tipo==="lavorato" && a.data===oggiStr);
  // Raggruppa per operaio
  const squadraOggi = Object.values(
    oreOggi.reduce((map, a) => {
      if (!map[a.operaio]) map[a.operaio] = { operaio:a.operaio, cantieri:[], oreTotal:0 };
      const existing = map[a.operaio].cantieri.find(c=>c.cantiere===a.cantiere);
      if (existing) existing.ore += parseFloat(a.ore)||0;
      else map[a.operaio].cantieri.push({ cantiere:a.cantiere||"—", ore:parseFloat(a.ore)||0 });
      map[a.operaio].oreTotal += parseFloat(a.ore)||0;
      return map;
    }, {})
  );
  const assenzeOggi = assenzeDB.filter(a => a.tipo!=="lavorato" && a.tipo!=="foto" && a.data===oggiStr);

  // ── FINANZE MESE ─────────────────────────────────────────────────────────
  // Usa pagamenti dalla Contabilità (per ora stima da importi cantieri completati nel mese)
  const cantieriMese = cantieriDB.filter(c => {
    const d = c.dataCompletamento||c.fine||"";
    const p = d.split("/");
    if (p.length!==3) return false;
    const dt = new Date(`${p[2]}-${p[1]}-${p[0]}`);
    return dt.getMonth()===meseCorrente && dt.getFullYear()===annoCorrente;
  });
  const valoreAttivo = cantieriAttivi.reduce((s,c)=>s+(parseFloat(c.importo)||0),0);
  const oreMese = assenzeDB.filter(a=>a.tipo==="lavorato"&&(a.data||"").slice(-4)===String(annoCorrente)&&(a.data||"").slice(3,5)===String(meseCorrente+1).padStart(2,"0")).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);

  // ── MATERIALI URGENTI ─────────────────────────────────────────────────────
  const matUrgenti = materialiDB.filter(m => m.stato!=="consegnato" && m.urgenza==="alta");
  const matDaOrdinare = materialiDB.filter(m => m.stato==="da_ordinare").length;

  // ── SOPRALLUOGHI RECENTI ──────────────────────────────────────────────────
  const sopRecenti = [...sopralluoghiDB]
    .sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""))
    .slice(0,3);

  // ── ALERT ─────────────────────────────────────────────────────────────────
  const alerts = [
    ...cantieriConAvanz.filter(c=>c.rischio==="alto").map(c=>({
      tipo:"danger", icon:"🚨", testo:`Consegna tra ${c.giorniRim} giorni: ${c.nome}`,
      azione:()=>onNavigate("cantieri")
    })),
    ...cantieriConAvanz.filter(c=>c.rischio==="medio").map(c=>({
      tipo:"warning", icon:"⚠️", testo:`Scadenza vicina (${c.giorniRim}gg): ${c.nome}`,
      azione:()=>onNavigate("cantieri")
    })),
    ...matUrgenti.slice(0,2).map(m=>({
      tipo:"warning", icon:"📦", testo:`Materiale urgente: ${m.materiale} per ${m.cantiere||"—"}`,
      azione:()=>onNavigate("materiali")
    })),
    // F12.2: alert scadenze documenti
    ...(scadenzeData.conteggi.scaduti > 0 ? [{
      tipo:"danger", icon:"⛔", testo:`${scadenzeData.conteggi.scaduti} document${scadenzeData.conteggi.scaduti===1?"o":"i"} SCADUT${scadenzeData.conteggi.scaduti===1?"O":"I"} - clicca per dettagli`,
      azione:()=>setShowScadenzeModal(true)
    }] : []),
    ...(scadenzeData.conteggi.inScadenza > 0 ? [{
      tipo:"warning", icon:"⚠️", testo:`${scadenzeData.conteggi.inScadenza} document${scadenzeData.conteggi.inScadenza===1?"o":"i"} in scadenza nei prossimi 30gg - clicca per dettagli`,
      azione:()=>setShowScadenzeModal(true)
    }] : []),
  ];

  const RISCHIO_COLOR = { alto:"#f06060", medio:"#f5a623", basso:"#3ecf8e" };

  return (
    <div className="content">

      {/* F12.2: Modal scadenze documenti */}
      {showScadenzeModal && (
        <div onClick={()=>setShowScadenzeModal(false)} style={{
          position:"fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.7)",
          zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20,
        }}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:theme.cardBg, borderRadius:12, padding:20, maxWidth:800, width:"100%",
            maxHeight:"85vh", overflowY:"auto", border:`1px solid ${theme.border}`,
          }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20 }}>
                📋 Scadenze documenti
              </div>
              <button onClick={()=>setShowScadenzeModal(false)} style={{
                background:"transparent", border:"none", color:theme.text, fontSize:24, cursor:"pointer",
              }}>✕</button>
            </div>

            {scadenzeData.scaduti.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontWeight:700, color:"#f06060", fontSize:14, marginBottom:8 }}>
                  ⛔ SCADUTI ({scadenzeData.scaduti.length})
                </div>
                {scadenzeData.scaduti.map((d,i) => (
                  <div key={i} style={{
                    padding:"8px 10px", background:"#f0606011", border:"1px solid #f0606044",
                    borderRadius:6, marginBottom:6, fontSize:12,
                  }}>
                    <div style={{ fontWeight:600 }}>
                      {d.tipo_documento || d.filename}
                      {d.lavoratore_nome && <span style={{ color:theme.textMuted, fontWeight:400 }}> — {d.lavoratore_nome}</span>}
                    </div>
                    <div style={{ color:theme.textMuted, fontSize:11, marginTop:2 }}>
                      Scaduto il {d.scadenza} (da {Math.abs(d.giorni_rimanenti)} giorni)
                      {d.ente && ` · ${d.ente}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {scadenzeData.inScadenza.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontWeight:700, color:"#f5a623", fontSize:14, marginBottom:8 }}>
                  ⚠️ IN SCADENZA NEI PROSSIMI 30 GIORNI ({scadenzeData.inScadenza.length})
                </div>
                {scadenzeData.inScadenza.map((d,i) => (
                  <div key={i} style={{
                    padding:"8px 10px", background:"#f5a62311", border:"1px solid #f5a62344",
                    borderRadius:6, marginBottom:6, fontSize:12,
                  }}>
                    <div style={{ fontWeight:600 }}>
                      {d.tipo_documento || d.filename}
                      {d.lavoratore_nome && <span style={{ color:theme.textMuted, fontWeight:400 }}> — {d.lavoratore_nome}</span>}
                    </div>
                    <div style={{ color:theme.textMuted, fontSize:11, marginTop:2 }}>
                      Scade il {d.scadenza} (tra {d.giorni_rimanenti} giorni)
                      {d.ente && ` · ${d.ente}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {scadenzeData.scaduti.length === 0 && scadenzeData.inScadenza.length === 0 && (
              <div style={{ textAlign:"center", padding:"40px 0", color:theme.textMuted, fontSize:14 }}>
                ✓ Tutto in regola! Nessun documento scaduto o in scadenza.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22 }}>
          Buongiorno, {utente?.nome||"Marco"}! 👋
        </div>
        <div style={{ fontSize:13, color:theme.textMuted, marginTop:2 }}>{oggiLabel}</div>
      </div>

      {/* Alert bar */}
      {alerts.length > 0 && (
        <div style={{ marginBottom:16, display:"flex", flexDirection:"column", gap:8 }}>
          {alerts.map((a,i) => (
            <div key={i} onClick={a.azione} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 16px", borderRadius:10, cursor:"pointer",
              background:a.tipo==="danger"?"#f0606011":"#f5a62311",
              border:`1px solid ${a.tipo==="danger"?"#f0606044":"#f5a62344"}` }}>
              <span style={{ fontSize:18 }}>{a.icon}</span>
              <span style={{ fontSize:13, fontWeight:600, color:a.tipo==="danger"?"#f06060":"#f5a623", flex:1 }}>{a.testo}</span>
              <span style={{ fontSize:11, color:theme.textDim }}>→</span>
            </div>
          ))}
        </div>
      )}

      {/* KPI grid */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"Cantieri attivi", val:cantieriAttivi.length, icon:"🏗️", color:theme.accent, click:()=>onNavigate("cantieri") },
          { label:"Valore in lavorazione", val:"€ "+Math.round(valoreAttivo/1000)+"k", icon:"💰", color:"#3ecf8e", click:()=>onNavigate("cantieri") },
          { label:"Ore lavorate "+meseName.split(" ")[0], val:oreMese.toFixed(0)+"h", icon:"⏱️", color:"#4e9af5", click:()=>onNavigate("giornaliera") },
          { label:"Materiali da ordinare", val:matDaOrdinare, icon:"📦", color:matDaOrdinare>0?"#f5a623":"#3ecf8e", click:()=>onNavigate("materiali") },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:"16px 18px", cursor:"pointer" }} onClick={k.click}>
            <div style={{ fontSize:22, marginBottom:6 }}>{k.icon}</div>
            <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:26, color:k.color }}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Squadra di oggi */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>👷 Chi ha lavorato oggi</div>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate("giornaliera")}>Giornaliera →</button>
        </div>
        {squadraOggi.length === 0 && assenzeOggi.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0", color:theme.textDim, fontSize:13 }}>
            Nessuna ora registrata oggi. Le ore appaiono quando gli operai le inseriscono.
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px,1fr))", gap:10 }}>
            {squadraOggi.map(s => {
              const dp = dipendenti.find(d=>d.nome===s.operaio);
              return (
                <div key={s.operaio} style={{ background:theme.bg, borderRadius:10, padding:"10px 12px", border:`1px solid ${dp?.colore||theme.border}33` }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                    <div className="avatar" style={{ background:dp?.colore||"#888", width:30, height:30, fontSize:11, flexShrink:0 }}>{s.operaio[0]}</div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{s.operaio}</div>
                    <div style={{ marginLeft:"auto", fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, color:dp?.colore||theme.accent }}>{s.oreTotal.toFixed(1)}h</div>
                  </div>
                  {s.cantieri.map((c,i) => (
                    <div key={i} style={{ fontSize:11, color:theme.textMuted, padding:"2px 0", display:"flex", justifyContent:"space-between" }}>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"75%" }}>{c.cantiere}</span>
                      <span style={{ color:theme.accent, fontWeight:600, flexShrink:0 }}>{c.ore}h</span>
                    </div>
                  ))}
                </div>
              );
            })}
            {assenzeOggi.map((a,i) => {
              const dp = dipendenti.find(d=>d.nome===a.operaio);
              return (
                <div key={i} style={{ background:"#3b82f608", borderRadius:10, padding:"10px 12px", border:"1px dashed #3b82f633", opacity:0.8 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <div className="avatar" style={{ background:dp?.colore||"#3b82f6", width:30, height:30, fontSize:11 }}>{a.operaio[0]}</div>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>{a.operaio}</div>
                      <div style={{ fontSize:11, color:"#3b82f6" }}>{a.tipo}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cantieri attivi con avanzamento */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>🚦 Cantieri attivi</div>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate("cantieri")}>Tutti →</button>
        </div>
        {cantieriConAvanz.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0", color:theme.textDim, fontSize:13 }}>Nessun cantiere attivo</div>
        ) : cantieriConAvanz.slice(0,5).map(c => (
          <div key={c._id||c.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 0", borderBottom:`1px solid ${theme.border}44`, cursor:"pointer" }}
            onClick={()=>onNavigate("cantieri")}>
            <div style={{ width:10, height:10, borderRadius:"50%", background:RISCHIO_COLOR[c.rischio], flexShrink:0, boxShadow:`0 0 6px ${RISCHIO_COLOR[c.rischio]}66` }} />
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontWeight:600, fontSize:13 }}>{c.nome}</div>
              <div style={{ fontSize:11, color:theme.textMuted, marginTop:1 }}>
                {c.cliente||"—"}
                {c.giorniRim!==null && <span style={{ marginLeft:8, color:RISCHIO_COLOR[c.rischio], fontWeight:600 }}>· {c.giorniRim>0?`${c.giorniRim}gg`:"SCADUTO"}</span>}
              </div>
              <div style={{ height:4, background:theme.border, borderRadius:2, marginTop:5, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${c.avanz}%`, background:c.avanz>80?theme.success:RISCHIO_COLOR[c.rischio], borderRadius:2 }} />
              </div>
            </div>
            <div style={{ textAlign:"right", flexShrink:0 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:RISCHIO_COLOR[c.rischio] }}>{c.avanz}%</div>
              <div style={{ fontSize:11, color:theme.textDim }}>€ {Math.round(parseFloat(c.importo||0)/1000)}k</div>
            </div>
          </div>
        ))}
      </div>

      {/* Riga inferiore: Sopralluoghi + Materiali */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>

        {/* Sopralluoghi recenti */}
        <div className="card">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>📋 Ultimi sopralluoghi</div>
            <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate("sopralluogo")}>Nuovo →</button>
          </div>
          {sopRecenti.length === 0 ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:theme.textDim, fontSize:13 }}>Nessun sopralluogo ancora</div>
          ) : sopRecenti.map(s => (
            <div key={s._id} style={{ padding:"9px 0", borderBottom:`1px solid ${theme.border}33`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{s.cliente||"—"}</div>
                <div style={{ fontSize:11, color:theme.textMuted, marginTop:1 }}>{s.nomeCantiere||"—"} · {(s.lavorazioni||[]).length} lavorazioni</div>
              </div>
              <div style={{ fontSize:11, color:theme.textDim }}>{s.createdAt?new Date(s.createdAt).toLocaleDateString("it-IT"):"—"}</div>
            </div>
          ))}
        </div>

        {/* Materiali urgenti */}
        <div className="card">
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>📦 Materiali urgenti</div>
            <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate("materiali")}>Tutti →</button>
          </div>
          {matUrgenti.length === 0 ? (
            <div style={{ textAlign:"center", padding:"16px 0", color:"#3ecf8e", fontSize:13 }}>✅ Nessun materiale urgente</div>
          ) : matUrgenti.slice(0,4).map(m => (
            <div key={m._id} style={{ padding:"9px 0", borderBottom:`1px solid ${theme.border}33`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600 }}>{m.materiale}</div>
                <div style={{ fontSize:11, color:theme.textMuted, marginTop:1 }}>{m.cantiere||"—"} · {m.qta} {m.um}</div>
              </div>
              <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:5, background:"#f0606022", color:"#f06060", border:"1px solid #f0606044" }}>
                {m.stato==="da_ordinare"?"Da ordinare":"Ordinato"}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── DPI disponibili ───────────────────────────────────────────────────────────
const TUTTI_DPI = [
  { id:"casco",      emoji:"⛑️",  label:"Casco" },
  { id:"guanti",     emoji:"🧤", label:"Guanti" },
  { id:"scarpe",     emoji:"👟", label:"Scarpe antinfort." },
  { id:"occhiali",   emoji:"🥽", label:"Occhiali protez." },
  { id:"mascherina", emoji:"😷", label:"Mascherina/Maschera" },
  { id:"imbragatura",emoji:"🦺", label:"Imbragatura" },
  { id:"cuffia",     emoji:"🎧", label:"Cuffie antirumore" },
  { id:"gilet",      emoji:"🦺", label:"Gilet alta visibilità" },
];

// Dizionario a due livelli: categoria → sottocategorie
// Ogni sottocategoria ha materiali, attrezzatura, dpi propri
// Se sottocategoria non ha campi propri, eredita dalla categoria padre
let dizionarioMateriali = {
  "imbiancatura": {
    label: "Imbiancatura",
    materiali: ["Pittura lavabile", "Stucco in pasta", "Nastro carta", "Teli protezione", "Primer"],
    attrezzatura: ["Pennelli vari", "Rullo medio", "Spatola", "Secchio", "Scala"],
    dpi: ["guanti","occhiali","mascherina"],
    sottocategorie: {
      "tinteggiatura interna": { label:"Tinteggiatura interna", materiali:[], attrezzatura:[], dpi:[] },
      "tinteggiatura esterna": { label:"Tinteggiatura esterna", materiali:["Pittura per esterni","Rasante","Rete fibra"], attrezzatura:["Ponteggio","Compressore"], dpi:["casco","imbragatura","gilet"] },
      "stuccatura": { label:"Stuccatura", materiali:["Stucco in pasta","Carta vetro"], attrezzatura:["Spatola","Lijatrice"], dpi:["guanti","mascherina","occhiali"] },
    }
  },
  "cartongesso": {
    label: "Cartongesso",
    materiali: ["Lastre cartongesso", "Profili metallici", "Viti autofilettanti", "Nastro carta per giunti", "Stucco per cartongesso"],
    attrezzatura: ["Avvitatore", "Seghetto", "Livella", "Metro", "Taglierino"],
    dpi: ["guanti","occhiali","mascherina","cuffia"],
    sottocategorie: {
      "controsoffitti in cartongesso": { label:"Controsoffitti in cartongesso", materiali:[], attrezzatura:[], dpi:[] },
      "pareti in cartongesso": { label:"Pareti in cartongesso", materiali:[], attrezzatura:[], dpi:[] },
      "contropareti in cartongesso": { label:"Contropareti in cartongesso", materiali:[], attrezzatura:[], dpi:[] },
    }
  },
  "piastrelle": {
    label: "Piastrelle",
    materiali: ["Colla per piastrelle", "Fugante", "Piastrelle (da preventivo)", "Distanziatori", "Silicone"],
    attrezzatura: ["Flessibile con disco diamantato", "Spatola dentata", "Livella laser", "Martello di gomma", "Tagliapiastrelle"],
    dpi: ["casco","guanti","scarpe","occhiali","cuffia"],
    sottocategorie: {
      "posa piastrelle pavimento": { label:"Posa piastrelle pavimento", materiali:[], attrezzatura:[], dpi:[] },
      "posa piastrelle rivestimento": { label:"Posa piastrelle rivestimento", materiali:[], attrezzatura:[], dpi:[] },
    }
  },
  "muratura": {
    label: "Muratura",
    materiali: ["Mattoni", "Malta", "Rete elettrosaldata", "Primer"],
    attrezzatura: ["Cazzuola", "Livella", "Mescolatore", "Carriola"],
    dpi: ["casco","guanti","scarpe","occhiali","mascherina"],
    sottocategorie: {
      "demolizione pareti": { label:"Demolizione pareti", materiali:["Sacchi macerie","Teli protezione"], attrezzatura:["Martello demolitore","Carriola"], dpi:["casco","guanti","scarpe","occhiali","mascherina","cuffia"] },
      "demolizione massetti": { label:"Demolizione massetti", materiali:["Sacchi macerie"], attrezzatura:["Martello demolitore","Carriola"], dpi:["casco","guanti","scarpe","occhiali","mascherina","cuffia"] },
      "costruzione pareti": { label:"Costruzione pareti", materiali:[], attrezzatura:[], dpi:[] },
    }
  },
  "intonaco": {
    label: "Intonaco",
    materiali: ["Intonaco premiscelato", "Rete fibra di vetro", "Primer aggrappante", "Profili angolari"],
    attrezzatura: ["Frattazzo", "Spatola grande", "Secchio", "Mescolatore elettrico", "Scala"],
    dpi: ["guanti","occhiali","mascherina"],
    sottocategorie: {
      "intonaco interno": { label:"Intonaco interno", materiali:[], attrezzatura:[], dpi:[] },
      "intonaco esterno": { label:"Intonaco esterno", materiali:[], attrezzatura:["Ponteggio"], dpi:["casco","imbragatura","gilet"] },
    }
  },
  "pavimento": {
    label: "Pavimento",
    materiali: ["Massetto autolivellante", "Primer", "Pavimento (da preventivo)", "Battiscopa", "Colla"],
    attrezzatura: ["Spatola dentata", "Rullo", "Taglierina", "Livella", "Misuratore laser"],
    dpi: ["guanti","scarpe","occhiali"],
    sottocategorie: {
      "posa parquet": { label:"Posa parquet", materiali:["Parquet","Colla per parquet","Battiscopa legno"], attrezzatura:["Taglierina","Martello","Distanziatori"], dpi:["guanti","scarpe"] },
      "massetto": { label:"Massetto", materiali:["Sabbia","Cemento","Rete elettrosaldata"], attrezzatura:["Mescolatore","Frattazzo","Livella"], dpi:["guanti","scarpe","occhiali","mascherina"] },
    }
  },
  "facciata": {
    label: "Facciata / Cappotto",
    materiali: ["Rasante per esterni", "Pittura per esterni", "Rete fibra di vetro", "Primer", "Tasselli"],
    attrezzatura: ["Ponteggio/trabattello", "Compressore", "Pistola a spruzzo", "Rullo manico lungo"],
    dpi: ["casco","guanti","scarpe","occhiali","mascherina","imbragatura","gilet"],
    sottocategorie: {
      "cappotto termico": { label:"Cappotto termico", materiali:["Pannelli isolanti","Tasselli","Rasante","Rete fibra"], attrezzatura:["Trabattello","Spatola dentata"], dpi:["casco","guanti","scarpe","occhiali","mascherina","imbragatura","gilet"] },
      "tinteggiatura facciata": { label:"Tinteggiatura facciata", materiali:[], attrezzatura:[], dpi:[] },
    }
  },
  "idraulica": {
    label: "Idraulica",
    materiali: ["Tubi", "Raccordi", "Silicone", "Teflon"],
    attrezzatura: ["Chiave idraulica", "Flessibile", "Saldatore"],
    dpi: ["guanti","scarpe","occhiali"],
    sottocategorie: {}
  },
  "elettrica": {
    label: "Elettrica",
    materiali: ["Cavi", "Tubi corrugati", "Scatole di derivazione", "Interruttori"],
    attrezzatura: ["Cacciavite", "Tester", "Trapano"],
    dpi: ["guanti","scarpe","occhiali"],
    sottocategorie: {}
  },
  "serramenti": {
    label: "Serramenti / Infissi",
    materiali: ["Silicone", "Schiuma poliuretanica", "Viti", "Tasselli"],
    attrezzatura: ["Trapano", "Livella", "Smerigliatrice"],
    dpi: ["guanti","scarpe","occhiali","cuffia"],
    sottocategorie: {
      "posa finestre": { label:"Posa finestre", materiali:[], attrezzatura:[], dpi:[] },
      "posa porte": { label:"Posa porte", materiali:[], attrezzatura:[], dpi:[] },
    }
  },
};

// Funzione che trova i dati di una lavorazione per descrizione + categoria opzionale
// FIX_NORMMAT_v1: normMat in scope esterno per essere accessibile a entrambe getDatiLavorazione e _legacy
const normMat = (arr) => (arr||[]).map(m => typeof m === "string" ? m : (m.nome||"")).filter(Boolean);

// FIX_DATILAV_v1: aggiunto supporto a sottocategoria + lookup diretto prima del match testuale
const getDatiLavorazione = (desc, categoria, sottocategoria) => {
  // Lookup DIRETTO se categoria e sottocategoria sono passate (priorita' massima)
  if (categoria && dizionarioMateriali[categoria]) {
    const cat = dizionarioMateriali[categoria];
    if (sottocategoria && cat.sottocategorie?.[sottocategoria]) {
      const sub = cat.sottocategorie[sottocategoria];
      const mats = sub.materiali?.length ? sub.materiali : (cat.materiali || []);
      const attr = sub.attrezzatura?.length ? sub.attrezzatura : (cat.attrezzatura || []);
      const dpi = sub.dpi?.length ? sub.dpi : (cat.dpi || []);
      return { materiali: normMat(mats), materialiObj: mats, attrezzatura: attr, dpi, categoria, sottocategoria };
    }
    // solo categoria, no sottocategoria valida
    return { materiali: normMat(cat.materiali), materialiObj: cat.materiali||[], attrezzatura: cat.attrezzatura, dpi: cat.dpi, categoria, sottocategoria: null };
  }
  // Vecchio comportamento (match testuale su desc) come fallback
  return _getDatiLavorazione_legacy(desc, categoria);
};

const _getDatiLavorazione_legacy = (desc, categoria) => {
  const text = ((desc||"")+" "+(categoria||"")).toLowerCase();
  // Helper per normalizzare i materiali (stringa o oggetto)
  // FIX_NORMMAT_v1: normMat spostata in scope esterno (sopra)

  // Prima cerca corrispondenza con sottocategoria specifica
  for (const [catKey, catVal] of Object.entries(dizionarioMateriali)) {
    for (const [subKey, subVal] of Object.entries(catVal.sottocategorie||{})) {
      if (text.includes(subKey)) {
        return {
          materiali: normMat(subVal.materiali?.length ? subVal.materiali : catVal.materiali),
          materialiObj: (subVal.materiali?.length ? subVal.materiali : catVal.materiali)||[],
          attrezzatura: subVal.attrezzatura?.length ? subVal.attrezzatura : catVal.attrezzatura,
          dpi: subVal.dpi?.length ? subVal.dpi : catVal.dpi,
          categoria: catKey,
          sottocategoria: subKey,
        };
      }
    }
  }
  // Poi cerca categoria principale
  const key = Object.keys(dizionarioMateriali).find(k => text.includes(k));
  if (key) {
    const cat = dizionarioMateriali[key];
    return { materiali: normMat(cat.materiali), materialiObj: cat.materiali||[], attrezzatura: cat.attrezzatura, dpi: cat.dpi, categoria: key, sottocategoria: null };
  }
  if (categoria && dizionarioMateriali[categoria]) {
    const cat = dizionarioMateriali[categoria];
    return { materiali: normMat(cat.materiali), materialiObj: cat.materiali||[], attrezzatura: cat.attrezzatura, dpi: cat.dpi, categoria, sottocategoria: null };
  }
  return null;
};

const DPIBadges = ({ dpiIds }) => {
  if (!dpiIds || !dpiIds.length) return null;
  return (
    <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginTop:6 }}>
      {dpiIds.map(id => {
        const dpi = TUTTI_DPI.find(d => d.id === id);
        if (!dpi) return null;
        return (
          <div key={id} title={dpi.label}
            style={{ background:"#1a1f2e", border:"1px solid #f5a62344", borderRadius:6, padding:"3px 8px", fontSize:13, display:"flex", alignItems:"center", gap:4 }}>
            <span>{dpi.emoji}</span>
            <span style={{ fontSize:10, color:theme.textMuted }}>{dpi.label}</span>
          </div>
        );
      })}
    </div>
  );
};

/* PROTEGGI_CANTIERE_v1 - avviso prima di cancellare un cantiere con ore registrate */
function oreDelCantiere(assenze, c) {
  const cid = c && (c._id || c.id);
  const nome = String((c && c.nome) || "").trim().toLowerCase();
  let ore = 0, n = 0;
  (assenze || []).forEach(a => {
    const suo = (a.cantiereId && cid && a.cantiereId === cid)
      || (nome !== "" && String(a.cantiere || "").trim().toLowerCase() === nome);
    if (suo !== true) return;
    n += 1;
    ore += parseFloat(a.ore) || 0;
  });
  return { ore, n };
}
function confermaEliminaCantiere(assenze, c) {
  const r = oreDelCantiere(assenze, c);
  const nome = (c && c.nome) || "";
  if (r.n > 0) {
    return window.confirm(
      "ATTENZIONE\n\nSu \"" + nome + "\" ci sono " + r.n + " registrazioni per "
      + r.ore.toLocaleString("it-IT") + " ore di lavoro.\n\n"
      + "Cancellando il cantiere quelle ore NON vengono cancellate: restano nel database senza "
      + "piu un cantiere a cui appartenere, e i conti non torneranno piu.\n\n"
      + "Se volevi solo togliertelo dalla vista, annulla e usa Nascondi.\n\n"
      + "Eliminare comunque il cantiere?");
  }
  return window.confirm("Eliminare definitivamente il cantiere \"" + nome
    + "\"?\n\nQuesta operazione e irreversibile.");
}

function Sopralluogo({ onNavigate, sopralluoghiDB = [], clientiDB = [] }) { /* SELETTORE_CLIENTE_v1 */
  const [view, setView] = useState("list"); // "list" | "new"
  const [clienteIdSel, setClienteIdSel] = useState("");
  const [showCliSugg, setShowCliSugg] = useState(false); /* SELETTORE_CLIENTE_v2 */
  // Steps: 0=Cliente/Cantiere, 1=Misure, 2=Foto&Note, 3=Riepilogo
  const [step, setStep] = useState(0);

  // ── Dati cliente e cantiere ──────────────────────────────────────────────
  const [cliente, setCliente] = useState({
    nome:"", telefono:"", email:"", cf:"", note:""
  });
  const [cantiere, setCantiere] = useState({
    nome:"", indirizzo:"", citta:""
  });

  // ── Lavorazioni con misure annidate ─────────────────────────────────────
  // struttura: [{ id, nome, misure:[{id,nome,a,b,h,...calcoli}] }]
  const [lavorazioni, setLavorazioni] = useState([]);
  const [nuovaLav, setNuovaLav] = useState("");
  const [lavAperta, setLavAperta] = useState(null); // id lavorazione espansa

  // Form misura corrente (per la lavorazione aperta)
  const [misuraForm, setMisuraForm] = useState({ nome:"", a:"", b:"", h:"", tipo:"ambiente" });
  // tipo: "ambiente" (LxLxH), "numerica" (solo N)

  // Foto e note
  const [foto, setFoto] = useState([]);
  const [noteGenerali, setNoteGenerali] = useState("");
  const [capitolato, setCapitolato] = useState(null);
  const [sopralluogoId, setSopralluogoId] = useState(null);

  const inputStyle = {
    width:"100%", padding:"10px 13px", borderRadius:8,
    border:`1px solid ${theme.border}`, background:theme.bg,
    color:theme.text, fontSize:14, fontFamily:"'DM Sans',sans-serif", outline:"none"
  };

  // ── Calcoli misura ───────────────────────────────────────────────────────
  const calcolaMisura = (tipo, a, b, h) => {
    const na = parseFloat(a)||0, nb = parseFloat(b)||0, nh = parseFloat(h)||0;
    if (tipo === "ambiente") {
      const mqPavimento = parseFloat((na*nb).toFixed(2));
      const mqSoffitto  = mqPavimento;
      const mlPareti    = parseFloat((2*(na+nb)).toFixed(2));
      const mqPareti    = parseFloat((mlPareti*nh).toFixed(2));
      return { mqPavimento, mqSoffitto, mlPareti, mqPareti };
    } else if (tipo === "lato") {
      // Lunghezza × Altezza = mq di una singola parete
      const mqLato = parseFloat((na*nh).toFixed(2));
      return { mqLato, mlLato: na };
    } else {
      return { numerica: parseFloat(a)||0 };
    }
  };

  // ── Totali di una lavorazione ────────────────────────────────────────────
  const totaliLavorazione = (misure) => {
    let mqPavimento=0, mqSoffitto=0, mlPareti=0, mqPareti=0, numerica=0, mqLato=0, mlLato=0;
    misure.forEach(m => {
      if (m.tipo==="ambiente") {
        mqPavimento += m.mqPavimento||0;
        mqSoffitto  += m.mqSoffitto||0;
        mlPareti    += m.mlPareti||0;
        mqPareti    += m.mqPareti||0;
      } else if (m.tipo==="lato") {
        // FIX_PARETI_SINGOLE_v1: le pareti singole confluiscono nei totali pareti
        mqLato += m.mqLato||0;
        mlLato += m.mlLato||0;
        mqPareti += m.mqLato||0;
        mlPareti += m.mlLato||0;
      } else {
        numerica += m.numerica||0;
      }
    });
    return { mqPavimento, mqSoffitto, mlPareti, mqPareti, numerica, mqLato, mlLato };
  };

  // ── Azioni lavorazioni ───────────────────────────────────────────────────
  const aggiungiLavorazione = () => {
    if (!nuovaLav.trim()) return;
    const id = Date.now();
    setLavorazioni([...lavorazioni, { id, nome:nuovaLav.trim(), misure:[] }]);
    setNuovaLav("");
    setLavAperta(id);
  };

  const eliminaLavorazione = (id) => {
    setLavorazioni(lavorazioni.filter(l => l.id !== id));
    if (lavAperta === id) setLavAperta(null);
  };

  const aggiungiMisura = (lavId) => {
    const { a, b, h, nome, tipo } = misuraForm;
    if (tipo === "ambiente" && (!a || !b)) return;
    if (tipo === "numerica" && !a) return;
    const calc = calcolaMisura(tipo, a, b, h);
    const nuovaMisura = { id:Date.now(), nome:nome||"Senza nome", a, b, h, tipo, ...calc };
    setLavorazioni(lavorazioni.map(l =>
      l.id === lavId ? { ...l, misure:[...l.misure, nuovaMisura] } : l
    ));
    setMisuraForm({ nome:"", a:"", b:"", h:"", tipo:misuraForm.tipo });
  };

  const eliminaMisura = (lavId, misId) => {
    setLavorazioni(lavorazioni.map(l =>
      l.id === lavId ? { ...l, misure: l.misure.filter(m => m.id !== misId) } : l
    ));
  };

  // ── Foto ─────────────────────────────────────────────────────────────────
  const handleFoto = async (e) => {
    const files = Array.from(e.target.files);
    const nuove = await Promise.all(files.map(f => new Promise((resolve) => {
      // Ridimensiona e converti in base64 per salvare su Firestore
      const reader = new FileReader();
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX = 800;
          let w = img.width, h = img.height;
          if (w > MAX) { h = Math.round(h * MAX / w); w = MAX; }
          if (h > MAX) { w = Math.round(w * MAX / h); h = MAX; }
          canvas.width = w; canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          const base64 = canvas.toDataURL("image/jpeg", 0.7);
          resolve({ nome:f.name, url:base64, base64 });
        };
        img.src = ev.target.result;
      };
      reader.readAsDataURL(f);
    })));
    setFoto([...foto, ...nuove]);
  };

  // ── Reset ────────────────────────────────────────────────────────────────
  const resetNuovo = () => {
    setStep(0);
    setCliente({ nome:"", telefono:"", email:"", cf:"", note:"" });
    setCantiere({ nome:"", indirizzo:"", citta:"" });
    setLavorazioni([]);
    setNuovaLav("");
    setLavAperta(null);
    setMisuraForm({ nome:"", a:"", b:"", h:"", tipo:"ambiente" });
    setFoto([]);
    setNoteGenerali("");
    setCapitolato(null);
    setSopralluogoId(null);
  };

  // ── Salvataggio Firebase ─────────────────────────────────────────────────
  const salvaSopralluogo = async (stato) => {
    try {
      const docData = {
        clienteId: clienteIdSel || "",
        cliente: cliente.nome, telefono:cliente.telefono, email:cliente.email,
        cf:cliente.cf, noteCliente:cliente.note,
        nomeCantiere: cantiere.nome,
        indirizzo: cantiere.indirizzo, citta: cantiere.citta,
        lavorazioni, noteGenerali, stato,
        foto: foto.map(f => ({ nome:f.nome, url:f.base64||f.url })),
        capitolato: capitolato ? { name:capitolato.name, data:capitolato.data } : null,
        updatedAt: new Date().toISOString(),
      };
      if (sopralluogoId) {
        await setDoc(doc(db, "sopralluoghi", sopralluogoId), docData, { merge:true });
      } else {
        const ref = await addDoc(collection(db, "sopralluoghi"), { ...docData, createdAt:new Date().toISOString() });
        setSopralluogoId(ref.id);
      }
    } catch(e) { alert("❌ Errore: "+e.message); }
  };

  // ── Carica sopralluogo esistente ─────────────────────────────────────────
  const caricaSopralluogo = (s) => {
    setCliente({ nome:s.cliente||"", telefono:s.telefono||"", email:s.email||"", cf:s.cf||"", note:s.noteCliente||s.note||"" });
    setClienteIdSel(s.clienteId||"");
    setCantiere({ nome:s.nomeCantiere||"", indirizzo:s.indirizzo||"", citta:s.citta||"" });
    setLavorazioni(s.lavorazioni||[]);
    setNoteGenerali(s.noteGenerali||"");
    setFoto((s.foto||[]).map(f => ({ ...f, url: f.base64||f.url })));
    setCapitolato(s.capitolato||null);
    setSopralluogoId(s._id||null);
    setStep(0);
    setView("new");
  };

  // ── LISTA ────────────────────────────────────────────────────────────────
  const renderLista = () => (
    <>
      <div style={{ background:"#1a2a1a", border:"1px solid #3ecf8e44", borderRadius:10, padding:"12px 16px", marginBottom:20, fontSize:13, color:"#3ecf8e", display:"flex", gap:10, alignItems:"center" }}>
        📱 <span>Ottimizzato per smartphone — inserisci i dati direttamente sul cantiere.</span>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Sopralluoghi salvati</div>
          <button className="btn btn-primary" onClick={() => { resetNuovo(); setView("new"); }}>+ Nuovo sopralluogo</button>
        </div>
        <div className="table-wrap">
          {sopralluoghiDB.length === 0 ? (
            <div style={{ textAlign:"center", padding:"40px 0", color:theme.textMuted }}>Nessun sopralluogo ancora.</div>
          ) : (
            <table>
              <thead><tr><th>Cliente</th><th>Cantiere</th><th>Indirizzo</th><th>Data</th><th>Lavorazioni</th><th>Stato</th><th></th></tr></thead>
              <tbody>
                {sopralluoghiDB.map(s => (
                  <tr key={s._id||s.id}>
                    <td style={{ fontWeight:500 }}>{s.cliente}</td>
                    <td style={{ fontSize:13 }}>{s.nomeCantiere||"—"}</td>
                    <td style={{ color:theme.textMuted, fontSize:13 }}>{s.indirizzo||"—"}</td>
                    <td style={{ fontSize:13 }}>{s.createdAt ? new Date(s.createdAt).toLocaleDateString("it-IT") : s.data||"—"}</td>
                    <td style={{ fontSize:13 }}>{(s.lavorazioni||[]).length} lav.</td>
                    <td><StatoBadge stato={s.stato} /></td>
                    <td style={{ display:"flex", gap:6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => caricaSopralluogo(s)}>Apri</button>
                    <button className="btn btn-ghost btn-sm" style={{ color:theme.danger }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm("Eliminare questo sopralluogo?")) return;
                        try { await deleteDoc(doc(db,"sopralluoghi",s._id)); } catch(err) { alert("❌ "+err.message); }
                      }}>🗑️</button>
                  </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );

  const steps = ["👤 Cliente", "📐 Misure", "📷 Foto & Note", "✅ Riepilogo"];

  // ── NUOVO SOPRALLUOGO ────────────────────────────────────────────────────
  const renderNuovo = () => (
    <>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <button className="btn btn-ghost" onClick={() => { setView("list"); resetNuovo(); }}>← Tutti i sopralluoghi</button>
        <span style={{ color:theme.textMuted, fontSize:14 }}>/ {sopralluogoId ? "Modifica" : "Nuovo"} sopralluogo</span>
      </div>

      {/* Barra step */}
      <div style={{ display:"flex", alignItems:"center", marginBottom:28, gap:0 }}>
        {steps.map((s,i) => (
          <div key={i} style={{ flex:1, display:"flex", alignItems:"center" }}>
            <div onClick={() => i < step && setStep(i)}
              style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, cursor:i<step?"pointer":"default", flex:1 }}>
              <div style={{ width:32, height:32, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:13, fontWeight:700, transition:"all 0.2s",
                background: i===step?theme.accent:i<step?theme.success:theme.border,
                color: i===step?"#111":i<step?"#fff":theme.textDim }}>
                {i < step ? "✓" : i+1}
              </div>
              <div style={{ fontSize:10, color:i===step?theme.accent:i<step?theme.success:theme.textDim, textAlign:"center", whiteSpace:"nowrap" }}>
                {s.split(" ").slice(1).join(" ")}
              </div>
            </div>
            {i < steps.length-1 && (
              <div style={{ height:2, flex:1, background:i<step?theme.success:theme.border, marginBottom:18, transition:"background 0.3s" }} />
            )}
          </div>
        ))}
      </div>

      {/* ── STEP 0: CLIENTE + CANTIERE ── */}
      {step === 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom:20 }}>👤 Dati cliente e cantiere</div>

          <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:theme.accent, fontWeight:700, marginBottom:12 }}>Cliente</div>
          <div className="form-grid" style={{ marginBottom:24 }}>
            <div className="form-group" style={{ position:"relative" }}>
              <label>Nome e cognome *</label>
              <input style={inputStyle} placeholder="Mario Rossi" value={cliente.nome}
                onChange={e => { setCliente({...cliente,nome:e.target.value}); setClienteIdSel(""); setShowCliSugg(true); }}
                onFocus={() => setShowCliSugg(true)}
                onBlur={() => setTimeout(()=>setShowCliSugg(false), 200)} />
              {showCliSugg && (() => {
                const qc = String(cliente.nome||"").toLowerCase();
                const trovati = (clientiDB||[]).filter(c => c.attivo !== false
                  && String(c.nome||"").toLowerCase().includes(qc) && c.nome !== cliente.nome).slice(0, 40);
                if (!trovati.length) return null;
                return (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:100, background:"#151929", border:"1px solid #2a2e45", borderRadius:8, boxShadow:"0 8px 24px #00000066", maxHeight:220, overflowY:"auto" }}>
                    {trovati.map(c => (
                      <div key={c._id} onMouseDown={() => {
                        setCliente({ nome:c.nome||"", telefono:c.telefono||"", email:c.email||"", cf:c.piva||c.cf||"", note:c.note||"" });
                        setClienteIdSel(c._id);
                        setShowCliSugg(false);
                      }} style={{ padding:"10px 14px", cursor:"pointer", fontSize:13, borderBottom:"1px solid #2a2e4533" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#1e2438"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        👤 {c.nome}
                        {c.telefono ? <span style={{ color:"#7b82a0", marginLeft:8 }}>{c.telefono}</span> : null}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {clienteIdSel
                ? <div style={{ fontSize:11, color:"#4ec97e", marginTop:4 }}>✓ cliente in anagrafica</div>
                : (cliente.nome ? <div style={{ fontSize:11, color:"#f5a623", marginTop:4 }}>cliente nuovo</div> : null)}
            </div>
            <div className="form-group">
              <label>Telefono</label>
              <input style={inputStyle} placeholder="+39 333 000 0000" value={cliente.telefono} onChange={e => setCliente({...cliente,telefono:e.target.value})} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input style={inputStyle} placeholder="mario@email.com" value={cliente.email} onChange={e => setCliente({...cliente,email:e.target.value})} />
            </div>
            <div className="form-group">
              <label>Codice fiscale / P.IVA</label>
              <input style={inputStyle} placeholder="RSSMRA80A01H501U" value={cliente.cf} onChange={e => setCliente({...cliente,cf:e.target.value})} />
            </div>
            <div className="form-group full">
              <label>Note cliente</label>
              <textarea style={{...inputStyle, minHeight:60}} placeholder="Riferimenti, come contattare..." value={cliente.note} onChange={e => setCliente({...cliente,note:e.target.value})} />
            </div>
          </div>

          <div style={{ height:1, background:theme.border, marginBottom:20 }} />
          <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:theme.accent, fontWeight:700, marginBottom:12 }}>Cantiere</div>
          <div className="form-grid">
            <div className="form-group full">
              <label>Nome cantiere *</label>
              <input style={inputStyle} placeholder="es. Ristrutturazione appartamento Rossi" value={cantiere.nome} onChange={e => setCantiere({...cantiere,nome:e.target.value})} />
            </div>
            <div className="form-group">
              <label>Indirizzo cantiere</label>
              <input style={inputStyle} placeholder="Via Roma 12" value={cantiere.indirizzo} onChange={e => setCantiere({...cantiere,indirizzo:e.target.value})} />
            </div>
            <div className="form-group">
              <label>Città / CAP</label>
              <input style={inputStyle} placeholder="Pistoia 51100" value={cantiere.citta} onChange={e => setCantiere({...cantiere,citta:e.target.value})} />
            </div>
          </div>
        </div>
      )}

      {/* ── STEP 1: MISURE PER LAVORAZIONE ── */}
      {step === 1 && (
        <div>
          {/* Aggiungi nuova lavorazione */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:14 }}>📐 Lavorazioni e misure</div>
            <div style={{ fontSize:13, color:theme.textMuted, marginBottom:14 }}>
              Crea una lavorazione (es. "Imbiancatura appartamento") e aggiungi le misure di ogni ambiente.
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <input style={{...inputStyle, flex:1}} placeholder="Nome lavorazione — es. Imbiancatura appartamento"
                value={nuovaLav} onChange={e => setNuovaLav(e.target.value)}
                onKeyDown={e => e.key==="Enter" && aggiungiLavorazione()} />
              <button className="btn btn-primary" onClick={aggiungiLavorazione}>+ Aggiungi</button>
            </div>
          </div>

          {lavorazioni.length === 0 && (
            <div style={{ textAlign:"center", padding:"32px 0", color:theme.textDim }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📐</div>
              <div style={{ fontSize:14, color:theme.textMuted }}>Aggiungi la prima lavorazione per iniziare</div>
            </div>
          )}

          {/* Lista lavorazioni */}
          {lavorazioni.map(lav => {
            const tot = totaliLavorazione(lav.misure);
            const isAperta = lavAperta === lav.id;
            return (
              <div key={lav.id} className="card" style={{ marginBottom:12, border:`1px solid ${isAperta?theme.accent+"66":theme.border}` }}>
                {/* Header lavorazione */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
                  onClick={() => setLavAperta(isAperta ? null : lav.id)}>
                  <div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>
                      {isAperta ? "▼" : "▶"} {lav.nome}
                    </div>
                    <div style={{ fontSize:12, color:theme.textMuted, marginTop:4, display:"flex", gap:12, flexWrap:"wrap" }}>
                      {lav.misure.length > 0 && (<>
                        <span>{lav.misure.length} ambienti</span>
                        {tot.mqSoffitto > 0 && <span>Soffitto: <strong>{tot.mqSoffitto.toFixed(2)} mq</strong></span>}
                        {tot.mqPareti > 0 && <span>Pareti: <strong>{tot.mqPareti.toFixed(2)} mq</strong></span>}
                        {tot.mlPareti > 0 && <span>ML pareti: <strong>{tot.mlPareti.toFixed(2)} ml</strong></span>}
                        {tot.mqPavimento > 0 && <span>Pavimento: <strong>{tot.mqPavimento.toFixed(2)} mq</strong></span>}
                        {tot.numerica > 0 && <span>Totale: <strong>{tot.numerica} pz</strong></span>}
                      </>)}
                      {lav.misure.length === 0 && <span style={{ color:theme.textDim }}>Nessuna misura ancora</span>}
                    </div>
                  </div>
                  <button onClick={e => { e.stopPropagation(); eliminaLavorazione(lav.id); }}
                    style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:18, padding:"4px 8px" }}>✕</button>
                </div>

                {/* Corpo espanso */}
                {isAperta && (
                  <div style={{ marginTop:16, borderTop:`1px solid ${theme.border}`, paddingTop:16 }}>

                    {/* Tipo misura */}
                    <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
                      {[["ambiente","🏠 Ambiente (L×L×H)"],["lato","📏 Lato/Parete (L×H)"],["numerica","🔢 Numerica (es. N. porte)"]].map(([v,l]) => (
                        <div key={v} onClick={() => setMisuraForm({...misuraForm,tipo:v})}
                          style={{ padding:"7px 14px", borderRadius:8, cursor:"pointer", fontSize:13,
                            border:`1px solid ${misuraForm.tipo===v?theme.accent:theme.border}`,
                            background:misuraForm.tipo===v?theme.accentSoft:theme.bg,
                            color:misuraForm.tipo===v?theme.accent:theme.textMuted, fontWeight:misuraForm.tipo===v?600:400 }}>
                          {l}
                        </div>
                      ))}
                    </div>

                    {/* Form misura */}
                    <div style={{ background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}`, marginBottom:14 }}>
                      <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>Aggiungi misura</div>
                      {misuraForm.tipo === "ambiente" ? (
                        <div className="form-grid-3">
                          <div className="form-group" style={{ gridColumn:"1/-1" }}>
                            <label>Nome ambiente (es. Bagno, Camera)</label>
                            <input style={inputStyle} placeholder="es. Bagno principale"
                              value={misuraForm.nome} onChange={e => setMisuraForm({...misuraForm,nome:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Lato A (m)</label>
                            <input style={inputStyle} type="number" step="0.01" placeholder="0.00"
                              value={misuraForm.a} onChange={e => setMisuraForm({...misuraForm,a:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Lato B (m)</label>
                            <input style={inputStyle} type="number" step="0.01" placeholder="0.00"
                              value={misuraForm.b} onChange={e => setMisuraForm({...misuraForm,b:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Altezza (m)</label>
                            <input style={inputStyle} type="number" step="0.01" placeholder="2.70"
                              value={misuraForm.h} onChange={e => setMisuraForm({...misuraForm,h:e.target.value})} />
                          </div>
                        </div>
                      ) : misuraForm.tipo === "lato" ? (
                        <div className="form-grid-3">
                          <div className="form-group" style={{ gridColumn:"1/-1" }}>
                            <label>Nome parete/lato (es. Parete Nord, Facciata)</label>
                            <input style={inputStyle} placeholder="es. Parete finestra"
                              value={misuraForm.nome} onChange={e => setMisuraForm({...misuraForm,nome:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Lunghezza (m)</label>
                            <input style={inputStyle} type="number" step="0.01" placeholder="0.00"
                              value={misuraForm.a} onChange={e => setMisuraForm({...misuraForm,a:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Altezza (m)</label>
                            <input style={inputStyle} type="number" step="0.01" placeholder="2.70"
                              value={misuraForm.h} onChange={e => setMisuraForm({...misuraForm,h:e.target.value})} />
                          </div>
                        </div>
                      ) : (
                        <div className="form-grid">
                          <div className="form-group">
                            <label>Descrizione (es. Porte, Finestre)</label>
                            <input style={inputStyle} placeholder="es. Porte interne"
                              value={misuraForm.nome} onChange={e => setMisuraForm({...misuraForm,nome:e.target.value})} />
                          </div>
                          <div className="form-group">
                            <label>Quantità (N.)</label>
                            <input style={inputStyle} type="number" placeholder="es. 8"
                              value={misuraForm.a} onChange={e => setMisuraForm({...misuraForm,a:e.target.value})} />
                          </div>
                        </div>
                      )}

                      {/* Anteprima calcoli in tempo reale */}
                      {misuraForm.tipo === "ambiente" && misuraForm.a && misuraForm.b && (
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:10, marginBottom:10 }}>
                          {(() => {
                            const c = calcolaMisura("ambiente", misuraForm.a, misuraForm.b, misuraForm.h);
                            return [
                              ["Pavimento", c.mqPavimento.toFixed(2)+" mq", "#4e9af5"],
                              ["Soffitto",  c.mqSoffitto.toFixed(2)+" mq",  "#3ecf8e"],
                              ["Pareti",    c.mqPareti.toFixed(2)+" mq",    "#f5a623"],
                              ["ML pareti", c.mlPareti.toFixed(2)+" ml",    "#a78bfa"],
                            ].map(([k,v,col]) => (
                              <div key={k} style={{ background:theme.surface, border:`1px solid ${col}44`, borderRadius:8, padding:"6px 12px" }}>
                                <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase" }}>{k}</div>
                                <div style={{ fontSize:15, fontFamily:"'Syne',sans-serif", fontWeight:700, color:col }}>{v}</div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      {misuraForm.tipo === "lato" && misuraForm.a && misuraForm.h && (
                        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginTop:10, marginBottom:10 }}>
                          {(() => {
                            const c = calcolaMisura("lato", misuraForm.a, null, misuraForm.h);
                            return [
                              ["Mq parete", c.mqLato.toFixed(2)+" mq", "#f5a623"],
                              ["ML lato",   c.mlLato.toFixed(2)+" ml",  "#a78bfa"],
                            ].map(([k,v,col]) => (
                              <div key={k} style={{ background:theme.surface, border:`1px solid ${col}44`, borderRadius:8, padding:"6px 12px" }}>
                                <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase" }}>{k}</div>
                                <div style={{ fontSize:15, fontFamily:"'Syne',sans-serif", fontWeight:700, color:col }}>{v}</div>
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      <button className="btn btn-primary btn-sm" onClick={() => aggiungiMisura(lav.id)}>+ Aggiungi questa misura</button>
                    </div>

                    {/* Lista misure della lavorazione */}
                    {lav.misure.length > 0 && (
                      <div>
                        <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:theme.textDim, fontWeight:600, marginBottom:8 }}>Misure inserite</div>
                        {lav.misure.map(m => (
                          <div key={m.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}`, marginBottom:6 }}>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13 }}>{m.nome}</div>
                              <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>
                                {m.tipo === "ambiente"
                                  ? `${m.a}m × ${m.b}m × h${m.h}m — Pav: ${m.mqPavimento}mq · Soff: ${m.mqSoffitto}mq · Pareti: ${m.mqPareti}mq · ${m.mlPareti}ml`
                                  : m.tipo === "lato"
                                  ? `${m.a}m × h${m.h}m — ${m.mqLato}mq · ${m.mlLato}ml`
                                  : `N. ${m.numerica}`}
                              </div>
                            </div>
                            <button onClick={() => eliminaMisura(lav.id, m.id)}
                              style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:16 }}>✕</button>
                          </div>
                        ))}

                        {/* Totali lavorazione */}
                        <div style={{ background:theme.accentSoft, border:`1px solid ${theme.accent}44`, borderRadius:8, padding:"10px 14px", marginTop:10 }}>
                          <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.accent, fontWeight:700, marginBottom:6 }}>Totali {lav.nome}</div>
                          <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
                            {tot.mqSoffitto > 0 && <span style={{ fontSize:13 }}>Soffitto: <strong>{tot.mqSoffitto.toFixed(2)} mq</strong></span>}
                            {tot.mqPareti  > 0 && <span style={{ fontSize:13 }}>Pareti: <strong>{tot.mqPareti.toFixed(2)} mq</strong></span>}
                            {tot.mlPareti  > 0 && <span style={{ fontSize:13 }}>ML pareti: <strong>{tot.mlPareti.toFixed(2)} ml</strong></span>}
                            {tot.mqPavimento > 0 && <span style={{ fontSize:13 }}>Pavimento: <strong>{tot.mqPavimento.toFixed(2)} mq</strong></span>}
                            {tot.numerica  > 0 && <span style={{ fontSize:13 }}>Totale: <strong>{tot.numerica} pz</strong></span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── STEP 2: FOTO & NOTE ── */}
      {step === 2 && (<>
        <div className="card">
          <div className="card-title" style={{ marginBottom:16 }}>📷 Foto del cantiere</div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:12 }}>
            Scatta con la fotocamera o scegli dalla libreria.
          </div>
          <label style={{ display:"block", border:`2px dashed ${theme.border}`, borderRadius:10, padding:"24px 16px", textAlign:"center", color:theme.textDim, cursor:"pointer", marginBottom:12 }}>
            <div style={{ fontSize:32, marginBottom:8 }}>📷</div>
            <div style={{ fontSize:14, fontWeight:500 }}>Tocca per scattare o scegliere dalla libreria</div>
            <div style={{ fontSize:12, marginTop:4 }}>jpg, png — più foto contemporaneamente</div>
            <input type="file" accept="image/*" multiple capture="environment" style={{ display:"none" }} onChange={handleFoto} />
          </label>
          {foto.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(110px,1fr))", gap:8 }}>
              {foto.map((f,i) => (
                <div key={i} style={{ position:"relative" }}>
                  <img src={f.url} alt={f.nome} style={{ width:"100%", aspectRatio:"1", objectFit:"cover", borderRadius:8, border:`1px solid ${theme.border}` }} />
                  <button onClick={() => setFoto(foto.filter((_,j) => j!==i))}
                    style={{ position:"absolute", top:4, right:4, background:theme.danger, border:"none", borderRadius:"50%", width:22, height:22, color:"#fff", cursor:"pointer", fontSize:13, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom:16 }}>📄 Capitolato cliente (opzionale)</div>
          <label style={{ display:"block", border:`2px dashed ${theme.border}`, borderRadius:10, padding:"20px 16px", textAlign:"center", color:theme.textDim, cursor:"pointer" }}>
            {capitolato ? (
              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10 }}>
                <span style={{ fontSize:24 }}>📄</span>
                <div>
                  <div style={{ fontSize:14, color:theme.text, fontWeight:600 }}>{capitolato.name}</div>
                  <div style={{ fontSize:12, color:theme.success }}>✓ Allegato</div>
                </div>
                <button onClick={e => { e.preventDefault(); setCapitolato(null); }} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", marginLeft:8 }}>✕</button>
              </div>
            ) : (
              <><div style={{ fontSize:28, marginBottom:8 }}>📎</div><div style={{ fontSize:14 }}>Allega PDF capitolato</div></>
            )}
            <input type="file" accept=".pdf" style={{ display:"none" }} onChange={e => setCapitolato(e.target.files[0])} />
          </label>
        </div>
        <div className="card">
          <div className="card-title" style={{ marginBottom:12 }}>📝 Note generali</div>
          <textarea style={{...inputStyle, minHeight:100}} placeholder="Osservazioni, condizioni cantiere, accessi, orari, note importanti..."
            value={noteGenerali} onChange={e => setNoteGenerali(e.target.value)} />
        </div>
      </>)}

      {/* ── STEP 3: RIEPILOGO ── */}
      {step === 3 && (<>
        <div style={{ background:"#0a1f14", border:`1px solid ${theme.success}44`, borderRadius:12, padding:"16px 20px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:24 }}>✅</span>
          <div>
            <div style={{ fontWeight:700, color:theme.success, fontSize:15 }}>Sopralluogo pronto!</div>
            <div style={{ fontSize:13, color:theme.textMuted, marginTop:2 }}>Verifica i dati e salva, oppure crea subito il preventivo.</div>
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom:16 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom:12 }}>👤 Cliente</div>
            {[["Nome",cliente.nome||"—"],["Telefono",cliente.telefono||"—"],["Email",cliente.email||"—"]].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                <span style={{ color:theme.textMuted }}>{k}</span><span style={{ fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="card-title" style={{ marginBottom:12 }}>🏗️ Cantiere</div>
            {[["Nome cantiere",cantiere.nome||"—"],["Indirizzo",cantiere.indirizzo||"—"],["Città",cantiere.citta||"—"]].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                <span style={{ color:theme.textMuted }}>{k}</span><span style={{ fontWeight:500 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {lavorazioni.map(lav => {
          const tot = totaliLavorazione(lav.misure);
          return (
            <div key={lav.id} className="card" style={{ marginBottom:12 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, marginBottom:12, color:theme.accent }}>
                📐 {lav.nome}
              </div>
              {lav.misure.map((m,i) => (
                <div key={m.id} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                  <span style={{ fontWeight:500 }}>{m.nome||`Misura ${i+1}`}</span>
                  <span style={{ color:theme.textMuted, fontSize:12 }}>
                    {m.tipo==="ambiente"
                      ? `Pav: ${(m.mqPavimento||0).toFixed(2)}mq · Soff: ${(m.mqSoffitto||0).toFixed(2)}mq · Par: ${(m.mqPareti||0).toFixed(2)}mq · ${(m.mlPareti||0).toFixed(2)}ml`
                      : m.tipo==="lato"
                      ? `${(m.mqLato||0).toFixed(2)}mq · ${(m.mlLato||0).toFixed(2)}ml`
                      : `N. ${m.numerica||0}`}
                  </span>
                </div>
              ))}
              {lav.misure.length > 0 && (
                <div style={{ display:"flex", gap:16, flexWrap:"wrap", marginTop:8, padding:"8px 0", borderTop:`1px solid ${theme.accent}33` }}>
                  {tot.mqSoffitto>0 && <span style={{ fontSize:13 }}>Soffitto: <strong style={{ color:theme.accent }}>{tot.mqSoffitto.toFixed(2)} mq</strong></span>}
                  {tot.mqPareti>0  && <span style={{ fontSize:13 }}>Pareti: <strong style={{ color:theme.accent }}>{tot.mqPareti.toFixed(2)} mq</strong></span>}
                  {tot.mlPareti>0  && <span style={{ fontSize:13 }}>ML pareti: <strong style={{ color:theme.accent }}>{tot.mlPareti.toFixed(2)} ml</strong></span>}
                  {tot.mqPavimento>0 && <span style={{ fontSize:13 }}>Pavimento: <strong style={{ color:theme.accent }}>{tot.mqPavimento.toFixed(2)} mq</strong></span>}
                  {tot.numerica>0  && <span style={{ fontSize:13 }}>Totale: <strong style={{ color:theme.accent }}>{tot.numerica} pz</strong></span>}
                </div>
              )}
            </div>
          );
        })}

        {foto.length > 0 && (
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:12 }}>📷 Foto ({foto.length})</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {foto.map((f,i) => (
                <img key={i} src={f.base64||f.url} alt={f.nome||""} onClick={()=>window.open(f.base64||f.url,"_blank")}
                  style={{ width:80, height:80, objectFit:"cover", borderRadius:8, cursor:"pointer", border:`1px solid ${theme.border}` }} />
              ))}
            </div>
          </div>
        )}

        <div style={{ display:"flex", gap:12, justifyContent:"flex-end", marginTop:8, flexWrap:"wrap" }}>
          <button className="btn btn-ghost" onClick={async () => { await salvaSopralluogo("bozza"); setView("list"); resetNuovo(); }}>
            Salva come bozza
          </button>
          <button className="btn btn-primary" style={{ padding:"12px 28px", fontSize:15 }}
            onClick={async () => { await salvaSopralluogo("da preventivare"); setView("list"); resetNuovo(); }}>
            💾 Salva sopralluogo
          </button>
          <button className="btn btn-primary" style={{ padding:"12px 28px", fontSize:15, background:theme.success, color:"#111" }}
            onClick={async () => {
              const id = await salvaSopralluogo("preventivo creato");
              setView("list"); resetNuovo();
              if (onNavigate) onNavigate("preventivi", id);
            }}>
            📋 Salva e crea preventivo →
          </button>
        </div>
      </>)}

      {/* Navigazione */}
      {step < 3 && (
        <div style={{ display:"flex", justifyContent:"space-between", marginTop:8, gap:10 }}>
          <button className="btn btn-ghost" onClick={() => step > 0 ? setStep(step-1) : setView("list")}>
            {step===0 ? "✕ Annulla" : "← Indietro"}
          </button>
          <button className="btn btn-primary" style={{ padding:"10px 28px" }} onClick={() => setStep(step+1)}>
            {step===2 ? "Vai al riepilogo →" : "Avanti →"}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="content">
      {view === "list" ? renderLista() : renderNuovo()}
    </div>
  );
}


// ─── LISTINO LAVORAZIONI (modificabile a runtime) ────────────────────────────
const LISTINO_DEFAULT = [
  { id:"L01", desc:"Tinteggiatura pareti e soffitti",   um:"mq",  costoMat:4.5,  minuti:18,  parolaChiave:"imbiancatura", smaltimento:false },
  { id:"L02", desc:"Stucco e rasatura pareti",           um:"mq",  costoMat:3.2,  minuti:25,  parolaChiave:"imbiancatura", smaltimento:false },
  { id:"L03", desc:"Posa piastrelle pavimento",          um:"mq",  costoMat:18,   minuti:45,  parolaChiave:"piastrelle",   smaltimento:false },
  { id:"L04", desc:"Posa piastrelle rivestimento",       um:"mq",  costoMat:16,   minuti:50,  parolaChiave:"piastrelle",   smaltimento:false },
  { id:"L05", desc:"Demolizione rivestimento esistente", um:"mq",  costoMat:0,    minuti:20,  parolaChiave:"demolizione",  smaltimento:true  },
  { id:"L06", desc:"Struttura cartongesso",              um:"mq",  costoMat:8,    minuti:30,  parolaChiave:"cartongesso",  smaltimento:false },
  { id:"L07", desc:"Posa lastre cartongesso",            um:"mq",  costoMat:6,    minuti:25,  parolaChiave:"cartongesso",  smaltimento:false },
  { id:"L08", desc:"Rasatura e finitura cartongesso",    um:"mq",  costoMat:3,    minuti:20,  parolaChiave:"cartongesso",  smaltimento:false },
  { id:"L09", desc:"Pulizia e preparazione facciata",    um:"mq",  costoMat:2,    minuti:15,  parolaChiave:"facciata",     smaltimento:false },
  { id:"L10", desc:"Tinteggiatura esterna facciata",     um:"mq",  costoMat:7,    minuti:22,  parolaChiave:"facciata",     smaltimento:false },
  { id:"L11", desc:"Rappezzi intonaco facciata",         um:"mq",  costoMat:12,   minuti:40,  parolaChiave:"facciata",     smaltimento:false },
  { id:"L12", desc:"Posa sanitari completi",             um:"cad", costoMat:280,  minuti:120, parolaChiave:"bagno",        smaltimento:false },
  { id:"L13", desc:"Impermeabilizzazione bagno",         um:"mq",  costoMat:9,    minuti:30,  parolaChiave:"bagno",        smaltimento:false },
  { id:"L14", desc:"Verniciatura porte interne",         um:"cad", costoMat:12,   minuti:45,  parolaChiave:"verniciatura", smaltimento:false },
  { id:"L15", desc:"Posa battiscopa",                    um:"ml",  costoMat:8,    minuti:10,  parolaChiave:"battiscopa",   smaltimento:false },
  { id:"L16", desc:"Posa pavimento in legno",            um:"mq",  costoMat:35,   minuti:40,  parolaChiave:"pavimento",    smaltimento:false },
  { id:"L17", desc:"Intonaco civile interno",            um:"mq",  costoMat:6,    minuti:35,  parolaChiave:"intonaco",     smaltimento:false },
];

function Preventivi({ clientiDB = [], preventiviDB = [], sopralluoghiDB = [], cantieriEsistenti = [], initialSopralluogoId = null, listinoMatDB = [], famiglieProdottoDB = [] }) {
  const [view, setView] = useState("list");
  const [preventivoId, setPreventivoId] = useState(null);

  // ── Listino modificabile — caricato da Firebase ──────────────────────────
  const [listino, setListino] = useState(LISTINO_DEFAULT);
  const [pacchetti, setPacchetti] = useState([]); /* PACCHETTI_v1 */
  useEffect(() => {
    const unsubPac = onSnapshot(doc(db, "impostazioni", "pacchetti"), snap => {
      if (snap.exists() && Array.isArray(snap.data().lista)) setPacchetti(snap.data().lista);
    }, () => {});
    return () => unsubPac();
  }, []);
  const salvaPacchettiFirebase = async (nuovi) => {
    try { await setDoc(doc(db, "impostazioni", "pacchetti"), { lista: nuovi, updatedAt: new Date().toISOString() }); }
    catch (e) { alert("Errore nel salvare i pacchetti: " + ((e && e.message) || e)); }
  };
  const [listinoLoaded, setListinoLoaded] = useState(false);
  const [showListino, setShowListino] = useState(false);
  const [showGestListino, setShowGestListino] = useState(false);
  const [searchListino, setSearchListino] = useState("");
  const [listinoForm, setListinoForm] = useState({ desc:"", um:"mq", costoMat:"0", minuti:"30", parolaChiave:"", smaltimento:false });
  const [editListinoId, setEditListinoId] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "impostazioni", "listino"), snap => {
      if (snap.exists() && snap.data().voci?.length) {
        setListino(snap.data().voci);
      } else {
        setListino(LISTINO_DEFAULT);
      }
      setListinoLoaded(true);
    }, () => { setListinoLoaded(true); });
    return () => unsub();
  }, []);

  const salvaListinoFirebase = async (nuovoListino) => {
    try {
      await setDoc(doc(db, "impostazioni", "listino"), { voci: nuovoListino, updatedAt: new Date().toISOString() });
    } catch(e) { console.error("Errore salvataggio listino:", e.message); }
  };

  // ── Sopralluogo collegato ─────────────────────────────────────────────────
  const [sopralluogoCollegato, setSopralluogoCollegato] = useState("");
  // ── Autocomplete cliente ──────────────────────────────────────────────────
  const [showClientiSuggeriti, setShowClientiSuggeriti] = useState(false);
  const [showCantieriCliente, setShowCantieriCliente] = useState(false);
  const [showMisureSop, setShowMisureSop] = useState(false);
  const [showPdfImport, setShowPdfImport] = useState(false);
  const [showCrono, setShowCrono] = useState(false); // FIX_CRONO_STATE_v1
  const [cronoMargine, setCronoMargine] = useState(15); // % sicurezza
  const [cronoOreGiorno, setCronoOreGiorno] = useState(8);
  const [cronoOperai, setCronoOperai] = useState({}); // { indiceLav: nOperai }
  const [cronoFasiEsterne, setCronoFasiEsterne] = useState([]); // FIX_CRONO_SAVE_v1: [{desc, giorni, chi}]
  const [cronoOrdine, setCronoOrdine] = useState(null); // ordine personalizzato (array di chiavi) o null=default
  const [cronoNote, setCronoNote] = useState({}); // FIX_CRONO_AI_STATE_v1: { key: nota } dall'AI
  const [cronoAiLoading, setCronoAiLoading] = useState(false);
  const [cronoSpezzate, setCronoSpezzate] = useState([]); // FIX_CRONO_SPEZZA_STATE_v1: key interne nascoste perche spezzate
  const [cronoParallele, setCronoParallele] = useState([]); // FIX_CRONO_PARALLELO_STATE_v1: key fasi in parallelo con la precedente
  const [pdfImportLoading, setPdfImportLoading] = useState(false);
  const [pdfImportResult, setPdfImportResult] = useState("");
  const [pdfImportTesto, setPdfImportTesto] = useState("");

  // Auto-importa sopralluogo se arrivato dalla navigazione
  useEffect(() => {
    if (initialSopralluogoId && sopralluoghiDB.length > 0) {
      importaSopralluogo(initialSopralluogoId);
      setView("new");
    }
  }, [initialSopralluogoId, sopralluoghiDB]);





  // ── Parametri di calcolo — caricati da Firebase ──────────────────────────
  const [params, setParamsState] = useState({ costoOraio:25, speseGenerali:15, utile:30, ricarico:15, iva:0, noIva:true });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "impostazioni", "params"), snap => {
      if (snap.exists() && snap.data().voci) setParamsState(snap.data().voci);
    }, () => {});
    return () => unsub();
  }, []);

  const setParams = (nuovi) => {
    setParamsState(nuovi);
    setDoc(doc(db, "impostazioni", "params"), { voci: nuovi, updatedAt: new Date().toISOString() }).catch(e => console.error("params:", e.message));
  };

  // ── Formattazione numeri italiana ────────────────────────────────────────
  const fmtEur = (n, dec=2) => fmtNumIT(n, dec); /* FIX_FORMATO_NUMERI_v2 */

  // ── Header preventivo ─────────────────────────────────────────────────────
  const [header, setHeader] = useState({
    cliente:"", nomeCantiere:"", indirizzo:"", telefono:"",
    pagamento:"30gg fine mese", validita:30,
    prezzoFisso:"", usaPrezzoFisso:false,
    dataEmissione: new Date().toISOString().slice(0,10),
    dataInizioLavori:"", dataFineLavori:"",
    dataInizio:"", usaDataInizio:false, stato:"bozza",
  });

  // ── Lavorazioni ───────────────────────────────────────────────────────────
  const [lavorazioni, setLavorazioni] = useState([]);
  const emptyLav = { desc:"", descBreve:"", um:"mq", righeMatCalcolo:{}, qta:"1", costoMat:"0", minuti:"0", prezzoMercato:"0", spesaEsterna:"0", ricaricoSpesa:"20", categoria:"", sottocategoria:"", qtaMat:"", umMat:"mq", smaltimento:false, escluso:false, gruppo:"", modalitaGruppo:"corpo", prodottoListino:"", prodottoNome:"" };

  // FIX_PREVENTIVO_MODALITA_GRUPPO_v1 — aggiorna modalita per tutte le righe del gruppo
  const setModalitaPerGruppo = (nomeGruppo, modalita) => {
    setLavorazioni(prev => prev.map(l => l.gruppo === nomeGruppo ? { ...l, modalitaGruppo: modalita } : l));
  };
  // Coerenza gruppo: tutte le righe devono avere stessa um e qta per consentire "a quantita"
  const gruppoCoerentePerQuantita = (nomeGruppo) => {
    const righe = lavorazioni.filter(l => l.gruppo === nomeGruppo);
    if (righe.length === 0) return false;
    const um0 = righe[0].um;
    const qta0 = String(righe[0].qta);
    return righe.every(r => r.um === um0 && String(r.qta) === qta0);
  };
  const [lavForm, setLavForm] = useState(emptyLav);
  const [editingLavId, setEditingLavId] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);

  // ── AI NAS ────────────────────────────────────────────────────────────────
  const [aiSuggerendo, setAiSuggerendo] = useState(false);
  const [aiIncidenze, setAiIncidenze] = useState(false);

  const suggerisciCategoriaAI = async (desc) => {
    if (!desc || desc.length < 10) return;
    setAiSuggerendo(true);
    try {
      const r = await fetch(`${NAS_URL}/api/suggerisci-categoria`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ desc })
      });
      const data = await r.json();
      if (data.categoria && data.confidenza >= 70) {
        setLavForm(f => ({ ...f, categoria: data.categoria, sottocategoria: "" }));
      }
    } catch(e) { console.warn("AI suggerisci categoria:", e); }
    setAiSuggerendo(false);
  };

  const calcolaIncidenzaAI = async () => {
    if (!lavForm.categoria || !lavForm.qta) return;
    setAiIncidenze(true);
    try {
      const catDati = dizionarioMateriali[lavForm.categoria];
      const subDati = catDati?.sottocategorie?.[lavForm.sottocategoria];
      const materiali = (subDati?.materiali?.length ? subDati.materiali : catDati?.materiali || [])
        .map(m => typeof m === "string" ? m : m.nome).filter(Boolean);
      const r = await fetch(`${NAS_URL}/api/incidenze`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          categoria: lavForm.categoria, sottocategoria: lavForm.sottocategoria || "",
          um: lavForm.um || "mq", qta: parseFloat(lavForm.qta) || 1, materiali
        })
      });
      const data = await r.json();
      if (data.incidenze && data.incidenze.length > 0) {
        const riepilogo = data.incidenze
          .map(i => `• ${i.nome}: ${i.qta} ${i.um}${i.note ? " ("+i.note+")" : ""}`)
          .join("\n");
        alert(`Incidenze AI per ${lavForm.qta} ${lavForm.um}:\n\n${riepilogo}\n\nVerifica e aggiorna i costi materiali.`);
      }
    } catch(e) { alert("Errore calcolo incidenze: " + e.message); }
    setAiIncidenze(false);
  };

  // Autosalvataggio — scatta 1.5s dopo ogni modifica alle lavorazioni, solo se il preventivo è già salvato
  const autoSaveRef = useRef(null);
  const daSalvareRef = useRef(false);   /* SALVA_USCITA_v1 */
    useEffect(() => { /* FIX_AUTOSAVE_BOZZA_v1 */
    const haContenuto = (header && header.cliente && header.cliente.trim() !== "") || lavorazioni.length > 0;
    if (!haContenuto) return; // non salva pagine completamente vuote
    daSalvareRef.current = true;        /* SALVA_USCITA_v1 */
    clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(async () => {
      setAutoSaving(true);
      await salvaBozza(null, true); // true = silenzioso, no alert
      daSalvareRef.current = false;     /* SALVA_USCITA_v1 */
      setAutoSaving(false);
    }, 1500);
    return () => clearTimeout(autoSaveRef.current);
  }, [lavorazioni, header, params]);

  /* SALVA_USCITA_v1: avvisa prima di chiudere se c'e' qualcosa da salvare */
  useEffect(() => {
    const avvisa = (e) => {
      if (!daSalvareRef.current) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    const salvaSeNascosta = () => {
      if (document.visibilityState === "hidden" && daSalvareRef.current) {
        clearTimeout(autoSaveRef.current);
        try { salvaBozza(null, true); daSalvareRef.current = false; } catch (err) {}
      }
    };
    window.addEventListener("beforeunload", avvisa);
    document.addEventListener("visibilitychange", salvaSeNascosta);
    return () => {
      window.removeEventListener("beforeunload", avvisa);
      document.removeEventListener("visibilitychange", salvaSeNascosta);
    };
  }, [lavorazioni, header, params]);

  const inputStyle = {
    width:"100%", padding:"9px 12px", borderRadius:8,
    border:`1px solid ${theme.border}`, background:theme.bg,
    color:theme.text, fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none"
  };

  // ── MOTORE DI CALCOLO ─────────────────────────────────────────────────────
  const parseNum = (v) => parseFloat(String(v).replace(",",".")) || 0;
  const fmtEuro = (n, dec=2) => fmtNumIT(n, dec); /* FIX_FORMATO_NUMERI_v2 */

  const calcolaLavorazione = (l) => {
    const qta=parseNum(l.qta), costoMat=parseNum(l.costoMat);
    const minuti=parseNum(l.minuti), prezzoMercato=parseNum(l.prezzoMercato);
    const spesaEsterna = parseNum(l.spesaEsterna||0);
    const ricaricoSpesa = parseNum(l.ricaricoSpesa||20);
    const oreUn = minuti/60;

    // Se U.M. = cad (a corpo) e c'è una misura reale, il costo materiale
    // va moltiplicato per la misura reale (es. 20 mq) non per qta (1)
    const qtaMat = l.um === "cad" && l.qtaMat ? parseNum(l.qtaMat) : qta;
    const costoMatTotale = costoMat * qtaMat; // costo materiale totale (già per tutta la lavorazione)
    const costoMatUnCorretto = qta > 0 ? costoMatTotale / qta : costoMat; // per unità fatturata

    // ── Logica SEPARATA: manodopera, materiali e spesa esterna ───────────────
    const costoManUn = oreUn * params.costoOraio;
    const costoManConSpese = costoManUn * (1+params.speseGenerali/100);
    const prezzoManUn = costoManConSpese / (1-params.utile/100);
    const costoMatConRicarico = costoMatUnCorretto * (1+params.ricarico/100);

    // Spesa esterna: costo + ricarico specifico (va nel prezzo cliente)
    const spesaEsternaConRicarico = spesaEsterna * (1 + ricaricoSpesa/100);

    // Prezzo unitario finale include spesa esterna con ricarico
    const prezzoUn = prezzoManUn + costoMatConRicarico + spesaEsternaConRicarico;
    const costoTotUn = costoManUn + costoMatUnCorretto + spesaEsterna;
    const costoConSpese = costoManConSpese + costoMatUnCorretto + spesaEsterna;

    // Margine reale
    const margineUn = prezzoUn - costoTotUn;
    const marginePerc = prezzoUn > 0 ? Math.round((margineUn/prezzoUn)*100) : 0;

    const totale = prezzoUn * qta;
    const totMercato = prezzoMercato * qta;
    const scarto = prezzoMercato > 0 ? ((prezzoUn-prezzoMercato)/prezzoMercato*100) : null;
    return { oreUn, costoManUn, costoMatConRicarico, costoTotUn, costoConSpese, prezzoManUn, prezzoUn, totale, totMercato, scarto, qta, prezzoMercato, spesaEsterna, spesaEsternaConRicarico, ricaricoSpesa, margineUn, marginePerc };
  };

  const totali = lavorazioni.map(l => ({ ...l, ...calcolaLavorazione(l) }));
  // Arrotonda ogni riga a 2 decimali prima di sommare — evita accumulo floating point
  const totaliAttive = totali.filter(l => l.escluso !== true); /* VOCE_ESCLUSA_v1 */
  const subtotaleCalcolato = totaliAttive.reduce((s,l) => {
    const prezzoRound = Math.round(l.prezzoUn * 100) / 100;
    const totaleRound = Math.round(prezzoRound * l.qta * 100) / 100;
    return s + totaleRound;
  }, 0);
  const subtotale = header.usaPrezzoFisso && header.prezzoFisso ? Number(header.prezzoFisso) : Math.round(subtotaleCalcolato * 100) / 100;
  const ivaAmt = subtotale * (params.iva/100);
  const totaleFinale = subtotale + ivaAmt;
  const totMercatoTot = totaliAttive.reduce((s,l) => s+l.totMercato, 0);
  const scartoTot = totMercatoTot > 0 ? ((subtotale-totMercatoTot)/totMercatoTot*100) : null;
  const oreTotal = totaliAttive.reduce((s,l) => s+(l.oreUn*l.qta), 0);

  // ── Importa sopralluogo ───────────────────────────────────────────────────
  const importaSopralluogo = (id) => {
    setSopralluogoCollegato(id);
    const s = sopralluoghiDB.find(x => x._id === id);
    if (!s) return;
    setHeader(h => ({
      ...h,
      cliente:      s.cliente     || h.cliente,
      nomeCantiere: s.nomeCantiere || h.nomeCantiere,
      indirizzo:    s.indirizzo ? `${s.indirizzo}${s.citta?", "+s.citta:""}` : h.indirizzo,
      telefono:     s.telefono    || h.telefono,
    }));
  };

  // ── Gestione listino ──────────────────────────────────────────────────────
  const aggiungiVoceListino = () => {
    if (!listinoForm.desc) return;
    let nuovoListino;
    if (editListinoId) {
      nuovoListino = listino.map(v => v.id===editListinoId ? { ...v, ...listinoForm, costoMat:parseFloat(listinoForm.costoMat)||0, minuti:parseFloat(listinoForm.minuti)||0 } : v);
      setEditListinoId(null);
    } else {
      const newId = "L"+String(Date.now()).slice(-6);
      nuovoListino = [...listino, { id:newId, ...listinoForm, costoMat:parseFloat(listinoForm.costoMat)||0, minuti:parseFloat(listinoForm.minuti)||0 }];
    }
    setListino(nuovoListino);
    salvaListinoFirebase(nuovoListino);
    setListinoForm({ desc:"", um:"mq", costoMat:"0", minuti:"30", parolaChiave:"", smaltimento:false });
  };

  const eliminaVoceListino = (id) => {
    const nuovoListino = listino.filter(v => v.id !== id);
    setListino(nuovoListino);
    salvaListinoFirebase(nuovoListino);
  };

  const salvaVoceNelListino = (lav) => {
    const esiste = listino.find(v => v.desc.trim().toLowerCase() === (lav.desc||"").trim().toLowerCase());
    // Dati completi da salvare — inclusi categoria, sottocategoria e scelte materiali
    const datiVoce = {
      um: lav.um,
      costoMat: parseFloat(lav.costoMat)||0,
      minuti: parseFloat(lav.minuti)||0,
      smaltimento: lav.smaltimento||false,
      descBreve: lav.descBreve||"",
      categoria: lav.categoria||"",
      sottocategoria: lav.sottocategoria||"",
      righeMatCalcolo: lav.righeMatCalcolo||{}, // scelte prodotti — NON i prezzi
      spesaEsterna: parseFloat(lav.spesaEsterna)||0,
      ricaricoSpesa: parseFloat(lav.ricaricoSpesa)||20,
    };
    if (esiste) {
      if (!window.confirm(`"${lav.desc}" esiste già nel listino.\nVuoi aggiornarlo con i valori attuali (categoria, materiali, prezzi)?`)) return;
      const nuovoListino = listino.map(v => v.id===esiste.id ? { ...v, ...datiVoce } : v);
      setListino(nuovoListino);
      salvaListinoFirebase(nuovoListino);
    } else {
      const newId = "L"+String(Date.now()).slice(-6);
      const nuovaVoce = { id:newId, desc:lav.desc, parolaChiave:"", ...datiVoce };
      const nuovoListino = [...listino, nuovaVoce];
      setListino(nuovoListino);
      salvaListinoFirebase(nuovoListino);
    }
    alert(`✅ "${lav.desc}" salvato nel listino con categoria e materiali!`);
  };

  const aggiungiDaListino = (voce) => {
    // Ricalcola costoMat con i prezzi attuali del listino
    let costoMatAggiornato = voce.costoMat || 0;
    const righeMatCalcolo = voce.righeMatCalcolo || {};
    if (voce.categoria && Object.keys(righeMatCalcolo).length > 0) {
      const subDati = dizionarioMateriali[voce.categoria]?.sottocategorie?.[voce.sottocategoria];
      const catDati = dizionarioMateriali[voce.categoria];
      const materialiDiz = (subDati?.materiali?.length ? subDati.materiali : catDati?.materiali||[])
        .map(m => typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m)
        .filter(m => m.nome && parseFloat(m.incidenza) > 0);
      let nuovoCosto = 0;
      let trovati = 0;
      materialiDiz.forEach(m => {
        const scelta = righeMatCalcolo[m.nome];
        const catCerca = (m.categoriaP||"").trim() || voce.categoria;
        const prodDisp = listinoMatDB.filter(l=>(l.categoriaP||"").toLowerCase()===catCerca.toLowerCase() && l.prezzoUltimo>0)
          .sort((a,b)=>(b.ultimaData||"").localeCompare(a.ultimaData||""));
        const prod = scelta?.prodottoId ? prodDisp.find(p=>p._id===scelta.prodottoId) : prodDisp[0];
        // FIX_LISTINO_PREZZO_BASE_v1: usa prezzoUltimoBase (€/UM base) se disponibile, fallback prezzoUltimo
        if (prod) {
          const prezzoUsato = (prod.umBase && prod.prezzoUltimoBase > 0) ? prod.prezzoUltimoBase : prod.prezzoUltimo;
          nuovoCosto += parseFloat(m.incidenza) * prezzoUsato;
          trovati++;
        }
      });
      if (trovati > 0) costoMatAggiornato = nuovoCosto;
    }
    const nuovaLav = {
      id: Date.now(),
      desc: voce.desc,
      descBreve: voce.descBreve||"",
      um: voce.um,
      qta: "1",
      costoMat: String(Math.round(costoMatAggiornato*100)/100),
      minuti: String(voce.minuti||30),
      prezzoMercato: "0",
      spesaEsterna: String(voce.spesaEsterna||0),
      ricaricoSpesa: String(voce.ricaricoSpesa||20),
      categoria: voce.categoria||"",
      sottocategoria: voce.sottocategoria||"",
      righeMatCalcolo: righeMatCalcolo,
      smaltimento: voce.smaltimento||false,
      gruppo: "",
      modalitaGruppo: "corpo",
      qtaMat: "",
      umMat: "mq",
      prodottoListino: "",
      prodottoNome: "",
    };
    setLavorazioni([...lavorazioni, nuovaLav]);
    setShowListino(false);
    if (costoMatAggiornato !== voce.costoMat && voce.costoMat > 0) {
      alert(`✅ "${voce.desc}" aggiunta!\n💰 Costo mat. aggiornato: € ${voce.costoMat.toFixed(2)} → € ${costoMatAggiornato.toFixed(2)}/um`);
    }
  };

  /* PACCHETTI_v1 - ricalcolo del costo materiali con i prezzi di oggi */
  const ricalcolaCostoMat = (voce) => {
    let costo = parseFloat(voce.costoMat) || 0;
    const righe = voce.righeMatCalcolo || {};
    if (voce.categoria && Object.keys(righe).length > 0) {
      const subDati = dizionarioMateriali[voce.categoria]?.sottocategorie?.[voce.sottocategoria];
      const catDati = dizionarioMateriali[voce.categoria];
      const materialiDiz = (subDati?.materiali?.length ? subDati.materiali : catDati?.materiali||[])
        .map(m => typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m)
        .filter(m => m.nome && parseFloat(m.incidenza) > 0);
      let nuovoCosto = 0, trovati = 0;
      materialiDiz.forEach(m => {
        const scelta = righe[m.nome];
        const catCerca = (m.categoriaP||"").trim() || voce.categoria;
        const prodDisp = listinoMatDB.filter(l=>(l.categoriaP||"").toLowerCase()===catCerca.toLowerCase() && l.prezzoUltimo>0)
          .sort((a,b)=>(b.ultimaData||"").localeCompare(a.ultimaData||""));
        const prod = scelta?.prodottoId ? prodDisp.find(p=>p._id===scelta.prodottoId) : prodDisp[0];
        if (prod) {
          const prezzoUsato = (prod.umBase && prod.prezzoUltimoBase > 0) ? prod.prezzoUltimoBase : prod.prezzoUltimo;
          nuovoCosto += parseFloat(m.incidenza) * prezzoUsato;
          trovati++;
        }
      });
      if (trovati > 0) costo = nuovoCosto;
    }
    return costo;
  };

  const salvaGruppoNelListino = (nome, voci) => {
    const nomeP = String(nome || "").trim();
    if (nomeP === "") { alert("Questo gruppo non ha un nome: dagliene uno prima di salvarlo."); return; }
    const datiVoci = (voci || []).map(v => ({
      desc: v.desc, descBreve: v.descBreve || "", um: v.um, qta: String(v.qta || 1),
      costoMat: parseFloat(v.costoMat) || 0, minuti: parseFloat(v.minuti) || 0,
      spesaEsterna: parseFloat(v.spesaEsterna) || 0, ricaricoSpesa: parseFloat(v.ricaricoSpesa) || 20,
      categoria: v.categoria || "", sottocategoria: v.sottocategoria || "",
      righeMatCalcolo: v.righeMatCalcolo || {}, smaltimento: v.smaltimento || false,
    }));
    const modo = (voci && voci[0] && voci[0].modalitaGruppo) || "corpo";
    const esiste = pacchetti.find(p => String(p.nome || "").trim().toLowerCase() === nomeP.toLowerCase());
    let nuovi;
    if (esiste) {
      if (window.confirm('Il pacchetto "' + nomeP + '" esiste gia.\nVuoi aggiornarlo con le voci di adesso?') !== true) return;
      nuovi = pacchetti.map(p => p.id === esiste.id ? { ...p, voci: datiVoci, modalitaGruppo: modo } : p);
    } else {
      nuovi = [...pacchetti, { id: "P" + String(Date.now()).slice(-6), nome: nomeP, modalitaGruppo: modo, voci: datiVoci }];
    }
    setPacchetti(nuovi);
    salvaPacchettiFirebase(nuovi);
    alert('Pacchetto "' + nomeP + '" salvato con ' + datiVoci.length + ' lavorazioni.\nLo ritrovi nel listino, in cima.');
  };

  const aggiungiPacchetto = (p) => {
    const base = Date.now();
    const nuove = (p.voci || []).map((v, i) => ({
      id: base + i,
      desc: v.desc, descBreve: v.descBreve || "", um: v.um, qta: String(v.qta || 1),
      costoMat: String(Math.round(ricalcolaCostoMat(v) * 100) / 100),
      minuti: String(v.minuti || 30), prezzoMercato: "0",
      spesaEsterna: String(v.spesaEsterna || 0), ricaricoSpesa: String(v.ricaricoSpesa || 20),
      categoria: v.categoria || "", sottocategoria: v.sottocategoria || "",
      righeMatCalcolo: v.righeMatCalcolo || {}, smaltimento: v.smaltimento || false,
      escluso: false, gruppo: p.nome, modalitaGruppo: p.modalitaGruppo || "corpo",
      qtaMat: "", umMat: "mq", prodottoListino: "", prodottoNome: "",
    }));
    if (nuove.length === 0) { alert("Questo pacchetto e vuoto."); return; }
    setLavorazioni([...lavorazioni, ...nuove]);
    setShowListino(false);
  };

  // ── Gestione lavorazioni preventivo ──────────────────────────────────────
  const [prezzoObiettivo, setPrezzoObiettivo] = useState(""); /* PREZZO_OBIETTIVO_v1 */
  /* RINOMINA_GRUPPO_v1: cambia il nome a tutte le voci del gruppo insieme */
  const rinominaGruppo = (vecchio) => {
    const nuovo = window.prompt("Nome del gruppo (e' la descrizione che legge il cliente):", vecchio || "");
    if (nuovo === null) return;
    const n = nuovo.trim();
    if (!n) { alert("Il nome non puo' essere vuoto."); return; }
    if (n === vecchio) return;
    setLavorazioni(prev => prev.map(l => l.gruppo === vecchio ? { ...l, gruppo: n } : l));
  };

  /* NUM_VOCE_v3: finche' e' bozza o inviato i numeri seguono l'ordine */
  const [baseNum, setBaseNum] = useState(0);
  useEffect(() => {
    const st = header.stato || "bozza";
    /* NUM_VOCE_v4: da accettato in poi i numeri restano, ma le voci nuove ne ricevono uno */
    setLavorazioni(cur => {
      const nuove = numeraVoci(cur, st, baseNum);
      const uguale = nuove.length === cur.length && nuove.every((l,i) => l.numVoce === cur[i].numVoce);
      return uguale ? cur : nuove;
    });
  }, [lavorazioni, header.stato, baseNum]);

  // INSERISCI_VOCE_v1: inserimento in mezzo + spostamento voci
  const [inserisciDopoId, setInserisciDopoId] = useState(null);
  const unitaLav = (arr) => {
    const u = []; const visti = new Set();
    arr.forEach(x => {
      if (!x.gruppo) u.push([x]);
      else if (!visti.has(x.gruppo)) { visti.add(x.gruppo); u.push(arr.filter(y => y.gruppo === x.gruppo)); }
    });
    return u;
  };
  const spostaLavorazione = (id, dir) => {
    const u = unitaLav(lavorazioni);
    const ui = u.findIndex(g => g.some(x => x.id === id));
    if (ui < 0) return;
    const g = u[ui];
    if (g[0].gruppo) {
      const gi = g.findIndex(x => x.id === id), gj = gi + dir;
      if (gj < 0 || gj >= g.length) return;
      const g2 = [...g]; [g2[gi], g2[gj]] = [g2[gj], g2[gi]]; u[ui] = g2;
    } else {
      const uj = ui + dir;
      if (uj < 0 || uj >= u.length) return;
      [u[ui], u[uj]] = [u[uj], u[ui]];
    }
    setLavorazioni(u.flat());
  };
  const scegliInserisciDopo = (id) => {
    if (inserisciDopoId === id) { setInserisciDopoId(null); return; }
    setEditingLavId(null); setLavForm(emptyLav); setInserisciDopoId(id);
    setTimeout(() => { const b = document.getElementById("btnAggiungiLav"); if (b) b.scrollIntoView({ behavior:"smooth", block:"end" }); }, 50);
  };

  const aggiungiLavorazione = () => {
    if (!lavForm.desc) return;
    if (editingLavId) {
      setLavorazioni(lavorazioni.map(l => l.id===editingLavId ? { ...lavForm, id:editingLavId, numVoce: l.numVoce || "" } : l)); /* NUM_VOCE_v4: la modifica non perde il numero */
      setEditingLavId(null);
    } else if (inserisciDopoId != null && lavorazioni.some(x => x.id === inserisciDopoId)) {
      const nuova = { ...lavForm, id:Date.now() };
      const u = unitaLav(lavorazioni);
      const ui = u.findIndex(g => g.some(x => x.id === inserisciDopoId));
      const g = u[ui];
      if (nuova.gruppo && g[0].gruppo === nuova.gruppo) {
        const gi = g.findIndex(x => x.id === inserisciDopoId);
        const g2 = [...g]; g2.splice(gi + 1, 0, nuova); u[ui] = g2;
      } else {
        u.splice(ui + 1, 0, [nuova]);
      }
      setLavorazioni(u.flat());
    } else {
      setLavorazioni([...lavorazioni, { ...lavForm, id:Date.now() }]);
    }
    setInserisciDopoId(null);
    setLavForm(emptyLav);
  };

  const modificaLavorazione = (l) => {
    setLavForm({ desc:l.desc, descBreve:l.descBreve||"", righeMatCalcolo:l.righeMatCalcolo||{}, um:l.um, qta:String(l.qta), costoMat:String(l.costoMat), minuti:String(l.minuti), prezzoMercato:String(l.prezzoMercato), spesaEsterna:String(l.spesaEsterna||"0"), ricaricoSpesa:String(l.ricaricoSpesa||"20"), categoria:l.categoria||"", sottocategoria:l.sottocategoria||"", qtaMat:String(l.qtaMat||""), umMat:l.umMat||"mq", smaltimento:l.smaltimento||false, escluso:l.escluso||false, gruppo:l.gruppo||"", modalitaGruppo:l.modalitaGruppo||"corpo", prodottoListino:l.prodottoListino||"", prodottoNome:l.prodottoNome||"" });
    setEditingLavId(l.id);
  };

  /* CLIENTE_AUTO_v1 - se il cliente scritto non e in anagrafica, propone di crearlo */
  const assicuraClienteInAnagrafica = async (silenzioso) => {
    const nomeCli = String(header.cliente || "").trim();
    if (nomeCli === "") return header.clienteId || "";
    const nzc = s => String(s || "").trim().toLowerCase();
    const trovato = (clientiDB || []).find(c => nzc(c.nome) === nzc(nomeCli));
    if (trovato) return trovato._id;
    if (silenzioso === true) return header.clienteId || "";
    if (window.confirm("\"" + nomeCli + "\" non e ancora tra i tuoi clienti.\n\nVuoi aggiungerlo all'anagrafica? Telefono e indirizzo li prendo da qui.") !== true) return "";
    try {
      const ora = new Date().toISOString();
      const ref = await addDoc(collection(db, "clienti"), {
        nome: nomeCli,
        tipo: /condomin/i.test(nomeCli) ? "condominio" : "privato",
        piva: "", cf: "",
        indirizzo: header.indirizzo || "", citta: header.citta || "", cap: "",
        telefono: header.telefono || "", email: "", pec: "",
        amministratore: "", amminTelefono: "", amminEmail: "",
        note: "", attivo: true, createdAt: ora, updatedAt: ora
      });
      return ref.id;
    } catch (e) {
      alert("Non sono riuscito a creare il cliente: " + ((e && e.message) || e));
      return "";
    }
  };

  // ── Firebase ──────────────────────────────────────────────────────────────
  const salvaBozza = async (statoForzato, silenzioso = false) => {
    const statoFinale = statoForzato || header.stato || "bozza";
    try {
      const _cliId = header.clienteId || await assicuraClienteInAnagrafica(silenzioso); /* CLIENTE_AUTO_v1 */
      if (_cliId !== "" && _cliId !== header.clienteId) setHeader(h => ({ ...h, clienteId: _cliId }));
      const docData = {
        header: { ...header, stato: statoFinale, clienteId: _cliId || header.clienteId || "" },
        lavorazioni, params,
        cronoprogramma: { margine:cronoMargine, oreGiorno:cronoOreGiorno, operai:cronoOperai, fasiEsterne:cronoFasiEsterne, ordine:cronoOrdine, note:cronoNote, spezzate:cronoSpezzate, parallele:cronoParallele }, /* FIX_CRONO_SAVE_v1 */
        totaleFinale: totaleFinale.toFixed(2),
        stato: statoFinale,
        cliente: header.cliente||"",
        clienteId: _cliId || header.clienteId || "",
        nomeCantiere: header.nomeCantiere||"",
        sopralluogoId: sopralluogoCollegato||null,
        updatedAt: new Date().toISOString()
      };
      if (preventivoId) {
        await setDoc(doc(db,"preventivi",preventivoId), docData, { merge:true });
        if (!statoForzato && !silenzioso) alert("✅ Salvato!");
      } else {
        const ref = await addDoc(collection(db,"preventivi"), { ...docData, createdAt:new Date().toISOString() });
        setPreventivoId(ref.id);
        if (!statoForzato && !silenzioso) alert("✅ Salvato!");
      }
    } catch(e) { if (!silenzioso) alert("❌ Errore: "+e.message); }
  };

  const convertiInCantiere = async () => {
    if (!header.cliente) { alert("Inserisci il nome del cliente."); return; }
    // Lavorazioni pulite da aggiungere
    const lavoriPuliti = lavorazioni.filter(l => l.escluso !== true).map(l => ({
      id:            l.id || Date.now() + Math.floor(Math.random()*1000), // FIX: id stabile per matching
      desc:          l.desc,
      descBreve:     l.descBreve||"",
      righeMatCalcolo: l.righeMatCalcolo||{},
      um:            l.um,
      qta:           parseFloat(l.qta)||0,
      minuti:        parseFloat(l.minuti)||0,
      costoMat:      parseFloat(l.costoMat)||0,
      prezzoUn:      calcolaLavorazione(l).prezzoUn,
      totale:        calcolaLavorazione(l).totale,
      categoria:     l.categoria||"",
      sottocategoria:l.sottocategoria||"",
      qtaMat:        l.qtaMat ? parseFloat(l.qtaMat) : null,
      umMat:         l.umMat||"",
      smaltimento:   l.smaltimento||false,
      gruppo:        l.gruppo||"",
      modalitaGruppo: l.modalitaGruppo||"corpo",
      numVoce:       l.numVoce||"",            /* NUM_VOCE_CANT_v1 */
    }));

    // Cerca cantiere esistente: prima per preventivoId, poi per nome cantiere + cliente
    const normStr = s => (s||"").toLowerCase().trim();
    const nomeCant = header.nomeCantiere || `Cantiere ${header.cliente}`;
    const cantiereEsistente = cantieriEsistenti?.find(c =>
      (preventivoId && c.preventivoId === preventivoId) ||
      (normStr(c.nome) === normStr(nomeCant) && normStr(c.cliente) === normStr(header.cliente))
    ) || null;

    if (cantiereEsistente) {
      // Cantiere già esiste — aggiorna lavorazioni e importo
      const scelta = window.confirm(
        `Il cantiere "${cantiereEsistente.nome}" esiste già.\n\nOK = Aggiorna lavorazioni e importo\nAnnulla = Non fare nulla`
      );
      if (!scelta) return;
      try {
        const lavAttuali = cantiereEsistente.lavorazioni||[];
        // FIX: matching basato su id stabile (fallback su desc per voci vecchie senza id)
        const matchVoce = (la, lp) => (la.id && lp.id) ? la.id === lp.id : normStr(la.desc) === normStr(lp.desc);
        // Aggiunge solo le lavorazioni non già presenti
        const lavNuove = lavoriPuliti.filter(lp =>
          !lavAttuali.some(la => matchVoce(la, lp))
        );
        // Aggiorna tutte le lavorazioni già presenti con i valori dal preventivo
        const noUndef = v => v === undefined ? null : v;
        // PREZZI_CANTIERE_v1: elenco differenze di prezzo e conferma
        const cambiPrezzo = [];
        lavAttuali.forEach(la => {
          const lp = lavoriPuliti.find(l => matchVoce(la, l));
          if (!lp) return;
          const p0 = parseFloat(la.prezzoUn)||0, p1 = parseFloat(lp.prezzoUn)||0;
          if (Math.abs(p0-p1) < 0.001) return;
          cambiPrezzo.push({ desc: (lp.desc||la.desc||"").slice(0,55), p0, p1,
            q: parseFloat(lp.qta)||0 });
        });
        let applicaPrezzi = false;
        if (cambiPrezzo.length > 0) {
          const diff = cambiPrezzo.reduce((s,c)=>s+((c.p1-c.p0)*c.q), 0);
          const _e2 = (n)=>(Math.round((parseFloat(n)||0)*100)/100).toFixed(2); /* ARROTONDA_CONFERME_v1 */
          const righe = cambiPrezzo.map(c=>"\u2022 "+c.desc+"\n   \u20ac"+_e2(c.p0)+" \u2192 \u20ac"+_e2(c.p1)+"  x "+c.q).join("\n");
          applicaPrezzi = window.confirm(
            "Nel preventivo sono cambiati "+cambiPrezzo.length+" prezzi:\n\n"+righe+
            "\n\nVariazione totale: "+(diff>=0?"+":"")+"\u20ac"+Math.round(diff)+
            "\n\nOK = aggiorna i prezzi nel cantiere\nAnnulla = lascia i prezzi come sono (aggiorna solo il resto)"
          );
        }
        const lavAggiornate = lavAttuali.map(la => {
          const lp = lavoriPuliti.find(l => matchVoce(la, l));
          if (lp) {
            return {
              ...la,
              id: la.id || lp.id, // FIX: assegna id se mancante
              desc: lp.desc || la.desc, // FIX: aggiorna anche descrizione
              prezzoUn: applicaPrezzi ? (noUndef(lp.prezzoUn) ?? la.prezzoUn ?? 0) : (la.prezzoUn ?? 0),
              totale: applicaPrezzi ? (noUndef(lp.totale) ?? la.totale ?? 0) : (((parseFloat(la.prezzoUn)||0) * (parseFloat(noUndef(lp.qta) ?? la.qta ?? 1)||0)) || (la.totale ?? 0)), /* TOTALE_COERENTE_v1: senza aggiornare i prezzi il totale segue comunque la quantita' nuova */
              costoMat: noUndef(lp.costoMat) ?? la.costoMat ?? 0,
              minuti: noUndef(lp.minuti) ?? la.minuti ?? 30,
              qta: noUndef(lp.qta) ?? la.qta ?? 1,
              um: lp.um || la.um || "cad",
              qtaMat: lp.qtaMat != null ? lp.qtaMat : (la.qtaMat ?? null),
              umMat: lp.umMat || la.umMat || "",
              spesaEsterna: noUndef(lp.spesaEsterna) ?? la.spesaEsterna ?? 0,
              ricaricoSpesa: noUndef(lp.ricaricoSpesa) ?? la.ricaricoSpesa ?? 0,
              descBreve: lp.descBreve || la.descBreve || "",
              categoria: lp.categoria || la.categoria || "",
              sottocategoria: lp.sottocategoria || la.sottocategoria || "",
              gruppo: lp.gruppo !== undefined ? lp.gruppo : (la.gruppo||""),   /* GRUPPO_RICONV_v1 */
              modalitaGruppo: lp.modalitaGruppo || la.modalitaGruppo || "corpo",
              numVoce: lp.numVoce || la.numVoce || "",   /* NUM_VOCE_CANT_v1 */
            };
          }
          return la;
        });
        // Calcola importo totale sommando gli imponibili di tutti i preventivi accettati + quello corrente
        const preventiviSnap = await getDocs(collection(db,"preventivi"));
        const tuttiPrev = preventiviSnap.docs.map(d => ({ ...d.data(), _id: d.id }));
        const prevCantiere = tuttiPrev.filter(p => {
            const nomePrev = p.nomeCantiere||p.header?.nomeCantiere||"";
            const clientePrev = p.cliente||p.header?.cliente||"";
            return normStr(nomePrev)===normStr(nomeCant) &&
              normStr(clientePrev)===normStr(header.cliente);
          });
        const importoTotale = prevCantiere
          .filter(p => p.stato==="accettato" || p._id===preventivoId)
          .reduce((s,p) => {
            if (p._id === preventivoId) return s + (parseFloat(subtotale)||0);
            const imp = parseFloat(p.imponibile||p.subtotale||0);
            const tot = parseFloat(p.totaleFinale||0);
            const noIva = p.params?.noIva === true || p.noIva === true;
            const iva = parseFloat(p.params?.iva||10)/100;
            let imponibile = 0;
            if (imp > 0) imponibile = imp;
            else if (noIva || iva === 0) imponibile = tot;
            else if (tot > 0) imponibile = Math.round(tot/(1+iva)*100)/100;
            return s + imponibile;
          }, 0);

        await updateDoc(doc(db,"cantieri",cantiereEsistente._id), {
          lavorazioni: [...lavAggiornate, ...lavNuove],
          importo: parseFloat((importoTotale || subtotale).toFixed(2)),
          preventivoId: preventivoId||cantiereEsistente.preventivoId||null,
          updatedAt: new Date().toISOString(),
        });
        if (preventivoId) await setDoc(doc(db,"preventivi",preventivoId), { cantiereCreato:true, updatedAt:new Date().toISOString() }, { merge:true });
        const msg = lavNuove.length > 0
          ? `✅ Cantiere aggiornato! Aggiunte ${lavNuove.length} nuove lavorazioni.`
          : "✅ Importo aggiornato. Nessuna lavorazione nuova da aggiungere.";
        alert(msg);
      } catch(e) { alert("❌ Errore: "+e.message); }
    } else {
      if (!window.confirm(`Creare il cantiere "${nomeCant}"?`)) return;
      try {
        // Converti date ISO in formato italiano se presenti
        const fmtData = iso => iso ? iso.split("-").reverse().join("/") : "";
        await addDoc(collection(db,"cantieri"), {
          nome: nomeCant,
          cliente: header.cliente, indirizzo: header.indirizzo||"", citta: header.citta||"",
          importo: parseFloat(subtotale.toFixed(2)), stato:"in corso", completato:false,
          ivaPerc: params.noIva ? 0 : (params.iva || 0),
          noIva: params.noIva || false,
          preventivoId: preventivoId||null,
          inizio: header.dataInizioLavori ? fmtData(header.dataInizioLavori) : new Date().toLocaleDateString("it-IT"),
          fine: header.dataFineLavori ? fmtData(header.dataFineLavori) : "",
          avanzamento:0, priorita:"media",
          lavorazioni: lavoriPuliti,
          createdAt: serverTimestamp(), updatedAt: new Date().toISOString(),
        });
        if (preventivoId) await setDoc(doc(db,"preventivi",preventivoId), { cantiereCreato:true, updatedAt:new Date().toISOString() }, { merge:true });
        alert("✅ Cantiere creato!");
      } catch(e) { alert("❌ Errore: "+e.message); }
    }
  };

  const stampaCronoprogramma = () => { /* FIX_CRONO_PDF_v1 */
    const ORE_G = parseFloat(cronoOreGiorno)||8;
    const MARG = (parseFloat(cronoMargine)||0)/100;
    const coloreCategoriaPDF = (cat) => { if(!cat) return "#94a3b8"; const palette=["#4e9af5","#3ecf8e","#f5a623","#a78bfa","#f06060","#22d3ee","#eab308","#ec4899","#10b981","#8b5cf6"]; let h=0; for(let i=0;i<cat.length;i++) h=(h*31+cat.charCodeAt(i))>>>0; return palette[h%palette.length]; };
    const fasiInt = totali.filter(l=>l.desc).map((l,i) => {
      const ore=(parseFloat(l.oreUn)||0)*(parseFloat(l.qta)||0);
      const nop=parseFloat(cronoOperai["i"+i])||1;
      return { key:"i"+i, desc:l.descBreve||l.desc, categoria:l.categoria||"", giorni:Math.ceil((ore>0?ore/ORE_G/nop:0)*(1+MARG)) };
    }).filter(f => !(cronoSpezzate||[]).includes(f.key)); /* FIX_CRONO_PDF_SPEZZATE_v1 */
    const fasiExt=(cronoFasiEsterne||[]).map((f,j)=>({ key:"e"+j, desc:f.desc, categoria:"", giorni:parseInt(f.giorni)||0 }));
    let fasi=[...fasiInt,...fasiExt];
    if(Array.isArray(cronoOrdine)&&cronoOrdine.length){ const m={}; fasi.forEach(f=>m[f.key]=f); const o=cronoOrdine.map(k=>m[k]).filter(Boolean); const mn=fasi.filter(f=>!cronoOrdine.includes(f.key)); fasi=[...o,...mn]; }
    fasi=fasi.filter(f=>f.giorni>0);
    const isParPDF=(k)=>(cronoParallele||[]).includes(k); // FIX_CRONO_PARALLELO_PDF_v1
    const giorniTot=fasi.reduce((s,f)=>s+(isParPDF(f.key)?0:f.giorni),0);
    if(giorniTot===0){ alert("Nessuna fase con durata. Compila il cronoprogramma prima di stamparlo."); return; }
    // date: assegna a ogni fase inizio/fine in giorni lavorativi (salta domenica)
    let cursore = header.dataInizioLavori ? new Date(header.dataInizioLavori+"T00:00:00") : null;
    const addGiorniLav=(d,n)=>{ let x=new Date(d); let c=0; while(c<n){ x.setDate(x.getDate()+1); if(x.getDay()!==0)c++; } return x; };
    let giornoCorrente=0, inizioPrec=0;
    const righe = fasi.map(f=>{
      const par = isParPDF(f.key); // FIX_CRONO_PARALLELO_PDF_v1
      const inizioG = par ? inizioPrec : giornoCorrente;
      const fineG = inizioG + f.giorni;
      let dataInizio="", dataFine="";
      if(cursore){ let di=new Date(cursore); let ci=0; while(ci<inizioG){ di.setDate(di.getDate()+1); if(di.getDay()!==0)ci++; } di.setDate(di.getDate()+1); while(di.getDay()===0)di.setDate(di.getDate()+1); const df=addGiorniLav(cursore, fineG); dataInizio=di.toLocaleDateString("it-IT"); dataFine=df.toLocaleDateString("it-IT"); }
      if(!par){ inizioPrec=giornoCorrente; giornoCorrente=fineG; }
      const denom = giorniTot>0?giorniTot:1;
      const barLeft=(inizioG/denom)*100, barW=(f.giorni/denom)*100;
      return { ...f, dataInizio, dataFine, barLeft, barW, par };
    });
    const dataFineTot = cursore ? addGiorniLav(cursore, giorniTot).toLocaleDateString("it-IT") : "—";
    const settimane=Math.ceil(giorniTot/6);
    const righeHtml = righe.map(r=>`<tr><td style="word-wrap:break-word">${r.desc}</td><td style="text-align:center;white-space:nowrap">${r.giorni} gg</td><td style="text-align:center;white-space:nowrap;color:#555">${r.dataInizio||"—"}</td><td style="text-align:center;white-space:nowrap;color:#555">${r.dataFine||"—"}</td></tr>`).join("");
    const barreHtml = righe.map(r=>{ const col=coloreCategoriaPDF(r.categoria); return `<div style="display:flex;align-items:center;margin-bottom:6px;gap:10px"><div style="width:280px;font-size:10px;display:flex;align-items:center;gap:6px;overflow:hidden"><span style="width:9px;height:9px;border-radius:2px;background:${col};flex-shrink:0"></span><span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.desc}</span></div><div style="width:150px;font-size:9px;color:#666;white-space:nowrap">${r.dataInizio||"—"} → ${r.dataFine||"—"}</div><div style="width:38px;font-size:10px;font-weight:700;color:${col};white-space:nowrap">${r.giorni}gg</div><div style="flex:1;position:relative;height:16px;background:#f3f4f6;border-radius:4px"><div style="position:absolute;left:${r.barLeft}%;width:${r.barW}%;height:100%;background:${col};border-radius:4px;min-width:6px"></div></div></div>`; }).join("");
    const catUniche=[...new Set(righe.map(r=>r.categoria).filter(Boolean))];
    const legendaHtml = catUniche.length ? '<div style="display:flex;flex-wrap:wrap;gap:12px;margin:10px 0 4px;font-size:10px">'+catUniche.map(c=>`<div style="display:flex;align-items:center;gap:5px"><span style="width:10px;height:10px;border-radius:3px;background:${coloreCategoriaPDF(c)};display:inline-block"></span>${c}</div>`).join("")+'</div>' : "";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cronoprogramma — ${header.cliente||"Cliente"}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700;800&family=Syne:wght@800&display=swap');
  @page{size:A4 landscape;margin:14mm}
  *{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}
  body{font-family:'DM Sans',sans-serif;padding:28px;color:#111;max-width:1180px;margin:auto;-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}
  @media print{ *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important} }
  .logo{font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#f5a623;white-space:nowrap;flex-shrink:0;overflow:visible} /*LOGO_PDF_v1*/.logo-sub{font-size:10px;color:#888;letter-spacing:2px;text-transform:uppercase}
  .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #111;margin-bottom:24px}
  .cliente-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px;margin-bottom:20px}
  h2{font-family:'Syne',sans-serif;font-size:16px;margin:22px 0 10px}
  table{width:100%;border-collapse:collapse;margin:10px 0}th{background:#f3f4f6;padding:9px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666}
  th:nth-child(2),th:nth-child(3),th:nth-child(4){text-align:center}
  td{padding:7px 10px;border-bottom:1px solid #f3f4f6;font-size:11px}
  .riepilogo{background:#eef4ff;border:1px solid #4e9af544;border-radius:10px;padding:16px 20px;margin:20px 0;display:flex;justify-content:space-between;flex-wrap:wrap;gap:10px}
  @media print{button{display:none}}
</style></head><body>
  <div class="header">
    <div><div class="logo">BertocProject</div><div class="logo-sub">Gestionale Edile</div></div>
    <div style="text-align:right;font-size:11px;color:#555">BertocProject Srl<br>P.IVA 01747090478<br>Serravalle Pistoiese (PT)</div>
  </div>
  <div class="cliente-box">
    <div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Cronoprogramma lavori</div>
    <div style="font-size:15px;font-weight:700">${header.nomeCantiere||"Cantiere"}</div>
    <div style="font-size:12px;color:#555;margin-top:2px">Cliente: ${header.cliente||"—"}${header.indirizzo?" · "+header.indirizzo:""}</div>
  </div>
  <div class="riepilogo">
    <div><div style="font-size:11px;color:#666">DURATA STIMATA</div><div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#4e9af5">${giorniTot} giorni lavorativi</div><div style="font-size:11px;color:#888">~${settimane} settimane</div></div>
    <div style="text-align:right"><div style="font-size:11px;color:#666">PERIODO</div><div style="font-size:13px;font-weight:700">${header.dataInizioLavori?new Date(header.dataInizioLavori+"T00:00:00").toLocaleDateString("it-IT"):"da definire"} → ${dataFineTot}</div></div>
  </div>
  <h2>Diagramma delle fasi</h2>
  ${legendaHtml}
  <div style="margin:14px 0 24px">${barreHtml}</div>
<!-- dettaglio fasi rimosso: date gia nelle righe del diagramma - FIX_CRONO_PDF_LAND_v1 -->
  <div style="font-size:10px;color:#999;margin-top:24px;font-style:italic">Le tempistiche sono indicative e possono variare in base a condizioni meteo, forniture e imprevisti di cantiere. BertocProject Srl — ${new Date().toLocaleDateString("it-IT")}</div>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const stampaPDF = () => {
    // Genera righe PDF — i gruppi appaiono come voce unica col totale
    const gruppiVisti = new Set();
    const righePDF = totaliAttive.flatMap(l => {
      if (!l.gruppo) {
        const prezzoUnRound = Math.round(l.prezzoUn * 100) / 100;
        const totaleRound = Math.round(prezzoUnRound * l.qta * 100) / 100;
        const fmtIT = n => fmtNumIT(n, 2); /* FIX_FORMATO_NUMERI_v2 */
        /* FIX_PDF_UM_QTA_v1: ordine colonne U.M. poi Qtà (era invertito) */
        return [`<tr><td style="word-wrap:break-word;white-space:pre-line">${l.numVoce ? "<strong>"+l.numVoce+"</strong>  " : ""}${l.desc}</td><td style="text-align:center;white-space:nowrap">${l.um}</td><td style="text-align:center;white-space:nowrap">${l.qta}</td><td style="text-align:right;white-space:nowrap">€ ${fmtIT(prezzoUnRound)}</td><td style="text-align:right;white-space:nowrap"><strong>€ ${fmtIT(totaleRound)}</strong></td></tr>`];
      }
      if (gruppiVisti.has(l.gruppo)) return [];
      gruppiVisti.add(l.gruppo);
      const righeGruppo = totali.filter(x => x.gruppo === l.gruppo);
      const totGruppo = righeGruppo.reduce((s,x) => s+x.totale, 0);
      const fmtG = n => fmtNumIT(n, 2); /* FIX_FORMATO_NUMERI_v2 */
      // FIX_PREVENTIVO_MODALITA_GRUPPO_v1 — gestione modalita "a quantita"
      const modGruppo = l.modalitaGruppo || "corpo";
      const um0 = righeGruppo[0]?.um;
      const qta0 = righeGruppo[0]?.qta;
      const coerente = righeGruppo.every(r => r.um === um0 && r.qta === qta0);
      if (modGruppo === "quantita" && coerente && qta0 > 0) {
        const prezzoUnEquiv = Math.round((totGruppo / qta0) * 100) / 100;
        return [`<tr><td>${l.numVoce ? "<strong>"+l.numVoce+"</strong>  " : ""}<strong>${l.gruppo}</strong></td><td style="text-align:center;white-space:nowrap">${um0}</td><td style="text-align:center;white-space:nowrap">${qta0}</td><td style="text-align:right;white-space:nowrap">€ ${fmtG(prezzoUnEquiv)}</td><td style="text-align:right;white-space:nowrap"><strong>€ ${fmtG(Math.round(totGruppo*100)/100)}</strong></td></tr>`];
      }
      return [`<tr><td>${l.numVoce ? "<strong>"+l.numVoce+"</strong>  " : ""}<strong>${l.gruppo}</strong></td><td style="text-align:center">—</td><td style="text-align:center">—</td><td style="text-align:right">—</td><td style="text-align:right"><strong>€ ${fmtG(Math.round(totGruppo*100)/100)}</strong></td></tr>`];
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Preventivo — ${header.cliente||"Cliente"}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700;800&family=Syne:wght@800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',sans-serif;padding:36px;color:#111;max-width:820px;margin:auto}
  .logo{font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#f5a623;white-space:nowrap;flex-shrink:0;overflow:visible} /*LOGO_PDF_v1*/.logo-sub{font-size:10px;color:#888;letter-spacing:2px;text-transform:uppercase}
  .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #111;margin-bottom:24px}
  .cliente-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px 18px;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;margin:16px 0}th{background:#f3f4f6;padding:9px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666}
  th:nth-child(2),th:nth-child(3){text-align:center}th:nth-child(4),th:nth-child(5){text-align:right}
  td{padding:6px 10px;border-bottom:1px solid #f3f4f6;font-size:11px}/* FIX_PDF_TABELLA_NUMERO_v1 */
  .totale-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:16px 20px;float:right;min-width:280px;margin-top:16px}
  .totale-row{display:flex;justify-content:space-between;padding:5px 0;font-size:13px;color:#666}
  .totale-finale{display:flex;justify-content:space-between;font-family:'DM Sans',sans-serif;font-weight:700;font-size:16px;margin-top:10px;padding-top:10px;border-top:1px solid #ddd;color:#111}
  .firma{display:flex;justify-content:space-between;margin-top:48px;padding-top:16px;border-top:1px solid #e5e7eb;page-break-inside:avoid}
  .sign{border:1px solid #ddd;border-radius:6px;padding:10px 20px;min-width:160px;text-align:center;font-size:11px;color:#999}
  .footer{text-align:center;font-size:10px;color:#aaa;margin-top:20px;padding-top:12px;border-top:1px solid #f3f4f6}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact} html,body{height:auto!important} @page{margin:1.2cm 1.5cm;size:A4}}
</style></head><body>
<div class="header">
  <div><img src="data:image/svg+xml;base64,PHN2ZyBkYXRhLWZpeD0iTE9HT19TVkdfdjEiIHdpZHRoPSIxNTUiIGhlaWdodD0iNDQiIHZpZXdCb3g9IjAgMCAxNTUgNDQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPCEtLSBiIGxldHRlciAtLT4KICA8bGluZSB4MT0iMTAiIHkxPSI0IiB4Mj0iMTAiIHkyPSI0MCIgc3Ryb2tlPSIjZTg4MzRhIiBzdHJva2Utd2lkdGg9IjMuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+CiAgPGNpcmNsZSBjeD0iMjIiIGN5PSIyOCIgcj0iMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2U4ODM0YSIgc3Ryb2tlLXdpZHRoPSIzLjUiLz4KICA8IS0tIHZlcnRpY2FsIHNlcGFyYXRvciAtLT4KICA8bGluZSB4MT0iMzgiIHkxPSI2IiB4Mj0iMzgiIHkyPSIzOCIgc3Ryb2tlPSIjNTU1IiBzdHJva2Utd2lkdGg9IjEiLz4KICA8IS0tIGJlcnRvYyBwcm9qZWN0IHRleHQgLS0+CiAgPHRleHQgeD0iNDUiIHk9IjIyIiBmb250LWZhbWlseT0iJ0RNIFNhbnMnLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSI0MDAiIGZpbGw9IiMxMTEiIGxldHRlci1zcGFjaW5nPSItMC4zIj5iZXJ0b2MgcHJvamVjdDwvdGV4dD4KICA8IS0tIHVuZGVybGluZSAtLT4KICA8bGluZSB4MT0iNDUiIHkxPSIyNiIgeDI9IjEzMCIgeTI9IjI2IiBzdHJva2U9IiMxMTEiIHN0cm9rZS13aWR0aD0iMC44Ii8+CiAgPCEtLSB0YWdsaW5lIC0tPgogIDx0ZXh0IHg9IjQ1IiB5PSIzNiIgZm9udC1mYW1pbHk9IidETSBTYW5zJyxzYW5zLXNlcmlmIiBmb250LXNpemU9IjgiIGZpbGw9IiM4ODgiIGxldHRlci1zcGFjaW5nPSIwLjMiPmlsIHR1byBwYXJ0bmVyIGVkaWxlPC90ZXh0Pgo8L3N2Zz4=" class="logo-img" style="height:88px" alt="bertoc project"/><div class="logo-sub" style="margin-top:4px">Via Vecchia Provinciale Lucchese 47C — Serravalle Pistoiese · P.IVA 01747090478</div></div>
  <div style="text-align:right"><!-- FIX_PDF_TABELLA_NUMERO_v1: rimosso Preventivo n. --><div style="font-size:11px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Data</div><div style="font-family:'Syne',sans-serif;font-size:16px;font-weight:800">${new Date().toLocaleDateString("it-IT")}</div></div>
</div>
<div class="cliente-box">
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
    <div><div style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px">Cliente</div><div style="font-size:15px;font-weight:700">${header.cliente||"—"}</div></div>
    <div><div style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px">Cantiere</div><div style="font-size:15px;font-weight:700">${header.nomeCantiere||"—"}</div></div>
    <div style="grid-column:1/-1"><div style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px">Indirizzo</div><div style="font-size:13px">${header.indirizzo||"—"}</div></div>
    <div><div style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px">Pagamento</div><div style="font-size:13px">${header.pagamento||"—"}</div></div>
    <div><div style="font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px">Validità offerta</div><div style="font-size:13px">${header.validita||30} giorni</div></div>
  </div>
</div>
<table style="table-layout:fixed;width:100%">
  <colgroup>
    <col style="width:52%">
    <col style="width:8%">
    <col style="width:8%">
    <col style="width:16%">
    <col style="width:16%">
  </colgroup>
  <thead><tr>
    <th style="text-align:left">Descrizione</th>
    <th style="text-align:center">U.M.</th>
    <th style="text-align:center">Qtà</th>
    <th style="text-align:right">Prezzo unit.</th>
    <th style="text-align:right">Totale</th>
  </tr></thead>
  <tbody>${righePDF}</tbody>
</table>
<div class="totale-box">
  <div class="totale-row"><span>Imponibile</span><span>€ ${fmtNumIT(subtotale,2)}</span></div>
  ${params.noIva ? "" : `<div class="totale-row"><span>IVA ${params.iva}%</span><span>€ ${fmtNumIT(ivaAmt,2)}</span></div>`}
  <div class="totale-finale"><span>${params.noIva ? "Imponibile" : "Totale preventivo"}</span><span>€ ${fmtNumIT(params.noIva ? subtotale : totaleFinale, 2)}</span></div>
</div>
<div style="clear:both"></div>
<div class="firma">
  <div><div style="font-size:11px;color:#999;margin-bottom:8px;font-weight:600;text-transform:uppercase;letter-spacing:1px">Firma cliente per accettazione</div><div class="sign">${header.cliente||"—"}<br><br><br>_______________________</div></div>
  <div style="text-align:right"><div style="font-size:11px;color:#999;margin-bottom:8px;font-weight:600;text-transform:uppercase;letter-spacing:1px">Per BertocProject Srl</div><div class="sign">BertocProject Srl<br><br><br>_______________________</div></div>
</div>
<div class="footer">Preventivo emesso da BertocProject Srl — P.IVA 01747090478 — Serravalle Pistoiese (PT) — Valido ${header.validita||30} giorni dalla data di emissione</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const blob = new Blob([html], { type:"text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const inviaEmail = () => {
    const oggetto = encodeURIComponent(`Preventivo BertocProject — ${header.nomeCantiere||header.cliente||"Cantiere"}`);
    const corpo = encodeURIComponent(`Gentile ${header.cliente||"Cliente"},\n\nIn allegato il preventivo per i lavori presso ${header.nomeCantiere||header.indirizzo||"cantiere"}.\n\nImporto totale: € ${totaleFinale.toFixed(2)} (IVA ${params.iva}% inclusa)\nValidità: ${header.validita||30} giorni\nPagamento: ${header.pagamento||"da concordare"}\n\nCordiali saluti,\nBertocProject Srl\nP.IVA 01747090478`);
    window.open(`mailto:${header.emailCliente||""}?subject=${oggetto}&body=${corpo}`);
  };

  /* NUM_VOCE_v1: il numero vive sulla voce (campo numVoce).
     bozza/inviato -> rinumera; accettato/rifiutato -> congela e riempie i buchi. */
  const numeraVoci = (lavs, stato, partenza) => {
    const congelato = stato === "accettato" || stato === "rifiutato";
    const base = parseInt(partenza, 10) || 0;
    const numGruppo = new Map();
    let prog = base;
    if (congelato) {
      lavs.forEach(l => { const n = parseInt(l.numVoce, 10); if (n > prog) prog = n; });
    }
    return lavs.map(l => {
      const g = (l.gruppo || "").trim();
      if (g && numGruppo.has(g)) return { ...l, numVoce: numGruppo.get(g) };
      if (congelato && l.numVoce) { if (g) numGruppo.set(g, l.numVoce); return l; }
      prog += 1;
      const num = String(prog).padStart(2, "0");
      if (g) numGruppo.set(g, num);
      return { ...l, numVoce: num };
    });
  };

  const partenzaNumeri = async (nomeCant, cliente, idCorrente) => {
    try {
      const normF = s => (s||"").toLowerCase().replace(/\s+/g,"").replace(/[^a-z0-9]/g,"");
      const snap = await getDocs(collection(db,"preventivi"));
      let max = 0;
      snap.docs.forEach(d => {
        const p = { ...d.data(), _id: d.id };
        if (p._id === idCorrente) return;
        if (p.stato !== "accettato") return;
        if (normF(p.nomeCantiere||p.header?.nomeCantiere||"") !== normF(nomeCant)) return;
        if (normF(p.cliente||p.header?.cliente||"") !== normF(cliente)) return;
        (p.lavorazioni||[]).forEach(l => { const n = parseInt(l.numVoce,10); if (n > max) max = n; });
      });
      return max;
    } catch (e) { console.warn("partenzaNumeri:", e); return 0; }
  };

  useEffect(() => { /* NUM_PARTENZA_v1 - ricalcola il numero di partenza se cambia cantiere o cliente */
    if (header.stato === "accettato" || header.stato === "rifiutato") return;
    const t = setTimeout(() => {
      partenzaNumeri(header.nomeCantiere || "", header.cliente || "", preventivoId)
        .then(b => setBaseNum(parseInt(b, 10) || 0))
        .catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [header.nomeCantiere, header.cliente, header.stato, preventivoId]);

  const apriPreventivo = (p) => {
    // Ripristina header: usa p.header se esiste, altrimenti ricostruisce dai campi root
    const hdr = p.header || {
      cliente: p.cliente||"",
      nomeCantiere: p.nomeCantiere||"",
      indirizzo: p.indirizzo||"",
      pagamento: "30gg fine mese",
      validita: 30,
      prezzoFisso: "",
      usaPrezzoFisso: false,
      dataInizio: "",
      usaDataInizio: false,
      stato: p.stato||"bozza",
    };
    // Assicura che lo stato nell'header sia sincronizzato con lo stato root
    setHeader({ ...hdr, stato: p.stato || hdr.stato || "bozza" });
    /* NUM_VOCE_v1 */
    const _st = p.stato || hdr.stato || "bozza";
    setLavorazioni(numeraVoci(p.lavorazioni || [], _st, 0));
    setBaseNum(0);   /* NUM_VOCE_v3 */
    partenzaNumeri(p.nomeCantiere||hdr.nomeCantiere||"", p.cliente||hdr.cliente||"", p._id)
      .then(base => { if (base > 0) { setBaseNum(base); setLavorazioni(cur => numeraVoci(cur, _st, base)); } });
    setParams(p.params || { costoOraio:25, speseGenerali:15, utile:30, ricarico:15, iva:0, noIva:true });
    // FIX_CRONO_SAVE_v1: ripristina cronoprogramma salvato
    const cp = p.cronoprogramma || {};
    setCronoMargine(cp.margine!=null?cp.margine:15);
    setCronoOreGiorno(cp.oreGiorno!=null?cp.oreGiorno:8);
    setCronoOperai(cp.operai||{});
    setCronoFasiEsterne(Array.isArray(cp.fasiEsterne)?cp.fasiEsterne:[]);
    setCronoOrdine(cp.ordine||null);
    setCronoNote(cp.note||{});
    setCronoSpezzate(Array.isArray(cp.spezzate)?cp.spezzate:[]);
    setCronoParallele(Array.isArray(cp.parallele)?cp.parallele:[]);
    setPreventivoId(p._id);
    setSopralluogoCollegato(p.sopralluogoId||"");
    setView("new");
  };

  const eliminaPreventivo = async (p) => {
    const cliente = p.cliente||p.header?.cliente||"questo cliente";
    const cantiere = p.nomeCantiere||p.header?.nomeCantiere||"";
    if (!window.confirm(`Eliminare il preventivo di "${cliente}"${cantiere?" — "+cantiere:""}?\n\nQuesta operazione è irreversibile.`)) return;
    try { await deleteDoc(doc(db,"preventivi",p._id)); } catch(e) { alert("❌ "+e.message); }
  };

  // ── LISTA PREVENTIVI ──────────────────────────────────────────────────────
  const [tabPrev, setTabPrev] = useState("bozza");
  const [cercaPreventivi, setCercaPreventivi] = useState(""); // FIX_CERCA_PREV_v1
  const accettati = preventiviDB.filter(p => p.stato==="accettato");
  const inviati   = preventiviDB.filter(p => p.stato==="inviato");
  const bozze     = preventiviDB.filter(p => p.stato==="bozza" || !p.stato);
  const rifiutati = preventiviDB.filter(p => p.stato==="rifiutato" || p.stato==="scaduto");
  const totAccettati = accettati.reduce((s,p) => s+parseFloat(p.totaleFinale||0), 0);
  const totInviati   = inviati.reduce((s,p) => s+parseFloat(p.totaleFinale||0), 0);
  const totBozze     = bozze.reduce((s,p) => s+parseFloat(p.totaleFinale||0), 0);

  // Sopralluogo attualmente collegato
  const sopCollegato = sopralluoghiDB.find(s => s._id === sopralluogoCollegato);

  if (view === "list") {
    const TAB_PREV = [
      { id:"bozza",     label:"📝 Bozze",     lista:bozze,     color:theme.textMuted, tot:totBozze },
      { id:"inviato",   label:"→ Inviati",    lista:inviati,   color:theme.info,      tot:totInviati },
      { id:"accettato", label:"✓ Accettati",  lista:accettati, color:theme.success,   tot:totAccettati },
      { id:"rifiutato", label:"✕ Rifiutati",  lista:rifiutati, color:theme.danger,    tot:0 },
    ];
    const prevTab = TAB_PREV.find(t => t.id === tabPrev) || TAB_PREV[0];
    const _qPrev = cercaPreventivi.toLowerCase().trim();
    const listaCorrente = prevTab.lista.filter(p => { if(!_qPrev) return true; const nome=(p.nomeCantiere||p.header?.nomeCantiere||p.nome||"").toLowerCase(); const cli=(p.cliente||p.header?.cliente||"").toLowerCase(); return nome.includes(_qPrev)||cli.includes(_qPrev); }); // FIX_CERCA_PREV_v1
    return (
    <div className="content">
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20 }}>📄 Preventivi</div>
        <button className="btn btn-primary" onClick={() => { setPreventivoId(null); setSopralluogoCollegato(""); setHeader({ cliente:"", nomeCantiere:"", indirizzo:"", telefono:"", pagamento:"30gg fine mese", validita:30, prezzoFisso:"", usaPrezzoFisso:false, dataEmissione:new Date().toISOString().slice(0,10), dataInizio:"", usaDataInizio:false, stato:"bozza" }); setLavorazioni([]); setView("new"); }}>+ Nuovo preventivo</button>
      </div>
      {/* Tab bar */}
      <div style={{ display:"flex", gap:0, marginBottom:20, background:theme.surface, borderRadius:12, padding:4, border:`1px solid ${theme.border}` }}>
        {TAB_PREV.map(t => (
          <div key={t.id} onClick={() => setTabPrev(t.id)}
            style={{ flex:1, textAlign:"center", padding:"10px 8px", borderRadius:10, cursor:"pointer",
              background: tabPrev===t.id ? theme.bg : "transparent",
              border: tabPrev===t.id ? `1px solid ${theme.border}` : "1px solid transparent",
              transition:"all 0.15s" }}>
            <div style={{ fontSize:13, fontWeight:600, color: tabPrev===t.id ? t.color : theme.textDim }}>{t.label}</div>
            <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>
              {t.lista.length} prev.{t.tot > 0 ? ` · € ${Math.round(t.tot).toLocaleString("it-IT")}` : ""}
            </div>
          </div>
        ))}
      </div>
      {/* FIX_CERCA_PREV_UI_v1 */}
      <div style={{ marginBottom:16 }}>
        <input style={{...inputStyle, maxWidth:360}} placeholder="🔍 Cerca cantiere o cliente..." value={cercaPreventivi} onChange={e=>setCercaPreventivi(e.target.value)} />
      </div>
      {/* Lista */}
      <div className="card">
        {listaCorrente.length === 0 ? (
          <div style={{ textAlign:"center", padding:"40px 0", color:theme.textMuted }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📄</div>
            <div>Nessun preventivo in questa sezione.</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Cliente</th><th>Cantiere</th><th>Importo</th><th>Data</th><th>Stato</th><th></th></tr></thead>
              <tbody>
                {listaCorrente.sort((a,b) => (b.createdAt||"").localeCompare(a.createdAt||"")).map(p => (
                  <tr key={p._id}>
                    <td style={{ fontWeight:500 }}>{p.cliente||p.header?.cliente||"—"}</td>
                    <td style={{ color:theme.textMuted }}>{p.nomeCantiere||p.header?.nomeCantiere||"—"}</td>
                    <td style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.success }}>€ {parseFloat(p.totaleFinale||0).toLocaleString("it-IT")}</td>
                    <td style={{ color:theme.textMuted }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString("it-IT") : "—"}</td>
                    <td><StatoBadge stato={p.stato||"bozza"} /></td>
                    <td style={{ display:"flex", gap:6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => apriPreventivo(p)}>Apri</button>
                      <button className="btn btn-ghost btn-sm" style={{ color:theme.danger }} onClick={() => eliminaPreventivo(p)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );}

  // ── EDITOR PREVENTIVO ─────────────────────────────────────────────────────
  return (
    <div className="content">
      {/* Topbar */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <button className="btn btn-ghost" onClick={() => setView("list")}>← Tutti i preventivi</button>
        <span style={{ color:theme.textMuted, fontSize:14 }}>/ {preventivoId ? "Modifica" : "Nuovo"} preventivo</span>
        {header.stato==="accettato" && (
          <span style={{ fontSize:11, background:"#f5a62322", color:"#f5a623", border:"1px solid #f5a62344", borderRadius:6, padding:"3px 10px", fontWeight:600 }}>
            ⚠️ Preventivo accettato — le modifiche aggiornano il documento salvato
          </span>
        )}
        <div style={{ marginLeft:"auto", display:"flex", gap:8, flexWrap:"wrap" }}>
          {["bozza","inviato","accettato","rifiutato"].map(s => (
            <button key={s} onClick={() => setHeader({...header, stato:s})}
              style={{ padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, border:"none", cursor:"pointer",
                background: header.stato===s ? (s==="accettato"?theme.success:s==="rifiutato"?theme.danger:s==="inviato"?theme.info:theme.accent) : theme.border,
                color: header.stato===s ? "#111" : theme.textMuted }}>
              {s.charAt(0).toUpperCase()+s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* ── SEZ 1: DATI + PARAMETRI ── */}
      <div className="grid-2" style={{ marginBottom:16 }}>
        <div className="card">
          <div className="card-title" style={{ marginBottom:16 }}>📋 Dati preventivo</div>

          {/* Importa da sopralluogo */}
          {sopralluoghiDB.length > 0 && (
            <div style={{ marginBottom:16, padding:"12px 14px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}` }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.accent, fontWeight:700, marginBottom:8 }}>🔗 Importa da sopralluogo</div>
              <select style={inputStyle} value={sopralluogoCollegato} onChange={e => importaSopralluogo(e.target.value)}>
                <option value="">— Seleziona sopralluogo —</option>
                {sopralluoghiDB.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.cliente||"—"} — {s.nomeCantiere||s.indirizzo||"—"} ({s.createdAt?.slice(0,10)||"—"})
                  </option>
                ))}
              </select>
              {sopCollegato && (
                <button className="btn btn-ghost btn-sm" style={{ marginTop:8 }} onClick={() => setShowMisureSop(v=>!v)}>
                  📐 {showMisureSop?"Nascondi misure":"Vedi misure sopralluogo"}
                </button>
              )}
            </div>
          )}

          {/* Misure sopralluogo collegato */}
          {sopCollegato && showMisureSop && (
            <div style={{ marginBottom:16, background:theme.bg, borderRadius:8, border:`1px solid ${theme.accent}44`, padding:"12px 14px" }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.accent, fontWeight:700, marginBottom:10 }}>📐 Misure dal sopralluogo</div>
              {(sopCollegato.lavorazioni||[]).length === 0 && <div style={{ fontSize:13, color:theme.textDim }}>Nessuna misura registrata</div>}
              {(sopCollegato.lavorazioni||[]).map(lav => {
                let mqSoff=0, mqPar=0, mlPar=0, mqPav=0, num=0, mqLato=0;
                (lav.misure||[]).forEach(m => {
                  if(m.tipo==="ambiente"){mqSoff+=m.mqSoffitto||0;mqPar+=m.mqPareti||0;mlPar+=m.mlPareti||0;mqPav+=m.mqPavimento||0;}
                  else if(m.tipo==="lato"){mqLato+=m.mqLato||0;mlPar+=m.mlLato||0;}
                  else if(m.tipo==="numerica"){num+=m.numerica||0;}
                });
                return (
                  <div key={lav.id} style={{ marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${theme.border}44` }}>
                    <div style={{ fontWeight:700, fontSize:13, color:theme.accent, marginBottom:4 }}>{lav.nome}</div>
                    {(lav.misure||[]).map(m => (
                      <div key={m.id} style={{ fontSize:12, color:theme.textMuted, padding:"2px 0" }}>
                        · <strong>{m.nome||"—"}</strong> —{" "}
                        {m.tipo==="ambiente" ? `Pav: ${(m.mqPavimento||0).toFixed(2)}mq · Soff: ${(m.mqSoffitto||0).toFixed(2)}mq · Par: ${(m.mqPareti||0).toFixed(2)}mq · ${(m.mlPareti||0).toFixed(2)}ml`
                        : m.tipo==="lato" ? `${(m.mqLato||0).toFixed(2)}mq · ${(m.mlLato||0).toFixed(2)}ml`
                        : m.tipo==="numerica" ? `N. ${m.numerica||0}`
                        : "—"}
                      </div>
                    ))}
                    <div style={{ marginTop:6, fontSize:12, background:theme.accentSoft, borderRadius:6, padding:"5px 10px", display:"flex", gap:12, flexWrap:"wrap" }}>
                      {mqSoff>0&&<span>Soffitto: <strong>{mqSoff.toFixed(2)} mq</strong></span>}
                      {mqPar>0&&<span>Pareti: <strong>{mqPar.toFixed(2)} mq</strong></span>}
                      {mlPar>0&&<span>ML pareti: <strong>{mlPar.toFixed(2)} ml</strong></span>}
                      {mqPav>0&&<span>Pavimento: <strong>{mqPav.toFixed(2)} mq</strong></span>}
                      {mqLato>0&&<span>Lato: <strong>{mqLato.toFixed(2)} mq</strong></span>}
                      {num>0&&<span>N.: <strong>{num}</strong></span>}
                    </div>
                  </div>
                );
              })}
              {/* Foto sopralluogo */}
              {(sopCollegato.foto||[]).length > 0 && (
                <div style={{ marginTop:10 }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.accent, fontWeight:700, marginBottom:8 }}>📷 Foto sopralluogo</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {(sopCollegato.foto||[]).map((f,i) => (
                      <img key={i} src={f.base64||f.url||""} alt={f.nome||""} onClick={()=>{ const u=f.base64||f.url; if(!u)return; const o=document.createElement('div'); o.style.cssText='position:fixed;inset:0;background:rgba(0,0,0,0.92);display:flex;align-items:center;justify-content:center;z-index:99999;cursor:zoom-out;padding:20px'; const im=document.createElement('img'); im.src=u; im.style.cssText='max-width:95%;max-height:95%;object-fit:contain;border-radius:8px'; o.appendChild(im); o.onclick=function(){o.remove();}; document.body.appendChild(o); }} /* FIX_FOTO_SOPR_LIGHTBOX_v1 */
                        style={{ width:80, height:80, objectFit:"cover", borderRadius:8, cursor:"pointer", border:`1px solid ${theme.border}` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="form-grid">
            <div className="form-group" style={{ position:"relative" }}>
              <label>Cliente *</label>
              <input style={inputStyle} placeholder="Nome cliente" value={header.cliente}
                onChange={e => { setHeader({...header,cliente:e.target.value}); setShowClientiSuggeriti(true); setShowCantieriCliente(false); }}
                onFocus={() => setShowClientiSuggeriti(true)}
                onBlur={() => setTimeout(()=>setShowClientiSuggeriti(false), 200)}
              />
              {showClientiSuggeriti && (() => {
                // Raccoglie clienti unici da cantieri e preventivi
                const daAnag = (clientiDB||[]).filter(c => c.attivo !== false)
                  .map(c => ({ id:c._id, nome:c.nome, telefono:c.telefono||"" }));
                const nomiAnag = new Set(daAnag.map(c => String(c.nome||"").toLowerCase()));
                const vecchi = [...new Set([
                  ...cantieriEsistenti.map(c=>c.cliente),
                  ...preventiviDB.map(p=>p.cliente||p.header?.cliente)
                ].filter(Boolean))].filter(n => !nomiAnag.has(String(n).toLowerCase()))
                  .map(n => ({ id:null, nome:n, telefono:"" }));
                const qCli = String(header.cliente||"").toLowerCase();
                const filtrati = [...daAnag, ...vecchi]
                  .filter(c => String(c.nome||"").toLowerCase().includes(qCli) && c.nome !== header.cliente)
                  .slice(0, 40);
                if (!filtrati.length) return null;
                return (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:100, background:"#151929", border:"1px solid #2a2e45", borderRadius:8, boxShadow:"0 8px 24px #00000066", maxHeight:200, overflowY:"auto" }}>
                    {filtrati.map(c => (
                      <div key={(c.id||"vecchio")+"-"+c.nome} onMouseDown={() => {
                        setHeader({...header, cliente:c.nome, clienteId:c.id||"", telefono: header.telefono || c.telefono || ""});
                        setShowClientiSuggeriti(false);
                        setShowCantieriCliente(true);
                      }} style={{ padding:"10px 14px", cursor:"pointer", fontSize:13, borderBottom:"1px solid #2a2e4533" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#1e2438"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        {c.id ? "👤" : "✎"} {c.nome}
                        {c.telefono ? <span style={{ color:"#7b82a0", marginLeft:8 }}>{c.telefono}</span> : null}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div className="form-group"><label>Telefono</label><input style={inputStyle} placeholder="+39 333 000 0000" value={header.telefono||""} onChange={e => setHeader({...header,telefono:e.target.value})} /></div>
            <div className="form-group" style={{ position:"relative" }}>
              <label>Nome cantiere</label>
              <input style={inputStyle} placeholder="es. Ristrutturazione bagno" value={header.nomeCantiere}
                onChange={e => setHeader({...header,nomeCantiere:e.target.value})}
                onFocus={() => setShowCantieriCliente(true)}
                onBlur={() => setTimeout(()=>setShowCantieriCliente(false), 200)}
              />
              {showCantieriCliente && header.cliente && (() => {
                const cantieriCliente = cantieriEsistenti.filter(c =>
                  (c.cliente||"").toLowerCase() === header.cliente.toLowerCase() && !c.completato
                );
                if (!cantieriCliente.length) return null;
                return (
                  <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:100, background:"#151929", border:"1px solid #f5a62344", borderRadius:8, boxShadow:"0 8px 24px #00000066", maxHeight:200, overflowY:"auto" }}>
                    <div style={{ padding:"6px 14px", fontSize:10, color:"#f5a623", textTransform:"uppercase", letterSpacing:1, fontWeight:700, borderBottom:"1px solid #2a2e4533" }}>
                      🏗️ Cantieri aperti di {header.cliente}
                    </div>
                    {cantieriCliente.map(c => (
                      <div key={c._id||c.id} onMouseDown={() => {
                        setHeader({...header,
                          nomeCantiere: c.nome,
                          indirizzo: c.indirizzo||header.indirizzo,
                          citta: c.citta||header.citta
                        });
                        setShowCantieriCliente(false);
                      }} style={{ padding:"10px 14px", cursor:"pointer", fontSize:13, borderBottom:"1px solid #2a2e4533" }}
                        onMouseEnter={e=>e.currentTarget.style.background="#1e2438"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div style={{ fontWeight:600 }}>🏗️ {c.nome}</div>
                        <div style={{ fontSize:11, color:"#7b82a0", marginTop:2 }}>{c.indirizzo||"—"} · {c.citta||"—"}</div>
                      </div>
                    ))}
                    <div style={{ padding:"8px 14px", fontSize:11, color:"#7b82a0", borderTop:"1px solid #2a2e4533", fontStyle:"italic" }}>
                      Seleziona per aggiungere lavorazioni al cantiere esistente
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="form-group"><label>Indirizzo cantiere</label><input style={inputStyle} placeholder="Via Roma 12" value={header.indirizzo} onChange={e => setHeader({...header,indirizzo:e.target.value})} /></div>
            <div className="form-group"><label>Città</label><input style={inputStyle} placeholder="es. Serravalle Pistoiese" value={header.citta||""} onChange={e => setHeader({...header,citta:e.target.value})} /></div>
            <div className="form-group">
              <label>Tipo pagamento</label>
              <input style={inputStyle}
                list="pagamento-options"
                value={header.pagamento}
                onChange={e => setHeader({...header,pagamento:e.target.value})}
                placeholder="es. 30gg fine mese, bonifico anticipato..."
              />
              <datalist id="pagamento-options">
                <option value="30gg fine mese" />
                <option value="Bonifico anticipato" />
                <option value="50% anticipato - 50% fine lavori" />
                <option value="SAL (stati avanzamento lavori)" />
                <option value="Fine lavori" />
                <option value="Contanti alla consegna" />
                <option value="60gg fine mese" />
                <option value="Riba 30gg" />
              </datalist>
            </div>
            <div className="form-group"><label>Validità (giorni)</label><input style={inputStyle} type="number" value={header.validita} onChange={e => setHeader({...header,validita:e.target.value})} /></div>
            <div className="form-group">
              <label>% IVA</label>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <input style={{...inputStyle,width:90}} type="number" min="0" max="99" value={params.iva} onChange={e => setParams({...params,iva:Number(e.target.value)})} />
                <span style={{ fontSize:13, color:theme.textMuted }}>%</span>
              </div>
            </div>
            <div className="form-group full">
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8 }}>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>📅 Data emissione</div>
                  <input style={inputStyle} type="date" value={header.dataEmissione||new Date().toISOString().slice(0,10)} onChange={e => setHeader({...header,dataEmissione:e.target.value})} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>🏗️ Inizio lavori</div>
                  <input style={inputStyle} type="date" value={header.dataInizioLavori||""} onChange={e => setHeader({...header,dataInizioLavori:e.target.value})} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>🏁 Fine lavori</div>
                  <input style={inputStyle} type="date" value={header.dataFineLavori||""} onChange={e => setHeader({...header,dataFineLavori:e.target.value})} />
                </div>
              </div>
            </div>
            <div className="form-group full">
              <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
                <input type="checkbox" checked={header.usaPrezzoFisso} onChange={e => setHeader({...header,usaPrezzoFisso:e.target.checked})} /> Inserisci imponibile fisso
              </label>
              {header.usaPrezzoFisso && <input style={{...inputStyle,marginTop:8}} type="number" min="0" step="0.01" placeholder="0,00" value={header.prezzoFisso} onChange={e => setHeader({...header,prezzoFisso:e.target.value})} />}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title" style={{ marginBottom:4 }}>⚙️ Parametri di calcolo</div>
          <div style={{ fontSize:12, color:theme.textMuted, marginBottom:16 }}>Si applicano a tutte le lavorazioni di questo preventivo.</div>
          {[["Costo operaio (€/ora)","costoOraio","€/h"],["Spese generali (%)","speseGenerali","%"],["Utile desiderato (%)","utile","%"],["Ricarico materiali (%)","ricarico","%"]].map(([label,key,unit]) => (
            <div key={key} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${theme.border}44` }}>
              <div style={{ fontSize:13, fontWeight:500 }}>{label}</div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <input type="number" value={params[key]} onChange={e => setParams({...params,[key]:Number(e.target.value)})} style={{...inputStyle,width:80,textAlign:"right",padding:"6px 10px"}} />
                <span style={{ fontSize:12, color:theme.textMuted, width:24 }}>{unit}</span>
              </div>
            </div>
          ))}
          <div style={{ marginTop:16, background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}` }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:10 }}>Simulazione su 1 ora di lavoro (senza materiali)</div>
            {(() => {
              const man = params.costoOraio;
              const costoBase = man*(1+params.speseGenerali/100); // solo manodopera
              const prezzo = costoBase/(1-params.utile/100);
              return (
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {[["Costo tot.",`€ ${costoBase.toFixed(0)}`],["Prezzo offerta",`€ ${prezzo.toFixed(0)}`],["Utile netto",`€ ${(prezzo-costoBase).toFixed(0)}`],["Margine",`${((prezzo-costoBase)/prezzo*100).toFixed(1)}%`]].map(([k,v]) => (
                    <div key={k} style={{ background:theme.surface, borderRadius:6, padding:"6px 10px" }}>
                      <div style={{ fontSize:10, color:theme.textDim }}>{k}</div>
                      <div style={{ fontSize:14, fontWeight:700, fontFamily:"'Syne',sans-serif", color:theme.accent }}>{v}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── SEZ 2: LAVORAZIONI ── */}
      <div className="card">
        <div className="card-header" style={{ marginBottom:0 }}>
          <div className="card-title">🔨 Lavorazioni</div>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowGestListino(!showGestListino); setShowListino(false); setShowPdfImport(false); }}>⚙️ Gestisci listino</button>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowListino(!showListino); setShowGestListino(false); setShowPdfImport(false); }}>📋 Da listino</button>
            <button className="btn btn-ghost btn-sm" style={{color:"#a78bfa",borderColor:"#a78bfa44"}} onClick={() => { setShowPdfImport(!showPdfImport); setShowListino(false); setShowGestListino(false); }}>🤖 Importa capitolato PDF</button>
          </div>
        </div>
        {pdfImportResult && (
          <div style={{ margin:"10px 0 0", padding:"8px 14px", background:"#0a2218", border:"1px solid #3ecf8e44", borderRadius:8, fontSize:13, color:"#3ecf8e", fontWeight:600 }}>
            {pdfImportResult}
          </div>
        )}

        {/* Importa capitolato PDF con AI */}
        {showPdfImport && (
          <div style={{ marginTop:14, background:"#0d0a1f", border:"1px solid #a78bfa44", borderRadius:10, padding:16 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, color:"#a78bfa", marginBottom:6 }}>🤖 Importa capitolato PDF</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginBottom:14 }}>
              Carica il PDF del capitolato — Claude leggerà le voci e le importerà nel preventivo. Poi aggiungi categoria, prezzi e minuti per completare.
            </div>
            <div style={{ padding:"10px 14px", background:"#0a2218", borderRadius:8, border:"1px solid #3ecf8e33", marginBottom:14, fontSize:12, color:"#3ecf8e" }}>
              ✅ NAS Synology attivo — funzione abilitata
            </div>
            <input type="file" accept=".pdf"
              style={{ display:"none" }}
              id="pdf-capitolato-input"
              onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                setPdfImportLoading(true);
                setPdfImportResult("");
                try {
                  const base64 = await new Promise((res,rej) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result.split(",")[1]);
                    r.onerror = () => rej(new Error("Lettura fallita"));
                    r.readAsDataURL(file);
                  });
                  const resp = await fetch(`${NAS_URL}/api/analizza-pdf`, {
                    method:"POST",
                    headers:{ "Content-Type":"application/json" },
                    body: JSON.stringify({ pdfBase64: base64 })
                  });
                  const data = await resp.json();
                  if (data.error) throw new Error(data.error);
                  const testo = JSON.stringify(data.voci);
                  let voci;
                  try {
                    voci = data.voci;
                  } catch(e) { throw e; }
                  if (!Array.isArray(voci) || voci.length===0) throw new Error("Nessuna voce trovata");
                  // Importa le voci come lavorazioni
                  const nuove = voci.map(v => ({
                    id: Date.now()+Math.random(),
                    desc: v.desc||"",
                    descBreve: v.descBreve||"",
                    um: v.um||"cad",
                    qta: String(parseFloat(v.qta)||1),
                    costoMat:"0", minuti:"30", prezzoMercato:"0",
                    spesaEsterna:"0", ricaricoSpesa:"20",
                    categoria:"", sottocategoria:"",
                    qtaMat:"", umMat:"mq", smaltimento:false, gruppo:"", modalitaGruppo:"corpo",
                    prodottoListino:"", prodottoNome:""
                  }));
                  setLavorazioni(prev => [...prev, ...nuove]);
                  setPdfImportResult(`✅ Importate ${nuove.length} voci dal capitolato! Aggiungi categoria e prezzi per completare.`);
                  setShowPdfImport(false);
                } catch(err) {
                  setPdfImportResult("❌ Errore: "+err.message);
                } finally {
                  setPdfImportLoading(false);
                  e.target.value = "";
                }
              }}
            />
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <label htmlFor="pdf-capitolato-input" className="btn btn-primary" style={{background:"#a78bfa",color:"#fff",cursor:"pointer"}}>
                📄 Seleziona PDF capitolato
              </label>
              {pdfImportLoading && <div style={{ fontSize:12, color:"#a78bfa" }}>Claude sta leggendo il PDF...</div>}
            </div>
          </div>
        )}

        {/* Gestione listino */}
        {showGestListino && (
          <div style={{ marginTop:14, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"12px 14px", borderBottom:`1px solid ${theme.border}`, fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:13 }}>⚙️ Gestione listino voci</div>
            <div style={{ padding:"12px 14px", borderBottom:`1px solid ${theme.border}` }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 80px 80px 1fr 80px", gap:8, marginBottom:8 }}>
                {[["Descrizione","desc","Descrizione voce"],["U.M.","um","mq"],["Mat €/um","costoMat","0"],["Min/u","minuti","30"],["Parola chiave","parolaChiave","es. imbiancatura"]].map(([label,key,ph]) => (
                  <div key={key}>
                    <div style={{ fontSize:10, color:theme.textDim, marginBottom:3 }}>{label}</div>
                    <input style={inputStyle} placeholder={ph} value={listinoForm[key]||""} onChange={e => setListinoForm({...listinoForm,[key]:e.target.value})} />
                  </div>
                ))}
                <div style={{ display:"flex", alignItems:"flex-end" }}>
                  <button className="btn btn-primary btn-sm" onClick={aggiungiVoceListino} style={{ width:"100%" }}>
                    {editListinoId ? "✓ Salva" : "+ Aggiungi"}
                  </button>
                </div>
              </div>
              <div onClick={()=>setListinoForm({...listinoForm,smaltimento:!listinoForm.smaltimento})}
                style={{ display:"inline-flex", alignItems:"center", gap:8, cursor:"pointer", padding:"5px 10px", borderRadius:8,
                  border:`1px solid ${listinoForm.smaltimento?"#f5a62366":theme.border}`,
                  background:listinoForm.smaltimento?"#f5a62311":theme.bg }}>
                <div style={{ width:14, height:14, borderRadius:3, border:`2px solid ${listinoForm.smaltimento?theme.accent:theme.textDim}`,
                  background:listinoForm.smaltimento?theme.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center" }}>
                  {listinoForm.smaltimento && <span style={{fontSize:10,color:"#111",fontWeight:900}}>✓</span>}
                </div>
                <span style={{ fontSize:11, color:listinoForm.smaltimento?theme.accent:theme.textMuted, fontWeight:listinoForm.smaltimento?700:400 }}>
                  🗑️ Smaltimento / non approvvigionabile
                </span>
              </div>
            </div>
            <div style={{ maxHeight:280, overflowY:"auto" }}>
              {listino.map(v => (
                <div key={v.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", borderBottom:`1px solid ${theme.border}33` }}>
                  <div style={{ flex:1, fontSize:13 }}>{v.desc}{v.smaltimento && <span style={{marginLeft:6,fontSize:10,background:"#f5a62322",color:theme.accent,border:`1px solid ${theme.accent}44`,borderRadius:4,padding:"1px 5px"}}>🗑️</span>}</div>
                  <div style={{ fontSize:11, color:theme.textDim, minWidth:120 }}>{v.um} · {v.minuti}min · mat €{v.costoMat}</div>
                  <button onClick={() => { setListinoForm({ desc:v.desc, um:v.um, costoMat:String(v.costoMat), minuti:String(v.minuti), parolaChiave:v.parolaChiave||"", smaltimento:v.smaltimento||false }); setEditListinoId(v.id); }}
                    style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:14 }}>✎</button>
                  <button onClick={() => { if(window.confirm(`Eliminare "${v.desc}" dal listino lavorazioni?`)) eliminaVoceListino(v.id); }}
                    style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14 }}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Listino selezione rapida */}
        {showListino && (
          <div style={{ marginTop:12, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:10, overflow:"hidden" }}>
            <div style={{ padding:"10px 12px", borderBottom:`1px solid ${theme.border}` }}>
              <input style={{...inputStyle,padding:"7px 12px"}} placeholder="🔍 Cerca nel listino..." value={searchListino} onChange={e => setSearchListino(e.target.value)} />
            </div>
            <div style={{ maxHeight:240, overflowY:"auto" }}>
            {pacchetti.length > 0 && (
              <div style={{ borderBottom:`1px solid ${theme.border}` }}>{/* PACCHETTI_v1 */}
                <div style={{ padding:"6px 14px", fontSize:10, textTransform:"uppercase", letterSpacing:1, color:theme.accent, fontWeight:700 }}>📦 Pacchetti di voci</div>
                {pacchetti.filter(p => !searchListino || String(p.nome||"").toLowerCase().includes(searchListino.toLowerCase())).map(p => (
                  <div key={p.id} onClick={() => aggiungiPacchetto(p)}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderBottom:`1px solid ${theme.border}33`, cursor:"pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background=theme.surface}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:theme.accent }}>📦 {p.nome}</div>
                      <div style={{ fontSize:11, color:theme.textDim, marginTop:2, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                        {(p.voci||[]).length} lavorazioni — {(p.voci||[]).map(v=>v.desc).join(" · ")}
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); if (window.confirm('Eliminare il pacchetto "'+p.nome+'" dal listino?')) { const n = pacchetti.filter(x => x.id !== p.id); setPacchetti(n); salvaPacchettiFirebase(n); } }}
                      style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14, flexShrink:0 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
              {listino.filter(v => !searchListino || v.desc.toLowerCase().includes(searchListino.toLowerCase()) || (v.parolaChiave||"").includes(searchListino.toLowerCase())).map(v => {
                const c = calcolaLavorazione({...v,qta:1});
                const nMat = Object.keys(v.righeMatCalcolo||{}).length;
                return (
                  <div key={v.id} onClick={() => aggiungiDaListino(v)}
                    style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", borderBottom:`1px solid ${theme.border}33`, cursor:"pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background=theme.surface}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500 }}>{v.desc}</div>
                      <div style={{ fontSize:11, color:theme.textDim, marginTop:2, display:"flex", gap:8, flexWrap:"wrap" }}>
                        <span>{v.um} · {v.minuti} min/u · mat €{v.costoMat}/{v.um}</span>
                        {v.categoria && <span style={{ color:theme.accent }}>📂 {dizionarioMateriali[v.categoria]?.label||v.categoria}</span>}
                        {nMat > 0 && <span style={{ color:theme.success }}>🧱 {nMat} materiali → prezzi aggiornati al richiamo</span>}
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0 }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.accent }}>€ {fmtEur(c.prezzoUn)}/{v.um}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Form nuova lavorazione manuale */}
        <div style={{ marginTop:14, background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}` }}>
          <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:10 }}>
            {editingLavId ? "✎ Modifica lavorazione" : "+ Nuova lavorazione manuale"}
          </div>
          {/* Categoria e sottocategoria */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
            <div>
              <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Categoria (per statistiche)</div>
              <select style={inputStyle} value={lavForm.categoria||""} onChange={e=>setLavForm({...lavForm,categoria:e.target.value,sottocategoria:""})}>
                <option value="">— Nessuna categoria —</option>
                {Object.entries(dizionarioMateriali).sort(([ka,va],[kb,vb])=>(va.label||ka).localeCompare(vb.label||kb)).map(([k,v])=>(
                  <option key={k} value={k}>{v.label||k.charAt(0).toUpperCase()+k.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Sottocategoria</div>
              <select style={inputStyle} value={lavForm.sottocategoria||""} onChange={e=>setLavForm({...lavForm,sottocategoria:e.target.value})}
                disabled={!lavForm.categoria||!Object.keys(dizionarioMateriali[lavForm.categoria]?.sottocategorie||{}).length}>
                <option value="">— Nessuna —</option>
                {lavForm.categoria && Object.entries(dizionarioMateriali[lavForm.categoria]?.sottocategorie||{}).sort(([ka,va],[kb,vb])=>(va.label||ka).localeCompare(vb.label||kb)).map(([k,v])=>(
                  <option key={k} value={k}>{v.label||k}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Bottone incidenze AI */}
          {lavForm.categoria && (
            <div style={{ marginBottom:8, display:"flex", justifyContent:"flex-end" }}>
              <button
                onClick={calcolaIncidenzaAI}
                disabled={aiIncidenze}
                style={{ fontSize:11, padding:"5px 12px", borderRadius:8, border:"1px solid #3ecf8e44",
                  background:"#3ecf8e11", color:"#3ecf8e", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                {aiIncidenze ? "⏳ Calcolo AI..." : "🧮 Calcola incidenze AI"}
              </button>
            </div>
          )}

          {/* Form campi lavorazione */}
          {/* Riga 1: Descrizione grande */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span>Descrizione</span>
              <button
                onClick={() => suggerisciCategoriaAI(lavForm.desc)}
                disabled={aiSuggerendo || !lavForm.desc || lavForm.desc.length < 10}
                style={{ fontSize:10, padding:"3px 8px", borderRadius:6, border:"1px solid #f5a62344",
                  background:"#f5a62311", color:"#f5a623", cursor:"pointer", fontFamily:"'DM Sans',sans-serif" }}>
                {aiSuggerendo ? "⏳ AI..." : "🤖 Suggerisci categoria"}
              </button>
            </div>
            <textarea style={{...inputStyle, height:60, resize:"vertical", lineHeight:1.4}}
              placeholder="es. Tinteggiatura parete soggiorno con due mani di pittura lavabile compreso fissativo..."
              value={lavForm.desc||""}
              onChange={e=>setLavForm({...lavForm,desc:e.target.value})} />
          </div>
          {/* Descrizione sintetica per operai */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:"#4e9af5", marginBottom:4, display:"flex", alignItems:"center", gap:6 }}>
              👷 Descrizione sintetica (vista operai)
              <span style={{ fontSize:10, color:theme.textDim }}>— se vuota usa la descrizione completa</span>
            </div>
            <input style={{...inputStyle, borderColor:"#4e9af544"}}
              placeholder="es. Tinteggiatura interna camere"
              value={lavForm.descBreve||""}
              onChange={e=>setLavForm({...lavForm,descBreve:e.target.value})} />
          </div>

          {/* Flag smaltimento */}
          <div style={{ marginBottom:8 }}>
            <div onClick={()=>setLavForm({...lavForm,smaltimento:!lavForm.smaltimento})}
              style={{ display:"inline-flex", alignItems:"center", gap:8, cursor:"pointer", padding:"6px 12px", borderRadius:8,
                border:`1px solid ${lavForm.smaltimento?"#f5a62366":theme.border}`,
                background:lavForm.smaltimento?"#f5a62311":theme.bg }}>
              <div style={{ width:16, height:16, borderRadius:4, border:`2px solid ${lavForm.smaltimento?theme.accent:theme.textDim}`,
                background:lavForm.smaltimento?theme.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center" }}>
                {lavForm.smaltimento && <span style={{fontSize:11,color:"#111",fontWeight:900}}>✓</span>}
              </div>
              <span style={{ fontSize:12, color:lavForm.smaltimento?theme.accent:theme.textMuted, fontWeight:lavForm.smaltimento?700:400 }}>
                🗑️ Smaltimento / non approvvigionabile — il costo è incluso nel preventivo ma non genera ordini materiali
              </span>
            </div>
          </div>

          {/* Gruppo lavorazioni */}
          <div style={{ marginBottom:8 }}>
            <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>📦 Raggruppa in voce unica (opzionale)</div>
            <div style={{ display:"flex", gap:8 }}>
              <input style={{...inputStyle, flex:1}} placeholder="es. Rifacimento bagno completo — lascia vuoto per voce singola"
                value={lavForm.gruppo||""}
                onChange={e=>setLavForm({...lavForm,gruppo:e.target.value})}
                list="gruppi-esistenti"
              />
              <datalist id="gruppi-esistenti">
                {[...new Set(lavorazioni.filter(l=>l.gruppo).map(l=>l.gruppo))].map(g=>(
                  <option key={g} value={g}/>
                ))}
              </datalist>
              {lavForm.gruppo && (
                <button onClick={()=>setLavForm({...lavForm,gruppo:""})}
                  style={{ background:"none", border:`1px solid ${theme.border}`, borderRadius:8, color:theme.textDim, cursor:"pointer", padding:"0 10px", fontSize:13 }}>✕</button>
              )}
            </div>
            {lavForm.gruppo && (
              <div style={{ fontSize:11, color:theme.textDim, marginTop:4 }}>
                💡 Nel PDF il cliente vedrà solo "<strong style={{color:theme.accent}}>{lavForm.gruppo}</strong>" con il totale del gruppo
              </div>
            )}
          </div>
          {/* Pannello materiali interattivo */}
          {lavForm.categoria && (() => {
            const subDati = dizionarioMateriali[lavForm.categoria]?.sottocategorie?.[lavForm.sottocategoria];
            const catDati = dizionarioMateriali[lavForm.categoria];
            const materialiDiz = (subDati?.materiali?.length ? subDati.materiali : catDati?.materiali||[])
              .map(m => typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m)
              .filter(m => m.nome && parseFloat(m.incidenza) > 0);
            if (!materialiDiz.length) return null;
            // Per ogni materiale del dizionario trova i prodotti disponibili nel listino
            const righeCalcolo = lavForm.righeMatCalcolo || {};
            let costoTotCalcolato = 0;
            const righeUI = materialiDiz.map(m => {
              const catCerca = (m.categoriaP||"").trim() || lavForm.categoria;
              const prodDisp = listinoMatDB.filter(l=>(l.categoriaP||"").toLowerCase()===catCerca.toLowerCase() && l.prezzoUltimo>0)
                .sort((a,b)=>(b.ultimaData||"").localeCompare(a.ultimaData||""));
              const selId = righeCalcolo[m.nome]?.prodottoId || "";
              const selProd = selId ? (prodDisp.find(p=>p._id===selId) || null) : null;
              // FIX_PREVENTIVO_PREZZO_BASE_v1: usa prezzoUltimoBase (€/kg) se disponibile, fallback prezzoUltimo
              const prezzo = (selProd?.umBase && selProd?.prezzoUltimoBase > 0)
                ? selProd.prezzoUltimoBase
                : (selProd?.prezzoUltimo || 0);
              const incidenza = parseFloat(m.incidenza)||0;
              const qtaPerUm = lavForm.um==="cad" && lavForm.qtaMat ? parseFloat(lavForm.qtaMat)||1 : parseFloat(lavForm.qta)||1;
              const costoRiga = incidenza * prezzo;
              costoTotCalcolato += costoRiga;
              return { m, prodDisp, selId: selProd?._id||"", selProd, prezzo, incidenza, costoRiga };
            });
            return (
              <div style={{ marginBottom:10, background:"#0a1520", borderRadius:10, border:`1px solid #4e9af544`, overflow:"hidden" }}>
                <div style={{ padding:"10px 14px", borderBottom:`1px solid #4e9af522`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div style={{ fontSize:11, color:"#4e9af5", textTransform:"uppercase", letterSpacing:1, fontWeight:600 }}>
                    🧱 Materiali — scegli i prodotti dal listino
                  </div>
                  {costoTotCalcolato > 0 && (
                    <button className="btn btn-primary btn-sm" style={{fontSize:11}}
                      onClick={()=>setLavForm({...lavForm, costoMat: costoTotCalcolato.toFixed(2)})}>
                      ✓ Usa € {costoTotCalcolato.toFixed(2)}/um come costo mat.
                    </button>
                  )}
                </div>
                {righeUI.map(({m, prodDisp, selId, selProd, prezzo, incidenza, costoRiga}) => (
                  <div key={m.nome} style={{ display:"grid", gridTemplateColumns:"140px 1fr 80px 90px", gap:8, alignItems:"center", padding:"8px 14px", borderBottom:`1px solid #4e9af511` }}>
                    <div style={{ fontSize:12, fontWeight:600, color:theme.text }}>{m.nome}</div>
                    <select style={{...inputStyle, padding:"5px 8px", fontSize:12}}
                      value={selId}
                      onChange={e=>{
                        const nuoveRighe = {...(lavForm.righeMatCalcolo||{}), [m.nome]:{ prodottoId: e.target.value }};
                        setLavForm({...lavForm, righeMatCalcolo: nuoveRighe});
                      }}>
                      <option value="">— Nessun prodotto —</option>
                      {/* FIX_PREVENTIVO_PREZZO_BASE_v1: mostra €/UM base se disponibile */}
                      {prodDisp.map(p=>{
                        const usaBase = p.umBase && p.prezzoUltimoBase > 0;
                        const prezzoMostra = usaBase ? p.prezzoUltimoBase : p.prezzoUltimo;
                        const umMostra = usaBase ? p.umBase : p.um;
                        return <option key={p._id} value={p._id}>{p.prodotto} — €{parseFloat(prezzoMostra).toFixed(prezzoMostra<1?3:2)}/{umMostra}</option>;
                      })}
                    </select>
                    <div style={{ fontSize:11, color:theme.textDim, textAlign:"center" }}>
                      {incidenza} {m.um}/um
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:costoRiga>0?theme.success:theme.textDim, textAlign:"right" }}>
                      {costoRiga>0 ? `€ ${costoRiga.toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:2})}/um` : "—"}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          <div style={{ display:"grid", gridTemplateColumns:"70px 70px 90px 80px 90px 90px 70px", gap:8, marginBottom:8 }}>
            {[["U.M.","um",null],["Quantità","qta","1"],["Mat. €/um","costoMat","0"],["Min/u","minuti","30"],["Prezzo merc.","prezzoMercato","0"],["Sp.est. €/um","spesaEsterna","0"],["Ric. %","ricaricoSpesa","20"]].map(([label,key,ph]) => (
              <div key={key}>
                <div style={{ fontSize:10, color:theme.textDim, marginBottom:3 }}>{label}</div>
                {key==="um" ? (
                  <select style={{...inputStyle, padding:"6px 6px", fontSize:12}} value={lavForm.um}
                    onChange={e=>setLavForm({...lavForm, um:e.target.value, qtaMat:"", umMat:"mq"})}>
                    {["mq","ml","cad","mc","h","kg"].map(u=><option key={u}>{u}</option>)}
                  </select>
                ) : (
                  <input style={{...inputStyle, padding:"6px 8px", fontSize:12}} placeholder={ph}
                    inputMode="decimal" value={lavForm[key]||""}
                    onChange={e=>setLavForm({...lavForm,[key]:e.target.value})} />
                )}
              </div>
            ))}
          </div>

          {/* PREZZO_OBIETTIVO_v1: scrivi il prezzo e calcola i minuti */}
          <div style={{ display:"flex", gap:8, alignItems:"flex-end", marginTop:8, flexWrap:"wrap" }}>
            <div style={{ minWidth:150 }}>
              <div style={{ fontSize:10, color:theme.textDim, marginBottom:3 }}>🎯 Prezzo obiettivo €/um</div>
              <input style={{...inputStyle, padding:"6px 8px", fontSize:12}} placeholder="es. 25"
                inputMode="decimal" value={prezzoObiettivo}
                onChange={e=>setPrezzoObiettivo(e.target.value)} />
            </div>
            <button className="btn btn-ghost btn-sm" style={{color:"#f5a623", borderColor:"#f5a62344"}}
              onClick={()=>{
                const target = parseNum(prezzoObiettivo);
                if (target <= 0) { alert("Scrivi prima il prezzo obiettivo (es. 25)."); return; }
                const c = calcolaLavorazione({ ...lavForm, minuti:"0" });
                const restante = target - c.costoMatConRicarico - (parseNum(lavForm.spesaEsterna||0) * (1 + parseNum(lavForm.ricaricoSpesa||20)/100));
                if (restante <= 0) {
                  alert("Con €"+target+" non si copre nemmeno il materiale e la spesa esterna (€"+
                    (Math.round((target-restante)*100)/100)+"). Alza il prezzo o abbassa i costi.");
                  return;
                }
                const costoManConSpese = restante * (1 - params.utile/100);
                const costoManUn = costoManConSpese / (1 + params.speseGenerali/100);
                const minuti = (costoManUn / params.costoOraio) * 60;
                if (!isFinite(minuti) || minuti <= 0) { alert("Calcolo non riuscito: controlla costo orario e parametri."); return; }
                const min2 = Math.round(minuti * 100) / 100;
                setLavForm({ ...lavForm, minuti: String(min2) });
                setPrezzoObiettivo("");
              }}>← Calcola i minuti</button>
            <div style={{ fontSize:11, color:theme.textDim, paddingBottom:6 }}>
              Scrive nel campo "Min/u" i minuti che servono per arrivare a quel prezzo.
            </div>
          </div>

          {/* Riga 3: misura reale — solo se U.M. = cad */}
          {lavForm.um === "cad" && (
            <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8, padding:"6px 10px", background:theme.bg, borderRadius:8, border:`1px solid #3ecf8e44` }}>
              <div style={{ fontSize:11, color:"#3ecf8e", whiteSpace:"nowrap" }}>📐 Misura reale:</div>
              <input style={{...inputStyle, width:80, padding:"5px 8px", fontSize:12}} placeholder="es. 12"
                inputMode="decimal" value={lavForm.qtaMat||""}
                onChange={e=>setLavForm({...lavForm,qtaMat:e.target.value})} />
              <select style={{...inputStyle, width:70, padding:"5px 6px", fontSize:12}} value={lavForm.umMat||"mq"}
                onChange={e=>setLavForm({...lavForm,umMat:e.target.value})}>
                {["mq","ml","mc","kg","lt"].map(u=><option key={u}>{u}</option>)}
              </select>
              <div style={{ fontSize:10, color:theme.textDim }}>usata per calcolo materiali</div>
            </div>
          )}
          {lavForm.desc && (() => {
            const c = calcolaLavorazione(lavForm);
            const pmNum = parseNum(lavForm.prezzoMercato);
            return (
              <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:10 }}>
                {[["Prezzo/um",`€ ${fmtEur(c.prezzoUn)}`],["Totale",`€ ${fmtEur(c.totale,0)}`],["Ore tot.",`${(c.oreUn*c.qta).toFixed(1)}h`],pmNum>0?["vs mercato",`${c.scarto>0?"+":""}${c.scarto?.toFixed(1)}%`]:null].filter(Boolean).map(([k,v]) => (
                  <div key={k} style={{ background:theme.surface, border:`1px solid ${theme.accent}44`, borderRadius:7, padding:"5px 12px" }}>
                    <div style={{ fontSize:10, color:theme.textDim }}>{k}</div>
                    <div style={{ fontSize:14, fontWeight:700, fontFamily:"'Syne',sans-serif", color:theme.accent }}>{v}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn btn-primary btn-sm" id="btnAggiungiLav" onClick={aggiungiLavorazione}>{editingLavId?"✓ Salva modifica":(inserisciDopoId!=null?"⤵ Inserisci sotto la voce scelta":"+ Aggiungi lavorazione")}</button>
            {editingLavId && <button className="btn btn-ghost btn-sm" onClick={() => { setEditingLavId(null); setLavForm(emptyLav); }}>Annulla</button>}
            {lavForm.desc && <button className="btn btn-ghost btn-sm" onClick={() => salvaVoceNelListino({...lavForm, ...calcolaLavorazione(lavForm)})} title="Salva questa voce nel listino per riusarla">💾 Salva nel listino</button>}

          </div>
        </div>

        {/* Tabella lavorazioni */}
        <div style={{ marginTop:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"28px 1fr 60px 60px 100px 90px 90px 100px 110px 150px", gap:6, padding:"6px 0 10px", borderBottom:`1px solid ${theme.border}` }}>
            {["#","Descrizione","U.M.","Qtà","€/um","Ore tot.","Sp.Est.","vs Mercato","Totale",""].map((h,i) => (
              <div key={i} style={{ fontSize:10, textTransform:"uppercase", letterSpacing:0.8, color:theme.textDim, fontWeight:600 }}>{h}</div>
            ))}
          </div>
          {totali.length === 0 && (
            <div style={{ textAlign:"center", padding:"24px 0", color:theme.textDim, fontSize:13 }}>Aggiungi lavorazioni dal listino o manualmente</div>
          )}
          {(() => {
            // Raggruppa lavorazioni: prima quelle senza gruppo, poi per gruppo
            const rendered = [];
            const gruppiVisti = new Set();
            totali.forEach((l, i) => {
              if (!l.gruppo) {
                // Voce singola
                // FIX_SPEST_COLONNA_v1: griglia a 10 colonne (aggiunta Sp.Est.)
                rendered.push(
                  <div key={l.id} style={{ display:"grid", gridTemplateColumns:"28px 1fr 60px 60px 100px 90px 90px 100px 110px 150px", gap:6, alignItems:"center", padding:"10px 0", opacity:l.escluso?0.45:1, textDecoration:l.escluso?"line-through":"none", borderBottom:`1px solid ${theme.border}33` }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:13, color:theme.accent }}>{l.numVoce || String(i+1).padStart(2,"0")}</div>{/* NUM_VOCE_v2 */}
                    <div>
                      <div style={{ fontSize:13, fontWeight:500, whiteSpace:"pre-line" }}>{l.desc}</div>
                      {l.escluso && <span style={{ display:"inline-block", fontSize:10, background:"#f0606022", color:"#f06060", border:"1px solid #f0606044", borderRadius:4, padding:"1px 6px", marginTop:2, marginRight:4 }}>🚫 non approvata</span>}
                      {l.smaltimento && <span style={{ display:"inline-block", fontSize:10, background:"#f5a62322", color:theme.accent, border:`1px solid ${theme.accent}44`, borderRadius:4, padding:"1px 6px", marginTop:2 }}>🗑️ Smaltimento</span>}
                      {(l.categoria||l.sottocategoria) && (
                        <div style={{ fontSize:10, marginTop:2, display:"flex", gap:4 }}>
                          {l.categoria && <span style={{ background:theme.accentSoft, color:theme.accent, borderRadius:4, padding:"1px 6px" }}>{dizionarioMateriali[l.categoria]?.label||l.categoria}</span>}
                          {l.sottocategoria && <span style={{ background:theme.infoSoft, color:theme.info, borderRadius:4, padding:"1px 6px" }}>{dizionarioMateriali[l.categoria]?.sottocategorie?.[l.sottocategoria]?.label||l.sottocategoria}</span>}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:theme.textMuted }}>{l.um}</div>
                    <div style={{ fontSize:13 }}>{l.qta}</div>
                    <div style={{ fontSize:13, fontWeight:600 }}>€ {fmtEuro(l.prezzoUn)}</div>
                    <div style={{ fontSize:12, color:theme.textMuted }}>{(l.oreUn*l.qta).toFixed(1)}h</div>
                    {/* FIX_SPEST_COLONNA_v1: colonna Sp.Est. */}
                    <div style={{ fontSize:12, color:theme.textMuted }}>{(parseFloat(l.spesaEsterna)||0)>0 ? `€ ${fmtEuro((parseFloat(l.spesaEsterna)||0)*(parseFloat(l.qta)||0),0)}` : "—"}</div>
                    <div>{l.prezzoMercato>0?<span style={{ fontSize:12, fontWeight:700, color:l.scarto<=0?theme.success:theme.danger }}>{l.scarto>0?"+":""}{l.scarto?.toFixed(1)}%</span>:<span style={{ color:theme.textDim,fontSize:12 }}>—</span>}</div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.success, fontSize:14 }}>€ {fmtEuro(l.totale,0)}</div>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap", justifyContent:"flex-end" }}>
                      <button onClick={() => setLavorazioni(lavorazioni.map(x => x.id===l.id ? {...x, escluso: x.escluso!==true} : x))} style={{ background:"none", border:"none", color:l.escluso?theme.danger:theme.textMuted, cursor:"pointer", fontSize:12, padding:"2px 3px" }} title={l.escluso?"Rimettila tra i lavori da fare":"Segna come non approvata dal cliente"}>{l.escluso?"🚫":"○"}</button>
                      <button onClick={() => spostaLavorazione(l.id,-1)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, padding:"2px 3px" }} title="Sposta su">↑</button><button onClick={() => spostaLavorazione(l.id,1)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, padding:"2px 3px" }} title="Sposta giù">↓</button><button onClick={() => scegliInserisciDopo(l.id)} style={{ background:"none", border:"none", color:inserisciDopoId===l.id?theme.accent:theme.textMuted, cursor:"pointer", fontSize:12, padding:"2px 3px" }} title="Inserisci una voce sotto questa">➕</button><button onClick={() => modificaLavorazione(l)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:14, padding:"2px 4px" }} title="Modifica">✎</button>
                      <button onClick={() => setLavorazioni(lavorazioni.filter(x => x.id!==l.id))} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14, padding:"2px 4px" }} title="Elimina">✕</button>
                    </div>
                  </div>
                );
              } else if (!gruppiVisti.has(l.gruppo)) {
                // Prima voce del gruppo — renderizza tutto il gruppo
                gruppiVisti.add(l.gruppo);
                const vociGruppo = totali.filter(x => x.gruppo === l.gruppo);
                const totaleGruppo = vociGruppo.filter(x => x.escluso !== true).reduce((s,x) => s+x.totale, 0);
                const oreGruppo = vociGruppo.filter(x => x.escluso !== true).reduce((s,x) => s+(x.oreUn*x.qta), 0);
                rendered.push(
                  <div key={`gruppo-${l.gruppo}`} style={{ marginBottom:4, opacity: vociGruppo.every(x=>x.escluso===true) ? 0.45 : 1, border:`1px solid ${theme.accent}33`, borderRadius:10, overflow:"hidden" }}>
                    {/* Intestazione gruppo */}
                    <div style={{ display:"grid", gridTemplateColumns:"28px 1fr 60px 60px 100px 90px 90px 100px 110px 150px", gap:6, alignItems:"center", padding:"10px 12px", background:theme.accentSoft, borderBottom:`1px solid ${theme.accent}22` }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:13, color:theme.accent }}>{l.numVoce ? l.numVoce : "📦"}</div>{/* NUM_VOCE_v2 */}
                      <div>
                        <div style={{ fontSize:14, fontWeight:700, color:theme.accent }}>{l.gruppo}<button onClick={()=>rinominaGruppo(l.gruppo)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, padding:"0 6px" }} title="Modifica il nome del gruppo">✎</button></div>
                        <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>{vociGruppo.length} lavorazioni · {(() => {
                          const mod = l.modalitaGruppo || "corpo";
                          const coerente = gruppoCoerentePerQuantita(l.gruppo);
                          return mod === "quantita" && coerente
                            ? `visibile come ${l.um} × ${l.qta} con prezzo unitario`
                            : "visibile al cliente come voce unica";
                        })()}</div>
                        {/* FIX_PREVENTIVO_MODALITA_GRUPPO_v1 — toggle modalita */}
                        <div style={{ display:"flex", gap:4, marginTop:6 }}>
                          {(() => {
                            const mod = l.modalitaGruppo || "corpo";
                            const coerente = gruppoCoerentePerQuantita(l.gruppo);
                            const btnBase = { fontSize:11, padding:"3px 10px", borderRadius:6, border:"1px solid", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", fontWeight:600 };
                            const attivo = { background:theme.accent, color:"#fff", borderColor:theme.accent };
                            const inattivo = { background:"transparent", color:theme.textMuted, borderColor:theme.border };
                            const disabled = { background:"transparent", color:theme.textDim, borderColor:theme.border, cursor:"not-allowed", opacity:0.5 };
                            return (<>
                              <button style={{...btnBase, ...(mod==="corpo"?attivo:inattivo)}}
                                onClick={()=>setModalitaPerGruppo(l.gruppo, "corpo")}
                                title="Il cliente vede solo descrizione + totale">
                                A corpo
                              </button>
                              <button style={{...btnBase, ...(!coerente?disabled:(mod==="quantita"?attivo:inattivo))}}
                                onClick={()=>{ if (coerente) setModalitaPerGruppo(l.gruppo, "quantita"); }}
                                disabled={!coerente}
                                title={coerente ? "Mostra U.M. × Qtà × prezzo unitario al cliente" : "Disponibile solo se tutte le lavorazioni hanno stessa U.M. e stessa quantità"}>
                                A quantità
                              </button>
                            </>);
                          })()}
                        </div>
                      </div>
                      <div/>
                      <div/>
                      <div/>
                      <div style={{ fontSize:12, color:theme.textMuted }}>{oreGruppo.toFixed(1)}h</div>
                      <div/>
                      <div/>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, color:theme.accent, fontSize:16 }}>€ {fmtEuro(totaleGruppo,0)}</div>
                      <div style={{ display:"flex", gap:4, flexWrap:"wrap", justifyContent:"flex-end" }}>{/* VOCE_ESCLUSA_v2 */}
                        <button onClick={() => salvaGruppoNelListino(l.gruppo, vociGruppo)}
                          style={{ background:"none", border:"none", color:theme.info, cursor:"pointer", fontSize:13, padding:"2px 4px" }}
                          title="Salva questo gruppo nel listino come pacchetto riutilizzabile">💾</button>
                        <button onClick={() => { const nuovo = vociGruppo.every(x => x.escluso === true) !== true; setLavorazioni(lavorazioni.map(x => x.gruppo === l.gruppo ? {...x, escluso: nuovo} : x)); }}
                          style={{ background:"none", border:"none", color: vociGruppo.every(x=>x.escluso===true) ? theme.danger : theme.textMuted, cursor:"pointer", fontSize:13, padding:"2px 4px" }}
                          title="Segna tutto il gruppo come non approvato dal cliente">{vociGruppo.every(x=>x.escluso===true) ? "\u{1F6AB}" : "\u25CB"}</button>
                      </div>
                    </div>
                    {/* Lavorazioni del gruppo */}
                    {vociGruppo.map((lv, gi) => (
                      <div key={lv.id} style={{ display:"grid", gridTemplateColumns:"28px 1fr 60px 60px 100px 90px 90px 100px 110px 150px", gap:6, /* PULSANTI_GRUPPO_v3 */ alignItems:"center", padding:"8px 12px 8px 28px", borderBottom:`1px solid ${theme.border}22`, background:theme.bg }}>
                        <div style={{ fontSize:11, color:theme.textDim }}>└</div>
                        <div>
                          <div style={{ fontSize:13, color:theme.textMuted }}>{lv.desc}</div>
                          {lv.smaltimento && <span style={{ fontSize:10, background:"#f5a62322", color:theme.accent, borderRadius:4, padding:"1px 6px" }}>🗑️</span>}
                        </div>
                        <div style={{ fontSize:12, color:theme.textDim }}>{lv.um}</div>
                        <div style={{ fontSize:12, color:theme.textDim }}>{lv.qta}</div>
                        <div style={{ fontSize:12, color:theme.textDim }}>€ {fmtEur(lv.prezzoUn)}</div>
                        <div style={{ fontSize:11, color:theme.textDim }}>{(lv.oreUn*lv.qta).toFixed(1)}h</div>
                        <div/>
                        <div/>
                        <div style={{ fontSize:13, color:theme.textMuted }}>€ {fmtEuro(lv.totale,0)}</div>
                        <div style={{ display:"flex", gap:4 }}>
                          <button onClick={() => spostaLavorazione(lv.id,-1)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, padding:"2px 3px" }} title="Sposta su">↑</button><button onClick={() => spostaLavorazione(lv.id,1)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:13, padding:"2px 3px" }} title="Sposta giù">↓</button><button onClick={() => scegliInserisciDopo(lv.id)} style={{ background:"none", border:"none", color:inserisciDopoId===lv.id?theme.accent:theme.textMuted, cursor:"pointer", fontSize:12, padding:"2px 3px" }} title="Inserisci una voce sotto questa">➕</button><button onClick={() => modificaLavorazione(lv)} style={{ background:"none", border:"none", color:theme.textMuted, cursor:"pointer", fontSize:14, padding:"2px 4px" }}>✎</button>
                          <button onClick={() => setLavorazioni(lavorazioni.filter(x => x.id!==lv.id))} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14, padding:"2px 4px" }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }
            });
            return rendered;
          })()}
        </div>

        {/* Banner competitività */}
        {totMercatoTot>0 && scartoTot!==null && (
          <div style={{ marginTop:14, padding:"12px 16px", background:scartoTot<=0?"#0a1f14":"#1f0a0a", border:`1px solid ${scartoTot<=0?theme.success:theme.danger}44`, borderRadius:8, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:13, color:scartoTot<=0?theme.success:theme.danger }}>
              {scartoTot<=0?"✅":"⚠️"} Il tuo preventivo è <strong>{Math.abs(scartoTot).toFixed(1)}% {scartoTot<=0?"sotto":"sopra"} il prezzo di mercato</strong>
            </div>
            <div style={{ fontSize:12, color:theme.textMuted }}>Mercato: € {fmtEur(totMercatoTot,0)} · Tuo: € {fmtEur(subtotale,0)}</div>
          </div>
        )}

        {/* Fabbisogno materiali */}
        {(() => {
          // Calcola fabbisogno da lavorazioni salvate + form corrente in tempo reale
          const fabbisogno = {};
          // Combina lavorazioni salvate (esclusa quella in modifica) + form corrente in tempo reale
          const tutteleLav = [
            ...lavorazioni.filter(l => l.id !== editingLavId),
            ...(lavForm.desc && lavForm.categoria ? [{ ...lavForm, id:"__preview__", qta: parseFloat(lavForm.qta)||0, qtaMat: lavForm.qtaMat ? parseFloat(lavForm.qtaMat) : null }] : [])
          ];
          tutteleLav.forEach(l => {
            const catKey = l.categoria;
            const subKey = l.sottocategoria;
            if (!catKey) return;
            if (l.smaltimento) return; // escludi voci smaltimento dal fabbisogno materiali
            const subDati = dizionarioMateriali[catKey]?.sottocategorie?.[subKey];
            const catDati = dizionarioMateriali[catKey];
            const materialiList = subDati?.materiali?.length ? subDati.materiali : catDati?.materiali||[];
            // Usa qtaMat se disponibile (preventivo a corpo ma materiale a misura reale)
            const qta = parseFloat(l.qtaMat||l.qta)||0;
            const umRef = l.qtaMat ? (l.umMat||l.um) : l.um;
            materialiList.forEach(m => {
              const mObj = typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m;
              if (!mObj.nome || !mObj.incidenza || parseFloat(mObj.incidenza)<=0) return;
              const key = mObj.nome;
              const qtaFabb = qta * parseFloat(mObj.incidenza);
              if (!fabbisogno[key]) fabbisogno[key] = { nome:mObj.nome, um:mObj.um, qtaTot:0, categoriaP:mObj.categoriaP||"", lavorazioni:[] };
              fabbisogno[key].qtaTot += qtaFabb;
              fabbisogno[key].lavorazioni.push(`${l.desc} (${qta} ${umRef}${l.qtaMat?" — a corpo":""})`);
            });
          });
          const voci = Object.values(fabbisogno);
          if (!voci.length) return null;
          return (
            <div style={{ marginTop:14, background:theme.surface, borderRadius:10, padding:"14px 16px", border:`1px solid #3ecf8e44` }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#3ecf8e", marginBottom:12, textTransform:"uppercase", letterSpacing:1 }}>📦 Fabbisogno materiali stimato</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8 }}>
                {voci.map(v => {
                  // Cerca prezzo dal listino per categoria prodotto
                  const prodListino = listinoMatDB?.filter(l=>(l.categoriaP||"").toLowerCase()===(v.categoriaP||"").toLowerCase());
                  const prezzoMin = prodListino?.length ? Math.min(...prodListino.map(l=>parseFloat(l.prezzoUltimo)||0).filter(p=>p>0)) : 0;
                  return (
                    <div key={v.nome} style={{ background:theme.bg, borderRadius:8, padding:"8px 12px", border:`1px solid ${theme.border}` }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{v.nome}</div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:"#3ecf8e", marginTop:2 }}>
                        {v.qtaTot.toFixed(2)} {v.um}
                      </div>
                      {v.categoriaP && prezzoMin>0 && (
                        <div style={{ fontSize:11, color:theme.textMuted, marginTop:2 }}>
                          {v.categoriaP} · da € {prezzoMin.toFixed(2)}/{prodListino[0]?.um||"cad"}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize:11, color:theme.textDim, marginTop:10 }}>
                💡 Quantità calcolate dalle incidenze del dizionario — verifica prima dell'acquisto.
              </div>
            </div>
          );
        })()}

        {/* Riepilogo finale */}
        <div style={{ marginTop:20, display:"flex", justifyContent:"flex-end" }}>
          <div style={{ minWidth:300, background:theme.bg, borderRadius:12, padding:20, border:`1px solid ${theme.border}` }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:14 }}>Riepilogo</div>
            {(() => {
              const totSpesaEst = totali.reduce((s,l)=>s+(parseNum(l.spesaEsterna||0)*parseNum(l.qta)),0);
              return [
                ["Ore manodopera totali", `${oreTotal.toFixed(1)} h`],
                totSpesaEst>0 ? ["Spese esterne", `€ ${fmtEur(totSpesaEst,0)}`] : null,
                header.dataInizioLavori ? ["Inizio lavori", new Date(header.dataInizioLavori+"T00:00:00").toLocaleDateString("it-IT")] : null,
                header.dataFineLavori ? ["Fine lavori", new Date(header.dataFineLavori+"T00:00:00").toLocaleDateString("it-IT")] : null,
              ].filter(Boolean);
            })().map(([l,v]) => (
              <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}`, fontSize:13, color:theme.textMuted }}>
                <span>{l}</span><span style={{ color:theme.text }}>{v}</span>
              </div>
            ))}
            {/* Imponibile — sempre visibile */}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"12px 0 8px", borderBottom:`1px solid ${theme.border}` }}>
              <span style={{ fontSize:14, fontWeight:600 }}>Imponibile</span>
              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>€ {fmtEur(subtotale)}</span>
            </div>
            {/* IVA — solo se attiva */}
            {!params.noIva && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${theme.border}`, fontSize:13 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:theme.textMuted }}>+ IVA {params.iva}%</span>
                  <button onClick={() => setParams({...params, noIva:true, iva:0})}
                    style={{ background:"none", border:"none", color:theme.textDim, cursor:"pointer", fontSize:11, padding:"1px 6px", borderRadius:4, border:`1px solid ${theme.border}` }}>
                    togli
                  </button>
                </div>
                <span style={{ color:theme.textMuted }}>€ {fmtEur(ivaAmt)}</span>
              </div>
            )}
            {params.noIva && (
              <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${theme.border}`, fontSize:13 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ color:theme.textDim, fontStyle:"italic" }}>IVA esclusa</span>
                  <button onClick={() => setParams({...params, noIva:false, iva:10})}
                    style={{ background:"none", border:"none", color:theme.accent, cursor:"pointer", fontSize:11, padding:"1px 6px", borderRadius:4, border:`1px solid ${theme.accent}44` }}>
                    + aggiungi IVA
                  </button>
                </div>
                <span style={{ color:theme.textDim }}>—</span>
              </div>
            )}
            {/* Totale finale */}
            <div style={{ display:"flex", justifyContent:"space-between", padding:"14px 0 0" }}>
              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:18 }}>
                {params.noIva ? "Imponibile" : "Totale"}
              </span>
              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:theme.success }}>
                € {fmtEur(totaleFinale)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* FIX_CRONO_PANEL_v2: pannello cronoprogramma con riordino + fasi esterne */}
      {showCrono && (() => {
        // FIX_CRONO_COLORI_A_v1: colore stabile per categoria
        const coloreCategoria = (cat) => {
          if(!cat) return "#94a3b8"; // grigio per fasi senza categoria / esterne
          const palette = ["#4e9af5","#3ecf8e","#f5a623","#a78bfa","#f06060","#22d3ee","#eab308","#ec4899","#10b981","#8b5cf6"];
          let h=0; for(let i=0;i<cat.length;i++) h=(h*31+cat.charCodeAt(i))>>>0;
          return palette[h % palette.length];
        };
        const ORE_G = parseFloat(cronoOreGiorno)||8;
        const MARG = (parseFloat(cronoMargine)||0)/100;
        // fasi interne (dal preventivo)
        const fasiInt = totali.filter(l=>l.desc).map((l,i) => {
          const ore = (parseFloat(l.oreUn)||0)*(parseFloat(l.qta)||0);
          const nop = parseFloat(cronoOperai["i"+i])||1;
          const giorni = Math.ceil((ore>0?ore/ORE_G/nop:0)*(1+MARG));
          return { key:"i"+i, tipo:"interna", desc:l.descBreve||l.desc, categoria:l.categoria||"", ore, nop, giorni }; /* FIX_CRONO_COLORI_A_v1 */
        }).filter(f => !(cronoSpezzate||[]).includes(f.key)); /* FIX_CRONO_SPEZZA_BTN_v1: nascondi spezzate */
        // fasi esterne (aggiunte a mano)
        const fasiExt = (cronoFasiEsterne||[]).map((f,j) => ({ key:"e"+j, tipo:"esterna", desc:f.desc, chi:f.chi||"", ore:0, nop:0, giorni:parseInt(f.giorni)||0, _j:j }));
        let fasi = [...fasiInt, ...fasiExt];
        // applica ordine personalizzato se presente
        if (Array.isArray(cronoOrdine) && cronoOrdine.length) {
          const mappa = {}; fasi.forEach(f=>mappa[f.key]=f);
          const ordinate = cronoOrdine.map(k=>mappa[k]).filter(Boolean);
          const mancanti = fasi.filter(f=>!cronoOrdine.includes(f.key));
          fasi = [...ordinate, ...mancanti];
        }
        const isPar = (k) => (cronoParallele||[]).includes(k); // FIX_CRONO_PARALLELO_CALC_v1
        const giorniTot = fasi.reduce((s,f)=> s + (isPar(f.key)?0:f.giorni), 0);
        let dataFine=null, settimane=Math.ceil(giorniTot/6);
        if (header.dataInizioLavori) {
          let d=new Date(header.dataInizioLavori+"T00:00:00"); let contati=0;
          while(contati<giorniTot){ d.setDate(d.getDate()+1); if(d.getDay()!==0) contati++; }
          dataFine=d;
        }
        const spostaFase = (idx, dir) => {
          const chiavi = fasi.map(f=>f.key);
          const ni = idx+dir; if(ni<0||ni>=chiavi.length) return;
          [chiavi[idx],chiavi[ni]]=[chiavi[ni],chiavi[idx]];
          setCronoOrdine(chiavi);
        };
        const aggiungiEsterna = () => setCronoFasiEsterne([...(cronoFasiEsterne||[]), {desc:"Nuova fase esterna", giorni:1, chi:""}]);
        const modEsterna = (j,campo,val) => { const a=[...cronoFasiEsterne]; a[j]={...a[j],[campo]:val}; setCronoFasiEsterne(a); };
        const rimuoviEsterna = (j) => setCronoFasiEsterne(cronoFasiEsterne.filter((_,k)=>k!==j));
        const toggleParallela = (k, idx) => { /* FIX_CRONO_PARALLELO_CALC_v1 */
          if(idx===0){ alert("La prima fase non pu\u00f2 essere in parallelo (non ha una fase precedente)."); return; }
          setCronoParallele(isPar(k) ? (cronoParallele||[]).filter(x=>x!==k) : [...(cronoParallele||[]), k]);
        };
        const spezzaFase = (f) => { /* FIX_CRONO_SPEZZA_BTN_v1 */
          const g1 = Math.ceil(f.giorni/2), g2 = Math.max(1, f.giorni-g1);
          setCronoFasiEsterne([...(cronoFasiEsterne||[]),
            { desc:f.desc+" — parte 1", giorni:g1, chi:"", _origKey:f.key, _origDesc:f.desc },
            { desc:f.desc+" — parte 2 (chiusura/finitura)", giorni:g2, chi:"", _origKey:f.key, _origDesc:f.desc }
          ]);
          setCronoSpezzate([...(cronoSpezzate||[]), f.key]);
          setCronoOrdine(null); // ricalcola ordine con le nuove parti
        };
        const ricomponiFase = (origKey) => { /* FIX_CRONO_RICOMPONI_v1 */
          if(!window.confirm("Ricomporre la lavorazione? Le due parti spezzate verranno riunite nella voce originale.")) return;
          setCronoFasiEsterne((cronoFasiEsterne||[]).filter(x => x._origKey !== origKey));
          setCronoSpezzate((cronoSpezzate||[]).filter(k => k !== origKey));
          setCronoOrdine(null);
        };
        return (
          <div style={{ marginTop:20, marginBottom:16, background:theme.bg, borderRadius:12, padding:20, border:`1px solid #4e9af544` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, color:"#4e9af5" }}>📅 Cronoprogramma (stima)</div>
              <div style={{ display:"flex", gap:14, alignItems:"center", flexWrap:"wrap" }}>
                <label style={{ fontSize:12, color:theme.textMuted }}>Ore/giorno <input type="number" value={cronoOreGiorno} onChange={e=>setCronoOreGiorno(e.target.value)} style={{ width:56, marginLeft:6, padding:"4px 8px", borderRadius:6, border:`1px solid ${theme.border}`, background:theme.card, color:theme.text }} /></label>
                <label style={{ fontSize:12, color:theme.textMuted }}>Margine % <input type="number" value={cronoMargine} onChange={e=>setCronoMargine(e.target.value)} style={{ width:56, marginLeft:6, padding:"4px 8px", borderRadius:6, border:`1px solid ${theme.border}`, background:theme.card, color:theme.text }} /></label>
              </div>
            </div>
            <div style={{ overflowX:"auto" }}>
              <div style={{ display:"grid", gridTemplateColumns:"54px 1fr 70px 70px 70px 60px", gap:8, padding:"6px 4px", borderBottom:`1px solid ${theme.border}`, fontSize:10, textTransform:"uppercase", letterSpacing:1, color:theme.textDim }}>
                <div>Ordine</div><div>Fase</div><div style={{textAlign:"center"}}>Ore</div><div style={{textAlign:"center"}}>Operai</div><div style={{textAlign:"center"}}>Giorni</div><div></div>
              </div>
              {fasi.map((f,idx) => (
                <div key={f.key} style={{ display:"grid", gridTemplateColumns:"54px 1fr 70px 70px 70px 60px", gap:8, padding:"8px 4px", borderBottom:`1px solid ${theme.border}22`, alignItems:"center" }}>
                  <div style={{ display:"flex", gap:2 }}>
                    <button onClick={()=>spostaFase(idx,-1)} disabled={idx===0} style={{ padding:"2px 6px", borderRadius:4, border:`1px solid ${theme.border}`, background:"transparent", color:idx===0?theme.textDim:theme.text, cursor:idx===0?"default":"pointer", fontSize:11 }}>↑</button>
                    <button onClick={()=>spostaFase(idx,1)} disabled={idx===fasi.length-1} style={{ padding:"2px 6px", borderRadius:4, border:`1px solid ${theme.border}`, background:"transparent", color:idx===fasi.length-1?theme.textDim:theme.text, cursor:idx===fasi.length-1?"default":"pointer", fontSize:11 }}>↓</button>
                  </div>
                  <div style={{ fontSize:13, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ width:10, height:10, borderRadius:3, background:coloreCategoria(f.categoria), flexShrink:0 }}></span>
                    {f.tipo==="esterna"
                      ? <input value={f.desc} onChange={e=>modEsterna(f._j,"desc",e.target.value)} style={{ flex:1, padding:"4px 8px", borderRadius:6, border:`1px solid #f5a62366`, background:theme.card, color:theme.text, fontSize:13 }} />
                      : <span>{f.desc}</span>}
                    {f.tipo==="esterna" && ((cronoFasiEsterne[f._j]||{})._origKey ? <span style={{ fontSize:10, color:"#4e9af5", marginLeft:6 }}>🔗 spezzata</span> : <span style={{ fontSize:10, color:"#f5a623", marginLeft:6 }}>esterna</span>)}
                    {isPar(f.key) && <div style={{ fontSize:10, color:"#a78bfa", marginTop:2 }}>⇄ in parallelo con la fase sopra</div>}
                    {cronoNote[f.key] && <div style={{ fontSize:11, color:"#7c5cff", marginTop:2, fontStyle:"italic" }}>🤖 {cronoNote[f.key]}</div>}
                  </div>
                  <div style={{ textAlign:"center", fontSize:13, color:theme.textMuted }}>{f.tipo==="interna"?f.ore.toFixed(1)+"h":"—"}</div>
                  <div style={{ textAlign:"center" }}>{f.tipo==="interna"
                    ? <input type="number" min="1" value={cronoOperai[f.key]||1} onChange={e=>setCronoOperai({...cronoOperai,[f.key]:e.target.value})} style={{ width:50, padding:"4px 6px", borderRadius:6, border:`1px solid ${theme.border}`, background:theme.card, color:theme.text, textAlign:"center" }} />
                    : <span style={{color:theme.textDim}}>—</span>}</div>
                  <div style={{ textAlign:"center", fontSize:14, fontWeight:700, color:"#4e9af5" }}>{f.tipo==="esterna"
                    ? <input type="number" min="0" value={f.giorni} onChange={e=>modEsterna(f._j,"giorni",e.target.value)} style={{ width:50, padding:"4px 6px", borderRadius:6, border:`1px solid #f5a62366`, background:theme.card, color:"#4e9af5", textAlign:"center", fontWeight:700 }} />
                    : f.giorni}</div>
                  <div style={{ textAlign:"center", display:"flex", gap:2, justifyContent:"center" }}>
                    <button title={idx===0?"La prima fase non puo essere in parallelo":(isPar(f.key)?"Togli dal parallelo":"Metti in parallelo con la fase precedente")} onClick={()=>toggleParallela(f.key, idx)} style={{ background:isPar(f.key)?"#a78bfa33":"none", border:"none", color:idx===0?theme.textDim:"#a78bfa", cursor:idx===0?"default":"pointer", fontSize:14, borderRadius:4, padding:"0 3px" }}>⇄</button>
                    {f.tipo==="esterna" ? ((cronoFasiEsterne[f._j]||{})._origKey ? <button title="Ricomponi la lavorazione (annulla la spezzatura)" onClick={()=>ricomponiFase((cronoFasiEsterne[f._j]||{})._origKey)} style={{ background:"none", border:"none", color:"#4e9af5", cursor:"pointer", fontSize:14 }}>↩️</button> : <button onClick={()=>rimuoviEsterna(f._j)} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14 }}>✕</button>) : <button title="Spezza questa fase in due (per far entrare impianti in mezzo)" onClick={()=>spezzaFase(f)} style={{ background:"none", border:"none", color:"#4e9af5", cursor:"pointer", fontSize:14 }}>✂️</button>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop:10 }}>
              <button onClick={aggiungiEsterna} style={{ padding:"6px 12px", borderRadius:8, background:"#f5a62322", border:"1px solid #f5a623", color:"#f5a623", cursor:"pointer", fontSize:12 }}>➕ Aggiungi fase esterna (elettricista, idraulico...)</button>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:14, paddingTop:12, borderTop:`1px solid ${theme.border}`, flexWrap:"wrap", gap:10 }}>
              <div style={{ fontSize:14 }}>Durata totale: <strong style={{ color:"#4e9af5", fontSize:18, fontFamily:"'Syne',sans-serif" }}>{giorniTot} giorni</strong> <span style={{ color:theme.textDim, fontSize:12 }}>(~{settimane} settimane)</span></div>
              <div style={{ fontSize:13, color:theme.textMuted }}>
                {header.dataInizioLavori ? <>Inizio: <strong style={{color:theme.text}}>{new Date(header.dataInizioLavori+"T00:00:00").toLocaleDateString("it-IT")}</strong> · Fine stimata: <strong style={{color:"#3ecf8e"}}>{dataFine?dataFine.toLocaleDateString("it-IT"):"—"}</strong></> : <span style={{color:theme.accent}}>⚠️ Imposta la data inizio lavori per vedere la data fine</span>}
              </div>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:12, flexWrap:"wrap" }}>
              <button onClick={()=>salvaBozza()} style={{ padding:"8px 16px", borderRadius:8, background:theme.accent, border:"none", color:"#111", fontWeight:700, cursor:"pointer", fontSize:13 }}>💾 Salva cronoprogramma</button>
              <button onClick={stampaCronoprogramma} style={{ padding:"8px 16px", borderRadius:8, background:"#3ecf8e22", border:"1px solid #3ecf8e", color:"#3ecf8e", fontWeight:600, cursor:"pointer", fontSize:13 }}>🖨️ Stampa per il cliente</button>
              <button onClick={()=>{ if(window.confirm("Azzerare ordine e riportare le fasi come nel preventivo?\n\nLe fasi esterne restano.")) setCronoOrdine(null); }} style={{ padding:"8px 16px", borderRadius:8, background:"transparent", border:`1px solid ${theme.border}`, color:theme.textMuted, cursor:"pointer", fontSize:13 }}>🔄 Riordina come preventivo</button>
              <button disabled={cronoAiLoading} onClick={async()=>{ /* FIX_CRONO_AI_BTN_v1 */
                setCronoAiLoading(true);
                try {
                  const payload = fasi.map(f=>({ key:f.key, desc:f.desc, ore:f.ore||0 }));
                  const resp = await fetch('/api/cronoprogramma-ai', { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({ fasi:payload }) });
                  if(!resp.ok){ const e=await resp.json().catch(()=>({})); throw new Error(e.error||('HTTP '+resp.status)); }
                  const dati = await resp.json();
                  const ord = Array.isArray(dati.ordine)?dati.ordine:[];
                  if(ord.length===0) throw new Error('Nessuna proposta ricevuta');
                  setCronoOrdine(ord.map(o=>o.key).filter(Boolean));
                  const nuoviOperai={...cronoOperai}, nuoveNote={...cronoNote};
                  ord.forEach(o=>{ if(o.key){ if(o.operai) nuoviOperai[o.key]=o.operai; if(o.nota) nuoveNote[o.key]=o.nota; } });
                  setCronoOperai(nuoviOperai); setCronoNote(nuoveNote);
                  alert('✅ Proposta applicata! Verifica ordine, operai e note, poi salva.');
                } catch(e){ alert('❌ '+((e&&e.message)||e)); }
                finally { setCronoAiLoading(false); }
              }} style={{ padding:"8px 16px", borderRadius:8, background:cronoAiLoading?"#7c5cff44":"#7c5cff22", border:"1px solid #7c5cff", color:"#7c5cff", cursor:cronoAiLoading?"wait":"pointer", fontSize:13, fontWeight:600 }}>{cronoAiLoading?"🤖 Sto pensando...":"🤖 Proponi ordine e durate"}</button>
            </div>
            <div style={{ fontSize:11, color:theme.textDim, marginTop:10, fontStyle:"italic" }}>Riordina con le frecce, aggiungi fasi esterne, poi salva. Le nuove lavorazioni del preventivo si accodano da sole. Prossimi strati: proposta AI e PDF per il cliente.</div>
          </div>
        );
      })()}
      <div style={{ display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap", alignItems:"center" }}>
        {autoSaving && (
          <span style={{ fontSize:12, color:theme.textMuted, display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", background:theme.accent, display:"inline-block", animation:"pulse 1s infinite" }} />
            Salvataggio automatico...
          </span>
        )}
        {!autoSaving && preventivoId && (
          <span style={{ fontSize:12, color:theme.success }}>✓ Salvato</span>
        )}
        <button className="btn btn-ghost" style={{color:"#4e9af5",borderColor:"#4e9af544"}} onClick={() => setShowCrono(!showCrono)}>📅 Cronoprogramma</button>
        <button className="btn btn-ghost" onClick={() => salvaBozza()}>💾 Salva</button>
        <button className="btn btn-ghost" onClick={stampaPDF}>🖨️ Stampa PDF</button>
        <button className="btn btn-ghost" onClick={inviaEmail}>📧 Invia per email</button>
        <button className="btn btn-primary" style={{ padding:"11px 24px" }} onClick={async () => { setHeader(h => ({...h,stato:"inviato"})); await salvaBozza("inviato"); }}>✓ Segna come inviato →</button>
        {header.stato==="accettato" && (
          <button className="btn btn-primary" style={{ padding:"11px 24px", background:theme.success, color:"#111" }} onClick={convertiInCantiere}>🏗️ Converti in cantiere →</button>
        )}
      </div>
    </div>
  );
}


function Cantiere({ initialSelected = null, vistaOperaio: vistaOpProp = false, nomeOperaio: nomeOpProp = "", cantieriDB = [], assenzeDB = [], bolleDB = [], costoOrario = null, preventiviDB = [] }) { /* FIX_PREV_TO_CANT_v1 */
  const fmtEuro = (n, dec=2) => fmtNumIT(n, dec); /* FIX_FORMATO_NUMERI_v2 */
  const [selected, setSelected] = useState(initialSelected);
  const [vistaOperaio, setVistaOperaio] = useState(vistaOpProp);
  const [operaioSelezionato, setOperaioSelezionato] = useState(nomeOpProp || "");
  const [cantiereOperaioAperto, setCantiereOperaioAperto] = useState(null);

  // Stato locale bolle (non ancora su Firebase — si salva su assenze/bolle)
  const [bolle, setBolle] = useState({});
  const [bollaForm, setBollaForm] = useState({ num:"", fornitore:"", lavorazione:"", descrizione:"", importo:"", data:"" });
  const [showBollaForm, setShowBollaForm] = useState(false);

  // Lavorazioni extra — caricate da Firebase (campo lavExtra nel cantiere)
  const [lavExtra, setLavExtra] = useState({});
  const [showLavExtra, setShowLavExtra] = useState(false);
  const [lavExtraForm, setLavExtraForm] = useState({ desc:"", um:"mq", qta:"", prezzoUn:"", note:"" });

  // Diario — caricato da Firebase (campo diario nel cantiere)
  const [diario, setDiario] = useState({});
  const [showDiarioForm, setShowDiarioForm] = useState(false);
  const [diarioForm, setDiarioForm] = useState({ testo:"", icon:"📝" });

  // Carica lavExtra e diario da Firebase quando cambia cantieriDB
  useEffect(() => {
    const nuovoLavExtra = {};
    const nuovoDiario = {};
    cantieriDB.forEach(c => {
      const cid = c._id||c.id;
      if (c.lavExtra) nuovoLavExtra[cid] = c.lavExtra;
      if (c.diario)   nuovoDiario[cid]   = c.diario;
    });
    setLavExtra(nuovoLavExtra);
    setDiario(nuovoDiario);
  }, [cantieriDB]);

  const salvaLavExtraFirebase = async (cid, nuovaLista) => {
    try { await updateDoc(doc(db,"cantieri",cid), { lavExtra: nuovaLista, updatedAt: new Date().toISOString() }); }
    catch(e) { console.error("lavExtra:", e.message); }
  };

  const salvaDiarioFirebase = async (cid, nuovaLista) => {
    try { await updateDoc(doc(db,"cantieri",cid), { diario: nuovaLista, updatedAt: new Date().toISOString() }); }
    catch(e) { console.error("diario:", e.message); }
  };

  // Foto lavorazione
  const [fotoLav, setFotoLav] = useState({});

  // Form nuovo cantiere
  const [showNuovoCantiere, setShowNuovoCantiere] = useState(false);
  const [nuovoC, setNuovoC] = useState({ nome:"", cliente:"", indirizzo:"", citta:"", inizio:"", fine:"", importo:"", note:"", priorita:"media" });
  const [salvandoC, setSalvandoC] = useState(false);

  // Avanzamento edit locale
  const [avanzamentoEdit, setAvanzamentoEdit] = useState({});
  const [tabDettaglio, setTabDettaglio] = useState({}); // { cantiereId: "cantiere"|"consuntivo" }

  // Modal modifica lavorazione cantiere
  const [editLavCantiere, setEditLavCantiere] = useState(null);
  const [editLavForm, setEditLavForm] = useState({});
  const [salvandoEditLav, setSalvandoEditLav] = useState(false);
  const [spostaOreModal, setSpostaOreModal] = useState(false);
  const [editBollaRiga, setEditBollaRiga] = useState(null); // FIX_EDIT_BOLLA_RIGA_v1
  const [spostaDestIdx, setSpostaDestIdx] = useState("");
  const [spostaSelezione, setSpostaSelezione] = useState([]); // FIX_SPOSTA_ORE_SEL_v1: id registrazioni da spostare
  const [salvandoSposta, setSalvandoSposta] = useState(false);
  const [lavApertaIdx, setLavApertaIdx] = useState(null);
  const [catAperte, setCatAperte] = useState({});
  const toggleCat = (catKey) => setCatAperte(prev => ({...prev, [catKey]: !prev[catKey]}));

  // FIX_VISTA_CLIENTE_v1 - genera link pubblico cliente
  const [linkCliente, setLinkCliente] = useState({});
  const [generandoLink, setGenerandoLink] = useState(false);
  const generaLinkCliente = async (cidLink, ruolo = 'cliente') => {
    setGenerandoLink(true);
    try {
      const jwt = localStorage.getItem('bertoc_jwt');
      const r = await fetch(`${NAS_URL}/api/cliente/qr-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + jwt },
        body: JSON.stringify({ cantiereId: cidLink, ruolo })
      });
      const data = await r.json();
      if (!r.ok || !data.url) throw new Error(data.error || 'Errore generazione link');
      setLinkCliente(prev => ({ ...prev, [cidLink + '_' + ruolo]: data.url }));
    } catch (e) { alert('Errore: ' + e.message); }
    finally { setGenerandoLink(false); }
  };

  // FIX_SPESE_REALI_v1 - stato popup spese esterne reali + salvataggio
  const [spesaModalKey, setSpesaModalKey] = useState(null);
  const [spesaForm, setSpesaForm] = useState({});
  const [salvandoSpesa, setSalvandoSpesa] = useState(false);
  const nuovaSpesaForm = () => ({ editId:null, data:new Date().toISOString().slice(0,10), fornitore:"", documento:"", importo:"", note:"", pagato:false, dataPagamento:"", metodo:"bonifico" });
  const salvaSpeseReali = async (cid, idx, nuoveSpese) => {
    const cantiere = cantieriDB.find(c => (c._id||c.id) === cid);
    if (!cantiere) return false;
    const lavs = [...(cantiere.lavorazioni || [])];
    if (!lavs[idx]) return false;
    lavs[idx] = { ...lavs[idx], speseReali: nuoveSpese };
    await updateDoc(doc(db, "cantieri", cid), { lavorazioni: lavs, updatedAt: new Date().toISOString() });
    return true;
  };

  // FIX_AVANZ_MANUALE_v1 - salvataggio % fatto manuale per lavorazione
  const [avanzManEdit, setAvanzManEdit] = useState({});
  const salvaAvanzManuale = async (cid, idx, val) => {
    const cantiere = cantieriDB.find(c => (c._id||c.id) === cid);
    if (!cantiere) return false;
    const lavs = [...(cantiere.lavorazioni || [])];
    if (!lavs[idx]) return false;
    const nuova = { ...lavs[idx] };
    if (val === null || val === undefined || val === '') delete nuova.avanzManuale;
    else nuova.avanzManuale = val;
    lavs[idx] = nuova;
    await updateDoc(doc(db, "cantieri", cid), { lavorazioni: lavs, updatedAt: new Date().toISOString() });
    return true;
  };

  // FIX_AVANZ_IBRIDO_v1 - salva/azzera avanzamento manuale del cantiere
  const salvaAvanzCantiere = async (cid, val) => {
    const v = (val === null || val === undefined || val === '') ? null : Math.max(0, Math.min(100, Math.round(parseFloat(val)||0)));
    await updateDoc(doc(db, "cantieri", cid), { avanzamentoManuale: v, updatedAt: new Date().toISOString() });
    setAvanzamentoEdit(prev => { const cp = { ...prev }; delete cp[cid]; return cp; });
    return true;
  };

  const apriModificaLav = (cid, idx, lav) => {
    const lavCantMod = cantieriDB.find(c => (c._id||c.id) === cid) || {}; /* LAV_BY_ID_v2 */ const oreRegistrate = assenzeDB.filter(a => a.tipo === "lavorato" && (a.cantiereId === cid || (!a.cantiereId && normLavTxt(a.cantiere) === normLavTxt(lavCantMod.nome))) && trovaIdxLav(a, lavCantMod.lavorazioni) === idx).reduce((s, a) => s + (parseFloat(a.ore) || 0), 0);
    setEditLavCantiere({ cid, idx, oreRegistrate });
    setEditLavForm({ desc: lav.desc || "", descBreve: lav.descBreve || "", gruppo: lav.gruppo || "", modalitaGruppo: lav.modalitaGruppo || "corpo", um: lav.um || "mq", qta: String(lav.qta || "1"), minuti: String(lav.minuti || "30"), costoMat: String(lav.costoMat || "0"), spesaEsterna: String(lav.spesaEsterna || "0"), categoria: lav.categoria || "", sottocategoria: lav.sottocategoria || "" });
  };

  const salvaModificaLav = async () => {
    if (!editLavCantiere) return;
    const { cid, idx } = editLavCantiere;
    const cantiere = cantieriDB.find(c => (c._id||c.id) === cid);
    if (!cantiere) return;
    setSalvandoEditLav(true);
    try {
      const lavs = [...(cantiere.lavorazioni || [])];
      lavs[idx] = { ...lavs[idx], desc: editLavForm.desc, descBreve: editLavForm.descBreve, gruppo: editLavForm.gruppo, modalitaGruppo: editLavForm.modalitaGruppo || "corpo", um: editLavForm.um, qta: parseFloat(editLavForm.qta) || 1, minuti: parseFloat(editLavForm.minuti) || 30, costoMat: parseFloat(editLavForm.costoMat) || 0, spesaEsterna: parseFloat(editLavForm.spesaEsterna) || 0, categoria: editLavForm.categoria, sottocategoria: editLavForm.sottocategoria };
      await updateDoc(doc(db, "cantieri", cid), { lavorazioni: lavs, updatedAt: new Date().toISOString() });
      setEditLavCantiere(null);
    } catch(e) { alert("Errore: " + e.message); }
    finally { setSalvandoEditLav(false); }
  };

  const spostaOre = async () => { /* FIX_SPOSTA_ORE_SEL_v1: sposta solo le registrazioni selezionate */
    if (!editLavCantiere || spostaDestIdx === "") return;
    if (!spostaSelezione || spostaSelezione.length === 0) { alert("Seleziona almeno una registrazione da spostare."); return; }
    const { cid } = editLavCantiere;
    const destIdx = parseInt(spostaDestIdx);
    const cantiere = cantieriDB.find(c => (c._id||c.id) === cid);
    if (!cantiere) return;
    setSalvandoSposta(true);
    try {
      const destLav = cantiere.lavorazioni?.[destIdx];
      const daSpostare = assenzeDB.filter(a => spostaSelezione.includes(a._id));
      for (const ore of daSpostare) { await updateDoc(doc(db, "assenze", ore._id), { lavorazioneIdx: destIdx, lavorazione: destLav?.desc || ore.lavorazione, lavorazioneId: (destLav && destLav.id != null) ? destLav.id : null /* LAV_BY_ID_v2 */ }); }
      alert(daSpostare.length + " registrazioni spostate!");
      setSpostaOreModal(false); setSpostaDestIdx(""); setSpostaSelezione([]);
    } catch(e) { alert("Errore: " + e.message); }
    finally { setSalvandoSposta(false); }
  };

  const eliminaLavorazione = async () => {
    if (!editLavCantiere) return;
    const { cid, idx, oreRegistrate } = editLavCantiere;
    if (oreRegistrate > 0) { alert("Sposta prima le ore!"); return; }
    const cantiere = cantieriDB.find(c => (c._id||c.id) === cid);
    if (!cantiere) return;
    if (!window.confirm("Eliminare questa lavorazione?")) return;
    try {
      const lavs = [...(cantiere.lavorazioni || [])];
      lavs.splice(idx, 1);
      await updateDoc(doc(db, "cantieri", cid), { lavorazioni: lavs, updatedAt: new Date().toISOString() });
      setEditLavCantiere(null);
    } catch(e) { alert("Errore: " + e.message); }
  };

  // Consuntivo modificabile per cantiere: { cantiereId: { lavorazioni:[...], note:"" } }
  const [consuntivoEdit, setConsuntivoEdit] = useState({});

  // Ore operaio
  const [oreInputs, setOreInputs] = useState({});
  const [oreSalvate, setOreSalvate] = useState({});

  const inputSt = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%" };

  const [showGenPos, setShowGenPos] = useState(false); const [showPonteggio, setShowPonteggio] = useState(false); // PONT_EDIFICIO_v1
  const c = selected !== null ? cantieriDB.find(x => (x._id||x.id) === selected) : null;

  // ── Calcolo avanzamento per lavorazione da ore reali ─────────────────────
  // orePerLavorazione: { cantiereId: { lavIdx: oreEffettive } }
  const orePerLavorazione = {};
  const norm = s => (s||"").toLowerCase().trim();
  assenzeDB.filter(a => a.tipo==="lavorato").forEach(a => {
    if (!a.cantiere && !a.cantiereId) return;
    const cant = cantieriDB.find(c =>
      (c._id && c._id === a.cantiereId) ||
      norm(c.nome) === norm(a.cantiere)
    );
    if (!cant) return;
    const cid = cant._id||cant.id;
    if (!orePerLavorazione[cid]) orePerLavorazione[cid] = {};
    const lavs = cant.lavorazioni||[];
    // Prima cerca per nome lavorazione (più affidabile)
    let idx = trovaIdxLav(a, lavs); // LAV_BY_ID_v1
    if (idx >= 0) {
      orePerLavorazione[cid][idx] = (orePerLavorazione[cid][idx]||0) + (parseFloat(a.ore)||0);
    } else {
      // Ore senza lavorazione specifica — le sommo sul cantiere globalmente usando indice -1
      orePerLavorazione[cid]["_totale"] = (orePerLavorazione[cid]["_totale"]||0) + (parseFloat(a.ore)||0);
    }
  });

  // Calcola avanzamento % di una lavorazione in base alle ore
  // FIX_AVANZ_SPESE_v1 - avanzamento da spese pagate (lavorazioni a 0 ore con spesa esterna)
  // FIX_AVANZ_MANUALE_v1 - lettura % fatto manuale (override avanzamento)
  const avanzManualeVal = (lav) => {
    if (!lav) return null;
    const v = lav.avanzManuale;
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return (!isNaN(n)) ? Math.max(0, Math.min(100, Math.round(n))) : null;
  };
  const avanzamentoDaSpese = (lav) => {
    if (!lav) return null;
    const spese = Array.isArray(lav.speseReali) ? lav.speseReali : [];
    const prevista = (parseFloat(lav.spesaEsterna)||0) * (parseFloat(lav.qta)||0);
    if (prevista <= 0 && spese.length === 0) return null;
    const pagato = spese.filter(s => s && s.pagato).reduce((a,s) => a + (parseFloat(s.importo)||0), 0);
    if (prevista > 0) return Math.min(100, Math.round((pagato/prevista)*100));
    return pagato > 0 ? 100 : 0;
  };
  const avanzamentoLavorazione = (cant, lavIdx) => {
    const lav = (cant.lavorazioni||[])[lavIdx];
    if (!lav) return 0;
    { const _m = avanzManualeVal(lav); if (_m !== null) return _m; } // FIX_AVANZ_MANUALE_v1
    const cid = cant._id||cant.id;
    const oreEffettive = (orePerLavorazione[cid]||{})[lavIdx] || 0;
    // ore previste = (minuti/60) * qta
    const orePreviste = ((parseFloat(lav.minuti)||0)/60) * (parseFloat(lav.qta)||0);
    if (orePreviste <= 0) { const aS = avanzamentoDaSpese(lav); return aS !== null ? aS : (oreEffettive > 0 ? 100 : 0); } // FIX_AVANZ_SPESE_v1
    return Math.min(100, Math.round((oreEffettive / orePreviste) * 100));
  };

  // Avanzamento complessivo cantiere = media pesata per qta delle lavorazioni
  const avanzamentoCalcolato = (cant) => {
    const lavs = cant.lavorazioni||[];
    const cid = cant._id||cant.id;
    if (lavs.length === 0) return cant.avanzamento||0;
    const oreCant = orePerLavorazione[cid]||{};

    // Se ci sono ore totali sul cantiere ma non per lavorazione specifica
    // usa il totale ore / ore previste totali
    const oreTotPrev = lavs.reduce((s,l) => s + ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0), 0);
    const oreTotEff = Object.entries(oreCant)
      .filter(([k]) => k !== "_totale")
      .reduce((s,[,v]) => s+v, 0) + (oreCant["_totale"]||0);

    if (false) { // FIX_AVANZ_MANUALE_v1 - totale sempre media pesata (conta anche % fatto)
      return Math.min(100, Math.round((oreTotEff / oreTotPrev) * 100));
    }

    // Fallback: media pesata per qta
    // FIX_PESO_UGUALE_v1 - media semplice: ogni lavorazione pesa uguale
    const _nLav = lavs.length;
    if (_nLav === 0) return cant.avanzamento||0;
    const sommaAvanz = lavs.reduce((s,l,i) => s + avanzamentoLavorazione(cant,i), 0);
    const calc = Math.round(sommaAvanz/_nLav);
    return calc;
  };

  // FIX_AVANZ_IBRIDO_v1 - avanzamento mostrato: override manuale se presente, altrimenti automatico
  const avanzamentoMostrato = (cant) => {
    if (!cant) return 0;
    const m = cant.avanzamentoManuale;
    const hasM = (m === 0) || (m !== '' && m !== null && m !== undefined && !isNaN(parseFloat(m)));
    if (hasM) return Math.max(0, Math.min(100, Math.round(parseFloat(m))));
    const lavs = cant.lavorazioni || [];
    if (lavs.length > 0) return avanzamentoCalcolato(cant);
    return Math.max(0, Math.min(100, Math.round(parseFloat(cant.avanzamento)||0)));
  };

  // ── Firebase actions ──────────────────────────────────────────────────────
  const salvaNuovoCantiere = async () => {
    if (!nuovoC.nome || !nuovoC.cliente) { alert("Nome cantiere e cliente obbligatori."); return; }
    setSalvandoC(true);
    try {
      await addDoc(collection(db,"cantieri"), { ...nuovoC, importo:parseFloat(nuovoC.importo)||0, avanzamento:0, completato:false, createdAt:serverTimestamp(), updatedAt:new Date().toISOString() });
      setShowNuovoCantiere(false);
      setNuovoC({ nome:"", cliente:"", indirizzo:"", citta:"", inizio:"", fine:"", importo:"", note:"", priorita:"media" });
    } catch(e) { alert("❌ "+e.message); }
    setSalvandoC(false);
  };

  const salvaAvanzamento = async (id, val) => {
    try { await updateDoc(doc(db,"cantieri",id), { avanzamento:parseInt(val), updatedAt:new Date().toISOString() }); }
    catch(e) { console.error(e); }
  };

  const completaCantiere = async (id) => {
    if (!window.confirm("Segnare come completato?\n\nIl cantiere passerà in Contabilità → Da incassare. Andrà in Archivio solo dopo che tutti i pagamenti sono stati saldati.")) return;
    try { await updateDoc(doc(db,"cantieri",id), { completato:true, avanzamento:100, daIncassare:true, dataCompletamento:new Date().toLocaleDateString("it-IT"), updatedAt:new Date().toISOString() }); }
    catch(e) { alert("❌ "+e.message); }
  };

  const salvaOreOperaio = async (cantiereId, cantiereName, lavorazioneDesc, lavorazioneIdx, operaio, dataISO) => {
    const key = `${cantiereId}-${lavorazioneIdx}`;
    const ore = parseFloat(oreInputs[key])||0;
    if (!ore || ore<=0) { alert("Inserisci le ore."); return; }
    const dataIT = dataISO
      ? dataISO.split("-").reverse().join("/")
      : (() => {
          const n = new Date();
          const gg = String(n.getDate()).padStart(2,"0");
          const mm = String(n.getMonth()+1).padStart(2,"0");
          const aaaa = n.getFullYear();
          return `${gg}/${mm}/${aaaa}`;
        })();
    try {
      await addDoc(collection(db,"assenze"), { operaio, data:dataIT, cantiere:cantiereName, cantiereId:cantiereId, lavorazione:lavorazioneDesc, lavorazioneIdx:lavorazioneIdx, lavorazioneId:idLavDaPos(((cantieriDB.find(c => (c._id||c.id) === cantiereId)||{}).lavorazioni), lavorazioneIdx, lavorazioneDesc) /* LAV_BY_ID_v4 */, ore, tipo:"lavorato", cantiereCE:false, createdAt:new Date().toISOString() });
      setOreSalvate({ ...oreSalvate, [key]:ore });
      setOreInputs({ ...oreInputs, [key]:"" });
    } catch(e) { alert("❌ "+e.message); }
  };

  // ── Ordinamento lista per priorità + data ────────────────────────────────
  const PRIO_ORD = { alta:0, media:1, bassa:2 };
  const [cercaCantieriLista, setCercaCantieriLista] = useState(""); // FIX_CERCA_CANTIERI_v1
  const cantieriOrdinati = [...cantieriDB.filter(c=>!c.completato)].sort((a,b) => {
    const pa = PRIO_ORD[a.priorita||"media"]??1;
    const pb = PRIO_ORD[b.priorita||"media"]??1;
    if (pa !== pb) return pa - pb;
    // stessa priorità: più vecchio prima
    const da = a.inizio ? a.inizio.split("/").reverse().join("") : "99999999";
    const db2 = b.inizio ? b.inizio.split("/").reverse().join("") : "99999999";
    return da.localeCompare(db2);
  });

  const PRIO_COLORI = { alta:{color:"#f06060",bg:"#f0606022",label:"Alta"}, media:{color:"#f5a623",bg:"#f5a62322",label:"Media"}, bassa:{color:"#3ecf8e",bg:"#3ecf8e22",label:"Bassa"} };

  // ── LISTA ────────────────────────────────────────────────────────────────
  const renderLista = () => {
    const cantieriAttivi = cantieriDB.filter(c=>!c.completato);
    const valore = cantieriAttivi.reduce((s,c)=>s+parseFloat(c.importo||0),0);
    return (
      <>
        <div className="stats-grid" style={{ gridTemplateColumns:"repeat(3,1fr)" }}>
          <div className="stat-card accent"><div className="stat-label">Cantieri attivi</div><div className="stat-value">{cantieriAttivi.length}</div></div>
          <div className="stat-card info"><div className="stat-label">Priorità alta</div><div className="stat-value">{cantieriAttivi.filter(c=>c.priorita==="alta").length}</div></div>
          <div className="stat-card success"><div className="stat-label">Valore in lavorazione</div><div className="stat-value">€{Math.round(valore/1000)}k</div></div>
        </div>
        <div style={{ marginBottom:16 }}>{/* FIX_CERCA_CANTIERI_v1 */}
          <input style={{...inputSt, maxWidth:360}} placeholder="🔍 Cerca cantiere o cliente..." value={cercaCantieriLista} onChange={e=>setCercaCantieriLista(e.target.value)} />
        </div>

        {/* Form nuovo cantiere — nascosto, si usa sempre dal preventivo */}
        {showNuovoCantiere && (
          <div className="card" style={{ marginBottom:20, border:`1px solid ${theme.accent}55` }}>
            <div className="card-title" style={{ marginBottom:18 }}>🏗️ Nuovo cantiere</div>
            <div className="form-grid" style={{ gap:14 }}>
              <div className="form-group"><label>Nome cantiere *</label><input style={inputSt} placeholder="es. Ristrutturazione bagno Bianchi" value={nuovoC.nome} onChange={e=>setNuovoC({...nuovoC,nome:e.target.value})} /></div>
              <div className="form-group"><label>Cliente *</label><input style={inputSt} placeholder="Mario Bianchi" value={nuovoC.cliente} onChange={e=>setNuovoC({...nuovoC,cliente:e.target.value})} /></div>
              <div className="form-group"><label>Indirizzo</label><input style={inputSt} placeholder="Via Roma 1" value={nuovoC.indirizzo} onChange={e=>setNuovoC({...nuovoC,indirizzo:e.target.value})} /></div>
              <div className="form-group"><label>Città</label><input style={inputSt} placeholder="Arezzo" value={nuovoC.citta} onChange={e=>setNuovoC({...nuovoC,citta:e.target.value})} /></div>
              <div className="form-group"><label>Data inizio</label><input style={inputSt} placeholder="gg/mm/aaaa" value={nuovoC.inizio} onChange={e=>setNuovoC({...nuovoC,inizio:e.target.value})} /></div>
              <div className="form-group"><label>Fine prevista</label><input style={inputSt} placeholder="gg/mm/aaaa" value={nuovoC.fine} onChange={e=>setNuovoC({...nuovoC,fine:e.target.value})} /></div>
              <div className="form-group"><label>Importo (€)</label><input style={inputSt} type="number" placeholder="0" value={nuovoC.importo} onChange={e=>setNuovoC({...nuovoC,importo:e.target.value})} /></div>
              <div className="form-group">
                <label>Priorità</label>
                <select style={inputSt} value={nuovoC.priorita} onChange={e=>setNuovoC({...nuovoC,priorita:e.target.value})}>
                  <option value="alta">🔴 Alta</option>
                  <option value="media">🟡 Media</option>
                  <option value="bassa">🟢 Bassa</option>
                </select>
              </div>
              <div className="form-group full"><label>Note</label><textarea style={{...inputSt,minHeight:60}} value={nuovoC.note} onChange={e=>setNuovoC({...nuovoC,note:e.target.value})} /></div>
            </div>
            <div style={{ display:"flex", gap:10, marginTop:18, justifyContent:"flex-end" }}>
              <button className="btn btn-ghost" onClick={()=>setShowNuovoCantiere(false)}>Annulla</button>
              <button className="btn btn-primary" onClick={salvaNuovoCantiere} disabled={salvandoC}>{salvandoC?"Salvataggio...":"✓ Crea cantiere"}</button>
            </div>
          </div>
        )}

        {/* Switch vista operaio */}
        {!vistaOpProp && (
          <div className="card" style={{ marginBottom:20 }}>
            <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
              <div style={{ fontSize:13, color:theme.textMuted, fontWeight:500 }}>👷 Entra come operaio:</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                {dipendenti.map(d => (
                  <div key={d.nome} onClick={()=>{ setOperaioSelezionato(d.nome); setVistaOperaio(true); setCantiereOperaioAperto(null); }}
                    style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px", borderRadius:8, cursor:"pointer", border:`1px solid ${theme.border}`, background:theme.bg }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor=d.colore}
                    onMouseLeave={e=>e.currentTarget.style.borderColor=theme.border}>
                    <div className="avatar" style={{ background:d.colore, width:22, height:22, fontSize:9 }}>{d.nome[0]}</div>
                    <span style={{ fontSize:12, color:theme.textMuted }}>{d.nome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Lista cantieri ordinata per priorità + data */}
        {cantieriOrdinati.length === 0 && (
          <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏗️</div>
            <div style={{ fontSize:15, color:theme.textMuted }}>Nessun cantiere attivo</div>
          </div>
        )}
        {cantieriOrdinati.filter(cant => { const q=cercaCantieriLista.toLowerCase().trim(); if(!q) return true; return (cant.nome||"").toLowerCase().includes(q)||(cant.cliente||"").toLowerCase().includes(q); }).map(cant => {
          const cid = cant._id||cant.id;
          const avanz = avanzamentoCalcolato(cant); // FIX_VISTA_REALE_v1
          const prio = PRIO_COLORI[cant.priorita||"media"];
          return (
            <div className="cantiere-card" key={cid} onClick={()=>setSelected(cid)}
              style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:16, alignItems:"start", cursor:"pointer", marginBottom:12, borderLeft:`4px solid ${prio.color}` }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6, flexWrap:"wrap" }}>
                  <div className="cantiere-name">{cant.nome}</div>
                  <span style={{ background:prio.bg, color:prio.color, border:`1px solid ${prio.color}44`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>
                    {prio.label}
                  </span>
                  <span className={`badge ${avanz>80?"badge-success":avanz>50?"badge-warning":"badge-info"}`}>
                    {avanz>80?"In chiusura":avanz>50?"In corso":"Avviato"}
                  </span>
                </div>
                <div className="cantiere-meta">📍 {cant.indirizzo||"—"} &nbsp;·&nbsp; 👤 {cant.cliente}</div>
                <div style={{ marginTop:10 }}>
                  <div className="cantiere-progress-label">
                    <span>Avanzamento {cant.lavorazioni?.length>0?"(da ore)":"(manuale)"}</span>
                    <span style={{ fontWeight:600 }}>{avanz}%</span>
                  </div>
                  <div className="progress-bar" style={{ height:8 }}>
                    <div className="progress-fill" style={{ width:`${avanz}%`, background:avanz>80?theme.success:avanz>50?theme.accent:theme.info }} />
                  </div>
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:theme.success }}>€ {parseFloat(cant.importo||0).toLocaleString("it-IT")}</div>
                <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>{cant.inizio||"—"} → {cant.fine||"—"}</div>
                <button className="btn btn-ghost btn-sm" style={{ marginTop:10 }}>Apri →</button>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // ── DETTAGLIO CANTIERE (titolare) ────────────────────────────────────────
  const renderDettaglio = () => {
    if (!c) return null;
    const cid = c._id||c.id;
    const lavori = c.lavorazioni||[];
    const extra = lavExtra[cid]||[];
    // Righe bolle da Firebase filtrate per questo cantiere
    const bolleRigheC = bolleDB.flatMap(b =>
      (b.righe||[]).filter(r => r.cantiere === (c.nome||""))
        .map(r => ({ ...r, _bollaNum:b.num, _bollaId:b._id }))
    );
    const diarioC = diario[cid]||[];
    const importo = parseFloat(c.importo||0);

    // Calcoli economici
    const totBolleImporto = bolleRigheC.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
    const totExtraImporto = extra.reduce((s,l)=>s+(parseFloat(l.qta)||0)*(parseFloat(l.prezzoUn)||0),0);

    // Ore previste e reali per lavorazione
    const costoOra = costoOrario || COSTO_ORARIO_GLOBALE;
    const lavoriConAvanz = lavori.map((l,i) => {
      const orePreviste  = ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0);
      const oreEffettive = (orePerLavorazione[cid]||{})[i]||0;
      const percOre = orePreviste>0 ? Math.min(150,Math.round((oreEffettive/orePreviste)*100)) : (oreEffettive>0?100:0);
      const _lavRaw = (c.lavorazioni||[])[i] || l; // FIX_AVANZ_SPESE_v1
      const _aS = (orePreviste<=0) ? avanzamentoDaSpese(_lavRaw) : null; // FIX_AVANZ_SPESE_v1
      const _m = avanzManualeVal(_lavRaw); // FIX_AVANZ_MANUALE_v1
      const perc = (_m !== null) ? _m : ((_aS !== null) ? _aS : percOre); // FIX_AVANZ_MANUALE_v1

      // Materiali: budget previsto vs bolle collegate a questa lavorazione
      // Se c'è misura reale (qtaMat) usa quella per il budget materiali, altrimenti usa qta normale
      const qtaPerMat = (l.qtaMat && parseFloat(l.qtaMat) > 0) ? parseFloat(l.qtaMat) : parseFloat(l.qta)||0;
      const budgetMat = (parseFloat(l.costoMat)||0) * qtaPerMat;
      const righeIdx  = bolleRigheC.filter(r =>
        r.lavorazione === l.desc ||
        (l.sottocategoria && r.sottocategoria === l.sottocategoria) ||
        (l.categoria && r.categoriaP === l.categoria && !r.lavorazione && !r.sottocategoria)
      );
      const matRealeAssegnato = righeIdx.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
      const righeNonAssegnate = bolleRigheC.filter(r => !r.lavorazione && !r.sottocategoria && !r.categoriaP);
      const totNonAssegnato = righeNonAssegnate.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
      const budgetTotale = lavori.reduce((s,ll)=>(s+(parseFloat(ll.costoMat)||0)*(parseFloat(ll.qta)||0)),0);
      const quotaProporzionale = budgetTotale > 0 ? (budgetMat / budgetTotale) * totNonAssegnato : lavori.length > 0 ? totNonAssegnato / lavori.length : 0;
      const matReale = matRealeAssegnato + quotaProporzionale;
      const percMat   = budgetMat>0 ? Math.min(150,Math.round((matReale/budgetMat)*100)) : (matReale>0?100:0);

      // Soglia alert: >100% = sforamento
      const alertOre = percOre > 100;
      const alertMat = percMat > 100;

      const costoLavPrev = orePreviste * costoOra;
      const costoLavEff  = oreEffettive * costoOra;
      return { ...l, i, orePreviste, oreEffettive, percOre, percMat, perc,
               budgetMat, matReale, alertOre, alertMat, costoLavPrev, costoLavEff };
    });

    const oreTotPrev = lavoriConAvanz.reduce((s,l)=>s+l.orePreviste,0);
    const oreTotEff  = lavoriConAvanz.reduce((s,l)=>s+l.oreEffettive,0);
    const avanzTot = avanzamentoCalcolato(c); // FIX_VISTA_REALE_v1 (mia vista = reale)

    const avanzEdit = avanzamentoEdit[cid] ?? c.avanzamento ?? 0;
    const prio = PRIO_COLORI[c.priorita||"media"];

    // ── Consuntivo: inizializza da lavorazioni preventivo se non esiste ─────
    const tabCorrente = tabDettaglio[cid] || "cantiere";
    const initConsuntivo = () => {
      const lavBase = (c.lavorazioni||[]).map(l => {
        const totP = parseFloat(l.totale||0) || (parseFloat(l.prezzoUn||0)*parseFloat(l.qta||0));
        return {
          id: l.id||Date.now()+Math.random(),
          desc: l.desc||"",
          um: l.um||"mq",
          qta: parseFloat(l.qta)||0,
          prezzoUn: parseFloat(l.prezzoUn)||0,
          totale: totP,
          qtaPrev: parseFloat(l.qta)||0,
          prezzoUnPrev: parseFloat(l.prezzoUn)||0,
          totalePrev: totP,
          tipo: "preventivo",
          gruppo: l.gruppo||"",                            /* GRUPPI_CONSUNTIVO_v1 */
          modalitaGruppo: l.modalitaGruppo||"corpo",
          numVoce: l.numVoce||"",               /* NUM_VOCE_CANT_v1 */
        };
      });
      const lavExtraC = (lavExtra[cid]||[]).map(l => ({
        id: l.id, desc: l.desc, um: l.um||"cad",
        qta: parseFloat(l.qta)||0, prezzoUn: parseFloat(l.prezzoUn)||0,
        totale: (parseFloat(l.qta)||0)*(parseFloat(l.prezzoUn)||0),
        qtaPrev:0, prezzoUnPrev:0, totalePrev:0, tipo:"extra",
      }));
      const extraOperaiGruppi = {};
      assenzeDB.filter(a => a.extra===true && (a.cantiereId===cid || (a.cantiere||"")===(c.nome||""))).forEach(a => {
        const k = (a.lavorazione||"Attività extra").trim();
        if (!extraOperaiGruppi[k]) extraOperaiGruppi[k] = { ore:0, operai:new Set() };
        extraOperaiGruppi[k].ore += parseFloat(a.ore)||0;
        extraOperaiGruppi[k].operai.add(a.operaio);
      });
      const lavExtraOperai = Object.entries(extraOperaiGruppi).map(([desc, d]) => ({
        id: "extra-op-"+desc, desc, um: "h",
        qta: parseFloat(d.ore.toFixed(1)), prezzoUn: 0, totale: 0,
        qtaPrev:0, prezzoUnPrev:0, totalePrev:0, tipo:"extra",
        note: "Attività operai: "+[...d.operai].join(", "),
      }));
      return { lavorazioni: [...lavBase, ...lavExtraC, ...lavExtraOperai], note:"" };
    };

    // Carica consuntivo da Firebase (campo consuntivo nel cantiere) o da stato locale o inizializza
    const consuntivoSalvato = c.consuntivo || null;
    const consuntivo = consuntivoEdit[cid] || (consuntivoSalvato ? consuntivoSalvato : initConsuntivo());
    const setConsuntivo = (val) => setConsuntivoEdit({...consuntivoEdit,[cid]:val});

    const salvaConsuntivo = async (dati) => {
      try {
        const nuovoTotale = dati.lavorazioni.reduce((s,l)=>s+(parseFloat(l.totale)||0),0);
        // Calcola e salva anche i costi per l'archivio
        const oreTot = lavoriConAvanz.reduce((s,l)=>s+l.oreEffettive,0);
        const costoLavoroCalc = Math.round(oreTot * costoOra);
        const costoMateriaiCalc = Math.round(bolleRigheC.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0));
        const margineCalc = Math.round(nuovoTotale - costoLavoroCalc - costoMateriaiCalc);
        await updateDoc(doc(db,"cantieri",cid), {
          consuntivo: dati,
          importo: parseFloat(nuovoTotale.toFixed(2)),
          costoLavoro: costoLavoroCalc,
          costoMateriali: costoMateriaiCalc,
          margine: margineCalc,
          updatedAt: new Date().toISOString()
        });
        setConsuntivoEdit({...consuntivoEdit,[cid]:dati});
      } catch(e) { alert("❌ Errore salvataggio: "+e.message); }
    };

    const totConsuntivo = consuntivo.lavorazioni.reduce((s,l)=>s+(parseFloat(l.totale)||0),0);
    const totPreventivo = (c.lavorazioni||[]).reduce((s,l)=>s+(parseFloat(l.totale||0)||(parseFloat(l.prezzoUn||0)*parseFloat(l.qta||0))),0);
    const scostamento = totConsuntivo - totPreventivo;

    // PDF cliente (solo voci e prezzi finali)
    /* NUM_VOCE_CONS_v1: numero della voce nel consuntivo (stabile, poi ordina, poi dal cantiere) */
    const _numDi = (l) => (l && (l.numVoce || l.numPrev)) ||
      (((c.lavorazioni||[]).find(x => l && l.id!=null && String(x.id)===String(l.id))||{}).numVoce) || "";

    const generaPDFCliente = () => {
      const fmtPDF = (n) => parseFloat(n||0).toLocaleString("it-IT", { minimumFractionDigits:2, maximumFractionDigits:2 });
      const ivaPerc = parseFloat(consuntivo.iva||0);
      const totImponibile = totConsuntivo;
      const totIva = ivaPerc > 0 ? totImponibile * ivaPerc / 100 : 0;
      const totFinale = totImponibile + totIva;
      const dataOggi = new Date().toLocaleDateString("it-IT");

      /* GRUPPI_CONSUNTIVO_v1: le voci dello stesso gruppo escono come riga unica */
      const _gruppiVistiPdf = new Set();
      const righeHtml = consuntivo.lavorazioni.filter(l=>l.desc).map((l,i) => {
        const bg = i%2===0 ? "#fff" : "#fafafa";
        if (l.gruppo) {
          if (_gruppiVistiPdf.has(l.gruppo)) return "";
          _gruppiVistiPdf.add(l.gruppo);
          const _rg = consuntivo.lavorazioni.filter(x => x.gruppo === l.gruppo);
          const _totG = _rg.reduce((s,x)=>s+(parseFloat(x.totale)||0),0);
          const _um0 = _rg[0]?.um, _qta0 = parseFloat(_rg[0]?.qta)||0;
          const _coerente = _rg.every(x => x.um===_um0 && (parseFloat(x.qta)||0)===_qta0);
          const _aQta = (l.modalitaGruppo||"corpo")==="quantita" && _coerente && _qta0>0;
          const _qtaCel = _aQta ? (_qta0.toLocaleString("it-IT",{maximumFractionDigits:2})+" "+_um0) : "\u2014";
          const _punCel = _aQta ? ("\u20ac "+fmtPDF(Math.round((_totG/_qta0)*100)/100)) : "\u2014";
          return '<tr style="background:'+bg+'">'
            +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;line-height:1.4">'+(_numDi(l)?'<strong>'+_numDi(l)+'</strong>  ':'')+'<strong>'+l.gruppo+'</strong></td>'
            +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:center;white-space:nowrap;color:#555">'+_qtaCel+'</td>'
            +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right;white-space:nowrap;color:#555">'+_punCel+'</td>'
            +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right;white-space:nowrap;font-weight:700">\u20ac '+fmtPDF(_totG)+'</td>'
            +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;width:50px"></td>'
            +'</tr>';
        }
        const extra = l.tipo==="extra" ? '<span style="background:#fef3c7;color:#d97706;border-radius:4px;padding:2px 6px;font-size:10px;font-weight:600">Extra</span>' : (l.tipo==="modifica" ? '<span style="background:#dbeafe;color:#2563eb;border-radius:4px;padding:2px 6px;font-size:10px;font-weight:600">Modifica</span>' : ""); /* FIX_PDF_MODIFICA_v1 */
        const qtaFmt = parseFloat(l.qta||0).toLocaleString("it-IT",{maximumFractionDigits:2});
        return '<tr style="background:'+bg+'">'
          +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;line-height:1.4">'+(_numDi(l)?'<strong>'+_numDi(l)+'</strong>  ':'')+l.desc+'</td>'
          +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:center;white-space:nowrap;color:#555">'+qtaFmt+' '+l.um+'</td>'
          +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right;white-space:nowrap;color:#555">\u20ac '+fmtPDF(l.prezzoUn)+'</td>'
          +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;text-align:right;white-space:nowrap;font-weight:700">\u20ac '+fmtPDF(l.totale)+'</td>'
          +'<td style="padding:8px 12px;border-bottom:1px solid #f0f0f0;width:50px">'+extra+'</td>'
          +'</tr>';
      }).join("");

      const rigaIva = ivaPerc > 0
        ? '<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:13px;color:#555;border-bottom:1px solid #f0f0f0"><span>IVA '+ivaPerc+'%</span><span style="font-weight:600;color:#111">\u20ac '+fmtPDF(totIva)+'</span></div>'
        : '<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:13px;color:#555;border-bottom:1px solid #f0f0f0"><span>IVA</span><span>Esente</span></div>';

      const noteHtml = consuntivo.note
        ? '<div style="margin-top:14px;padding:10px 14px;background:#f9fafb;border-left:3px solid #ddd;font-size:12px;color:#666;font-style:italic">'+consuntivo.note+'</div>'
        : "";

      const html = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Consuntivo '+c.nome+'</title>'
        +'<style>'
        +'@import url(\'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&family=Syne:wght@800&display=swap\');'
        +'*{box-sizing:border-box;margin:0;padding:0}'
        +'body{font-family:\'DM Sans\',sans-serif;color:#111;background:#fff;font-size:13px}'
        +'.page{max-width:820px;margin:auto;padding:32px 40px}'
        +'.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:2px solid #111;margin-bottom:20px}'
        +'.logo-sub{font-size:9px;color:#888;letter-spacing:1px;text-transform:uppercase;margin-top:3px}'
        +'.cliente-box{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;margin-bottom:20px}'
        +'.campo-label{font-size:9px;color:#999;text-transform:uppercase;letter-spacing:1px;margin-bottom:3px}'
        +'.campo-val{font-size:14px;font-weight:700}'
        +'table{width:100%;border-collapse:collapse}'
        +'thead tr{background:#f3f4f6;border-bottom:2px solid #ddd}'
        +'th{padding:9px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#555;font-weight:600}'
        +'.totale-section{display:flex;justify-content:flex-end;margin-top:20px}'
        +'.totale-box{min-width:280px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden}'
        +'.totale-row{display:flex;justify-content:space-between;padding:8px 14px;font-size:13px;color:#555;border-bottom:1px solid #f0f0f0}'
        +'.totale-finale{display:flex;justify-content:space-between;padding:10px 14px;font-weight:800;font-size:15px;color:#111;background:#f9fafb;border-top:2px solid #111}'
        +'.firma{display:flex;justify-content:space-between;margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb}'
        +'.firma-label{font-size:9px;color:#999;font-weight:600;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px}'
        +'.sign{border:1px solid #ddd;border-radius:6px;padding:10px 20px;min-width:200px;text-align:center;font-size:12px;color:#555;font-weight:600}'
        +'.sign-line{border-top:1px solid #bbb;margin-top:28px;padding-top:5px;font-size:9px;color:#aaa}'
        +'@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.firma{page-break-inside:avoid}.totale-section{page-break-inside:avoid}}'
        +'</style></head><body>'
        +'<div class="page">'
        +'<div class="header">'
        +'<div><img src="data:image/svg+xml;base64,PHN2ZyBkYXRhLWZpeD0iTE9HT19TVkdfdjEiIHdpZHRoPSIxNTUiIGhlaWdodD0iNDQiIHZpZXdCb3g9IjAgMCAxNTUgNDQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPCEtLSBiIGxldHRlciAtLT4KICA8bGluZSB4MT0iMTAiIHkxPSI0IiB4Mj0iMTAiIHkyPSI0MCIgc3Ryb2tlPSIjZTg4MzRhIiBzdHJva2Utd2lkdGg9IjMuNSIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIi8+CiAgPGNpcmNsZSBjeD0iMjIiIGN5PSIyOCIgcj0iMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2U4ODM0YSIgc3Ryb2tlLXdpZHRoPSIzLjUiLz4KICA8IS0tIHZlcnRpY2FsIHNlcGFyYXRvciAtLT4KICA8bGluZSB4MT0iMzgiIHkxPSI2IiB4Mj0iMzgiIHkyPSIzOCIgc3Ryb2tlPSIjNTU1IiBzdHJva2Utd2lkdGg9IjEiLz4KICA8IS0tIGJlcnRvYyBwcm9qZWN0IHRleHQgLS0+CiAgPHRleHQgeD0iNDUiIHk9IjIyIiBmb250LWZhbWlseT0iJ0RNIFNhbnMnLHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIGZvbnQtd2VpZ2h0PSI0MDAiIGZpbGw9IiMxMTEiIGxldHRlci1zcGFjaW5nPSItMC4zIj5iZXJ0b2MgcHJvamVjdDwvdGV4dD4KICA8IS0tIHVuZGVybGluZSAtLT4KICA8bGluZSB4MT0iNDUiIHkxPSIyNiIgeDI9IjEzMCIgeTI9IjI2IiBzdHJva2U9IiMxMTEiIHN0cm9rZS13aWR0aD0iMC44Ii8+CiAgPCEtLSB0YWdsaW5lIC0tPgogIDx0ZXh0IHg9IjQ1IiB5PSIzNiIgZm9udC1mYW1pbHk9IidETSBTYW5zJyxzYW5zLXNlcmlmIiBmb250LXNpemU9IjgiIGZpbGw9IiM4ODgiIGxldHRlci1zcGFjaW5nPSIwLjMiPmlsIHR1byBwYXJ0bmVyIGVkaWxlPC90ZXh0Pgo8L3N2Zz4=" style="height:70px" alt="bertoc project"/>'
        +'<div class="logo-sub">Via Vecchia Provinciale Lucchese 47C \u2014 Serravalle Pistoiese \u00b7 P.IVA 01747090478</div></div>'
        +'<div style="text-align:right"><div style="font-size:9px;color:#888;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">Conto finale lavori</div>'
        +'<div style="font-size:12px;color:#555;margin-top:4px">Data: '+dataOggi+'</div></div>'
        +'</div>'
        +'<div class="cliente-box">'
        +'<div><div class="campo-label">Cliente</div><div class="campo-val">'+(c.cliente||'\u2014')+'</div></div>'
        +'<div><div class="campo-label">Cantiere</div><div class="campo-val">'+c.nome+'</div></div>'
        +'<div><div class="campo-label">Indirizzo</div><div style="font-size:13px;font-weight:500">'+(c.indirizzo||'\u2014')+'</div></div>'
        +'</div>'
        +'<table><thead><tr>'
        +'<th>Descrizione lavori</th>'
        +'<th style="text-align:center">Qt\u00e0</th>'
        +'<th style="text-align:right">Prezzo unit.</th>'
        +'<th style="text-align:right">Totale</th>'
        +'<th></th>'
        +'</tr></thead><tbody>'+righeHtml+'</tbody></table>'
        +noteHtml
        +'<div class="totale-section"><div class="totale-box">'
        +'<div class="totale-row"><span>Imponibile</span><span style="font-weight:600;color:#111">\u20ac '+fmtPDF(totImponibile)+'</span></div>'
        +rigaIva
        +'<div class="totale-finale"><span>TOTALE</span><span>\u20ac '+fmtPDF(totFinale)+'</span></div>'
        +'</div></div>'
        +'<div class="firma">'
        +'<div><div class="firma-label">Firma cliente per accettazione</div>'
        +'<div class="sign">'+(c.cliente||'\u2014')+'<div class="sign-line">firma e data</div></div></div>'
        +'<div style="text-align:right"><div class="firma-label">Per BertocProject Srl</div>'
        +'<div class="sign">BertocProject Srl<div class="sign-line">firma e timbro</div></div></div>'
        +'</div>'
        +'</div></body></html>';

      const blob = new Blob([html],{type:"text/html"});
      window.open(URL.createObjectURL(blob),"_blank");
    };
    return (
      <>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, flexWrap:"wrap" }}>
          <button className="btn btn-ghost" onClick={()=>setSelected(null)}>← Tutti i cantieri</button>
          <span style={{ color:theme.textMuted }}>/ {c.nome}</span>
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>

            {!c.completato && (
              <button className="btn btn-ghost btn-sm" style={{ color:theme.success, borderColor:"#3ecf8e44" }} onClick={()=>completaCantiere(cid)}>
                ✓ Segna come completato
              </button>
            )}
            <button className="btn btn-ghost btn-sm" style={{ color:theme.accent, borderColor:`${theme.accent}44` }} onClick={()=>setShowGenPos(true)}>
              📋 Genera POS
            </button>
            <button className="btn btn-ghost btn-sm" style={{ color:theme.accent, borderColor:`${theme.accent}44` }} onClick={()=>setShowPonteggio(true)}>
              🏗️ Ponteggio
            </button>
            {c.completato && !c.daIncassare && <span className="badge badge-success">✓ COMPLETATO</span>}
            {c.completato && c.daIncassare && <span className="badge badge-warning">💰 DA INCASSARE</span>}
            <button className="btn btn-ghost btn-sm" style={{ color:theme.danger, borderColor:"#f0606044" }}
              onClick={async () => {
                if (confermaEliminaCantiere(assenzeDB, c) !== true) return;
                try { await deleteDoc(doc(db,"cantieri",cid)); setSelected(null); }
                catch(e) { alert("❌ Errore: "+e.message); }
              }}>
              🗑️ Elimina
            </button>
          </div>

        {showGenPos && (
          <GeneraPos cantiere={c} onClose={() => setShowGenPos(false)} />
        )}
        {showPonteggio && (
          <PonteggioCantiere cantiere={{ ...c, _id: cid }} onChiudi={() => setShowPonteggio(false)} />
        )}
        </div>

        {/* Tab selector */}
        <div className="tabs" style={{ marginBottom:20 }}>
          <div className={`tab ${tabCorrente==="cantiere"?"active":""}`} onClick={()=>setTabDettaglio({...tabDettaglio,[cid]:"cantiere"})}>🏗️ Cantiere</div>
          <div className={`tab ${tabCorrente==="consuntivo"?"active":""}`} onClick={()=>setTabDettaglio({...tabDettaglio,[cid]:"consuntivo"})}>📊 Consuntivo</div>
        </div>

        {/* ── TAB CONSUNTIVO ── */}
        {tabCorrente === "consuntivo" && (
          <div>
            {/* KPI riepilogo */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
              {[
                ["Totale preventivo", "€ "+fmtEuro(totPreventivo), "#4e9af5"],
                ["Totale consuntivo", "€ "+fmtEuro(totConsuntivo), theme.accent],
                ["Scostamento", (scostamento>=0?"+":"")+"€ "+Math.abs(scostamento).toFixed(2),
                  scostamento>0?"#f59e0b":scostamento<0?"#3ecf8e":theme.textDim,
                  scostamento>0?"⬆️":scostamento<0?"⬇️":"="],
              ].map(([label,val,color,icon])=>(
                <div key={label} className="card" style={{ padding:"16px 18px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{label}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color }}>{icon} {val}</div>
                </div>
              ))}
            </div>

            {/* Tabella confronto voce per voce */}
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
                <div className="card-title">📋 Lavorazioni — Preventivo vs Consuntivo</div>
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  setConsuntivo({...consuntivo, lavorazioni:[...consuntivo.lavorazioni,{
                    id:Date.now(), desc:"", um:"cad", qta:0, prezzoUn:0, totale:0,
                    qtaPrev:0, prezzoUnPrev:0, totalePrev:0, tipo:"extra"
                  }]});
                }}>+ Aggiungi voce</button>
              </div>

              {/* Header */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 80px 80px 60px 60px 80px 80px 90px 70px 34px", gap:6, padding:"6px 4px 10px", borderBottom:`1px solid ${theme.border}`, fontSize:10, textTransform:"uppercase", letterSpacing:1, color:theme.textDim }}>
                <div>Lavorazione</div>
                <div>U.M.</div>
                <div>Qtà cons.</div>
                <div>€/um cons.</div>
                <div style={{color:"#4e9af5"}}>Ore reali</div>
                <div style={{color:"#f5a623"}}>Mat. reali</div>
                <div style={{color:"#4e9af5"}}>Tot. prev.</div>
                <div style={{color:theme.accent}}>Tot. cons.</div>
                <div>Tipo</div>
                <div></div>
              </div>

              {consuntivo.lavorazioni.map((l,i)=>{
                const totP = parseFloat(l.totalePrev||0);
                const totC = parseFloat(l.totale||0);
                const delta = totC - totP;
                const colTipo = l.tipo==="extra"?"#f59e0b":l.tipo==="modifica"?"#4e9af5":theme.textDim;
                const _numRiga = _numDi(l); const _numCell = _numRiga ? (   /* ORDINA_CONSUNTIVO_v2 */
                  <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:11, color:theme.accent,
                    background:theme.accentSoft, borderRadius:5, padding:"1px 5px", marginRight:7 }}>{_numRiga}</span>
                ) : null;
                // Ore reali per questa lavorazione
                /* MATCH_ID_CONSUNTIVO_v1: prima per id, poi per descrizione */
                const _lcMatch = (l.id!=null ? lavoriConAvanz.find(lc => lc.id===l.id) : null)
                  || lavoriConAvanz.find(lc => lc.desc===l.desc) || null;
                const oreRealiLav = _lcMatch?.oreEffettive || 0;
                // Materiali reali per questa lavorazione
                const matRealiLav = bolleRigheC.filter(r => r.lavorazione===l.desc || (_lcMatch && r.lavorazione===_lcMatch.desc)).reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
                // FIX_MARGINE_RIGA_v2: spesa esterna della voce (reale se c'e, altrimenti prevista) + margine
                const _lavCant = (c.lavorazioni||[]).find(x => (x.desc||"")===l.desc);
                const _speseRealiArr = _lavCant && Array.isArray(_lavCant.speseReali) ? _lavCant.speseReali : [];
                const _spesaEstReale = _speseRealiArr.reduce((a,s)=>a+(parseFloat(s.importo)||0),0);
                const _spesaEst = _spesaEstReale; // FIX_MARGINE_RIGA_v3: tutto reale
                const _isEsterna = _spesaEst>0;
                const _margineRiga = totC - (oreRealiLav*costoOra) - matRealiLav - _spesaEst;
                const aggiorna = (campo, val) => {
                  const lavs=[...consuntivo.lavorazioni];
                  lavs[i]={...lavs[i],[campo]:val};
                  if (campo==="qta"||campo==="prezzoUn") {
                    const q = campo==="qta" ? parseFloat(val)||0 : parseFloat(lavs[i].qta)||0;
                    const p = campo==="prezzoUn" ? parseFloat(val)||0 : parseFloat(lavs[i].prezzoUn)||0;
                    lavs[i].totale = q*p;
                  }
                  setConsuntivo({...consuntivo,lavorazioni:lavs});
                };
                return (
                  <div key={l.id||i}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 80px 80px 60px 60px 80px 80px 90px 70px 34px", gap:6, alignItems:"center", padding:"8px 4px", borderBottom:`1px solid ${theme.border}22` }}>
                      {/* Descrizione */}
                      <div style={{ display:"flex", alignItems:"center", minWidth:0 }}>
                      {_numCell}
                      <input style={{...inputSt,padding:"5px 8px",fontSize:13,border:`1px solid ${l.tipo==="extra"?"#f59e0b44":theme.border}`}}
                        value={l.desc} placeholder="Descrizione"
                        onChange={e=>aggiorna("desc",e.target.value)} />
                      </div>
                      {/* U.M. */}
                      <select style={{...inputSt,padding:"5px 6px",fontSize:12}}
                        value={l.um} onChange={e=>aggiorna("um",e.target.value)}>
                        {["mq","ml","cad","mc","h","kg"].map(u=><option key={u}>{u}</option>)}
                      </select>
                      {/* Qtà consuntivo */}
                      <input type="number" style={{...inputSt,padding:"5px 8px",fontSize:13}}
                        value={l.qta} placeholder="0"
                        onChange={e=>aggiorna("qta",e.target.value)} />
                      {/* €/um consuntivo */}
                      <input type="number" style={{...inputSt,padding:"5px 8px",fontSize:13}}
                        value={l.prezzoUn} placeholder="0"
                        onChange={e=>aggiorna("prezzoUn",e.target.value)} />
                      {/* Ore reali */}
                      <div style={{ fontSize:12, color:"#4e9af5", textAlign:"center" }}>
                        {oreRealiLav>0 ? <span style={{fontWeight:700}}>{oreRealiLav.toFixed(1)}h</span> : <span style={{color:theme.textDim}}>—</span>}
                      </div>
                      {/* Materiali reali */}
                      <div style={{ fontSize:12, color:"#f5a623", textAlign:"center" }}>
                        {matRealiLav>0 ? <span style={{fontWeight:700}}>€{matRealiLav.toFixed(0)}</span> : <span style={{color:theme.textDim}}>—</span>}
                      </div>
                      {/* Totale preventivo (sola lettura) */}
                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:13, color:"#4e9af5", textAlign:"right" }}>
                        {totP>0 ? "€ "+totP.toFixed(0) : <span style={{color:theme.textDim}}>—</span>}
                      </div>
                      {/* Totale consuntivo */}
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, color:theme.success, textAlign:"right" }}>
                        € {totC.toFixed(0)}
                        {totP>0 && delta!==0 && (
                          <div style={{ fontSize:10, fontWeight:400, color:delta>0?"#f59e0b":"#3ecf8e" }}>
                            {delta>0?"+":""}{delta.toFixed(0)}€
                          </div>
                        )}
                      </div>
                      {/* Margine riga - FIX_MARGINE_RIGA_v2 */}
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:13, textAlign:"right", color: _margineRiga>=0?"#3ecf8e":"#f06060" }}>
                        {(_margineRiga>=0?"+":"")+"€"+_margineRiga.toFixed(0)}
                        {_isEsterna && <div style={{ fontSize:9, fontWeight:400, color:theme.textDim }}>esterna</div>}
                      </div>
                      {/* Tipo */}
                      <select style={{...inputSt,padding:"4px 4px",fontSize:10,color:colTipo,border:`1px solid ${colTipo}44`}}
                        value={l.tipo} onChange={e=>aggiorna("tipo",e.target.value)}>
                        <option value="preventivo">Prev.</option>
                        <option value="extra">Extra</option>
                        <option value="modifica">Mod.</option>
                      </select>
                      {/* Elimina */}
                      <button onClick={()=>setConsuntivo({...consuntivo,lavorazioni:consuntivo.lavorazioni.filter((_,j)=>j!==i)})}
                        style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>✕</button>
                    </div>
                  </div>
                );
              })}

              {/* Totali finali */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 60px 80px 80px 60px 60px 80px 80px 90px 70px 34px", gap:6, padding:"12px 4px 0", borderTop:`1px solid ${theme.border}`, marginTop:8 }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, gridColumn:"1/5" }}>TOTALI</div>
                <div style={{ fontSize:12, color:"#4e9af5", textAlign:"center", fontWeight:700 }}>
                  {lavoriConAvanz.reduce((s,l)=>s+l.oreEffettive,0).toFixed(1)}h
                </div>
                <div style={{ fontSize:12, color:"#f5a623", textAlign:"center", fontWeight:700 }}>
                  €{bolleRigheC.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0).toFixed(0)}
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, color:"#4e9af5", textAlign:"right" }}>€ {totPreventivo.toFixed(0)}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:theme.success, textAlign:"right" }}>€ {totConsuntivo.toFixed(0)}</div>
                <div style={{ gridColumn:"9/11", textAlign:"right" }}>
                  <span style={{ fontSize:13, fontWeight:700, color:scostamento>0?"#f59e0b":scostamento<0?"#3ecf8e":theme.textDim }}>
                    {scostamento>=0?"+":""}{scostamento.toFixed(0)}€
                  </span>
                </div>
              </div>

              {/* Riepilogo per tipo */}
              {(() => {
                const extra = consuntivo.lavorazioni.filter(l=>l.tipo==="extra").reduce((s,l)=>s+parseFloat(l.totale||0),0);
                const mod   = consuntivo.lavorazioni.filter(l=>l.tipo==="modifica").reduce((s,l)=>s+(parseFloat(l.totale||0)-parseFloat(l.totalePrev||0)),0);
                return (extra>0 || mod!==0) ? (
                  <div style={{ marginTop:12, display:"flex", gap:10, flexWrap:"wrap" }}>
                    {extra>0 && <div style={{ padding:"6px 12px", borderRadius:8, background:"#1a1500", border:"1px solid #f59e0b33", fontSize:12 }}>
                      <span style={{color:"#f59e0b", fontWeight:600}}>⚡ Extra aggiunti:</span> <strong style={{color:"#f59e0b"}}>+€ {extra.toFixed(0)}</strong>
                    </div>}
                    {mod!==0 && <div style={{ padding:"6px 12px", borderRadius:8, background:"#0a1020", border:"1px solid #4e9af544", fontSize:12 }}>
                      <span style={{color:"#4e9af5", fontWeight:600}}>✎ Modifiche preventivo:</span> <strong style={{color: mod>0?"#f59e0b":"#3ecf8e"}}>{mod>0?"+":""}{mod.toFixed(0)}€</strong>
                    </div>}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Resoconto interno (non va nel PDF) */}
            <div className="card" style={{ marginBottom:16, border:`1px solid ${theme.border}` }}>
              <div className="card-title" style={{ marginBottom:12 }}>🔒 Resoconto interno (non visibile al cliente)</div>
              {(() => {
                const oreTot = lavoriConAvanz.reduce((s,l)=>s+l.oreEffettive,0);
                const costoMod = oreTot * costoOra;
                const matTot = bolleRigheC.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
                const speseEstTot = (lavoriConAvanz||[]).reduce((s,l)=>{ const sp=Array.isArray(l.speseReali)?l.speseReali:[]; return s+sp.reduce((a,x)=>a+(parseFloat(x.importo)||0),0); },0); // FIX_SPESE_MARGINE_v1
                const ricavo = totConsuntivo;
                const costi = costoMod + matTot + speseEstTot;
                const margine = ricavo - costi;
                const percMargine = ricavo>0 ? Math.round((margine/ricavo)*100) : 0;
                // FIX_CONFRONTO_OBIETTIVO_v1: confronto col preventivo collegato
                const _prevColl = (preventiviDB||[]).find(p => (p._id||p.id) === c.preventivoId);
                const _prm = _prevColl && _prevColl.params ? _prevColl.params : null;
                const _utileObiettivo = _prm && _prm.utile!=null ? parseFloat(_prm.utile) : null;
                const _spGenPerc = _prm && _prm.speseGenerali!=null ? parseFloat(_prm.speseGenerali) : null;
                const _speseGenEuro = _spGenPerc!=null ? (costoMod * _spGenPerc/100) : 0;
                const _margineNetto = margine - _speseGenEuro;
                const _percNetto = ricavo>0 ? Math.round((_margineNetto/ricavo)*100) : 0;
                return (
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                    {[
                      ["Ricavo consuntivo", "€ "+ricavo.toFixed(0), theme.success],
                      ["Costo manodopera ("+oreTot.toFixed(1)+"h × €"+costoOra+")", "€ "+costoMod.toFixed(0), "#f06060"],
                      ["Costo materiali (bolle)", "€ "+matTot.toFixed(0), "#f5a623"],
                      ["Spese esterne", "€ "+speseEstTot.toFixed(0), "#f5a623"],
                      ["Margine lordo", (margine>=0?"+":"")+"€ "+margine.toFixed(0)+" ("+percMargine+"%)", margine>=0?"#3ecf8e":"#f06060"],
                      ..._utileObiettivo!=null ? [["Margine netto (− spese gen. "+(_spGenPerc||0)+"%)", (_margineNetto>=0?"+":"")+"€ "+_margineNetto.toFixed(0)+" ("+_percNetto+"%)", _margineNetto>=0?"#3ecf8e":"#f06060"], ["Obiettivo preventivo", _utileObiettivo+"%", "#4e9af5"], ["Verdetto", (_percNetto>=_utileObiettivo+3?"✅ Meglio dell'obiettivo":_percNetto>=_utileObiettivo-3?"➖ In linea":"⚠️ Sotto obiettivo"), (_percNetto>=_utileObiettivo-3?"#3ecf8e":"#f5a623")]] : [],
                    ].map(([k,v,col])=>(
                      <div key={k} style={{ background:theme.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${theme.border}` }}>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>{k}</div>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, color:col }}>{v}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Note per il cliente */}
            <div className="card" style={{ marginBottom:16 }}>
              <div className="card-title" style={{ marginBottom:10 }}>📝 Note per il cliente (appariranno nel PDF)</div>
              <textarea style={{...inputSt, minHeight:60}}
                placeholder="es. Prezzi modificati per varianti richieste dal cliente..."
                value={consuntivo.note}
                onChange={e=>setConsuntivo({...consuntivo,note:e.target.value})} />

              {/* Riepilogo IVA */}
              <div style={{ marginTop:16, display:"flex", justifyContent:"flex-end" }}>
                <div style={{ background:theme.bg, borderRadius:10, padding:"14px 20px", border:`1px solid ${theme.border}`, minWidth:280 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontSize:13, color:theme.textMuted }}>Imponibile</span>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700 }}>€ {totConsuntivo.toLocaleString("it-IT",{minimumFractionDigits:2})}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:13, color:theme.textMuted }}>IVA</span>
                      <select style={{...inputSt, width:80, padding:"4px 8px", fontSize:13}}
                        value={consuntivo.iva||"0"}
                        onChange={e=>setConsuntivo({...consuntivo, iva:e.target.value})}>
                        <option value="0">Esclusa</option>
                        <option value="4">4%</option>
                        <option value="10">10%</option>
                        <option value="22">22%</option>
                      </select>
                    </div>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#4e9af5" }}>
                      € {(totConsuntivo * (parseFloat(consuntivo.iva||0)/100)).toLocaleString("it-IT",{minimumFractionDigits:2})}
                    </span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", paddingTop:8, borderTop:`1px solid ${theme.border}` }}>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:15 }}>Totale</span>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:theme.success }}>
                      € {(totConsuntivo * (1 + parseFloat(consuntivo.iva||0)/100)).toLocaleString("it-IT",{minimumFractionDigits:2})}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Azioni */}
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap" }}>
              <button className="btn btn-ghost" style={{color:"#4e9af5", borderColor:"#4e9af544"}} onClick={()=>{ /* FIX_AGGIORNA_CONSUNTIVO_v1: aggiunge solo le voci mancanti senza toccare quelle esistenti */
                const init=initConsuntivo();
                const esistenti=(consuntivo.lavorazioni||[]);
                const normalizza=(s)=>(s||"").trim().toLowerCase();
                const descEsistenti=new Set(esistenti.map(l=>normalizza(l.desc)));
                const mancanti=(init.lavorazioni||[]).filter(l=>!descEsistenti.has(normalizza(l.desc)));
                if(mancanti.length===0){ alert("Nessuna voce nuova da aggiungere. Il consuntivo è già aggiornato."); return; }
                const nuovo={...consuntivo, lavorazioni:[...esistenti, ...mancanti]};
                setConsuntivo(nuovo); salvaConsuntivo(nuovo);
                alert("✅ Aggiunte "+mancanti.length+" voci nuove: "+mancanti.map(m=>m.desc).join(", ")+".\nLe voci già presenti non sono state toccate.");
              }}>➕ Aggiorna (voci nuove)</button>
              {/* PREZZI_CONSUNTIVO_v1 */}
              <button className="btn btn-ghost" style={{color:"#f5a623", borderColor:"#f5a62344"}} onClick={()=>{
                const init = initConsuntivo();
                const norm = (s)=>(s||"").trim().toLowerCase();
                const daPrev = new Map();     /* PREZZI_BYID_v1 */
                const daPrevId = new Map();
                (init.lavorazioni||[]).filter(l=>l.tipo==="preventivo").forEach(l=>{
                  daPrev.set(norm(l.desc), l);
                  if (l.id != null) daPrevId.set(String(l.id), l);
                });
                const esistenti = (consuntivo.lavorazioni||[]);
                const cambi = [];
                esistenti.forEach((l,i)=>{
                  if (l.tipo !== "preventivo") return;
                  const p = (l.id!=null ? daPrevId.get(String(l.id)) : null) || daPrev.get(norm(l.desc));
                  if (!p) return;
                  const q0 = parseFloat(l.qta)||0, p0 = parseFloat(l.prezzoUn)||0;
                  const q1 = parseFloat(p.qta)||0, p1 = parseFloat(p.prezzoUn)||0;
                  if (Math.abs(q0-q1) < 0.001 && Math.abs(p0-p1) < 0.001) return;
                  cambi.push({ i, desc:(p.desc||l.desc), q0, p0, q1, p1, t0:q0*p0, t1:q1*p1, descNuova:p.desc||l.desc });
                });
                if (cambi.length === 0) { alert("Nessun prezzo da aggiornare: il consuntivo e' gia' allineato al preventivo."); return; }
                const diff = cambi.reduce((s,c)=>s+(c.t1-c.t0), 0);
                const _e2 = (n)=>(Math.round((parseFloat(n)||0)*100)/100).toFixed(2); /* ARROTONDA_CONFERME_v1 */
                const righe = cambi.map(c=>"\u2022 "+c.desc.slice(0,55)+"\n   "+c.q0+" x \u20ac"+_e2(c.p0)+" = \u20ac"+Math.round(c.t0)+"   \u2192   "+c.q1+" x \u20ac"+_e2(c.p1)+" = \u20ac"+Math.round(c.t1)).join("\n");
                if (!window.confirm("Aggiornare "+cambi.length+" prezzi dal preventivo?\n\n"+righe+"\n\nVariazione totale: "+(diff>=0?"+":"")+"\u20ac"+Math.round(diff)+"\n\nOre, bolle e voci extra NON vengono toccate.")) return;
                const mappa = new Map(cambi.map(c=>[c.i,c]));
                const lavNuove = esistenti.map((l,i)=>{
                  const c = mappa.get(i);
                  if (!c) return l;
                  return { ...l, desc:c.descNuova||l.desc, qta:c.q1, prezzoUn:c.p1, totale:c.t1, qtaPrev:c.q1, prezzoUnPrev:c.p1, totalePrev:c.t1 };
                });
                const nuovo = { ...consuntivo, lavorazioni: lavNuove };
                setConsuntivo(nuovo); salvaConsuntivo(nuovo);
                alert("\u2705 Aggiornati "+cambi.length+" prezzi. Variazione: "+(diff>=0?"+":"")+"\u20ac"+Math.round(diff));
              }}>🔄 Aggiorna prezzi dal preventivo</button>
              {/* ORDINA_CONSUNTIVO_v1 */}
              <button className="btn btn-ghost" style={{color:"#4e9af5", borderColor:"#4e9af544"}} onClick={async ()=>{
                /* ORDINA_CONSUNTIVO_v2: ordine e numeri dai preventivi */
                const normFuzzy = s => (s||"").toLowerCase().replace(/\s+/g,"").replace(/[^a-z0-9]/g,"");
                let vociPrev = [];
                try {
                  const snap = await getDocs(collection(db,"preventivi"));
                  const prevs = snap.docs.map(d=>({...d.data(),_id:d.id}))
                    .filter(p => normFuzzy(p.nomeCantiere||p.header?.nomeCantiere||"")===normFuzzy(c.nome||"")
                              && normFuzzy(p.cliente||p.header?.cliente||"")===normFuzzy(c.cliente||""))
                    .sort((a,b)=>String(a.header?.dataEmissione||a.dataEmissione||"").localeCompare(String(b.header?.dataEmissione||b.dataEmissione||"")));
                  if (prevs.length===0) { alert("Nessun preventivo trovato per questo cantiere."); return; }
                  prevs.forEach(p => (p.lavorazioni||[]).forEach(l => vociPrev.push(l)));
                } catch(err) { alert("\u274c Errore nel leggere i preventivi: "+err.message); return; }
                const ordine = new Map();
                vociPrev.forEach((l,i)=>{ if (l.id!=null && !ordine.has(String(l.id))) ordine.set(String(l.id), i); });
                const norm = (s)=>(s||"").trim().toLowerCase();
                const ordineDesc = new Map();
                vociPrev.forEach((l,i)=>{ if (!ordineDesc.has(norm(l.desc))) ordineDesc.set(norm(l.desc), i); });
                /* ORDINA_CONSUNTIVO_v3: il gruppo e' un blocco unico */
                const grpDi = new Map(), posGrp = new Map();
                vociPrev.forEach((l,i)=>{
                  const g = (l.gruppo||"").trim();
                  if (g && !posGrp.has(g)) posGrp.set(g, i);
                  if (l.id!=null) grpDi.set(String(l.id), g);
                  const k = "d:"+norm(l.desc);
                  if (!grpDi.has(k)) grpDi.set(k, g);
                });
                const gruppoDi = (l) => {
                  if (l.id!=null && grpDi.has(String(l.id))) return grpDi.get(String(l.id));
                  const k = "d:"+norm(l.desc);
                  return grpDi.has(k) ? grpDi.get(k) : "";
                };
                const pos = (l) => {
                  const g = gruppoDi(l);
                  if (g && posGrp.has(g)) return posGrp.get(g);
                  if (l.id!=null && ordine.has(String(l.id))) return ordine.get(String(l.id));
                  if (ordineDesc.has(norm(l.desc))) return ordineDesc.get(norm(l.desc));
                  return 99999;
                };
                /* ORDINA_CONSUNTIVO_v3: voci marcate Extra che pero' sono nei preventivi */
                let righe = [...(consuntivo.lavorazioni||[])];
                const _recup = righe.filter(l => l.tipo!=="preventivo" && pos(l)!==99999);
                if (_recup.length > 0) {
                  const _el = _recup.map(l=>"\u2022 "+(l.desc||"").slice(0,55)+"  \u20ac"+Math.round(parseFloat(l.totale)||0)).join("\n");
                  if (window.confirm(_recup.length+" voci sono marcate Extra ma esistono nei preventivi:\n\n"+_el
                      +"\n\nOK = riportale tra le voci di preventivo (usciranno dal totale Extra)"
                      +"\nAnnulla = lasciale Extra")) {
                    const _ids = new Set(_recup.map(l=>String(l.id)));
                    righe = righe.map(l => _ids.has(String(l.id)) ? { ...l, tipo:"preventivo" } : l);
                  }
                }
                const nuove = righe
                  .map((l,i)=>({ l, i, p: l.tipo==="preventivo" ? pos(l) : 99999 }))
                  .sort((a,b)=> a.p!==b.p ? a.p-b.p : a.i-b.i)
                  .map(x=>x.l);
                const cambiato = nuove.some((l,i)=> l !== righe[i]);
                if (!cambiato) { alert("Le righe sono gia' nell'ordine del preventivo."); return; }
                const _fuori = nuove.filter(l => l.tipo==="preventivo" && pos(l)===99999).length;
                if (!window.confirm("Riordinare e numerare le righe come nei preventivi ("+vociPrev.length+" voci)?"
                  +(_fuori>0 ? "\n\n\u26a0\ufe0f "+_fuori+" voci non si ritrovano nei preventivi: niente numero, vanno in fondo." : "")
                  +"\n\nLe voci extra e le attivita' degli operai vanno in fondo.\nImporti, ore e materiali non cambiano.")) return;
                let _n = 0, _lastG = null;
                const conNum = nuove.map(l => {
                  if (l.tipo==="preventivo" && pos(l)!==99999) {
                    const g = gruppoDi(l);
                    if (!g || g !== _lastG) _n++;
                    _lastG = g || null;
                    return { ...l, numPrev: String(_n).padStart(2,"0") };
                  }
                  _lastG = null;
                  return { ...l, numPrev: "" };
                });
                const nuovo = { ...consuntivo, lavorazioni: conNum };
                setConsuntivo(nuovo); salvaConsuntivo(nuovo);
              }}>🔃 Ordina come il preventivo</button>
              <button className="btn btn-ghost" onClick={()=>{ if(!window.confirm("Ripristinare tutto dal preventivo?\n\nATTENZIONE: sovrascrive TUTTE le voci del consuntivo con quelle del preventivo. Perderai i valori reali che hai compilato a mano.")) return; const init=initConsuntivo(); setConsuntivo(init); salvaConsuntivo(init); }}>↺ Ripristina da preventivo</button>
              <button className="btn btn-ghost btn-sm" style={{color:theme.success, borderColor:`${theme.success}44`}} onClick={()=>salvaConsuntivo(consuntivo)}>💾 Salva consuntivo</button>
              <button className="btn btn-primary" onClick={generaPDFCliente}>🖨️ PDF per il cliente</button>
            </div>
          </div>
        )}

        {/* ── TAB CANTIERE (contenuto esistente) ── */}
        {tabCorrente === "cantiere" && (<>

        {/* INFO + AVANZAMENTO */}
        <div className="grid-2" style={{ marginBottom:16 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom:16 }}>📋 Info cantiere</div>
            {[
              ["Nome cantiere", c.nome],
              ["Cliente", c.cliente],
              ["Indirizzo", c.indirizzo ? `${c.indirizzo}${c.citta?", "+c.citta:""}` : "—"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${theme.border}44` }}>
                <span style={{ fontSize:12, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>{k}</span>
                <span style={{ fontSize:14, fontWeight:500 }}>{v||"—"}</span>
              </div>
            ))}
            {/* Importo modificabile */}
            <div style={{ padding:"8px 0", borderBottom:`1px solid ${theme.border}44` }}>
              <span style={{ fontSize:12, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>Importo incassato (IVA compresa)</span>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:6 }}>
                <span style={{ fontSize:13, color:theme.textDim }}>€</span>
                <input type="number" style={{...inputSt, flex:1, padding:"6px 10px", fontSize:15, fontWeight:700}}
                  defaultValue={importo}
                  onBlur={async e => {
                    const val = parseFloat(e.target.value)||0;
                    if (val === importo) return;
                    try { await updateDoc(doc(db,"cantieri",cid),{importo:val}); }
                    catch(err){ alert("❌ "+err.message); }
                  }} />
                <button className="btn btn-ghost btn-sm" style={{ color:theme.accent, borderColor:`${theme.accent}44`, whiteSpace:"nowrap" }}
                  onClick={async () => {
                    try {
                      const snap = await getDocs(collection(db,"preventivi"));
                      // Normalizza rimuovendo spazi, accenti e confrontando solo caratteri alfanumerici
                      const normFuzzy = s => (s||"").toLowerCase().replace(/\s+/g,"").replace(/[^a-z0-9]/g,"");
                      const prevs = snap.docs.map(d=>({...d.data(),_id:d.id}))
                        .filter(p => {
                          const nome = normFuzzy(p.nomeCantiere||p.header?.nomeCantiere||"");
                          const cli  = normFuzzy(p.cliente||p.header?.cliente||"");
                          const nomeC = normFuzzy(c.nome||"");
                          const cliC  = normFuzzy(c.cliente||"");
                          // Confronto fuzzy — uno deve contenere l'altro (gestisce typo come Codominio/Condominio)
                          const nomeOk = nome===nomeC || nome.includes(nomeC) || nomeC.includes(nome) ||
                            // Distanza di Levenshtein semplificata: se differiscono per max 2 caratteri
                            Math.abs(nome.length-nomeC.length)<=2 && nome.slice(0,6)===nomeC.slice(0,6);
                          const cliOk  = cli===cliC || cli.includes(cliC) || cliC.includes(cli);
                          return (p.stato==="accettato" || p.cantiereCreato===true) && nomeOk && cliOk;
                        });
                      if (!prevs.length) { alert("Nessun preventivo accettato trovato."); return; }
                      const totale = prevs.reduce((s,p) => {
                        const imp = parseFloat(p.imponibile||p.subtotale||0);
                        const tot = parseFloat(p.totaleFinale||0);
                        const noIva = p.params?.noIva === true || p.noIva === true;
                        const iva = parseFloat(p.params?.iva||10)/100;
                        let imponibile = 0;
                        if (imp > 0) imponibile = imp;
                        else if (noIva || iva === 0) imponibile = tot;
                        else if (tot > 0) imponibile = Math.round(tot/(1+iva)*100)/100;
                        return s + imponibile;
                      }, 0);
                      await updateDoc(doc(db,"cantieri",cid),{importo:parseFloat(totale.toFixed(2))});
                      alert(`✅ Importo aggiornato: € ${totale.toLocaleString("it-IT",{minimumFractionDigits:2})}\n(${prevs.length} preventivi trovati)`);
                    } catch(err){ alert("❌ "+err.message); }
                  }}>🔄 Ricalcola</button>
                <button className="btn btn-ghost btn-sm" style={{ color:theme.accent, borderColor:`${theme.accent}44`, whiteSpace:"nowrap" }}
                  title="Riallinea ore e materiali dal preventivo, tenendo % fatto, spese e foto"
                  onClick={async () => { /* FIX_AGGIORNA_DA_PREV_v1 */
                    if (!window.confirm("Aggiornare ore e materiali di questo cantiere prendendoli dal preventivo?\n\nIl % fatto manuale, le spese e le foto restano invariati.")) return;
                    try {
                      const snap = await getDocs(collection(db,"preventivi"));
                      const normFuzzy = s => (s||"").toLowerCase().replace(/\s+/g,"").replace(/[^a-z0-9]/g,"");
                      const prevs = snap.docs.map(d=>({...d.data(),_id:d.id})).filter(p => {
                        const nome = normFuzzy(p.nomeCantiere||p.header?.nomeCantiere||"");
                        const cli  = normFuzzy(p.cliente||p.header?.cliente||"");
                        const nomeC = normFuzzy(c.nome||""); const cliC = normFuzzy(c.cliente||"");
                        const nomeOk = nome===nomeC || nome.includes(nomeC) || nomeC.includes(nome) || (Math.abs(nome.length-nomeC.length)<=2 && nome.slice(0,6)===nomeC.slice(0,6));
                        const cliOk  = cli===cliC || cli.includes(cliC) || cliC.includes(cli);
                        return (p.stato==="accettato" || p.cantiereCreato===true) && nomeOk && cliOk;
                      });
                      if (!prevs.length) { alert("Nessun preventivo collegato trovato."); return; }
                      const totale = prevs.reduce((s,p) => { const imp=parseFloat(p.imponibile||p.subtotale||0); const tot=parseFloat(p.totaleFinale||0); const noIva=p.params?.noIva===true||p.noIva===true; const iva=parseFloat(p.params?.iva||10)/100; let imponibile=0; if(imp>0)imponibile=imp; else if(noIva||iva===0)imponibile=tot; else if(tot>0)imponibile=Math.round(tot/(1+iva)*100)/100; return s+imponibile; }, 0);
                      const _norm = s => (s||"").toLowerCase().trim();
                      const _vociPrev = prevs.flatMap(p => (p.lavorazioni||[]));
                      const _lavPuliti = _vociPrev.map(l => ({ id: l.id || (Date.now()+Math.floor(Math.random()*1000)), desc: l.desc, descBreve: l.descBreve||"", um: l.um, qta: parseFloat(l.qta)||0, minuti: parseFloat(l.minuti)||0, costoMat: parseFloat(l.costoMat)||0, prezzoUn: (function(){ try { return calcolaLavorazione(l).prezzoUn; } catch(e){ return l.prezzoUn||0; } })(), totale: (function(){ try { return calcolaLavorazione(l).totale; } catch(e){ return l.totale||0; } })(), categoria: l.categoria||"", sottocategoria: l.sottocategoria||"", qtaMat: l.qtaMat ? parseFloat(l.qtaMat) : null, umMat: l.umMat||"" }));
                      const _match = (la, lp) => (la.id && lp.id) ? la.id === lp.id : _norm(la.desc) === _norm(lp.desc);
                      const _attuali = c.lavorazioni || [];
                      const _noU = v => v === undefined ? null : v;
                      const _aggiornate = _attuali.map(la => { const lp = _lavPuliti.find(l => _match(la, l)); if (!lp) return la; return { ...la, id: la.id || lp.id, desc: lp.desc || la.desc, costoMat: _noU(lp.costoMat) ?? la.costoMat ?? 0, minuti: _noU(lp.minuti) ?? la.minuti ?? 30, qta: _noU(lp.qta) ?? la.qta ?? 1, um: lp.um || la.um || "cad", qtaMat: lp.qtaMat != null ? lp.qtaMat : (la.qtaMat ?? null), umMat: lp.umMat || la.umMat || "", descBreve: lp.descBreve || la.descBreve || "", categoria: lp.categoria || la.categoria || "", sottocategoria: lp.sottocategoria || la.sottocategoria || "" }; });
                      const _nuove = _lavPuliti.filter(lp => !_attuali.some(la => _match(la, lp)));
                      const _nMatch = _attuali.filter(la => _lavPuliti.some(lp => _match(la, lp))).length;
                      await updateDoc(doc(db,"cantieri",cid),{ importo:parseFloat(totale.toFixed(2)), lavorazioni:[..._aggiornate, ..._nuove] });
                      alert("Aggiornato dal preventivo.\nLavorazioni riallineate: "+_nMatch+", nuove aggiunte: "+_nuove.length+"\nImporto: € "+totale.toLocaleString("it-IT",{minimumFractionDigits:2})+"\n(% fatto, spese e foto conservati)");
                    } catch(err){ alert("Errore: "+err.message); }
                  }}>📥 Aggiorna dal preventivo</button>
              </div>
            </div>
            {/* Date modificabili */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginTop:10 }}>
              <div>
                <div style={{ fontSize:11, color:theme.textDim, marginBottom:3, textTransform:"uppercase", letterSpacing:1 }}>🏗️ Inizio lavori</div>
                <input style={{...inputSt, fontSize:13, padding:"6px 10px"}} type="date"
                  value={c.inizio ? c.inizio.split("/").reverse().join("-") : ""}
                  onChange={async e => {
                    const val = e.target.value ? e.target.value.split("-").reverse().join("/") : "";
                    try { await updateDoc(doc(db,"cantieri",cid),{inizio:val}); } catch(err){}
                  }} />
              </div>
              <div>
                <div style={{ fontSize:11, color:theme.textDim, marginBottom:3, textTransform:"uppercase", letterSpacing:1 }}>🏁 Fine lavori (contratto)</div>
                <input style={{...inputSt, fontSize:13, padding:"6px 10px"}} type="date"
                  value={c.fine ? c.fine.split("/").reverse().join("-") : ""}
                  onChange={async e => {
                    const val = e.target.value ? e.target.value.split("-").reverse().join("/") : "";
                    try { await updateDoc(doc(db,"cantieri",cid),{fine:val}); } catch(err){}
                  }} />
              </div>
            </div>
            <div style={{ marginTop:12, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:12, color:theme.textMuted }}>Priorità:</span>
              <select value={c.priorita||"media"} onChange={async e => { try { await updateDoc(doc(db,"cantieri",cid),{priorita:e.target.value}); } catch(err){} }}
                style={{ ...inputSt, width:"auto", padding:"4px 10px", fontSize:12, background:prio.bg, color:prio.color, border:`1px solid ${prio.color}44` }}>
                <option value="alta">🔴 Alta</option>
                <option value="media">🟡 Media</option>
                <option value="bassa">🟢 Bassa</option>
              </select>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom:12 }}>📊 Stato economico</div>

            {/* Avanzamento big */}
            <div style={{ display:"flex", gap:12, marginBottom:16 }}>
              <div style={{ flex:1, background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}`, textAlign:"center" }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:38, fontWeight:800, color:avanzTot>80?theme.success:theme.accent }}>{avanzTot}%</div>
                <div style={{ fontSize:11, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>
                  {lavori.length>0?"avanz. da ore":"avanz. manuale"}
                </div>
              </div>
              <div style={{ flex:1, background:theme.bg, borderRadius:10, padding:14, textAlign:"center", border:`1px solid ${theme.border}` }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontSize:22, fontWeight:800, color:"#4e9af5" }}>{oreTotEff.toFixed(1)}h</div>
                <div style={{ fontSize:11, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>ore effettive</div>
                <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>su {oreTotPrev.toFixed(1)}h previste</div>
              </div>
            </div>

            <div className="progress-bar" style={{ height:10, marginBottom:14 }}>
              <div className="progress-fill" style={{ width:`${avanzTot}%`, background:avanzTot>80?theme.success:theme.accent }} />
            </div>

            {/* Costi */}
            {(() => {
              const budgetMatTot = lavori.reduce((s,l)=>{ const qtaM=(l.qtaMat&&parseFloat(l.qtaMat)>0)?parseFloat(l.qtaMat):parseFloat(l.qta)||0; return s+(parseFloat(l.costoMat)||0)*qtaM; },0);
              const sforaOre = oreTotEff > oreTotPrev && oreTotPrev > 0;
              const sforaMat = totBolleImporto > budgetMatTot && budgetMatTot > 0;
              return [
                ["Importo preventivo", "€ "+importo.toLocaleString("it-IT"), theme.text, false],
                ["Budget materiali prev.", "€ "+budgetMatTot.toFixed(0), "#3ecf8e", false],
                ["Bolle materiali reali", "€ "+totBolleImporto.toLocaleString("it-IT"), sforaMat?"#f06060":"#f5a623", sforaMat],
                ["Ore previste / reali", oreTotPrev.toFixed(1)+"h / "+oreTotEff.toFixed(1)+"h", sforaOre?"#f06060":theme.text, sforaOre],
                ["Extra cantiere", totExtraImporto>0?"€ "+totExtraImporto.toLocaleString("it-IT"):"—", "#f59e0b", false],
              ].map(([k,v,col,alert]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                  <span style={{ color:theme.textMuted }}>{k}{alert?" ⚠️":""}</span>
                  <span style={{ fontWeight:600, color:col }}>{v}</span>
                </div>
              ));
            })()}

            {/* FIX_AVANZ_IBRIDO_v1 - avanzamento mostrato al cliente: auto di default, correggibile a mano */}
            {(() => {
              const autoCalc = (c.lavorazioni && c.lavorazioni.length > 0) ? avanzamentoCalcolato(c) : null;
              const overrideSet = (c.avanzamentoManuale === 0) || (c.avanzamentoManuale !== '' && c.avanzamentoManuale !== null && c.avanzamentoManuale !== undefined && !isNaN(parseFloat(c.avanzamentoManuale)));
              const baseVal = overrideSet ? Math.round(parseFloat(c.avanzamentoManuale)||0) : (autoCalc !== null ? autoCalc : (c.avanzamento||0));
              const editVal = avanzamentoEdit[cid] ?? baseVal;
              return (
                <div style={{ marginTop:14, padding:"12px 14px", background:theme.bg, borderRadius:10, border:`1px solid ${theme.border}` }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <span style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, fontWeight:600 }}>✎ Avanzamento mostrato al cliente</span>
                    <span style={{ fontSize:11, fontWeight:600, color: overrideSet ? theme.accent : theme.textDim }}>{overrideSet ? "a mano" : "automatico"}</span>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <input type="range" min="0" max="100" step="1" value={editVal}
                      onChange={e=>setAvanzamentoEdit({...avanzamentoEdit,[cid]:parseInt(e.target.value)})}
                      style={{ flex:1, accentColor:"#f5a623" }} />
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:editVal>80?theme.success:theme.accent, minWidth:50, textAlign:"right" }}>{editVal}%</span>
                    <button className="btn btn-primary btn-sm" onClick={()=>salvaAvanzCantiere(cid, editVal)}>Salva</button>
                    {overrideSet && <button className="btn btn-ghost btn-sm" onClick={()=>salvaAvanzCantiere(cid, null)}>↺ Auto</button>}
                  </div>
                  {autoCalc !== null && (
                    <div style={{ fontSize:11, color:theme.textDim, marginTop:6 }}>
                      Calcolo automatico dalle lavorazioni: <b>{autoCalc}%</b>{overrideSet ? " — ora mostri il valore manuale" : " — mostrato al cliente"}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <FotoCantiereBtn cant={c} assenze={assenzeDB} />
        {/* FIX_VISTA_CLIENTE_v1 - link pubblico cliente */}
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, flexWrap:"wrap" }}>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14 }}>🔗 Link cliente</div>
              <div style={{ fontSize:12, color:theme.textDim }}>Pagina pubblica con avanzamento e tempo, senza costi.</div>
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>{/* FIX_LINK_RUOLI_v1 */}
              <button className="btn btn-primary" disabled={generandoLink} onClick={()=>generaLinkCliente(cid,'cliente')} style={{ padding:"8px 14px" }}>👤 Cliente</button>
              <button className="btn btn-primary" disabled={generandoLink} onClick={()=>generaLinkCliente(cid,'tecnico')} style={{ padding:"8px 14px" }}>📐 Tecnico/DL</button>
              <button className="btn btn-primary" disabled={generandoLink} onClick={()=>generaLinkCliente(cid,'amministratore')} style={{ padding:"8px 14px" }}>🏢 Amministratore</button>
            </div>
          </div>
          {['cliente','tecnico','amministratore'].map(ruolo => {
            const u = linkCliente[cid + '_' + ruolo];
            if (!u) return null;
            const etich = ruolo==='cliente' ? '👤 Cliente' : ruolo==='tecnico' ? '📐 Tecnico/DL' : '🏢 Amministratore';
            return (
              <div key={ruolo} style={{ marginTop:12 }}>
                <div style={{ fontSize:12, color:theme.textDim, marginBottom:4 }}>{etich}</div>
                <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                  <input readOnly value={u} onClick={e=>e.target.select()} style={{ flex:1, minWidth:200, padding:"8px 10px", borderRadius:8, border:`1px solid ${theme.border}`, background:theme.bg, color:theme.text, fontSize:13 }} />
                  <button onClick={()=>{ if(navigator.clipboard) navigator.clipboard.writeText(u); }} style={{ padding:"8px 12px", borderRadius:8, border:`1px solid ${theme.accent}`, background:"none", color:theme.accent, cursor:"pointer", fontSize:13 }}>Copia</button>
                  <a href={u} target="_blank" rel="noreferrer" style={{ padding:"8px 12px", borderRadius:8, background:theme.accent, color:"#fff", textDecoration:"none", fontSize:13 }}>Apri</a>
                </div>
              </div>
            );
          })}
        </div>
        {/* LAVORAZIONI con avanzamento da ore */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">🔨 Lavorazioni</div>
            <div style={{ fontSize:12, color:theme.textMuted }}>
              {lavori.length>0 ? `${lavori.length} lavorazioni · ${oreTotEff.toFixed(1)}h/${oreTotPrev.toFixed(1)}h` : "Nessuna lavorazione da preventivo"}
            </div>
          </div>

          {lavori.length === 0 && (
            <div style={{ padding:"16px 0", textAlign:"center" }}>
              <div style={{ fontSize:13, color:theme.textDim, marginBottom:16 }}>
                Nessuna lavorazione collegata. Le lavorazioni appaiono automaticamente se il cantiere è stato creato da un preventivo accettato.
              </div>
              <div style={{ fontSize:12, color:theme.textDim, marginBottom:12 }}>
                Per cantieri esistenti usa "⚡ Aggiungi lavorazione extra" qui sotto.
              </div>
            </div>
          )}

          {lavoriConAvanz.map(l => {
            /* NUMERO_LAV_v1 */
            const numLav = (
              <span style={{ display:"inline-block", minWidth:26, textAlign:"center", fontFamily:"'Syne',sans-serif",
                fontWeight:800, fontSize:11, color:theme.accent, background:theme.accentSoft,
                borderRadius:5, padding:"1px 5px", marginRight:7, verticalAlign:"middle" }}
                title={l.numVoce ? ("Voce n. "+l.numVoce+" del preventivo \u00b7 posizione #"+((l.i??0)+1)) : ("Posizione #"+((l.i??0)+1))}
                >{l.numVoce || ("#"+((l.i??0)+1))}</span>
            );
            const completata = l.perc >= 100;
            const inCorso = l.perc>0 && !completata;
            const fotoKey = `${cid}-${l.i}`;
            const foteLav = fotoLav[fotoKey]||[];
            const oreRestanti = Math.max(0, l.orePreviste - l.oreEffettive);
            return (
              <div key={l.i} style={{ padding:"16px 0", borderBottom:`1px solid ${theme.border}44` }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4, flexWrap:"wrap" }}>
                      {numLav}<span style={{ fontSize:14, fontWeight:600, color:l.perc===0?theme.textDim:theme.text }}>{l.desc}</span>
                      {l.gruppo && <span style={{ fontSize:11, color:theme.accent, background:theme.accentSoft, borderRadius:4, padding:"1px 8px" }}>{l.gruppo}</span>}
                      <span className={`badge ${completata?"badge-success":inCorso?"badge-warning":"badge-muted"}`}>
                        {completata?"✓ Completata":inCorso?"In corso":"Da fare"}
                      </span>
                      {/* FIX_SPOSTA_ORE_SEL2_v1: pulsante sposta ore (conta per indice OPPURE nome) */}
                      {(() => {
                        const oreReg = assenzeDB.filter(a => a.tipo==="lavorato" && (a.cantiereId===cid||(a.cantiere||"")===(c.nome||"")) && (trovaIdxLav(a, c.lavorazioni /* LAV_BY_ID_v3 */)===l.i)).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
                        if (oreReg <= 0) return null;
                        return (
                          <button title={"Sposta le "+oreReg+"h su un'altra lavorazione"}
                            onClick={()=>{ setEditLavCantiere({ cid, idx:l.i, desc:l.desc, oreRegistrate:oreReg }); setSpostaDestIdx(""); setSpostaSelezione([]); setSpostaOreModal(true); }}
                            style={{ marginLeft:6, padding:"4px 8px", borderRadius:6, background:"#4e9af522", border:"1px solid #4e9af5", color:"#4e9af5", cursor:"pointer", fontSize:12 }}>
                            🔀 Sposta ore
                          </button>
                        );
                      })()}
                      {/* FIX: bottone elimina voce - vista titolare (renderDettaglio) */}
                      {(() => {
                        const oreRegistrate = assenzeDB.filter(a => a.tipo==="lavorato" && (a.cantiereId===cid||(a.cantiere||"")===(c.nome||"")) && (a.lavorazioneIdx===l.i||a.lavorazione===l.desc||trovaIdxLav(a, c.lavorazioni /* LAV_BY_ID_v3 */)===l.i)).length; // FIX_CESTINO_ORE_NOME_v1: conta per indice OPPURE nome
                        const haOre = oreRegistrate > 0;
                        return (
                          <button
                            title={haOre ? `Impossibile eliminare: ${oreRegistrate} ore registrate. Sposta prima le ore su un'altra voce dalla vista operaio.` : "Elimina questa voce dal cantiere"}
                            disabled={haOre}
                            onClick={async () => {
                              if (!window.confirm(`Eliminare definitivamente la voce "${l.desc}" dal cantiere?\n\nQuesta operazione non si puo' annullare.`)) return;
                              try {
                                const nuoveLav = (c.lavorazioni || []).filter((_,idx) => idx !== l.i);
                                await updateDoc(doc(db, "cantieri", cid), { lavorazioni: nuoveLav, updatedAt: new Date().toISOString() });
                                alert("Voce eliminata.");
                              } catch(e) { alert("Errore: "+e.message); }
                            }}
                            style={{
                              padding:"4px 9px", borderRadius:8,
                              background: haOre ? "#33333344" : "#f0606022",
                              border: `1px solid ${haOre ? "#666" : "#f06060"}`,
                              color: haOre ? "#666" : "#f06060",
                              cursor: haOre ? "not-allowed" : "pointer",
                              fontSize:13,
                            }}
                          >🗑️</button>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize:12, color:theme.textDim, marginBottom:10 }}>
                      {l.qta} {l.um} · {l.minuti} min/{l.um} · {l.orePreviste.toFixed(1)}h previste · mat. prev. € {l.budgetMat.toFixed(0)}
                    </div>

                    {/* Alert sforamento */}
                    {(l.alertOre || l.alertMat) && (
                      <div style={{ marginBottom:10, padding:"8px 12px", borderRadius:8, background:"#1a0a0a", border:"1px solid #f0606044", fontSize:12, color:"#f06060", display:"flex", gap:12, flexWrap:"wrap" }}>
                        ⚠️ Sforamento:
                        {l.alertOre && <span>⏱ Ore: {l.oreEffettive.toFixed(1)}h su {l.orePreviste.toFixed(1)}h previste (+{(l.oreEffettive-l.orePreviste).toFixed(1)}h)</span>}
                        {l.alertMat && <span>🧱 Materiali: € {l.matReale.toFixed(0)} su € {l.budgetMat.toFixed(0)} previsti (+€ {(l.matReale-l.budgetMat).toFixed(0)})</span>}
                      </div>
                    )}

                    {/* Barra ORE */}
                    <div style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:l.alertOre?"#f06060":theme.textDim, marginBottom:3 }}>
                        <span>⏱ Ore lavorate {l.alertOre?"⚠️":""}</span>
                        <span style={{ fontWeight:600, color:l.alertOre?"#f06060":l.percOre>=80?theme.accent:theme.textDim }}>
                          {l.oreEffettive.toFixed(1)}h / {l.orePreviste.toFixed(1)}h ({l.percOre}%)
                        </span>
                      </div>
                      <div style={{ position:"relative", height:8, background:theme.border, borderRadius:4 }}>
                        <div style={{ position:"absolute", left:0, top:0, height:"100%", borderRadius:4,
                          width:`${Math.min(100,l.percOre)}%`,
                          background:l.alertOre?"#f06060":l.percOre>=80?"#f5a623":"#4e9af5",
                          transition:"width 0.4s" }} />
                        {/* Marcatore 100% */}
                        <div style={{ position:"absolute", left:"100%", top:-2, width:2, height:12, background:theme.textDim, borderRadius:1 }} />
                      </div>
                    </div>

                    {/* Barra MATERIALI */}
                    <div style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:l.alertMat?"#f06060":theme.textDim, marginBottom:3 }}>
                        <span>🧱 Materiali (bolle) {l.alertMat?"⚠️":""}</span>
                        <span style={{ fontWeight:600, color:l.alertMat?"#f06060":l.percMat>=80?"#f59e0b":theme.textDim }}>
                          € {l.matReale.toFixed(0)} / € {l.budgetMat.toFixed(0)} ({l.percMat}%)
                        </span>
                      </div>
                      <div style={{ position:"relative", height:8, background:theme.border, borderRadius:4 }}>
                        <div style={{ position:"absolute", left:0, top:0, height:"100%", borderRadius:4,
                          width:`${Math.min(100,l.percMat)}%`,
                          background:l.alertMat?"#f06060":l.percMat>=80?"#f59e0b":"#3ecf8e",
                          transition:"width 0.4s" }} />
                        <div style={{ position:"absolute", left:"100%", top:-2, width:2, height:12, background:theme.textDim, borderRadius:1 }} />
                      </div>
                      {l.budgetMat===0 && <div style={{ fontSize:11, color:theme.textDim, marginTop:3 }}>Budget materiali non definito nel preventivo</div>}
                    </div>

                    {/* FIX_AVANZ_MANUALE_v1 - % fatto manuale (override avanzamento) */}
                    {(() => {
                      const lavM = (c.lavorazioni || [])[l.i] || {};
                      const amKey = `${cid}-${l.i}`;
                      const amSaved = (lavM.avanzManuale === 0 || lavM.avanzManuale) ? String(lavM.avanzManuale) : "";
                      const amVal = avanzManEdit[amKey] !== undefined ? avanzManEdit[amKey] : amSaved;
                      const attivo = amSaved !== "";
                      return (
                        <div style={{ marginBottom:10, padding:"8px 12px", borderRadius:8, background:"#0f1117", border:`1px solid ${theme.border}` }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                            <span style={{ fontSize:12, fontWeight:600, color: attivo ? theme.accent : theme.textDim }}>✋ % fatto (manuale)</span>
                            <input type="number" min="0" max="100" inputMode="numeric" placeholder="auto"
                              value={amVal}
                              onChange={e=>setAvanzManEdit({ ...avanzManEdit, [amKey]: e.target.value })}
                              style={{ width:72, padding:"5px 8px", borderRadius:6, border:`1px solid ${theme.border}`, background:"#0f1117", color:theme.text, fontSize:13 }} />
                            <span style={{ fontSize:12, color:theme.textDim }}>%</span>
                            <button className="btn btn-primary btn-sm"
                              onClick={async ()=>{
                                const raw = String(amVal).trim().replace(",", ".");
                                let v = null;
                                if (raw !== "") v = Math.max(0, Math.min(100, Math.round(parseFloat(raw)||0)));
                                try { const ok = await salvaAvanzManuale(cid, l.i, v); if (ok) { const cp = { ...avanzManEdit }; delete cp[amKey]; setAvanzManEdit(cp); } }
                                catch(err){ alert("Errore: " + err.message); }
                              }}>Salva</button>
                            {attivo && (
                              <button className="btn btn-ghost btn-sm"
                                onClick={async ()=>{ try { const ok = await salvaAvanzManuale(cid, l.i, null); if (ok) { const cp = { ...avanzManEdit }; delete cp[amKey]; setAvanzManEdit(cp); } } catch(err){ alert("Errore: " + err.message); } }}>↺ Auto</button>
                            )}
                          </div>
                          <div style={{ fontSize:11, color:theme.textDim, marginTop:4 }}>
                            {attivo ? "Impostato a mano (sovrascrive il calcolo automatico)" : "Vuoto = automatico (ore/spese). Utile per lavori esterni: ponteggi, subappalti."}
                          </div>
                        </div>
                      );
                    })()}
                    {/* FIX_SPESE_REALI_v1 - spese esterne reali sulla lavorazione */}
                    {(() => {
                      const lavR = (c.lavorazioni || [])[l.i] || {};
                      const spesaPrev = (parseFloat(lavR.spesaEsterna)||0) * (parseFloat(lavR.qta)||0);
                      const spese = Array.isArray(lavR.speseReali) ? lavR.speseReali : [];
                      const spesaReale = spese.reduce((s,x)=>s+(parseFloat(x.importo)||0),0);
                      const haQualcosa = spesaPrev > 0 || spese.length > 0;
                      const spKey = `${cid}-${l.i}`;
                      const fmtE = (n) => "EUR " + Math.round(n).toLocaleString("it-IT");
                      const fmtD = (d) => d ? String(d).split("-").reverse().join("/") : "";
                      const apri = () => { setSpesaForm(nuovaSpesaForm()); setSpesaModalKey(spKey); };
                      const inSt = { width:"100%", boxSizing:"border-box", padding:"8px 10px", borderRadius:8, border:`1px solid ${theme.border}`, background:theme.bg, color:theme.text, fontSize:14 };
                      const inRosso = spesaPrev > 0 && spesaReale > spesaPrev;
                      const sfor = spesaPrev > 0 ? Math.round((spesaReale - spesaPrev)/spesaPrev*100) : null;
                      return (
                        <div style={{ marginBottom:10 }}>
                          {!haQualcosa ? (
                            <button onClick={apri} style={{ background:"none", border:`1px dashed ${theme.border}`, color:theme.textMuted, cursor:"pointer", fontSize:12, padding:"4px 10px", borderRadius:8 }}>+ Spesa esterna</button>
                          ) : (
                            <div style={{ background:theme.bg, borderRadius:8, padding:"10px 12px", border:`1px solid ${theme.border}` }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline", flexWrap:"wrap", gap:6 }}>
                                <span style={{ fontSize:11, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>Spese esterne</span>
                                {spesaPrev > 0 && <span style={{ fontSize:12, color:theme.textMuted }}>Prevista: <b>{fmtE(spesaPrev)}</b></span>}
                              </div>
                              {(spesaPrev > 0 || spesaReale > 0) && (
                                <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:4 }}>
                                  <span style={{ fontSize:13 }}>Reale: <b style={{ color: inRosso ? theme.danger : theme.success }}>{fmtE(spesaReale)}</b></span>
                                  {sfor !== null && spese.length > 0 && <span style={{ fontSize:12, fontWeight:700, color: inRosso ? theme.danger : theme.success }}>{sfor > 0 ? "+" : ""}{sfor}%</span>}
                                </div>
                              )}
                              {spese.length > 0 && (
                                <div style={{ marginTop:8, display:"flex", flexDirection:"column", gap:4 }}>
                                  {spese.map((sp) => (
                                    <div key={sp.id} onClick={() => { setSpesaForm({ editId:sp.id, data:sp.data||"", fornitore:sp.fornitore||"", documento:sp.documento||"", importo:String(sp.importo||""), note:sp.note||"", pagato:!!sp.pagato, dataPagamento:sp.dataPagamento||"", metodo:sp.metodo||"bonifico" }); setSpesaModalKey(spKey); }} style={{ fontSize:12, color:theme.textMuted, cursor:"pointer", padding:"5px 8px", borderRadius:6, border:`1px solid ${theme.border}`, display:"flex", justifyContent:"space-between", gap:8 }}>
                                      <span>{fmtD(sp.data)} - {sp.fornitore||"-"}{sp.documento ? " - "+sp.documento : ""}</span>
                                      <span style={{ whiteSpace:"nowrap" }}><b>{fmtE(parseFloat(sp.importo)||0)}</b> {sp.pagato ? "ok" : "att."}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <button onClick={apri} style={{ marginTop:8, background:"none", border:`1px dashed ${theme.accent}66`, color:theme.accent, cursor:"pointer", fontSize:12, padding:"5px 12px", borderRadius:8 }}>+ Aggiungi spesa</button>
                            </div>
                          )}
                          {spesaModalKey === spKey && (
                            <div onClick={()=>!salvandoSpesa && setSpesaModalKey(null)} style={{ position:"fixed", inset:0, background:"#00000099", display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999, padding:16 }}>
                              <div onClick={e=>e.stopPropagation()} style={{ background: theme.card || theme.bg, borderRadius:14, padding:20, width:"100%", maxWidth:440, maxHeight:"90vh", overflowY:"auto", border:`1px solid ${theme.border}`, boxShadow:"0 10px 40px #000000aa" }}>
                                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, marginBottom:4 }}>{spesaForm.editId ? "Modifica spesa" : "Nuova spesa esterna"}</div>
                                <div style={{ fontSize:12, color:theme.textDim, marginBottom:16 }}>{l.desc}</div>
                                {[["Data","data","date"],["Fornitore","fornitore","text"],["Documento (es. Fattura 23/2026)","documento","text"],["Importo EUR","importo","number"]].map(([label,campo,tipo]) => (
                                  <div key={campo} style={{ marginBottom:10 }}>
                                    <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>{label}</div>
                                    <input type={tipo} style={inSt} value={spesaForm[campo]||""} onChange={e=>setSpesaForm({...spesaForm,[campo]:e.target.value})} />
                                  </div>
                                ))}
                                <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, cursor:"pointer", marginBottom:10 }}>
                                  <input type="checkbox" checked={!!spesaForm.pagato} onChange={e=>setSpesaForm({...spesaForm,pagato:e.target.checked})} /> Pagato
                                </label>
                                {spesaForm.pagato && (
                                  <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>Data pagamento</div>
                                      <input type="date" style={inSt} value={spesaForm.dataPagamento||""} onChange={e=>setSpesaForm({...spesaForm,dataPagamento:e.target.value})} />
                                    </div>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>Metodo</div>
                                      <select style={inSt} value={spesaForm.metodo||"bonifico"} onChange={e=>setSpesaForm({...spesaForm,metodo:e.target.value})}>
                                        <option value="bonifico">Bonifico</option>
                                        <option value="contanti">Contanti</option>
                                        <option value="assegno">Assegno</option>
                                        <option value="altro">Altro</option>
                                      </select>
                                    </div>
                                  </div>
                                )}
                                <div style={{ marginBottom:16 }}>
                                  <div style={{ fontSize:11, color:theme.textMuted, marginBottom:4 }}>Note</div>
                                  <textarea rows={2} style={{...inSt, resize:"vertical"}} value={spesaForm.note||""} onChange={e=>setSpesaForm({...spesaForm,note:e.target.value})} />
                                </div>
                                <div style={{ display:"flex", gap:8, justifyContent:"space-between", alignItems:"center" }}>
                                  {spesaForm.editId ? (
                                    <button disabled={salvandoSpesa} onClick={async ()=>{ if (!window.confirm("Eliminare questa spesa?")) return; setSalvandoSpesa(true); try { const nuove = spese.filter(x=>x.id!==spesaForm.editId); const ok = await salvaSpeseReali(cid, l.i, nuove); if (ok) setSpesaModalKey(null); } catch(e){ alert("Errore: "+e.message); } finally { setSalvandoSpesa(false); } }} style={{ background:"none", border:`1px solid ${theme.danger}`, color:theme.danger, borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:13 }}>Elimina</button>
                                  ) : <span />}
                                  <div style={{ display:"flex", gap:8 }}>
                                    <button disabled={salvandoSpesa} onClick={()=>setSpesaModalKey(null)} style={{ background:"none", border:`1px solid ${theme.border}`, color:theme.textMuted, borderRadius:8, padding:"8px 14px", cursor:"pointer", fontSize:13 }}>Annulla</button>
                                    <button disabled={salvandoSpesa} onClick={async ()=>{ const imp = parseFloat(String(spesaForm.importo).replace(",","."))||0; if (imp <= 0) { alert("Inserisci un importo valido."); return; } setSalvandoSpesa(true); try { const nuove = [...spese]; const rec = { id: spesaForm.editId || ("sp_"+Date.now().toString(36)+Math.random().toString(36).slice(2,6)), data:spesaForm.data||"", fornitore:spesaForm.fornitore||"", documento:spesaForm.documento||"", importo:imp, note:spesaForm.note||"", pagato:!!spesaForm.pagato, dataPagamento:spesaForm.pagato?(spesaForm.dataPagamento||""):"", metodo:spesaForm.metodo||"bonifico" }; if (spesaForm.editId) { const pos = nuove.findIndex(x=>x.id===spesaForm.editId); if (pos>=0) nuove[pos]=rec; else nuove.push(rec); } else nuove.push(rec); const ok = await salvaSpeseReali(cid, l.i, nuove); if (ok) setSpesaModalKey(null); } catch(e){ alert("Errore: "+e.message); } finally { setSalvandoSpesa(false); } }} style={{ background:theme.accent, border:"none", color:"#fff", borderRadius:8, padding:"8px 16px", cursor:"pointer", fontSize:13, fontWeight:600 }}>{salvandoSpesa ? "Salvo..." : "Salva"}</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {/* Foto locali + foto salvate su Firebase */}
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                      {/* Foto da Firebase (tipo="foto") */}
                      {assenzeDB.filter(a => a.tipo==="foto" && a.cantiereId===cid && trovaIdxLav(a, ((cantieriDB.find(x=>(x._id||x.id)===cid)||{}).lavorazioni) /* LAV_BY_ID_v5 */)===l.i)
                        .map(docAssenza => (docAssenza.foto||[]).map((f,fi) => (
                          <div key={docAssenza._id+"-"+fi} style={{ position:"relative" }}>
                            <img src={f.url} alt="" style={{ width:52, height:52, objectFit:"cover", borderRadius:8, border:`1px solid ${theme.border}`, cursor:"pointer" }}
                              onClick={()=>{ const u=f.full||f.base64||f.url; if(u)window.open(u,"_blank"); }} />
                            <button
                              onClick={async(e)=>{
                                e.stopPropagation();
                                if (!window.confirm("Eliminare questa foto?")) return; fotoCantiereElimina(f);
                                try {
                                  const nuoveFoto = (docAssenza.foto||[]).filter((_,j)=>j!==fi);
                                  if (nuoveFoto.length===0) { await deleteDoc(doc(db,"assenze",docAssenza._id)); }
                                  else { await updateDoc(doc(db,"assenze",docAssenza._id),{foto:nuoveFoto}); }
                                } catch(err){ alert("❌ "+err.message); }
                              }}
                              style={{ position:"absolute", top:-5, right:-5, background:theme.danger, border:"none", borderRadius:"50%", width:18, height:18, color:"#fff", cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                            <div style={{ display:"flex", gap:1, marginTop:3, justifyContent:"center" }}>{/* FIX_FOTO_VIS_v1 */}
                              <button title="Cliente" onClick={async(e)=>{ e.stopPropagation(); try { const nuove=(docAssenza.foto||[]).map((ff,j)=>j===fi?{...ff,vCli:!ff.vCli}:ff); await updateDoc(doc(db,"assenze",docAssenza._id),{foto:nuove}); } catch(err){ alert("Errore: "+err.message); } }} style={{ width:18, height:16, borderRadius:4, border:`1px solid ${theme.border}`, cursor:"pointer", fontSize:10, padding:0, background:(f.vCli?theme.accent:"transparent"), opacity:(f.vCli?1:0.4) }}>👤</button>
                              <button title="Tecnico/DL" onClick={async(e)=>{ e.stopPropagation(); try { const nuove=(docAssenza.foto||[]).map((ff,j)=>j===fi?{...ff,vTec:!ff.vTec}:ff); await updateDoc(doc(db,"assenze",docAssenza._id),{foto:nuove}); } catch(err){ alert("Errore: "+err.message); } }} style={{ width:18, height:16, borderRadius:4, border:`1px solid ${theme.border}`, cursor:"pointer", fontSize:10, padding:0, background:(f.vTec?theme.accent:"transparent"), opacity:(f.vTec?1:0.4) }}>📐</button>
                              <button title="Amministratore" onClick={async(e)=>{ e.stopPropagation(); try { const nuove=(docAssenza.foto||[]).map((ff,j)=>j===fi?{...ff,vAmm:!ff.vAmm}:ff); await updateDoc(doc(db,"assenze",docAssenza._id),{foto:nuove}); } catch(err){ alert("Errore: "+err.message); } }} style={{ width:18, height:16, borderRadius:4, border:`1px solid ${theme.border}`, cursor:"pointer", fontSize:10, padding:0, background:(f.vAmm?theme.accent:"transparent"), opacity:(f.vAmm?1:0.4) }}>🏢</button>
                            </div>
                          </div>
                        ))).flat()
                      }
                      {/* Foto locali */}
                      {foteLav.map((f,fi) => (
                        <div key={fi} style={{ position:"relative" }}>
                          <img src={f.url} alt="" style={{ width:52, height:52, objectFit:"cover", borderRadius:8, border:`1px solid ${theme.border}`, cursor:"pointer" }}
                            onClick={()=>{ const u=f.full||f.base64||f.url; if(u)window.open(u,"_blank"); }} />
                          <button onClick={()=>setFotoLav({...fotoLav,[fotoKey]:foteLav.filter((_v,j)=>j!==fi)})}
                            style={{ position:"absolute", top:-5, right:-5, background:theme.danger, border:"none", borderRadius:"50%", width:18, height:18, color:"#fff", cursor:"pointer", fontSize:11, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                        </div>
                      ))}
                      <label style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, border:`1px solid ${theme.accent}66`, cursor:"pointer", fontSize:12, color:theme.accent, background:"#1a1500", fontWeight:500 }}>
                        📷 {foteLav.length>0?`${foteLav.length} foto`:"Fotocamera"}
                        <input type="file" accept="image/*" style={{ display:"none" }}
                          onChange={async e=>{ /* FIX_FOTO_SAVE_v1 */ const files=Array.from(e.target.files); const converted=await Promise.all(files.map(f=>new Promise(resolve=>{ const reader=new FileReader(); reader.onload=ev=>{ const img=new Image(); img.onload=()=>{ const canvas=document.createElement("canvas"); const MAX=600; let w=img.width,h=img.height; if(w>MAX){h=Math.round(h*MAX/w);w=MAX;} canvas.width=w;canvas.height=h; canvas.getContext("2d").drawImage(img,0,0,w,h); resolve({nome:f.name,url:canvas.toDataURL("image/jpeg",0.65)}); }; img.src=ev.target.result; }; reader.readAsDataURL(f); }))); setFotoLav({...fotoLav,[fotoKey]:[...foteLav,...converted]}); try{ await addDoc(collection(db,"assenze"),{ operaio:"Titolare", data:new Date().toLocaleDateString("it-IT"), cantiere:((cantieriDB.find(x=>(x._id||x.id)===cid)||{}).nome)||"" /* FIX_FOTO_SAVE_v2 */, cantiereId:cid, lavorazione:l.desc||"", lavorazioneIdx:l.i, lavorazioneId:(l.id!=null?l.id:null) /* LAV_BY_ID_v5 */, tipo:"foto", ore:0, foto:converted, createdAt:new Date().toISOString() }); }catch(err){ alert("Salvataggio foto fallito: "+((err&&err.message)||err)); } }} />
                      </label>
                      <label style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, border:`1px solid ${theme.border}`, cursor:"pointer", fontSize:12, color:theme.textMuted, background:theme.bg, fontWeight:500 }}>
                        🖼️ Libreria
                        <input type="file" accept="image/*" multiple style={{ display:"none" }}
                          onChange={async e=>{ /* FIX_FOTO_SAVE_v1 */ const files=Array.from(e.target.files); const converted=await Promise.all(files.map(f=>new Promise(resolve=>{ const reader=new FileReader(); reader.onload=ev=>{ const img=new Image(); img.onload=()=>{ const canvas=document.createElement("canvas"); const MAX=600; let w=img.width,h=img.height; if(w>MAX){h=Math.round(h*MAX/w);w=MAX;} canvas.width=w;canvas.height=h; canvas.getContext("2d").drawImage(img,0,0,w,h); resolve({nome:f.name,url:canvas.toDataURL("image/jpeg",0.65)}); }; img.src=ev.target.result; }; reader.readAsDataURL(f); }))); setFotoLav({...fotoLav,[fotoKey]:[...foteLav,...converted]}); try{ await addDoc(collection(db,"assenze"),{ operaio:"Titolare", data:new Date().toLocaleDateString("it-IT"), cantiere:((cantieriDB.find(x=>(x._id||x.id)===cid)||{}).nome)||"" /* FIX_FOTO_SAVE_v2 */, cantiereId:cid, lavorazione:l.desc||"", lavorazioneIdx:l.i, lavorazioneId:(l.id!=null?l.id:null) /* LAV_BY_ID_v5 */, tipo:"foto", ore:0, foto:converted, createdAt:new Date().toISOString() }); }catch(err){ alert("Salvataggio foto fallito: "+((err&&err.message)||err)); } }} />
                      </label>
                    </div>
                  </div>

                  {/* Costi colonna destra */}
                  <div style={{ textAlign:"right", flexShrink:0, minWidth:140 }}>
                    <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", marginBottom:4 }}>Costi manodopera</div>
                    <div style={{ display:"flex", gap:10, justifyContent:"flex-end", flexWrap:"wrap" }}>
                      <div><div style={{ fontSize:10, color:theme.textDim }}>Previsto</div><div style={{ fontSize:13, color:theme.textMuted }}>€ {Math.round(l.costoLavPrev)}</div></div>
                      <div><div style={{ fontSize:10, color:theme.textDim }}>Effettivo</div><div style={{ fontSize:14, fontWeight:700, color:l.costoLavEff>l.costoLavPrev?"#f59e0b":theme.success }}>€ {Math.round(l.costoLavEff)}</div></div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Extra lavorazioni titolare */}
          {extra.length>0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:"#f59e0b", marginBottom:8, fontWeight:600 }}>⚡ Extra titolare (fuori preventivo)</div>
              {extra.map(l => (
                <div key={l.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 12px", background:"#1a1500", border:"1px solid #f59e0b33", borderRadius:8, marginBottom:6 }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500 }}>{l.desc}</div>
                    {l.note && <div style={{ fontSize:12, color:theme.textDim }}>{l.note}</div>}
                    <div style={{ fontSize:12, color:theme.textDim }}>{l.qta} {l.um} × € {l.prezzoUn}/{l.um}</div>
                  </div>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#f59e0b" }}>€ {((parseFloat(l.qta)||0)*(parseFloat(l.prezzoUn)||0)).toFixed(0)}</span>
                    <button onClick={()=>{ const nuovaLista=extra.filter(x=>x.id!==l.id); setLavExtra({...lavExtra,[cid]:nuovaLista}); salvaLavExtraFirebase(cid,nuovaLista); }} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer" }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── ATTIVITÀ EXTRA DAGLI OPERAI (da Firebase) ── */}
          {(() => {
            const extraOperai = assenzeDB.filter(a =>
              a.extra === true &&
              (a.cantiereId === cid || (a.cantiere||"").toLowerCase().trim() === (c.nome||"").toLowerCase().trim())
            );
            if (!extraOperai.length) return null;
            const totOreExtra = extraOperai.reduce((s,a) => s+(parseFloat(a.ore)||0), 0);
            return (
              <div style={{ marginTop:16, padding:"14px", background:"#12100a", borderRadius:10, border:"1px solid #f59e0b44" }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1.5, color:"#f59e0b", fontWeight:700 }}>
                    ⚡ Attività extra dagli operai ({extraOperai.length})
                  </div>
                  <div style={{ fontSize:12, color:"#f59e0b", fontWeight:700 }}>{totOreExtra.toFixed(1)}h totali</div>
                </div>
                {extraOperai.map((a, i) => {
                  const dp = dipendenti.find(d => d.nome === a.operaio);
                  return (
                    <div key={a._id||i} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 12px", background:theme.bg, borderRadius:8, marginBottom:6, border:`1px solid ${theme.border}` }}>
                      <div className="avatar" style={{ background:dp?.colore||"#888", width:28, height:28, fontSize:11, flexShrink:0 }}>
                        {(a.operaio||"?")[0]}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{a.lavorazione||"—"}</div>
                        <div style={{ fontSize:11, color:theme.textDim, marginTop:1 }}>
                          {a.operaio} · {a.data||"—"}
                        </div>
                      </div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:"#f59e0b" }}>
                        {parseFloat(a.ore)||0}h
                      </div>
                      {/* FIX_EXTRA_CID_v1: promuovi attivita extra a lavorazione vera (usa id certo del cantiere) */}
                      {(() => {
                        const _cid = c._id||c.id;
                        const nomeAtt = (a.lavorazione||"").trim();
                        const giaLav = (lavExtra[_cid]||[]).some(x => (x.desc||"").trim().toLowerCase() === nomeAtt.toLowerCase());
                        if (!nomeAtt) return null;
                        if (giaLav) return <span style={{ fontSize:11, color:"#3ecf8e" }}>✓ già in lista</span>;
                        return (
                          <button title="Crea una lavorazione vera da questa attività (poi ci aggiungi prezzo e materiale)"
                            onClick={()=>{
                              const nuovaLista=[...(lavExtra[_cid]||[]), { desc:nomeAtt, um:"cad", qta:1, prezzoUn:0, note:"Da attività operai", id:Date.now() }];
                              setLavExtra({...lavExtra,[_cid]:nuovaLista});
                              salvaLavExtraFirebase(_cid,nuovaLista);
                              alert("✅ Creata la lavorazione \""+nomeAtt+"\". Ora la trovi nella lista lavori: aggiungi prezzo e materiale.");
                            }}
                            style={{ padding:"5px 10px", borderRadius:6, background:"#f59e0b22", border:"1px solid #f59e0b", color:"#f59e0b", cursor:"pointer", fontSize:12, whiteSpace:"nowrap" }}>
                            ➕ Crea lavorazione
                          </button>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {showLavExtra && (
            <div style={{ marginTop:14, background:theme.bg, borderRadius:10, padding:14, border:"1px solid #f59e0b55" }}>
              <div style={{ fontSize:11, color:"#f59e0b", textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>⚡ Nuova lavorazione extra</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 70px 80px 90px", gap:8, marginBottom:8 }}>
                <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Descrizione</div><input style={inputSt} placeholder="es. Rimozione massetto" value={lavExtraForm.desc} onChange={e=>setLavExtraForm({...lavExtraForm,desc:e.target.value})} /></div>
                <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>U.M.</div><select style={inputSt} value={lavExtraForm.um} onChange={e=>setLavExtraForm({...lavExtraForm,um:e.target.value})}>{["mq","ml","cad","mc","h","kg"].map(u=><option key={u}>{u}</option>)}</select></div>
                <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Qtà</div><input style={inputSt} inputMode="decimal" placeholder="0" value={lavExtraForm.qta} onChange={e=>setLavExtraForm({...lavExtraForm,qta:e.target.value})} /></div>
                <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>€/um</div><input style={inputSt} inputMode="decimal" placeholder="0" value={lavExtraForm.prezzoUn} onChange={e=>setLavExtraForm({...lavExtraForm,prezzoUn:e.target.value})} /></div>
              </div>
              <div style={{ marginBottom:10 }}><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Note</div><input style={inputSt} placeholder="es. Richiesto dal cliente il 12/03" value={lavExtraForm.note} onChange={e=>setLavExtraForm({...lavExtraForm,note:e.target.value})} /></div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={()=>{ if(!lavExtraForm.desc)return; const nuovaLista=[...extra,{...lavExtraForm,id:Date.now()}]; setLavExtra({...lavExtra,[cid]:nuovaLista}); salvaLavExtraFirebase(cid,nuovaLista); setLavExtraForm({desc:"",um:"mq",qta:"",prezzoUn:"",note:""}); setShowLavExtra(false); }}>+ Aggiungi</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowLavExtra(false)}>Annulla</button>
              </div>
            </div>
          )}
          <div style={{ marginTop:14, display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn btn-ghost btn-sm" onClick={()=>setShowLavExtra(!showLavExtra)}>⚡ Aggiungi lavorazione extra</button>
          </div>
        </div>

        {/* FABBISOGNO PER LAVORAZIONE */}
        {(() => {
          const lavoriCant = c.lavorazioni || [];
          if (!lavoriCant.length) return null;
          // Per ogni lavorazione, calcola la lista materiali con qta = lavQta * incidenza
          const sezioni = lavoriCant
            .filter(l => !l.smaltimento && l.categoria)
            .map(l => {
              const subDati = dizionarioMateriali[l.categoria]?.sottocategorie?.[l.sottocategoria];
              const catDati = dizionarioMateriali[l.categoria];
              const materialiList = subDati?.materiali?.length ? subDati.materiali : (catDati?.materiali || []);
              const qta = parseFloat(l.qtaMat || l.qta) || 0;
              const umRef = l.qtaMat ? (l.umMat || l.um) : l.um;
              const materiali = materialiList
                .map(m => (typeof m === "string" ? { nome: m, incidenza: "", um: "cad", categoriaP: "" } : m))
                .filter(m => m.nome && parseFloat(m.incidenza) > 0)
                .map(m => ({
                  nome: m.nome,
                  qta: (qta * parseFloat(m.incidenza)).toFixed(2),
                  um: m.um || "cad",
                }));
              return {
                desc: l.desc || (catDati?.label || l.categoria),
                qtaLav: qta,
                umLav: umRef,
                materiali,
              };
            })
            .filter(s => s.materiali.length > 0);
          if (!sezioni.length) return null;
          return (
            <div className="card stampa-fabbisogno-area" style={{ marginBottom: 20, border: "1px solid #3ecf8e44" }}>
              <div className="stampa-fabbisogno-header-print" style={{ display: "none" }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>{c.nome || "Cantiere"}</div>
                <div style={{ fontSize: 12, color: "#666" }}>Fabbisogno materiali — generato il {new Date().toLocaleDateString("it-IT")}</div>
                <hr style={{ margin: "8px 0 12px 0" }} />
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }} className="stampa-fabbisogno-titlebar">
                <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15, color: "#3ecf8e", textTransform: "uppercase", letterSpacing: 1 }}>
                  📦 Fabbisogno materiali per lavorazione
                </div>
                <button onClick={() => {
                  const nomeCant = c.nome || "Cantiere";
                  const oggi = new Date().toLocaleDateString("it-IT");
                  let html = '<html><head><meta charset="utf-8"><title>Fabbisogno - ' + nomeCant + '</title>';
                  html += '<style>';
                  html += 'body{font-family:Arial,sans-serif;padding:24px;color:#222;max-width:800px;margin:0 auto;}';
                  html += 'h1{font-size:20px;margin:0 0 4px;}';
                  html += '.subtitle{font-size:12px;color:#666;margin-bottom:18px;border-bottom:1px solid #ccc;padding-bottom:10px;}';
                  html += 'h2{font-size:14px;color:#0a7d4f;margin:20px 0 8px;text-transform:uppercase;letter-spacing:0.5px;}';
                  html += '.qta-lav{font-weight:normal;color:#666;font-size:13px;}';
                  html += '.materiali{display:grid;grid-template-columns:1fr 1fr;gap:6px 20px;margin-bottom:6px;}';
                  html += '.row{display:flex;justify-content:space-between;padding:5px 10px;border:1px solid #eee;border-radius:4px;font-size:13px;}';
                  html += '.row .nome{color:#222;}';
                  html += '.row .qta{font-weight:700;color:#0a7d4f;}';
                  html += '.footer{margin-top:30px;padding-top:14px;border-top:1px solid #ccc;font-size:11px;color:#999;font-style:italic;}';
                  html += '@media print{button{display:none;}}';
                  html += '</style></head><body>';
                  html += '<h1>' + nomeCant + '</h1>';
                  html += '<div class="subtitle">Fabbisogno materiali per lavorazione &mdash; generato il ' + oggi + '</div>';
                  sezioni.forEach(function(s){
                    html += '<h2>' + s.desc + ' <span class="qta-lav">(' + s.qtaLav + ' ' + s.umLav + ')</span></h2>';
                    html += '<div class="materiali">';
                    s.materiali.forEach(function(m){
                      html += '<div class="row"><span class="nome">' + m.nome + '</span><span class="qta">' + m.qta + ' ' + m.um + '</span></div>';
                    });
                    html += '</div>';
                  });
                  html += '<div class="footer">Quantita calcolate dalle incidenze del dizionario &mdash; verifica prima dell\'acquisto.</div>';
                  html += '<div style="text-align:center;margin-top:30px;"><button onclick="window.print()" style="padding:10px 20px;background:#0a7d4f;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:14px;">Stampa o salva PDF</button></div>';
                  html += '</body></html>';
                  const w = window.open("", "_blank", "width=900,height=700");
                  if (!w) { alert("Il browser ha bloccato la nuova finestra. Abilita i popup per questo sito."); return; }
                  w.document.open();
                  w.document.write(html);
                  w.document.close();
                }} className="no-print" style={{ background: "transparent", border: "1px solid #3ecf8e44", color: "#3ecf8e", padding: "6px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                  🖨️ Stampa
                </button>
              </div>
              {sezioni.map((s, idx) => (
                <div key={idx} style={{ marginBottom: 16, paddingBottom: 14, borderBottom: idx < sezioni.length - 1 ? `1px solid ${theme.border}` : "none" }}>
                  <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 14, color: theme.accent, marginBottom: 8 }}>
                    {s.desc} <span style={{ color: theme.textMuted, fontWeight: 500, fontSize: 13 }}>({s.qtaLav} {s.umLav})</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 6 }}>
                    {s.materiali.map((m, mi) => (
                      <div key={mi} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: theme.bg, padding: "6px 10px", borderRadius: 6, border: `1px solid ${theme.border}` }}>
                        <span style={{ fontSize: 13, color: theme.text }}>{m.nome}</span>
                        <span style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 13, color: "#3ecf8e" }}>{m.qta} {m.um}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <div style={{ fontSize: 11, color: theme.textDim, marginTop: 6, fontStyle: "italic" }} className="no-print">
                💡 Quantità calcolate dalle incidenze del dizionario — verifica prima dell'acquisto.
              </div>
              <style>{`
                @media print {
                  html, body { background: white !important; height: auto !important; overflow: visible !important; }
                  body > *:not(.stampa-print-overlay) { display: none !important; }
                  .stampa-print-overlay { display: block !important; position: static !important; padding: 0 !important; margin: 0 !important; background: white !important; color: black !important; }
                  .stampa-print-overlay * { color: black !important; background: transparent !important; border-color: #ccc !important; box-shadow: none !important; }
                  .stampa-print-overlay .no-print { display: none !important; }
                  .stampa-print-overlay .stampa-fabbisogno-header-print { display: block !important; }
                  .stampa-print-overlay .stampa-fabbisogno-titlebar { border-bottom: 1px solid #ccc; padding-bottom: 8px; }
                  @page { margin: 1.5cm; size: A4; }
                }
              `}</style>
            </div>
          );
        })()}

        {/* BOLLE */}
        {(() => {
          const salvaRigaBolla = async (bollaId, rigaIdx, campo, valore) => { /* FIX_EDIT_BOLLA_RIGA_v1 */
            try {
              const bolla = bolleDB.find(b => b._id === bollaId);
              if (!bolla) { alert("Bolla non trovata"); return; }
              const righe = [...(bolla.righe||[])];
              if (!righe[rigaIdx]) { alert("Riga non trovata"); return; }
              righe[rigaIdx] = { ...righe[rigaIdx], [campo]: valore };
              const totale = righe.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
              await updateDoc(doc(db,"bolle",bollaId), { righe, totale, updatedAt:new Date().toISOString() });
              setEditBollaRiga(null);
            } catch(e){ alert("Errore: "+((e&&e.message)||e)); }
          };
          const bolleC = bolleDB.flatMap(b =>
            (b.righe||[])
              .map((r,_ri) => ({ ...r, _rigaIdx:_ri })) // FIX_BOLLA_RIGA_IDX_v1: indice riga dentro la bolla
              .filter(r => r.cantiere === (c.nome||""))
              .map(r => ({ ...r, _bollaId:b._id, _bollaNum:b.num, _bollaFornitore:b.fornitore, _bollaData:b.data }))
          );
          const totBolleC = bolleC.reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
          return (
            <div className="card">
              <div className="card-header">
                <div className="card-title">📦 Materiali acquistati (bolle)</div>
                {totBolleC>0 && <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#3ecf8e" }}>€ {totBolleC.toLocaleString("it-IT",{minimumFractionDigits:2})}</span>}
              </div>
              {bolleC.length===0 ? (
                <div style={{ fontSize:13, color:theme.textDim, textAlign:"center", padding:"16px 0" }}>
                  Nessun materiale acquistato per questo cantiere.<br/>
                  <span style={{ fontSize:12 }}>Inserisci le bolle da <strong style={{color:theme.accent}}>Materiali → Bolle</strong> assegnandole a questo cantiere.</span>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Bolla</th><th>Fornitore</th><th>Data</th><th>Prodotto</th><th>Lavorazione</th><th>Qtà</th><th style={{textAlign:"right"}}>Importo</th></tr></thead>
                    <tbody>
                      {bolleC.map((r,i) => {
                        const dataFmt = r._bollaData ? new Date(r._bollaData).toLocaleDateString("it-IT") : "—";
                        const imp = (parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1);
                        const _isEdit = editBollaRiga && editBollaRiga.bollaId===r._bollaId && editBollaRiga.rigaIdx===r._rigaIdx;
                        return (
                          <React.Fragment key={i}>
                          <tr onClick={()=>setEditBollaRiga(_isEdit?null:{bollaId:r._bollaId, rigaIdx:r._rigaIdx})} style={{cursor:"pointer", background:_isEdit?theme.accentSoft:"transparent"}}>
                            <td style={{ fontFamily:"monospace", color:theme.accent, fontSize:12 }}>N° {r._bollaNum}</td>
                            <td style={{ fontSize:12, color:theme.textMuted }}>{r._bollaFornitore||"—"}</td>
                            <td style={{ fontSize:12, color:theme.textMuted }}>{dataFmt}</td>
                            <td style={{ fontWeight:600, fontSize:13 }}>{r.prodotto} <span style={{fontSize:11,color:theme.textDim}}>✏️</span></td>
                            <td style={{ fontSize:12, color:theme.textMuted }}>{r.lavorazione && r.lavorazione!=="__extra__" ? r.lavorazione : r.lavorazione==="__extra__" ? "⚡ Extra" : "—"}</td>
                            <td style={{ fontSize:13 }}>{r.qta} {r.um}</td>
                            <td style={{ textAlign:"right", fontWeight:700, color:"#3ecf8e" }}>€ {imp.toLocaleString("it-IT",{minimumFractionDigits:2})}</td>
                          </tr>
                          {_isEdit && (
                            <tr>
                              <td colSpan={7} style={{background:theme.bg, padding:"12px 14px"}}>
                                <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
                                  <div style={{flex:2,minWidth:220}}>
                                    <div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Lavorazione</div>
                                    <select defaultValue={r.lavorazione||""} onChange={e=>salvaRigaBolla(r._bollaId,r._rigaIdx,"lavorazione",e.target.value)} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.card,color:theme.text,fontSize:13}}>
                                      <option value="">— nessuna —</option>
                                      <option value="__extra__">⚡ Extra</option>
                                      {/* NUM_VOCE_BOLLE_v1 */}{(c.lavorazioni||[]).map((lv,li)=><option key={li} value={lv.desc}>{((lv.numVoce||'').trim() ? lv.numVoce+' - ' : '')+(lv.descBreve||lv.desc)}</option>)}
                                    </select>
                                  </div>
                                  <div style={{width:100}}>
                                    <div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Quantità</div>
                                    <input type="number" defaultValue={r.qta} onBlur={e=>{ if(parseFloat(e.target.value)!==parseFloat(r.qta)) salvaRigaBolla(r._bollaId,r._rigaIdx,"qta",parseFloat(e.target.value)||0); }} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.card,color:theme.text,fontSize:13}} />
                                  </div>
                                  <div style={{width:110}}>
                                    <div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Prezzo €/{r.um||"u"}</div>
                                    <input type="number" defaultValue={r.prezzo} onBlur={e=>{ if(parseFloat(e.target.value)!==parseFloat(r.prezzo)) salvaRigaBolla(r._bollaId,r._rigaIdx,"prezzo",parseFloat(e.target.value)||0); }} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.card,color:theme.text,fontSize:13}} />
                                  </div>
                                  <button onClick={()=>setEditBollaRiga(null)} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${theme.border}`,background:"transparent",color:theme.textMuted,cursor:"pointer",fontSize:13}}>Chiudi</button>
                                </div>
                                <div style={{fontSize:11,color:theme.textDim,marginTop:8}}>La lavorazione si salva subito. Quantità e prezzo si salvano quando esci dal campo. Il totale della bolla si ricalcola.</div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* DIARIO */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">📓 Diario di cantiere</div>
            <button className="btn btn-ghost btn-sm" onClick={()=>setShowDiarioForm(!showDiarioForm)}>+ Nuova nota</button>
          </div>
          {showDiarioForm && (
            <div style={{ background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}`, marginBottom:14 }}>
              <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                {["📝","🔨","✅","⚠️","🌧️","📷","📞"].map(ic => (
                  <div key={ic} onClick={()=>setDiarioForm({...diarioForm,icon:ic})}
                    style={{ width:34, height:34, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, cursor:"pointer", border:`2px solid ${diarioForm.icon===ic?theme.accent:theme.border}`, background:diarioForm.icon===ic?theme.accentSoft:theme.bg }}>
                    {ic}
                  </div>
                ))}
              </div>
              <textarea style={{...inputSt,minHeight:70,marginBottom:10}} placeholder="Nota del giorno — es. Completata posa primo strato piastrelle bagno..." value={diarioForm.testo} onChange={e=>setDiarioForm({...diarioForm,testo:e.target.value})} />
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={()=>{ if(!diarioForm.testo)return; const nuovaLista=[{id:Date.now(),data:new Date().toLocaleDateString("it-IT"),ore:new Date().toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"}),...diarioForm},...(diario[cid]||[])]; setDiario({...diario,[cid]:nuovaLista}); salvaDiarioFirebase(cid,nuovaLista); setDiarioForm({testo:"",icon:"📝"}); setShowDiarioForm(false); }}>Salva nota</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowDiarioForm(false)}>Annulla</button>
              </div>
            </div>
          )}
          <div className="timeline">
            {diarioC.length===0 ? (
              <div style={{ fontSize:13, color:theme.textDim, padding:"16px 0", textAlign:"center" }}>Nessuna nota nel diario. Clicca "+ Nuova nota" per aggiungere.</div>
            ) : diarioC.map(ev => (
              <div className="timeline-item" key={ev.id}>
                <div className="timeline-dot active">{ev.icon}</div>
                <div className="timeline-content">
                  <div className="timeline-title">{ev.testo}</div>
                  <div className="timeline-date">{ev.data} {ev.ore}</div>
                </div>
                <button onClick={()=>{ const nuovaLista=diarioC.filter(x=>x.id!==ev.id); setDiario({...diario,[cid]:nuovaLista}); salvaDiarioFirebase(cid,nuovaLista); }}
                  style={{ background:"none", border:"none", color:theme.textDim, cursor:"pointer", fontSize:14, marginLeft:"auto" }}>✕</button>
              </div>
            ))}
          </div>
        </div>
        </>)}
      </>
    );
  };

  // ── VISTA OPERAIO ────────────────────────────────────────────────────────
  const renderVistaOperaio = () => {
    const cantieriOperaio = cantieriDB.filter(c=>!c.completato);
    const dp = dipendenti.find(x=>x.nome===operaioSelezionato);
    const cantAperto = cantieriOperaio.find(c=>c._id===cantiereOperaioAperto||c.id===cantiereOperaioAperto);

    // Statistiche ore operaio
    const oggi = new Date();
    const mese = oggi.toLocaleDateString("it-IT").slice(3); // mm/aaaa
    const anno = oggi.getFullYear().toString();
    const oreOp = assenzeDB.filter(a=>a.operaio===operaioSelezionato&&a.tipo==="lavorato");
    const oreMese = oreOp.filter(a=>a.data?.slice(3)===mese).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
    const oreAnno = oreOp.filter(a=>a.data?.slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
    const assenzeOp = assenzeDB.filter(a=>a.operaio===operaioSelezionato&&a.tipo!=="lavorato");
    const ferie = assenzeOp.filter(a=>a.tipo==="Ferie").reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);
    const permessi = assenzeOp.filter(a=>a.tipo==="Permesso").reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
    const malattia = assenzeOp.filter(a=>(a.tipo==="Malattia"||a.tipo==="Infortunio")).reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);

    const headerOperaio = (
      <div style={{ background:theme.surface, borderRadius:14, padding:"14px 18px", marginBottom:16, border:`1px solid ${dp?.colore||theme.border}44` }}>
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:14 }}>
          <div className="avatar" style={{ background:dp?.colore||"#888", width:44, height:44, fontSize:16 }}>{operaioSelezionato[0]}</div>
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:17 }}>Ciao, {operaioSelezionato}! 👋</div>
            <div style={{ fontSize:12, color:theme.textMuted }}>{new Date().toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}</div>
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
          {[["Ore questo mese",oreMese.toFixed(1)+"h","#4e9af5"],["Ore anno",oreAnno.toFixed(1)+"h",theme.accent],["Giorni ferie",Math.round(ferie)+"gg","#3ecf8e"],["Malattia/Inf.",Math.round(malattia)+"gg","#f06060"]].map(([label,val,col])=>(
            <div key={label} style={{ background:theme.bg, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:col }}>{val}</div>
              <div style={{ fontSize:10, color:theme.textDim, marginTop:3 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    );

    // Dettaglio cantiere (operaio)
    if (cantAperto) {
      const lavs = cantAperto.lavorazioni||[];

      // Colori per categoria
      const coloriCategoria = {
        demolizione: { bg:"#f0606018", border:"#f0606066", text:"#f06060", icon:"🔨" },
        muratura:    { bg:"#f5a62318", border:"#f5a62366", text:"#f5a623", icon:"🧱" },
        intonaco:    { bg:"#4e9af518", border:"#4e9af566", text:"#4e9af5", icon:"🪣" },
        rasatura:    { bg:"#4e9af518", border:"#4e9af566", text:"#4e9af5", icon:"🪣" },
        imbiancatura:{ bg:"#a78bfa18", border:"#a78bfa66", text:"#a78bfa", icon:"🎨" },
        verniciatura:{ bg:"#a78bfa18", border:"#a78bfa66", text:"#a78bfa", icon:"🖌️" },
        cartongesso: { bg:"#3ecf8e18", border:"#3ecf8e66", text:"#3ecf8e", icon:"📐" },
        pavimento:   { bg:"#f59e0b18", border:"#f59e0b66", text:"#f59e0b", icon:"⬜" },
        piastrelle:  { bg:"#f59e0b18", border:"#f59e0b66", text:"#f59e0b", icon:"🔲" },
        bagno:       { bg:"#60c0f018", border:"#60c0f066", text:"#60c0f0", icon:"🚿" },
        resina:      { bg:"#34d39918", border:"#34d39966", text:"#34d399", icon:"💧" },
        tetti:       { bg:"#fb923c18", border:"#fb923c66", text:"#fb923c", icon:"🏠" },
        ponteggio:   { bg:"#94a3b818", border:"#94a3b866", text:"#94a3b8", icon:"🏗️" },
        impermeabilizzazioni: { bg:"#0ea5e918", border:"#0ea5e966", text:"#0ea5e9", icon:"💦" },
        cappotto:    { bg:"#f9731618", border:"#f9731666", text:"#f97316", icon:"🧥" },
        cemento:     { bg:"#78716c18", border:"#78716c66", text:"#78716c", icon:"🔩" },
        altro:       { bg:"#2a2e4566", border:"#2a2e45", text:"#7b82a0", icon:"🔧" },
      };
      const getCatColore = (cat) => {
        const key = Object.keys(coloriCategoria).find(k => (cat||"").toLowerCase().includes(k));
        return coloriCategoria[key] || coloriCategoria.altro;
      };
      // Raggruppa lavorazioni per categoria
      const lavsPerCategoria = {};
      lavs.forEach((l, i) => {
        const cat = l.categoria || "altro";
        const catLabel = dizionarioMateriali[cat]?.label || (cat === "altro" ? "🔧 Altre lavorazioni" : cat.charAt(0).toUpperCase() + cat.slice(1));
        if (!lavsPerCategoria[cat]) lavsPerCategoria[cat] = { label: catLabel, lavs: [], colore: getCatColore(cat) };
        lavsPerCategoria[cat].lavs.push({ ...l, _idx: i });
      });

      return (
        <div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
            <button className="btn btn-ghost" onClick={()=>setCantiereOperaioAperto(null)}>← I miei cantieri</button>
            {!vistaOpProp && (
              <button className="btn btn-ghost btn-sm" onClick={()=>setVistaOperaio(false)}>✕ Esci vista operaio</button>
            )}
          </div>
          {headerOperaio}
          <FotoCantiereBtn cant={cantAperto} grande />
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:12 }}>🏗️ {cantAperto.nome}</div>
            {[["Indirizzo",cantAperto.indirizzo||"—"],["Fine prevista",cantAperto.fine||"—"]].map(([k,v])=>(
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                <span style={{ color:theme.textMuted }}>{k}</span><span style={{ fontWeight:500 }}>{v}</span>
              </div>
            ))}
            {(() => {
              const avanzRT = avanzamentoCalcolato(cantAperto); // FIX_VISTA_REALE_v1
              return (
                <div style={{ marginTop:12 }}>
                  <div className="progress-bar" style={{ height:10 }}>
                    <div className="progress-fill" style={{ width:`${avanzRT}%`, background:avanzRT>80?theme.success:theme.accent }} />
                  </div>
                  <div style={{ fontSize:12, color:theme.textMuted, marginTop:4, textAlign:"right" }}>{avanzRT}% completato</div>
                </div>
              );
            })()}
          </div>

          {lavs.length===0 && <div className="card" style={{ color:theme.textDim, textAlign:"center", padding:24 }}>Nessuna lavorazione assegnata dal preventivo.</div>}

          {/* Lavorazioni raggruppate per categoria */}
          {Object.entries(lavsPerCategoria).map(([catKey, catData]) => {
            const totLavs = catData.lavs.length;
            const completate = catData.lavs.filter(l => avanzamentoLavorazione(cantAperto, l._idx) >= 100).length;
            return (
              <div key={catKey} style={{ marginBottom:16 }}>
                <div onClick={()=>toggleCat(catKey)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", background:catData.colore.bg, borderRadius: catAperte[catKey] ? "10px 10px 0 0" : 10, border:`2px solid ${catData.colore.border}`, cursor:"pointer", userSelect:"none" }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:catData.colore.text }}>
                    {catData.colore.icon} {catData.label}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ fontSize:13, color:completate===totLavs?theme.success:catData.colore.text, fontWeight:700, background:"#00000033", borderRadius:20, padding:"3px 12px" }}>
                      {completate}/{totLavs} {completate===totLavs?"✅":""}
                    </div>
                    <span style={{ color:catData.colore.text, fontSize:18, fontWeight:700 }}>{catAperte[catKey] ? "▲" : "▼"}</span>
                  </div>
                </div>
                {catAperte[catKey] && catData.lavs.map((l) => {
                  const i = l._idx;
            const key = `${cantAperto._id||cantAperto.id}-${i}`;
            const avanzL = avanzamentoLavorazione(cantAperto,i);
            const completata = avanzL>=100;
            return (
              <div key={i} className="card" style={{ marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14 }}>
                      {l.numVoce && (
                        <span style={{ display:"inline-block", fontFamily:"'Syne',sans-serif", fontWeight:800,
                          fontSize:11, color:theme.accent, background:theme.accentSoft, borderRadius:5,
                          padding:"1px 5px", marginRight:7, verticalAlign:"middle" }}>{l.numVoce}</span>
                      )}
                      {l.descBreve || l.desc}
                      {l.gruppo && <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:400, fontSize:12, color:theme.accent, marginLeft:8 }}>({l.gruppo})</span>}
                    </div>
                    {l.descBreve && !vistaOperaio && <div style={{ fontSize:11, color:theme.textDim, marginTop:2, fontStyle:"italic" }}>{l.desc}</div>}
                    {!vistaOperaio && <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>{l.qta} {l.um} · {l.minuti}min/{l.um}</div>}
                    {/* DPI automatici */}
                    {(() => { const d = getDatiLavorazione(l.desc, l.categoria, l.sottocategoria); return d?.dpi ? <DPIBadges dpiIds={d.dpi} /> : null; })()}
                  </div>
                  <span className={`badge ${completata?"badge-success":avanzL>0?"badge-warning":"badge-muted"}`}>
                    {completata?"✓ Completata":avanzL>0?"In corso":"Da fare"}
                  </span>
                  {/* FIX: bottone elimina voce - SOLO per titolare, nascosto a operaio */}
                  {!vistaOperaio && (() => {
                    const oreRegistrate = assenzeDB.filter(a => a.tipo==="lavorato" && (a.cantiereId===cantAperto._id || a.cantiereId===cantAperto.id || (!a.cantiereId && normLavTxt(a.cantiere)===normLavTxt(cantAperto.nome))) && (a.lavorazioneIdx===i || (a.lavorazione && normLavTxt(a.lavorazione)===normLavTxt((lavs[i]||{}).desc)) || trovaIdxLav(a, lavs /* LAV_BY_ID_v7 */)===i)).length;
                    const haOre = oreRegistrate > 0;
                    return (
                      <button
                        title={haOre ? `Impossibile eliminare: ${oreRegistrate} ore registrate. Sposta prima le ore su un'altra voce.` : "Elimina questa voce dal cantiere"}
                        disabled={haOre}
                        onClick={async () => {
                          if (!window.confirm(`Eliminare definitivamente la voce "${l.descBreve || l.desc}" dal cantiere?\n\nQuesta operazione non si puo' annullare.`)) return;
                          try {
                            const cantId = cantAperto._id || cantAperto.id;
                            const nuoveLav = (cantAperto.lavorazioni || []).filter((_,idx) => idx !== i);
                            await updateDoc(doc(db, "cantieri", cantId), { lavorazioni: nuoveLav, updatedAt: new Date().toISOString() });
                            alert("✅ Voce eliminata.");
                          } catch(e) { alert("❌ "+e.message); }
                        }}
                        style={{
                          marginLeft:8, padding:"6px 10px", borderRadius:8,
                          background: haOre ? "#33333344" : "#f0606022",
                          border: `1px solid ${haOre ? "#666" : "#f06060"}`,
                          color: haOre ? "#666" : "#f06060",
                          cursor: haOre ? "not-allowed" : "pointer",
                          fontSize:14,
                        }}
                      >🗑️</button>
                    );
                  })()}
                </div>
                {/* Pulsanti materiali e attrezzatura */}
                {(() => {
                  const datiLav = getDatiLavorazione(l.desc, l.categoria, l.sottocategoria);
                  if (!datiLav) return null;
                  return (
                    <div style={{ display:"flex", gap:8, marginBottom:10 }}>
                      <details style={{ flex:1 }}>
                        <summary style={{ cursor:"pointer", fontSize:12, color:theme.accent, fontWeight:600, padding:"6px 12px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.accent}44`, listStyle:"none", display:"flex", alignItems:"center", gap:6 }}>
                          🧱 Materiali ({datiLav.materiali.length})
                        </summary>
                        <div style={{ marginTop:6, padding:"8px 12px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}` }}>
                          {datiLav.materiali.map(m => <div key={m} style={{ fontSize:12, color:theme.textMuted, padding:"2px 0" }}>· {m}</div>)}
                        </div>
                      </details>
                      <details style={{ flex:1 }}>
                        <summary style={{ cursor:"pointer", fontSize:12, color:"#4e9af5", fontWeight:600, padding:"6px 12px", background:theme.bg, borderRadius:8, border:`1px solid #4e9af544`, listStyle:"none", display:"flex", alignItems:"center", gap:6 }}>
                          🔧 Attrezzatura ({datiLav.attrezzatura.length})
                        </summary>
                        <div style={{ marginTop:6, padding:"8px 12px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}` }}>
                          {datiLav.attrezzatura.map(a => <div key={a} style={{ fontSize:12, color:theme.textMuted, padding:"2px 0" }}>· {a}</div>)}
                        </div>
                      </details>
                    </div>
                  );
                })()}
                {!vistaOperaio && (<>
                <div className="progress-bar" style={{ height:8, marginBottom:8 }}>
                  <div className="progress-fill" style={{ width:`${avanzL}%`, background:completata?theme.success:theme.accent }} />
                </div>
                <div style={{ fontSize:12, color:theme.textMuted, textAlign:"right", marginBottom:12 }}>{avanzL}%</div>
                </>)}

                {/* Inserimento ore */}
                <div style={{ borderTop:`1px solid ${theme.border}`, paddingTop:12 }}>
                  <div style={{ fontSize:11, color:theme.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>⏱ Ore su questa lavorazione</div>
                  <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                    <div>
                      <div style={{ fontSize:10, color:theme.textDim, marginBottom:3 }}>Ore</div>
                      <input type="number" min="0.5" max="12" step="0.5" placeholder="es. 4"
                        style={{...inputSt, width:80, fontSize:16, padding:"10px 12px"}}
                        value={oreInputs[key]||""} onChange={e=>setOreInputs({...oreInputs,[key]:e.target.value})} />
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:theme.textDim, marginBottom:3 }}>Data</div>
                      <input type="date"
                        style={{...inputSt, width:150, padding:"10px 12px"}}
                        value={oreInputs[key+"-data"]||new Date().toISOString().slice(0,10)}
                        onChange={e=>setOreInputs({...oreInputs,[key+"-data"]:e.target.value})} />
                    </div>
                    <div style={{ alignSelf:"flex-end" }}>
                      <button className="btn btn-primary" style={{ padding:"10px 20px" }}
                        onClick={()=>salvaOreOperaio(cantAperto._id||cantAperto.id, cantAperto.nome, l.desc, i, operaioSelezionato, oreInputs[key+"-data"])}>
                        Salva ore
                      </button>
                    </div>
                    {oreSalvate[key] && <span style={{ fontSize:12, color:theme.success }}>✅ {oreSalvate[key]}h salvate</span>}
                  </div>
                  {/* Ore già registrate per questa lavorazione — ultimi 5 giorni */}
                  {(() => {
                    const cid2 = cantAperto._id||cantAperto.id;
                    const docOre = assenzeDB
                      .filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid2||(a.cantiere||"")===(cantAperto.nome||""))&&trovaIdxLav(a, lavs /* LAV_BY_ID_v7 */)===i&&a.operaio===operaioSelezionato)
                      .sort((a,b)=>{
                        const pa=a.data?.split("/").reverse().join("")||"";
                        const pb=b.data?.split("/").reverse().join("")||"";
                        return pb.localeCompare(pa);
                      });
                    if (!docOre.length) return null;
                    const mostraTutte = oreInputs[key+"-tutte"];
                    const visibili = mostraTutte ? docOre : docOre.slice(0,5);
                    return (
                      <div style={{ marginTop:10, background:theme.bg, borderRadius:8, padding:"10px 12px", border:`1px solid ${theme.border}` }}>
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                          <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>Ore registrate ({docOre.length})</div>
                          {docOre.length > 5 && (
                            <button onClick={()=>setOreInputs({...oreInputs,[key+"-tutte"]:!mostraTutte})}
                              style={{ background:"none", border:"none", color:theme.accent, cursor:"pointer", fontSize:11 }}>
                              {mostraTutte ? "Mostra ultime 5" : `Vedi tutte (${docOre.length})`}
                            </button>
                          )}
                        </div>
                        {visibili.map(assDoc => (
                          <div key={assDoc._id} style={{ display:"flex", alignItems:"center", gap:10, padding:"5px 0", borderBottom:`1px solid ${theme.border}33` }}>
                            <span style={{ fontSize:12, color:theme.textMuted, minWidth:80 }}>{assDoc.data}</span>
                            <input type="number" min="0.5" max="12" step="0.5"
                              defaultValue={assDoc.ore}
                              style={{...inputSt, width:70, padding:"4px 8px", fontSize:13}}
                              onBlur={async e => {
                                const nuoveOre = parseFloat(e.target.value)||0;
                                if (nuoveOre === assDoc.ore) return;
                                try { await updateDoc(doc(db,"assenze",assDoc._id),{ore:nuoveOre}); }
                                catch(err){ alert("❌ "+err.message); }
                              }} />
                            <span style={{ fontSize:12, color:theme.textDim }}>h</span>
                            <button onClick={async()=>{
                              if(!window.confirm("Eliminare queste ore?"))return;
                              try{ await deleteDoc(doc(db,"assenze",assDoc._id)); }catch(err){alert("❌ "+err.message);}
                            }} style={{marginLeft:"auto",background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>🗑️</button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Foto lavorazione */}
                <div style={{ borderTop:`1px solid ${theme.border}`, paddingTop:12, marginTop:12 }}>
                  <div style={{ fontSize:11, color:theme.textMuted, marginBottom:8, textTransform:"uppercase", letterSpacing:1 }}>📷 Foto lavorazione</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
                    {/* Foto salvate su Firebase */}
                    {assenzeDB
                      .filter(a => a.tipo==="foto" && a.cantiereId===(cantAperto._id||cantAperto.id) && trovaIdxLav(a, lavs /* LAV_BY_ID_v6 */)===i)
                      .map(docAssenza => (docAssenza.foto||[]).map((f,fi) => (
                        <div key={docAssenza._id+"-"+fi} style={{ position:"relative" }}>
                          <img src={f.url} alt="" style={{ width:64, height:64, objectFit:"cover", borderRadius:8, border:`1px solid ${theme.accent}44`, cursor:"pointer" }}
                            onClick={()=>{ const u=f.full||f.base64||f.url; if(u)window.open(u,"_blank"); }} />
                          <button
                            onClick={async(e)=>{
                              e.stopPropagation();
                              if (!window.confirm("Eliminare questa foto?")) return; fotoCantiereElimina(f);
                              try {
                                const nuoveFoto = (docAssenza.foto||[]).filter((_,j)=>j!==fi);
                                if (nuoveFoto.length === 0) {
                                  await deleteDoc(doc(db,"assenze",docAssenza._id));
                                } else {
                                  await updateDoc(doc(db,"assenze",docAssenza._id),{foto:nuoveFoto});
                                }
                              } catch(err){ alert("❌ "+err.message); }
                            }}
                            style={{ position:"absolute", top:-5, right:-5, background:theme.danger, border:"none", borderRadius:"50%", width:20, height:20, color:"#fff", cursor:"pointer", fontSize:12, display:"flex", alignItems:"center", justifyContent:"center" }}>✕</button>
                        </div>
                      ))).flat()
                    }
                    {/* Foto locali (scattate in questa sessione, prima del salvataggio) */}
                    {(fotoLav[key]||[]).map((f,fi)=>(
                      <div key={fi} style={{ position:"relative" }} onClick={()=>window.open(f.url,"_blank")}>
                        <img src={f.url} alt="" style={{ width:64, height:64, objectFit:"cover", borderRadius:8, border:`1px solid ${theme.border}`, cursor:"pointer" }} />
                      </div>
                    ))}
                    <label style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:8, border:`1px solid ${theme.accent}66`, cursor:"pointer", fontSize:13, color:theme.accent, background:"#1a1500" }}>
                      {(() => {
                        const fotoFB = assenzeDB.filter(a=>a.tipo==="foto"&&a.cantiereId===(cantAperto._id||cantAperto.id)&&trovaIdxLav(a, lavs /* LAV_BY_ID_v6 */)===i).flatMap(a=>a.foto||[]).length;
                        const fotoLoc = (fotoLav[key]||[]).length;
                        const tot = fotoFB + fotoLoc;
                        return <>📷 {tot>0?`${tot} foto`:"Fotocamera"}</>;
                      })()}
                      <input type="file" accept="image/*" style={{ display:"none" }}
                        onChange={async e => {
                          const files = Array.from(e.target.files);
                          const converted = await Promise.all(files.map(f => new Promise(resolve => {
                            const reader = new FileReader();
                            reader.onload = ev => {
                              const img = new Image();
                              img.onload = () => {
                                const canvas = document.createElement("canvas");
                                const MAX = 600; let w=img.width,h=img.height;
                                if(w>MAX){h=Math.round(h*MAX/w);w=MAX;}
                                canvas.width=w;canvas.height=h;
                                canvas.getContext("2d").drawImage(img,0,0,w,h);
                                resolve({ nome:f.name, url:canvas.toDataURL("image/jpeg",0.65) });
                              };
                              img.src = ev.target.result;
                            };
                            reader.readAsDataURL(f);
                          })));
                          const nuoveFoto = [...(fotoLav[key]||[]), ...converted];
                          setFotoLav({...fotoLav,[key]:nuoveFoto});
                          try {
                            await addDoc(collection(db,"assenze"), {
                              operaio: operaioSelezionato,
                              data: new Date().toLocaleDateString("it-IT"),
                              cantiere: cantAperto.nome,
                              cantiereId: cantAperto._id||cantAperto.id,
                              lavorazione: lavs[i]?.desc||"",
                              lavorazioneIdx: i,
                              lavorazioneId: (lavs[i] && lavs[i].id != null) ? lavs[i].id : null, /* LAV_BY_ID_v6 */
                              tipo: "foto",
                              ore: 0,
                              foto: converted,
                              createdAt: new Date().toISOString(),
                            });
                          } catch(err) { alert("Salvataggio foto fallito: "+((err&&err.message)||err)); }
                        }} />
                    </label>
                    <label style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:8, border:`1px solid ${theme.border}`, cursor:"pointer", fontSize:13, color:theme.textMuted, background:theme.bg }}>
                      🖼️ Libreria
                      <input type="file" accept="image/*" multiple style={{ display:"none" }}
                        onChange={async e => {
                          const files = Array.from(e.target.files);
                          const converted = await Promise.all(files.map(f => new Promise(resolve => {
                            const reader = new FileReader();
                            reader.onload = ev => {
                              const img = new Image();
                              img.onload = () => {
                                const canvas = document.createElement("canvas");
                                const MAX = 600; let w=img.width,h=img.height;
                                if(w>MAX){h=Math.round(h*MAX/w);w=MAX;}
                                canvas.width=w;canvas.height=h;
                                canvas.getContext("2d").drawImage(img,0,0,w,h);
                                resolve({ nome:f.name, url:canvas.toDataURL("image/jpeg",0.65) });
                              };
                              img.src = ev.target.result;
                            };
                            reader.readAsDataURL(f);
                          })));
                          const nuoveFoto = [...(fotoLav[key]||[]), ...converted];
                          setFotoLav({...fotoLav,[key]:nuoveFoto});
                          try {
                            await addDoc(collection(db,"assenze"), {
                              operaio: operaioSelezionato,
                              data: new Date().toLocaleDateString("it-IT"),
                              cantiere: cantAperto.nome,
                              cantiereId: cantAperto._id||cantAperto.id,
                              lavorazione: lavs[i]?.desc||"",
                              lavorazioneIdx: i,
                              lavorazioneId: (lavs[i] && lavs[i].id != null) ? lavs[i].id : null, /* LAV_BY_ID_v6 */
                              tipo: "foto",
                              ore: 0,
                              foto: converted,
                              createdAt: new Date().toISOString(),
                            });
                          } catch(err) { alert("Salvataggio foto fallito: "+((err&&err.message)||err)); }
                        }} />
                    </label>
                  </div>
                </div>
              </div>
            );
                })}
              </div>
            );
          })}
          {/* ── ATTIVITÀ EXTRA GIÀ SALVATE ── */}
          {(() => {
            const cid = cantAperto._id||cantAperto.id;
            const extra = assenzeDB.filter(a => a.extra===true && (a.cantiereId===cid||(a.cantiere||"")===(cantAperto.nome||"")) && a.operaio===operaioSelezionato)
              .sort((a,b)=>{
                const pa=a.data?.split("/").reverse().join("")||"";
                const pb=b.data?.split("/").reverse().join("")||"";
                return pb.localeCompare(pa);
              });
            if (!extra.length) return null;
            return (
              <div className="card" style={{ border:`1px solid #4e9af533`, marginBottom:12 }}>
                <div className="card-title" style={{ marginBottom:12, color:"#4e9af5" }}>📋 Attività extra registrate ({extra.length})</div>
                {extra.map(assDoc => (
                  <div key={assDoc._id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`1px solid ${theme.border}33` }}>
                    <span style={{ fontSize:12, color:theme.textMuted, minWidth:80 }}>{assDoc.data}</span>
                    <span style={{ fontSize:13, flex:1 }}>{assDoc.lavorazione||"—"}</span>
                    <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.accent, minWidth:40, textAlign:"right" }}>{assDoc.ore}h</span>
                    <button onClick={async()=>{
                      if(!window.confirm(`Eliminare "${assDoc.lavorazione}" (${assDoc.ore}h)?`))return;
                      try{ await deleteDoc(doc(db,"assenze",assDoc._id)); }catch(err){alert("❌ "+err.message);}
                    }} style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:16,padding:"0 4px"}}>🗑️</button>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* ── LAVORAZIONE LIBERA ── */}
          <div className="card" style={{ border:`1px solid #f5a62355` }}>
            <div className="card-title" style={{ marginBottom:14, color:theme.accent }}>⚡ Registra attività extra</div>
            <div style={{ fontSize:13, color:theme.textMuted, marginBottom:14 }}>
              Lavoro non previsto nel preventivo? Scrivilo qui con le ore.
            </div>
            {(() => {
              const keyLibera = `libera-${cantAperto._id||cantAperto.id}`;
              const [descLibera, setDescLibera] = [oreInputs[keyLibera+"-desc"]||"", v => setOreInputs({...oreInputs,[keyLibera+"-desc"]:v})];
              const [oreLibera, setOreLibera]   = [oreInputs[keyLibera+"-ore"]||"",  v => setOreInputs({...oreInputs,[keyLibera+"-ore"]:v})];
              const salvaLibera = async () => {
                if (!descLibera || !oreLibera) { alert("Inserisci descrizione e ore."); return; }
                try {
                  await addDoc(collection(db,"assenze"), {
                    operaio: operaioSelezionato,
                    data: (() => { const d = oreInputs[keyLibera+"-data"]; if(d) return d.split("-").reverse().join("/"); const n=new Date(); return `${String(n.getDate()).padStart(2,"0")}/${String(n.getMonth()+1).padStart(2,"0")}/${n.getFullYear()}`; })(),
                    cantiere: cantAperto.nome,
                    cantiereId: cantAperto._id||cantAperto.id,
                    lavorazione: descLibera,
                    lavorazioneIdx: -1,
                    ore: parseFloat(oreLibera)||0,
                    tipo: "lavorato", cantiereCE: false,
                    extra: true,
                    createdAt: new Date().toISOString(),
                  });
                  setOreInputs({...oreInputs,[keyLibera+"-desc"]:"",[keyLibera+"-ore"]:""});
                  alert(`✅ Attività "${descLibera}" (${oreLibera}h) salvata!`);
                } catch(e) { alert("❌ Errore: "+e.message); }
              };
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Descrizione attività</div>
                    <input style={{...inputSt, fontSize:15, padding:"12px 14px"}}
                      placeholder="es. Portato via mobili, Pulizia cantiere..."
                      value={descLibera}
                      onChange={e => setOreInputs({...oreInputs,[keyLibera+"-desc"]:e.target.value})} />
                  </div>
                  <div style={{ display:"flex", gap:10, alignItems:"flex-end", flexWrap:"wrap" }}>
                    <div style={{ flex:1, minWidth:80 }}>
                      <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Ore</div>
                      <input type="number" min="0.5" max="12" step="0.5"
                        style={{...inputSt, fontSize:16, padding:"12px 14px"}}
                        placeholder="es. 4"
                        value={oreLibera}
                        onChange={e => setOreInputs({...oreInputs,[keyLibera+"-ore"]:e.target.value})} />
                    </div>
                    <div style={{ flex:1, minWidth:130 }}>
                      <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Data</div>
                      <input type="date"
                        style={{...inputSt, padding:"12px 14px"}}
                        value={oreInputs[keyLibera+"-data"]||new Date().toISOString().slice(0,10)}
                        onChange={e => setOreInputs({...oreInputs,[keyLibera+"-data"]:e.target.value})} />
                    </div>
                    <button className="btn btn-primary" style={{ padding:"12px 24px", fontSize:15 }} onClick={salvaLibera}>
                      ✓ Salva
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      );
    }

    // Lista cantieri (operaio)
    return (
      <div>
        {!vistaOpProp && (
          <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:12 }}>
            <button className="btn btn-ghost btn-sm" onClick={()=>setVistaOperaio(false)}>✕ Esci vista operaio</button>
          </div>
        )}
        {headerOperaio}
        {cantieriOperaio.length===0 ? (
          <div className="card" style={{ textAlign:"center", color:theme.textMuted, padding:32 }}>Nessun cantiere attivo.</div>
        ) : cantieriOperaio.map(cant => {
          const avanz = avanzamentoCalcolato(cant); // FIX_VISTA_REALE_v1
          return (
            <div key={cant._id||cant.id} className="card" style={{ marginBottom:12, cursor:"pointer" }} onClick={()=>setCantiereOperaioAperto(cant._id||cant.id)}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, marginBottom:3 }}>{cant.nome}</div>
                  <div style={{ fontSize:12, color:theme.textMuted }}>📍 {cant.indirizzo||"—"}</div>
                  <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>Fine prevista: {cant.fine||"—"}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:32, color:avanz>80?theme.success:theme.accent }}>{avanz}%</div>
                  <div style={{ fontSize:10, color:theme.textDim }}>completato</div>
                </div>
              </div>
              <div className="progress-bar" style={{ height:10, marginBottom:8 }}>
                <div className="progress-fill" style={{ width:`${avanz}%`, background:avanz>80?theme.success:avanz>50?theme.accent:theme.info }} />
              </div>
              <div style={{ fontSize:13, color:theme.accent, fontWeight:600, textAlign:"right" }}>Apri cantiere →</div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="content">
      {vistaOperaio ? renderVistaOperaio() : !c ? renderLista() : renderDettaglio()}
      {/* FIX_SPOSTA_ORE_SEL2_v1: modal sposta ore con selezione registrazioni */}
      {spostaOreModal && editLavCantiere && (() => {
        const cantiere = cantieriDB.find(c2 => (c2._id||c2.id) === editLavCantiere.cid);
        const lavs = cantiere?.lavorazioni || [];
        const vociPart = lavs[editLavCantiere.idx];
        const cnome = cantiere?.nome || "";
        const regs = assenzeDB.filter(a => a.tipo==="lavorato" && ((a.cantiereId===editLavCantiere.cid)||((a.cantiere||"")===cnome)) && (trovaIdxLav(a, lavs /* LAV_BY_ID_v3 */)===editLavCantiere.idx))
          .sort((x,y)=>(x.operaio||"").localeCompare(y.operaio||"")||(x.data||"").localeCompare(y.data||""));
        const toggle = (id)=> setSpostaSelezione(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);
        const oreSel = regs.filter(r=>spostaSelezione.includes(r._id)).reduce((s,r)=>s+(parseFloat(r.ore)||0),0);
        return (
          <div onClick={()=>!salvandoSposta&&setSpostaOreModal(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999,padding:16}}>
            <div onClick={e=>e.stopPropagation()} style={{background:theme.card,border:`1px solid ${theme.border}`,borderRadius:14,padding:22,maxWidth:560,width:"100%",maxHeight:"85vh",overflowY:"auto"}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16,marginBottom:6}}>🔀 Sposta ore</div>
              <div style={{fontSize:13,color:theme.textMuted,marginBottom:12}}>Da: <span style={{color:theme.accent}}>{vociPart?.descBreve||vociPart?.desc||"—"}</span></div>
              <div style={{fontSize:12,color:theme.textMuted,marginBottom:6}}>Seleziona le giornate da spostare:</div>
              <div style={{border:`1px solid ${theme.border}`,borderRadius:8,marginBottom:14,maxHeight:260,overflowY:"auto"}}>
                {regs.length===0 ? <div style={{padding:14,fontSize:13,color:theme.textDim}}>Nessuna registrazione trovata.</div> :
                 regs.map(r => {
                   const sel = spostaSelezione.includes(r._id);
                   return (
                     <div key={r._id} onClick={()=>toggle(r._id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",cursor:"pointer",borderBottom:`1px solid ${theme.border}44`,background:sel?theme.accentSoft:"transparent"}}>
                       <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${sel?theme.accent:theme.border}`,background:sel?theme.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#111",fontWeight:700}}>{sel?"✓":""}</div>
                       <div style={{flex:1,fontSize:13}}><strong>{r.operaio||"—"}</strong> · {r.data||"—"}</div>
                       <div style={{fontSize:13,fontWeight:700,color:"#4e9af5"}}>{r.ore}h</div>
                     </div>
                   );
                 })}
              </div>
              <div style={{fontSize:12,color:theme.textMuted,marginBottom:6}}>Sposta verso:</div>
              <select value={spostaDestIdx} onChange={e=>setSpostaDestIdx(e.target.value)} style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,marginBottom:14}}>
                <option value="">— scegli la lavorazione di destinazione —</option>
                {lavs.map((lv,i)=> i!==editLavCantiere.idx ? <option key={i} value={i}>{lv.descBreve||lv.desc}</option> : null)}
              </select>
              <div style={{display:"flex",gap:8,justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:12,color:theme.textMuted}}>Selezionate: <strong style={{color:theme.text}}>{spostaSelezione.length}</strong> ({oreSel}h)</div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setSpostaOreModal(false)} disabled={salvandoSposta} style={{padding:"9px 16px",borderRadius:8,border:`1px solid ${theme.border}`,background:"transparent",color:theme.textMuted,cursor:"pointer"}}>Annulla</button>
                  <button onClick={spostaOre} disabled={salvandoSposta||spostaDestIdx===""||spostaSelezione.length===0} style={{padding:"9px 20px",borderRadius:8,border:"none",background:theme.accent,color:"#111",fontWeight:700,cursor:(salvandoSposta||spostaDestIdx===""||spostaSelezione.length===0)?"not-allowed":"pointer",opacity:(spostaDestIdx===""||spostaSelezione.length===0)?0.5:1}}>{salvandoSposta?"Sposto...":"Sposta selezionate"}</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}


// Dati di esempio: lavorazioni consuntivo per cantiere

function Contabilita({ fattureDB = [], cantieriDB = [] }) {
  const [tab, setTab] = useState("pagamenti");
  const [pagamenti, setPagamenti] = useState({});
  const [showPagForm, setShowPagForm] = useState(null);
  const [pagForm, setPagForm] = useState({ importo:"", tipo:"acconto", data:"", note:"", metodo:"bonifico" });
  const [cercaCantiere, setCercaCantiere] = useState("");
  const [loading, setLoading] = useState(true);

  // ── Registro rapido ───────────────────────────────────────────────────────
  const [registro, setRegistro] = useState([]);
  const [showRegForm, setShowRegForm] = useState(false);
  const [regForm, setRegForm] = useState({ desc:"", importo:"", tipo:"entrata", data:"", note:"" });

  // Carica pagamenti e registro da Firebase
  useEffect(() => {
    const unsubPag = onSnapshot(collection(db, "pagamenti"), snap => {
      const map = {};
      snap.docs.forEach(d => {
        const p = { ...d.data(), _id: d.id };
        if (!map[p.cantiereId]) map[p.cantiereId] = [];
        map[p.cantiereId].push(p);
      });
      setPagamenti(map);
      setLoading(false);
    }, () => setLoading(false));
    const unsubReg = onSnapshot(collection(db, "registro_rapido"), snap => {
      setRegistro(snap.docs.map(d => ({ ...d.data(), _id: d.id })).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")));
    }, () => {});
    return () => { unsubPag(); unsubReg(); };
  }, []);

  const inputSt = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%" };
  const fmt = (n) => "€ " + Number(n||0).toLocaleString("it-IT", { minimumFractionDigits:2, maximumFractionDigits:2 });

  const totReg  = registro.reduce((s,r)=>r.tipo==="entrata"?s+parseFloat(r.importo||0):s,0);
  const totRegU = registro.reduce((s,r)=>r.tipo==="uscita"?s+parseFloat(r.importo||0):s,0);

  const getPagCantiere = (cid) => pagamenti[cid]||[];
  const totIncassato = (cid) => getPagCantiere(cid).reduce((s,p)=>s+(parseFloat(p.importo)||0),0);

  const aggiungiPagamento = async (cid, cant) => {
    if (!pagForm.importo) return;
    try {
      await addDoc(collection(db,"pagamenti"), {
        cantiereId: cid,
        cantiere: cant?.nome||"",
        cliente: cant?.cliente||"",
        ...pagForm,
        /* FIX_PAG_IVA_SCORPORO_v1: l'importo digitato e il LORDO incassato; salvo lordo, iva% e imponibile scorporato */
        importoLordo: parseFloat(pagForm.importo)||0,
        iva: parseFloat(pagForm.iva)||0,
        importo: (parseFloat(pagForm.iva)||0) > 0 ? Math.round((parseFloat(pagForm.importo)||0)/(1+(parseFloat(pagForm.iva)||0)/100)*100)/100 : (parseFloat(pagForm.importo)||0),
        data: pagForm.data||new Date().toLocaleDateString("it-IT"),
        createdAt: new Date().toISOString()
      });
      setPagForm({importo:"",tipo:"acconto",data:"",note:"",metodo:"bonifico"});
      setShowPagForm(null);
    } catch(e) { alert("❌ Errore: "+e.message); }
  };

  const eliminaPagamento = async (id) => {
    if (!window.confirm("Eliminare questo pagamento?")) return;
    try { await deleteDoc(doc(db,"pagamenti",id)); } catch(e) { alert("❌ "+e.message); }
  };

  const aggiungiRegistro = async (voce) => {
    try {
      await addDoc(collection(db,"registro_rapido"), { ...voce, createdAt:new Date().toISOString() });
    } catch(e) { alert("❌ "+e.message); }
  };

  const eliminaRegistro = async (id) => {
    if (!window.confirm("Eliminare questa voce dal registro?")) return;
    try { await deleteDoc(doc(db,"registro_rapido",id)); } catch(e) { alert("❌ "+e.message); }
  };

  // KPI globali
  const tuttiCantieri = cantieriDB;
  const totImportiContratti = tuttiCantieri.reduce((s,c)=>s+(parseFloat(c.importo)||0),0);
  const totIncassatoGlobale = tuttiCantieri.reduce((s,c)=>s+totIncassato(c._id||c.id),0);
  const totDaIncassare = totImportiContratti - totIncassatoGlobale;

  return (
    <div className="content">
      <div className="tabs">
        {[["daincassare","🔴 Da incassare"],["pagamenti","💰 Tutti i cantieri"],["registro","⚡ Registro rapido"]].map(([id,label])=>(
          <div key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</div>
        ))}
      </div>

      {/* ── KPI GLOBALI ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
        {[
          ["Valore contratti", fmt(totImportiContratti), theme.text],
          ["Totale incassato",  fmt(totIncassatoGlobale), "#3ecf8e"],
          ["Da incassare",      fmt(totDaIncassare),      totDaIncassare>0?"#f5a623":"#3ecf8e"],
        ].map(([label,val,color])=>(
          <div key={label} className="card" style={{ padding:"16px 18px", textAlign:"center" }}>
            <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{label}</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color }}>{val}</div>
          </div>
        ))}
      </div>

      {/* ── TAB DA INCASSARE ── */}
      {tab==="daincassare" && (
        <div>
          {(() => {
            const daIncassare = cantieriDB.filter(c => {
              const cid = c._id||c.id;
              const importo = parseFloat(c.consuntivo ? c.consuntivo.lavorazioni?.reduce((s,l)=>s+(parseFloat(l.totale)||0),0) : c.importo)||0;
              const incassato = totIncassato(cid);
              // Mostra se ha flag daIncassare OPPURE se completato con pagamenti pendenti
              if (c.saldato) return false; // FIX_SALDATO_SPARISCE_v1: se saldato, sparisce sempre
              return c.completato && (c.daIncassare || incassato < importo);
            });
            if (daIncassare.length === 0) return (
              <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
                <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
                <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Tutto incassato!</div>
                <div style={{ fontSize:13, marginTop:6 }}>Nessun cantiere completato con pagamenti in sospeso.</div>
              </div>
            );
            return daIncassare.map(cant => {
              const cid = cant._id||cant.id;
              const pags = getPagCantiere(cid);
              const importoConsuntivo = cant.consuntivo ? cant.consuntivo.lavorazioni?.reduce((s,l)=>s+(parseFloat(l.totale)||0),0)||0 : parseFloat(cant.importo)||0;
              const ivaPerc = parseFloat(cant.consuntivo?.iva ?? cant.ivaPerc ?? 0);
              const importoIva = importoConsuntivo * (1 + ivaPerc / 100);
              const incassato = totIncassato(cid);
              const residuo = importoConsuntivo - incassato; // FIX_DAINCASSARE_IMPONIBILE_v1: incassi in imponibile -> confronto con imponibile
              const percInc = importoConsuntivo>0?Math.min(100,Math.round((incassato/importoConsuntivo)*100)):0;
              const isOpen = showPagForm===cid;
              return (
                <div key={cid} className="card" style={{ marginBottom:12, border:`1px solid #f5a62344` }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                    <div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{cant.nome}</div>
                      <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>👤 {cant.cliente||"—"} · ✅ Completato</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:11, color:theme.textDim, marginBottom:2 }}>Imponibile: <strong style={{color:theme.text}}>{fmt(importoConsuntivo)}</strong>{ivaPerc > 0 ? <> · IVA {ivaPerc}%: <strong style={{color:theme.text}}>{fmt(importoConsuntivo * ivaPerc / 100)}</strong></> : <span style={{color:theme.textDim}}> · IVA esclusa</span>}</div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:theme.success }}>
                        {fmt(incassato)} <span style={{ fontSize:12, color:theme.textDim, fontWeight:400 }}>/ {fmt(importoIva)}</span>
                      </div>
                      {residuo>0 && <div style={{ fontSize:12, color:"#f06060", fontWeight:600 }}>🔴 Da incassare: {fmt(residuo)}</div>}
                    </div>
                  </div>
                  <div style={{ height:6, background:theme.border, borderRadius:3, overflow:"hidden", marginBottom:10 }}>
                    <div style={{ height:"100%", width:`${percInc}%`, background:percInc===100?"#3ecf8e":"#f5a623", borderRadius:3 }} />
                  </div>
                  {pags.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      {pags.map(p=>(
                        <div key={p._id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:`1px solid ${theme.border}33`, fontSize:13 }}>
                          <span style={{ padding:"2px 8px", borderRadius:5, fontSize:11, fontWeight:700,
                            background:p.tipo==="saldo"?"#0a2218":p.tipo==="acconto"?"#1a1500":"#0a1020",
                            color:p.tipo==="saldo"?"#3ecf8e":p.tipo==="acconto"?"#f5a623":"#4e9af5",
                            border:`1px solid ${p.tipo==="saldo"?"#3ecf8e44":p.tipo==="acconto"?"#f5a62344":"#4e9af544"}` }}>
                            {p.tipo==="saldo"?"Saldo":p.tipo==="acconto"?"Acconto":"Pagamento"}
                          </span>
                          <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.success }}>{fmt(p.importo)}</span>
                          <span style={{ color:theme.textDim }}>{p.metodo}</span>
                          <span style={{ color:theme.textDim, marginLeft:"auto" }}>{p.data}</span>
                          {p.note && <span style={{ fontSize:11, color:theme.textDim }}>· {p.note}</span>}
                          <button onClick={()=>eliminaPagamento(p._id)} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:13 }}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {isOpen && (
                    <div style={{ background:theme.bg, borderRadius:10, padding:12, border:`1px solid ${theme.border}`, marginBottom:10 }}>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                        <div>
                          <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Importo €</div>
                          <input type="number" style={inputSt} placeholder="0" value={pagForm.importo} onChange={e=>setPagForm({...pagForm,importo:e.target.value})} />
                        </div>
                        <div>
                          <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>IVA %</div>
                          <select style={inputSt} value={pagForm.iva||"0"} onChange={e=>setPagForm({...pagForm,iva:e.target.value})}>
                            <option value="0">Esclusa</option>
                            <option value="4">4%</option>
                            <option value="10">10%</option>
                            <option value="22">22%</option>
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Tipo</div>
                          <select style={inputSt} value={pagForm.tipo} onChange={e=>setPagForm({...pagForm,tipo:e.target.value})}>
                            <option value="acconto">Acconto</option>
                            <option value="saldo">Saldo finale</option>
                            <option value="pagamento">Pagamento parziale</option>
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Metodo</div>
                          <select style={inputSt} value={pagForm.metodo} onChange={e=>setPagForm({...pagForm,metodo:e.target.value})}>
                            <option value="bonifico">Bonifico</option>
                            <option value="contanti">Contanti</option>
                            <option value="assegno">Assegno</option>
                            <option value="carta">Carta</option>
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Data</div>
                          <input style={inputSt} type="date" value={pagForm.data} onChange={e=>setPagForm({...pagForm,data:e.target.value})} />
                        </div>
                      </div>
                      <div style={{ marginBottom:8 }}>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Note</div>
                        <input style={inputSt} placeholder="es. Prima rata concordata..." value={pagForm.note} onChange={e=>setPagForm({...pagForm,note:e.target.value})} />
                      </div>
                      <div style={{ display:"flex", gap:8 }}>
                        <button className="btn btn-primary btn-sm" onClick={()=>aggiungiPagamento(cid, cant)}>✓ Salva pagamento</button>
                        <button className="btn btn-ghost btn-sm" onClick={()=>setShowPagForm(null)}>Annulla</button>
                      </div>
                    </div>
                  )}
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setShowPagForm(isOpen?null:cid); setPagForm({importo:"",tipo:"acconto",data:"",note:"",metodo:"bonifico",iva:"0"});}}>
                      {isOpen?"✕ Chiudi":"+ Registra pagamento"}
                    </button>
                    {percInc>=100 && (
                      <button className="btn btn-sm" style={{background:"#3ecf8e22",color:"#3ecf8e",border:"1px solid #3ecf8e44"}}
                        onClick={async()=>{
                          if(!window.confirm(`Segnare "${cant.nome}" come saldato e spostare in archivio?`)) return;
                          try { await updateDoc(doc(db,"cantieri",cid),{ saldato:true, daIncassare:false, dataSaldo:new Date().toLocaleDateString("it-IT") }); }
                          catch(e){ alert("❌ "+e.message); }
                        }}>✅ Segna come saldato → Archivio</button>
                    )}
                    {percInc<100 && incassato>0 && (
                      <button className="btn btn-sm" style={{background:"#f5a62322",color:"#f5a623",border:"1px solid #f5a62344"}}
                        onClick={async()=>{ /* FIX_CHIUDI_SCONTO_v1 */
                          const sc = Math.round(residuo*100)/100;
                          if(!window.confirm(`Chiudere "${cant.nome}" con uno SCONTO di € ${sc.toLocaleString("it-IT",{minimumFractionDigits:2})}?\n\nIncassato: € ${incassato.toLocaleString("it-IT",{minimumFractionDigits:2})}\nIl cantiere andrà in archivio come saldato.`)) return;
                          try { await updateDoc(doc(db,"cantieri",cid),{ saldato:true, daIncassare:false, sconto:sc, importoScontato:Math.round(incassato*100)/100, dataSaldo:new Date().toLocaleDateString("it-IT"), updatedAt:new Date().toISOString() }); }
                          catch(e){ alert("❌ "+e.message); }
                        }}>🏷️ Chiudi con sconto → Archivio</button>
                    )}
                    <button className="btn btn-sm" style={{background:"#f5a62322",color:"#f5a623",border:"1px solid #f5a62344"}}
                      onClick={async()=>{
                        if(!window.confirm(`Riportare "${cant.nome}" in lavorazione?\n\nIl cantiere tornerà tra i cantieri attivi.`)) return;
                        try { await updateDoc(doc(db,"cantieri",cid),{ completato:false, daIncassare:false, saldato:false, avanzamento:99, dataCompletamento:null, updatedAt:new Date().toISOString() }); }
                        catch(e){ alert("❌ "+e.message); }
                      }}>↩ Riapri cantiere</button>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* ── TAB PAGAMENTI CANTIERI ── */}
      {tab==="pagamenti" && (
        <div>
          <div style={{ marginBottom:16 }}>
            <input style={{...inputSt, maxWidth:320}} placeholder="🔍 Cerca cantiere..."
              value={cercaCantiere} onChange={e=>setCercaCantiere(e.target.value)} />
          </div>
          {cantieriDB.filter(c=>!cercaCantiere||(c.nome||"").toLowerCase().includes(cercaCantiere.toLowerCase())||(c.cliente||"").toLowerCase().includes(cercaCantiere.toLowerCase()))
            .map(cant => {
            const cid = cant._id||cant.id;
            const pags = getPagCantiere(cid);
            const incassato = totIncassato(cid);
            const importo = cant.consuntivo ? (cant.consuntivo.lavorazioni?.reduce((s,l)=>s+(parseFloat(l.totale)||0),0)||0) : (parseFloat(cant.importo)||0); // FIX_TUTTI_CANT_IMPONIBILE_v1: usa imponibile consuntivo come la scheda Da incassare
            const residuo = importo - incassato;
            const percInc = importo>0?Math.min(100,Math.round((incassato/importo)*100)):0;
            const isOpen = showPagForm===cid;
            return (
              <div key={cid} className="card" style={{ marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10 }}>
                  <div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{cant.nome}</div>
                    <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>👤 {cant.cliente||"—"}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:theme.success }}>
                      {fmt(incassato)} <span style={{ fontSize:12, color:theme.textDim, fontWeight:400 }}>/ {fmt(importo)}</span>
                    </div>
                    {residuo>0 && <div style={{ fontSize:12, color:"#f5a623", fontWeight:600 }}>Da incassare: {fmt(residuo)}</div>}
                    {residuo<=0 && incassato>0 && <div style={{ fontSize:12, color:"#3ecf8e", fontWeight:600 }}>✓ Saldato</div>}
                  </div>
                </div>

                {/* Barra incasso */}
                <div style={{ height:6, background:theme.border, borderRadius:3, overflow:"hidden", marginBottom:10 }}>
                  <div style={{ height:"100%", width:`${percInc}%`, background:percInc===100?"#3ecf8e":"#f5a623", borderRadius:3, transition:"width 0.4s" }} />
                </div>

                {/* Lista pagamenti */}
                {pags.length > 0 && (
                  <div style={{ marginBottom:10 }}>
                    {pags.map(p=>(
                      <div key={p.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 0", borderBottom:`1px solid ${theme.border}33`, fontSize:13 }}>
                        <span style={{ padding:"2px 8px", borderRadius:5, fontSize:11, fontWeight:700,
                          background:p.tipo==="saldo"?"#0a2218":p.tipo==="acconto"?"#1a1500":"#0a1020",
                          color:p.tipo==="saldo"?"#3ecf8e":p.tipo==="acconto"?"#f5a623":"#4e9af5",
                          border:`1px solid ${p.tipo==="saldo"?"#3ecf8e44":p.tipo==="acconto"?"#f5a62344":"#4e9af544"}` }}>
                          {p.tipo==="saldo"?"Saldo":p.tipo==="acconto"?"Acconto":"Pagamento"}
                        </span>
                        <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.success }}>{fmt(p.importo)}</span>
                        <span style={{ color:theme.textDim }}>{p.metodo}</span>
                        <span style={{ color:theme.textDim, marginLeft:"auto" }}>{p.data}</span>
                        {p.note && <span style={{ fontSize:11, color:theme.textDim }}>· {p.note}</span>}
                        <button onClick={()=>eliminaPagamento(p._id||p.id)}
                          style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:13 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Form nuovo pagamento */}
                {isOpen && (
                  <div style={{ background:theme.bg, borderRadius:10, padding:12, border:`1px solid ${theme.border}`, marginBottom:10 }}>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr 1fr", gap:8, marginBottom:8 }}>
                      <div>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Importo €</div>
                        <input type="number" style={inputSt} placeholder="0" value={pagForm.importo} onChange={e=>setPagForm({...pagForm,importo:e.target.value})} />
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>IVA %</div>
                        <select style={inputSt} value={pagForm.iva||"0"} onChange={e=>setPagForm({...pagForm,iva:e.target.value})}>
                          <option value="0">Esclusa</option>
                          <option value="4">4%</option>
                          <option value="10">10%</option>
                          <option value="22">22%</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Tipo</div>
                        <select style={inputSt} value={pagForm.tipo} onChange={e=>setPagForm({...pagForm,tipo:e.target.value})}>
                          <option value="acconto">Acconto</option>
                          <option value="saldo">Saldo finale</option>
                          <option value="pagamento">Pagamento parziale</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Metodo</div>
                        <select style={inputSt} value={pagForm.metodo} onChange={e=>setPagForm({...pagForm,metodo:e.target.value})}>
                          <option value="bonifico">Bonifico</option>
                          <option value="contanti">Contanti</option>
                          <option value="assegno">Assegno</option>
                          <option value="carta">Carta</option>
                        </select>
                      </div>
                      <div>
                        <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Data</div>
                        <input style={inputSt} placeholder="gg/mm/aaaa" value={pagForm.data} onChange={e=>setPagForm({...pagForm,data:e.target.value})} />
                      </div>
                    </div>
                    <div style={{ marginBottom:8 }}>
                      <div style={{ fontSize:11, color:theme.textDim, marginBottom:3 }}>Note</div>
                      <input style={inputSt} placeholder="es. Prima rata concordata..." value={pagForm.note} onChange={e=>setPagForm({...pagForm,note:e.target.value})} />
                    </div>
                    <div style={{ display:"flex", gap:8 }}>
                      <button className="btn btn-primary btn-sm" onClick={()=>aggiungiPagamento(cid, cant)}>✓ Salva pagamento</button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setShowPagForm(null)}>Annulla</button>
                    </div>
                  </div>
                )}

                <button className="btn btn-ghost btn-sm" onClick={()=>{setShowPagForm(isOpen?null:cid); setPagForm({importo:"",tipo:"acconto",data:"",note:"",metodo:"bonifico"});}}>
                  {isOpen?"✕ Chiudi":"+ Registra pagamento"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── DASHBOARD (vecchia) — tenuta come stub per retrocompat ── */}
      {tab==="dashboard" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
            {[
              { label:"Crediti aperti",   val:fmt(creditiAperti),  color:"#f06060", icon:"⚠️", sub:`${fattureEmesse.filter(f=>f.stato!=="pagata").length} fatture non saldate` },
              { label:"Fatturato totale", val:fmt(totaleFatturato), color:"#3ecf8e", icon:"🧾", sub:`${fmt(totaleIncassato)} incassato` },
              { label:"Spese da pagare",  val:fmt(speseInAttesa),   color:"#f5a623", icon:"📦", sub:`${fmt(totaleSpese)} spese totali` },
            ].map(k=>(
              <div key={k.label} className="card" style={{ padding:"18px 20px" }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{k.icon}</div>
                <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:26, color:k.color }}>{k.val}</div>
                <div style={{ fontSize:12, color:theme.textMuted, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>
          {registro.length > 0 && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                <div className="card-title">⚡ Registro rapido</div>
                <div style={{ display:"flex", gap:16 }}>
                  <span style={{ fontSize:13, color:"#3ecf8e" }}>Entrate: <strong>{fmt(totReg)}</strong></span>
                  <span style={{ fontSize:13, color:"#f06060" }}>Uscite: <strong>{fmt(totRegU)}</strong></span>
                </div>
              </div>
              {registro.slice(0,3).map(r=>(
                <div key={r.id} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                  <span>{r.desc}</span>
                  <span style={{ fontWeight:700, color:r.tipo==="entrata"?"#3ecf8e":"#f06060" }}>{r.tipo==="uscita"?"-":""}{fmt(r.importo)}</span>
                </div>
              ))}
              {registro.length>3 && <div style={{ fontSize:12, color:theme.textDim, marginTop:8, cursor:"pointer" }} onClick={()=>setTab("registro")}>Vedi tutti ({registro.length}) →</div>}
            </div>
          )}
          {fattureUrgenti.length>0 && (
            <div className="card" style={{ marginBottom:20, borderColor:"#f0606044" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
                <span>🔴</span><span style={{ fontWeight:700, color:"#f06060" }}>Fatture scadute o in scadenza entro 7 giorni</span>
              </div>
              {fattureUrgenti.map(f=>{
                const diffGg=Math.round((parseDataIT(f.scadenza)-oggi)/(1000*60*60*24));
                return (
                  <div key={f.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 0", borderBottom:`1px solid ${theme.border}` }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13 }}>{f.cliente}</div>
                      <div style={{ fontSize:12, color:theme.textDim }}>{f.cantiere}</div>
                    </div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>{fmt(f.importo-f.acconto)}</div>
                    <div style={{ fontSize:12, color:diffGg<0?"#f06060":"#f5a623", fontWeight:600 }}>
                      {diffGg<0?`Scaduta ${Math.abs(diffGg)}gg fa`:diffGg===0?"Scade oggi":`Scade in ${diffGg}gg`}
                    </div>
                    {statoBadge(f.stato,f.importo,f.acconto)}
                  </div>
                );
              })}
            </div>
          )}
          <div className="card">
            <div className="card-header" style={{ marginBottom:14 }}>
              <div className="card-title">Ultime fatture</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>setTab("fatture")}>Vedi tutte →</button>
            </div>
            {fattureEmesse.length===0?<div style={{color:theme.textDim,fontSize:13}}>Nessuna fattura ancora.</div>:
              fattureEmesse.slice(0,5).map(f=>(
                <div key={f.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 0", borderBottom:`1px solid ${theme.border}` }}>
                  <div style={{ fontSize:11, color:theme.textDim, minWidth:60 }}>{f.id}</div>
                  <div style={{ flex:1 }}><div style={{ fontWeight:600, fontSize:13 }}>{f.cliente}</div><div style={{ fontSize:12, color:theme.textDim }}>{f.cantiere}</div></div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700 }}>{fmt(f.importo)}</div>
                  {statoBadge(f.stato,f.importo,f.acconto)}
                </div>
              ))}
          </div>
        </>
      )}

      {/* ── SAL CANTIERI ── */}
      {tab==="sal" && (
        <div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
            Seleziona un cantiere per emettere o gestire gli stati di avanzamento lavori.
          </div>
          {cantieriDB.filter(c=>!c.completato).length===0 && (
            <div style={{ textAlign:"center", padding:"40px 0", color:theme.textDim }}>
              <div style={{ fontSize:36, marginBottom:10 }}>🏗️</div>
              <div style={{ fontSize:14, color:theme.textMuted }}>Nessun cantiere attivo</div>
            </div>
          )}
          {cantieriDB.filter(c=>!c.completato).map(cant => {
            const cid = cant._id||cant.id;
            const sals = getSalCantiere(cid);
            const totEmesso = totSalEmessi(cid,cant);
            const residuo = (cant.importo||0) - totEmesso;
            const avanz = cant.avanzamento||0;
            const importoSuggerito = Math.round(((avanz/100)*(cant.importo||0)) - totEmesso);
            const isAperto = cantiereSAL === cid;
            return (
              <div key={cid} className="card" style={{ marginBottom:14, border:`1px solid ${isAperto?theme.accent+"66":theme.border}` }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
                  onClick={()=>setCantiereSAL(isAperto?null:cid)}>
                  <div>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{cant.nome}</div>
                    <div style={{ fontSize:12, color:theme.textMuted, marginTop:3 }}>👤 {cant.cliente} · Importo contratto: <strong>{fmt(cant.importo)}</strong></div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:theme.accent }}>{avanz}%</div>
                    <div style={{ fontSize:11, color:theme.textDim }}>avanzamento</div>
                  </div>
                </div>

                {/* Barra avanzamento */}
                <div style={{ marginTop:10, marginBottom:10 }}>
                  <div style={{ height:6, background:theme.border, borderRadius:3, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${avanz}%`, background:theme.accent, borderRadius:3 }} />
                  </div>
                </div>

                {/* Riepilogo SAL */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:10 }}>
                  {[
                    ["Importo contratto", fmt(cant.importo||0), theme.text],
                    ["SAL emessi",        fmt(totEmesso),       "#4e9af5"],
                    ["Residuo da fatturare", fmt(residuo),      residuo>0?"#3ecf8e":"#f06060"],
                  ].map(([label,val,color])=>(
                    <div key={label} style={{ background:theme.bg, borderRadius:8, padding:"10px 12px" }}>
                      <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{label}</div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                {isAperto && (
                  <div style={{ borderTop:`1px solid ${theme.border}`, paddingTop:14 }}>
                    {/* Lista SAL emessi */}
                    {sals.length > 0 && (
                      <div style={{ marginBottom:14 }}>
                        <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:8, fontWeight:600 }}>SAL emessi</div>
                        {sals.map(sal=>(
                          <div key={sal.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"9px 12px", background:theme.bg, borderRadius:8, marginBottom:6, border:`1px solid ${sal.pagato?"#3ecf8e33":theme.border}` }}>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:13, fontWeight:600 }}>SAL {sal.perc}% — {fmt(sal.importo)}</div>
                              <div style={{ fontSize:11, color:theme.textDim }}>{sal.data}{sal.note?" · "+sal.note:""}</div>
                            </div>
                            <span onClick={()=>setSalList({...salList,[cid]:sals.map(s=>s.id===sal.id?{...s,pagato:!s.pagato}:s)})}
                              style={{ fontSize:11, fontWeight:700, cursor:"pointer", padding:"3px 10px", borderRadius:6,
                                background:sal.pagato?"#0a2218":"#1a0a0a", color:sal.pagato?"#3ecf8e":"#f06060",
                                border:`1px solid ${sal.pagato?"#3ecf8e44":"#f0606044"}` }}>
                              {sal.pagato?"✓ Pagato":"Da incassare"}
                            </span>
                            <button onClick={()=>setSalList({...salList,[cid]:sals.filter(s=>s.id!==sal.id)})}
                              style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:16 }}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Nuovo SAL */}
                    {importoSuggerito > 0 && (
                      <div style={{ marginBottom:10, padding:"10px 14px", background:"#0a1f0a", borderRadius:8, border:"1px solid #3ecf8e33", fontSize:13, color:"#3ecf8e" }}>
                        💡 In base all'avanzamento attuale ({avanz}%), puoi fatturare <strong>{fmt(importoSuggerito)}</strong>
                        <button className="btn btn-ghost btn-sm" style={{ marginLeft:12, color:"#3ecf8e" }}
                          onClick={()=>{ setSalForm({perc:avanz,importo:String(importoSuggerito),data:new Date().toLocaleDateString("it-IT"),note:""}); setShowSalForm(true); }}>
                          Usa questo importo
                        </button>
                      </div>
                    )}

                    <button className="btn btn-primary btn-sm" onClick={()=>setShowSalForm(!showSalForm)}>+ Emetti SAL</button>

                    {showSalForm && (
                      <div style={{ marginTop:12, background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}` }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                          <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>% avanzamento</div><input style={inputSt} type="number" placeholder="es. 30" value={salForm.perc} onChange={e=>setSalForm({...salForm,perc:e.target.value})} /></div>
                          <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Importo SAL (€)</div><input style={inputSt} type="number" placeholder="0" value={salForm.importo} onChange={e=>setSalForm({...salForm,importo:e.target.value})} /></div>
                          <div><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Data</div><input style={inputSt} placeholder="gg/mm/aaaa" value={salForm.data} onChange={e=>setSalForm({...salForm,data:e.target.value})} /></div>
                        </div>
                        <div style={{ marginBottom:10 }}><div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Note</div><input style={inputSt} placeholder="es. SAL n.1 — primo stato avanzamento" value={salForm.note} onChange={e=>setSalForm({...salForm,note:e.target.value})} /></div>
                        <div style={{ display:"flex", gap:8 }}>
                          <button className="btn btn-primary btn-sm" onClick={()=>aggiungiSAL(cant)}>✓ Emetti SAL</button>
                          <button className="btn btn-ghost btn-sm" onClick={()=>setShowSalForm(false)}>Annulla</button>
                        </div>
                      </div>
                    )}

                    {/* Dettaglio lavorazioni + extra */}
                    {(cant.lavorazioni||[]).length > 0 && (
                      <div style={{ marginTop:16 }}>
                        <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:8, fontWeight:600 }}>Lavorazioni dal preventivo</div>
                        {cant.lavorazioni.map((l,i) => (
                          <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                            <span>{l.desc}</span>
                            <span style={{ color:theme.textMuted }}>{l.qta} {l.um} · <strong style={{ color:theme.accent }}>{fmt(l.totale||0)}</strong></span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── REGISTRO RAPIDO ── */}
      {tab==="registro" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:14, marginBottom:20 }}>
            {[
              ["Entrate totali",  fmt(totReg),          "#3ecf8e"],
              ["Uscite totali",   fmt(totRegU),          "#f06060"],
              ["Saldo",          fmt(totReg-totRegU),   totReg-totRegU>=0?"#3ecf8e":"#f06060"],
            ].map(([label,val,color])=>(
              <div key={label} className="card" style={{ padding:"16px 18px", textAlign:"center" }}>
                <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{label}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:24, color }}>{val}</div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="card-header">
              <div className="card-title">⚡ Registro rapido entrate/uscite</div>
              <button className="btn btn-primary btn-sm" onClick={()=>setShowRegForm(!showRegForm)}>+ Aggiungi voce</button>
            </div>
            <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
              Per lavori piccoli, pagamenti in contanti, spese minute — senza bisogno di fattura.
            </div>

            {showRegForm && (
              <div style={{ background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}`, marginBottom:16 }}>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                  <div style={{ gridColumn:"1/-1" }}>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Descrizione *</div>
                    <input style={inputSt} placeholder="es. Verniciatura ringhiera, Riparazione grondaia..." value={regForm.desc} onChange={e=>setRegForm({...regForm,desc:e.target.value})} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Importo (€) *</div>
                    <input style={inputSt} type="number" placeholder="150" value={regForm.importo} onChange={e=>setRegForm({...regForm,importo:e.target.value})} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Tipo</div>
                    <div style={{ display:"flex", gap:8 }}>
                      {[["entrata","✅ Entrata","#3ecf8e"],["uscita","❌ Uscita","#f06060"]].map(([v,l,col])=>(
                        <div key={v} onClick={()=>setRegForm({...regForm,tipo:v})}
                          style={{ flex:1, padding:"8px 12px", borderRadius:8, cursor:"pointer", textAlign:"center", fontSize:13, fontWeight:600,
                            border:`1px solid ${regForm.tipo===v?col:theme.border}`, background:regForm.tipo===v?col+"22":theme.bg, color:regForm.tipo===v?col:theme.textMuted }}>
                          {l}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Data</div>
                    <input style={inputSt} placeholder="gg/mm/aaaa" value={regForm.data} onChange={e=>setRegForm({...regForm,data:e.target.value})} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Note</div>
                    <input style={inputSt} placeholder="es. contanti, cliente Bianchi..." value={regForm.note} onChange={e=>setRegForm({...regForm,note:e.target.value})} />
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button className="btn btn-primary btn-sm" onClick={async ()=>{
                    if(!regForm.desc||!regForm.importo)return;
                    await aggiungiRegistro({...regForm,data:regForm.data||new Date().toLocaleDateString("it-IT")});
                    setRegForm({desc:"",importo:"",tipo:"entrata",data:"",note:""});
                    setShowRegForm(false);
                  }}>✓ Salva</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setShowRegForm(false)}>Annulla</button>
                </div>
              </div>
            )}

            {registro.length === 0 ? (
              <div style={{ textAlign:"center", padding:"32px 0", color:theme.textDim }}>
                <div style={{ fontSize:36, marginBottom:10 }}>⚡</div>
                <div style={{ fontSize:14, color:theme.textMuted }}>Nessuna voce ancora. Usa questo registro per lavori veloci senza fattura.</div>
              </div>
            ) : registro.map(r => (
              <div key={r.id} style={{ display:"flex", alignItems:"center", gap:14, padding:"11px 0", borderBottom:`1px solid ${theme.border}44` }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:r.tipo==="entrata"?"#3ecf8e":"#f06060", flexShrink:0 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>{r.desc}</div>
                  <div style={{ fontSize:11, color:theme.textDim, marginTop:1 }}>{r.data}{r.note?" · "+r.note:""}</div>
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:r.tipo==="entrata"?"#3ecf8e":"#f06060" }}>
                  {r.tipo==="uscita"?"-":"+"}€ {parseFloat(r.importo||0).toLocaleString("it-IT")}
                </div>
                <button onClick={()=>eliminaRegistro(r._id||r.id)}
                  style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:16 }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FATTURE ── */}
      {tab==="fatture" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">🧾 Fatture emesse</div>
            <button className="btn btn-primary btn-sm" onClick={()=>setShowFtForm(!showFtForm)}>+ Nuova fattura</button>
          </div>
          {showFtForm && (
            <div style={{ background:theme.bg, borderRadius:10, padding:16, border:`1px solid ${theme.border}`, marginBottom:18 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Cliente</div><input style={inputSt} placeholder="Nome cliente" value={ftForm.cliente} onChange={e=>setFtForm({...ftForm,cliente:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Cantiere</div><input list="cant-ft" style={inputSt} placeholder="Cantiere" value={ftForm.cantiere} onChange={e=>setFtForm({...ftForm,cantiere:e.target.value})} /><datalist id="cant-ft">{cantieriDB.map(c=><option key={c._id||c.id} value={c.nome}/>)}</datalist></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Importo (€)</div><input style={inputSt} type="number" placeholder="es. 5000" value={ftForm.importo} onChange={e=>setFtForm({...ftForm,importo:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Acconto ricevuto (€)</div><input style={inputSt} type="number" placeholder="0" value={ftForm.acconto} onChange={e=>setFtForm({...ftForm,acconto:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Data emissione</div><input style={inputSt} placeholder="gg/mm/aaaa" value={ftForm.dataEmissione} onChange={e=>setFtForm({...ftForm,dataEmissione:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Scadenza</div><input style={inputSt} placeholder="gg/mm/aaaa" value={ftForm.scadenza} onChange={e=>setFtForm({...ftForm,scadenza:e.target.value})} /></div>
              </div>
              <div style={{ display:"flex",gap:8 }}><button className="btn btn-primary btn-sm" onClick={aggFattura}>Salva fattura</button><button className="btn btn-ghost btn-sm" onClick={()=>setShowFtForm(false)}>Annulla</button></div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>N°</th><th>Cliente</th><th>Cantiere</th><th>Importo</th><th>Residuo</th><th>Scadenza</th><th>Stato</th><th></th></tr></thead>
              <tbody>
                {fattureEmesse.length===0?<tr><td colSpan={8} style={{textAlign:"center",padding:"20px",color:theme.textDim}}>Nessuna fattura ancora.</td></tr>:
                fattureEmesse.map(f=>{
                  const residuo=f.importo-f.acconto;
                  const sc=parseDataIT(f.scadenza);
                  const scaduta=f.stato!=="pagata"&&sc<oggi;
                  return (
                    <tr key={f.id}>
                      <td style={{fontSize:12,color:theme.textDim}}>{f.id}</td>
                      <td style={{fontWeight:600}}>{f.cliente}</td>
                      <td style={{fontSize:12,color:theme.textDim}}>{f.cantiere}</td>
                      <td style={{fontFamily:"'Syne',sans-serif",fontWeight:700}}>{fmt(f.importo)}</td>
                      <td style={{fontFamily:"'Syne',sans-serif",fontWeight:700,color:residuo>0?"#f06060":"#3ecf8e"}}>{residuo>0?fmt(residuo):"—"}</td>
                      <td style={{fontSize:13,color:scaduta?"#f06060":theme.textMuted,fontWeight:scaduta?700:400}}>{f.scadenza}{scaduta?" ⚠️":""}</td>
                      <td><select value={f.stato} onChange={e=>aggiornaStatoFt(f.id,e.target.value)} style={{...inputSt,width:"auto",padding:"4px 8px",fontSize:12}}><option value="non_pagata">Non pagata</option><option value="acconto">Acconto</option><option value="pagata">Pagata</option></select></td>
                      <td><button onClick={()=>eliminaFt(f.id)} style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:16}}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
              {fattureEmesse.length>0&&<tfoot><tr style={{background:"#0a1a0a"}}><td colSpan={3} style={{padding:"10px 12px",fontWeight:700}}>Totale</td><td style={{padding:"10px 12px",fontFamily:"'Syne',sans-serif",fontWeight:800,color:theme.success}}>{fmt(totaleFatturato)}</td><td style={{padding:"10px 12px",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#f06060"}}>{fmt(creditiAperti)}</td><td colSpan={3}/></tr></tfoot>}
            </table>
          </div>
        </div>
      )}

      {/* ── SPESE ── */}
      {tab==="spese" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">📦 Spese e forniture</div>
            <button className="btn btn-primary btn-sm" onClick={()=>setShowSpForm(!showSpForm)}>+ Nuova spesa</button>
          </div>
          {showSpForm && (
            <div style={{ background:theme.bg, borderRadius:10, padding:16, border:`1px solid ${theme.border}`, marginBottom:18 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Fornitore</div><input style={inputSt} placeholder="Nome fornitore" value={spForm.fornitore} onChange={e=>setSpForm({...spForm,fornitore:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Cantiere</div><input list="cant-sp" style={inputSt} placeholder="Cantiere" value={spForm.cantiere} onChange={e=>setSpForm({...spForm,cantiere:e.target.value})} /><datalist id="cant-sp">{cantieriDB.map(c=><option key={c._id||c.id} value={c.nome}/>)}</datalist></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Descrizione</div><input style={inputSt} placeholder="es. Piastrelle gres 60x60" value={spForm.descrizione} onChange={e=>setSpForm({...spForm,descrizione:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Importo (€)</div><input style={inputSt} type="number" placeholder="0" value={spForm.importo} onChange={e=>setSpForm({...spForm,importo:e.target.value})} /></div>
                <div><div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>Data</div><input style={inputSt} placeholder="gg/mm/aaaa" value={spForm.data} onChange={e=>setSpForm({...spForm,data:e.target.value})} /></div>
                <div style={{ display:"flex",alignItems:"center",gap:10,paddingTop:20 }}><input type="checkbox" checked={spForm.pagata} onChange={e=>setSpForm({...spForm,pagata:e.target.checked})} style={{width:16,height:16,accentColor:theme.accent}}/><span style={{fontSize:13}}>Già pagata</span></div>
              </div>
              <div style={{ display:"flex",gap:8 }}><button className="btn btn-primary btn-sm" onClick={aggSpesa}>Salva spesa</button><button className="btn btn-ghost btn-sm" onClick={()=>setShowSpForm(false)}>Annulla</button></div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Fornitore</th><th>Cantiere</th><th>Descrizione</th><th>Data</th><th>Importo</th><th>Stato</th><th></th></tr></thead>
              <tbody>
                {spese.length===0?<tr><td colSpan={7} style={{textAlign:"center",padding:"20px",color:theme.textDim}}>Nessuna spesa ancora.</td></tr>:
                spese.map(sp=>(
                  <tr key={sp.id}>
                    <td style={{fontWeight:600}}>{sp.fornitore}</td>
                    <td style={{fontSize:12,color:theme.textDim}}>{sp.cantiere||"—"}</td>
                    <td style={{fontSize:13}}>{sp.descrizione}</td>
                    <td style={{fontSize:12,color:theme.textMuted}}>{sp.data}</td>
                    <td style={{fontFamily:"'Syne',sans-serif",fontWeight:700}}>{fmt(sp.importo)}</td>
                    <td><span onClick={()=>setSpese(spese.map(s=>s.id===sp.id?{...s,pagata:!s.pagata}:s))} style={{background:sp.pagata?"#0a2218":"#1a0a0a",color:sp.pagata?"#3ecf8e":"#f06060",border:`1px solid ${sp.pagata?"#3ecf8e44":"#f0606044"}`,borderRadius:6,padding:"2px 10px",fontSize:11,fontWeight:700,cursor:"pointer"}}>{sp.pagata?"Pagata":"Da pagare"}</span></td>
                    <td><button onClick={()=>eliminaSp(sp.id)} style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:16}}>✕</button></td>
                  </tr>
                ))}
              </tbody>
              {spese.length>0&&<tfoot><tr style={{background:"#0a1a0a"}}><td colSpan={4} style={{padding:"10px 12px",fontWeight:700}}>Totale</td><td style={{padding:"10px 12px",fontFamily:"'Syne',sans-serif",fontWeight:800,color:"#f5a623"}}>{fmt(totaleSpese)}</td><td style={{padding:"10px 12px",fontSize:12,color:theme.textDim}}>Da pagare: <strong style={{color:"#f06060"}}>{fmt(speseInAttesa)}</strong></td><td/></tr></tfoot>}
            </table>
          </div>
        </div>
      )}

      {/* ── RIEPILOGO PER CANTIERE ── */}
      {tab==="riepilogo" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
            {[["Fatturato totale",fmt(totaleFatturato),theme.text],["Totale incassato",fmt(totaleIncassato),"#3ecf8e"],["Totale spese",fmt(totaleSpese),"#f5a623"]].map(([l,v,c])=>(
              <div key={l} className="card" style={{ padding:"16px 20px",textAlign:"center" }}>
                <div style={{ fontSize:11,color:theme.textDim,textTransform:"uppercase",letterSpacing:1,marginBottom:6 }}>{l}</div>
                <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:c }}>{v}</div>
              </div>
            ))}
          </div>
          {riepilogoCantieri.length===0?<div style={{textAlign:"center",padding:"40px 0",color:theme.textDim}}>Nessun dato ancora. Aggiungi fatture e spese per vedere il riepilogo.</div>:
          riepilogoCantieri.map(rc=>{
            const margine=rc.fatturato>0?Math.round((rc.utile/rc.fatturato)*100):0;
            return (
              <div key={rc.nome} className="card" style={{ marginBottom:14 }}>
                <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16 }}>
                  <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:15 }}>{rc.nome}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,color:rc.utile>=0?"#3ecf8e":"#f06060" }}>
                    {rc.utile>=0?"+":""}{fmt(rc.utile)}<span style={{fontSize:12,marginLeft:6,color:theme.textDim}}>utile</span>
                  </div>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:14 }}>
                  {[["Fatturato",fmt(rc.fatturato),theme.text],["Incassato",fmt(rc.incassato),"#3ecf8e"],["Spese",fmt(rc.costi),"#f5a623"],["Margine",`${margine}%`,margine>=30?"#3ecf8e":margine>=15?"#f5a623":"#f06060"]].map(([l,v,c])=>(
                    <div key={l} style={{ background:theme.bg,borderRadius:8,padding:"10px 14px",border:`1px solid ${theme.border}` }}>
                      <div style={{ fontSize:11,color:theme.textDim,marginBottom:4 }}>{l}</div>
                      <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16,color:c }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex",height:6,borderRadius:4,overflow:"hidden",background:theme.border }}>
                  <div style={{ width:`${Math.min(100,(rc.incassato/Math.max(1,rc.fatturato))*100)}%`,background:"#3ecf8e" }}/>
                  <div style={{ width:`${Math.min(100,(rc.costi/Math.max(1,rc.fatturato))*100)}%`,background:"#f5a623" }}/>
                </div>
                <div style={{ display:"flex",gap:16,marginTop:6,fontSize:11,color:theme.textDim }}>
                  <span style={{color:"#3ecf8e"}}>▬ Incassato</span><span style={{color:"#f5a623"}}>▬ Spese</span>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── SCADENZIARIO ── */}
      {tab==="scadenziario" && (
        <div className="card">
          <div className="card-title" style={{ marginBottom:16 }}>📅 Scadenziario pagamenti</div>
          <div style={{ marginBottom:20 }}>
            <div style={{ fontSize:12,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10,fontWeight:600 }}>💰 Incassi attesi (clienti)</div>
            {fattureEmesse.filter(f=>f.stato!=="pagata").sort((a,b)=>parseDataIT(a.scadenza)-parseDataIT(b.scadenza)).map(f=>{
              const diffGg=Math.round((parseDataIT(f.scadenza)-oggi)/(1000*60*60*24));
              const scaduta=diffGg<0; const urgente=diffGg>=0&&diffGg<=7;
              return (
                <div key={f.id} style={{ display:"flex",alignItems:"center",gap:14,padding:"12px 14px",marginBottom:8,background:theme.bg,borderRadius:10,border:`1px solid ${scaduta?"#f0606044":urgente?"#f5a62344":theme.border}` }}>
                  <div style={{ minWidth:90,fontSize:13,fontWeight:600,color:scaduta?"#f06060":urgente?"#f5a623":theme.textMuted }}>{f.scadenza}</div>
                  <div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:13 }}>{f.cliente}</div><div style={{ fontSize:12,color:theme.textDim }}>{f.cantiere}</div></div>
                  <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16 }}>{fmt(f.importo-f.acconto)}</div>
                  <div style={{ fontSize:12,color:scaduta?"#f06060":urgente?"#f5a623":theme.textDim,fontWeight:600 }}>
                    {scaduta?`Scaduta ${Math.abs(diffGg)}gg fa`:diffGg===0?"Scade oggi":`Scade in ${diffGg}gg`}
                  </div>
                </div>
              );
            })}
            {fattureEmesse.filter(f=>f.stato!=="pagata").length===0&&<div style={{color:theme.textDim,fontSize:13}}>Nessuna fattura da incassare.</div>}
          </div>
          <div>
            <div style={{ fontSize:12,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1,marginBottom:10,fontWeight:600 }}>📦 Pagamenti fornitori in sospeso</div>
            {spese.filter(s=>!s.pagata).map(sp=>(
              <div key={sp.id} style={{ display:"flex",alignItems:"center",gap:14,padding:"12px 14px",marginBottom:8,background:theme.bg,borderRadius:10,border:`1px solid #f5a62344` }}>
                <div style={{ flex:1 }}><div style={{ fontWeight:600,fontSize:13 }}>{sp.fornitore}</div><div style={{ fontSize:12,color:theme.textDim }}>{sp.descrizione} · {sp.cantiere}</div></div>
                <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16,color:"#f5a623" }}>{fmt(sp.importo)}</div>
                <span onClick={()=>setSpese(spese.map(s=>s.id===sp.id?{...s,pagata:true}:s))} className="btn btn-ghost btn-sm" style={{cursor:"pointer"}}>✓ Segna pagata</span>
              </div>
            ))}
            {spese.filter(s=>!s.pagata).length===0&&<div style={{color:theme.textDim,fontSize:13}}>✅ Nessun pagamento in sospeso.</div>}
          </div>
        </div>
      )}
    </div>
  );
}



// ── PIANIFICAZIONE INTELLIGENTE ──────────────────────────────────────────────

// Motore di pianificazione
// operaiCampo = array di nomi operai disponibili in campo
const generaPiano = (backlog, assenzeMock, includeSabato = false, meteoData = null, operaiCampo = []) => {
  const piano = {};
  const oreRimanenti = {};
  backlog.forEach(c => { oreRimanenti[c.id] = Math.max(0, c.oreTotali - c.oreEseguite); });

  const dateList = [];
  const start = new Date(); // parte da oggi
  for (let i = 0; i < 60; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const dow = d.getDay();
    if (dow === 0) continue; // no domenica
    if (dow === 6 && !includeSabato) continue; // sabato solo se abilitato
    const gg = String(d.getDate()).padStart(2,"0");
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const aaaa = d.getFullYear();
    dateList.push(`${gg}/${mm}/${aaaa}`);
  }

  const sorted = [...backlog].sort((a,b) => {
    if (a.priorita !== b.priorita) return a.priorita - b.priorita;
    const pa = a.dataConsegna.split("/").reverse().join("");
    const pb = b.dataConsegna.split("/").reverse().join("");
    return pa.localeCompare(pb);
  });

  dateList.forEach(dataIT => {
    piano[dataIT] = {};
    // Usa meteo reale se disponibile
    const dataISO = dataIT.split("/").reverse().join("-");
    const meteoGiorno = meteoData ? meteoData.find(g=>g.data===dataISO) : null;
    const pioggia = meteoGiorno?.pioggia || 0;

    // Operai disponibili: solo quelli configurati "in campo"
    // Marco è jolly: viene assegnato solo se necessario
    const operaiBase = operaiCampo.filter(op => op !== "Marco");
    const disponibiliBase = operaiBase.filter(op => !assenzeMock.has(`${op}||${dataIT}`));
    const marcoDisp = operaiCampo.includes("Marco") && !assenzeMock.has(`Marco||${dataIT}`);

    const oreOperaio = {};
    [...disponibiliBase, "Marco"].forEach(op => { oreOperaio[op] = 0; });

    // Prima passa: cantieri principali con operai base (senza Marco)
    sorted.forEach(c => {
      if (oreRimanenti[c.id] <= 0) return;
      if (c.esterno && pioggia >= 50) return;

      const opDisp = disponibiliBase
        .filter(op => oreOperaio[op] < 8)
        .slice(0, c.operaiN);

      if (opDisp.length === 0) return;

      const orePerOp = Math.min(8, oreRimanenti[c.id] / opDisp.length);
      opDisp.forEach(op => {
        if (oreOperaio[op] + orePerOp <= 8) {
          piano[dataIT][op] = { cantiereId:c.id, cantiere:c.nome, ore:Math.round(orePerOp*10)/10, colore:c.colore, esterno:c.esterno, piccolo:false };
          oreOperaio[op] += orePerOp;
          oreRimanenti[c.id] = Math.max(0, oreRimanenti[c.id] - orePerOp);
        }
      });
    });

    // Seconda passa: Marco jolly — riempie dove servono braccia in più
    if (marcoDisp) {
      // Trova il cantiere con più ore rimanenti che ha ancora bisogno
      const cantiereConBisogno = sorted.find(c =>
        oreRimanenti[c.id] > 0 && !(c.esterno && pioggia >= 50)
      );
      if (cantiereConBisogno) {
        piano[dataIT]["Marco"] = {
          cantiereId: cantiereConBisogno.id, cantiere: cantiereConBisogno.nome,
          ore: 8, colore: cantiereConBisogno.colore, esterno: cantiereConBisogno.esterno,
          piccolo: false, jolly: true
        };
        oreRimanenti[cantiereConBisogno.id] = Math.max(0, oreRimanenti[cantiereConBisogno.id] - 8);
      }
    }

    // Terza passa: cantieri piccoli per riempire giornate parziali
    sorted.forEach(c => {
      if (oreRimanenti[c.id] <= 0) return;
      if (c.oreTotali > 40) return;
      if (c.esterno && pioggia >= 50) return;

      disponibiliBase.forEach(op => {
        const oreLibere = 8 - oreOperaio[op];
        if (oreLibere >= 2 && !piano[dataIT][op] && oreRimanenti[c.id] > 0) {
          const oreAss = Math.min(oreLibere, oreRimanenti[c.id]);
          piano[dataIT][op] = { cantiereId:c.id, cantiere:c.nome, ore:oreAss, colore:c.colore, esterno:c.esterno, piccolo:true };
          oreOperaio[op] += oreAss;
          oreRimanenti[c.id] = Math.max(0, oreRimanenti[c.id] - oreAss);
        }
      });
    });
  });

  return { piano, oreRimanenti };
};

function Pianificazione({ cantieriDB = [], assenzeDB = [] }) {
  const [backlog, setBacklog] = useState([]);
  const [pianoApprovato, setPianoApprovato] = useState({});
  const [pianoSuggerito, setPianoSuggerito] = useState(null);

  // ── METEO REALE ──────────────────────────────────────────────────────────────
  const [meteoData, setMeteoData] = useState(null); // previsioni 7 giorni
  const [meteoLoading, setMeteoLoading] = useState(true);
  const [registroMeteo, setRegistroMeteo] = useState({}); // { cantiereId: [{data, condizione, pioggia, temp}] }

  // Coordinate Pistoia
  const LAT = 43.93, LON = 10.91;

  const WMO_DESC = {
    0:"Soleggiato",1:"Prevalentemente sereno",2:"Parzialmente nuvoloso",3:"Coperto",
    45:"Nebbia",48:"Nebbia ghiacciata",
    51:"Pioggia leggera",53:"Pioggia moderata",55:"Pioggia intensa",
    61:"Pioggia",63:"Pioggia moderata",65:"Pioggia forte",
    71:"Neve leggera",73:"Neve",75:"Neve intensa",
    80:"Rovesci",81:"Rovesci moderati",82:"Rovesci violenti",
    95:"Temporale",96:"Temporale con grandine",99:"Temporale forte"
  };
  const WMO_ICON = {
    0:"☀️",1:"🌤️",2:"⛅",3:"☁️",
    45:"🌫️",48:"🌫️",
    51:"🌦️",53:"🌧️",55:"🌧️",
    61:"🌧️",63:"🌧️",65:"🌧️",
    71:"🌨️",73:"❄️",75:"❄️",
    80:"🌦️",81:"🌧️",82:"⛈️",
    95:"⛈️",96:"⛈️",99:"⛈️"
  };

  useEffect(() => {
    const fetchMeteo = async () => {
      try {
        setMeteoLoading(true);
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${LAT}&longitude=${LON}&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum&timezone=Europe%2FRome&forecast_days=7`;
        const res = await fetch(url);
        const data = await res.json();
        const giorni = data.daily.time.map((data, i) => ({
          data,
          wmo: data.daily?.weathercode?.[i] ?? data.weathercode?.[i] ?? 0,
          tempMax: Math.round(data.daily?.temperature_2m_max?.[i] ?? data.temperature_2m_max?.[i] ?? 0),
          tempMin: Math.round(data.daily?.temperature_2m_min?.[i] ?? data.temperature_2m_min?.[i] ?? 0),
          pioggia: data.daily?.precipitation_probability_max?.[i] ?? data.precipitation_probability_max?.[i] ?? 0,
          mm: parseFloat((data.daily?.precipitation_sum?.[i] ?? data.precipitation_sum?.[i] ?? 0).toFixed(1)),
        }));
        // Ricostruisce correttamente dall'oggetto data
        const giorni2 = data.daily.time.map((d, i) => ({
          data: d,
          wmo: data.daily.weathercode[i],
          tempMax: Math.round(data.daily.temperature_2m_max[i]),
          tempMin: Math.round(data.daily.temperature_2m_min[i]),
          pioggia: data.daily.precipitation_probability_max[i],
          mm: parseFloat(data.daily.precipitation_sum[i].toFixed(1)),
        }));
        setMeteoData(giorni2);
        // Salva oggi nel registro meteo per ogni cantiere esterno
        const oggi = new Date().toISOString().slice(0,10);
        const oggiDati = giorni2[0];
        if (oggiDati) {
          const cantieriEst = cantieriDB.filter(c=>!c.completato);
          const updates = {};
          cantieriEst.forEach(c => {
            const cid = c._id||c.id;
            const voce = { data: oggi, wmo: oggiDati.wmo, pioggia: oggiDati.pioggia, mm: oggiDati.mm, tempMax: oggiDati.tempMax };
            updates[cid] = voce;
          });
          // Salva in Firebase registro meteo
          for (const [cid, voce] of Object.entries(updates)) {
            try {
              const docRef = doc(db, "cantieri", cid);
              const snap = await getDoc(docRef);
              if (snap.exists()) {
                const existing = snap.data().registroMeteo || [];
                // Non duplicare se già salvato oggi
                if (!existing.find(r => r.data === oggi)) {
                  await updateDoc(docRef, { registroMeteo: [...existing, voce] });
                }
              }
            } catch(e) { console.log("registro meteo:", e.message); }
          }
        }
      } catch(e) {
        console.error("Meteo fetch error:", e);
      } finally {
        setMeteoLoading(false);
      }
    };
    fetchMeteo();
  }, [cantieriDB.length]);

  // Config operai in campo (modificabile dall'utente)
  // Default: tutti tranne Edoardo. Marco è jolly.
  const [operaiCampo, setOperaiCampo] = useState(() =>
    dipendenti.length > 0 ? dipendenti.map(d => d.nome) : ["Ion","Ilie","Mazza","Mahamadou","Amir","Leo","Marco"]
  );

  // Aggiorna operaiCampo quando arrivano i dipendenti da Firebase
  useEffect(() => {
    if (dipendenti.length > 0 && operaiCampo.length <= 1) {
      setOperaiCampo(dipendenti.map(d => d.nome));
    }
  }, [dipendenti.length]);
  const [showConfigOperai, setShowConfigOperai] = useState(false);

  // Inizializza backlog da cantieriDB + ore reali da Firebase
  useEffect(() => {
    if (cantieriDB.length > 0) {
      setBacklog(prev => {
        // Aggiorna ore eseguite per ogni cantiere
        const updated = cantieriDB.filter(c=>!c.completato).map(c => {
          const cid = c._id||c.id;
          const oreTotali = (c.lavorazioni||[]).reduce((s,l)=>s+((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0),0)||40;
          const oreEseguite = assenzeDB
            .filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid||(a.cantiere||"")===(c.nome||"")))
            .reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
          const existing = prev.find(p=>p.id===cid);
          return {
            id: cid, nome: c.nome,
            oreTotali,
            oreEseguite: parseFloat(oreEseguite.toFixed(1)),
            operaiN: existing?.operaiN ?? 2,
            esterno: existing?.esterno ?? false,
            dataConsegna: c.fine||"",
            priorita: existing?.priorita ?? (c.priorita==="alta"?"1":c.priorita==="bassa"?"3":"2"),
            colore: existing?.colore ?? "#f5a623"
          };
        });
        return updated;
      });
    }
  }, [cantieriDB, assenzeDB]);
  const [tab, setTab] = useState("backlog");
  const [weekOffset, setWeekOffset] = useState(0);
  const [showAddCantiere, setShowAddCantiere] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ nome:"", oreTotali:"", operaiN:"1", esterno:false, dataConsegna:"", priorita:"2", colore:"#f5a623" });
  const [modalCella, setModalCella] = useState(null);
  const [cellaCantiereId, setCellaCantiereId] = useState("");
  const [cellaOre, setCellaOre] = useState("8");

  // Sabato: disabilitato di default, attivabile
  const [sabato, setSabato] = useState(false);

  // Assenze dinamiche — aggiungibili dal calendario cliccando sul nome operaio
  const [assenzeSet, setAssenzeSet] = useState(new Set());
  const toggleAssenza = (operaio, dataIT) => {
    setAssenzeSet(prev => {
      const next = new Set(prev);
      const key = `${operaio}||${dataIT}`;
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const inputSt = {
    background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8,
    color:"#e8eaf2", padding:"8px 12px", fontSize:13,
    fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%"
  };

  const generaSuggerimento = () => {
    if (backlog.length === 0) { alert("Nessun cantiere nel backlog. Verifica di avere cantieri attivi."); return; }
    if (operaiCampo.length === 0) { alert("Nessun operaio configurato. Vai su 👷 Forze lavoro e configura gli operai."); return; }
    const { piano } = generaPiano(backlog, assenzeSet, sabato, meteoData, operaiCampo);
    const nGiorni = Object.keys(piano).length;
    const nAssegnazioni = Object.values(piano).reduce((s,g)=>s+Object.keys(g).length,0);
    console.log("Piano generato:", nGiorni, "giorni,", nAssegnazioni, "assegnazioni");
    console.log("Backlog:", backlog.map(c=>c.nome+"("+c.oreTotali+"h)").join(", "));
    console.log("Operai:", operaiCampo.join(", "));
    console.log("Prime date piano:", Object.keys(piano).slice(0,3));
    if (nAssegnazioni === 0) {
      alert("⚠️ Piano vuoto!\n\nPossibili cause:\n• I cantieri nel backlog hanno 0 ore rimanenti\n• Gli operai configurati non coincidono con quelli in lista\n\nBacklog: " + backlog.map(c=>c.nome+" ("+Math.max(0,c.oreTotali-c.oreEseguite)+"h rim.)").join(", "));
      return;
    }
    setPianoSuggerito(piano);
    setTab("calendario");
  };

  const approvaPiano = () => {
    setPianoApprovato(pianoSuggerito || {});
    setPianoSuggerito(null);
  };

  // Modifica manuale cella — funziona sia sul piano suggerito che su quello approvato
  const salvaCella = () => {
    if (!modalCella) return;
    const { operaio, dataIT } = modalCella;
    const cant = backlog.find(c => c.id === cellaCantiereId);
    if (!cant && cellaCantiereId !== "__libero__") return;

    const aggiornaStato = (prev) => {
      const nuovo = {};
      Object.keys(prev).forEach(k => { nuovo[k] = { ...prev[k] }; });
      if (!nuovo[dataIT]) nuovo[dataIT] = {};
      if (cellaCantiereId === "__libero__") {
        const { [operaio]: _removed, ...rest } = nuovo[dataIT];
        nuovo[dataIT] = rest;
      } else {
        nuovo[dataIT][operaio] = {
          cantiereId: cant.id, cantiere: cant.nome,
          ore: parseFloat(cellaOre) || 8,
          colore: cant.colore, esterno: cant.esterno, piccolo: false
        };
      }
      return nuovo;
    };

    if (pianoSuggerito) {
      setPianoSuggerito(aggiornaStato(pianoSuggerito));
    } else {
      setPianoApprovato(aggiornaStato(pianoApprovato));
    }
    setModalCella(null);
  };

  const pianoAttivo = pianoSuggerito || pianoApprovato;

  // Settimana visualizzata
  const getLunedi = (offset) => {
    const base = new Date(); // parte da oggi
    const dow = base.getDay() === 0 ? 6 : base.getDay() - 1;
    const lun = new Date(base);
    lun.setDate(base.getDate() - dow + offset * 7);
    return lun;
  };
  const lunedi = getLunedi(weekOffset);
  const giorniSett = Array.from({length:6}, (_x,i) => {
    const d = new Date(lunedi); d.setDate(lunedi.getDate()+i);
    const gg = String(d.getDate()).padStart(2,"0");
    const mm = String(d.getMonth()+1).padStart(2,"0");
    const aaaa = d.getFullYear();
    const nomi = ["Lun","Mar","Mer","Gio","Ven","Sab"];
    return { label:`${nomi[i]} ${gg}/${mm}`, dataIT:`${gg}/${mm}/${aaaa}`, date:d };
  });
  const titolo = () => {
    const fine = new Date(lunedi); fine.setDate(lunedi.getDate()+5);
    const f = d => d.toLocaleDateString("it-IT",{day:"numeric",month:"short"});
    return `${f(lunedi)} — ${f(fine)} ${lunedi.getFullYear()}`;
  };

  // Statistiche forze lavoro
  const totOperai = operaiCampo.length;
  const oreSettimanali = totOperai * 6 * 8; // 6 giorni x 8 ore
  const oreImpegnate = Object.values(pianoAttivo).reduce((s, giorno) =>
    s + Object.values(giorno).reduce((sg, v) => sg + (v.ore||0), 0), 0);
  const saturazionePerc = Math.round((oreImpegnate / (oreSettimanali * 8)) * 100);

  // Ore rimanenti per cantiere
  const { oreRimanenti: oreRim } = pianoAttivo && Object.keys(pianoAttivo).length > 0
    ? generaPiano(backlog, assenzeSet)
    : { oreRimanenti: {} };

  const salvaCantiere = () => {
    if (!form.nome || !form.oreTotali || !form.dataConsegna) return;
    if (editId) {
      setBacklog(backlog.map(c => c.id === editId ? { ...c, ...form, oreTotali: parseInt(form.oreTotali), operaiN: parseInt(form.operaiN), priorita: parseInt(form.priorita), oreEseguite: c.oreEseguite } : c));
      setEditId(null);
    } else {
      const newId = `C-${String(Math.max(...backlog.map(c=>parseInt(c.id.slice(2)))) + 1).padStart(3,"0")}`;
      setBacklog([...backlog, { ...form, id: newId, oreTotali: parseInt(form.oreTotali), operaiN: parseInt(form.operaiN), priorita: parseInt(form.priorita), oreEseguite: 0 }]);
    }
    setForm({ nome:"", oreTotali:"", operaiN:"1", esterno:false, dataConsegna:"", priorita:"2", colore:"#f5a623" });
    setShowAddCantiere(false);
  };

  const PRIORITA_LABEL = { 1:"🔴 Urgente", 2:"🟡 Normale", 3:"🟢 Bassa" };
  const PRIORITA_COLOR = { 1:"#f06060", 2:"#f5a623", 3:"#3ecf8e" };

  return (
    <div className="content">

      {/* Header con bottone genera piano */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20 }}>🧠 Pianificazione intelligente</div>
          <div style={{ fontSize:13, color:theme.textMuted, marginTop:2 }}>{backlog.length} cantieri nel backlog · {totOperai} operai disponibili</div>
        </div>
        <button className="btn btn-primary" style={{ fontSize:14, padding:"10px 20px" }} onClick={generaSuggerimento}>
          ⚡ Genera piano ottimizzato
        </button>
      </div>

      {pianoSuggerito && (
        <div style={{ background:"#0a1a10", border:"1px solid #3ecf8e44", borderRadius:12, padding:"14px 18px", marginBottom:16, display:"flex", alignItems:"center", gap:16 }}>
          <span style={{ fontSize:20 }}>✨</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, color:"#3ecf8e", fontSize:14 }}>Piano suggerito generato</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>Rivedi il calendario, poi approva o modifica manualmente.</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={approvaPiano}>✓ Approva piano</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setPianoSuggerito(null)}>Scarta</button>
        </div>
      )}

      <div className="tabs">
        {[["backlog","📋 Backlog cantieri"],["calendario","📅 Calendario"],["forze","👷 Forze lavoro"],["meteo","🌤️ Meteo & rischi"]].map(([id,label]) => (
          <div key={id} className={`tab ${tab===id?"active":""}`} onClick={() => setTab(id)}>{label}</div>
        ))}
      </div>

      {/* ── TAB BACKLOG ── */}
      {tab === "backlog" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Cantieri da pianificare</div>
            <div style={{ fontSize:12, color:theme.textDim }}>Spunta "Esterno" per i cantieri all'aperto — il meteo li bloccherà nei giorni di pioggia</div>
          </div>

          {showAddCantiere && (
            <div style={{ background:theme.bg, borderRadius:10, padding:16, border:`1px solid ${theme.border}`, marginBottom:18 }}>
              <div style={{ fontWeight:700, marginBottom:14, fontSize:14 }}>{editId ? "✎ Modifica cantiere" : "➕ Nuovo cantiere"}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                <div className="form-group full">
                  <label>Nome cantiere / cliente</label>
                  <input style={inputSt} placeholder="es. Ristrutturazione bagno Rossi" value={form.nome} onChange={e => setForm({...form, nome:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Ore totali stimate</label>
                  <input style={inputSt} type="number" placeholder="es. 80" value={form.oreTotali} onChange={e => setForm({...form, oreTotali:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Operai contemporanei</label>
                  <input style={inputSt} type="number" min="1" max="8" value={form.operaiN} onChange={e => setForm({...form, operaiN:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Data consegna</label>
                  <input style={inputSt} placeholder="gg/mm/aaaa" value={form.dataConsegna} onChange={e => setForm({...form, dataConsegna:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Priorità</label>
                  <select style={inputSt} value={form.priorita} onChange={e => setForm({...form, priorita:e.target.value})}>
                    <option value="1">🔴 Urgente</option>
                    <option value="2">🟡 Normale</option>
                    <option value="3">🟢 Bassa priorità</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Colore cantiere</label>
                  <div style={{ display:"flex", gap:8, marginTop:4 }}>
                    {["#f5a623","#4e9af5","#3ecf8e","#f06060","#a78bfa","#f472b6","#34d399","#fb923c"].map(col => (
                      <div key={col} onClick={() => setForm({...form, colore:col})}
                        style={{ width:28, height:28, borderRadius:"50%", background:col, cursor:"pointer",
                          border:`3px solid ${form.colore===col?"#fff":"transparent"}`, transition:"all 0.1s" }} />
                    ))}
                  </div>
                </div>
                <div className="form-group" style={{ display:"flex", alignItems:"center", gap:10, paddingTop:20 }}>
                  <input type="checkbox" checked={form.esterno} onChange={e => setForm({...form, esterno:e.target.checked})}
                    style={{ width:16, height:16, accentColor:theme.accent }} />
                  <label style={{ cursor:"pointer" }} onClick={() => setForm({...form, esterno:!form.esterno})}>
                    🏗️ Lavoro esterno (sensibile al meteo)
                  </label>
                </div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={salvaCantiere}>Salva</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddCantiere(false)}>Annulla</button>
              </div>
            </div>
          )}

          {/* Lista backlog */}
          {[1,2,3].map(prio => {
            const group = backlog.filter(c => String(c.priorita) === String(prio));
            if (!group.length) return null;
            return (
              <div key={prio} style={{ marginBottom:20 }}>
                <div style={{ fontSize:12, fontWeight:700, color:PRIORITA_COLOR[prio], textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>
                  {PRIORITA_LABEL[prio]} · {group.length} cantieri
                </div>
                {group.map(c => {
                  const oreRim = Math.max(0, c.oreTotali - c.oreEseguite);
                  const avanz = Math.round((c.oreEseguite / c.oreTotali) * 100);
                  const giorniNec = Math.ceil(oreRim / (c.operaiN * 8));
                  const piccolo = c.oreTotali <= 40;
                  return (
                    <div key={c.id} style={{ background:theme.bg, borderRadius:12, padding:"14px 16px", marginBottom:10,
                      border:`1px solid ${c.colore}33`, display:"flex", alignItems:"center", gap:16 }}>
                      <div style={{ width:4, height:52, borderRadius:4, background:c.colore, flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                          <span style={{ fontWeight:700, fontSize:14 }}>{c.nome}</span>
                          {piccolo && <span style={{ fontSize:10, background:"#a78bfa22", color:"#a78bfa", border:"1px solid #a78bfa44", borderRadius:6, padding:"1px 7px", fontWeight:700 }}>PICCOLO</span>}
                          {c.esterno && <span style={{ fontSize:10, background:"#4e9af522", color:"#4e9af5", border:"1px solid #4e9af544", borderRadius:6, padding:"1px 7px" }}>🏗️ ESTERNO</span>}
                        </div>
                        <div style={{ height:4, background:theme.border, borderRadius:4, width:200, marginBottom:6, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${avanz}%`, background:c.colore, borderRadius:4 }} />
                        </div>
                        <div style={{ fontSize:12, color:theme.textMuted, display:"flex", gap:16 }}>
                          <span>⏱ <strong style={{ color:theme.text }}>{oreRim}h</strong> rimanenti</span>
                          <span>👷 <strong style={{ color:theme.text }}>{c.operaiN}</strong> operai</span>
                          <span>📅 <strong style={{ color:theme.text }}>{giorniNec}</strong> giorni stimati</span>
                          <span>🏁 Consegna: <strong style={{ color:PRIORITA_COLOR[prio] }}>{c.dataConsegna}</strong></span>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                        {/* Toggle rapido esterno */}
                        <div onClick={()=>setBacklog(backlog.map(b=>b.id===c.id?{...b,esterno:!b.esterno}:b))}
                          style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, cursor:"pointer", fontSize:11, fontWeight:600,
                            border:`1px solid ${c.esterno?"#4e9af5":"#2a2e45"}`,
                            background:c.esterno?"#4e9af522":"transparent",
                            color:c.esterno?"#4e9af5":theme.textDim }}>
                          🏗️ {c.esterno?"Esterno":"Interno"}
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => {
                          setEditId(c.id);
                          setForm({ nome:c.nome, oreTotali:String(c.oreTotali), operaiN:String(c.operaiN), esterno:c.esterno, dataConsegna:c.dataConsegna, priorita:String(c.priorita), colore:c.colore });
                          setShowAddCantiere(true);
                        }}>✎</button>
                        <button className="btn btn-sm" style={{ background:theme.danger+"22", color:theme.danger, border:"none", borderRadius:6, padding:"4px 8px", cursor:"pointer" }}
                          onClick={() => setBacklog(backlog.filter(b => b.id !== c.id))}>✕</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* ── TAB CALENDARIO ── */}
      {tab === "calendario" && (
        <>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div style={{ fontSize:15, fontWeight:700 }}>{titolo()}</div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <div onClick={() => setSabato(!sabato)}
              style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:8, cursor:"pointer",
                border:`1px solid ${sabato ? theme.accent : theme.border}`,
                background: sabato ? theme.accent+"22" : theme.bg, fontSize:12,
                color: sabato ? theme.accent : theme.textMuted }}>
              <span>{sabato ? "✓" : "○"}</span> Sabato lavorativo
            </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(weekOffset-1)}>‹ Prec.</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(0)}>Oggi</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setWeekOffset(weekOffset+1)}>Succ. ›</button>
        </div>
          </div>

          {Object.keys(pianoAttivo).length === 0 ? (
            <div className="card" style={{ textAlign:"center", padding:"40px 20px" }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🧠</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>Nessun piano generato</div>
              <div style={{ color:theme.textMuted, fontSize:13, marginBottom:20 }}>Clicca "Genera piano ottimizzato" per vedere il suggerimento automatico</div>
              <button className="btn btn-primary" onClick={generaSuggerimento}>⚡ Genera piano</button>
            </div>
          ) : (
            <div className="card" style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", minWidth:750 }}>
                <thead>
                  <tr>
                    <th style={{ padding:"10px 14px", textAlign:"left", fontSize:12, color:theme.textDim, borderBottom:`1px solid ${theme.border}`, minWidth:130 }}>Operaio</th>
                    {giorniSett.map(g => {
                      const meteo = null;
                      const rischioMeteo = meteo?.pioggia >= 50;
                      return (
                        <th key={g.dataIT} style={{ padding:"8px 8px", textAlign:"center", fontSize:11,
                          borderBottom:`1px solid ${theme.border}`,
                          background: rischioMeteo ? "#f0606008" : "transparent",
                          color: rischioMeteo ? "#f06060" : theme.textDim, minWidth:110 }}>
                          <div>{g.label}</div>
                          {meteo && <div style={{ fontSize:13, marginTop:3 }}>{meteo.icona} <span style={{ fontSize:10 }}>{meteo.pioggia}%</span></div>}
                          {rischioMeteo && <div style={{ fontSize:9, color:"#f06060", fontWeight:700 }}>STOP ESTERNI</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {operaiCampo.map((op, ri) => {
                    const dip = dipendenti.find(d => d.nome === op);
                    return (
                      <tr key={op} style={{ background: ri%2===0 ? "transparent" : "#ffffff06" }}>
                        <td style={{ padding:"8px 14px", borderBottom:`1px solid ${theme.border}` }}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div className="avatar" style={{ background:dip?.colore||"#888", width:26, height:26, fontSize:10 }}>{op[0]}</div>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13, display:"flex", alignItems:"center", gap:6 }}>
                                {op}
                                {op === "Marco" && <span style={{ fontSize:9, background:"#f5a62322", color:"#f5a623", border:"1px solid #f5a62344", borderRadius:4, padding:"1px 5px", fontWeight:700 }}>JOLLY</span>}
                              </div>
                              {/* Click rapido assenza per ogni giorno della settimana */}
                              <div style={{ display:"flex", gap:3, marginTop:4 }}>
                                {giorniSett.map(g => {
                                  const isAss = assenzeSet.has(`${op}||${g.dataIT}`);
                                  return (
                                    <div key={g.dataIT} onClick={() => toggleAssenza(op, g.dataIT)}
                                      title={`${isAss ? "Rimuovi assenza" : "Segna assente"} ${g.label}`}
                                      style={{ width:14, height:14, borderRadius:3, cursor:"pointer",
                                        background: isAss ? "#f0606066" : theme.border,
                                        border: `1px solid ${isAss ? "#f06060" : theme.border}`,
                                        transition:"all 0.15s" }} />
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </td>
                        {giorniSett.map(g => {
                          const ass = pianoAttivo[g.dataIT]?.[op];
                          const assente = assenzeSet.has(`${op}||${g.dataIT}`);
                          const meteo = null;
                          const stopMeteo = ass?.esterno && (meteo?.pioggia||0) >= 50;
                          const haPiano = Object.keys(pianoAttivo).length > 0;

                          const apriModifica = () => {
                            if (assente || !haPiano) return;
                            setModalCella({ operaio: op, dataIT: g.dataIT, label: g.label });
                            setCellaCantiereId(ass?.cantiereId || "");
                            setCellaOre(String(ass?.ore || 8));
                          };

                          return (
                            <td key={g.dataIT}
                              onClick={apriModifica}
                              style={{ padding:"5px 5px", borderBottom:`1px solid ${theme.border}`, verticalAlign:"middle",
                                cursor: haPiano && !assente ? "pointer" : "default" }}>
                              {assente ? (
                                <div style={{ textAlign:"center", padding:"6px 4px", borderRadius:8,
                                  background:"#3b82f611", border:"1px dashed #3b82f633",
                                  fontSize:11, color:"#3b82f6", fontWeight:600 }}>🏖️ Assente</div>
                              ) : stopMeteo ? (
                                <div style={{ textAlign:"center", padding:"6px 4px", borderRadius:8,
                                  background:"#f0606011", border:"1px dashed #f0606033",
                                  fontSize:11, color:"#f06060" }}>🌧️ Stop meteo</div>
                              ) : ass ? (
                                <div style={{ padding:"6px 8px", borderRadius:8,
                                  background: ass.colore+"22", border:`1px solid ${ass.colore}44`,
                                  position:"relative" }}>
                                  <div style={{ fontSize:11, fontWeight:700, color:ass.colore, lineHeight:1.3, marginBottom:3 }}>
                                    {ass.cantiere.length>22 ? ass.cantiere.slice(0,20)+"…" : ass.cantiere}
                                  </div>
                                  <div style={{ fontSize:10, color:theme.textDim, display:"flex", justifyContent:"space-between" }}>
                                    <span>{ass.ore}h {ass.piccolo ? "·🔸" : ""}</span>
                                    {haPiano && <span style={{ color:theme.accent, fontSize:10 }}>✎</span>}
                                  </div>
                                </div>
                              ) : haPiano ? (
                                <div style={{ textAlign:"center", color: theme.accent+"88",
                                  fontSize:16, padding:"8px 0", borderRadius:8,
                                  border:`1px dashed ${theme.accent+"44"}` }}>+</div>
                              ) : (
                                <div style={{ textAlign:"center", color:theme.border,
                                  fontSize:18, padding:"8px 0", borderRadius:8,
                                  border:`1px dashed ${theme.border}` }}>—</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Legenda */}
          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginTop:12 }}>
            {backlog.map(c => (
              <div key={c.id} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 10px",
                background:c.colore+"18", borderRadius:8, border:`1px solid ${c.colore}33` }}>
                <div style={{ width:8, height:8, borderRadius:"50%", background:c.colore }} />
                <span style={{ fontSize:11, fontWeight:600, color:c.colore }}>{c.nome}</span>
              </div>
            ))}
          </div>
          {Object.keys(pianoAttivo).length > 0 && (
            <div style={{ fontSize:12, color:theme.textMuted, marginTop:10, textAlign:"center" }}>
              ✎ Clicca su qualsiasi cella per cambiare il cantiere dell'operaio
            </div>
          )}
        </>
      )}

      {/* Modal modifica cella piano approvato */}
      {modalCella && (
        <div style={{ position:"fixed", inset:0, background:"#00000088", zIndex:1000,
          display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => setModalCella(null)}>
          <div style={{ background:"#1a1d27", borderRadius:14, padding:24, minWidth:340,
            border:`1px solid ${theme.border}`, boxShadow:"0 20px 60px #00000066" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, marginBottom:4 }}>
              ✎ Modifica assegnazione
            </div>
            <div style={{ fontSize:13, color:theme.textMuted, marginBottom:18 }}>
              {modalCella.operaio} · {modalCella.label}
            </div>

            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, color:theme.textDim, marginBottom:8 }}>Cantiere</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:280, overflowY:"auto" }}>
                {backlog.map(c => (
                  <div key={c.id} onClick={() => setCellaCantiereId(c.id)}
                    style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                      borderRadius:10, cursor:"pointer",
                      border:`1px solid ${cellaCantiereId===c.id ? c.colore : theme.border}`,
                      background: cellaCantiereId===c.id ? c.colore+"22" : theme.bg }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:c.colore, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600 }}>{c.nome}</div>
                      <div style={{ fontSize:11, color:theme.textDim }}>{Math.max(0,c.oreTotali-c.oreEseguite)}h rimanenti · {c.operaiN} operai</div>
                    </div>
                    {cellaCantiereId===c.id && <span style={{ color:c.colore }}>✓</span>}
                  </div>
                ))}
                <div onClick={() => setCellaCantiereId("__libero__")}
                  style={{ padding:"10px 14px", borderRadius:10, cursor:"pointer",
                    border:`1px solid ${cellaCantiereId==="__libero__" ? "#f06060" : theme.border}`,
                    background: cellaCantiereId==="__libero__" ? "#f0606011" : theme.bg,
                    fontSize:13, color:"#f06060", textAlign:"center", fontWeight:600 }}>
                  🗑️ Rimuovi assegnazione
                </div>
              </div>
            </div>

            {cellaCantiereId && cellaCantiereId !== "__libero__" && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, color:theme.textDim, marginBottom:6 }}>Ore per questo giorno</div>
                <div style={{ display:"flex", gap:8 }}>
                  {["4","5","6","7","8"].map(h => (
                    <div key={h} onClick={() => setCellaOre(h)}
                      style={{ flex:1, textAlign:"center", padding:"8px 0", borderRadius:8, cursor:"pointer",
                        border:`1px solid ${cellaOre===h ? theme.accent : theme.border}`,
                        background: cellaOre===h ? theme.accent+"22" : theme.bg,
                        fontFamily:"'Syne',sans-serif", fontWeight:700,
                        color: cellaOre===h ? theme.accent : theme.textMuted }}>
                      {h}h
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-primary" style={{ flex:1 }} onClick={salvaCella}>Salva</button>
              <button className="btn btn-ghost" onClick={() => setModalCella(null)}>Annulla</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB FORZE LAVORO ── */}
      {tab === "forze" && (
        <>
          {/* KPI */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
            {[
              { label:"Operai disponibili", val:totOperai, icon:"👷", color:theme.text, sub:"su 8 totali" },
              { label:"Ore settim. disponibili", val:`${totOperai*48}h`, icon:"⏱️", color:"#4e9af5", sub:`${totOperai} operai × 6gg × 8h` },
              { label:"Carico backlog", val:`${backlog.reduce((s,c)=>s+Math.max(0,c.oreTotali-c.oreEseguite),0)}h`, icon:"📋", color:"#f5a623", sub:"ore totali rimanenti" },
            ].map(k => (
              <div key={k.label} className="card" style={{ padding:"18px 20px" }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{k.icon}</div>
                <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:28, color:k.color }}>{k.val}</div>
                <div style={{ fontSize:12, color:theme.textMuted, marginTop:4 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Operai */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:16 }}>Squadra disponibile</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:12 }}>
              {dipendenti.map(dip => {
                const oreBacklog = backlog.reduce((s,c) => {
                  return s + Math.max(0, c.oreTotali - c.oreEseguite) / c.operaiN;
                }, 0);
                const settimaneNec = Math.ceil(oreBacklog / (5*8));
                return (
                  <div key={dip.nome} style={{ background:theme.bg, borderRadius:12, padding:"14px 16px",
                    border:`1px solid ${dip.colore}33`, display:"flex", alignItems:"center", gap:14 }}>
                    <div className="avatar" style={{ background:dip.colore, width:44, height:44, fontSize:18, flexShrink:0 }}>{dip.nome[0]}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:14, marginBottom:2 }}>{dip.nome}</div>
                      <div style={{ fontSize:12, color:theme.textDim, marginBottom:6 }}>{dip.ruolo}</div>
                      <div style={{ display:"flex", gap:12, fontSize:12, color:theme.textMuted }}>
                        <span>📋 <strong style={{ color:theme.text }}>{Math.round(oreBacklog)}h</strong> assegnate</span>
                        <span>📅 ~<strong style={{ color:theme.text }}>{settimaneNec}</strong> sett.</span>
                      </div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ width:40, height:40, borderRadius:"50%",
                        background: oreBacklog > 160 ? "#f0606022" : "#3ecf8e22",
                        border: `2px solid ${oreBacklog > 160 ? "#f06060" : "#3ecf8e"}`,
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:10, fontWeight:700, color: oreBacklog > 160 ? "#f06060" : "#3ecf8e" }}>
                        {oreBacklog > 160 ? "PIENO" : "OK"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Settimane necessarie per finire */}
          <div className="card">
            <div className="card-title" style={{ marginBottom:16 }}>Stima completamento cantieri</div>
            {backlog.map(c => {
              const oreRim = Math.max(0, c.oreTotali - c.oreEseguite);
              const giorniNec = Math.ceil(oreRim / (c.operaiN * 8));
              const settNec = Math.ceil(giorniNec / 6);
              const avanz = Math.round((c.oreEseguite/c.oreTotali)*100);
              return (
                <div key={c.id} style={{ marginBottom:14, padding:"12px 14px", background:theme.bg, borderRadius:10, border:`1px solid ${c.colore}33` }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:c.colore }}>{c.nome}</div>
                    <div style={{ fontSize:12, color:theme.textMuted }}>
                      🏁 <strong style={{ color:PRIORITA_COLOR[c.priorita] }}>{c.dataConsegna}</strong>
                      {c.esterno && <span style={{ marginLeft:8, color:"#4e9af5" }}>🏗️ esterno</span>}
                    </div>
                  </div>
                  <div style={{ height:6, background:theme.border, borderRadius:4, marginBottom:8, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${avanz}%`, background:c.colore, borderRadius:4 }} />
                  </div>
                  <div style={{ display:"flex", gap:20, fontSize:12, color:theme.textMuted }}>
                    <span>Avanzamento: <strong style={{ color:theme.text }}>{avanz}%</strong></span>
                    <span>Rimanenti: <strong style={{ color:theme.text }}>{oreRim}h</strong></span>
                    <span>Con {c.operaiN} operai: <strong style={{ color:theme.text }}>{giorniNec} giorni</strong> (~{settNec} sett.)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── TAB METEO & RISCHI ── */}
      {tab === "meteo" && (
        <div>
          {/* Previsioni 7 giorni */}
          <div className="card" style={{ marginBottom:16 }}>
            <div className="card-title" style={{ marginBottom:6 }}>🌤️ Previsioni meteo — Provincia di Pistoia</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginBottom:16 }}>
              I cantieri <strong>esterni</strong> vengono bloccati automaticamente nei giorni con pioggia &gt;50%.
            </div>
            {meteoLoading ? (
              <div style={{ color:theme.textMuted, padding:20, textAlign:"center" }}>Caricamento previsioni...</div>
            ) : !meteoData ? (
              <div style={{ color:"#f06060", padding:20, textAlign:"center" }}>❌ Impossibile caricare le previsioni. Verifica la connessione.</div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:8 }}>
                {meteoData.map((g, i) => {
                  const stop = g.pioggia >= 50;
                  const data = new Date(g.data+"T12:00:00");
                  const giornoLabel = i===0 ? "Oggi" : i===1 ? "Dom." : data.toLocaleDateString("it-IT",{weekday:"short"}).replace(".","");
                  const dataLabel = data.toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit"});
                  const wmoKey = Object.keys(WMO_ICON).map(Number).reverse().find(k=>k<=g.wmo) ?? 0;
                  return (
                    <div key={g.data} style={{ background:stop?"#1f0a0a":theme.bg, borderRadius:10, padding:"10px 8px", textAlign:"center", border:`1px solid ${stop?"#f0606044":theme.border}` }}>
                      <div style={{ fontSize:11, fontWeight:700, color:i===0?theme.accent:theme.textMuted, marginBottom:2 }}>{giornoLabel}</div>
                      <div style={{ fontSize:10, color:theme.textDim, marginBottom:6 }}>{dataLabel}</div>
                      <div style={{ fontSize:28, marginBottom:6 }}>{WMO_ICON[wmoKey]||"🌤️"}</div>
                      <div style={{ fontSize:11, fontWeight:600, marginBottom:4 }}>
                        <span style={{color:"#f5a623"}}>{g.tempMax}°</span>
                        <span style={{color:theme.textDim}}> / {g.tempMin}°</span>
                      </div>
                      <div style={{ fontSize:10, color:stop?"#f06060":"#4e9af5", fontWeight:700 }}>{g.pioggia}%</div>
                      {g.mm>0 && <div style={{ fontSize:9, color:theme.textDim }}>{g.mm}mm</div>}
                      {stop && <div style={{ fontSize:9, color:"#f06060", fontWeight:700, marginTop:4 }}>⛔ Stop ext.</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cantieri esterni con impatto meteo */}
          {backlog.filter(c=>c.esterno).length > 0 && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, marginBottom:12, fontSize:14 }}>🏗️ Cantieri esterni — impatto meteo prossimi 7 giorni</div>
              {backlog.filter(c=>c.esterno).map(c => {
                const giorniStop = meteoData ? meteoData.filter(g=>g.pioggia>=50).length : 0;
                const orePersa = giorniStop * c.operaiN * 8;
                return (
                  <div key={c.id} style={{ background:theme.bg, borderRadius:10, padding:"12px 16px", marginBottom:10, border:`1px solid ${c.colore}44`, display:"flex", gap:16, alignItems:"center" }}>
                    <div style={{ width:4, height:44, borderRadius:4, background:c.colore, flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13 }}>{c.nome}</div>
                      <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>
                        {giorniStop} giorni di stop previsti nei prossimi 7 giorni
                      </div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color: giorniStop>0?"#f06060":"#3ecf8e" }}>
                        {giorniStop>0 ? `-${orePersa}h` : "✓ OK"}
                      </div>
                      <div style={{ fontSize:11, color:theme.textDim }}>{giorniStop>0?"ore perse stimate":"nessun stop"}</div>
                    </div>
                    {giorniStop>0 && (
                      <div style={{ textAlign:"center", background:"#f5a62322", borderRadius:8, padding:"8px 12px" }}>
                        <div style={{ fontSize:11, color:theme.textDim }}>Slittamento</div>
                        <div style={{ fontWeight:700, color:"#f5a623" }}>+{giorniStop} gg</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Registro meteo per cantiere */}
          <div className="card">
            <div style={{ fontWeight:700, marginBottom:12, fontSize:14 }}>📋 Registro meteo cantieri — storico giorni di pioggia</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginBottom:14 }}>
              Aggiornato automaticamente ogni giorno. Utile per contestazioni su ritardi di consegna.
            </div>
            {cantieriDB.filter(c=>!c.completato && (c.registroMeteo||[]).length>0).length === 0 ? (
              <div style={{ color:theme.textDim, fontSize:13, fontStyle:"italic" }}>
                Il registro si popola automaticamente ogni giorno che apri la pianificazione. Torna domani per vedere il primo record.
              </div>
            ) : (
              cantieriDB.filter(c=>!c.completato && (c.registroMeteo||[]).length>0).map(c => {
                const registro = (c.registroMeteo||[]).sort((a,b)=>b.data.localeCompare(a.data));
                const giorniPioggia = registro.filter(r=>r.pioggia>=50).length;
                const totGiorni = registro.length;
                return (
                  <div key={c._id||c.id} style={{ marginBottom:16, background:theme.bg, borderRadius:10, padding:"12px 16px", border:`1px solid ${theme.border}` }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                      <div style={{ fontWeight:700 }}>{c.nome}</div>
                      <div style={{ display:"flex", gap:12, fontSize:12 }}>
                        <span style={{ color:"#f06060" }}>🌧️ {giorniPioggia} giorni pioggia</span>
                        <span style={{ color:theme.textMuted }}>su {totGiorni} giorni registrati</span>
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                      {registro.slice(0,30).map(r => {
                        const wmoKey = Object.keys(WMO_ICON).map(Number).reverse().find(k=>k<=r.wmo) ?? 0;
                        const isPioggia = r.pioggia >= 50;
                        const dataFmt = new Date(r.data+"T12:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"2-digit"});
                        return (
                          <div key={r.data} title={`${dataFmt} · ${r.pioggia}% pioggia · ${r.tempMax}°C`}
                            style={{ width:32, height:32, borderRadius:6, background:isPioggia?"#f0606022":"#3ecf8e11", border:`1px solid ${isPioggia?"#f0606044":"#3ecf8e44"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, cursor:"default" }}>
                            {WMO_ICON[wmoKey]||"🌤️"}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── GIORNALIERA ─────────────────────────────────────────────────────────────

// Dati iniziali: ogni voce = una riga della giornaliera
// { id, operaio, data, cantiere (testo libero modificabile), ore }
// Tipi di assenza
const TIPI_ASSENZA = ["Ferie", "Permesso", "Malattia", "Infortunio"];

const formatData = (dataIT) => {
  const [gg, mm, aaaa] = dataIT.split("/");
  const d = new Date(`${aaaa}-${mm}-${gg}`);
  const giorni = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];
  return `${giorni[d.getDay()]} ${gg}/${mm}`;
};

function Giornaliera({ assenzeDB = [], cantieriDB = [] }) {
  const [correzioni, setCorrezioni] = useState({});
  const [tab, setTab] = useState("riepilogo"); // riepilogo | singolo | assenze | pdf
  const [operaioAperto, setOperaioAperto] = useState(null); // nome operaio nel dettaglio
  const [operaioFiltro, setOperaioFiltro] = useState("Tutti");
  const [meseFiltro, setMeseFiltro] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth()+1).padStart(2,"0")}/${now.getFullYear()}`;
  });
  const [showAsForm, setShowAsForm] = useState(false);
  const [asForm, setAsForm] = useState({ operaio:"", data:"", tipo:"Ferie", ore:"8", note:"" });
  const [pdfOperaio, setPdfOperaio] = useState(dipendenti[0].nome);
  const [pdfMese, setPdfMese] = useState(() => { const n=new Date(); return `${String(n.getMonth()+1).padStart(2,"0")}/${n.getFullYear()}`; });
  const [editMode, setEditMode] = useState(false);

  const inputSt = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%" };

  // ── Ore lavorate da Firebase (tipo = "lavorato") ──────────────────────────
  const oreReali = assenzeDB.filter(a => a.tipo === "lavorato");
  // ── Assenze da Firebase (tipo != "lavorato") ──────────────────────────────
  // Assenze visibili: solo Ferie/Permesso/Malattia/Infortunio — NON le foto
  const assenzeFB = assenzeDB.filter(a => a.tipo !== "lavorato" && a.tipo !== "foto" && a.extra !== true);

  // Aggiungi assenza su Firebase
  const aggiungiAssenza = async () => {
    if (!asForm.operaio || !asForm.data || !asForm.ore) return;
    try {
      await addDoc(collection(db, "assenze"), { ...asForm, ore:parseFloat(asForm.ore), createdAt:new Date().toISOString() });
      setAsForm({ operaio:"", data:"", tipo:"Ferie", ore:"8", note:"" });
      setShowAsForm(false);
    } catch(e) { alert("❌ "+e.message); }
  };

  const eliminaAssenza = async (id) => {
    if (!window.confirm("Eliminare questa assenza?")) return;
    try { await deleteDoc(doc(db,"assenze",id)); } catch(e) { alert("❌ "+e.message); }
  };

  // Correzioni titolare (solo per PDF, non tocca Firebase)
  const getCorrVoce = (operaio, data, idx, campo, valDefault) => correzioni[`${operaio}||${data}||${idx}`]?.[campo] ?? valDefault;
  const setCorrVoce = (operaio, data, idx, campo, val) => {
    const key = `${operaio}||${data}||${idx}`;
    setCorrezioni(prev => ({ ...prev, [key]:{ ...prev[key],[campo]:val } }));
  };

  // Navigazione mese
  const cambioMese = (delta) => {
    const [mm, yyyy] = meseFiltro.split("/").map(Number);
    const d = new Date(yyyy, mm-1+delta, 1);
    setMeseFiltro(`${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`);
  };

  // Costruisce righe per un operaio in un mese — include TUTTI i giorni lavorativi
  const getRigheOperaio = (nomeOp, mese) => {
    const [mm, yyyy] = mese.split("/").map(Number);
    const oreOp = oreReali.filter(r => r.operaio===nomeOp && r.data?.slice(3)===mese);
    const assenzeOp = assenzeFB.filter(a => a.operaio===nomeOp && a.data?.slice(3)===mese);
    const perData = {};
    // Inserisci tutti i giorni del mese (lun-sab)
    const giorniMese = new Date(yyyy, mm, 0).getDate();
    for (let g=1; g<=giorniMese; g++) {
      const d = new Date(yyyy, mm-1, g);
      if (d.getDay()===0) continue; // salta domenica
      const key = `${String(g).padStart(2,"0")}/${String(mm).padStart(2,"0")}/${yyyy}`;
      perData[key] = { voci:[], assenze:[] };
    }
    oreOp.forEach(r => {
      if (!perData[r.data]) perData[r.data] = { voci:[], assenze:[] };
      perData[r.data].voci.push({...r});
    });
    assenzeOp.forEach(a => {
      if (!perData[a.data]) perData[a.data] = { voci:[], assenze:[] };
      perData[a.data].assenze.push({...a});
    });
    return Object.entries(perData).sort(([a],[b]) =>
      a.split("/").reverse().join("").localeCompare(b.split("/").reverse().join(""))
    );
  };

  // Totale ore lavorate nel mese (escluse assenze ferie/permesso/malattia)
  const oreMese = (nomeOp, mese) => {
    const righe = getRigheOperaio(nomeOp, mese);
    return righe.reduce((s,[_d,g]) =>
      s + g.voci.reduce((sv,v,i) => sv + parseFloat(getCorrVoce(nomeOp,_d,i,"ore",v.ore)||0), 0), 0);
  };

  // Totale ore assenze nel mese (ferie, permesso, malattia, infortunio)
  const oreAssenzeMese = (nomeOp, mese) => {
    const righe = getRigheOperaio(nomeOp, mese);
    return righe.reduce((s,[_d,g]) => s + g.assenze.reduce((sa,a)=>sa+a.ore,0), 0);
  };

  // Totale ore anno per un operaio
  const oreAnno = (nomeOp, anno) => {
    return assenzeDB.filter(a => a.operaio===nomeOp && a.data?.slice(-4)===anno)
      .reduce((s,a) => s+(parseFloat(a.ore)||0), 0);
  };

  const annoFiltro = meseFiltro.slice(-4) || String(new Date().getFullYear());

  const badgeAssenza = (tipo) => {
    const colori = { Ferie:"#3b82f6", Permesso:"#8b5cf6", Malattia:"#f59e0b", Infortunio:"#ef4444" };
    return <span style={{ background:(colori[tipo]||"#888")+"22", color:colori[tipo]||"#888", border:`1px solid ${colori[tipo]||"#888"}44`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{tipo}</span>;
  };

  // ── PDF ────────────────────────────────────────────────────────────────────
  const generaPDF = (opName, opMese) => {
    const nome = opName || pdfOperaio;
    const mese = opMese || pdfMese;
    const righe = getRigheOperaio(nome, mese);
    if (!righe.filter(([,g])=>g.voci.length>0||g.assenze.length>0).length) { alert("Nessun dato per questo mese."); return; }
    const dip = dipendenti.find(d => d.nome===nome);
    let totOre=0, totCE=0, totAssenze=0;
    const righeHTML = righe.map(([data, giorno]) => {
      const tutteVoci = [
        ...giorno.voci.map((v,idx) => {
          // FIX: salta voci marcate come cancellate
          if (getCorrVoce(nome,data,idx,"cancellato",false)) return null;
          const oreCorr = parseFloat(getCorrVoce(nome,data,idx,"ore",v.ore));
          const cantCorr = getCorrVoce(nome,data,idx,"cantiere",v.cantiere);
          const ceCorr = getCorrVoce(nome,data,idx,"cantiereCE", false);
          totOre+=oreCorr; if(ceCorr)totCE+=oreCorr;
          return { tipo:"cantiere", cantiere:cantCorr, ore:oreCorr, ce:ceCorr };
        }).filter(Boolean),
        // FIX: assenze sommate solo in totAssenze, NON in totOre
        ...giorno.assenze.map(a => { totAssenze+=a.ore; return { tipo:"assenza", cantiere:a.tipo, ore:a.ore, ce:false }; })
      ];
      // FIX: tot. giorno = solo ore lavorate (no assenze)
      const totG = tutteVoci.reduce((s,v)=>v.tipo==="cantiere"?s+v.ore:s, 0);
      return tutteVoci.map((v,i) => `
        <tr>
          ${i===0?`<td rowspan="${tutteVoci.length}" style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:600;font-size:13px;vertical-align:top;white-space:nowrap">${formatData(data)}</td>`:""}
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;${v.tipo==="assenza"?"color:#f59e0b;font-style:italic":""}">${v.cantiere}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;font-weight:700;text-align:center">${v.ore}h</td>
          <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:13px;text-align:center">${v.ce?"✓":""}</td>
          ${i===0?`<td rowspan="${tutteVoci.length}" style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-weight:800;font-size:15px;text-align:right;vertical-align:middle">${totG}h</td>`:""}
        </tr>`).join("");
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Presenze ${nome} — ${mese}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',sans-serif;color:#111;background:#fff}
  .header{background:#111;color:#fff;padding:24px 36px;display:flex;align-items:center;justify-content:space-between}
  .logo{font-size:18px;font-weight:800;color:#f5a623}.sub{font-size:10px;color:#888;letter-spacing:1.5px;text-transform:uppercase;margin-top:3px}
  .body{padding:24px 36px}
  .worker{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:16px 20px;margin-bottom:20px;display:flex;align-items:center;gap:16px}
  .avatar{width:44px;height:44px;border-radius:50%;background:${dip?.colore||"#888"};display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;color:#fff;flex-shrink:0}
  .stats{display:flex;gap:24px;margin-left:auto}.stat{text-align:center}.stat-val{font-size:24px;font-weight:800}.stat-lbl{font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px}
  table{width:100%;border-collapse:collapse}thead tr{background:#f3f4f6}
  th{text-align:left;padding:9px 12px;font-size:10px;text-transform:uppercase;letter-spacing:1.5px;color:#666;font-weight:600}
  .footer{margin-top:32px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between}
  .sign{border:1px solid #ddd;border-radius:8px;padding:12px 20px;min-width:160px;text-align:center;font-size:11px;color:#999;margin-top:8px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="header">
  <div><div class="logo">BertocProject Srl</div><div class="sub">Via Vecchia Provinciale Lucchese 47C — Serravalle Pistoiese · P.IVA 01747090478</div></div>
  <div style="text-align:right"><div style="font-size:11px;color:#aaa">Foglio presenze mensile</div><div style="font-size:18px;font-weight:800;color:#f5a623">${mese}</div></div>
</div>
<div class="body">
  <div class="worker">
    <div class="avatar">${nome[0]}</div>
    <div><div style="font-size:18px;font-weight:800">${nome}</div><div style="font-size:12px;color:#666;margin-top:2px">${dip?.ruolo||"Operaio edile"} · BertocProject Srl</div></div>
    <div class="stats">
      <div class="stat"><div class="stat-val">${totOre}</div><div class="stat-lbl">Ore lavorate</div></div>
      <div class="stat"><div class="stat-val">${totAssenze}</div><div class="stat-lbl">Ore assenze</div></div>
      <div class="stat"><div class="stat-val">${totCE}</div><div class="stat-lbl">Ore CE</div></div>
    </div>
  </div>
  <table>
    <thead><tr><th>Data</th><th>Cantiere / Assenza</th><th style="text-align:center">Ore</th><th style="text-align:center">Cassa Edile</th><th style="text-align:right">Tot. giorno</th></tr></thead>
    <tbody>${righeHTML}</tbody>
    <tfoot><tr style="background:#fffbeb"><td colspan="2" style="padding:10px 12px;font-weight:700;font-size:13px">TOTALE ORE LAVORATE MESE</td><td colspan="3" style="padding:10px 12px;font-weight:800;font-size:20px;text-align:right">${totOre}h</td></tr>
    <tr style="background:#f0f4ff"><td colspan="2" style="padding:8px 12px;font-weight:600;font-size:12px;color:#666">TOTALE ORE ASSENZE MESE</td><td colspan="3" style="padding:8px 12px;font-weight:700;font-size:14px;text-align:right;color:#666">${totAssenze}h</td></tr></tfoot>
  </table>
  <div style="margin-top:12px;font-size:11px;color:#999;font-style:italic">* Le ore con spunta Cassa Edile (${totCE}h) sono soggette a denuncia CE mensile.</div>
  <div class="footer">
    <div><div style="font-weight:600;font-size:12px">Firma lavoratore</div><div class="sign">${nome}<br><br>_______________________</div></div>
    <div style="text-align:right"><div style="font-weight:600;font-size:12px">Firma titolare</div><div class="sign">BertocProject Srl<br><br>_______________________</div></div>
  </div>
</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const blob = new Blob([html],{type:"text/html"});
    window.open(URL.createObjectURL(blob),"_blank");
  };

  // ── DETTAGLIO SINGOLO OPERAIO ─────────────────────────────────────────────
  if (operaioAperto) {
    const dip = dipendenti.find(d=>d.nome===operaioAperto);
    const righe = getRigheOperaio(operaioAperto, meseFiltro);
    const totMese = oreMese(operaioAperto, meseFiltro);
    const totAnno = oreAnno(operaioAperto, annoFiltro);
    return (
      <div className="content">
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button className="btn btn-ghost" onClick={()=>setOperaioAperto(null)}>← Tutti i lavoratori</button>
          <span style={{ color:theme.textMuted }}>/ {operaioAperto}</span>
          <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
            <button className={`btn ${editMode?"btn-primary":"btn-ghost"} btn-sm`} onClick={()=>{ if(editMode) setCorrezioni({}); setEditMode(!editMode); }}>
              {editMode?"✓ Fine modifica":"✏️ Modifica per PDF"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={()=>generaPDF(operaioAperto, meseFiltro)}>
              🖨️ Stampa PDF originale
            </button>
            {editMode && (
              <button className="btn btn-primary btn-sm" onClick={()=>generaPDF(operaioAperto, meseFiltro)}>
                🖨️ Stampa PDF modificato
              </button>
            )}
          </div>
        </div>

        {editMode && (
          <div style={{ background:"#1a1500", border:"1px solid #f59e0b55", borderRadius:10, padding:"10px 14px", marginBottom:14, fontSize:12, color:"#f59e0b" }}>
            ⚠️ Modalità modifica — le correzioni si applicano <strong>solo al PDF</strong>. I dati reali restano invariati.
          </div>
        )}

        {/* Header operaio con KPI */}
        <div style={{ background:theme.surface, borderRadius:14, padding:"16px 20px", marginBottom:20, border:`1px solid ${dip?.colore||theme.border}44` }}>
          <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:16 }}>
            <div className="avatar" style={{ background:dip?.colore||"#888", width:44, height:44, fontSize:16 }}>{operaioAperto[0]}</div>
            <div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:18 }}>{operaioAperto}</div>
              <div style={{ fontSize:12, color:theme.textMuted }}>{dip?.ruolo||"Operaio"}</div>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div style={{ background:theme.bg, borderRadius:10, padding:"14px", textAlign:"center", border:`1px solid ${theme.border}` }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:28, color:theme.accent }}>{totMese.toFixed(1)}h</div>
              <div style={{ fontSize:11, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>Ore mese {meseFiltro}</div>
            </div>
            <div style={{ background:theme.bg, borderRadius:10, padding:"14px", textAlign:"center", border:`1px solid ${theme.border}` }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:28, color:"#4e9af5" }}>{totAnno.toFixed(1)}h</div>
              <div style={{ fontSize:11, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1, marginTop:3 }}>Ore anno {annoFiltro}</div>
            </div>
          </div>
        </div>

        {/* Tabella dettaglio */}
        {righe.length === 0 ? (
          <div className="card" style={{ textAlign:"center", padding:32, color:theme.textDim }}>Nessun dato per {meseFiltro}</div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Data</th><th>Cantiere / Assenza</th><th style={{ textAlign:"center" }}>Ore</th><th style={{ textAlign:"center" }}>Cassa Edile</th><th style={{ textAlign:"right" }}>Tot. giorno</th></tr>
                </thead>
                <tbody>
                  {righe.map(([data, giorno]) => {
                    const tutteVoci = [
                      ...giorno.voci.map((v,idx)=>({ tipo:"cantiere",v,idx })),
                      ...giorno.assenze.map(a=>({ tipo:"assenza",a })),
                    ];
                    const totG = tutteVoci.reduce((s,item)=>item.tipo==="cantiere"?s+(parseFloat(item.v.ore)||0):s, 0); // FIX: UI usa valore originale, no correzioni, no assenze
                    const isWeekend = (() => { const [gg,mm,aaaa]=data.split("/"); return new Date(`${aaaa}-${mm}-${gg}`).getDay()===6; })();
                    // Giorno vuoto
                    if (tutteVoci.length === 0) return (
                      <tr key={data} style={{ opacity:0.4 }}>
                        <td style={{ fontWeight:500, fontSize:13, whiteSpace:"nowrap", color:isWeekend?theme.accent:theme.textMuted }}>{formatData(data)}</td>
                        <td colSpan={3} style={{ color:theme.textDim, fontSize:12, fontStyle:"italic" }}>{isWeekend?"Sabato":"—"}</td>
                        <td style={{ textAlign:"right", color:theme.textDim }}>—</td>
                      </tr>
                    );
                    return tutteVoci.map((item,i) => (
                      <tr key={`${data}-${i}`} style={{ background: i%2===0?"transparent":"#ffffff04" }}>
                        {i===0 && <td rowSpan={tutteVoci.length} style={{ verticalAlign:"top",paddingTop:12,fontWeight:600,fontSize:13,whiteSpace:"nowrap" }}>{formatData(data)}</td>}
                        {item.tipo==="cantiere" ? (<>
                          <td style={{opacity:editMode&&getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?0.4:1, textDecoration:editMode&&getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?"line-through":"none"}}>{editMode?(
                            <select disabled={getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)} style={{...inputSt,fontSize:12,padding:"4px 8px"}}
                              value={getCorrVoce(operaioAperto,data,item.idx,"cantiere",item.v.cantiere)}
                              onChange={e=>setCorrVoce(operaioAperto,data,item.idx,"cantiere",e.target.value)}>
                              <option value={getCorrVoce(operaioAperto,data,item.idx,"cantiere",item.v.cantiere)}>{getCorrVoce(operaioAperto,data,item.idx,"cantiere",item.v.cantiere)}</option>
                              {cantieriDB.filter(c=>!c.completato).map(c=>(
                                <option key={c._id||c.id} value={c.nome}>{c.nome}</option>
                              ))}
                            </select>
                          ):<span style={{fontSize:13}}>{item.v.cantiere}</span>}</td>
                          <td style={{textAlign:"center"}}>{editMode?(<div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"center"}}>
                            <input type="number" min="0.5" max="24" step="0.5" style={{...inputSt,width:60,textAlign:"center",padding:"4px 6px",fontSize:13,opacity:getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?0.4:1}} disabled={getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)} value={getCorrVoce(operaioAperto,data,item.idx,"ore",item.v.ore)} onChange={e=>setCorrVoce(operaioAperto,data,item.idx,"ore",e.target.value)}/>
                            <button onClick={()=>setCorrVoce(operaioAperto,data,item.idx,"cancellato",!getCorrVoce(operaioAperto,data,item.idx,"cancellato",false))} title={getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?"Ripristina":"Elimina dalla stampa"} style={{background:getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?"#3ecf8e22":"#f0606022",border:`1px solid ${getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?"#3ecf8e":"#f06060"}`,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontSize:13}}>{getCorrVoce(operaioAperto,data,item.idx,"cancellato",false)?"↺":"🗑️"}</button>
                          </div>):<span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,color:theme.accent}}>{item.v.ore}h</span>}</td>
                          <td style={{textAlign:"center"}}><input type="checkbox" checked={getCorrVoce(operaioAperto,data,item.idx,"cantiereCE", false)} onChange={e=>setCorrVoce(operaioAperto,data,item.idx,"cantiereCE",e.target.checked)} style={{width:16,height:16,cursor:"pointer",accentColor:theme.accent}}/></td>
                        </>) : (<>
                          <td>{badgeAssenza(item.a.tipo)}{item.a.note?<span style={{fontSize:11,color:theme.textDim,marginLeft:6}}>{item.a.note}</span>:null}</td>
                          <td style={{textAlign:"center"}}><span style={{fontFamily:"'Syne',sans-serif",fontWeight:700,color:"#3b82f6"}}>{item.a.ore}h</span></td>
                          <td style={{textAlign:"center",color:theme.textDim}}>—</td>
                        </>)}
                        {i===0&&<td rowSpan={tutteVoci.length} style={{textAlign:"right",verticalAlign:"middle",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:16}}>{totG>0?totG+"h":"—"}</td>}
                      </tr>
                    ));
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:"#0a1a0a"}}>
                    <td colSpan={2} style={{padding:"10px 12px",fontWeight:700,fontSize:13}}>Totale ore mese</td>
                    <td colSpan={3} style={{padding:"10px 12px",fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:18,textAlign:"right",color:theme.success}}>{totMese.toFixed(1)}h</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── VISTA PRINCIPALE ──────────────────────────────────────────────────────
  return (
    <div className="content">
      {/* Filtri */}
      <div className="card" style={{ marginBottom:16, padding:"12px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
          <div style={{ fontSize:13, color:theme.textMuted, fontWeight:500 }}>📅 Mese:</div>
          <input value={meseFiltro} onChange={e=>setMeseFiltro(e.target.value)} placeholder="mm/aaaa" style={{...inputSt,width:110}} />
          <div style={{ fontSize:13, color:theme.textMuted, fontWeight:500, marginLeft:8 }}>👤 Lavoratore:</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
            {["Tutti",...dipendenti.map(d=>d.nome)].map(n => {
              const dp = dipendenti.find(d=>d.nome===n);
              return (
                <div key={n} onClick={()=>setOperaioFiltro(n)}
                  style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 10px", borderRadius:8, cursor:"pointer",
                    border:`1px solid ${operaioFiltro===n?(dp?.colore||theme.accent):theme.border}`,
                    background:operaioFiltro===n?(dp?.colore||theme.accent)+"22":theme.bg }}>
                  {dp&&<div className="avatar" style={{background:dp.colore,width:18,height:18,fontSize:8}}>{n[0]}</div>}
                  <span style={{fontSize:12,fontWeight:operaioFiltro===n?700:400}}>{n}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="tabs">
        {[["riepilogo","📋 Riepilogo ore"],["assenze","🏖️ Assenze"],["pdf","🖨️ Stampa PDF"]].map(([id,label])=>(
          <div key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</div>
        ))}
      </div>

      {/* ── TAB RIEPILOGO ── */}
      {tab==="riepilogo" && (
        <div>
          {dipendenti.filter(d=>operaioFiltro==="Tutti"||d.nome===operaioFiltro).map(dip => {
            const totMeseOp  = oreMese(dip.nome, meseFiltro);
            const totAnnoOp  = oreAnno(dip.nome, annoFiltro);
            const totAssOp   = oreAssenzeMese(dip.nome, meseFiltro);
            const righe = getRigheOperaio(dip.nome, meseFiltro);
            return (
              <div key={dip.nome} className="card" style={{ marginBottom:14, cursor:"pointer" }} onClick={()=>setOperaioAperto(dip.nome)}>
                <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                  <div className="avatar" style={{ background:dip.colore, width:44, height:44, fontSize:16 }}>{dip.nome[0]}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>{dip.nome}</div>
                    <div style={{ fontSize:12, color:theme.textDim }}>{dip.ruolo} · {righe.length} giorni lavorati</div>
                    {totAssOp > 0 && <div style={{ fontSize:11, color:"#3b82f6", marginTop:2 }}>{totAssOp.toFixed(1)}h assenze</div>}
                  </div>
                  <div style={{ display:"flex", gap:20, alignItems:"center" }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:theme.accent }}>{totMeseOp.toFixed(1)}h</div>
                      <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>lavorate</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:"#4e9af5" }}>{totAnnoOp.toFixed(1)}h</div>
                      <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>anno</div>
                    </div>
                    <div style={{ fontSize:12, color:theme.textDim }}>Dettaglio →</div>
                  </div>
                </div>
              </div>
            );
          })}
          {dipendenti.every(d=>oreMese(d.nome,meseFiltro)===0&&oreAnno(d.nome,annoFiltro)===0) && (
            <div style={{ textAlign:"center", padding:"40px 0", color:theme.textDim }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📋</div>
              <div style={{ fontSize:14, color:theme.textMuted }}>Nessuna ora registrata per {meseFiltro}</div>
              <div style={{ fontSize:12, marginTop:6 }}>Le ore vengono aggiunte dagli operai direttamente dai cantieri.</div>
            </div>
          )}
        </div>
      )}

      {/* ── TAB ASSENZE ── */}
      {tab==="assenze" && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">🏖️ Assenze del mese</div>
            <button className="btn btn-primary btn-sm" onClick={()=>setShowAsForm(!showAsForm)}>+ Aggiungi assenza</button>
          </div>
          {showAsForm && (
            <div style={{ background:theme.bg, borderRadius:10, padding:14, border:`1px solid ${theme.border}`, marginBottom:16 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 80px", gap:10, marginBottom:10 }}>
                <div><div style={{fontSize:11,color:theme.textDim,marginBottom:4}}>Lavoratore</div><select style={inputSt} value={asForm.operaio} onChange={e=>setAsForm({...asForm,operaio:e.target.value})}><option value="">— Seleziona —</option>{dipendenti.map(d=><option key={d.nome} value={d.nome}>{d.nome}</option>)}</select></div>
                <div><div style={{fontSize:11,color:theme.textDim,marginBottom:4}}>Data</div><input style={inputSt} type="date" value={asForm.data ? asForm.data.split("/").reverse().join("-") : ""} onChange={e=>{ const [y,m,d]=e.target.value.split("-"); setAsForm({...asForm,data:`${d}/${m}/${y}`}); }} /></div>
                <div><div style={{fontSize:11,color:theme.textDim,marginBottom:4}}>Tipo</div><select style={inputSt} value={asForm.tipo} onChange={e=>setAsForm({...asForm,tipo:e.target.value})}>{TIPI_ASSENZA.map(t=><option key={t}>{t}</option>)}</select></div>
                <div><div style={{fontSize:11,color:theme.textDim,marginBottom:4}}>Ore</div><input style={inputSt} type="number" min="1" max="8" step="0.5" value={asForm.ore} onChange={e=>setAsForm({...asForm,ore:e.target.value})} /></div>
              </div>
              <div style={{marginBottom:10}}><div style={{fontSize:11,color:theme.textDim,marginBottom:4}}>Note</div><input style={inputSt} placeholder="es. Certificato medico..." value={asForm.note} onChange={e=>setAsForm({...asForm,note:e.target.value})} /></div>
              <div style={{display:"flex",gap:8}}><button className="btn btn-primary btn-sm" onClick={aggiungiAssenza}>Salva</button><button className="btn btn-ghost btn-sm" onClick={()=>setShowAsForm(false)}>Annulla</button></div>
            </div>
          )}
          {assenzeFB.filter(a=>a.data?.slice(3)===meseFiltro&&(operaioFiltro==="Tutti"||a.operaio===operaioFiltro)).length===0 ? (
            <div style={{textAlign:"center",color:theme.textDim,padding:"20px 0",fontSize:13}}>Nessuna assenza per questo mese.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Lavoratore</th><th>Data</th><th>Tipo</th><th>Ore</th><th>Note</th><th></th></tr></thead>
                <tbody>
                  {assenzeFB.filter(a=>a.data?.slice(3)===meseFiltro&&(operaioFiltro==="Tutti"||a.operaio===operaioFiltro)).map(a => {
                    const dp = dipendenti.find(d=>d.nome===a.operaio);
                    return (
                      <tr key={a._id||a.id}>
                        <td><div style={{display:"flex",alignItems:"center",gap:8}}><div className="avatar" style={{background:dp?.colore||"#888",width:26,height:26,fontSize:10}}>{(a.operaio||"?")[0]}</div><span style={{fontWeight:600}}>{a.operaio}</span></div></td>
                        <td style={{color:theme.textMuted,fontSize:13}}>{a.data}</td>
                        <td>{badgeAssenza(a.tipo)}</td>
                        <td style={{fontFamily:"'Syne',sans-serif",fontWeight:700,color:"#3b82f6"}}>{a.ore}h</td>
                        <td style={{fontSize:12,color:theme.textDim}}>{a.note||"—"}</td>
                        <td><button onClick={()=>eliminaAssenza(a._id)} style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:16}}>✕</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── TAB PDF ── */}
      {tab==="pdf" && (
        <div className="card">
          <div className="card-title" style={{marginBottom:6}}>🖨️ Foglio presenze mensile</div>
          <div style={{fontSize:13,color:theme.textMuted,marginBottom:20}}>Seleziona lavoratore e mese, poi scegli se stampare il PDF originale (dati reali) o quello modificato.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
            <div className="form-group">
              <label>Lavoratore</label>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:4}}>
                {dipendenti.map(d=>(
                  <div key={d.nome} onClick={()=>setPdfOperaio(d.nome)}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",borderRadius:8,cursor:"pointer",
                      border:`1px solid ${pdfOperaio===d.nome?d.colore:theme.border}`,
                      background:pdfOperaio===d.nome?d.colore+"22":theme.bg}}>
                    <div className="avatar" style={{background:d.colore,width:24,height:24,fontSize:10}}>{d.nome[0]}</div>
                    <span style={{fontSize:12,fontWeight:pdfOperaio===d.nome?700:400}}>{d.nome}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>Mese (mm/aaaa)</label>
              <input placeholder="es. 03/2026" value={pdfMese} onChange={e=>setPdfMese(e.target.value)} style={{maxWidth:160,...inputSt}} />
              {pdfOperaio && pdfMese && (() => {
                const tot = oreMese(pdfOperaio, pdfMese);
                const righe = getRigheOperaio(pdfOperaio, pdfMese);
                return tot > 0 ? (
                  <div style={{marginTop:10,padding:"10px 14px",background:theme.bg,borderRadius:8,border:`1px solid ${theme.border}`,fontSize:13,color:theme.textMuted}}>
                    <strong style={{color:theme.accent}}>{tot.toFixed(1)}h</strong> totali · <strong style={{color:theme.text}}>{righe.length}</strong> giorni lavorati
                  </div>
                ) : <div style={{marginTop:10,fontSize:13,color:theme.textDim}}>Nessun dato per questo mese.</div>;
              })()}
            </div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <button className="btn btn-ghost" onClick={()=>{ setEditMode(false); setCorrezioni({}); setTimeout(()=>generaPDF(), 50); }}>🖨️ PDF originale (dati reali)</button>
            <button className="btn btn-primary" onClick={()=>{ setEditMode(true); setOperaioAperto(pdfOperaio); }}>✏️ Apri per modificare → poi stampa</button>
          </div>
        </div>
      )}
    </div>
  );
}


function Archivio({ cantieriDB = [], preventiviDB = [], sopralluoghiDB = [], assenzeDB = [], bolleDB = [] }) { /* FIX_STATSPERCAT_REALI_v3 */
  const [tab, setTab] = useState("cantieri");
  const [search, setSearch] = useState("");
  const [dettaglio, setDettaglio] = useState(null);
  const [filtroPrev, setFiltroPrev] = useState("tutti");
  const [filtroAnno, setFiltroAnno] = useState("tutti");
  // Archivio per cliente
  const [annoCartelle, setAnnoCartelle] = useState("tutti");
  const [clienteAperto, setClienteAperto] = useState(null); // { anno, cliente, prevAperto }

  const inputSt = {
    background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8,
    color:"#e8eaf2", padding:"8px 12px", fontSize:13,
    fontFamily:"'DM Sans',sans-serif", outline:"none"
  };

  const safeNum = v => parseFloat(String(v||0).replace(/[^0-9.-]/g,"")) || 0;

  const STATO_PREV = {
    accettato: { label:"Accettato", color:"#3ecf8e", bg:"#3ecf8e22" },
    rifiutato:  { label:"Rifiutato", color:"#f06060", bg:"#f0606022" },
    scaduto:    { label:"Scaduto",   color:"#7b82a0", bg:"#7b82a022" },
    inviato:    { label:"Inviato",   color:"#4e9af5", bg:"#4e9af522" },
    bozza:      { label:"Bozza",     color:"#7b82a0", bg:"#7b82a022" },
  };

  const cantieriCompletati = cantieriDB.filter(c => (c.completato || c.stato === "completato") && !c.nascosto && !c.daIncassare);

  const anniDisponibili = [...new Set(cantieriCompletati.map(c => {
    const d = c.dataCompletamento || c.fine || "";
    return d.slice(-4);
  }).filter(a => /^20\d\d$/.test(a)))].sort().reverse();

  const cantieriFiltrati = cantieriCompletati
    .filter(c => filtroAnno === "tutti" || (c.dataCompletamento||c.fine||"").slice(-4) === filtroAnno)
    .filter(c => !search || (c.nome||"").toLowerCase().includes(search.toLowerCase()) || (c.cliente||"").toLowerCase().includes(search.toLowerCase()));

  const totImporto  = cantieriFiltrati.reduce((s,c) => s + safeNum(c.importo), 0);
  const totMargine  = cantieriFiltrati.reduce((s,c) => s + safeNum(c.margine), 0);
  const marginePerc = totImporto > 0 ? Math.round((totMargine/totImporto)*100) : 0;

  const prevAcc = preventiviDB.filter(p => (p.stato||p.header?.stato)==="accettato");
  const prevRif = preventiviDB.filter(p => (p.stato||p.header?.stato)==="rifiutato");
  const prevSca = preventiviDB.filter(p => (p.stato||p.header?.stato)==="scaduto");
  const tassoAcc = preventiviDB.length > 0 ? Math.round((prevAcc.length/preventiviDB.length)*100) : 0;
  const valAcc   = prevAcc.reduce((s,p) => s + safeNum(p.totaleFinale||p.importo), 0);

  // ── PDF RESOCONTO FINALE CANTIERE (interno) ──────────────────────────────
  const generaReportCantiere = (c, assenzeDB=[]) => {
    const importo  = safeNum(c.importo);
    const costoLav = safeNum(c.costoLavoro);
    const costoMat = safeNum(c.costoMateriali);
    const margine  = c.margine != null ? safeNum(c.margine) : importo - costoLav - costoMat;
    const ore      = safeNum(c.ore);
    const mPerc    = importo > 0 ? Math.round((margine/importo)*100) : 0;
    const mCol     = mPerc>=30?"#16a34a":mPerc>=20?"#d97706":"#dc2626";
    const operaiHtml = (c.operai||[]).map(op=>
      `<span style="background:#f3f4f6;border-radius:6px;padding:3px 10px;font-size:12px;margin-right:6px">${op}</span>`
    ).join("") || "—";
    // Lavorazioni con ore reali
    const cid = c._id||c.id;
    const orePerLav = {};
    (assenzeDB||[]).filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid||(a.cantiere||"")===(c.nome||""))).forEach(a=>{
      const idx = trovaIdxLav(a, c.lavorazioni); // LAV_BY_ID_v8
      orePerLav[idx]=(orePerLav[idx]||0)+(parseFloat(a.ore)||0);
    });
    const lavoriHtml = (c.lavorazioni||[]).length>0 ? `
      <table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px">
        <thead><tr style="background:#f3f4f6">
          <th style="padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#666">Lavorazione</th>
          <th style="padding:8px 10px;text-align:center">Q.tà</th>
          <th style="padding:8px 10px;text-align:right">Importo prev.</th>
          <th style="padding:8px 10px;text-align:right">Ore prev.</th>
          <th style="padding:8px 10px;text-align:right">Ore reali</th>
          <th style="padding:8px 10px;text-align:right">Δ ore</th>
        </tr></thead>
        <tbody>
          ${(c.lavorazioni||[]).map((l,i)=>{
            const orePrev = ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0);
            const oreReali = orePerLav[i]||0;
            const delta = oreReali - orePrev;
            const deltaCol = delta>0?"#dc2626":delta<0?"#16a34a":"#666";
            return `<tr style="border-bottom:1px solid #f3f4f6">
              <td style="padding:8px 10px">${l.desc||"—"}</td>
              <td style="padding:8px 10px;text-align:center">${l.qta} ${l.um}</td>
              <td style="padding:8px 10px;text-align:right;color:#666">€ ${parseFloat(l.totale||0).toFixed(0)}</td>
              <td style="padding:8px 10px;text-align:right;color:#666">${orePrev.toFixed(1)}h</td>
              <td style="padding:8px 10px;text-align:right;font-weight:700">${oreReali.toFixed(1)}h</td>
              <td style="padding:8px 10px;text-align:right;font-weight:700;color:${deltaCol}">${delta>=0?"+":""}${delta.toFixed(1)}h</td>
            </tr>`;
          }).join("")}
        </tbody>
      </table>` : "<div style='color:#999;font-size:13px;padding:10px 0'>Nessuna lavorazione registrata</div>";
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>Report — ${c.nome||"Cantiere"}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700;800&family=Syne:wght@800&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',sans-serif;color:#111;background:#fff}
  .header{background:#111;color:#fff;padding:28px 40px;display:flex;justify-content:space-between;align-items:flex-start}
  .logo{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#f5a623;white-space:nowrap;flex-shrink:0;overflow:visible}
  .logo-sub{font-size:10px;color:#888;letter-spacing:2px;text-transform:uppercase;margin-top:2px}
  .body{padding:28px 40px}
  .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px}
  .kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:14px;border-top:3px solid}
  .kpi-lbl{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#999;margin-bottom:4px}
  .kpi-val{font-family:'Syne',sans-serif;font-size:22px;font-weight:800}
  .row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #f3f4f6;font-size:13px}
  .row:last-child{border-bottom:none}
  .margine-box{background:${mCol}10;border:2px solid ${mCol}33;border-radius:12px;padding:18px;text-align:center;margin-bottom:16px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-bottom:20px}
  .section-title{font-family:'Syne',sans-serif;font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:2px;color:#f5a623;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid #e5e7eb}
  .footer{margin-top:28px;padding-top:16px;border-top:2px solid #e5e7eb;display:flex;justify-content:space-between}
  .sign{border:1px solid #ddd;border-radius:8px;padding:12px 20px;min-width:180px;text-align:center;font-size:11px;color:#999;margin-top:6px}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<div class="header">
  <div>
    <div class="logo">BertocProject Srl</div>
    <div class="logo-sub">Via Vecchia Provinciale Lucchese 47C · Serravalle Pistoiese · P.IVA 01747090478</div>
    <div style="margin-top:14px;font-size:11px;color:#888">REPORT CONSUNTIVO CANTIERE</div>
    <div style="font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#fff;margin-top:4px">${c.nome||"—"}</div>
    <div style="font-size:12px;color:#aaa;margin-top:6px">Cliente: <strong style="color:#fff">${c.cliente||"—"}</strong> · ${c.indirizzo||"—"}</div>
  </div>
  <div style="text-align:right">
    <div style="background:#16a34a22;color:#16a34a;border:1px solid #16a34a44;border-radius:6px;padding:4px 12px;font-size:11px;font-weight:700;display:inline-block;margin-bottom:8px">✓ COMPLETATO</div>
    <div style="font-size:12px;color:#aaa">📅 ${c.inizio||"—"} → ${c.dataCompletamento||c.fine||"—"}</div>
    <div style="font-size:11px;color:#555;margin-top:6px">Generato il ${new Date().toLocaleDateString("it-IT")}</div>
  </div>
</div>
<div class="body">
  <div class="kpi-grid">
    <div class="kpi" style="border-top-color:#4e9af5"><div class="kpi-lbl">Importo</div><div class="kpi-val" style="color:#4e9af5">€ ${importo.toLocaleString("it-IT")}</div></div>
    <div class="kpi" style="border-top-color:#f06060"><div class="kpi-lbl">Costi totali</div><div class="kpi-val" style="color:#f06060">€ ${(costoLav+costoMat).toLocaleString("it-IT")}</div></div>
    <div class="kpi" style="border-top-color:${mCol}"><div class="kpi-lbl">Margine netto</div><div class="kpi-val" style="color:${mCol}">€ ${margine.toLocaleString("it-IT")}</div></div>
    <div class="kpi" style="border-top-color:#f5a623"><div class="kpi-lbl">Ore lavorate</div><div class="kpi-val" style="color:#f5a623">${ore}h</div></div>
  </div>
  <div class="grid2">
    <div>
      <div class="section-title">💰 Consuntivo economico</div>
      <div class="row"><span style="color:#666">Importo fatturato</span><strong>€ ${importo.toLocaleString("it-IT")}</strong></div>
      <div class="row"><span style="color:#666">Costo manodopera</span><strong style="color:#f06060">€ ${costoLav.toLocaleString("it-IT")}</strong></div>
      <div class="row"><span style="color:#666">Costo materiali</span><strong style="color:#d97706">€ ${costoMat.toLocaleString("it-IT")}</strong></div>
      <div class="row" style="border-top:2px solid #e5e7eb;margin-top:4px;padding-top:10px"><strong>Margine netto</strong><strong style="font-size:16px;color:${mCol}">€ ${margine.toLocaleString("it-IT")}</strong></div>
      ${(c.operai||[]).length>0?`<div style="margin-top:14px"><div class="section-title">👷 Operai coinvolti</div><div>${operaiHtml}</div></div>`:""}
    </div>
    <div>
      <div class="margine-box">
        <div style="font-size:10px;color:#666;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">Margine conseguito</div>
        <div style="font-family:'Syne',sans-serif;font-size:52px;font-weight:800;color:${mCol};line-height:1">${mPerc}%</div>
        <div style="font-size:18px;font-weight:700;color:${mCol};margin-top:4px">€ ${margine.toLocaleString("it-IT")}</div>
      </div>
      <div class="section-title">📋 Dati cantiere</div>
      <div class="row"><span style="color:#666">Inizio lavori</span><strong>${c.inizio||"—"}</strong></div>
      <div class="row"><span style="color:#666">Completamento</span><strong>${c.dataCompletamento||c.fine||"—"}</strong></div>
      ${ore>0?`<div class="row"><span style="color:#666">Ore lavorate</span><strong>${ore}h</strong></div>`:""}
      ${(ore>0&&(costoLav+costoMat)>0)?`<div class="row"><span style="color:#666">Costo orario medio</span><strong>€ ${Math.round((costoLav+costoMat)/ore)}/h</strong></div>`:""}
    </div>
  </div>
  ${c.note?`<div style="background:#f9fafb;border-radius:8px;padding:12px 16px;font-size:13px;color:#666;font-style:italic;margin-bottom:20px">📝 ${c.note}</div>`:""}
  <div class="footer">
    <div><div style="font-size:11px;color:#999;margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:1px">Firma titolare</div><div class="sign">BertocProject Srl<br><br><br>_______________________</div></div>
    <div style="text-align:center;align-self:flex-end"><div style="border:2px solid #f5a623;border-radius:8px;padding:8px 16px;display:inline-block;color:#f5a623;font-size:10px;font-weight:700;letter-spacing:1px;transform:rotate(-4deg)">✓ CANTIERE COMPLETATO</div></div>
    <div style="text-align:right"><div style="font-size:11px;color:#999;margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:1px">Firma cliente</div><div class="sign">${c.cliente||"—"}<br><br><br>_______________________</div></div>
  </div>
</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const blob = new Blob([html], { type:"text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  // ── PDF REPORT ANNUALE ────────────────────────────────────────────────────
  const generaReportAnnuale = () => {
    const anno = filtroAnno === "tutti" ? String(new Date().getFullYear()) : filtroAnno;
    const list = cantieriCompletati.filter(c => (c.dataCompletamento||c.fine||"").slice(-4) === anno);
    if (!list.length) { alert(`Nessun cantiere completato nel ${anno}.`); return; }
    const tot  = list.reduce((s,c) => s + safeNum(c.importo), 0);
    const marg = list.reduce((s,c) => s + safeNum(c.margine), 0);
    const perc = tot > 0 ? Math.round((marg/tot)*100) : 0;
    const mCol = perc>=30?"#16a34a":perc>=20?"#d97706":"#dc2626";
    const righe = list.map(c => {
      const imp = safeNum(c.importo);
      const m   = safeNum(c.margine);
      const mp  = imp>0?Math.round((m/imp)*100):0;
      const mc  = mp>=30?"#16a34a":mp>=20?"#d97706":"#dc2626";
      return `<tr>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:500">${c.nome||"—"}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;color:#666">${c.cliente||"—"}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;color:#666">${c.dataCompletamento||c.fine||"—"}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:700">€ ${imp.toLocaleString("it-IT")}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:700;color:${mc}">${mp}%</td>
      </tr>`;
    }).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Report ${anno} — BertocProject</title>
<style>@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;700;800&family=Syne:wght@800&display=swap');
*{box-sizing:border-box;margin:0;padding:0}body{font-family:'DM Sans',sans-serif;color:#111}
.header{background:#111;padding:28px 40px;display:flex;justify-content:space-between;align-items:center}
.logo{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#f5a623;white-space:nowrap;flex-shrink:0;overflow:visible}
.body{padding:28px 40px}
.kpi-row{display:flex;gap:14px;margin-bottom:24px}
.kpi{background:#f9fafb;border:1px solid #e5e7eb;border-radius:10px;padding:14px 18px;flex:1;border-top:3px solid #f5a623}
.kpi-val{font-family:'Syne',sans-serif;font-size:26px;font-weight:800}
.kpi-lbl{font-size:10px;color:#999;text-transform:uppercase;letter-spacing:1px;margin-top:4px}
table{width:100%;border-collapse:collapse}thead tr{background:#f3f4f6}
th{text-align:left;padding:9px 12px;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#666}
.footer{margin-top:24px;padding-top:12px;border-top:1px solid #e5e7eb;text-align:center;font-size:11px;color:#999}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
<div class="header">
  <div><div class="logo">BertocProject Srl</div><div style="font-size:10px;color:#888;letter-spacing:2px;text-transform:uppercase;margin-top:2px">Report annuale consuntivo</div></div>
  <div style="text-align:right"><div style="font-family:'Syne',sans-serif;font-size:38px;font-weight:800;color:#f5a623">${anno}</div><div style="font-size:11px;color:#aaa">Generato il ${new Date().toLocaleDateString("it-IT")}</div></div>
</div>
<div class="body">
  <div class="kpi-row">
    <div class="kpi"><div class="kpi-val">${list.length}</div><div class="kpi-lbl">Cantieri completati</div></div>
    <div class="kpi"><div class="kpi-val">€ ${Math.round(tot/1000)}k</div><div class="kpi-lbl">Fatturato totale</div></div>
    <div class="kpi"><div class="kpi-val" style="color:${mCol}">€ ${Math.round(marg/1000)}k</div><div class="kpi-lbl">Margine netto</div></div>
    <div class="kpi"><div class="kpi-val" style="color:${mCol}">${perc}%</div><div class="kpi-lbl">Margine medio</div></div>
  </div>
  <table>
    <thead><tr><th>Cantiere</th><th>Cliente</th><th>Completamento</th><th style="text-align:right">Importo</th><th style="text-align:right">Margine %</th></tr></thead>
    <tbody>${righe}</tbody>
    <tfoot><tr style="background:#fffbeb"><td colspan="3" style="padding:10px 12px;font-weight:800">TOTALE ${anno}</td><td style="padding:10px 12px;text-align:right;font-weight:800;font-size:16px">€ ${tot.toLocaleString("it-IT")}</td><td style="padding:10px 12px;text-align:right;font-weight:800;font-size:16px;color:${mCol}">${perc}%</td></tr></tfoot>
  </table>
  <div class="footer">BertocProject Srl · Via Vecchia Provinciale Lucchese 47C, Serravalle Pistoiese · P.IVA 01747090478</div>
</div>
<script>window.onload=()=>window.print()</script>
</body></html>`;
    const blob = new Blob([html], { type:"text/html" });
    window.open(URL.createObjectURL(blob), "_blank");
  };

  // ── DETTAGLIO CANTIERE COMPLETATO ─────────────────────────────────────────
  if (dettaglio) {
    const c = dettaglio;
    const importo = safeNum(c.importo);
    const costoLavoro = safeNum(c.costoLavoro);
    const costoMateriali = safeNum(c.costoMateriali);
    const margine = c.margine != null ? safeNum(c.margine) : importo - costoLavoro - costoMateriali;
    const margPerc = importo > 0 ? Math.round((margine/importo)*100) : 0;
    return (
      <div className="content">
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20, flexWrap:"wrap" }}>
          <button className="btn btn-ghost" onClick={() => setDettaglio(null)}>← Archivio</button>
          <span style={{ color:theme.textMuted }}>/ {c.nome}</span>
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => generaReportCantiere(c)}>🖨️ Report PDF</button>
            <span className="badge badge-success">✓ COMPLETATO</span>
          </div>
        </div>
        <div className="grid-2" style={{ marginBottom:16 }}>
          <div className="card">
            <div className="card-title" style={{ marginBottom:14 }}>📋 Info cantiere</div>
            {[
              ["Cliente", c.cliente||"—"],
              ["Indirizzo", c.indirizzo ? `${c.indirizzo}${c.citta?", "+c.citta:""}` : "—"],
              ["Inizio lavori", c.inizio||"—"],
              ["Completato il", c.dataCompletamento||c.fine||"—"],
              ["Importo totale", "€ "+importo.toLocaleString("it-IT")],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${theme.border}44` }}>
                <span style={{ fontSize:12, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>{k}</span>
                <span style={{ fontSize:14, fontWeight:500 }}>{v}</span>
              </div>
            ))}
            {(c.operai||[]).length > 0 && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>Operai coinvolti</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {c.operai.map(op => {
                    const dp = dipendenti.find(d => d.nome===op);
                    return <div key={op} className="avatar" style={{ background:dp?.colore||"#888" }} title={op}>{op[0]}</div>;
                  })}
                </div>
              </div>
            )}
            {c.note && <div style={{ marginTop:14, padding:"10px 12px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}`, fontSize:13, color:theme.textMuted, fontStyle:"italic" }}>📝 {c.note}</div>}
          </div>
          <div className="card">
            <div className="card-title" style={{ marginBottom:14 }}>💰 Consuntivo economico</div>
            {[
              ["Importo fatturato", importo, theme.text],
              ["Costo manodopera",  costoLavoro, "#f06060"],
              ["Costo materiali",   costoMateriali, "#f5a623"],
              ["Margine netto",     margine, "#3ecf8e"],
            ].map(([k,v,col]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"10px 0", borderBottom:`1px solid ${theme.border}44` }}>
                <span style={{ fontSize:13, color:theme.textMuted }}>{k}</span>
                <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, color:col }}>€ {v.toLocaleString("it-IT")}</span>
              </div>
            ))}
            <div style={{ marginTop:16, padding:"14px", background:theme.bg, borderRadius:10, border:`1px solid #3ecf8e44`, textAlign:"center" }}>
              <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>Margine %</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:42, color:margPerc>=30?"#3ecf8e":margPerc>=20?"#f5a623":"#f06060" }}>
                {margPerc}%
              </div>
            </div>
          </div>
        </div>
        <div style={{ textAlign:"right" }}>
          <button className="btn btn-primary" onClick={() => generaReportCantiere(c)}>🖨️ Genera Report PDF cantiere</button>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div className="tabs" style={{ marginBottom:20 }}>
        <div className={`tab ${tab==="cantieri"?"active":""}`} onClick={() => setTab("cantieri")}>🏗️ Cantieri completati</div>
        <div className={`tab ${tab==="preventivi"?"active":""}`} onClick={() => setTab("preventivi")}>📄 Preventivi</div>
        <div className={`tab ${tab==="statistiche"?"active":""}`} onClick={() => setTab("statistiche")}>📊 Statistiche</div>
        <div className={`tab ${tab==="categorie"?"active":""}`} onClick={() => setTab("categorie")}>📈 Per categoria</div>
        <div className={`tab ${tab==="cartelle"?"active":""}`} onClick={() => setTab("cartelle")}>📁 Per cliente</div>
      </div>

      {/* ── TAB CANTIERI ── */}
      {tab === "cantieri" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
            {[
              { label:"Cantieri completati", val:cantieriCompletati.length, icon:"🏗️", color:theme.text },
              { label:"Fatturato totale", val:"€ "+totImporto.toLocaleString("it-IT"), icon:"💰", color:"#4e9af5" },
              { label:"Margine totale", val:"€ "+totMargine.toLocaleString("it-IT"), icon:"📈", color:"#3ecf8e" },
              { label:"Margine medio", val:marginePerc+"%", icon:"🎯", color:marginePerc>=30?"#3ecf8e":marginePerc>=20?"#f5a623":"#f06060" },
            ].map(k => (
              <div key={k.label} className="card" style={{ padding:"16px 18px" }}>
                <div style={{ fontSize:20, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:k.color }}>{k.val}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...inputSt, width:240 }} placeholder="🔍 Cerca cantiere o cliente..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {anniDisponibili.length > 0 && (
              <div style={{ display:"flex", gap:6 }}>
                {["tutti", ...anniDisponibili].map(a => (
                  <div key={a} onClick={() => setFiltroAnno(a)}
                    style={{ padding:"5px 12px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600,
                      border:`1px solid ${filtroAnno===a?theme.accent:theme.border}`,
                      background:filtroAnno===a?theme.accent+"22":theme.bg,
                      color:filtroAnno===a?theme.accent:theme.textMuted }}>
                    {a==="tutti"?"Tutti gli anni":a}
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginLeft:"auto" }}>
              <button className="btn btn-ghost btn-sm" onClick={generaReportAnnuale}>🖨️ Report annuale PDF</button>
            </div>
          </div>

          {cantieriFiltrati.length === 0 ? (
            <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🏁</div>
              <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Nessun cantiere completato</div>
              <div style={{ fontSize:13, marginTop:6 }}>I cantieri appariranno qui dopo essere stati segnati come completati.</div>
            </div>
          ) : cantieriFiltrati.map(c => {
            const imp  = safeNum(c.importo);
            const marg = c.margine != null ? safeNum(c.margine) : 0;
            const mPerc = imp > 0 && marg !== 0 ? Math.round((marg/imp)*100) : null;
            return (
              <div key={c._id||c.id} className="card" style={{ marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:12 }} onClick={() => setDettaglio(c)} className="cursor-pointer">
                  <div style={{ flex:1, cursor:"pointer" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4, flexWrap:"wrap" }}>
                      <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{c.nome}</span>
                      <span className="badge badge-success">✓ Completato</span>
                      {c.dataCompletamento && <span style={{ fontSize:11, color:theme.textDim }}>{c.dataCompletamento}</span>}
                    </div>
                    <div style={{ fontSize:12, color:theme.textMuted, marginBottom:4 }}>
                      👤 {c.cliente||"—"} · 📍 {c.indirizzo||"—"}
                    </div>
                    <div style={{ fontSize:12, color:theme.textDim }}>
                      {c.inizio||"—"} → {c.dataCompletamento||c.fine||"—"}
                    </div>
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20 }}>€ {imp.toLocaleString("it-IT")}</div>
                    {mPerc !== null && (
                      <div style={{ fontSize:13, fontWeight:700, marginTop:2, color:mPerc>=30?"#3ecf8e":mPerc>=20?"#f5a623":"#f06060" }}>
                        margine {mPerc}%
                      </div>
                    )}
                  </div>
                </div>
                {/* Pulsanti azioni — riga separata */}
                <div style={{ display:"flex", gap:8, paddingTop:10, borderTop:`1px solid ${theme.border}44`, flexWrap:"wrap" }}>
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); generaReportCantiere(c); }}>🖨️ PDF</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setDettaglio(c)}>📂 Apri dettaglio</button>
                  <button className="btn btn-ghost btn-sm" style={{ color:"#3ecf8e", borderColor:"#3ecf8e44" }}
                    onClick={async e => {
                      e.stopPropagation();
                      if (!window.confirm(`Riaprire "${c.nome}"?\nTornerà tra i cantieri attivi.`)) return;
                      try { await updateDoc(doc(db,"cantieri",c._id||c.id),{ completato:false, daIncassare:false, saldato:false, avanzamento:99, dataCompletamento:null, dataSaldo:null, updatedAt:new Date().toISOString() }); }
                      catch(err){ alert("❌ "+err.message); }
                    }}>🔓 Riapri</button>
                  <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
                    <button className="btn btn-ghost btn-sm" style={{ color:"#f5a623", borderColor:"#f5a62344" }}
                      onClick={async e => {
                        e.stopPropagation();
                        if (!window.confirm(`Nascondere "${c.nome}" dall'archivio?\nResterà nel database ma non sarà visibile.`)) return;
                        try { await updateDoc(doc(db,"cantieri",c._id||c.id),{nascosto:true}); }
                        catch(err){ alert("❌ "+err.message); }
                      }}>👁️ Nascondi</button>
                    <button className="btn btn-ghost btn-sm" style={{ color:theme.danger, borderColor:"#f0606044" }}
                      onClick={async e => {
                        e.stopPropagation();
                        if (confermaEliminaCantiere(assenzeDB, c) !== true) return;
                        try { await deleteDoc(doc(db,"cantieri",c._id||c.id)); }
                        catch(err){ alert("❌ "+err.message); }
                      }}>🗑️ Elimina</button>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ── TAB PREVENTIVI ── */}
      {tab === "preventivi" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
            {[
              { label:"Totale inviati", val:preventiviDB.length, icon:"📄", color:theme.text },
              { label:"Tasso accettazione", val:(isNaN(tassoAcc)?0:tassoAcc)+"%", icon:"✅", color:"#3ecf8e" },
              { label:"Valore accettati", val:"€ "+valAcc.toLocaleString("it-IT"), icon:"💰", color:"#4e9af5" },
              { label:"Rifiutati / Scaduti", val:`${prevRif.length} / ${prevSca.length}`, icon:"❌", color:"#f06060" },
            ].map(k => (
              <div key={k.label} className="card" style={{ padding:"16px 18px" }}>
                <div style={{ fontSize:20, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:k.color }}>{k.val}</div>
              </div>
            ))}
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
            {[["tutti","Tutti",theme.textMuted],["accettato","✅ Accettati","#3ecf8e"],["rifiutato","❌ Rifiutati","#f06060"],["scaduto","⏰ Scaduti","#7b82a0"],["inviato","📤 Inviati","#4e9af5"]].map(([id,label,col]) => (
              <div key={id} onClick={() => setFiltroPrev(id)}
                style={{ padding:"5px 12px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600,
                  border:`1px solid ${filtroPrev===id?col:theme.border}`,
                  background:filtroPrev===id?col+"22":theme.bg, color:filtroPrev===id?col:theme.textMuted }}>
                {label}
              </div>
            ))}
            <input style={{ ...inputSt, marginLeft:"auto", width:230 }} placeholder="🔍 Cerca cliente..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {preventiviDB
            .filter(p => { const s=p.stato||p.header?.stato||"bozza"; return filtroPrev==="tutti"||s===filtroPrev; })
            .filter(p => { const cl=p.cliente||p.header?.cliente||""; const nm=p.nomeCantiere||p.header?.nomeCantiere||""; return !search||cl.toLowerCase().includes(search.toLowerCase())||nm.toLowerCase().includes(search.toLowerCase()); })
            .map(p => {
              const stato = p.stato||p.header?.stato||"bozza";
              const s = STATO_PREV[stato]||STATO_PREV.bozza;
              const importo = safeNum(p.totaleFinale||p.importo);
              return (
                <div key={p._id||p.id} style={{ background:theme.surface, borderRadius:12, padding:"14px 18px", marginBottom:10, border:`1px solid ${s.color}33`, display:"flex", alignItems:"center", gap:16 }}>
                  <div style={{ width:4, height:44, borderRadius:4, background:s.color, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, marginBottom:4 }}>{p.nomeCantiere||p.header?.nomeCantiere||"—"}</div>
                    <div style={{ fontSize:12, color:theme.textMuted }}>👤 {p.cliente||p.header?.cliente||"—"}</div>
                  </div>
                  <div style={{ textAlign:"right" }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18 }}>€ {importo.toLocaleString("it-IT")}</div>
                    <div style={{ display:"inline-block", marginTop:6, padding:"3px 10px", borderRadius:6, background:s.bg, color:s.color, fontSize:11, fontWeight:700 }}>{s.label}</div>
                  </div>
                </div>
              );
            })}
          {preventiviDB.length === 0 && (
            <div style={{ textAlign:"center", padding:"40px 0", color:theme.textDim }}>
              <div style={{ fontSize:36, marginBottom:10 }}>📄</div>
              <div style={{ fontSize:14, color:theme.textMuted }}>Nessun preventivo in archivio</div>
            </div>
          )}
        </>
      )}

      {/* ── TAB STATISTICHE ── */}
      {tab === "statistiche" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
            <div className="card">
              <div className="card-title" style={{ marginBottom:16 }}>📊 Distribuzione margini</div>
              {cantieriCompletati.length === 0 ? (
                <div style={{ color:theme.textDim, fontSize:13, textAlign:"center", padding:"20px 0" }}>Nessun dato ancora</div>
              ) : (() => {
                const fasce = [{ label:"< 20%",color:"#f06060",count:0 },{ label:"20–30%",color:"#f5a623",count:0 },{ label:"> 30%",color:"#3ecf8e",count:0 }];
                cantieriCompletati.forEach(c => {
                  const p = safeNum(c.importo)>0?(safeNum(c.margine)/safeNum(c.importo))*100:0;
                  if(p<20)fasce[0].count++;else if(p<30)fasce[1].count++;else fasce[2].count++;
                });
                const mx = Math.max(...fasce.map(f=>f.count),1);
                return fasce.map(f => (
                  <div key={f.label} style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                      <span style={{ color:theme.textMuted }}>{f.label}</span>
                      <span style={{ fontWeight:700, color:f.color }}>{f.count} cantieri</span>
                    </div>
                    <div style={{ height:8, background:theme.border, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(f.count/mx)*100}%`, background:f.color, borderRadius:4 }} />
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="card">
              <div className="card-title" style={{ marginBottom:16 }}>📅 Fatturato per anno</div>
              {anniDisponibili.length === 0 ? (
                <div style={{ color:theme.textDim, fontSize:13, textAlign:"center", padding:"20px 0" }}>Nessun dato ancora</div>
              ) : anniDisponibili.map(anno => {
                const list = cantieriCompletati.filter(c=>(c.dataCompletamento||c.fine||"").slice(-4)===anno);
                const tot = list.reduce((s,c)=>s+safeNum(c.importo),0);
                const mx = Math.max(...anniDisponibili.map(a=>cantieriCompletati.filter(c=>(c.dataCompletamento||c.fine||"").slice(-4)===a).reduce((s,c)=>s+safeNum(c.importo),0)),1);
                return (
                  <div key={anno} style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:4 }}>
                      <span style={{ fontWeight:600 }}>{anno}</span>
                      <span style={{ fontWeight:700, color:"#4e9af5" }}>€ {Math.round(tot/1000)}k</span>
                    </div>
                    <div style={{ height:8, background:theme.border, borderRadius:4, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(tot/mx)*100}%`, background:"#4e9af5", borderRadius:4 }} />
                    </div>
                    <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>{list.length} cantieri</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="card">
            <div className="card-title" style={{ marginBottom:16 }}>🏆 Top clienti per fatturato</div>
            {(() => {
              const map = {};
              cantieriCompletati.forEach(c => {
                const n = c.cliente||"—";
                if(!map[n])map[n]={nome:n,fatturato:0,cantieri:0};
                map[n].fatturato+=safeNum(c.importo);map[n].cantieri++;
              });
              const sorted = Object.values(map).sort((a,b)=>b.fatturato-a.fatturato).slice(0,5);
              if(!sorted.length) return <div style={{ color:theme.textDim, fontSize:13 }}>Nessun dato ancora</div>;
              const mx = Math.max(...sorted.map(c=>c.fatturato),1);
              return sorted.map((cl,i)=>(
                <div key={cl.nome} style={{ display:"flex", alignItems:"center", gap:14, padding:"10px 0", borderBottom:`1px solid ${theme.border}44` }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:theme.textDim, minWidth:28 }}>#{i+1}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{cl.nome}</div>
                    <div style={{ height:5, background:theme.border, borderRadius:3, marginTop:5, overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${(cl.fatturato/mx)*100}%`, background:theme.accent, borderRadius:3 }} />
                    </div>
                  </div>
                  <div style={{ textAlign:"right", minWidth:110 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>€ {Math.round(cl.fatturato/1000)}k</div>
                    <div style={{ fontSize:11, color:theme.textDim }}>{cl.cantieri} cantieri</div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      {/* ── TAB PER CLIENTE / ANNO ── */}
      {tab === "cartelle" && (
        <div>
          {/* Filtro anno */}
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
            <div style={{ fontSize:13, color:theme.textMuted, fontWeight:500 }}>📅 Anno:</div>
            {["tutti",...anniDisponibili, ...[...new Set(cantieriDB.map(c=>(c.dataCompletamento||c.fine||"").slice(-4)).filter(a=>/^20\d\d$/.test(a)))].filter(a=>!anniDisponibili.includes(a))].map(a=>(
              <div key={a} onClick={()=>setAnnoCartelle(a)}
                style={{ padding:"6px 14px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600,
                  border:`1px solid ${annoCartelle===a?theme.accent:theme.border}`,
                  background:annoCartelle===a?theme.accentSoft:theme.bg,
                  color:annoCartelle===a?theme.accent:theme.textMuted }}>
                {a === "tutti" ? "Tutti gli anni" : a}
              </div>
            ))}
          </div>

          {/* Vista dettaglio cliente */}
          {clienteAperto ? (
            <div>
              <button className="btn btn-ghost" style={{ marginBottom:16 }} onClick={()=>setClienteAperto(null)}>
                ← Tutti i clienti
              </button>
              {(() => {
                const { anno, cliente } = clienteAperto;
                const cantieriCliente = cantieriDB.filter(c =>
                  c.cliente === cliente &&
                  (anno === "tutti" || (c.dataCompletamento||c.fine||"").slice(-4) === anno)
                );
                const preventiviCliente = preventiviDB.filter(p =>
                  (p.cliente||p.header?.cliente) === cliente
                );
                const sopralluoghiCliente = sopralluoghiDB.filter(s => s.cliente === cliente);

                return (
                  <div>
                    {/* Header cliente */}
                    <div className="card" style={{ marginBottom:16, borderColor:`${theme.accent}44` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                        <div style={{ width:52, height:52, borderRadius:"50%", background:theme.accent, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:"#111", flexShrink:0 }}>
                          {(cliente||"?")[0]}
                        </div>
                        <div>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20 }}>{cliente}</div>
                          <div style={{ fontSize:13, color:theme.textMuted, marginTop:3 }}>
                            {cantieriCliente.length} cantieri · {preventiviCliente.length} preventivi · {sopralluoghiCliente.length} sopralluoghi
                          </div>
                          <div style={{ fontSize:13, color:theme.success, marginTop:2, fontWeight:600 }}>
                            Totale fatturato: € {cantieriCliente.reduce((s,c)=>s+safeNum(c.importo),0).toLocaleString("it-IT")}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cantieri */}
                    {cantieriCliente.length > 0 && (
                      <div className="card" style={{ marginBottom:16 }}>
                        <div className="card-title" style={{ marginBottom:14 }}>🏗️ Cantieri</div>
                        {cantieriCliente.map(c => (
                          <div key={c._id||c.id} style={{ padding:"12px 0", borderBottom:`1px solid ${theme.border}44` }}>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                              <div>
                                <div style={{ fontWeight:600, fontSize:14 }}>{c.nome}</div>
                                <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>
                                  📍 {c.indirizzo||"—"} · {c.inizio||"—"} → {c.dataCompletamento||c.fine||"—"}
                                </div>
                              </div>
                              <div style={{ textAlign:"right" }}>
                                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, color:theme.success }}>€ {safeNum(c.importo).toLocaleString("it-IT")}</div>
                                <span className={`badge ${c.completato?"badge-success":"badge-warning"}`}>{c.completato?"Completato":"In corso"}</span>
                              </div>
                            </div>
                            <div style={{ display:"flex", gap:8 }}>
                              <button className="btn btn-ghost btn-sm" style={{ color:"#3ecf8e", borderColor:"#3ecf8e44" }}
                                onClick={async () => {
                                  if (!window.confirm(`Riaprire "${c.nome}"?\nTornerà tra i cantieri attivi.`)) return;
                                  try { await updateDoc(doc(db,"cantieri",c._id||c.id),{ completato:false, dataCompletamento:null }); }
                                  catch(err){ alert("❌ "+err.message); }
                                }}>🔓 Riapri</button>
                              <button className="btn btn-ghost btn-sm" style={{ color:"#f5a623", borderColor:"#f5a62344" }}
                                onClick={async () => {
                                  if (!window.confirm(`Nascondere "${c.nome}" dall'archivio?`)) return;
                                  try { await updateDoc(doc(db,"cantieri",c._id||c.id),{nascosto:true}); }
                                  catch(err){ alert("❌ "+err.message); }
                                }}>👁️ Nascondi</button>
                              <button className="btn btn-ghost btn-sm" style={{ color:theme.danger, borderColor:"#f0606044" }}
                                onClick={async () => {
                                  if (confermaEliminaCantiere(assenzeDB, c) !== true) return;
                                  try { await deleteDoc(doc(db,"cantieri",c._id||c.id)); }
                                  catch(err){ alert("❌ "+err.message); }
                                }}>🗑️ Elimina</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Preventivi */}
                    {preventiviCliente.length > 0 && (
                      <div className="card" style={{ marginBottom:16 }}>
                        <div className="card-title" style={{ marginBottom:14 }}>📄 Preventivi</div>
                        {preventiviCliente.map(p => {
                          const stato = p.stato||p.header?.stato||"bozza";
                          const s = STATO_PREV[stato]||STATO_PREV.bozza;
                          // Cerca cantiere collegato per recuperare prezzi se mancanti nel preventivo
                          const cantiereCollegato = cantieriDB.find(c => c.preventivoId===p._id || (c.cliente===clienteAperto.cliente && (c.nome===(p.nomeCantiere||p.header?.nomeCantiere))));
                          const lavoriConPrezzi = (p.lavorazioni||[]).map(l => {
                            if (parseFloat(l.prezzoUn||0)>0 || parseFloat(l.totale||0)>0) return l;
                            // Cerca nel cantiere collegato
                            const lCant = (cantiereCollegato?.lavorazioni||[]).find(lc => (lc.desc||"").toLowerCase()===(l.desc||"").toLowerCase());
                            return { ...l, prezzoUn: lCant?.prezzoUn||0, totale: lCant?.totale||0 };
                          });
                          return (
                            <div key={p._id}>
                            <div style={{ padding:"10px 0", borderBottom:`1px solid ${theme.border}44`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                              <div style={{ flex:1 }}>
                                <div style={{ fontWeight:600, fontSize:13 }}>{p.nomeCantiere||p.header?.nomeCantiere||"—"}</div>
                                <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>{p.createdAt?new Date(p.createdAt).toLocaleDateString("it-IT"):"—"}</div>
                              </div>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700 }}>€ {safeNum(p.totaleFinale||p.importo).toLocaleString("it-IT")}</div>
                                <span style={{ background:s.bg, color:s.color, border:`1px solid ${s.color}44`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>{s.label}</span>
                                <button className="btn btn-ghost btn-sm" onClick={()=>setClienteAperto({...clienteAperto, prevAperto: clienteAperto.prevAperto===p._id?null:p._id})}>
                                  {clienteAperto.prevAperto===p._id?"▲":"▼"}
                                </button>
                                <button className="btn btn-ghost btn-sm" style={{ color:theme.danger }}
                                  onClick={async()=>{
                                    if(!window.confirm("Eliminare questo preventivo?"))return;
                                    try{ await deleteDoc(doc(db,"preventivi",p._id)); }
                                    catch(e){ alert("❌ "+e.message); }
                                  }}>🗑️</button>
                              </div>
                            </div>
                            {clienteAperto.prevAperto===p._id && (
                              <div style={{ background:theme.bg, borderRadius:10, padding:"14px", margin:"8px 0", border:`1px solid ${theme.border}` }}>
                                <div style={{ fontSize:12, fontWeight:700, color:theme.accent, marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>📋 Voci preventivo</div>
                                {lavoriConPrezzi.length===0 ? (
                                  <div style={{ fontSize:13, color:theme.textDim }}>Nessuna voce registrata</div>
                                ) : (
                                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                                    <thead>
                                      <tr style={{ borderBottom:`1px solid ${theme.border}` }}>
                                        <th style={{ textAlign:"left", padding:"4px 8px", fontSize:11, color:theme.textDim }}>Descrizione</th>
                                        <th style={{ textAlign:"center", padding:"4px 8px", fontSize:11, color:theme.textDim }}>Q.tà</th>
                                        <th style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:theme.textDim }}>€/um</th>
                                        <th style={{ textAlign:"right", padding:"4px 8px", fontSize:11, color:theme.textDim }}>Totale</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {lavoriConPrezzi.map((l,i)=>{
                                        // Se prezzoUn non è salvato, prova a calcolarlo
                                        const qta = parseFloat(l.qta)||0;
                                        const totL = parseFloat(l.totale||0);
                                        const prezzoUn = parseFloat(l.prezzoUn||0) || (qta>0?totL/qta:0);
                                        return (
                                        <tr key={i} style={{ borderBottom:`1px solid ${theme.border}22` }}>
                                          <td style={{ padding:"6px 8px" }}>{l.desc||"—"}</td>
                                          <td style={{ textAlign:"center", padding:"6px 8px", color:theme.textMuted }}>{qta} {l.um}</td>
                                          <td style={{ textAlign:"right", padding:"6px 8px", color:theme.textMuted }}>{prezzoUn>0?"€ "+prezzoUn.toFixed(2):"—"}</td>
                                          <td style={{ textAlign:"right", padding:"6px 8px", fontWeight:700, color:theme.success }}>{totL>0?"€ "+totL.toFixed(2):"—"}</td>
                                        </tr>
                                        );
                                      })}
                                    </tbody>
                                    <tfoot>
                                      <tr style={{ borderTop:`1px solid ${theme.border}` }}>
                                        <td colSpan={3} style={{ padding:"8px", fontWeight:700 }}>Totale preventivo</td>
                                        <td style={{ textAlign:"right", padding:"8px", fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:theme.accent }}>€ {safeNum(p.totaleFinale||p.importo).toFixed(2)}</td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Sopralluoghi */}
                    {sopralluoghiCliente.length > 0 && (
                      <div className="card" style={{ marginBottom:16 }}>
                        <div className="card-title" style={{ marginBottom:14 }}>📋 Sopralluoghi</div>
                        {sopralluoghiCliente.map(s => (
                          <div key={s._id} style={{ padding:"10px 0", borderBottom:`1px solid ${theme.border}44`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                            <div>
                              <div style={{ fontWeight:600, fontSize:13 }}>{s.nomeCantiere||"—"}</div>
                              <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>
                                📍 {s.indirizzo||"—"} · {(s.lavorazioni||[]).length} lavorazioni
                              </div>
                            </div>
                            <div style={{ fontSize:11, color:theme.textDim }}>{s.createdAt?new Date(s.createdAt).toLocaleDateString("it-IT"):"—"}</div>
                          </div>
                        ))}
                      </div>
                    )}

                    {cantieriCliente.length===0&&preventiviCliente.length===0&&sopralluoghiCliente.length===0 && (
                      <div style={{ textAlign:"center", padding:"32px 0", color:theme.textDim }}>Nessun documento per questo cliente.</div>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            /* Lista clienti raggruppati per anno */
            (() => {
              // Raccogli tutti i clienti da cantieri + preventivi + sopralluoghi
              const clientiMap = {};
              const aggiungi = (cliente, anno, tipo) => {
                if (!cliente) return;
                const k = `${anno||"—"}__${cliente}`;
                if (!clientiMap[k]) clientiMap[k] = { cliente, anno:anno||"—", cantieri:0, preventivi:0, sopralluoghi:0, fatturato:0 };
                clientiMap[k][tipo]++;
              };
              cantieriDB.forEach(c => {
                const anno = (c.dataCompletamento||c.fine||"").slice(-4)||"in corso";
                if (annoCartelle === "tutti" || anno === annoCartelle) {
                  aggiungi(c.cliente, anno, "cantieri");
                  clientiMap[`${anno}__${c.cliente}`].fatturato += safeNum(c.importo);
                }
              });
              preventiviDB.forEach(p => {
                const cl = p.cliente||p.header?.cliente;
                const anno = p.createdAt?new Date(p.createdAt).getFullYear().toString():"—";
                if (annoCartelle === "tutti" || anno === annoCartelle) aggiungi(cl, anno, "preventivi");
              });
              sopralluoghiDB.forEach(s => {
                const anno = s.createdAt?new Date(s.createdAt).getFullYear().toString():"—";
                if (annoCartelle === "tutti" || anno === annoCartelle) aggiungi(s.cliente, anno, "sopralluoghi");
              });

              const lista = Object.values(clientiMap).sort((a,b)=>b.fatturato-a.fatturato);

              if (!lista.length) return (
                <div style={{ textAlign:"center", padding:"40px 0", color:theme.textDim }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>📁</div>
                  <div style={{ fontSize:14, color:theme.textMuted }}>Nessun cliente trovato per l'anno selezionato.</div>
                </div>
              );

              // Raggruppa per anno
              const perAnno = {};
              lista.forEach(cl => {
                if (!perAnno[cl.anno]) perAnno[cl.anno] = [];
                perAnno[cl.anno].push(cl);
              });

              return Object.entries(perAnno).sort(([a],[b])=>b.localeCompare(a)).map(([anno, clienti])=>(
                <div key={anno} style={{ marginBottom:24 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                    <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:theme.accent }}>📅 {anno}</div>
                    <div style={{ fontSize:12, color:theme.textDim }}>{clienti.length} clienti · € {clienti.reduce((s,c)=>s+c.fatturato,0).toLocaleString("it-IT")}</div>
                  </div>
                  {clienti.map(cl => (
                    <div key={cl.cliente} className="card" style={{ marginBottom:10, cursor:"pointer", border:`1px solid ${theme.border}` }}
                      onClick={()=>setClienteAperto({ anno:cl.anno, cliente:cl.cliente })}>
                      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                        <div style={{ width:40, height:40, borderRadius:"50%", background:theme.accent, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:"#111", flexShrink:0 }}>
                          {(cl.cliente||"?")[0]}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{cl.cliente}</div>
                          <div style={{ fontSize:12, color:theme.textMuted, marginTop:2, display:"flex", gap:12 }}>
                            {cl.cantieri>0 && <span>🏗️ {cl.cantieri} cantieri</span>}
                            {cl.preventivi>0 && <span>📄 {cl.preventivi} preventivi</span>}
                            {cl.sopralluoghi>0 && <span>📋 {cl.sopralluoghi} sopralluoghi</span>}
                          </div>
                        </div>
                        {cl.fatturato>0 && (
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, color:theme.success }}>€ {cl.fatturato.toLocaleString("it-IT")}</div>
                            <div style={{ fontSize:11, color:theme.textDim }}>fatturato</div>
                          </div>
                        )}
                        <div style={{ fontSize:13, color:theme.accent }}>→</div>
                      </div>
                    </div>
                  ))}
                </div>
              ));
            })()
          )}
        </div>
      )}

      {/* ── TAB PER CATEGORIA ── */}
      {tab === "categorie" && (
        <div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
            Le lavorazioni vengono raggruppate per categoria basata sul nome. Vedi dove guadagni di più e dove migliorare.
          </div>
          {(() => {
            // FIX_STATSPERCAT_ALLINEA_v2
            // Itera sui cantieriCompletati (4 cantieri completati+saldati, coerente
            // con la tab "Cantieri completati" della stessa pagina Archivio). Legge
            // dal preventivo del cantiere (dove c'e' minuti/categoria/spesaEsterna),
            // cerca la gemella nel consuntivo per il ricavo reale, ore reali da
            // assenze, materiali reali da bolle, spesa esterna sottratta nel margine.
            const statsPerCat = {};
            cantieriCompletati.forEach(cant => {
              const cid = cant._id||cant.id;
              const consuntivo = cant.consuntivo;
              const lavsConsuntivo = consuntivo?.lavorazioni || [];
              (cant.lavorazioni||[]).forEach((l,i) => {
                const desc = (l.desc||"").trim();
                if (!desc) return;
                const text = desc.toLowerCase();
                const chiave = l.categoria
                  || Object.keys(dizionarioMateriali).find(k => text.includes(k))
                  || "altro";
                if (!statsPerCat[chiave]) statsPerCat[chiave] = { chiave, lavorazioni:0, ricavoTot:0, oreRealiTot:0, matRealiTot:0, spesaEstTot:0, cantieri:new Set() };
                const s = statsPerCat[chiave];
                s.lavorazioni++;
                // ricavo reale: cerca gemella nel consuntivo, altrimenti usa preventivato
                let gemella = null;
                if (consuntivo) {
                  gemella = lavsConsuntivo.find(lc => lc.id && l.id && lc.id===l.id)
                         || lavsConsuntivo.find(lc => (lc.desc||"").trim()===desc);
                }
                const totP = parseFloat(l.totalePrev||l.totale||0) || (parseFloat(l.prezzoUn||0)*parseFloat(l.qta||0));
                // FIX_STATSPERCAT_RICAVO_ROBUSTO_v4: se la gemella non e' trovata, fallback su totP (preventivo)
                const ricavoLav = consuntivo
                  ? (gemella ? (parseFloat(gemella.totale)||totP) : totP)
                  : totP;
                s.ricavoTot += ricavoLav;
                // ore reali da assenze
                const oreR = assenzeDB.filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid||(a.cantiere||"")===(cant.nome||""))&&(trovaIdxLav(a, cant.lavorazioni /* LAV_BY_ID_v8 */)===i)).reduce((acc,a)=>acc+(parseFloat(a.ore)||0),0);
                s.oreRealiTot += oreR;
                // materiali reali da bolle
                const matR = bolleDB.flatMap(b=>(b.righe||[]).filter(r=>r.cantiere===cant.nome&&r.lavorazione===desc)).reduce((acc,r)=>acc+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
                s.matRealiTot += matR;
                // spesa esterna (subappalti): qta * spesaEsterna
                s.spesaEstTot += (parseFloat(l.spesaEsterna)||0)*(parseFloat(l.qta)||0);
                s.cantieri.add(cid);
              });
            });
            const lista = Object.values(statsPerCat).map(s => {
              const costoOra = s.oreRealiTot*COSTO_ORARIO_GLOBALE;
              const costiTot = costoOra + s.matRealiTot + s.spesaEstTot;
              const margine = s.ricavoTot - costiTot;
              return {
                ...s, cantieri:s.cantieri.size,
                costoOra,
                orePrevTot: s.oreRealiTot,   // compat: la UI legge orePrevTot
                matPrevTot: s.matRealiTot,   // compat: la UI legge matPrevTot
                margine,
                percMargine: s.ricavoTot>0?Math.round((margine/s.ricavoTot)*100):0
              };
            }).sort((a,b)=>b.ricavoTot-a.ricavoTot);

            if (!lista.length) return (
              <div style={{ textAlign:"center", padding:"40px 0", color:theme.textDim }}>
                <div style={{ fontSize:36, marginBottom:10 }}>📈</div>
                <div style={{ fontSize:14, color:theme.textMuted }}>Nessun dato ancora. Le statistiche si popolano man mano che inserisci lavorazioni nei cantieri.</div>
              </div>
            );
            const maxRicavo = Math.max(...lista.map(s=>s.ricavoTot),1);
            return lista.map(s => {
              const colMarg = s.percMargine>=30?"#3ecf8e":s.percMargine>=15?"#f5a623":"#f06060";
              const alertMarg = s.percMargine < 15;
              return (
                <div key={s.chiave} className="card" style={{ marginBottom:14, border:`1px solid ${alertMarg?"#f0606033":theme.border}` }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                    <div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16, textTransform:"capitalize" }}>
                        {s.chiave}
                        {alertMarg && <span style={{ marginLeft:10, fontSize:11, background:"#f0606022", color:"#f06060", border:"1px solid #f0606044", borderRadius:4, padding:"2px 8px", fontWeight:700 }}>⚠️ Margine basso</span>}
                      </div>
                      <div style={{ fontSize:12, color:theme.textMuted, marginTop:3 }}>{s.cantieri} cantieri · {s.lavorazioni} lavorazioni · {s.orePrevTot.toFixed(0)}h previste</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:24, color:colMarg }}>{s.percMargine}%</div>
                      <div style={{ fontSize:11, color:theme.textDim }}>margine</div>
                    </div>
                  </div>
                  <div style={{ height:6, background:theme.border, borderRadius:3, overflow:"hidden", marginBottom:12 }}>
                    <div style={{ height:"100%", width:`${(s.ricavoTot/maxRicavo)*100}%`, background:theme.accent, borderRadius:3 }} />
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
                    {[
                      ["Ricavo totale","€ "+Math.round(s.ricavoTot).toLocaleString("it-IT"),theme.accent],
                      ["Costo manodopera","€ "+Math.round(s.costoOra).toLocaleString("it-IT"),"#f06060"],
                      ["Costo materiali","€ "+Math.round(s.matPrevTot).toLocaleString("it-IT"),"#f5a623"],
                      ["Margine lordo",(s.margine>=0?"+":"")+"€ "+Math.round(s.margine).toLocaleString("it-IT"),colMarg],
                    ].map(([label,val,col])=>(
                      <div key={label} style={{ background:theme.bg, borderRadius:8, padding:"10px 12px" }}>
                        <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{label}</div>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14, color:col }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  {alertMarg && (
                    <div style={{ marginTop:12, padding:"10px 14px", background:"#1a0a0a", borderRadius:8, border:"1px solid #f0606033", fontSize:12 }}>
                      <div style={{ color:"#f06060", fontWeight:600, marginBottom:6 }}>💡 Possibili cause:</div>
                      <div style={{ color:theme.textMuted, display:"flex", flexDirection:"column", gap:3 }}>
                        {s.costoOra > s.ricavoTot*0.5 && <span>• Troppe ore rispetto al ricavo — valuta di aumentare il prezzo o ottimizzare i tempi</span>}
                        {s.matPrevTot > s.ricavoTot*0.4 && <span>• Costo materiali elevato — tratta prezzi migliori con i fornitori</span>}
                        {s.margine < 0 && <span>• Lavorazione in perdita — stai lavorando sotto costo</span>}
                        {s.lavorazioni > 5 && s.percMargine < 15 && <span>• Alta frequenza con margini bassi — questa categoria trascina i risultati</span>}
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}



// ─── MATERIALI ────────────────────────────────────────────────────────────────

// Categorie materiali predefinite
const CATEGORIE_MATERIALI = [
  { id:"cartongesso",  label:"Cartongesso",    emoji:"🧱" },
  { id:"vernice",      label:"Vernice/Pittura", emoji:"🎨" },
  { id:"cotto",        label:"Cotto/Ceramica",  emoji:"🏺" },
  { id:"malta",        label:"Malta/Stucco",    emoji:"🪣" },
  { id:"legno",        label:"Legno/Parquet",   emoji:"🪵" },
  { id:"ferro",        label:"Ferro/Metallo",   emoji:"🔩" },
  { id:"idraulica",    label:"Idraulica",        emoji:"🔧" },
  { id:"elettrica",    label:"Elettrica",        emoji:"⚡" },
  { id:"isolamento",   label:"Isolamento",       emoji:"🧊" },
  { id:"altro",        label:"Altro",            emoji:"📦" },
];

function Materiali({ cantieriDB = [], famiglieProdottoDB = [], bolleDB = [], utente = null }) {
  const [tab, setTab] = useState("bolle");
  const [materiali, setMateriali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ materiale:"", fornitore:"", cantiere:"", qta:"", um:"mq", urgenza:"media", stato:"da_ordinare", note:"", prezzo:"" });
  const [filtroStato, setFiltroStato] = useState("tutti");
  const [filtroUrgenza, setFiltroUrgenza] = useState("tutti");
  const [search, setSearch] = useState("");

  // ── BOLLE STATE ──────────────────────────────────────────────────────────────
  const [bolle, setBolle] = useState([]);
  const [loadingBolle, setLoadingBolle] = useState(true);
  const [showBollaForm, setShowBollaForm] = useState(false);
  const [bollaAperta, setBollaAperta] = useState(null); // id bolla aperta in dettaglio
  const [pdfBollaLoading, setPdfBollaLoading] = useState(false);
  const [pdfFatturaLoading, setPdfFatturaLoading] = useState(false);

  const importaFattura = async (file) => {
    if (!file) return;
    setPdfFatturaLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = () => rej(new Error("Lettura fallita"));
        r.readAsDataURL(file);
      });
      const resp = await fetch(`${NAS_URL}/api/analizza-fattura`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pdfBase64: base64 })
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      if (!data.righe || data.righe.length === 0) throw new Error("Nessuna riga trovata nella fattura");

      // FIX_FATTURA_AGGIORNA_BOLLE_v2 — match per DDT + nome normalizzato + resoconto aggregato
      // Normalizzazione robusta del nome prodotto (apostrofi, punteggiatura, spazi multipli)
      const normProdotto = (s) => (s||"")
        .toUpperCase()
        .replace(/['"\u2019\u2018]/g, '')
        .replace(/[.,;:!?]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      // Conferma utente prima di procedere
      const righeFatt = (data.righe||[]).filter(r => r.descrizione && parseFloat(r.prezzoUnitario) > 0);
      // FIX_SCONTI_FATTURA_v1c: dialogo mostra prezzoNetto se c'e sconto
      const riepilogo = righeFatt.slice(0,5).map(r => {
        const lordo = parseFloat(r.prezzoUnitario);
        const netto = r.prezzoNetto !== undefined ? parseFloat(r.prezzoNetto) : lordo;
        const sconto = parseFloat(r.scontoPerc || 0);
        const um = r.umBase || r.um || 'cad';
        if (sconto > 0 && Math.abs(netto - lordo) > 0.001) {
          return "  • " + r.descrizione + ": € " + netto.toFixed(2) + "/" + um + " (-" + sconto + "% su € " + lordo.toFixed(2) + ")";
        }
        return "  • " + r.descrizione + ": € " + lordo + "/" + um;
      }).join("\n");
      const altrePiu = righeFatt.length > 5 ? "\n  … e altre " + (righeFatt.length - 5) + " righe" : "";
      const msgConferma = "Fattura N. " + (data.numFattura||"?") + " — " + (data.fornitore||"?") + "\n\n" + righeFatt.length + " righe con prezzo:\n" + riepilogo + altrePiu + "\n\nAggiorno listino e bolle collegate?";
      if (!window.confirm(msgConferma)) return;

      // 1) Aggiorna listino materiali (FIX_SCONTI_FATTURA_v1b + FIX_CODICI_FORNITORE_v1)
      let listinoAggiornati = 0;
      if (righeFatt.length > 0) {
        aggiornaListinoMateriali(
          righeFatt.map(r => ({
            prodotto: r.descrizione,
            prezzo: parseFloat(r.prezzoUnitario),
            prezzoNetto: r.prezzoNetto !== undefined ? parseFloat(r.prezzoNetto) : undefined,
            scontoPerc: r.scontoPerc !== undefined ? parseFloat(r.scontoPerc) : undefined,
            codiceArticolo: r.codiceArticolo,
            um: r.umBase || r.um,
            fornitore: data.fornitore
          })),
          data.data
        );
        listinoAggiornati = righeFatt.length;
      }

      // 2) Costruisco indice prezzi fattura (FIX_SCONTI_FATTURA_v1b: usa prezzoNetto se presente)
      const prezziPerCodice = new Map();
      const prezziPerNome = new Map();
      const prezziPerDdtENome = new Map();
      for (const rf of righeFatt) {
        const nomeN = normProdotto(rf.descrizione);
        const ddtRif = (rf.ddtRiferimento || "").toString().trim();
        const codArt = (rf.codiceArticolo || "").toString().trim().toUpperCase();
        const prezzo = parseFloat(rf.prezzoNetto !== undefined ? rf.prezzoNetto : rf.prezzoUnitario);
        const info = { prezzo, um: rf.umBase || rf.um || 'cad', descFatt: rf.descrizione };
        if (codArt) prezziPerCodice.set(codArt, info);
        if (nomeN) prezziPerNome.set(nomeN, info);
        if (ddtRif && nomeN) prezziPerDdtENome.set(ddtRif + "||" + nomeN, info);
      }

      // 3) Scorro TUTTE le bolle, aggiorno solo righe SENZA prezzo
      let bolleAggiornate = 0;
      let righeAggiornateCnt = 0;
      try {
        for (const b of (bolle||[])) {
          let modificata = false;
          const bollaNum = (b.num || "").toString().trim();
          const nuoveRighe = (b.righe||[]).map(r => {
            const prezzoRiga = parseFloat(r.prezzo);
            const haPrezzo = !isNaN(prezzoRiga) && prezzoRiga > 0;
            if (haPrezzo || !r.prodotto) return r;
            const nomeN = normProdotto(r.prodotto);
            const codiceR = (r.codice || "").toString().trim().toUpperCase();
            // Livello 1: match per CODICE ARTICOLO (piu' affidabile, non confonde KG.5/KG.10)
            let match = null;
            if (codiceR) {
              match = prezziPerCodice.get(codiceR);
            }
            // Livello 2: match per DDT + nome
            if (!match && bollaNum && nomeN) {
              match = prezziPerDdtENome.get(bollaNum + "||" + nomeN);
            }
            // Livello 3: fallback solo nome esatto
            if (!match && nomeN) {
              match = prezziPerNome.get(nomeN);
            }
            if (match) {
              modificata = true;
              righeAggiornateCnt++;
              return {
                ...r,
                prezzo: match.prezzo,
                um: r.um || match.um,
                totaleRiga: (parseFloat(r.qta)||0) * match.prezzo,
              };
            }
            return r;
          });
          if (modificata) {
            const totaleNuovo = nuoveRighe.reduce((s,x) => s + (parseFloat(x.totaleRiga)||0), 0);
            await updateDoc(doc(db, "bolle", b._id), {
              righe: nuoveRighe,
              totale: totaleNuovo,
              fatturaNum: data.numFattura,
              fatturaData: data.data,
            });
            bolleAggiornate++;
          }
        }
      } catch (errScan) {
        console.warn('FIX_FATTURA_AGGIORNA_BOLLE_v2 scan fallito:', errScan.message);
      }

      // 4) Resoconto finale aggregato
      const parti = [];
      parti.push("✅ Fattura N. " + (data.numFattura||"?") + " caricata");
      if (listinoAggiornati > 0) parti.push("📋 Listino: " + listinoAggiornati + " prodotti aggiornati");
      if (bolleAggiornate > 0) {
        parti.push("📦 Bolle: " + bolleAggiornate + " aggiornate, " + righeAggiornateCnt + " righe valorizzate");
      } else {
        parti.push("📦 Bolle: nessuna riga aggiornata (i prezzi erano gia' presenti o nomi non corrispondenti)");
      }
      alert(parti.join("\n"));

    } catch(e) { alert("Errore: " + e.message); }
    setPdfFatturaLoading(false);
  };

  const importaBollaDaPDF = async (file) => {
    if (!file) return;
    setPdfBollaLoading(true);
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result.split(",")[1]);
        r.onerror = () => rej(new Error("Lettura fallita"));
        r.readAsDataURL(file);
      });
      const resp = await fetch(`${NAS_URL}/api/analizza-bolla`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pdfBase64: base64 })
      });
      const data = await resp.json();
      console.log("🔍 RISPOSTA AI BOLLA:", JSON.stringify(data, null, 2));
      if (data.error) throw new Error(data.error);
      if (!data.righe || data.righe.length === 0) throw new Error("Nessuna riga trovata nel PDF");
      
      // Estrai tutti i dati dalla risposta (nuovi campi v2 + fallback vecchi)
      const cantiereRiconosciuto = data.cantiereNome || data.cantiere || "";
      const cantiereIdRiconosciuto = data.cantiereId || "";
      const destinazione = data.destinazione || (cantiereIdRiconosciuto ? "cantiere" : "magazzino");
      const numBolla = data.numBolla || "";
      const fornitore = data.fornitore || "";
      const dataDoc = data.data ? data.data.split("-").reverse().join("/") : "";

      // Popola il form intestazione bolla
      setBollaForm(f => ({
        ...f,
        num: numBolla || f.num,
        fornitore: fornitore || f.fornitore,
        data: data.data || f.data,
        _cantierePred: cantiereRiconosciuto || f._cantierePred,
        _cantiereIdPred: cantiereIdRiconosciuto || f._cantiereIdPred,
        _destinazione: destinazione
      }));

      // Popola le righe estratte (mappa nuovi campi v2 con fallback ai vecchi)
      const nuoveRighe = data.righe.map(r => {
        // qtaTotale (nuovo) ha priorita su qta (vecchio)
        const totale = r.qtaTotale !== undefined && r.qtaTotale !== null ? r.qtaTotale : r.qta;
        const umBase = r.umBase || r.um || "cad";
        const nConf = r.numeroConfezioni !== undefined && r.numeroConfezioni !== null ? String(r.numeroConfezioni) : "";
        const contConf = r.qtaUnitaria !== undefined && r.qtaUnitaria !== null ? String(r.qtaUnitaria) : "";
        return {
          materialeDiz: "",
          prodotto: r.descrizione || "",
          qta: String(totale || "1"),
          um: umBase,
          // Nuovi campi confezione (auto-popolati per UI N.CONF + LT/KG x CONF)
          nConf: nConf,
          contConf: contConf,
          umConf: r.umConfezione || "",
          // Nuovi campi metadata (per match prodotti, salvataggio, e UI futura)
          codice: r.codiceArticolo || "",
          materialeId: r.materialeId || null,
          isNuovo: r.isNuovo !== undefined ? r.isNuovo : true,
          prezzo: String(r.prezzoUnitario || ""),
          cantiere: cantiereRiconosciuto,
          cantiereId: cantiereIdRiconosciuto,
          lavorazione: "",
          categoriaP: r.famigliaSuggerita || "",
          note: r.note || ""
        };
      });
      setRigheForm(nuoveRighe);
      setShowBollaForm(true);

      const dettagli = [numBolla?"N.Bolla: "+numBolla:null, fornitore?"Fornitore: "+fornitore:null, dataDoc?"Data: "+dataDoc:null, cantiereRiconosciuto?"Cantiere: "+cantiereRiconosciuto:null].filter(Boolean).join(", ");
      alert("Estratte "+nuoveRighe.length+" righe!\n"+dettagli+"\n\nAggiungi lavorazione e salva.");
    } catch(e) { alert("❌ Errore: " + e.message); }
    setPdfBollaLoading(false);
  };
  const [bollaForm, setBollaForm] = useState({ num:"", fornitore:"", data:new Date().toISOString().slice(0,10), note:"" });
  const [righeForm, setRigheForm] = useState([{ categoria:"altro", prodotto:"", qta:"", um:"cad", prezzo:"", cantiere:"", note:"" }]);
  const [searchBolle, setSearchBolle] = useState("");
  const [showScannerFoto, setShowScannerFoto] = useState(false);
  const [salvandoBolla, setSalvandoBolla] = useState(false);

  // ── MAGAZZINO STATE ───────────────────────────────────────────────────────
  const [scarichi, setScarichi] = useState([]);
  const [showScaricoForm, setShowScaricoForm] = useState(false);
  const [scaricoForm, setScaricoForm] = useState({ cantiere:"", data:new Date().toISOString().slice(0,10), note:"", lavorazione:"" });
  const emptyScaricoRiga = { prodotto:"", qta:"", um:"kg", categoriaP:"" };
  const [scaricoRighe, setScaricoRighe] = useState([{ ...emptyScaricoRiga }]);

  // ── LISTINO MATERIALI STATE ───────────────────────────────────────────────
  const [listinoMat, setListinoMat] = useState([]);
  const [loadingListino, setLoadingListino] = useState(true);
  const [searchListinoMat, setSearchListinoMat] = useState("");
  const [listinoView, setListinoView] = useState("prodotti");
  const [listinoAperto, setListinoAperto] = useState(null);

  const inputSt = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%" };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "materiali"), snap => {
      setMateriali(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
      setLoading(false);
    }, () => setLoading(false));

    // Listener bolle
    const unsubBolle = onSnapshot(query(collection(db, "bolle"), orderBy("createdAt","desc")), snap => {
      setBolle(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
      setLoadingBolle(false);
    }, () => setLoadingBolle(false));

    // Listener scarichi magazzino
    const unsubScarichi = onSnapshot(query(collection(db, "scarichi_magazzino"), orderBy("createdAt","desc")), snap => {
      setScarichi(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    }, () => {});

    // Listener listino materiali
    const unsubListino = onSnapshot(collection(db, "listino_materiali"), snap => {
      setListinoMat(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
      setLoadingListino(false);
    }, () => setLoadingListino(false));

    return () => { unsub(); unsubBolle(); unsubListino(); unsubScarichi(); };
  }, []);

  // Aggiorna listino materiali con i prezzi della bolla/fattura
  // FIX_LISTINO_BOLLE_v1: gestisce anche righe senza prezzo (bolle pure)
  // - skip solo se manca prodotto
  // - materiale esistente + senza prezzo: non tocca (bolla per prodotto già a listino)
  // - materiale esistente + con prezzo: aggiorna prezzo e storico (fattura/bolla con prezzo)
  // - materiale nuovo: crea record con prezzo null se da bolla, con valore se da fattura
  // FIX_AGGIORNA_LISTINO_BASE_v1: estrae da nome il contenuto della confezione (KG 25 → 25, LT 5 → 5)
  // Restituisce { umBase: 'kg'|'lt'|...|null, contenutoConf: number }
  // Conservativo: ignora ML.xxx (lunghezze/codici), non inventa nulla
  const estraiContenutoConfDaNome = (prodotto, umGrezza) => {
    const nome = (prodotto || '').toUpperCase();
    const um = (umGrezza || '').toLowerCase().trim();
    // Match KG: "KG.25", "KG25", "KG 25", "5KG", "DA 5 KG"
    let m = nome.match(/(?:^|[^A-Z])(?:KG[\s.]*?(\d+(?:[.,]\d+)?)|(\d+(?:[.,]\d+)?)\s*KG)(?=[^A-Z]|$)/);
    if (m) {
      const v = parseFloat((m[1]||m[2]).replace(',', '.'));
      if (v > 0) return { umBase: 'kg', contenutoConf: v };
    }
    // Match LT
    m = nome.match(/(?:^|[^A-Z])(?:LT[\s.]*?(\d+(?:[.,]\d+)?)|(\d+(?:[.,]\d+)?)\s*LT)(?=[^A-Z]|$)/);
    if (m) {
      const v = parseFloat((m[1]||m[2]).replace(',', '.'));
      if (v > 0) return { umBase: 'lt', contenutoConf: v };
    }
    // UM grezza gia' "base" (mq, ml, kg, lt) → 1:1
    if (['mq','ml','kg','lt'].includes(um)) return { umBase: um, contenutoConf: 1 };
    // UM grezza tipo cad/nr/pz/sa/conf senza nome che indichi contenuto → trattalo come pz
    if (['cad','nr','pz','pa','sa','conf','sacco'].includes(um)) return { umBase: 'pz', contenutoConf: 1 };
    // Altro: lascia null (non sappiamo)
    return { umBase: null, contenutoConf: 1 };
  };

  const aggiornaListinoMateriali = async (righe, dataDoc) => {
    // FIX_RACE_LISTINO_v1: dedup locale per evitare duplicati creati nella stessa chiamata
    const vistiInQuestaChiamata = new Map();
    for (const r of righe) {
      if (!r.prodotto) continue;
      // FIX_SCONTI_FATTURA_v1: usa prezzoNetto se presente (dopo sconto), altrimenti prezzo lordo
      const prezzoNum = parseFloat(r.prezzoNetto || r.prezzo);
      const hasPrezzo = !isNaN(prezzoNum) && prezzoNum > 0;
      const prezzoUnit = hasPrezzo ? prezzoNum : null;
      const qta = parseFloat(r.qta)||1;
      const prezzoTotale = hasPrezzo ? Math.round(prezzoUnit * qta * 100) / 100 : null;
      // FIX_AGGIORNA_LISTINO_BASE_v1: calcolo umBase + contenutoConf + prezzoUltimoBase
      const { umBase, contenutoConf } = estraiContenutoConfDaNome(r.prodotto, r.um);
      const prezzoUltimoBase = (hasPrezzo && contenutoConf > 0)
        ? Math.round((prezzoUnit / contenutoConf) * 10000) / 10000
        : null;
      // Normalizzazione robusta: trim + upper + collasso spazi multipli
      const normProd = (r.prodotto||"").trim().toUpperCase().replace(/\s+/g,' ');
      // Cerco prima in cache locale (race fix), poi in listinoMat (state React)
      let esistente = null;
      if (vistiInQuestaChiamata.has(normProd)) {
        const idCached = vistiInQuestaChiamata.get(normProd);
        esistente = listinoMat.find(l => l._id === idCached) || { _id: idCached, storico: [], um: r.um || 'cad' };
      } else {
        esistente = listinoMat.find(l => (l.prodotto||"").trim().toUpperCase().replace(/\s+/g,' ') === normProd);
      }
      try {
        if (esistente) {
          // Esistente: aggiorno solo se ho un prezzo nuovo (sennò non tocco)
          if (!hasPrezzo) continue;
          const nuovaVoce = {
            fornitore: r.fornitore||"",
            data: dataDoc,
            prezzoUnit,
            prezzoTotale,
            qta,
            um: r.um||"cad",
          };
          const storico = esistente.storico||[];
          storico.unshift(nuovaVoce);
          // FIX_CODICI_FORNITORE_v1: aggiungo codice fornitore se presente e non gia' in array
          const nuovoCod = (r.codice || r.codiceArticolo || "").toString().trim();
          let codiciAggiornati = Array.isArray(esistente.codiciFornitore) ? [...esistente.codiciFornitore] : [];
          if (nuovoCod && !codiciAggiornati.some(c => c.toUpperCase() === nuovoCod.toUpperCase())) {
            codiciAggiornati.push(nuovoCod);
          }
          await updateDoc(doc(db,"listino_materiali",esistente._id), {
            prezzoUltimo: prezzoUnit,
            um: r.um||esistente.um||"cad",
            ultimaData: dataDoc,
            ...(r.categoriaP ? { categoriaP: r.categoriaP } : {}),
            storico: storico.slice(0,20),
            // FIX_AGGIORNA_LISTINO_BASE_v1
            ...(umBase ? { umBase, contenutoConf } : {}),
            ...(prezzoUltimoBase !== null ? { prezzoUltimoBase } : {}),
            // FIX_CODICI_FORNITORE_v1
            ...(codiciAggiornati.length > 0 ? { codiciFornitore: codiciAggiornati } : {}),
            aggiornato: new Date().toISOString(),
          });
          // FIX_RACE_LISTINO_v1: memorizza id per match successivi nella stessa chiamata
          vistiInQuestaChiamata.set(normProd, esistente._id);
        } else {
          // Nuovo: lo creo sempre. Se senza prezzo (bolla pura) lascio prezzoUltimo=null.
          const nuovaVoce = hasPrezzo ? {
            fornitore: r.fornitore||"",
            data: dataDoc,
            prezzoUnit,
            prezzoTotale,
            qta,
            um: r.um||"cad",
          } : null;
          // FIX_CODICI_FORNITORE_v1: salvo codice fornitore se presente
          const codNuovo = (r.codice || r.codiceArticolo || "").toString().trim();
          const refNuovo = await addDoc(collection(db,"listino_materiali"), {
            prodotto: r.prodotto.trim(),
            um: r.um||"cad",
            categoriaP: r.categoriaP||"",
            prezzoUltimo: hasPrezzo ? prezzoUnit : null,
            ultimaData: hasPrezzo ? dataDoc : null,
            ultimoFornitore: r.fornitore||"",
            storico: nuovaVoce ? [nuovaVoce] : [],
            // FIX_AGGIORNA_LISTINO_BASE_v1
            ...(umBase ? { umBase, contenutoConf } : {}),
            ...(prezzoUltimoBase !== null ? { prezzoUltimoBase } : {}),
            // FIX_CODICI_FORNITORE_v1
            ...(codNuovo ? { codiciFornitore: [codNuovo] } : {}),
            createdAt: new Date().toISOString(),
            aggiornato: new Date().toISOString(),
          });
          // FIX_RACE_LISTINO_v1: memorizza id per match successivi nella stessa chiamata
          if (refNuovo && refNuovo.id) vistiInQuestaChiamata.set(normProd, refNuovo.id);
        }
      } catch(e) { console.error("Listino:", e.message); }
    }
  };

  // FIX_LISTINO_MATCH_V2: normalizzazione nome + match cascading
  // - Match 1: per codice fornitore (codiciFornitore[] sul listino)
  // - Match 2: per nome normalizzato (ignora punti, spazi, trattini, virgole tra cifre)
  const normalizzaNomeProd = (s) => {
    return (s||"")
      .toUpperCase()
      .replace(/[.,\-]/g, ' ')      // punti/virgole/trattini → spazi
      .replace(/\s+/g, ' ')          // spazi multipli → 1
      .trim();
  };
  const normalizzaCodice = (s) => (s||"").toUpperCase().replace(/[\s\-.]/g, '').trim();

  const cercaProdottoListino = (rigaBolla) => {
    if (!rigaBolla) return null;
    const cod = normalizzaCodice(rigaBolla.codice);
    const nomeNorm = normalizzaNomeProd(rigaBolla.prodotto);
    // Match 1: per codice fornitore
    if (cod) {
      const byCod = (listinoMat||[]).find(l => {
        const codes = Array.isArray(l.codiciFornitore) ? l.codiciFornitore : [];
        return codes.some(c => normalizzaCodice(c) === cod);
      });
      if (byCod) return byCod;
    }
    // Match 2: per nome normalizzato esatto
    if (nomeNorm) {
      const byNome = (listinoMat||[]).find(l => normalizzaNomeProd(l.prodotto) === nomeNorm);
      if (byNome) return byNome;
    }
    // FIX_LISTINO_MATCH_V4: Match 3 starts-with (per nomi troncati o con suffissi)
    // Es: bolla "DECIWOOL M 45X600X(2X07800)" matcha listino "DECIWOOL M 45X600X(2X07800) - RIF CANTIERE DALILA"
    // Solo se la stringa piu' corta e' >= 10 char (evita match troppo deboli)
    if (nomeNorm && nomeNorm.length >= 10) {
      const candidati = (listinoMat||[]).filter(l => {
        const ln = normalizzaNomeProd(l.prodotto);
        if (!ln || ln.length < 10) return false;
        // Match in entrambe le direzioni: bolla startsWith listino OR listino startsWith bolla
        return ln.startsWith(nomeNorm) || nomeNorm.startsWith(ln);
      });
      if (candidati.length > 0) {
        // In caso di piu' match, prendo quello con nome piu' corto (piu' specifico per troncamenti)
        candidati.sort((a, b) => (a.prodotto||"").length - (b.prodotto||"").length);
        return candidati[0];
      }
    }
    return null;
  };

  const applicaPrezziDaListino = (righe) => {
    return (righe||[]).map(r => {
      const prezzoNum = parseFloat(r.prezzo);
      const haPrezzo = !isNaN(prezzoNum) && prezzoNum > 0;
      if (haPrezzo) return r;
      if (!r.prodotto) return r;
      const trovato = cercaProdottoListino(r);
      if (!trovato || !trovato.prezzoUltimo) return r;
      // FIX_LISTINO_MATCH_V3: scegli il prezzo giusto in base all'UM della bolla
      const umBolla = (r.um||'').toLowerCase().trim();
      const umBase = (trovato.umBase||'').toLowerCase().trim();
      let prezzoApplicato;
      let umFinale;
      if (umBolla && umBase && umBolla === umBase && trovato.prezzoUltimoBase > 0) {
        // Bolla in UM base (kg/lt/mq/ml/pz) → usa €/UM base
        prezzoApplicato = trovato.prezzoUltimoBase;
        umFinale = umBolla;
      } else {
        // Bolla in confezione (cad/conf/sacco/nr...) → usa €/conf
        prezzoApplicato = trovato.prezzoUltimo;
        umFinale = r.um || trovato.um || 'cad';
      }
      return {
        ...r,
        prezzo: prezzoApplicato,
        um: umFinale,
        categoriaP: r.categoriaP || trovato.categoriaP || '',
        totaleRiga: (parseFloat(r.qta)||0) * prezzoApplicato,
      };
    });
  };

  // ── BOLLE FUNCTIONS ──────────────────────────────────────────────────────────
  const aggiungiRiga = () => setRigheForm([...righeForm, { materialeDiz:"", prodotto:"", qta:"", um:"cad", prezzo:"", cantiere:"", lavorazione:"", categoriaP:"", note:"" }]);
  const rimuoviRiga = (i) => setRigheForm(righeForm.filter((_,idx)=>idx!==i));
  const aggiornaRiga = (i, campo, val) => setRigheForm(righeForm.map((r,idx)=>idx===i?{...r,[campo]:val}:r));

  const [modificaBollaId, setModificaBollaId] = useState(null);
  const [righeDettaglio, setRigheDettaglio] = useState({}); // { bollaId: [righe locali] }

  const getRigheDettaglio = (b) => righeDettaglio[b._id] || b.righe || [];

  const apricBollaAperta = (b, isAperta) => {
    if (isAperta) {
      setBollaAperta(null);
    } else {
      setBollaAperta(b._id);
      // Sempre inizializza con dati freschi da Firebase
      setRigheDettaglio(prev => ({
        ...prev,
        [b._id]: (b.righe||[]).map(r => ({...r}))
      }));
    }
  };

  const aggiornaRigaDettaglioLocale = (bollaId, rigaIdx, campo, val) => {
    setRigheDettaglio(prev => {
      const righeBase = prev[bollaId] || bolle.find(b=>b._id===bollaId)?.righe || [];
      const nuove = righeBase.map((r,i) => {
        if (i !== rigaIdx) return r;
        const updated = { ...r, [campo]: campo==="prezzo"||campo==="qta" ? val : val };
        updated.totaleRiga = (parseFloat(campo==="prezzo"?val:updated.prezzo)||0) * (parseFloat(campo==="qta"?val:updated.qta)||1);
        return updated;
      });
      return { ...prev, [bollaId]: nuove };
    });
  };

  const salvaRigheDettaglio = async (bollaId) => {
    const righe = righeDettaglio[bollaId];
    if (!righe) return;
    const righePulite = righe.map(r => ({ ...r, qta:parseFloat(r.qta)||0, prezzo:parseFloat(r.prezzo)||0, totaleRiga:(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1) }));
    const totale = calcolaTotaleRighe(righePulite);
    try {
      await updateDoc(doc(db,"bolle",bollaId), { righe:righePulite, totale });
      setRigheDettaglio(prev => { const n={...prev}; delete n[bollaId]; return n; });
    } catch(e) { alert("❌ "+e.message); }
  };

  const haModificheDettaglio = (bollaId) => !!righeDettaglio[bollaId];

  const calcolaTotaleRighe = (righe) => righe.reduce((s,r) => s + (parseFloat(r.prezzo)||0) * (parseFloat(r.qta)||1), 0);

  const salvaBolla = async () => {
    if (!bollaForm.num) { alert("Inserisci il numero bolla."); return; }
    if (righeForm.every(r => !r.prodotto)) { alert("Inserisci almeno un materiale."); return; }
    // FIX_NO_DUP_BOLLE_v1: blocco se esiste gia' una bolla con stesso num+fornitore+data (solo per nuove, non per modifiche)
    if (!modificaBollaId) {
      const numN = (bollaForm.num||"").trim().toLowerCase();
      const fornN = (bollaForm.fornitore||"").trim().toLowerCase();
      const dataN = (bollaForm.data||"").trim();
      const duplicato = (bolle||[]).find(b =>
        (b.num||"").trim().toLowerCase() === numN &&
        (b.fornitore||"").trim().toLowerCase() === fornN &&
        (b.data||"").trim() === dataN
      );
      if (duplicato) {
        alert("⚠️ Bolla gia' presente!\n\nN. " + bollaForm.num + " del " + bollaForm.data + " — fornitore: " + (bollaForm.fornitore||"(vuoto)") + "\n\nModifica i campi oppure apri la bolla esistente dall'elenco.");
        return;
      }
    }
    setSalvandoBolla(true);
    const idModifica = modificaBollaId; // cattura prima di qualsiasi reset
    try {
      const righePulite = righeForm.filter(r => r.prodotto).map(r => ({
        ...r,
        qta: parseFloat(r.qta)||0,
        prezzo: parseFloat(r.prezzo)||0,
        totaleRiga: (parseFloat(r.prezzo)||0) * (parseFloat(r.qta)||0),
      }));
      const totale = calcolaTotaleRighe(righePulite);
      if (idModifica) {
        // Modifica bolla esistente
        await updateDoc(doc(db,"bolle",idModifica), {
          num: bollaForm.num,
          fornitore: bollaForm.fornitore,
          data: bollaForm.data,
          note: bollaForm.note,
          righe: righePulite,
          totale,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // FIX_PREZZI_DA_LISTINO_v1: bolla LEGGE prezzi dal listino prima di salvare (NON aggiorna listino)
        const righeConPrezzi = applicaPrezziDaListino(righePulite);
        const totaleConPrezzi = calcolaTotaleRighe(righeConPrezzi);
        // Nuova bolla
        await addDoc(collection(db,"bolle"), {
          num: bollaForm.num,
          fornitore: bollaForm.fornitore,
          data: bollaForm.data,
          note: bollaForm.note,
          righe: righeConPrezzi,
          totale: totaleConPrezzi,
          createdAt: new Date().toISOString(),
        });
        // ROLLBACK_FIX_LISTINO_BOLLE_v2: NON chiamo piu' aggiornaListinoMateriali da bolla.
        // Il listino e' alimentato SOLO da fatture (e da scarico magazzino).
      }
      setBollaForm({ num:"", fornitore:"", data:new Date().toISOString().slice(0,10), note:"", _catSel:"", _subSel:"", _cantiereSel:"" });
      setRigheForm([{ materialeDiz:"", prodotto:"", qta:"", um:"cad", prezzo:"", cantiere:"", lavorazione:"", categoriaP:"", note:"" }]);
      setModificaBollaId(null);
      setShowBollaForm(false);
    } catch(e) { alert("❌ "+e.message); }
    setSalvandoBolla(false);
  };

  const apriModificaBolla = (b) => {
    setBollaForm({ num:b.num||"", fornitore:b.fornitore||"", data:b.data||new Date().toISOString().slice(0,10), note:b.note||"" });
    setRigheForm((b.righe||[]).map(r => ({
      categoria:   r.categoria||"altro",
      prodotto:    r.prodotto||"",
      qta:         String(r.qta||""),
      um:          r.um||"cad",
      prezzo:      String(r.prezzo||""),
      categoriaP:  r.categoriaP||"",
      cantiere:    r.cantiere||"",
      lavorazione: r.lavorazione||"",
      note:        r.note||"",
    })));
    setModificaBollaId(b._id);
    setBollaAperta(null);
    setShowBollaForm(true);
    setTimeout(() => {
      document.getElementById("bolla-form-top")?.scrollIntoView({ behavior:"smooth", block:"start" });
    }, 80);
  };

  const eliminaBolla = async (id) => {
    const bolla = (bolleDB||bolle||[]).find(b => b._id === id);
    const nRighe = (bolla?.righe||[]).length;
    const cantieri = [...new Set((bolla?.righe||[]).map(r=>r.cantiere).filter(Boolean))];
    const isDaScaricoMag = bolla?.daScaricoMagazzino;
    const msg = [
      `⚠️ Eliminare questa bolla?`,
      ``,
      `• ${nRighe} riga${nRighe!==1?"e":""} di materiali verranno rimosse`,
      cantieri.length ? `• Collegamento rimosso dal cantiere: ${cantieri.join(", ")}` : null,
      isDaScaricoMag ? `• Era uno scarico da magazzino — lo stock tornerà disponibile` : null,
      ``,
      `✅ I prezzi nel listino rimangono invariati.`,
      ``,
      `Continuare?`
    ].filter(r=>r!==null).join("\n");
    if (!window.confirm(msg)) return;
    try {
      await deleteDoc(doc(db,"bolle",id));
      setRigheDettaglio(prev => { const n={...prev}; delete n[id]; return n; });
      if (bollaAperta === id) setBollaAperta(null);
    } catch(e) { alert("❌ "+e.message); }
  };

  const aggiornaRigaDettaglio = async (bollaId, righe, rigaIdx, campo, val) => {
    const nuoveRighe = righe.map((r,i) => {
      if (i !== rigaIdx) return r;
      const updated = {...r, [campo]: campo==="prezzo"||campo==="qta" ? parseFloat(val)||0 : val };
      updated.totaleRiga = (parseFloat(updated.prezzo)||0) * (parseFloat(updated.qta)||1);
      return updated;
    });
    const totale = calcolaTotaleRighe(nuoveRighe);
    try { await updateDoc(doc(db,"bolle",bollaId), { righe:nuoveRighe, totale }); } catch(e) { alert("❌ "+e.message); }
  };

  const bolleFiltrate = bolle.filter(b =>
    !searchBolle ||
    (b.num||"").toLowerCase().includes(searchBolle.toLowerCase()) ||
    (b.fornitore||"").toLowerCase().includes(searchBolle.toLowerCase()) ||
    (b.righe||[]).some(r => (r.prodotto||"").toLowerCase().includes(searchBolle.toLowerCase()))
  );

  const inputSt2 = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%" };


  const aggiungi = async () => {
    if (!form.materiale) return;
    try {
      await addDoc(collection(db, "materiali"), { ...form, prezzo:parseFloat(form.prezzo)||0, createdAt:new Date().toISOString() });
      setForm({ materiale:"", fornitore:"", cantiere:"", qta:"", um:"mq", urgenza:"media", stato:"da_ordinare", note:"", prezzo:"" });
      setShowForm(false);
    } catch(e) { alert("Errore: "+e.message); }
  };

  const aggiornaStato = async (id, nuovoStato) => {
    try { await updateDoc(doc(db, "materiali", id), { stato:nuovoStato }); } catch(e) {}
  };

  const elimina = async (id) => {
    if (window.confirm("Eliminare questo materiale dall'elenco? L'operazione è irreversibile.")) await deleteDoc(doc(db, "materiali", id));
  };

  const URG = { alta:{label:"Alta",color:"#f06060",bg:"#f0606022"}, media:{label:"Media",color:"#f5a623",bg:"#f5a62322"}, bassa:{label:"Bassa",color:"#3ecf8e",bg:"#3ecf8e22"} };
  const STATI = { da_ordinare:{label:"Da ordinare",color:"#f5a623"}, ordinato:{label:"Ordinato",color:"#4e9af5"}, consegnato:{label:"Consegnato",color:"#3ecf8e"} };

  const matFiltrati = materiali
    .filter(m => filtroStato==="tutti" || m.stato===filtroStato)
    .filter(m => filtroUrgenza==="tutti" || m.urgenza===filtroUrgenza)
    .filter(m => !search || (m.materiale||"").toLowerCase().includes(search.toLowerCase()) || (m.cantiere||"").toLowerCase().includes(search.toLowerCase()));

  const kpiDaOrdinare = materiali.filter(m => m.stato==="da_ordinare").length;
  const kpiOrdinati   = materiali.filter(m => m.stato==="ordinato").length;
  const kpiAlta       = materiali.filter(m => m.urgenza==="alta" && m.stato!=="consegnato").length;
  const kpiValore     = materiali.filter(m => m.stato!=="consegnato").reduce((s,m) => s+(parseFloat(m.prezzo)||0), 0);

  // raggruppamento per fornitore
  const perFornitore = {};
  materiali.forEach(m => {
    const f = m.fornitore || "Senza fornitore";
    if (!perFornitore[f]) perFornitore[f] = [];
    perFornitore[f].push(m);
  });

  return (
    <div className="content">
      {/* KPI */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"Da ordinare", val:kpiDaOrdinare, icon:"🛒", color:theme.accent },
          { label:"Già ordinati", val:kpiOrdinati, icon:"📦", color:"#4e9af5" },
          { label:"Urgenza alta", val:kpiAlta, icon:"🔴", color:"#f06060" },
          { label:"Valore aperto", val:"€ "+Math.round(kpiValore).toLocaleString("it-IT"), icon:"💰", color:"#3ecf8e" },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:"16px 18px" }}>
            <div style={{ fontSize:20, marginBottom:4 }}>{k.icon}</div>
            <div style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{k.label}</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:k.color }}>{k.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[["bolle","🧾 Bolle"],["magazzino","📦 Magazzino"],["listino","📋 Listino prezzi"],["lista","📦 Da ordinare"],["fornitori","🏭 Per fornitore"]].map(([id,label]) => (
          <div key={id} className={`tab ${tab===id?"active":""}`} onClick={() => setTab(id)}>{label}</div>
        ))}
      </div>

      {/* ── TAB MAGAZZINO ── */}
      {tab === "magazzino" && (() => {
        // FIX_MAGAZZINO_LEGGE_LISTINO_v1
        // Helper: normalizza nome prodotto (rimuove apostrofi, punteggiatura, spazi multipli)
        const normProd = (s) => (s||"")
          .toUpperCase()
          .replace(/['"\u2019\u2018]/g, '')
          .replace(/[.,;:!?\/]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        // Helper: toglie prefisso codice articolo (es. "AMD485/13-20LT")
        // Funziona DOPO normProd, quindi non cerca "/" o "-" (gia' rimossi).
        // Riconosce parole iniziali che contengono SIA cifre SIA lettere = codice articolo.
        const togliCodice = (s) => {
          const parole = (s||"").split(' ');
          if (parole.length >= 2) {
            const prima = parole[0];
            if (/\d/.test(prima) && /[A-Z]/i.test(prima)) {
              let toglie = 1;
              while (toglie < parole.length - 1 && /\d/.test(parole[toglie]) && /[A-Z]/i.test(parole[toglie])) {
                toglie++;
              }
              if (parole.length - toglie >= 2) return parole.slice(toglie).join(' ');
            }
          }
          return s;
        };
        // Helper: cerca prezzo nel listino per nome normalizzato (SOLO 2 livelli, conservativo)
        // Niente substring per evitare di confondere prodotti simili (KG.5 vs KG.10)
        const lookupPrezzoListino = (nomeProd) => {
          if (!nomeProd) return { prezzo: 0, um: null };
          const nNorm = normProd(nomeProd);
          if (!nNorm) return { prezzo: 0, um: null };
          const nSenzaCod = togliCodice(nNorm);
          // Match 1: esatto normalizzato
          let match = listinoMat.find(l => normProd(l.prodotto) === nNorm);
          // Match 2: ENTRAMBI senza prefisso codice articolo (gestisce solo varianti OCR del prefisso codice)
          if (!match && nSenzaCod && nSenzaCod !== nNorm) {
            match = listinoMat.find(l => {
              const lSenzaCod = togliCodice(normProd(l.prodotto));
              return lSenzaCod === nSenzaCod && lSenzaCod.length > 8;
            });
          }
          if (match && match.prezzoUltimo > 0) return { prezzo: parseFloat(match.prezzoUltimo), um: match.um || null };
          return { prezzo: 0, um: null };
        };
        // Calcola stock per prodotto: entrate (bolle magazzino) - uscite (scarichi)
        const stock = {};
        // Entrate: righe bolle senza cantiere (= magazzino)
        bolle.forEach(b => {
          (b.righe||[]).filter(r => !r.cantiere).forEach(r => {
            if (!r.prodotto) return;
            const k = (r.prodotto||"").trim().toUpperCase();
            const prezzoRiga = parseFloat(r.prezzo)||0;
            // Se prezzo bolla = 0, fallback al listino
            const fallback = prezzoRiga > 0 ? null : lookupPrezzoListino(r.prodotto);
            const prezzoEffettivo = prezzoRiga > 0 ? prezzoRiga : (fallback ? fallback.prezzo : 0);
            const umEffettiva = (fallback && fallback.um) ? fallback.um : (r.um||"cad");
            if (!stock[k]) stock[k] = { prodotto:r.prodotto.trim(), um:umEffettiva, qtaIn:0, qtaOut:0, prezzoUltimo:prezzoEffettivo, categoriaP:r.categoriaP||"" };
            stock[k].qtaIn += parseFloat(r.qta)||0;
            if (prezzoEffettivo > 0) stock[k].prezzoUltimo = prezzoEffettivo;
          });
        });
        // Uscite: scarichi
        scarichi.forEach(s => {
          const k = (s.prodotto||"").trim().toUpperCase();
          if (!stock[k]) {
            // Fallback listino anche qui per prodotti che hanno solo uscite (caso raro)
            const fb = lookupPrezzoListino(s.prodotto);
            stock[k] = { prodotto:s.prodotto.trim(), um:(fb.um||s.um||"cad"), qtaIn:0, qtaOut:0, prezzoUltimo:fb.prezzo, categoriaP:"" };
          } else if (stock[k].prezzoUltimo === 0) {
            // Stock esiste ma senza prezzo (bolla vuota) -> riprovo listino
            const fb = lookupPrezzoListino(s.prodotto);
            if (fb.prezzo > 0) stock[k].prezzoUltimo = fb.prezzo;
          }
          stock[k].qtaOut += parseFloat(s.qta)||0;
        });
        const prodottiMagazzino = Object.values(stock).map(s => ({ ...s, disponibile: s.qtaIn - s.qtaOut }));
        const fmt2 = n => parseFloat(n||0).toFixed(2);

        return (
          <div>
            {/* KPI magazzino */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
              {[
                ["Prodotti in stock", prodottiMagazzino.filter(p=>p.disponibile>0).length, theme.text],
                ["Sotto zero", prodottiMagazzino.filter(p=>p.disponibile<0).length, "#f06060"],
                ["Valore stock", "€ "+prodottiMagazzino.reduce((s,p)=>s+(p.disponibile>0?p.disponibile*p.prezzoUltimo:0),0).toLocaleString("it-IT",{minimumFractionDigits:2}), "#3ecf8e"],
              ].map(([label,val,color])=>(
                <div key={label} className="card" style={{ padding:"16px 18px", textAlign:"center" }}>
                  <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:6 }}>{label}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Bottone scarico */}
            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
              <button className="btn btn-primary" onClick={()=>setShowScaricoForm(!showScaricoForm)}>
                {showScaricoForm ? "✕ Chiudi" : "➖ Scarica a cantiere"}
              </button>
            </div>

            {/* Form scarico */}
            {showScaricoForm && (
              <div className="card" style={{ marginBottom:20, border:`1px solid #f5a62355` }}>
                <div className="card-title" style={{ marginBottom:16 }}>➖ Scarico magazzino → Cantiere</div>
                {/* Dati comuni: cantiere, data, lavorazione, note */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12, marginBottom:12 }}>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Cantiere *</div>
                    <select style={inputSt2} value={scaricoForm.cantiere} onChange={e=>setScaricoForm({...scaricoForm,cantiere:e.target.value})}>
                      <option value="">— Seleziona cantiere —</option>
                      {cantieriDB.filter(c=>!c.completato).map(c=><option key={c._id||c.id} value={c.nome}>{c.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Data</div>
                    <input style={inputSt2} type="date" value={scaricoForm.data} onChange={e=>setScaricoForm({...scaricoForm,data:e.target.value})} />
                  </div>
                  <div>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Lavorazione (opzionale)</div>
                    <select style={inputSt2} value={scaricoForm.lavorazione||""} onChange={e=>setScaricoForm({...scaricoForm,lavorazione:e.target.value})}>
                      <option value="">— Non assegnare —</option>
                      {scaricoForm.cantiere && (() => {
                        const cant = cantieriDB.find(c=>c.nome===scaricoForm.cantiere);
                        return (cant?.lavorazioni||[]).map((l,i)=>(
                          <option key={i} value={l.desc}>{l.descBreve||l.desc}</option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4 }}>Note</div>
                  <input style={inputSt2} placeholder="es. Portata sul cantiere da Ion" value={scaricoForm.note} onChange={e=>setScaricoForm({...scaricoForm,note:e.target.value})} />
                </div>

                {/* Righe prodotti */}
                <div style={{ marginBottom:8 }}>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 80px 70px 28px", gap:8, padding:"4px 0 8px", borderBottom:`1px solid ${theme.border}` }}>
                    {["Prodotto","Qta","U.M.",""].map((h,i)=>(
                      <div key={i} style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>{h}</div>
                    ))}
                  </div>
                  {scaricoRighe.map((riga, ri) => (
                    <div key={ri} style={{ display:"grid", gridTemplateColumns:"1fr 80px 70px 28px", gap:8, alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${theme.border}33` }}>
                      {/* Prodotto con autocomplete */}
                      <div style={{ position:"relative" }}>
                        <input style={{...inputSt2, padding:"6px 10px", fontSize:12}}
                          placeholder="es. Malta pronta..."
                          value={riga.prodotto}
                          onChange={e=>{
                            const nr=[...scaricoRighe]; nr[ri]={...nr[ri],prodotto:e.target.value}; setScaricoRighe(nr);
                          }}
                        />
                        {riga.prodotto.length > 1 && (() => {
                          const matches = prodottiMagazzino.filter(p=>p.disponibile>0&&p.prodotto.toLowerCase().includes(riga.prodotto.toLowerCase()));
                          if (!matches.length) return null;
                          return (
                            <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:8, zIndex:100, maxHeight:140, overflowY:"auto", boxShadow:"0 8px 24px #00000066" }}>
                              {matches.map(p=>(
                                <div key={p.prodotto} onMouseDown={()=>{
                                  const nr=[...scaricoRighe];
                                  nr[ri]={...nr[ri],prodotto:p.prodotto,um:p.um,categoriaP:p.categoriaP||nr[ri].categoriaP||""};
                                  setScaricoRighe(nr);
                                }}
                                  style={{ padding:"6px 12px", cursor:"pointer", fontSize:12, borderBottom:`1px solid ${theme.border}33`, display:"flex", justifyContent:"space-between" }}
                                  onMouseEnter={e=>e.currentTarget.style.background=theme.surfaceHover}
                                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                  <span>{p.prodotto}</span>
                                  <span style={{ color:p.disponibile>0?theme.success:"#f06060", fontWeight:700, fontSize:11 }}>{p.disponibile.toFixed(1)} {p.um}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      {/* Qta */}
                      <input style={{...inputSt2, padding:"6px 8px", fontSize:12}} type="number" placeholder="0"
                        value={riga.qta} onChange={e=>{const nr=[...scaricoRighe];nr[ri]={...nr[ri],qta:e.target.value};setScaricoRighe(nr);}} />
                      {/* UM */}
                      <select style={{...inputSt2, padding:"6px 6px", fontSize:12}} value={riga.um}
                        onChange={e=>{const nr=[...scaricoRighe];nr[ri]={...nr[ri],um:e.target.value};setScaricoRighe(nr);}}>
                        {["kg","lt","cad","mq","ml","mc","sacco","rotolo"].map(u=><option key={u}>{u}</option>)}
                      </select>

                      {/* Rimuovi riga */}
                      {scaricoRighe.length > 1 && (
                        <button onClick={()=>setScaricoRighe(scaricoRighe.filter((_,i)=>i!==ri))}
                          style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:16}}>✕</button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Totale stimato */}
                {(() => {
                  const tot = scaricoRighe.reduce((s,riga)=>{
                    const prod = prodottiMagazzino.find(p=>p.prodotto.toLowerCase()===(riga.prodotto||"").toLowerCase());
                    return s + (parseFloat(riga.qta)||0) * (prod?.prezzoUltimo||0);
                  },0);
                  if (!tot) return null;
                  return (
                    <div style={{ marginBottom:12, padding:"8px 14px", background:"#0a1f14", borderRadius:8, border:"1px solid #3ecf8e33", fontSize:13, color:"#3ecf8e" }}>
                      💰 Totale stimato: <strong>€ {tot.toLocaleString("it-IT",{minimumFractionDigits:2})}</strong>
                    </div>
                  );
                })()}

                <div style={{ display:"flex", gap:8 }}>
                  <button className="btn btn-ghost btn-sm" style={{color:theme.accent,borderColor:theme.accent+"44"}}
                    onClick={()=>setScaricoRighe([...scaricoRighe,{...emptyScaricoRiga}])}>
                    + Aggiungi prodotto
                  </button>
                  <button className="btn btn-primary" onClick={async()=>{
                    if(!scaricoForm.cantiere){alert("Seleziona il cantiere.");return;}
                    const righePulite = scaricoRighe.filter(r=>r.prodotto&&r.qta);
                    if(!righePulite.length){alert("Inserisci almeno un prodotto con quantità.");return;}
                    const lavorazioneFinale = scaricoForm.lavorazione||"";
                    try {
                      const righeBolle = [];
                      let costoTotBolla = 0;
                      for (const riga of righePulite) {
                        const prod = prodottiMagazzino.find(p=>p.prodotto.toLowerCase()===riga.prodotto.toLowerCase());
                        const prezzoUnitario = prod?.prezzoUltimo||0;
                        const costoRiga = parseFloat(riga.qta) * prezzoUnitario;
                        const catP = riga.categoriaP || prod?.categoriaP || "";
                        costoTotBolla += costoRiga;
                        // Scarico magazzino per ogni riga
                        await addDoc(collection(db,"scarichi_magazzino"), {
                          prodotto: riga.prodotto.trim(),
                          qta: parseFloat(riga.qta),
                          um: riga.um||"cad",
                          cantiere: scaricoForm.cantiere,
                          data: scaricoForm.data,
                          note: scaricoForm.note||"",
                          categoriaP: catP,
                          lavorazione: lavorazioneFinale,
                          prezzoUnitario,
                          costoTotale: costoRiga,
                          createdAt: new Date().toISOString(),
                        });
                        righeBolle.push({
                          prodotto: riga.prodotto.trim(),
                          qta: parseFloat(riga.qta),
                          um: riga.um||"cad",
                          prezzo: prezzoUnitario,
                          totaleRiga: costoRiga,
                          cantiere: scaricoForm.cantiere,
                          categoriaP: catP,
                          lavorazione: lavorazioneFinale,
                          note: scaricoForm.note||"",
                        });
                        if (prezzoUnitario > 0) {
                          aggiornaListinoMateriali([{
                            prodotto: riga.prodotto.trim(),
                            qta: parseFloat(riga.qta),
                            um: riga.um||"cad",
                            prezzo: prezzoUnitario,
                            categoriaP: catP,
                            fornitore: "Magazzino interno",
                          }], scaricoForm.data);
                        }
                      }
                      // Una sola bolla con tutte le righe
                      await addDoc(collection(db,"bolle"), {
                        num: `MAG-${scaricoForm.data.replace(/-/g,"")}`,
                        fornitore: "Magazzino interno",
                        data: scaricoForm.data,
                        note: `Scarico automatico da magazzino${scaricoForm.note ? " — "+scaricoForm.note : ""}`,
                        righe: righeBolle,
                        totale: costoTotBolla,
                        daScaricoMagazzino: true,
                        createdAt: new Date().toISOString(),
                      });
                      setScaricoForm({ cantiere:"", data:new Date().toISOString().slice(0,10), note:"", lavorazione:"" });
                      setScaricoRighe([{...emptyScaricoRiga}]);
                      setShowScaricoForm(false);
                      alert(`✅ Scarico registrato!\n📋 ${righePulite.length} prodotti → cantiere "${scaricoForm.cantiere}"\n💰 Totale: € ${costoTotBolla.toFixed(2)}`);
                    } catch(e){ alert("❌ "+e.message); }
                  }}>✓ Conferma scarico</button>
                  <button className="btn btn-ghost" onClick={()=>{setShowScaricoForm(false);setScaricoRighe([{...emptyScaricoRiga}]);}}>Annulla</button>
                </div>
              </div>
            )}

            {/* Stock prodotti */}
            <div className="card">
              <div className="card-title" style={{ marginBottom:16 }}>📦 Stock magazzino</div>
              {prodottiMagazzino.length === 0 ? (
                <div style={{ textAlign:"center", padding:"32px 0", color:theme.textDim }}>
                  <div style={{ fontSize:36, marginBottom:10 }}>📦</div>
                  <div style={{ fontSize:14, color:theme.textMuted }}>Nessun prodotto in magazzino.</div>
                  <div style={{ fontSize:12, marginTop:6 }}>Le righe delle bolle senza cantiere vengono conteggiate come magazzino.</div>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Prodotto</th>
                        <th>Entrate</th>
                        <th>Uscite</th>
                        <th>Disponibile</th>
                        <th>Prezzo/um</th>
                        <th>Valore stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prodottiMagazzino.sort((a,b)=>a.prodotto.localeCompare(b.prodotto)).map(p=>(
                        <tr key={p.prodotto}>
                          <td style={{ fontWeight:600 }}>{p.prodotto}</td>
                          <td style={{ color:theme.success }}>{fmt2(p.qtaIn)} {p.um}</td>
                          <td style={{ color:"#f06060" }}>{fmt2(p.qtaOut)} {p.um}</td>
                          <td>
                            <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:15, color:p.disponibile>0?theme.success:p.disponibile===0?theme.textMuted:"#f06060" }}>
                              {fmt2(p.disponibile)} {p.um}
                            </span>
                          </td>
                          <td style={{ color:theme.textMuted }}>€ {fmt2(p.prezzoUltimo)}</td>
                          <td style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.accent }}>
                            {p.disponibile>0 ? `€ ${(p.disponibile*p.prezzoUltimo).toLocaleString("it-IT",{minimumFractionDigits:2})}` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Storico scarichi */}
            {scarichi.length > 0 && (
              <div className="card" style={{ marginTop:20 }}>
                <div className="card-title" style={{ marginBottom:16 }}>📋 Storico scarichi</div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Data</th><th>Prodotto</th><th>Quantità</th><th>Cantiere</th><th>Costo</th><th>Note</th><th></th></tr></thead>
                    <tbody>
                      {scarichi.map(s=>(
                        <tr key={s._id}>
                          <td style={{ fontSize:12, color:theme.textMuted }}>{s.data}</td>
                          <td style={{ fontWeight:600 }}>{s.prodotto}</td>
                          <td>{s.qta} {s.um}</td>
                          <td style={{ color:theme.accent }}>{s.cantiere}</td>
                          <td style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#f06060" }}>
                            {s.costoTotale>0?`€ ${s.costoTotale.toLocaleString("it-IT",{minimumFractionDigits:2})}`:"—"}
                          </td>
                          <td style={{ fontSize:12, color:theme.textDim }}>{s.note||"—"}</td>
                          <td>
                            <button onClick={async()=>{ if(!window.confirm("Eliminare questo scarico?"))return; await deleteDoc(doc(db,"scarichi_magazzino",s._id)); }}
                              style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:16 }}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── TAB LISTINO PREZZI ── */}
      {tab === "listino" && (
        <div>
          {/* Toolbar */}
          <div style={{ display:"flex", gap:10, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
            <input style={{ ...inputSt, width:260 }} placeholder="🔍 Cerca prodotto o categoria..." value={searchListinoMat} onChange={e=>setSearchListinoMat(e.target.value)} />
            <div className="tabs" style={{ marginBottom:0, borderBottom:"none" }}>
              {[["prodotti","📦 Per prodotto"],["confronto","🏭 Confronto fornitori"]].map(([id,label])=>(
                <div key={id} className={`tab ${(listinoView||"prodotti")===id?"active":""}`}
                  onClick={()=>setListinoView(id)} style={{ padding:"6px 14px", fontSize:12 }}>{label}</div>
              ))}
            </div>
            <div style={{ marginLeft:"auto", fontSize:12, color:theme.textMuted }}>
              {listinoMat.length} prodotti
            </div>
          </div>

          {loadingListino ? (
            <div style={{ color:theme.textMuted, padding:20 }}>Caricamento...</div>
          ) : listinoMat.length === 0 ? (
            <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
              <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
              <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Listino vuoto</div>
              <div style={{ fontSize:13, marginTop:6 }}>Il listino si popola automaticamente quando inserisci i prezzi nelle bolle.</div>
            </div>
          ) : (listinoView||"prodotti") === "prodotti" ? (
            /* ── VISTA PER PRODOTTO ── */
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Prodotto</th>
                    <th>Categoria</th>
                    <th>U.M.</th>
                    <th style={{textAlign:"right"}}>Prezzo unitario</th>
                    {/* FIX_LISTINO_UI_BASE_v1 */}
                    <th style={{textAlign:"right"}}>€ / UM base</th>
                    <th>Ultimo fornitore</th>
                    <th>Ultimo acquisto</th>
                    <th>Min / Max</th>
                    <th style={{textAlign:"right"}}>Acquisti</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {listinoMat
                    .filter(l => !searchListinoMat || (l.prodotto||"").toLowerCase().includes(searchListinoMat.toLowerCase()) || (l.categoriaP||"").toLowerCase().includes(searchListinoMat.toLowerCase()))
                    .sort((a,b)=>(a.prodotto||"").localeCompare(b.prodotto||""))
                    .map(l => {
                      const storico = l.storico||[];
                      const prezziStorico = storico.map(s=>parseFloat(s.prezzoUnit||s.prezzo)||0).filter(p=>p>0);
                      const prezzoMin = prezziStorico.length ? Math.min(...prezziStorico) : 0;
                      const prezzoMax = prezziStorico.length ? Math.max(...prezziStorico) : 0;
                      const trend = prezziStorico.length>1 ? (prezziStorico[0]>prezziStorico[1]?"↑":"↓") : "—";
                      const trendColor = trend==="↑"?"#f06060":trend==="↓"?"#3ecf8e":theme.textDim;
                      const ultimoFornitore = storico[0]?.fornitore||"—";
                      const isEditing = listinoAperto === l._id;
                      return (
                        <tr key={l._id}>
                          <td style={{ fontWeight:600 }}>{l.prodotto}</td>
                          <td>
                            <select style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:6, color:theme.info, padding:"3px 8px", fontSize:11, outline:"none" }}
                              value={l.categoriaP||""}
                              onChange={async e=>{
                                await updateDoc(doc(db,"listino_materiali",l._id), { categoriaP: e.target.value });
                              }}>
                              <option value="">— famiglia —</option>
                              {famiglieProdottoDB.sort((a,b)=>a.localeCompare(b)).map(f=>(
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ color:theme.textMuted }}>
                            {isEditing ? (
                              <select id={`um-${l._id}`} defaultValue={l.um||"cad"}
                                style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:6, color:theme.text, padding:"3px 6px", fontSize:12, outline:"none" }}>
                                <option value="lt">lt</option>
                                <option value="kg">kg</option>
                                <option value="ml">ml</option>
                                <option value="mq">mq</option>
                                <option value="mc">mc</option>
                                <option value="cad">cad</option>
                                <option value="pz">pz</option>
                                <option value="g">g</option>
                                <option value="conf">conf</option>
                                <option value="sacco">sacco</option>
                              </select>
                            ) : (
                              <>{l.um||"cad"}{l.umVerificata ? <span title="UM verificata manualmente" style={{ marginLeft:4, color:theme.success, fontSize:10 }}>✓</span> : null}</>
                            )}
                          </td>
                          <td style={{ textAlign:"right" }}>
                            {isEditing ? (
                              <div style={{ display:"flex", gap:6, alignItems:"center", justifyContent:"flex-end" }}>
                                <input type="number" step="0.01"
                                  defaultValue={parseFloat(l.prezzoUltimo||0).toFixed(2)}
                                  id={`prezzo-${l._id}`}
                                  style={{ ...inputSt, width:90, padding:"4px 8px", fontSize:13, textAlign:"right" }} />
                                <button className="btn btn-primary btn-sm" style={{padding:"3px 8px"}} onClick={async()=>{
                                  const nuovoPrezzo = parseFloat(document.getElementById(`prezzo-${l._id}`)?.value)||0;
                                  const nuovaUm = document.getElementById(`um-${l._id}`)?.value || l.um || "cad";
                                  if (!nuovoPrezzo) return;
                                  const umCambiata = nuovaUm !== (l.um||"cad");
                                  // FIX_LISTINO_UI_BASE_v1: leggo anche umBase + contenutoConf, calcolo prezzoUltimoBase
                                  const nuovoUmBase = document.getElementById(`umBase-${l._id}`)?.value || l.umBase || "";
                                  const nuovoContConf = parseFloat(document.getElementById(`contConf-${l._id}`)?.value) || l.contenutoConf || 1;
                                  const nuovoPrezzoBase = nuovoContConf > 0 ? Math.round((nuovoPrezzo / nuovoContConf) * 10000) / 10000 : nuovoPrezzo;
                                  await updateDoc(doc(db,"listino_materiali",l._id), {
                                    prezzoUltimo: nuovoPrezzo,
                                    um: nuovaUm,
                                    ...(umCambiata ? { umVerificata: true } : {}),
                                    ...(nuovoUmBase ? { umBase: nuovoUmBase, contenutoConf: nuovoContConf, prezzoUltimoBase: nuovoPrezzoBase } : {}),
                                    aggiornato: new Date().toISOString(),
                                  });
                                  setListinoAperto(null);
                                }}>✓</button>
                                <button style={{background:"none",border:"none",color:theme.textDim,cursor:"pointer"}} onClick={()=>setListinoAperto(null)}>✕</button>
                              </div>
                            ) : (
                              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, color:theme.accent, fontSize:15 }}>
                                € {parseFloat(l.prezzoUltimo||0).toLocaleString("it-IT",{minimumFractionDigits:2})}
                                <span style={{ fontSize:14, marginLeft:6, color:trendColor }}>{trend}</span>
                              </span>
                            )}
                          </td>
                          {/* FIX_LISTINO_UI_BASE_v1: cella €/UM base */}
                          <td style={{ textAlign:"right" }}>
                            {isEditing ? (
                              <div style={{ display:"flex", gap:4, alignItems:"center", justifyContent:"flex-end" }}>
                                <input type="number" step="0.001"
                                  defaultValue={l.contenutoConf||1}
                                  id={`contConf-${l._id}`}
                                  title="Contenuto per confezione (es. 10 kg/sacco)"
                                  style={{ ...inputSt, width:55, padding:"4px 6px", fontSize:12, textAlign:"right" }} />
                                <select id={`umBase-${l._id}`} defaultValue={l.umBase||""}
                                  style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:6, color:theme.text, padding:"4px 6px", fontSize:12, outline:"none" }}>
                                  <option value="">—</option>
                                  <option value="kg">kg</option>
                                  <option value="lt">lt</option>
                                  <option value="mq">mq</option>
                                  <option value="ml">ml</option>
                                  <option value="pz">pz</option>
                                </select>
                              </div>
                            ) : l.umBase && l.prezzoUltimoBase ? (
                              <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:600, color:theme.info, fontSize:13 }}>
                                € {parseFloat(l.prezzoUltimoBase).toLocaleString("it-IT",{minimumFractionDigits:2,maximumFractionDigits:4})}
                                <span style={{ fontSize:11, marginLeft:3, color:theme.textDim }}>/ {l.umBase}</span>
                              </span>
                            ) : (
                              <span style={{ fontSize:10, color:theme.warning, padding:"2px 6px", border:`1px solid ${theme.warning}`, borderRadius:4 }}>
                                da verificare
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize:12, color:theme.textMuted }}>{ultimoFornitore}</td>
                          <td style={{ color:theme.textMuted, fontSize:12 }}>{l.ultimaData||"—"}</td>
                          <td style={{ fontSize:11, color:theme.textDim }}>
                            {prezziStorico.length>1 ? `€ ${prezzoMin.toFixed(2)} · € ${prezzoMax.toFixed(2)}` : "—"}
                          </td>
                          <td style={{ textAlign:"right", color:theme.textMuted }}>{storico.length}</td>
                          <td style={{ whiteSpace:"nowrap" }}>
                            <button onClick={()=>setListinoAperto(isEditing?null:l._id)}
                              style={{background:"none",border:"none",color:theme.textMuted,cursor:"pointer",fontSize:14,marginRight:4}}>✎</button>
                            <button onClick={async()=>{ if(!window.confirm(`Eliminare "${l.prodotto}" dal listino?`)) return; await deleteDoc(doc(db,"listino_materiali",l._id)); }}
                              style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>🗑️</button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ) : (
            /* ── VISTA CONFRONTO FORNITORI ── */
            (() => {
              // Raggruppa per categoria prodotto
              const categorie = {};
              listinoMat
                .filter(l => !searchListinoMat || (l.prodotto||"").toLowerCase().includes(searchListinoMat.toLowerCase()) || (l.categoriaP||"").toLowerCase().includes(searchListinoMat.toLowerCase()))
                .forEach(l => {
                  const cat = l.categoriaP || "Senza categoria";
                  if (!categorie[cat]) categorie[cat] = [];
                  categorie[cat].push(l);
                });
              const catKeys = Object.keys(categorie).sort();
              if (!catKeys.length) return <div style={{ color:theme.textDim, padding:20 }}>Nessun risultato</div>;
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  {catKeys.map(cat => {
                    const prodotti = categorie[cat].sort((a,b)=>parseFloat(a.prezzoUltimo||0)-parseFloat(b.prezzoUltimo||0));
                    const prezzoMin = Math.min(...prodotti.map(p=>parseFloat(p.prezzoUltimo)||0).filter(p=>p>0));
                    return (
                      <div key={cat} className="card" style={{ padding:"14px 16px" }}>
                        {/* Header categoria */}
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>{cat}</div>
                          <div style={{ fontSize:12, color:theme.textMuted }}>{prodotti.length} prodott{prodotti.length===1?"o":"i"}</div>
                        </div>
                        {/* Prodotti */}
                        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                          {prodotti.map(p => {
                            const prezzo = parseFloat(p.prezzoUltimo||0);
                            const isMigliore = prezzo === prezzoMin && prezzo > 0;
                            const ultimoFornitore = (p.storico||[])[0]?.fornitore || "—";
                            const ultimaData = p.ultimaData || "—";
                            // Storico fornitori unici
                            const fornitori = [...new Map((p.storico||[]).map(s=>([s.fornitore, s]))).values()].filter(s=>s.fornitore);
                            return (
                              <div key={p._id} style={{ display:"grid", gridTemplateColumns:"1fr 120px 140px 100px 80px", gap:10, alignItems:"center", padding:"8px 10px", borderRadius:8, background:isMigliore?"#0a1f14":theme.bg, border:`1px solid ${isMigliore?"#3ecf8e44":theme.border}` }}>
                                <div>
                                  <div style={{ fontSize:13, fontWeight:500 }}>{p.prodotto}</div>
                                  <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>
                                    {fornitori.slice(0,2).map(s=>s.fornitore).join(" · ")}
                                    {fornitori.length>2 && ` +${fornitori.length-2}`}
                                  </div>
                                </div>
                                <div style={{ fontSize:11, color:theme.textMuted }}>{ultimoFornitore}</div>
                                <div style={{ fontSize:11, color:theme.textDim }}>{ultimaData}</div>
                                <div style={{ fontSize:11, color:theme.textMuted }}>{p.um||"cad"}</div>
                                <div style={{ textAlign:"right" }}>
                                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:isMigliore?"#3ecf8e":theme.accent }}>
                                    € {prezzo.toLocaleString("it-IT",{minimumFractionDigits:2})}
                                  </div>
                                  {isMigliore && <div style={{ fontSize:9, color:"#3ecf8e", fontWeight:700 }}>MIGLIORE</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* ── TAB BOLLE ── */}
      {tab === "bolle" && (
        <div>
          {/* Toolbar bolle */}
          <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...inputSt, width:260 }} placeholder="🔍 Cerca bolla, fornitore, materiale..." value={searchBolle} onChange={e => setSearchBolle(e.target.value)} />
            <label style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #a78bfa44", background:"#a78bfa11", color:"#a78bfa", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>
              {pdfBollaLoading ? "⏳ Lettura PDF..." : "📄 Importa da PDF"}
              <input type="file" accept=".pdf" style={{ display:"none" }} onChange={e => { importaBollaDaPDF(e.target.files?.[0]); e.target.value=""; }} />
            </label>
            <button type="button" onClick={() => setShowScannerFoto(true)} style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #f5a62344", background:"#f5a62311", color:"#f5a623", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>
              📷 Scannerizza
            </button>
            <label style={{ padding:"6px 14px", borderRadius:8, border:"1px solid #3ecf8e44", background:"#3ecf8e11", color:"#3ecf8e", cursor:"pointer", fontSize:13, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>
              {pdfFatturaLoading ? "⏳ Lettura fattura..." : "🧾 Importa fattura"}
              <input type="file" accept=".pdf" style={{ display:"none" }} onChange={e => { importaFattura(e.target.files?.[0]); e.target.value=""; }} />
            </label>
            <button className="btn btn-primary btn-sm" style={{ marginLeft:"auto" }} onClick={() => { setShowBollaForm(!showBollaForm); setBollaAperta(null); }}>
              {showBollaForm ? "✕ Chiudi" : "+ Nuova bolla"}
            </button>
          </div>

          {/* Modal scanner fotocamera (uniformato a vista operaio: cantiere->lavorazione->foto) */}
          {showScannerFoto && (
            <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, background:theme.bg, zIndex:9999, overflowY:"auto" }}>
              <div style={{ position:"sticky", top:0, background:theme.bg, padding:"12px 20px", borderBottom:`1px solid ${theme.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", zIndex:10000 }}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>📷 Scannerizza bolla</div>
                <button onClick={()=>setShowScannerFoto(false)} style={{ background:"transparent", border:`1px solid ${theme.border}`, color:theme.text, padding:"6px 14px", borderRadius:8, cursor:"pointer", fontSize:13 }}>✕ Chiudi</button>
              </div>
              <ScansionaBolla
                cantieriDB={cantieriDB}
                dizionarioMateriali={dizionarioMateriali}
                nomeOperaio={(utente && utente.nome) || "Titolare"}
                creatoDaRuolo="titolare"
                onFine={() => setShowScannerFoto(false)}
              />
            </div>
          )}

          {/* Form nuova/modifica bolla */}
          {showBollaForm && (
            <div id="bolla-form-top" className="card" style={{ marginBottom:20, border:`1px solid ${modificaBollaId ? "#f5a62388" : theme.accent+"55"}` }}>
              {modificaBollaId && (
                <div style={{ background:"#f5a62318", border:`1px solid #f5a62344`, borderRadius:8, padding:"10px 14px", marginBottom:16, display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18 }}>✏️</span>
                  <div>
                    <div style={{ fontWeight:700, color:theme.accent, fontSize:14 }}>Stai modificando la bolla N° {bollaForm.num||"—"}</div>
                    <div style={{ fontSize:12, color:theme.textMuted }}>Modifica i campi e clicca "Salva modifiche" per confermare</div>
                  </div>
                  <button style={{ marginLeft:"auto", background:"none", border:"none", color:theme.textDim, cursor:"pointer", fontSize:18 }}
                    onClick={()=>{ setShowBollaForm(false); setModificaBollaId(null); setBollaForm({ num:"", fornitore:"", data:new Date().toISOString().slice(0,10), note:"", _catSel:"", _subSel:"", _cantiereSel:"" }); setRigheForm([{ materialeDiz:"", prodotto:"", qta:"", um:"cad", prezzo:"", cantiere:"", lavorazione:"", categoriaP:"", note:"" }]); }}>✕</button>
                </div>
              )}
              <div className="card-title" style={{ marginBottom:16 }}>{modificaBollaId ? "" : "🧾 Nuova bolla di consegna"}</div>

              {/* Cantiere predefinito — assegna a tutte le righe */}
              {!modificaBollaId && (
                <div style={{ marginBottom:12, padding:"10px 14px", background:"#0a1520", borderRadius:8, border:"1px solid #4e9af533", display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ fontSize:12, color:"#4e9af5", fontWeight:600, whiteSpace:"nowrap" }}>🏗️ Cantiere predefinito:</div>
                  <select style={{ ...inputSt2, flex:1, margin:0 }} value={bollaForm._cantierePred||""} onChange={e => {
                    const cant = e.target.value;
                    setBollaForm({...bollaForm, _cantierePred: cant});
                    if (cant) setRigheForm(prev => prev.map(r => ({ ...r, cantiere: cant })));
                  }}>
                    <option value="">— Nessuno (assegna riga per riga) —</option>
                    {cantieriDB.filter(c=>!c.completato).map(c=>(
                      <option key={c._id} value={c.nome}>{c.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Intestazione bolla */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:12, marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>N° Bolla *</div>
                  <input style={inputSt2} placeholder="es. 00234/2026" value={bollaForm.num} onChange={e=>setBollaForm({...bollaForm,num:e.target.value})} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Fornitore</div>
                  <div style={{ position:"relative" }}>
                    <input style={inputSt2} placeholder="es. Edil Center Pistoia" value={bollaForm.fornitore}
                      onChange={e=>setBollaForm({...bollaForm,fornitore:e.target.value})} />
                    {(() => {
                      const fornitori = [...new Set(bolle.map(b=>b.fornitore).filter(Boolean))];
                      const matches = bollaForm.fornitore.length>0
                        ? fornitori.filter(f=>f.toLowerCase().includes(bollaForm.fornitore.toLowerCase()) && f.toLowerCase()!==bollaForm.fornitore.toLowerCase())
                        : [];
                      if (!matches.length) return null;
                      return (
                        <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:8, zIndex:20, maxHeight:150, overflowY:"auto" }}>
                          {matches.map(f=>(
                            <div key={f} onClick={()=>setBollaForm({...bollaForm,fornitore:f})}
                              style={{ padding:"8px 12px", cursor:"pointer", fontSize:13, borderBottom:`1px solid ${theme.border}33` }}
                              onMouseEnter={e=>e.target.style.background=theme.surfaceHover}
                              onMouseLeave={e=>e.target.style.background="transparent"}>
                              {f}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Data</div>
                  <input style={inputSt2} type="date" value={bollaForm.data} onChange={e=>setBollaForm({...bollaForm,data:e.target.value})} />
                </div>
                <div>
                  <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Note</div>
                  <input style={inputSt2} placeholder="Note generali..." value={bollaForm.note} onChange={e=>setBollaForm({...bollaForm,note:e.target.value})} />
                </div>
              </div>

              {/* Righe materiali — flusso guidato dal dizionario */}
              <div style={{ marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={{ fontSize:12, color:theme.textMuted, textTransform:"uppercase", letterSpacing:1 }}>Materiali</div>
                  <div style={{ display:"flex", gap:8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={aggiungiRiga}>+ Riga libera</button>
                  </div>
                </div>

                {/* FIX_BOLLA_SEMPLIFICATA_v1: rimossa sezione 'Aggiungi da dizionario' */}

                {/* Header colonne */}
                {righeForm.some(r=>r.prodotto!==undefined||r.materialeDiz) && (
                <div style={{ display:"grid", gridTemplateColumns:"130px 1fr 60px 70px 70px 55px 85px 1fr 32px", gap:8, marginBottom:6 }}>
                    {["Materiale","Prodotto","N.Conf","Lt/Kg x conf","Totale","U.M.","€/um","Cantiere/Lav.",""].map(h => (
                      <div key={h} style={{ fontSize:10, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>{h}</div>
                    ))}
                  </div>
                )}

                {righeForm.map((r,i) => {
                  const cantSelezionato = cantieriDB.find(c => c.nome === (r.cantiere||bollaForm._cantiereSel));
                  const lavsCant = (cantSelezionato?.lavorazioni||[]); // FIX_SMALT_BOLLE_v1: smaltimento ammesso nelle bolle/fatture
                  return (
                  <div key={i} style={{ display:"grid", gridTemplateColumns:"130px 1fr 60px 70px 70px 55px 85px 1fr 32px", gap:8, marginBottom:8, alignItems:"start",
                    background:r.materialeDiz?theme.bg:"transparent", borderRadius:r.materialeDiz?8:0, padding:r.materialeDiz?"8px":"0" }}>

                    {/* Materiale dal dizionario */}
                    <div>
                      {r.materialeDiz ? (
                        <div style={{ fontSize:12, fontWeight:600, color:theme.accent, padding:"8px 4px" }}>
                          {r.materialeDiz}
                        </div>
                      ) : (
                        <div style={{ fontSize:11, color:theme.textDim, padding:"8px 4px" }}>— riga libera —</div>
                      )}
                    </div>

                    {/* Prodotto specifico */}
                    <div style={{ position:"relative" }}>
                      <input style={{...inputSt2, padding:"7px 8px"}}
                        placeholder={r.materialeDiz ? `es. ${r.materialeDiz} Mapei...` : "es. FUGENFUELLER KG.5"}
                        value={r.prodotto||""}
                        onChange={e=>aggiornaRiga(i,"prodotto",e.target.value)}
                        onBlur={e=>{
                          // FIX_BOLLA_SEMPLIFICATA_v1: onBlur usa prezzoUltimoBase + umBase + contConf come click suggerimento
                          const normInput = e.target.value.trim().toUpperCase();
                          if (!normInput) return;
                          const match = listinoMat.find(l=>(l.prodotto||"").trim().toUpperCase()===normInput);
                          if (!match || r.prezzo) return;
                          const umBaseLst = (match.umBase||'').toLowerCase().trim();
                          let prezzoApplicato = match.prezzoUltimo;
                          let umApplicata = match.um || 'cad';
                          if (umBaseLst && match.prezzoUltimoBase > 0) {
                            prezzoApplicato = match.prezzoUltimoBase;
                            umApplicata = umBaseLst;
                          }
                          setRigheForm(prev => prev.map((riga,idx) => idx===i ? {
                            ...riga,
                            prezzo: String(prezzoApplicato),
                            um: umApplicata,
                            contConf: match.contenutoConf ? String(match.contenutoConf) : (riga.contConf||''),
                            categoriaP: match.categoriaP || riga.categoriaP || '',
                          } : riga));
                        }}
                      />
                      {(() => {
                        const normInput = (r.prodotto||"").trim().toUpperCase();
                        if (normInput.length < 2) return null;
                        // Match esatto — mostra prezzo
                        const matchEsatto = listinoMat.find(l=>(l.prodotto||"").trim().toUpperCase()===normInput);
                        // Match parziale — autocomplete
                        const matches = listinoMat.filter(l=>(l.prodotto||"").trim().toUpperCase().includes(normInput) && (l.prodotto||"").trim().toUpperCase()!==normInput).slice(0,5);
                        if (matchEsatto) return (
                          <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface, border:`1px solid ${theme.accent}44`, borderRadius:6, padding:"6px 10px", zIndex:10, fontSize:12 }}>
                            {/* FIX_FORM_BOLLA_UM_BASE_v2: mostra €/UM base se disponibile */}
                            💡 <strong style={{color:theme.accent}}>
                              € {matchEsatto.umBase && matchEsatto.prezzoUltimoBase > 0 ? matchEsatto.prezzoUltimoBase : matchEsatto.prezzoUltimo}
                            </strong>/{matchEsatto.umBase && matchEsatto.prezzoUltimoBase > 0 ? matchEsatto.umBase : matchEsatto.um} · {matchEsatto.ultimaData||"—"}
                            <button onClick={async ()=>{
                              // FIX_FORM_BOLLA_UM_BASE_v2: applica €/UM base se disponibile
                              const umBaseLst = (matchEsatto.umBase||'').toLowerCase().trim();
                              let prezzoApplicato = matchEsatto.prezzoUltimo;
                              let umApplicata = matchEsatto.um || 'cad';
                              if (umBaseLst && matchEsatto.prezzoUltimoBase > 0) {
                                prezzoApplicato = matchEsatto.prezzoUltimoBase;
                                umApplicata = umBaseLst;
                              }
                              setRigheForm(prev => prev.map((riga,idx) => idx===i ? {
                                ...riga,
                                prezzo: String(prezzoApplicato),
                                um: umApplicata,
                                contConf: matchEsatto.contenutoConf ? String(matchEsatto.contenutoConf) : (riga.contConf||''),
                              } : riga));
                              // mantiene comunque l'autoaggiornamento prezzo originale per retrocompatibilita'
                              aggiornaRiga(i,"prezzo",String(prezzoApplicato));
                              // FIX_CODICI_DA_BOLLA_v1: salva codice bolla nel listino (se presente e non gia' lista)
                              try {
                                const codBolla = (r.codice||"").toString().trim();
                                if (codBolla) {
                                  const codes = Array.isArray(matchEsatto.codiciFornitore) ? matchEsatto.codiciFornitore : [];
                                  if (!codes.some(c => (c||"").toUpperCase() === codBolla.toUpperCase())) {
                                    await updateDoc(doc(db,"listino_materiali",matchEsatto._id), {
                                      codiciFornitore: [...codes, codBolla],
                                      aggiornato: new Date().toISOString(),
                                    });
                                  }
                                }
                              } catch(e) { console.error("Salva codice bolla:", e.message); }
                            }}
                              style={{marginLeft:8,background:theme.accentSoft,border:`1px solid ${theme.accent}44`,borderRadius:4,color:theme.accent,cursor:"pointer",padding:"1px 6px",fontSize:11}}>
                              Usa
                            </button>
                          </div>
                        );
                        if (!matches.length) return null;
                        return (
                          <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface, border:`1px solid ${theme.accent}44`, borderRadius:6, zIndex:20, overflow:"hidden" }}>
                            {matches.map(m => (
                              <div key={m._id} onClick={async ()=>{
                                // FIX_FORM_BOLLA_UM_BASE_v2: priorita' a umBase + prezzoUltimoBase del listino
                                const umBaseLst = (m.umBase||'').toLowerCase().trim();
                                let prezzoApplicato = m.prezzoUltimo;
                                let umApplicata = m.um || 'cad';
                                // Se il listino ha umBase + prezzoUltimoBase, usali sempre (sono €/kg base)
                                if (umBaseLst && m.prezzoUltimoBase > 0) {
                                  prezzoApplicato = m.prezzoUltimoBase;
                                  umApplicata = umBaseLst;
                                }
                                setRigheForm(prev => prev.map((riga,idx) => idx===i ? {
                                  ...riga,
                                  prodotto: m.prodotto,
                                  prezzo: String(prezzoApplicato),
                                  um: umApplicata,
                                  categoriaP: m.categoriaP || riga.categoriaP || '',
                                  contConf: m.contenutoConf ? String(m.contenutoConf) : (riga.contConf||''),
                                  umConf: m.umBase ? '' : (riga.umConf||''),
                                } : riga));
                                // FIX_CODICI_DA_BOLLA_v1: salva codice bolla nel listino
                                try {
                                  const codBolla = (r.codice||"").toString().trim();
                                  if (codBolla) {
                                    const codes = Array.isArray(m.codiciFornitore) ? m.codiciFornitore : [];
                                    if (!codes.some(c => (c||"").toUpperCase() === codBolla.toUpperCase())) {
                                      await updateDoc(doc(db,"listino_materiali",m._id), {
                                        codiciFornitore: [...codes, codBolla],
                                        aggiornato: new Date().toISOString(),
                                      });
                                    }
                                  }
                                } catch(e) { console.error("Salva codice bolla:", e.message); }
                              }}
                                style={{ padding:"8px 12px", cursor:"pointer", borderBottom:`1px solid ${theme.border}22`, fontSize:12, display:"flex", justifyContent:"space-between", alignItems:"center" }}
                                onMouseEnter={e=>e.currentTarget.style.background=theme.accentSoft}
                                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                <span style={{ color:theme.text }}>{m.prodotto}</span>
                                <span style={{ color:theme.accent, fontWeight:600 }}>
                                  {/* FIX_FORM_BOLLA_UM_BASE_v2: mostra €/UM base se disponibile */}
                                  {m.umBase && m.prezzoUltimoBase > 0
                                    ? '€ ' + m.prezzoUltimoBase + '/' + m.umBase
                                    : '€ ' + m.prezzoUltimo + '/' + m.um}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* FIX_FORM_BOLLA_MIGLIORATO_v1: N. confezioni con tooltip */}
                    <input style={{...inputSt2, padding:"7px 8px"}} type="number" placeholder="2"
                      title="Numero confezioni (es. 2 sacchi)"
                      value={r.nConf||""}
                      onChange={e=>{
                        const nConf = e.target.value;
                        const tot = nConf && r.contConf ? String(Math.round(parseFloat(nConf)*parseFloat(r.contConf)*100)/100) : "";
                        setRigheForm(righeForm.map((riga,idx)=>idx===i?{...riga,nConf,qta:tot||riga.qta}:riga));
                      }} />

                    {/* Contenuto per confezione - placeholder e tooltip dinamici in base a UM */}
                    <input style={{...inputSt2, padding:"7px 8px"}} type="number" placeholder={r.um==='kg'?'25':r.um==='lt'?'5':'1'}
                      title={'Contenuto per confezione (es. 25 ' + (r.um||'kg') + ' per sacco)'}
                      value={r.contConf||""}
                      onChange={e=>{
                        const contConf = e.target.value;
                        const tot = contConf && r.nConf ? String(Math.round(parseFloat(r.nConf)*parseFloat(contConf)*100)/100) : "";
                        setRigheForm(righeForm.map((riga,idx)=>idx===i?{...riga,contConf,qta:tot||riga.qta}:riga));
                      }} />

                    {/* Qta totale - calcolata o manuale */}
                    <input style={{...inputSt2, padding:"7px 8px", background: r.nConf&&r.contConf?"#0a1f14":theme.bg, color:r.nConf&&r.contConf?theme.success:theme.text, fontWeight: r.nConf&&r.contConf?700:400}}
                      type="number" placeholder="Totale"
                      title={r.nConf && r.contConf ? 'Calcolato automaticamente: ' + r.nConf + ' × ' + r.contConf : 'Quantità totale'}
                      value={r.qta||""}
                      onChange={e=>aggiornaRiga(i,"qta",e.target.value)} />

                    {/* U.M. */}
                    <select style={{...inputSt2, padding:"7px 8px"}} value={r.um||"cad"} onChange={e=>aggiornaRiga(i,"um",e.target.value)}>
                      {["cad","mq","ml","kg","lt","mc","sacco","rotolo"].map(u=><option key={u}>{u}</option>)}
                    </select>

                    {/* Prezzo */}
                    <input style={{...inputSt2, padding:"7px 8px"}} type="number" placeholder="0,00" value={r.prezzo||""} onChange={e=>aggiornaRiga(i,"prezzo",e.target.value)} />

                    {/* Lavorazione/Cantiere */}
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      <select style={{...inputSt2, padding:"5px 6px", fontSize:11}} value={r.cantiere||""} onChange={e=>aggiornaRiga(i,"cantiere",e.target.value)}>
                        <option value="">🏠 Magazzino</option>
                        {cantieriDB.filter(c=>!c.completato).map(c=><option key={c._id||c.id} value={c.nome}>{c.nome}</option>)}
                      </select>
                      <select style={{...inputSt2, padding:"5px 6px", fontSize:11}} value={r.lavorazione||""} onChange={e=>aggiornaRiga(i,"lavorazione",e.target.value)}>
                        <option value="">— Lavorazione —</option>
                        {/* FIX_TELEFONO_LAVORAZIONE_v2: SOLO descBreve (fallback desc) */}
                        {lavsCant.map((l,li)=>{
                          const breve = (l.descBreve||'').trim();
                          const lunga = (l.desc||'').trim();
                          return <option key={li} value={l.desc}>{breve || lunga}</option>;
                        })}
                        <option value="__extra__">⚡ Extra / Generica</option>
                      </select>
                    </div>

                    {/* Elimina riga */}
                    <button onClick={()=>rimuoviRiga(i)} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:18, padding:0, alignSelf:"center" }}>✕</button>
                  </div>
                  );
                })}
              </div>

              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:16 }}>
                <div style={{ fontSize:13, color:theme.textMuted }}>
                  Totale: <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, color:theme.accent, fontSize:16 }}>
                    € {calcolaTotaleRighe(righeForm).toLocaleString("it-IT",{minimumFractionDigits:2})}
                  </span>
                </div>
                <div style={{ display:"flex", gap:10 }}>
                  <button className="btn btn-ghost" onClick={()=>{ setShowBollaForm(false); setModificaBollaId(null); setBollaForm({ num:"", fornitore:"", data:new Date().toISOString().slice(0,10), note:"", _catSel:"", _subSel:"", _cantiereSel:"" }); setRigheForm([{ materialeDiz:"", prodotto:"", qta:"", um:"cad", prezzo:"", cantiere:"", lavorazione:"", categoriaP:"", note:"" }]); }}>Annulla</button>
                  <button className="btn btn-primary" onClick={salvaBolla} disabled={salvandoBolla}>
                    {salvandoBolla ? "Salvataggio..." : modificaBollaId ? "✓ Salva modifiche" : "✓ Salva bolla"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lista bolle */}
          {loadingBolle ? (
            <div style={{ color:theme.textMuted, padding:20 }}>Caricamento...</div>
          ) : bolleFiltrate.length === 0 ? (
            <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
              <div style={{ fontSize:40, marginBottom:12 }}>🧾</div>
              <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Nessuna bolla registrata</div>
              <div style={{ fontSize:13, color:theme.textDim, marginTop:6 }}>Clicca "+ Nuova bolla" per iniziare</div>
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {bolleFiltrate.map(b => {
                const isAperta = bollaAperta === b._id;
                const dataFmt = b.data ? new Date(b.data).toLocaleDateString("it-IT") : "—";
                const totale = calcolaTotaleRighe(b.righe||[]);
                const haPrezzo = totale > 0;
                return (
                  <div key={b._id} className="card" style={{ marginBottom:0, border:`1px solid ${isAperta?theme.accent:theme.border}` }}>
                    {/* Header bolla */}
                    <div style={{ display:"flex", alignItems:"center", gap:14, cursor:"pointer" }} onClick={()=>apricBollaAperta(b, isAperta)}>
                      <div style={{ fontSize:24 }}>🧾</div>
                      <div style={{ flex:1 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:15 }}>N° {b.num}</span>
                          {!haPrezzo && <span style={{ fontSize:11, background:"#f5a62322", color:"#f5a623", border:"1px solid #f5a62344", borderRadius:6, padding:"2px 8px" }}>⏳ Prezzi mancanti</span>}
                        </div>
                        <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>
                          {b.fornitore||"Fornitore non specificato"} · {dataFmt} · {(b.righe||[]).length} articoli
                        </div>
                      </div>
                      {haPrezzo && (
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:"#3ecf8e" }}>
                          € {totale.toLocaleString("it-IT",{minimumFractionDigits:2})}
                        </div>
                      )}
                      <div style={{ fontSize:18, color:theme.textDim }}>{isAperta?"▲":"▼"}</div>
                    </div>

                    {/* Dettaglio bolla */}
                    {isAperta && (
                      <div style={{ marginTop:16, borderTop:`1px solid ${theme.border}`, paddingTop:16 }}>
                        {b.note && <div style={{ fontSize:13, color:theme.textMuted, marginBottom:12, fontStyle:"italic" }}>📝 {b.note}</div>}

                        {/* Tabella righe */}
                        <div style={{ overflowX:"auto" }}>
                          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                            <thead>
                              <tr>
                                {["Categoria","Prodotto","Qtà","U.M.","Cantiere","Lavorazione","Prezzo €","Totale €"].map(h=>(
                                  <th key={h} style={{ textAlign:"left", padding:"8px 10px", fontSize:10, letterSpacing:1.5, textTransform:"uppercase", color:theme.textDim, borderBottom:`1px solid ${theme.border}` }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {getRigheDettaglio(b).map((r,ri) => {
                                const cat = CATEGORIE_MATERIALI.find(c=>c.id===r.categoria)||CATEGORIE_MATERIALI[CATEGORIE_MATERIALI.length-1];
                                const totRiga = (parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1);
                                const cantSelDett = cantieriDB.find(c => c.nome === r.cantiere);
                                const lavsDett = cantSelDett?.lavorazioni || [];
                                return (
                                  <tr key={ri}>
                                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{cat.emoji} {cat.label}</td>
                                    <td style={{ padding:"8px 10px", fontWeight:600 }}>{r.prodotto}</td>
                                    <td style={{ padding:"8px 10px" }}>
                                      <input type="number" value={r.qta||""} placeholder="0"
                                        style={{ ...inputSt2, width:60, padding:"4px 6px", fontSize:13 }}
                                        onChange={e => aggiornaRigaDettaglioLocale(b._id, ri, "qta", e.target.value)} />
                                    </td>
                                    <td style={{ padding:"8px 10px", color:theme.textMuted }}>{r.um}</td>
                                    <td style={{ padding:"8px 10px" }}>
                                      <select style={{ ...inputSt2, padding:"4px 6px", fontSize:12, width:150 }}
                                        value={r.cantiere||""}
                                        onChange={e => aggiornaRigaDettaglioLocale(b._id, ri, "cantiere", e.target.value)}>
                                        <option value="">🏠 Magazzino</option>
                                        {cantieriDB.filter(c=>!c.completato).map(c=><option key={c._id||c.id} value={c.nome}>{c.nome}</option>)}
                                      </select>
                                    </td>
                                    <td style={{ padding:"8px 10px" }}>
                                      <select style={{ ...inputSt2, padding:"4px 6px", fontSize:12, width:160 }}
                                        value={r.lavorazione||""}
                                        onChange={e => aggiornaRigaDettaglioLocale(b._id, ri, "lavorazione", e.target.value)}>
                                        <option value="">— Lavorazione —</option>
                                        {lavsDett.map((l,li)=><option key={li} value={l.desc}>{l.desc}</option>)}/* FIX_SMALT_BOLLE_v1 */
                                        <option value="__extra__">⚡ Extra / Generica</option>
                                      </select>
                                    </td>
                                    <td style={{ padding:"8px 10px" }}>
                                      <input type="number" value={r.prezzo||""} placeholder="0,00"
                                        style={{ ...inputSt2, width:80, padding:"4px 6px", fontSize:13 }}
                                        onChange={e => aggiornaRigaDettaglioLocale(b._id, ri, "prezzo", e.target.value)} />
                                    </td>
                                    <td style={{ padding:"8px 10px", fontFamily:"'Syne',sans-serif", fontWeight:700, color:"#3ecf8e" }}>
                                      € {totRiga.toLocaleString("it-IT",{minimumFractionDigits:2})}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12 }}>
                          <div style={{ fontSize:13, color:theme.textMuted }}>
                            Totale bolla: <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, color:haPrezzo?"#3ecf8e":theme.textDim, fontSize:16 }}>
                              {haPrezzo ? "€ "+calcolaTotaleRighe(getRigheDettaglio(b)).toLocaleString("it-IT",{minimumFractionDigits:2}) : "— prezzi da inserire"}
                            </span>
                          </div>
                          <div style={{ display:"flex", gap:8 }}>
                            {/* FIX_LISTINO_MATCH_V2: ricalcola prezzi da listino */}
                            <button onClick={()=>{
                              const righeAttuali = getRigheDettaglio(b);
                              const righeAggiornate = applicaPrezziDaListino(righeAttuali);
                              const cambiate = righeAggiornate.filter((r,i) => parseFloat(r.prezzo||0) !== parseFloat(righeAttuali[i]?.prezzo||0)).length;
                              if (cambiate === 0) {
                                alert("Nessun prezzo trovato nel listino per i prodotti senza prezzo. Verifica i nomi o aggiungili al listino.");
                                return;
                              }
                              setRigheDettaglio(prev => ({ ...prev, [b._id]: righeAggiornate }));
                              alert("✓ " + cambiate + " righe aggiornate. Clicca 'Salva modifiche' per confermare.");
                            }} className="btn btn-ghost btn-sm" title="Cerca prezzi mancanti nel listino">🔄 Ricalcola prezzi</button>
                            {haModificheDettaglio(b._id) && (
                              <button onClick={()=>salvaRigheDettaglio(b._id)} className="btn btn-primary btn-sm">💾 Salva modifiche</button>
                            )}
                            <button onClick={()=>apriModificaBolla(b)} className="btn btn-ghost btn-sm">✏️ Modifica</button>
                            <button onClick={()=>eliminaBolla(b._id)} className="btn btn-danger btn-sm">🗑️ Elimina</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Toolbar lista materiali — solo nei tab lista/fornitori */}
      {(tab === "lista" || tab === "fornitori") && (
        <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <input style={{ ...inputSt, width:220 }} placeholder="🔍 Cerca materiale o cantiere..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display:"flex", gap:6 }}>
          {[["tutti","Tutti"],["da_ordinare","Da ordinare"],["ordinato","Ordinato"],["consegnato","Consegnato"]].map(([id,label]) => (
            <div key={id} onClick={() => setFiltroStato(id)} style={{ padding:"5px 11px", borderRadius:8, cursor:"pointer", fontSize:12, fontWeight:600,
              border:`1px solid ${filtroStato===id?theme.accent:theme.border}`, background:filtroStato===id?theme.accent+"22":theme.bg, color:filtroStato===id?theme.accent:theme.textMuted }}>
              {label}
            </div>
          ))}
        </div>
        <button className="btn btn-primary btn-sm" style={{ marginLeft:"auto" }} onClick={() => setShowForm(!showForm)}>
          {showForm ? "✕ Chiudi" : "+ Aggiungi materiale"}
        </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:20, border:`1px solid ${theme.accent}55` }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}>
            <div className="card-title">📦 Nuovo materiale</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowForm(false)}>✕</button>
          </div>
          <div className="form-grid" style={{ gap:12 }}>
            <div className="form-group"><label>Materiale *</label><input placeholder="es. Piastrelle gres 60x60" value={form.materiale} onChange={e => setForm({...form,materiale:e.target.value})} /></div>
            <div className="form-group"><label>Fornitore</label><input placeholder="es. Edil Ceramiche Srl" value={form.fornitore} onChange={e => setForm({...form,fornitore:e.target.value})} /></div>
            <div className="form-group">
              <label>Cantiere</label>
              <select value={form.cantiere} onChange={e => setForm({...form,cantiere:e.target.value})} style={{ ...inputSt }}>
                <option value="">— Seleziona —</option>
                {cantieriDB.filter(c => !c.completato).map(c => <option key={c._id||c.id} value={c.nome}>{c.nome}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Quantità + U.M.</label>
              <div style={{ display:"flex", gap:6 }}>
                <input placeholder="25" value={form.qta} onChange={e => setForm({...form,qta:e.target.value})} style={{ flex:2 }} />
                <select value={form.um} onChange={e => setForm({...form,um:e.target.value})} style={{ ...inputSt, flex:1 }}>
                  {["mq","ml","cad","kg","lt","mc","sacco"].map(u => <option key={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label>Prezzo totale (€)</label><input type="number" placeholder="0" value={form.prezzo} onChange={e => setForm({...form,prezzo:e.target.value})} /></div>
            <div className="form-group">
              <label>Urgenza</label>
              <select value={form.urgenza} onChange={e => setForm({...form,urgenza:e.target.value})} style={{ ...inputSt }}>
                <option value="alta">🔴 Alta</option>
                <option value="media">🟡 Media</option>
                <option value="bassa">🟢 Bassa</option>
              </select>
            </div>
            <div className="form-group full"><label>Note</label><input placeholder="Colore, referenza, specifiche..." value={form.note} onChange={e => setForm({...form,note:e.target.value})} /></div>
          </div>
          <div style={{ display:"flex", gap:10, marginTop:14, justifyContent:"flex-end" }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Annulla</button>
            <button className="btn btn-primary" onClick={aggiungi}>✓ Aggiungi</button>
          </div>
        </div>
      )}

      {/* TAB LISTA */}
      {tab === "lista" && (
        loading ? <div style={{ color:theme.textMuted, padding:20 }}>Caricamento...</div>
        : matFiltrati.length === 0 ? (
          <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📦</div>
            <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Nessun materiale trovato</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Materiale</th><th>Cantiere</th><th>Fornitore</th><th>Qtà</th><th>Prezzo</th><th>Urgenza</th><th>Stato</th><th></th></tr>
              </thead>
              <tbody>
                {matFiltrati.map(m => {
                  const urg = URG[m.urgenza]||URG.media;
                  const st  = STATI[m.stato]||STATI.da_ordinare;
                  return (
                    <tr key={m._id}>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{m.materiale}</div>
                        {m.note && <div style={{ fontSize:11, color:theme.textDim, marginTop:2 }}>{m.note}</div>}
                      </td>
                      <td style={{ fontSize:12, color:theme.textMuted }}>{m.cantiere||"—"}</td>
                      <td style={{ fontSize:12, color:theme.textMuted }}>{m.fornitore||"—"}</td>
                      <td style={{ fontSize:13, fontWeight:600 }}>{m.qta||"—"} {m.um}</td>
                      <td style={{ fontSize:13, fontWeight:700, color:"#3ecf8e" }}>
                        {m.prezzo ? "€ "+parseFloat(m.prezzo).toLocaleString("it-IT") : "—"}
                      </td>
                      <td>
                        <span style={{ background:urg.bg, color:urg.color, border:`1px solid ${urg.color}44`, borderRadius:6, padding:"2px 8px", fontSize:11, fontWeight:700 }}>
                          {urg.label}
                        </span>
                      </td>
                      <td>
                        <select value={m.stato} onChange={e => aggiornaStato(m._id, e.target.value)}
                          style={{ background:"#0f1117", border:`1px solid ${st.color}44`, borderRadius:6, color:st.color, padding:"4px 8px", fontSize:12, fontWeight:700, cursor:"pointer" }}>
                          <option value="da_ordinare">🛒 Da ordinare</option>
                          <option value="ordinato">📦 Ordinato</option>
                          <option value="consegnato">✅ Consegnato</option>
                        </select>
                      </td>
                      <td>
                        <button onClick={() => elimina(m._id)} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:16 }}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}

      {/* TAB FORNITORI */}
      {tab === "fornitori" && (
        Object.keys(perFornitore).length === 0 ? (
          <div style={{ textAlign:"center", padding:"48px 0", color:theme.textDim }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🏭</div>
            <div style={{ fontSize:15, fontWeight:600, color:theme.textMuted }}>Nessun materiale registrato</div>
          </div>
        ) : Object.entries(perFornitore).map(([fornitore, items]) => {
          const totValore = items.reduce((s,m) => s+(parseFloat(m.prezzo)||0), 0);
          const daOrd = items.filter(m => m.stato!=="consegnato").length;
          return (
            <div key={fornitore} className="card" style={{ marginBottom:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>🏭 {fornitore}</div>
                  <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>{items.length} articoli · {daOrd} da evadere</div>
                </div>
                {totValore > 0 && <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:"#3ecf8e" }}>€ {totValore.toLocaleString("it-IT")}</div>}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {items.map(m => {
                  const urg = URG[m.urgenza]||URG.media;
                  const st  = STATI[m.stato]||STATI.da_ordinare;
                  return (
                    <div key={m._id} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.border}` }}>
                      <span style={{ background:urg.bg, color:urg.color, borderRadius:5, padding:"2px 7px", fontSize:10, fontWeight:700 }}>{urg.label}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:600 }}>{m.materiale}</div>
                        {m.cantiere && <div style={{ fontSize:11, color:theme.textDim }}>{m.cantiere}</div>}
                      </div>
                      <div style={{ fontSize:12, color:theme.textMuted }}>{m.qta} {m.um}</div>
                      <span style={{ color:st.color, fontSize:12, fontWeight:700 }}>● {st.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}



// ─── DASHBOARD OPERAIO ───────────────────────────────────────────────────────

// ── SCARICO MAGAZZINO OPERAIO ─────────────────────────────────────────────────
function ScaricoMagazzinoOperaio({ nomeOperaio, cantieriDB = [], bolleDB = [] }) {
  const [prodotto, setProdotto] = useState("");
  const [qta, setQta] = useState("");
  const [um, setUm] = useState("kg");
  const [cantiere, setCantiere] = useState("");
  const [lavorazione, setLavorazione] = useState("");
  const [note, setNote] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [successo, setSuccesso] = useState(false);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const [scarichi, setScarichi] = useState([]);

  const inputSt = {
    background:"#0f1117", border:"1px solid #2a2e45", borderRadius:10,
    color:"#e8eaf2", padding:"14px 16px", fontSize:16,
    fontFamily:"'DM Sans',sans-serif", outline:"none", width:"100%"
  };

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db,"scarichi_magazzino"), orderBy("createdAt","desc")),
      snap => setScarichi(snap.docs.map(d=>({...d.data(),_id:d.id}))),
      () => {}
    );
    return () => unsub();
  }, []);

  // Calcola stock da bolle — prodotti senza cantiere
  const stock = {};
  bolleDB.forEach(b => {
    (b.righe||[]).filter(r => !r.cantiere && r.prodotto).forEach(r => {
      const k = (r.prodotto||"").trim().toUpperCase();
      if (!stock[k]) stock[k] = { prodotto:r.prodotto.trim(), um:r.um||"kg", qtaIn:0, qtaOut:0, prezzoUltimo:parseFloat(r.prezzo)||0 };
      stock[k].qtaIn += parseFloat(r.qta)||0;
      if (parseFloat(r.prezzo)>0) stock[k].prezzoUltimo = parseFloat(r.prezzo);
    });
  });
  scarichi.forEach(s => {
    const k = (s.prodotto||"").trim().toUpperCase();
    if (stock[k]) stock[k].qtaOut += parseFloat(s.qta)||0;
  });
  const prodottiStock = Object.values(stock)
    .map(s => ({ ...s, disponibile: s.qtaIn - s.qtaOut }))
    .filter(s => s.disponibile > 0)
    .sort((a,b) => a.prodotto.localeCompare(b.prodotto));

  // Filtra suggerimenti in base a quello che scrivi
  const suggerimenti = prodotto.length >= 2
    ? prodottiStock.filter(p => p.prodotto.toLowerCase().includes(prodotto.toLowerCase()))
    : prodottiStock;

  const cantSelezionato = cantieriDB.find(c => c.nome === cantiere);
  const lavsCant = (cantSelezionato?.lavorazioni||[]); // FIX_SMALT_BOLLE_v1

  const reset = () => {
    setProdotto(""); setQta(""); setCantiere(""); setLavorazione(""); setNote("");
    setShowSuggerimenti(false);
  };

  const conferma = async () => {
    if (!prodotto || !qta || !cantiere) { alert("Prodotto, quantità e cantiere sono obbligatori."); return; }
    setSalvando(true);
    const prod = prodottiStock.find(p => p.prodotto.toLowerCase() === prodotto.toLowerCase());
    const prezzoUnitario = prod?.prezzoUltimo || 0;
    try {
      await addDoc(collection(db,"scarichi_magazzino"), {
        prodotto: prodotto.trim(),
        qta: parseFloat(qta),
        um,
        cantiere,
        lavorazione,
        note,
        operaio: nomeOperaio,
        prezzoUnitario,
        costoTotale: parseFloat(qta) * prezzoUnitario,
        data: new Date().toISOString().slice(0,10),
        createdAt: new Date().toISOString(),
      });
      setSuccesso(true);
      reset();
      setTimeout(() => setSuccesso(false), 3000);
    } catch(e) { alert("❌ "+e.message); }
    setSalvando(false);
  };

  return (
    <div className="content" style={{ maxWidth:520, margin:"0 auto" }}>

      {/* Titolo */}
      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:theme.accent, marginBottom:8 }}>
        📦 Scarico Magazzino
      </div>
      <div style={{ fontSize:14, color:theme.textMuted, marginBottom:24 }}>
        Registra i materiali che prendi dal magazzino per il cantiere
      </div>

      {/* Successo */}
      {successo && (
        <div style={{ background:"#0a2218", border:"1px solid #3ecf8e44", borderRadius:12, padding:"14px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
          <span style={{ fontSize:24 }}>✅</span>
          <div style={{ fontWeight:700, color:"#3ecf8e", fontSize:16 }}>Scarico registrato!</div>
        </div>
      )}

      <div className="card" style={{ padding:20 }}>

        {/* Prodotto */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Prodotto *</div>
          <div style={{ position:"relative" }}>
            <input style={inputSt} placeholder="Scrivi per cercare... es. Malta"
              value={prodotto}
              onChange={e => { setProdotto(e.target.value); setShowSuggerimenti(true); }}
              onFocus={() => setShowSuggerimenti(true)}
              onBlur={() => setTimeout(() => setShowSuggerimenti(false), 200)}
            />
            {showSuggerimenti && suggerimenti.length > 0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface,
                border:`1px solid ${theme.accent}44`, borderRadius:10, zIndex:100,
                maxHeight:220, overflowY:"auto", boxShadow:"0 8px 32px #00000088", marginTop:4 }}>
                {suggerimenti.map(p => (
                  <div key={p.prodotto}
                    onMouseDown={() => { setProdotto(p.prodotto); setUm(p.um); setShowSuggerimenti(false); }}
                    style={{ padding:"12px 16px", cursor:"pointer", borderBottom:`1px solid ${theme.border}33`,
                      display:"flex", justifyContent:"space-between", alignItems:"center" }}
                    onMouseEnter={e=>e.currentTarget.style.background=theme.surfaceHover}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:600, color:theme.text }}>{p.prodotto}</div>
                      <div style={{ fontSize:12, color:theme.textDim, marginTop:2 }}>U.M.: {p.um}</div>
                    </div>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:16, color:theme.success }}>
                        {p.disponibile.toFixed(1)} {p.um}
                      </div>
                      <div style={{ fontSize:11, color:theme.textDim }}>disponibili</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {showSuggerimenti && prodotto.length >= 2 && suggerimenti.length === 0 && (
              <div style={{ position:"absolute", top:"100%", left:0, right:0, background:theme.surface,
                border:`1px solid ${theme.border}`, borderRadius:10, zIndex:100, padding:"12px 16px",
                fontSize:13, color:theme.textDim, marginTop:4 }}>
                Nessun prodotto trovato in magazzino
              </div>
            )}
          </div>
        </div>

        {/* Quantità + UM */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 100px", gap:12, marginBottom:16 }}>
          <div>
            <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Quantità *</div>
            <input style={inputSt} type="number" placeholder="es. 50" inputMode="decimal"
              value={qta} onChange={e => setQta(e.target.value)} />
          </div>
          <div>
            <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>U.M.</div>
            <select style={inputSt} value={um} onChange={e => setUm(e.target.value)}>
              {["kg","lt","cad","mq","ml","mc","sacco","rotolo"].map(u=><option key={u}>{u}</option>)}
            </select>
          </div>
        </div>

        {/* Cantiere */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Cantiere *</div>
          <select style={inputSt} value={cantiere} onChange={e => { setCantiere(e.target.value); setLavorazione(""); }}>
            <option value="">— Seleziona cantiere —</option>
            {cantieriDB.filter(c=>!c.completato).map(c=>(
              <option key={c._id||c.id} value={c.nome}>{c.nome}</option>
            ))}
          </select>
        </div>

        {/* Lavorazione */}
        {cantiere && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Lavorazione</div>
            <select style={inputSt} value={lavorazione} onChange={e => setLavorazione(e.target.value)}>
              <option value="">— Seleziona lavorazione (opzionale) —</option>
              {/* FIX_TELEFONO_LAVORAZIONE_v2: SOLO descBreve (fallback desc) */}
              {lavsCant.map((l,i)=>{
                const breve = (l.descBreve||'').trim();
                const lunga = (l.desc||'').trim();
                return <option key={i} value={l.desc}>{breve || lunga}</option>;
              })}
            </select>
          </div>
        )}

        {/* Note */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Note</div>
          <input style={inputSt} placeholder="es. Portata da Ion stamattina" value={note} onChange={e => setNote(e.target.value)} />
        </div>

        {/* Anteprima */}
        {prodotto && qta && (() => {
          const prod = prodottiStock.find(p => p.prodotto.toLowerCase() === prodotto.toLowerCase());
          if (!prod) return null;
          const costo = parseFloat(qta) * prod.prezzoUltimo;
          return (
            <div style={{ background:"#0a1f14", borderRadius:10, padding:"12px 16px", marginBottom:20,
              border:"1px solid #3ecf8e33", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontSize:13, color:theme.textMuted }}>
                Disponibile: <strong style={{color:theme.success}}>{prod.disponibile.toFixed(1)} {prod.um}</strong>
              </div>
              {costo > 0 && (
                <div style={{ fontSize:13, color:theme.textMuted }}>
                  Costo: <strong style={{color:theme.accent}}>€ {costo.toLocaleString("it-IT",{minimumFractionDigits:2})}</strong>
                </div>
              )}
            </div>
          );
        })()}

        {/* Bottone conferma */}
        <button className="btn btn-primary" style={{ width:"100%", padding:"16px", fontSize:16, fontWeight:700 }}
          onClick={conferma} disabled={salvando}>
          {salvando ? "Registrazione..." : "✓ Conferma scarico"}
        </button>
      </div>

      {/* Storico scarichi operaio oggi */}
      {(() => {
        const oggi = new Date().toISOString().slice(0,10);
        const scarichiOggi = scarichi.filter(s => s.operaio === nomeOperaio && s.data === oggi);
        if (!scarichiOggi.length) return null;
        return (
          <div className="card" style={{ marginTop:20 }}>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, marginBottom:12 }}>📋 I tuoi scarichi di oggi</div>
            {scarichiOggi.map(s => (
              <div key={s._id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                padding:"10px 0", borderBottom:`1px solid ${theme.border}44`, fontSize:13 }}>
                <div>
                  <div style={{ fontWeight:600 }}>{s.prodotto}</div>
                  <div style={{ fontSize:12, color:theme.textDim }}>{s.cantiere}{s.lavorazione ? " · "+s.lavorazione : ""}</div>
                </div>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:theme.accent }}>
                  {s.qta} {s.um}
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

function DashboardOperaio({ nomeOperaio, cantieriDB = [], assenzeDB = [] }) {
  const dp = dipendenti.find(d => d.nome === nomeOperaio);

  const oggi = new Date();
  const mese = `${String(oggi.getMonth()+1).padStart(2,"0")}/${oggi.getFullYear()}`;
  const anno = String(oggi.getFullYear());

  // Ore lavorate
  const oreOp = assenzeDB.filter(a => a.operaio === nomeOperaio && a.tipo === "lavorato");
  const oreMese = oreOp.filter(a => a.data?.slice(3) === mese).reduce((s,a) => s+(parseFloat(a.ore)||0), 0);
  const oreAnno = oreOp.filter(a => a.data?.slice(-4) === anno).reduce((s,a) => s+(parseFloat(a.ore)||0), 0);

  // Assenze
  const assenzeOp = assenzeDB.filter(a => a.operaio === nomeOperaio && a.tipo !== "lavorato");
  const ferieAnno    = assenzeOp.filter(a => a.tipo==="Ferie"    && a.data?.slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);
  const permessiAnno = assenzeOp.filter(a => a.tipo==="Permesso" && a.data?.slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
  const malattiaAnno = assenzeOp.filter(a => (a.tipo==="Malattia"||a.tipo==="Infortunio") && a.data?.slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);

  // Cantieri attivi dove l'operaio ha lavorato o è assegnato
  const cantieriOperaio = cantieriDB.filter(c => {
    if (c.completato) return false;
    const cid = c._id||c.id;
    return oreOp.some(a => a.cantiereId===cid || a.cantiere===c.nome);
  });
  // Se nessun cantiere ha ore, mostra tutti i cantieri attivi
  const cantieriAttivi = cantieriOperaio.length > 0 ? cantieriOperaio : cantieriDB.filter(c => !c.completato);

  // Calcola avanzamento da ore per un cantiere
  const avanzamentoCantiere = (cant) => {
    const lavs = cant.lavorazioni||[];
    const cid = cant._id||cant.id;
    if (lavs.length === 0) return cant.avanzamento||0;
    const totQ = lavs.reduce((s,l)=>s+(parseFloat(l.qta)||1),0);
    if (totQ === 0) return cant.avanzamento||0;
    const somma = lavs.reduce((s,l,i) => {
      const orePrev = ((parseFloat(l.minuti)||0)/60)*(parseFloat(l.qta)||0);
      const oreEff  = oreOp.filter(a =>
        (a.cantiereId===cid || a.cantiere===cant.nome) &&
        (trovaIdxLav(a, lavs /* LAV_BY_ID_v8 */)===i)
      ).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
      const perc    = orePrev>0 ? Math.min(100,Math.round((oreEff/orePrev)*100)) : (oreEff>0?100:0);
      return s + perc*(parseFloat(l.qta)||1);
    }, 0);
    return Math.round(somma/totQ);
  };

  // Ultime ore registrate (ultime 5 voci)
  const ultimeOre = [...oreOp].sort((a,b) => {
    const da = a.data?.split("/").reverse().join("")||"";
    const db2 = b.data?.split("/").reverse().join("")||"";
    return db2.localeCompare(da);
  }).slice(0,5);

  return (
    <div className="content">
      {/* Header personale */}
      <div style={{ background:theme.surface, borderRadius:16, padding:"20px 24px", marginBottom:20, border:`1px solid ${dp?.colore||theme.border}44`, display:"flex", alignItems:"center", gap:18 }}>
        <div style={{ width:56, height:56, borderRadius:"50%", background:dp?.colore||theme.accent, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:"#111", flexShrink:0 }}>
          {nomeOperaio[0]}
        </div>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22 }}>Ciao, {nomeOperaio}! 👋</div>
          <div style={{ fontSize:13, color:theme.textMuted, marginTop:3 }}>
            {oggi.toLocaleDateString("it-IT", { weekday:"long", day:"numeric", month:"long", year:"numeric" })}
          </div>
        </div>
      </div>

      {/* KPI ore */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14, marginBottom:20 }}>
        <div className="card" style={{ padding:"18px 20px", textAlign:"center" }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:36, color:theme.accent }}>{oreMese.toFixed(1)}h</div>
          <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Ore questo mese</div>
          <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>{mese}</div>
        </div>
        <div className="card" style={{ padding:"18px 20px", textAlign:"center" }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:36, color:"#4e9af5" }}>{oreAnno.toFixed(1)}h</div>
          <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginTop:4 }}>Ore anno {anno}</div>
          <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>da inizio anno</div>
        </div>
      </div>

      {/* KPI assenze */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:20 }}>
        {[
          ["Giorni ferie", Math.round(ferieAnno), "🏖️", "#3b82f6"],
          ["Ore permesso", permessiAnno.toFixed(1)+"h", "🕐", "#8b5cf6"],
          ["Giorni mal./inf.", Math.round(malattiaAnno), "🤒", "#f06060"],
        ].map(([label, val, icon, color]) => (
          <div key={label} className="card" style={{ padding:"14px 16px", textAlign:"center" }}>
            <div style={{ fontSize:22, marginBottom:6 }}>{icon}</div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:24, color }}>{val}</div>
            <div style={{ fontSize:11, color:theme.textDim, marginTop:3, textTransform:"uppercase", letterSpacing:1 }}>{label}</div>
            <div style={{ fontSize:11, color:theme.textDim }}>anno {anno}</div>
          </div>
        ))}
      </div>

      {/* I miei cantieri */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-title" style={{ marginBottom:16 }}>🏗️ I miei cantieri attivi</div>
        {cantieriAttivi.length === 0 ? (
          <div style={{ textAlign:"center", padding:"20px 0", color:theme.textDim, fontSize:13 }}>Nessun cantiere attivo al momento.</div>
        ) : cantieriAttivi.map(cant => {
          const avanz = avanzamentoCantiere(cant);
          return (
            <div key={cant._id||cant.id} style={{ padding:"12px 0", borderBottom:`1px solid ${theme.border}44` }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                <div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14 }}>{cant.nome}</div>
                  <div style={{ fontSize:12, color:theme.textMuted, marginTop:2 }}>📍 {cant.indirizzo||"—"} · Fine: {cant.fine||"—"}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:26, color:avanz>80?theme.success:theme.accent }}>{avanz}%</div>
                  <div style={{ fontSize:10, color:theme.textDim }}>completato</div>
                </div>
              </div>
              <div style={{ height:8, background:theme.border, borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${avanz}%`, background:avanz>80?theme.success:avanz>50?theme.accent:theme.info, borderRadius:4, transition:"width 0.4s" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Ultime ore registrate */}
      {ultimeOre.length > 0 && (
        <div className="card">
          <div className="card-title" style={{ marginBottom:14 }}>⏱️ Ultime ore registrate</div>
          {ultimeOre.map((r, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:14, padding:"9px 0", borderBottom:`1px solid ${theme.border}44` }}>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, color:theme.accent, minWidth:36 }}>{r.ore}h</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:500 }}>{r.cantiere||"—"}</div>
                {r.lavorazione && <div style={{ fontSize:11, color:theme.textDim, marginTop:1 }}>{r.lavorazione}</div>}
              </div>
              <div style={{ fontSize:12, color:theme.textDim, whiteSpace:"nowrap" }}>{r.data||"—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── IMPOSTAZIONI ────────────────────────────────────────────────────────────

function Impostazioni({ utente, famiglieProdottoDB = [], listinoMatDB = [] }) { // FIX_RINOMINA_FAMIGLIA_v3
  const [tab, setTab] = useState("utenti"); // "utenti" | "dizionario" | "famiglie"
  const [nuovaFam, setNuovaFam] = useState("");
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPin, setEditPin] = useState({});
  const [saving, setSaving] = useState({});
  const [msg, setMsg] = useState({});

  const [nuovoNome, setNuovoNome] = useState("");
  const [nuovoPin, setNuovoPin] = useState("");
  const [nuovoRuolo, setNuovoRuolo] = useState("operaio");
  const [nuovoColore, setNuovoColore] = useState("#4e9af5");
  const [addingUser, setAddingUser] = useState(false);
  const [costoOrarioEdit, setCostoOrarioEdit] = useState(String(COSTO_ORARIO_GLOBALE));

  // Dizionario lavorazioni — caricato da Firebase
  const [dizionario, setDizionario] = useState({ ...dizionarioMateriali });
  // FIX_DIZIONARIO_GESTIONE_v1: state per sposta sottocategoria
  // formato: "chiaveCategoria|subKey" oppure null
  const [spostaSubKey, setSpostaSubKey] = useState(null);
  const [lavAperta, setLavAperta] = useState(null);
  const [nuovaChiave, setNuovaChiave] = useState("");

  // STEP M3: Modalita organizzative default
  const MOD_DEFAULT_VUOTO = {
    aspetti: {
      recinzione:           { descrizione: "", vedasiAllegato: false },
      viabilita:            { descrizione: "", vedasiAllegato: false },
      depositoMateriali:    { descrizione: "", vedasiAllegato: false },
      depositoAttrezzature: { descrizione: "", vedasiAllegato: false },
      altro:                { descrizione: "", vedasiAllegato: false },
    },
    servizi: {
      prontoSoccorso: { opzione: "", chi: "", note: "" },
      wc:             { opzione: "", chi: "", note: "" },
      lavabi:         { opzione: "", chi: "", note: "" },
      docce:          { opzione: "", chi: "", note: "" },
      spogliatoi:     { opzione: "", chi: "", note: "" },
      altro:          { opzione: "", chi: "", note: "" },
    },
  };
  const [modDefault, setModDefault] = useState(MOD_DEFAULT_VUOTO);
  const [modDefaultOriginale, setModDefaultOriginale] = useState(MOD_DEFAULT_VUOTO);
  const [modDefaultDirty, setModDefaultDirty] = useState(false);
  const modDefaultSaveTimer = React.useRef(null);

  // STEP R2: Rifiuti default
  const RIF_DEFAULT_VUOTO = {
    responsabile: "",
    tipologia: { pericolosi: "", nonPericolosi: "", altro: "" },
    documentazione: {
      formulario:      { stato: "", note: "" },
      registroCarScar: { stato: "", note: "" },
      altro: "",
    },
    misure: {
      refettorio:  { stato: "", note: "" },
      spogliatoio: { stato: "", note: "" },
      baracche:    { stato: "", note: "" },
      ufficio:     { stato: "", note: "" },
      wcChimico:   { stato: "", note: "" },
    },
  };
  const [rifDefault, setRifDefault] = useState(RIF_DEFAULT_VUOTO);
  const [rifDefaultOriginale, setRifDefaultOriginale] = useState(RIF_DEFAULT_VUOTO);
  const [rifDefaultDirty, setRifDefaultDirty] = useState(false);
  const rifDefaultSaveTimer = React.useRef(null);

  const salvaRifDefault = async (data) => {
    try {
      await setDoc(doc(db, "impostazioni", "rifiuti_default"), { ...data, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ "+e.message); }
  };

  const aggiornaRifResp = (valore) => {
    const nuovo = { ...rifDefault, responsabile: valore };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifTip = (campo, valore) => {
    const nuovo = { ...rifDefault, tipologia: { ...rifDefault.tipologia, [campo]: valore } };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifDoc = (key, campo, valore) => {
    let nuovaDoc;
    if (key === "altro") {
      nuovaDoc = { ...rifDefault.documentazione, altro: valore };
    } else {
      nuovaDoc = { ...rifDefault.documentazione, [key]: { ...rifDefault.documentazione[key], [campo]: valore } };
    }
    const nuovo = { ...rifDefault, documentazione: nuovaDoc };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const aggiornaRifMis = (key, campo, valore) => {
    const nuovo = { ...rifDefault, misure: { ...rifDefault.misure, [key]: { ...rifDefault.misure[key], [campo]: valore } } };
    setRifDefault(nuovo);
    setRifDefaultDirty(true);
    if (rifDefaultSaveTimer.current) clearTimeout(rifDefaultSaveTimer.current);
    rifDefaultSaveTimer.current = setTimeout(() => salvaRifDefault(nuovo), 800);
  };

  const salvaModDefault = async (data) => {
    try {
      await setDoc(doc(db, "impostazioni", "modalita_organizzative_default"), { ...data, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ "+e.message); }
  };

  const aggiornaAspetto = (key, campo, valore) => {
    const nuovo = { ...modDefault, aspetti: { ...modDefault.aspetti, [key]: { ...modDefault.aspetti[key], [campo]: valore } } };
    setModDefault(nuovo);
    setModDefaultDirty(true);
    if (modDefaultSaveTimer.current) clearTimeout(modDefaultSaveTimer.current);
    modDefaultSaveTimer.current = setTimeout(() => salvaModDefault(nuovo), 800);
  };

  const aggiornaServizio = (key, campo, valore) => {
    const nuovo = { ...modDefault, servizi: { ...modDefault.servizi, [key]: { ...modDefault.servizi[key], [campo]: valore } } };
    setModDefault(nuovo);
    setModDefaultDirty(true);
    if (modDefaultSaveTimer.current) clearTimeout(modDefaultSaveTimer.current);
    modDefaultSaveTimer.current = setTimeout(() => salvaModDefault(nuovo), 800);
  };

  // STEP A: Macchine e attrezzature — caricato da Firebase
  const [macchineAttr, setMacchineAttr] = useState([]);
  const [nuovaMacchinaNome, setNuovaMacchinaNome] = useState("");
  const [nuovaMacchinaLibretto, setNuovaMacchinaLibretto] = useState(false);
  const [nuovaMacchinaCollaudo, setNuovaMacchinaCollaudo] = useState(false);
  const [nuovaMacchinaVerifiche, setNuovaMacchinaVerifiche] = useState(false);
  const [nuovaMacchinaUsoComune, setNuovaMacchinaUsoComune] = useState(false);
  const [editingMacchinaId, setEditingMacchinaId] = useState(null);

  const salvaMacchine = async (lista) => {
    try {
      await setDoc(doc(db, "impostazioni", "macchine_attrezzature"), { voci: lista, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ "+e.message); }
  };

  // STEP_A2: upload/delete file per macchina
  const uploadDocMacchina = async (macchinaId, tipo, file) => {
    try {
      const token = localStorage.getItem('bertoc_jwt');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('macchinaId', macchinaId);
      fd.append('tipo', tipo);
      const r = await fetch('/api/macchine/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ('HTTP ' + r.status));
      }
      const data = await r.json();
      // Aggiorna l'oggetto macchina in Firestore: aggiungi filename a documenti[tipo]
      const lista = macchineAttr.map(m => {
        if (m.id !== macchinaId) return m;
        const docs = m.documenti || {};
        const arr = Array.isArray(docs[tipo]) ? docs[tipo] : [];
        if (arr.includes(data.filename)) return m;
        return { ...m, documenti: { ...docs, [tipo]: [...arr, data.filename] } };
      });
      await salvaMacchine(lista);
      return true;
    } catch (e) {
      alert("❌ Upload fallito: " + e.message);
      return false;
    }
  };

  const deleteDocMacchina = async (macchinaId, tipo, filename) => {
    if (!window.confirm(`Eliminare "${filename}"?`)) return;
    try {
      const token = localStorage.getItem('bertoc_jwt');
      const url = '/api/macchine/file?macchinaId=' + encodeURIComponent(macchinaId)
                + '&tipo=' + encodeURIComponent(tipo)
                + '&filename=' + encodeURIComponent(filename);
      const r = await fetch(url, {
        method: 'DELETE',
        headers: { 'Authorization': 'Bearer ' + token },
      });
      if (!r.ok && r.status !== 404) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || ('HTTP ' + r.status));
      }
      // Rimuovi dal Firestore
      const lista = macchineAttr.map(m => {
        if (m.id !== macchinaId) return m;
        const docs = m.documenti || {};
        const arr = (Array.isArray(docs[tipo]) ? docs[tipo] : []).filter(f => f !== filename);
        return { ...m, documenti: { ...docs, [tipo]: arr } };
      });
      await salvaMacchine(lista);
    } catch (e) {
      alert("❌ " + e.message);
    }
  };
  const salvaFamiglie = async (lista) => {
    try {
      await setDoc(doc(db, "impostazioni", "famiglie_prodotto"), { voci: lista, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ "+e.message); }
  };
  const [salvandoDiz, setSalvandoDiz] = useState(false);

  const inputSt = {
    background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 8,
    color: theme.text, padding: "8px 12px", fontSize: 13,
    fontFamily: "'DM Sans',sans-serif", outline: "none", width: "100%"
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "utenti"), snap => {
      setUtenti(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
      setLoading(false);
    });
    // Carica dizionario da Firebase
    // STEP R2: Carica rifiuti_default
    const unsubRif = onSnapshot(doc(db, "impostazioni", "rifiuti_default"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        const merged = {
          responsabile: data.responsabile || "",
          tipologia: { ...RIF_DEFAULT_VUOTO.tipologia, ...(data.tipologia || {}) },
          documentazione: { ...RIF_DEFAULT_VUOTO.documentazione, ...(data.documentazione || {}) },
          misure: { ...RIF_DEFAULT_VUOTO.misure, ...(data.misure || {}) },
        };
        setRifDefault(merged);
        setRifDefaultOriginale(merged);
        setRifDefaultDirty(false);
      }
    });

    // STEP M3: Carica modalita_organizzative_default
    const unsubMod = onSnapshot(doc(db, "impostazioni", "modalita_organizzative_default"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        const merged = {
          aspetti: { ...MOD_DEFAULT_VUOTO.aspetti, ...(data.aspetti || {}) },
          servizi: { ...MOD_DEFAULT_VUOTO.servizi, ...(data.servizi || {}) },
        };
        setModDefault(merged);
        setModDefaultOriginale(merged);
        setModDefaultDirty(false);
      }
    });

    // STEP A: Carica macchine_attrezzature
    const unsubMacchine = onSnapshot(doc(db, "impostazioni", "macchine_attrezzature"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setMacchineAttr(Array.isArray(data.voci) ? data.voci : []);
      } else {
        setMacchineAttr([]);
      }
    });
    const unsubDiz = onSnapshot(doc(db, "impostazioni", "dizionario"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setDizionario(data.voci || { ...dizionarioMateriali });
        // Aggiorna variabile globale
        Object.assign(dizionarioMateriali, data.voci || {});
      }
    }, () => {});
    // Carica costo orario da Firebase
    const unsubParams = onSnapshot(doc(db, "impostazioni", "params"), snap => {
      if (snap.exists() && snap.data().voci?.costoOraio) {
        const val = parseFloat(snap.data().voci.costoOraio)||25;
        COSTO_ORARIO_GLOBALE = val;
        setCostoOrarioEdit(String(val));
      }
    }, () => {});
    return () => { unsub(); unsubDiz(); unsubMacchine(); unsubMod(); unsubRif(); unsubParams(); };
  }, []);

  const salvaDizionario = async (nuovoDiz) => {
    setSalvandoDiz(true);
    try {
      await setDoc(doc(db, "impostazioni", "dizionario"), { voci: nuovoDiz, updatedAt: new Date().toISOString() });
      // Aggiorna variabile globale
      Object.keys(dizionarioMateriali).forEach(k => delete dizionarioMateriali[k]);
      Object.assign(dizionarioMateriali, nuovoDiz);
    } catch(e) { alert("❌ "+e.message); }
    setSalvandoDiz(false);
  };

  const salvaPIN = async (u) => {
    const nuovoPinVal = editPin[u._id];
    if (!nuovoPinVal || nuovoPinVal.length < 4) {
      setMsg({ [u._id]: "⚠️ PIN deve essere di almeno 4 cifre" });
      return;
    }
    setSaving({ [u._id]: true });
    try {
      await updateDoc(doc(db, "utenti", u._id), { pin: nuovoPinVal });
      setMsg({ [u._id]: "✅ PIN aggiornato!" });
      setEditPin({ ...editPin, [u._id]: "" });
    } catch(e) {
      setMsg({ [u._id]: "❌ Errore: " + e.message });
    }
    setSaving({ [u._id]: false });
    setTimeout(() => setMsg({}), 3000);
  };

  const aggiungiUtente = async () => {
    if (!nuovoNome || !nuovoPin || nuovoPin.length < 4) {
      alert("Nome e PIN (min 4 cifre) sono obbligatori");
      return;
    }
    try {
      await addDoc(collection(db, "utenti"), {
        nome: nuovoNome, pin: nuovoPin, ruolo: nuovoRuolo,
        colore: nuovoColore,
        createdAt: new Date().toISOString()
      });
      setNuovoNome(""); setNuovoPin(""); setNuovoRuolo("operaio"); setNuovoColore("#4e9af5");
      setAddingUser(false);
    } catch(e) {
      alert("❌ Errore: " + e.message);
    }
  };

  const eliminaUtente = async (u) => {
    if (u.nome === utente?.nome) { alert("Non puoi eliminare te stesso!"); return; }
    if (!window.confirm(`Eliminare ${u.nome}?`)) return;
    await deleteDoc(doc(db, "utenti", u._id));
  };

  if (utente?.ruolo !== "titolare" && utente?.nome !== "Edoardo") {
    return <div className="content"><div style={{ color: theme.danger, padding: 20 }}>⛔ Accesso non autorizzato.</div></div>;
  }

  if (loading) return <div className="content"><div style={{ color: theme.textMuted }}>Caricamento...</div></div>;

  return (
    <div className="content">

      <AgganciaOreId />
      <div className="tabs" style={{ marginBottom:20 }}>
        <div className={`tab ${tab==="utenti"?"active":""}`} onClick={()=>setTab("utenti")}>👥 Utenti e PIN</div>
        <div className={`tab ${tab==="dizionario"?"active":""}`} onClick={()=>setTab("dizionario")}>🔧 Dizionario lavorazioni</div>
        <div className={`tab ${tab==="famiglie"?"active":""}`} onClick={()=>setTab("famiglie")}>🏷️ Famiglie prodotto</div>
        {/* FIX_NASCONDI_TAB_IMPOSTAZIONI_v1 — tab spostati in Sicurezza Azienda, nascosti qui (form sottostanti ancora presenti, dormienti)
        <div className={`tab ${tab==="macchine"?"active":""}`} onClick={()=>setTab("macchine")}>🛠️ Macchine attrezzature</div>
        <div className={`tab ${tab==="modalita_default"?"active":""}`} onClick={()=>setTab("modalita_default")}>🔧 Modalità organizzative</div>
        <div className={`tab ${tab==="rifiuti_default"?"active":""}`} onClick={()=>setTab("rifiuti_default")}>🗑️ Gestione rifiuti</div>
        */}
      </div>

      {/* ── TAB UTENTI ── */}
      {tab === "utenti" && (
      <div>
      {/* Costo orario */}
      <div className="card" style={{ maxWidth:600, marginBottom:16 }}>
        <div className="card-title" style={{ marginBottom:14 }}>💰 Parametri aziendali</div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Costo orario operaio (€/h)</div>
            <div style={{ fontSize:12, color:theme.textMuted }}>Usato per calcolare margini nel consuntivo, statistiche e barre avanzamento costi.</div>
          </div>
          <input type="number" style={{...inputSt, width:100, textAlign:"center", fontSize:18, fontWeight:700}}
            value={costoOrarioEdit}
            onChange={e => setCostoOrarioEdit(e.target.value)}
            onBlur={async () => {
              const val = parseFloat(costoOrarioEdit)||25;
              COSTO_ORARIO_GLOBALE = val;
              setCostoOrarioEdit(String(val));
              try {
                const snap = await getDoc(doc(db,"impostazioni","params"));
                const existing = snap.exists() ? (snap.data().voci||{}) : {};
                await setDoc(doc(db,"impostazioni","params"), { voci:{...existing, costoOraio:val}, updatedAt:new Date().toISOString() });
              } catch(e) { console.error("costo orario:", e.message); }
            }}
          />
          <span style={{ fontSize:13, color:theme.textMuted }}>€/h</span>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 600 }}>
        <div className="card-header" style={{ marginBottom: 20 }}>
          <div className="card-title">👥 Gestione utenti e PIN</div>
          <button className="btn btn-primary" onClick={() => setAddingUser(!addingUser)}>+ Nuovo utente</button>
        </div>

        {addingUser && (
          <div style={{ background: theme.bg, borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${theme.border}` }}>
            <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 14 }}>Nuovo utente</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 4 }}>Nome *</div>
                <input style={inputSt} placeholder="es. Mario" value={nuovoNome} onChange={e => setNuovoNome(e.target.value)} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 4 }}>PIN * (min 4 cifre)</div>
                <input style={inputSt} placeholder="es. 1234" value={nuovoPin} onChange={e => setNuovoPin(e.target.value.replace(/\D/g,""))} maxLength={8} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 4 }}>Ruolo</div>
                <select style={inputSt} value={nuovoRuolo} onChange={e => setNuovoRuolo(e.target.value)}>
                  <option value="titolare">Titolare</option>
                  <option value="operaio">Operaio</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 4 }}>Colore avatar</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {["#f5a623","#3ecf8e","#4e9af5","#f06060","#a78bfa","#34d399","#60c0f0","#e09520","#f59e0b","#ec4899"].map(col => (
                    <div key={col} onClick={() => setNuovoColore(col)}
                      style={{ width: 28, height: 28, borderRadius: "50%", background: col, cursor: "pointer",
                        border: nuovoColore === col ? "3px solid #fff" : "3px solid transparent",
                        boxShadow: nuovoColore === col ? `0 0 0 2px ${col}` : "none" }} />
                  ))}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" onClick={aggiungiUtente}>✓ Aggiungi</button>
              <button className="btn btn-ghost" onClick={() => setAddingUser(false)}>Annulla</button>
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {utenti.map(u => (
            <div key={u._id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: theme.bg, borderRadius: 10, border: `1px solid ${theme.border}` }}>
              <div className="avatar" style={{ background: u.colore || "#888", flexShrink: 0 }}>{u.nome?.[0]}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{u.nome}</div>
                <div style={{ fontSize: 12, color: theme.textMuted }}>{u.ruolo} {u.nome === utente?.nome ? "— (tu)" : ""}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", maxWidth:200 }}>
                {["#f5a623","#3ecf8e","#4e9af5","#f06060","#a78bfa","#34d399","#60c0f0","#e09520","#f59e0b","#ec4899"].map(col => (
                  <div key={col} onClick={async () => { try { await updateDoc(doc(db,"utenti",u._id),{colore:col}); } catch(e){} }}
                    style={{ width:22, height:22, borderRadius:"50%", background:col, cursor:"pointer",
                      border: u.colore===col ? "3px solid #fff" : "2px solid transparent",
                      boxShadow: u.colore===col ? `0 0 0 2px ${col}` : "none" }} />
                ))}
              </div>
              <input
                style={{ ...inputSt, width: 110, textAlign: "center", letterSpacing: 4 }}
                placeholder="Nuovo PIN"
                value={editPin[u._id] || ""}
                onChange={e => setEditPin({ ...editPin, [u._id]: e.target.value.replace(/\D/g,"") })}
                maxLength={8}
                type="password"
              />
              <button className="btn btn-primary btn-sm" onClick={() => salvaPIN(u)} disabled={saving[u._id]}>
                {saving[u._id] ? "..." : "Salva"}
              </button>
              {u.nome !== utente?.nome && (
                <button className="btn btn-ghost btn-sm" style={{ color: theme.danger }} onClick={() => eliminaUtente(u)}>✕</button>
              )}
              {msg[u._id] && <span style={{ fontSize: 12, color: theme.success }}>{msg[u._id]}</span>}
            </div>
          ))}
        </div>
      </div>
      </div>
      )}

      {/* ── TAB FAMIGLIE PRODOTTO ── */}
      {tab === "famiglie" && (
        <div className="card" style={{ maxWidth:700 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, marginBottom:6 }}>🏷️ Famiglie prodotto</div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
            Collegano i materiali del dizionario ai prodotti del listino. Es: famiglia "pittura interna" include OKAY EXTRA, SILOXAN, ecc.
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:16 }}>
            <input style={{ flex:1, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, color:theme.text, padding:"8px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" }}
              placeholder="es. pittura interna, primer, lastra ba13..."
              value={nuovaFam}
              onChange={e=>setNuovaFam(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter" && nuovaFam.trim()) { if(famiglieProdottoDB.includes(nuovaFam.trim().toLowerCase())){ alert("Famiglia già esistente!"); return; } const n=[...famiglieProdottoDB, nuovaFam.trim().toLowerCase()].sort(); salvaFamiglie(n); setNuovaFam(""); }}}
            />
            <button className="btn btn-primary btn-sm" onClick={()=>{ if(!nuovaFam.trim()) return; if(famiglieProdottoDB.includes(nuovaFam.trim().toLowerCase())){ alert("Famiglia già esistente!"); return; } const n=[...famiglieProdottoDB, nuovaFam.trim().toLowerCase()].sort(); salvaFamiglie(n); setNuovaFam(""); }}>
              + Aggiungi
            </button>
          </div>
          {famiglieProdottoDB.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 0", color:theme.textDim, fontSize:13 }}>Nessuna famiglia. Aggiungine una per iniziare.</div>
          ) : (
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {famiglieProdottoDB.sort((a,b)=>a.localeCompare(b)).map(f=>(
                <div key={f} style={{ display:"flex", alignItems:"center", gap:6, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:20, padding:"5px 12px" }}>
                  <span style={{ fontSize:13, color:theme.accent }}>🏷️ {f}</span>
                  {/* FIX_RINOMINA_FAMIGLIA_v1: bottone Rinomina con propagazione */}
                  <button onClick={async ()=>{
                    const nuovo = window.prompt(`Nuovo nome per la famiglia "${f}":`, f);
                    if (nuovo === null) return;
                    const nuovoTrim = nuovo.trim();
                    if (!nuovoTrim) { alert("Nome non valido."); return; }
                    if (nuovoTrim === f) return;
                    if (famiglieProdottoDB.includes(nuovoTrim)) {
                      alert(`La famiglia "${nuovoTrim}" esiste già. Per unirle, elimina prima la duplicata.`);
                      return;
                    }
                    // FIX_RINOMINA_FAMIGLIA_v3: usa prop listinoMatDB
                    const fLow = f.toLowerCase().trim();
                    const prodottiCount = listinoMatDB.filter(p => (p.categoriaP||"").toLowerCase().trim() === fLow).length;
                    let materialiCount = 0;
                    Object.values(dizionario||{}).forEach(cat => {
                      (cat.materiali||[]).forEach(m => { if (typeof m === "object" && (m.categoriaP||"").toLowerCase().trim() === fLow) materialiCount++; });
                      Object.values(cat.sottocategorie||{}).forEach(sub => {
                        (sub.materiali||[]).forEach(m => { if (typeof m === "object" && (m.categoriaP||"").toLowerCase().trim() === fLow) materialiCount++; });
                      });
                    });
                    const dettagli = [];
                    if (prodottiCount > 0) dettagli.push(`${prodottiCount} prodotti del listino`);
                    if (materialiCount > 0) dettagli.push(`${materialiCount} materiali del dizionario`);
                    const msg = dettagli.length
                      ? `Rinomino "${f}" in "${nuovoTrim}" e aggiorno ${dettagli.join(" + ")}.\n\nContinuo?`
                      : `Rinomino "${f}" in "${nuovoTrim}".\n\nNessun prodotto/materiale collegato. Continuo?`;
                    if (!window.confirm(msg)) return;
                    try {
                      // 1) Aggiorno la lista famiglie
                      const nuoveFam = famiglieProdottoDB.map(x => x === f ? nuovoTrim : x).sort((a,b)=>a.localeCompare(b));
                      await salvaFamiglie(nuoveFam);
                      // 2) Propago nei prodotti del listino
                      for (const p of listinoMatDB) {
                        if ((p.categoriaP||"").toLowerCase().trim() === fLow) {
                          await updateDoc(doc(db, "listino_materiali", p._id), { categoriaP: nuovoTrim, aggiornato: new Date().toISOString() });
                        }
                      }
                      // 3) Propago nel dizionario lavorazioni
                      let dizModificato = false;
                      const nuovoDiz = JSON.parse(JSON.stringify(dizionario));
                      Object.keys(nuovoDiz).forEach(catKey => {
                        const cat = nuovoDiz[catKey];
                        (cat.materiali||[]).forEach(m => {
                          if (typeof m === "object" && (m.categoriaP||"").toLowerCase().trim() === fLow) {
                            m.categoriaP = nuovoTrim; dizModificato = true;
                          }
                        });
                        Object.values(cat.sottocategorie||{}).forEach(sub => {
                          (sub.materiali||[]).forEach(m => {
                            if (typeof m === "object" && (m.categoriaP||"").toLowerCase().trim() === fLow) {
                              m.categoriaP = nuovoTrim; dizModificato = true;
                            }
                          });
                        });
                      });
                      if (dizModificato) { setDizionario(nuovoDiz); await salvaDizionario(nuovoDiz); }
                      alert(`✓ Famiglia rinominata in "${nuovoTrim}".`);
                    } catch(err) {
                      console.error("Errore rinomina famiglia:", err);
                      alert("Errore durante la rinomina: " + err.message);
                    }
                  }} title="Rinomina (propaga in listino e dizionario)"
                    style={{ background:"none", border:"none", color:theme.accent, cursor:"pointer", fontSize:13 }}>✎</button>
                  <button onClick={()=>{ if(!window.confirm(`Eliminare "${f}"?`)) return; salvaFamiglie(famiglieProdottoDB.filter(x=>x!==f)); }}
                    style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:13 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB MACCHINE E ATTREZZATURE (Step A) ── */}
      {tab === "macchine" && (
        <div className="card" style={{ maxWidth:900 }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15, marginBottom:6 }}>🛠️ Macchine, attrezzature e impianti</div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
            Configura le macchine e attrezzature di Bertoc Project. Le voci appariranno nel modal "Genera POS" per essere flaggate quando presenti in un cantiere specifico.
          </div>

          {/* Form aggiungi nuova */}
          <div style={{ background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, padding:14, marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>
              {editingMacchinaId ? "✎ Modifica attrezzatura" : "+ Aggiungi attrezzatura"}
            </div>
            <input style={{...inputSt, marginBottom:10}}
              placeholder="es. Molatrice portatile, Avvitatore elettrico, Trabattello..."
              value={nuovaMacchinaNome}
              onChange={e=>setNuovaMacchinaNome(e.target.value)}
            />
            <div style={{ display:"flex", flexWrap:"wrap", gap:14, marginBottom:12 }}>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                <input type="checkbox" checked={nuovaMacchinaLibretto} onChange={e=>setNuovaMacchinaLibretto(e.target.checked)} />
                Libretto disponibile
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                <input type="checkbox" checked={nuovaMacchinaCollaudo} onChange={e=>setNuovaMacchinaCollaudo(e.target.checked)} />
                Soggetto a collaudo
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                <input type="checkbox" checked={nuovaMacchinaVerifiche} onChange={e=>setNuovaMacchinaVerifiche(e.target.checked)} />
                Verifiche periodiche
              </label>
              <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:13, cursor:"pointer" }}>
                <input type="checkbox" checked={nuovaMacchinaUsoComune} onChange={e=>setNuovaMacchinaUsoComune(e.target.checked)} />
                Uso comune con altre imprese
              </label>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-primary btn-sm" onClick={()=>{
                if (!nuovaMacchinaNome.trim()) return;
                const trimmed = nuovaMacchinaNome.trim();
                if (editingMacchinaId) {
                  const updated = macchineAttr.map(m => m.id === editingMacchinaId
                    ? { ...m, nome: trimmed, libretto: nuovaMacchinaLibretto, collaudo: nuovaMacchinaCollaudo, verifiche: nuovaMacchinaVerifiche, usoComune: nuovaMacchinaUsoComune }
                    : m
                  );
                  salvaMacchine(updated);
                  setEditingMacchinaId(null);
                } else {
                  const exists = macchineAttr.some(m => m.nome.toLowerCase() === trimmed.toLowerCase());
                  if (exists) { alert("Attrezzatura già esistente!"); return; }
                  const newId = "macchina_" + Date.now() + "_" + Math.random().toString(36).slice(2, 7);
                  const ordine = macchineAttr.length + 1;
                  const updated = [...macchineAttr, { id: newId, nome: trimmed, libretto: nuovaMacchinaLibretto, collaudo: nuovaMacchinaCollaudo, verifiche: nuovaMacchinaVerifiche, usoComune: nuovaMacchinaUsoComune, ordine }];
                  salvaMacchine(updated);
                }
                setNuovaMacchinaNome("");
                setNuovaMacchinaLibretto(false);
                setNuovaMacchinaCollaudo(false);
                setNuovaMacchinaVerifiche(false);
                setNuovaMacchinaUsoComune(false);
              }}>{editingMacchinaId ? "💾 Salva modifiche" : "+ Aggiungi"}</button>
              {editingMacchinaId && (
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  setEditingMacchinaId(null);
                  setNuovaMacchinaNome("");
                  setNuovaMacchinaLibretto(false);
                  setNuovaMacchinaCollaudo(false);
                  setNuovaMacchinaVerifiche(false);
                  setNuovaMacchinaUsoComune(false);
                }}>Annulla</button>
              )}
            </div>

            {/* STEP_A2: sezione documenti (solo in modalita' modifica) */}
            {editingMacchinaId && (() => {
              const m = macchineAttr.find(x => x.id === editingMacchinaId);
              if (!m) return null;
              const docs = m.documenti || {};
              const sezioni = [
                { tipo: 'libretto',  label: '📘 Libretto',                 visibile: true },
                { tipo: 'collaudo',  label: '✅ Collaudo',                 visibile: true },
                { tipo: 'verifiche', label: '🔄 Verifiche periodiche',    visibile: true },
              ];
              return (
                <div style={{ marginTop:16, paddingTop:14, borderTop:`1px dashed ${theme.border}` }}>
                  <div style={{ fontSize:12, fontWeight:600, color:theme.textMuted, marginBottom:10, textTransform:"uppercase", letterSpacing:1 }}>
                    📎 Documenti caricati
                  </div>
                  {sezioni.map(s => {
                    const files = Array.isArray(docs[s.tipo]) ? docs[s.tipo] : [];
                    return (
                      <div key={s.tipo} style={{ marginBottom:14, background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8, padding:"10px 12px" }}>
                        <div style={{ fontSize:13, fontWeight:600, marginBottom:8, color:theme.text }}>{s.label}</div>
                        {files.length === 0 ? (
                          <div style={{ fontSize:12, color:theme.textDim, fontStyle:"italic", marginBottom:8 }}>(nessun documento)</div>
                        ) : (
                          <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:8 }}>
                            {files.map(fn => (
                              <div key={fn} style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, color:theme.text }}>
                                <span style={{ flex:1 }}>📄 {fn}</span>
                                <a href={`/api/macchine/file?macchinaId=${encodeURIComponent(m.id)}&tipo=${s.tipo}&filename=${encodeURIComponent(fn)}`}
                                  target="_blank" rel="noreferrer"
                                  style={{ color:theme.accent, fontSize:11, textDecoration:"none" }}
                                  onClick={async (e) => {
                                    // Inietta token come query param? No, fetch+blob
                                    e.preventDefault();
                                    const token = localStorage.getItem('bertoc_jwt');
                                    const r = await fetch(e.currentTarget.href, { headers: { 'Authorization': 'Bearer ' + token } });
                                    if (!r.ok) { alert("Errore caricamento file"); return; }
                                    const blob = await r.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  }}>👁️ Apri</a>
                                <button onClick={() => deleteDocMacchina(m.id, s.tipo, fn)}
                                  style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:13 }}>✕</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <label style={{
                          display:"inline-block", padding:"6px 12px", borderRadius:6,
                          background:theme.surface, border:`1px solid ${theme.border}`,
                          cursor:"pointer", fontSize:12, color:theme.textMuted,
                        }}>
                          + Carica PDF
                          <input type="file" accept="application/pdf" style={{ display:"none" }}
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (file.size > 10 * 1024 * 1024) { alert("File troppo grande (max 10MB)"); return; }
                              await uploadDocMacchina(m.id, s.tipo, file);
                              e.target.value = "";  // reset input
                            }} />
                        </label>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Lista attrezzature */}
          <div style={{ fontSize:13, fontWeight:600, marginBottom:10, color:theme.textMuted }}>
            📋 Elenco attuale ({macchineAttr.length} {macchineAttr.length === 1 ? "voce" : "voci"})
          </div>
          {macchineAttr.length === 0 ? (
            <div style={{ textAlign:"center", padding:"24px 0", color:theme.textDim, fontSize:13 }}>
              Nessuna attrezzatura. Aggiungine una per iniziare.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {macchineAttr.slice().sort((a,b) => (a.ordine||0) - (b.ordine||0)).map(m => (
                <div key={m.id} style={{
                  display:"flex", alignItems:"center", gap:10, padding:"8px 12px",
                  background:theme.bg, border:`1px solid ${theme.border}`, borderRadius:8,
                }}>
                  <div style={{ flex:1, fontSize:13, color:theme.text, fontWeight:500 }}>🔧 {m.nome}</div>
                  <div style={{ display:"flex", gap:8, fontSize:11, color:theme.textMuted }}>
                    {m.libretto && <span title="Libretto">📘</span>}
                    {m.collaudo && <span title="Collaudo">✅</span>}
                    {m.verifiche && <span title="Verifiche periodiche">🔄</span>}
                    {m.usoComune && <span title="Uso comune">👥</span>}
                  </div>
                  <button onClick={()=>{
                    setEditingMacchinaId(m.id);
                    setNuovaMacchinaNome(m.nome);
                    setNuovaMacchinaLibretto(!!m.libretto);
                    setNuovaMacchinaCollaudo(!!m.collaudo);
                    setNuovaMacchinaVerifiche(!!m.verifiche);
                    setNuovaMacchinaUsoComune(!!m.usoComune);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }} style={{ background:"none", border:"none", color:theme.accent, cursor:"pointer", fontSize:14 }}>✎</button>
                  <button onClick={async ()=>{
                    if (!window.confirm(`Eliminare "${m.nome}"?\n\nI documenti caricati saranno spostati nel cestino del NAS.`)) return;
                    try {
                      const token = localStorage.getItem('bertoc_jwt');
                      const r = await fetch('/api/macchine/cartella?macchinaId=' + encodeURIComponent(m.id), {
                        method: 'DELETE',
                        headers: { 'Authorization': 'Bearer ' + token },
                      });
                      if (!r.ok && r.status !== 404) {
                        console.warn('[MACCHINE] Cestino HTTP', r.status, '- procedo comunque');
                      }
                    } catch (e) {
                      console.warn('[MACCHINE] Errore cestino:', e.message);
                    }
                    salvaMacchine(macchineAttr.filter(x=>x.id !== m.id));
                  }} style={{ background:"none", border:"none", color:theme.danger, cursor:"pointer", fontSize:14 }}>✕</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB MODALITA ORGANIZZATIVE DEFAULT (Step M3) ── */}
      {tab === "modalita_default" && (
        <div style={{ maxWidth: 900 }}>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15 }}>🔧 Modalità organizzative — default Bertoc</div>
              <button className="btn btn-ghost btn-sm" disabled={!modDefaultDirty}
                onClick={() => {
                  if (!window.confirm("Annullare le modifiche e ripristinare l'ultimo salvataggio?")) return;
                  setModDefault(modDefaultOriginale);
                  setModDefaultDirty(false);
                  salvaModDefault(modDefaultOriginale);
                }}
                style={{ opacity: modDefaultDirty ? 1 : 0.4 }}
              >↶ Annulla modifiche</button>
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              Imposta i valori predefiniti proposti quando generi un POS. Potrai modificarli per ogni cantiere singolarmente. Salvataggio automatico.
            </div>
          </div>

          {/* ASPETTI GENERALI */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Aspetti generali del cantiere
            </div>
            {[
              { key: "recinzione",           label: "Recinzione area di cantiere" },
              { key: "viabilita",            label: "Viabilità ed accessi di cantiere" },
              { key: "depositoMateriali",    label: "Aree di deposito materiali" },
              { key: "depositoAttrezzature", label: "Aree di deposito attrezzature" },
              { key: "altro",                label: "Altro (specificare)" },
            ].map(r => {
              const val = modDefault.aspetti[r.key] || { descrizione: "", vedasiAllegato: false };
              return (
                <div key={r.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{r.label}</div>
                  <textarea style={{ ...inputSt, minHeight: 50, resize: "vertical", marginBottom: 6 }}
                    placeholder="Descrizione (es. Si rimanda al PSC, A cura impresa esecutrice, ...)"
                    value={val.descrizione}
                    onChange={e => aggiornaAspetto(r.key, "descrizione", e.target.value)} />
                  <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={val.vedasiAllegato}
                      onChange={e => aggiornaAspetto(r.key, "vedasiAllegato", e.target.checked)} />
                    Vedasi documentazione allegata
                  </label>
                </div>
              );
            })}
          </div>

          {/* SERVIZI IGIENICO-ASSISTENZIALI */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Servizi igienico-assistenziali
            </div>
            {[
              { key: "prontoSoccorso", label: "Cassetta Pronto Soccorso" },
              { key: "wc",             label: "WC" },
              { key: "lavabi",         label: "Lavabi" },
              { key: "docce",          label: "Docce" },
              { key: "spogliatoi",     label: "Spogliatoi" },
              { key: "altro",          label: "Altro (specificare)" },
            ].map(r => {
              const val = modDefault.servizi[r.key] || { opzione: "", chi: "", note: "" };
              return (
                <div key={r.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{r.label}</div>
                  {[
                    { v: "committente",    l: "Messo a disposizione da", chiVis: true },
                    { v: "non_pertinente", l: "Non pertinente",          chiVis: false },
                    { v: "accordo",        l: "Organizzato in accordo con", chiVis: true },
                    { v: "impresa",        l: "Da installarsi a cura impresa esecutrice", chiVis: false },
                  ].map(o => (
                    <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 4, cursor: "pointer" }}>
                      <input type="radio" name={`opt_${r.key}`} checked={val.opzione === o.v}
                        onChange={() => aggiornaServizio(r.key, "opzione", o.v)} />
                      <span>{o.l}</span>
                      {o.chiVis && val.opzione === o.v && (
                        <input style={{ ...inputSt, width: 280, padding: "4px 8px", marginLeft: 8 }}
                          placeholder="Specificare chi..."
                          value={val.chi}
                          onClick={e => e.stopPropagation()}
                          onChange={e => aggiornaServizio(r.key, "chi", e.target.value)} />
                      )}
                    </label>
                  ))}
                  <input style={{ ...inputSt, marginTop: 6 }}
                    placeholder="Note (opzionale)"
                    value={val.note}
                    onChange={e => aggiornaServizio(r.key, "note", e.target.value)} />
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 11, color: theme.textDim, textAlign: "center", padding: "8px 0" }}>
            {modDefaultDirty ? "💾 Salvataggio in corso..." : "✓ Tutti i dati salvati"}
          </div>
        </div>
      )}

      {/* ── TAB GESTIONE RIFIUTI DEFAULT (Step R2) ── */}
      {tab === "rifiuti_default" && (
        <div style={{ maxWidth: 900 }}>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15 }}>🗑️ Gestione rifiuti — default Bertoc</div>
              <button className="btn btn-ghost btn-sm" disabled={!rifDefaultDirty}
                onClick={() => {
                  if (!window.confirm("Annullare le modifiche e ripristinare l'ultimo salvataggio?")) return;
                  setRifDefault(rifDefaultOriginale);
                  setRifDefaultDirty(false);
                  salvaRifDefault(rifDefaultOriginale);
                }}
                style={{ opacity: rifDefaultDirty ? 1 : 0.4 }}
              >↶ Annulla modifiche</button>
            </div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>
              Imposta i valori predefiniti proposti quando generi un POS. Salvataggio automatico.
            </div>
          </div>

          {/* Responsabile */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Responsabile della gestione dei rifiuti</div>
            <input style={inputSt} placeholder="es. Marco Bertocci"
              value={rifDefault.responsabile}
              onChange={e => aggiornaRifResp(e.target.value)} />
          </div>

          {/* TIPOLOGIA */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Tipologia di rifiuti prodotti
            </div>
            {[
              { key: "pericolosi",    label: "Pericolosi" },
              { key: "nonPericolosi", label: "Non pericolosi" },
            ].map(r => {
              const val = rifDefault.tipologia[r.key] || "";
              return (
                <div key={r.key} style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 12, fontWeight: 600, minWidth: 130 }}>{r.label}</div>
                  {[
                    { v: "si",          l: "SI" },
                    { v: "no",          l: "NO" },
                    { v: "da_definire", l: "Da definire" },
                  ].map(o => (
                    <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                      <input type="radio" name={`tip_${r.key}`} checked={val === o.v}
                        onChange={() => aggiornaRifTip(r.key, o.v)} />
                      <span>{o.l}</span>
                    </label>
                  ))}
                </div>
              );
            })}
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Altro (specificare)</div>
              <input style={inputSt} placeholder="es. rifiuti speciali..."
                value={rifDefault.tipologia.altro}
                onChange={e => aggiornaRifTip("altro", e.target.value)} />
            </div>
          </div>

          {/* DOCUMENTAZIONE */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Documentazione presente in cantiere
            </div>
            {[
              { key: "formulario",      label: "Formulario" },
              { key: "registroCarScar", label: "Registro di carico e scarico" },
            ].map(r => {
              const val = rifDefault.documentazione[r.key] || { stato: "", note: "" };
              return (
                <div key={r.key} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{r.label}</div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
                    {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                      <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                        <input type="radio" name={`doc_${r.key}`} checked={val.stato === o.v}
                          onChange={() => aggiornaRifDoc(r.key, "stato", o.v)} />
                        <span>{o.l}</span>
                      </label>
                    ))}
                  </div>
                  <input style={inputSt} placeholder="Note (opz.)"
                    value={val.note}
                    onChange={e => aggiornaRifDoc(r.key, "note", e.target.value)} />
                </div>
              );
            })}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Altro (specificare)</div>
              <input style={inputSt} placeholder="es. registro storico..."
                value={rifDefault.documentazione.altro || ""}
                onChange={e => aggiornaRifDoc("altro", null, e.target.value)} />
            </div>
          </div>

          {/* MISURE */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
              Misure tecniche per la gestione dei rifiuti
            </div>
            {[
              { key: "refettorio",  label: "Rifiuti dal locale Refettorio/mensa" },
              { key: "spogliatoio", label: "Rifiuti dallo spogliatoio" },
              { key: "baracche",    label: "Pulizia servizi e baracche" },
              { key: "ufficio",     label: "Ufficio in cantiere" },
              { key: "wcChimico",   label: "Altri rifiuti (WC chimico)" },
            ].map(r => {
              const val = rifDefault.misure[r.key] || { stato: "", note: "" };
              return (
                <div key={r.key} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{r.label}</div>
                  <div style={{ display: "flex", gap: 12, marginBottom: 6 }}>
                    {[{v:"si",l:"SI"},{v:"no",l:"NO"}].map(o => (
                      <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer" }}>
                        <input type="radio" name={`mis_${r.key}`} checked={val.stato === o.v}
                          onChange={() => aggiornaRifMis(r.key, "stato", o.v)} />
                        <span>{o.l}</span>
                      </label>
                    ))}
                  </div>
                  <input style={inputSt} placeholder='Misure tecniche / note (es. "Conferimento al servizio locale")'
                    value={val.note}
                    onChange={e => aggiornaRifMis(r.key, "note", e.target.value)} />
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 11, color: theme.textDim, textAlign: "center", padding: "8px 0" }}>
            {rifDefaultDirty ? "💾 Salvataggio in corso..." : "✓ Tutti i dati salvati"}
          </div>
        </div>
      )}

      {/* ── TAB DIZIONARIO LAVORAZIONI ── */}
      {tab === "dizionario" && (
        <div>
          <div style={{ fontSize:13, color:theme.textMuted, marginBottom:16 }}>
            Gestisci categorie e sottocategorie di lavorazione con materiali, attrezzatura e DPI. Nel preventivo assegni la categoria alla voce per le statistiche.
          </div>

          {/* Aggiungi nuova categoria */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>+ Aggiungi categoria principale</div>
            <div style={{ display:"flex", gap:8 }}>
              <input style={{...inputSt, flex:1}} placeholder="es. pavimentazione, copertura, serramenti..."
                value={nuovaChiave} onChange={e=>setNuovaChiave(e.target.value.toLowerCase())} />
              <button className="btn btn-primary btn-sm" onClick={()=>{
                if (!nuovaChiave.trim()) return;
                const nuovo = {...dizionario,[nuovaChiave.trim()]:{label:nuovaChiave.trim().charAt(0).toUpperCase()+nuovaChiave.trim().slice(1),materiali:[],attrezzatura:[],dpi:[],sottocategorie:{}}};
                setDizionario(nuovo);
                salvaDizionario(nuovo);
                setNuovaChiave("");
              }}>+ Aggiungi</button>
            </div>
          </div>

          {/* Lista categorie */}
          {Object.entries(dizionario).sort(([ka,va],[kb,vb])=>(va.label||ka).localeCompare(vb.label||kb)).map(([chiave, dati]) => (
            <div key={chiave} className="card" style={{ marginBottom:12, border:`1px solid ${lavAperta===chiave?theme.accent+"66":theme.border}` }}>
              {/* Header categoria */}
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", cursor:"pointer" }}
                onClick={()=>setLavAperta(lavAperta===chiave?null:chiave)}>
                <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:15 }}>
                  {lavAperta===chiave?"▼":"▶"} {dati.label||chiave.charAt(0).toUpperCase()+chiave.slice(1)}
                </div>
                <div style={{ fontSize:12, color:theme.textDim }}>
                  {Object.keys(dati.sottocategorie||{}).length} sottocategorie · {dati.materiali?.length||0} mat · {dati.dpi?.length||0} DPI
                </div>
              </div>

              {lavAperta === chiave && (
                <div style={{ marginTop:16, borderTop:`1px solid ${theme.border}`, paddingTop:16 }}>

                  {/* Label categoria */}
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:11, color:theme.textDim, marginBottom:4, textTransform:"uppercase", letterSpacing:1 }}>Nome categoria (visibile nelle statistiche)</div>
                    <div style={{ display:"flex", gap:8 }}>
                      <input style={{...inputSt, flex:1}} value={dati.label||chiave}
                        onChange={e=>setDizionario({...dizionario,[chiave]:{...dati,label:e.target.value}})}
                        onBlur={e=>salvaDizionario({...dizionario,[chiave]:{...dati,label:e.target.value}})} />
                      {/* FIX_DIZIONARIO_GESTIONE_v1: bottone Elimina categoria */}
                      <button onClick={()=>{
                        const numSub = Object.keys(dati.sottocategorie||{}).length;
                        if (numSub > 0) {
                          alert("Impossibile eliminare: la categoria contiene " + numSub + " sottocategoria/e.\n\nSposta o elimina prima le sottocategorie.");
                          return;
                        }
                        const numMat = (dati.materiali||[]).length;
                        const numAttr = (dati.attrezzatura||[]).length;
                        if (numMat > 0 || numAttr > 0) {
                          if (!window.confirm("La categoria '" + (dati.label||chiave) + "' contiene " + numMat + " materiali e " + numAttr + " attrezzature. Eliminare definitivamente?")) return;
                        } else {
                          if (!window.confirm("Eliminare definitivamente la categoria '" + (dati.label||chiave) + "'?")) return;
                        }
                        const n = {...dizionario};
                        delete n[chiave];
                        setDizionario(n);
                        salvaDizionario(n);
                        setLavAperta(null);
                      }} style={{background:"none",border:`1px solid ${theme.danger}66`,color:theme.danger,cursor:"pointer",padding:"6px 12px",borderRadius:6,fontSize:12,whiteSpace:"nowrap"}}>
                        🗑️ Elimina categoria
                      </button>
                    </div>
                  </div>

                  {/* Materiali/Attrezzatura/DPI della categoria principale */}
                  <div style={{ background:theme.bg, borderRadius:8, padding:"12px 14px", border:`1px solid ${theme.border}`, marginBottom:16 }}>
                    <div style={{ fontSize:12, color:theme.accent, fontWeight:600, marginBottom:12 }}>⚙️ Valori predefiniti categoria (ereditati dalle sottocategorie se non specificato)</div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:12 }}>
                      {/* Materiali */}
                      <div>
                        <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:6, fontWeight:600 }}>🧱 Materiali</div>
                        {(dati.materiali||[]).map((m,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <input style={{...inputSt,flex:1,padding:"5px 8px",fontSize:12}} value={m}
                              onChange={e=>{ const arr=[...(dati.materiali||[])]; arr[i]=e.target.value; setDizionario({...dizionario,[chiave]:{...dati,materiali:arr}}); }}
                              onBlur={e=>{ const arr=[...(dati.materiali||[])]; arr[i]=e.target.value; salvaDizionario({...dizionario,[chiave]:{...dati,materiali:arr}}); }} />
                            <button onClick={()=>{ const arr=(dati.materiali||[]).filter((_,j)=>j!==i); const n={...dizionario,[chiave]:{...dati,materiali:arr}}; setDizionario(n); salvaDizionario(n); }}
                              style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>✕</button>
                          </div>
                        ))}
                        <button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>{ const arr=[...(dati.materiali||[]),""]; const n={...dizionario,[chiave]:{...dati,materiali:arr}}; setDizionario(n); salvaDizionario(n); }}>+ Aggiungi</button>
                      </div>
                      {/* Attrezzatura */}
                      <div>
                        <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:6, fontWeight:600 }}>🔧 Attrezzatura</div>
                        {(dati.attrezzatura||[]).map((a,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <input style={{...inputSt,flex:1,padding:"5px 8px",fontSize:12}} value={a}
                              onChange={e=>{ const arr=[...(dati.attrezzatura||[])]; arr[i]=e.target.value; setDizionario({...dizionario,[chiave]:{...dati,attrezzatura:arr}}); }}
                              onBlur={e=>{ const arr=[...(dati.attrezzatura||[])]; arr[i]=e.target.value; salvaDizionario({...dizionario,[chiave]:{...dati,attrezzatura:arr}}); }} />
                            <button onClick={()=>{ const arr=(dati.attrezzatura||[]).filter((_,j)=>j!==i); const n={...dizionario,[chiave]:{...dati,attrezzatura:arr}}; setDizionario(n); salvaDizionario(n); }}
                              style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>✕</button>
                          </div>
                        ))}
                        <button className="btn btn-ghost btn-sm" style={{marginTop:4}} onClick={()=>{ const arr=[...(dati.attrezzatura||[]),""]; const n={...dizionario,[chiave]:{...dati,attrezzatura:arr}}; setDizionario(n); salvaDizionario(n); }}>+ Aggiungi</button>
                      </div>
                    </div>
                    {/* DPI */}
                    <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:1, color:theme.textDim, marginBottom:6, fontWeight:600 }}>⛑️ DPI</div>
                    <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                      {TUTTI_DPI.map(dpi => {
                        const attivo = (dati.dpi||[]).includes(dpi.id);
                        return (
                          <div key={dpi.id} onClick={()=>{ const nuoviDpi=attivo?(dati.dpi||[]).filter(d=>d!==dpi.id):[...(dati.dpi||[]),dpi.id]; const n={...dizionario,[chiave]:{...dati,dpi:nuoviDpi}}; setDizionario(n); salvaDizionario(n); }}
                            style={{ display:"flex", alignItems:"center", gap:4, padding:"4px 10px", borderRadius:8, cursor:"pointer", border:`1px solid ${attivo?theme.accent:theme.border}`, background:attivo?theme.accentSoft:theme.bg }}>
                            <span style={{fontSize:14}}>{dpi.emoji}</span>
                            <span style={{fontSize:11, color:attivo?theme.accent:theme.textMuted, fontWeight:attivo?700:400}}>{dpi.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sottocategorie */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:theme.text }}>📂 Sottocategorie</div>
                      <button className="btn btn-ghost btn-sm" onClick={()=>{
                        const nuovaSub = prompt("Nome sottocategoria (es. 'tinteggiatura interna'):");
                        if (!nuovaSub?.trim()) return;
                        const key = nuovaSub.trim().toLowerCase();
                        const n = {...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[key]:{label:nuovaSub.trim(),materiali:[],attrezzatura:[],dpi:[]}}}};
                        setDizionario(n); salvaDizionario(n);
                      }}>+ Aggiungi sottocategoria</button>
                    </div>
                    {Object.keys(dati.sottocategorie||{}).length === 0 && (
                      <div style={{ fontSize:12, color:theme.textDim, fontStyle:"italic" }}>Nessuna sottocategoria — clicca "+ Aggiungi sottocategoria" per crearne una.</div>
                    )}
                    {Object.entries(dati.sottocategorie||{}).map(([subKey, subDati]) => (
                      <div key={subKey} style={{ background:theme.bg, borderRadius:8, padding:"10px 14px", border:`1px solid ${theme.border}`, marginBottom:8 }}>
                        {/* Header sottocategoria */}
                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                          <div>
                            <input style={{...inputSt, width:280, padding:"4px 8px", fontSize:13, fontWeight:600}}
                              value={subDati.label||subKey}
                              onChange={e=>{ const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,label:e.target.value}}}}; setDizionario(n); }}
                              onBlur={e=>{ const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,label:e.target.value}}}}; salvaDizionario(n); }} />
                            <div style={{ fontSize:10, color:theme.textDim, marginTop:2 }}>chiave: <code style={{color:theme.accent}}>{subKey}</code></div>
                          </div>
                          {/* FIX_DIZIONARIO_GESTIONE_v1: Duplica + Sposta + Elimina */}
                          <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                            <button onClick={()=>{
                              // Duplica nella stessa categoria
                              const subs = {...(dati.sottocategorie||{})};
                              let newKey = subKey + "_copia";
                              let counter = 2;
                              while (subs[newKey]) { newKey = subKey + "_copia" + counter; counter++; }
                              const copia = JSON.parse(JSON.stringify(subDati));
                              copia.label = (subDati.label||subKey) + " (copia)";
                              subs[newKey] = copia;
                              const n = {...dizionario,[chiave]:{...dati,sottocategorie:subs}};
                              setDizionario(n); salvaDizionario(n);
                            }} title="Duplica nella stessa categoria"
                              style={{background:"none",border:"none",color:theme.accent,cursor:"pointer",fontSize:14,padding:"2px 6px"}}>📋</button>
                            <button onClick={()=>{
                              const id = chiave + "|" + subKey;
                              setSpostaSubKey(spostaSubKey === id ? null : id);
                            }} title="Sposta in altra categoria"
                              style={{background:"none",border:"none",color:theme.accent,cursor:"pointer",fontSize:14,padding:"2px 6px"}}>🔀</button>
                            <button onClick={()=>{ if(!window.confirm(`Eliminare "${subDati.label||subKey}"?`)) return; const subs={...(dati.sottocategorie||{})}; delete subs[subKey]; const n={...dizionario,[chiave]:{...dati,sottocategorie:subs}}; setDizionario(n); salvaDizionario(n); }}
                              style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14,padding:"2px 6px"}}>🗑️</button>
                          </div>
                        </div>
                        {/* FIX_DIZIONARIO_GESTIONE_v1: dropdown sposta inline */}
                        {spostaSubKey === (chiave + "|" + subKey) && (
                          <div style={{ marginBottom:12, padding:"10px 12px", background:theme.accentSoft, borderRadius:6, border:`1px solid ${theme.accent}44` }}>
                            <div style={{ fontSize:12, color:theme.text, marginBottom:8 }}>🔀 Sposta "<strong>{subDati.label||subKey}</strong>" nella categoria:</div>
                            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                              <select style={{...inputSt, flex:1, padding:"6px 8px", fontSize:12}}
                                id={`sposta-${chiave}-${subKey}`}>
                                <option value="">— Scegli destinazione —</option>
                                {Object.entries(dizionario).filter(([k])=>k!==chiave).sort(([ka,va],[kb,vb])=>(va.label||ka).localeCompare(vb.label||kb)).map(([k,v])=>(
                                  <option key={k} value={k}>{v.label||k}</option>
                                ))}
                              </select>
                              <button className="btn btn-primary btn-sm" onClick={()=>{
                                const sel = document.getElementById(`sposta-${chiave}-${subKey}`);
                                const dest = sel?.value;
                                if (!dest) { alert("Scegli una categoria di destinazione"); return; }
                                if (dest === chiave) { alert("La sottocategoria è già in questa categoria"); return; }
                                const destDati = dizionario[dest];
                                let newKey = subKey;
                                if ((destDati.sottocategorie||{})[newKey]) {
                                  newKey = subKey + "_moved";
                                  let counter = 2;
                                  while ((destDati.sottocategorie||{})[newKey]) { newKey = subKey + "_moved" + counter; counter++; }
                                }
                                // rimuovo da chiave, aggiungo a dest
                                const subsOrigine = {...(dati.sottocategorie||{})};
                                delete subsOrigine[subKey];
                                const subsDest = {...(destDati.sottocategorie||{}), [newKey]: subDati};
                                const n = {...dizionario,
                                  [chiave]: {...dati, sottocategorie: subsOrigine},
                                  [dest]: {...destDati, sottocategorie: subsDest}
                                };
                                setDizionario(n); salvaDizionario(n);
                                setSpostaSubKey(null);
                              }}>Sposta</button>
                              <button className="btn btn-ghost btn-sm" onClick={()=>setSpostaSubKey(null)}>Annulla</button>
                            </div>
                          </div>
                        )}

                        {/* Materiali unificati con incidenza */}
                        <div style={{ marginBottom:12 }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                            <div style={{ fontSize:11, color:theme.textDim, fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>🧱 Materiali</div>
                            <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:"2px 8px"}} onClick={()=>{
                              const mat=[...(subDati.materiali||[])];
                              // Converti stringhe in oggetti se necessario
                              const matObj = mat.map(m => typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m);
                              matObj.push({nome:"",incidenza:"",um:"cad",categoriaP:""});
                              const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,materiali:matObj}}}};
                              setDizionario(n); salvaDizionario(n);
                            }}>+ Aggiungi materiale</button>
                          </div>
                          <div style={{ display:"grid", gridTemplateColumns:"10px 1fr 80px 70px 120px 28px", gap:6, marginBottom:4 }}>
                            {["","Nome materiale","Incidenza","U.M.","Cat. prodotto (listino)",""].map((h,hi)=>(
                              <div key={hi} style={{ fontSize:9, color:theme.textDim, textTransform:"uppercase", letterSpacing:1 }}>{h}</div>
                            ))}
                          </div>
                          {(subDati.materiali||[]).map((m,mi) => {
                            const mObj = typeof m === "string" ? {nome:m,incidenza:"",um:"cad",categoriaP:""} : m;
                            const aggiornaMat = (campo, val) => {
                              const arr = (subDati.materiali||[]).map((x,xi) => {
                                if (xi!==mi) return x;
                                const xObj = typeof x === "string" ? {nome:x,incidenza:"",um:"cad",categoriaP:""} : {...x};
                                xObj[campo] = val;
                                return xObj;
                              });
                              const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,materiali:arr}}}};
                              return n;
                            };
                            return (
                              <div key={mi} style={{ display:"grid", gridTemplateColumns:"10px 1fr 80px 70px 120px 28px", gap:6, marginBottom:4, alignItems:"center" }}>
                                <div style={{ fontSize:11, color:theme.textDim, textAlign:"center" }}>·</div>
                                <input style={{...inputSt,padding:"4px 8px",fontSize:12}} placeholder="es. Profili metallici" value={mObj.nome||""}
                                  onChange={e=>setDizionario(aggiornaMat("nome",e.target.value))}
                                  onBlur={e=>salvaDizionario(aggiornaMat("nome",e.target.value))} />
                                <input style={{...inputSt,padding:"4px 8px",fontSize:12}} placeholder="es. 3.5" type="number" step="0.001" value={mObj.incidenza||""}
                                  onChange={e=>setDizionario(aggiornaMat("incidenza",e.target.value))}
                                  onBlur={e=>salvaDizionario(aggiornaMat("incidenza",e.target.value))} />
                                <select style={{...inputSt,padding:"4px 6px",fontSize:11}} value={mObj.um||"cad"}
                                  onChange={e=>{ const n=aggiornaMat("um",e.target.value); setDizionario(n); salvaDizionario(n); }}>
                                  {["cad","kg","lt","ml","mq","ml","m","sacco","rotolo"].map(u=><option key={u}>{u}</option>)}
                                </select>
                                <select style={{...inputSt,padding:"4px 6px",fontSize:11}} value={mObj.categoriaP||""}
                                  onChange={e=>{ const n=aggiornaMat("categoriaP",e.target.value); setDizionario(n); salvaDizionario(n); }}>
                                  <option value="">— famiglia —</option>
                                  {famiglieProdottoDB.sort((a,b)=>a.localeCompare(b)).map(f=>(
                                    <option key={f} value={f}>{f}</option>
                                  ))}
                                </select>
                                <button onClick={()=>{ const arr=(subDati.materiali||[]).filter((_,j)=>j!==mi); const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,materiali:arr}}}}; setDizionario(n); salvaDizionario(n); }}
                                  style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:14}}>✕</button>
                              </div>
                            );
                          })}
                        </div>

                        {/* Attrezzatura */}
                        <div style={{ marginBottom:12 }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                            <div style={{ fontSize:11, color:theme.textDim, fontWeight:600, textTransform:"uppercase", letterSpacing:1 }}>🔧 Attrezzatura</div>
                            <button className="btn btn-ghost btn-sm" style={{fontSize:10,padding:"2px 8px"}} onClick={()=>{ const arr=[...(subDati.attrezzatura||[]),""]; const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,attrezzatura:arr}}}}; setDizionario(n); salvaDizionario(n); }}>+ Aggiungi</button>
                          </div>
                          {(subDati.attrezzatura||[]).map((a,ai) => (
                            <div key={ai} style={{ display:"flex", gap:4, marginBottom:3 }}>
                              <input style={{...inputSt,flex:1,padding:"4px 8px",fontSize:12}} value={a}
                                onChange={e=>{ const arr=[...(subDati.attrezzatura||[])]; arr[ai]=e.target.value; setDizionario({...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,attrezzatura:arr}}}}); }}
                                onBlur={e=>{ const arr=[...(subDati.attrezzatura||[])]; arr[ai]=e.target.value; salvaDizionario({...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,attrezzatura:arr}}}}); }} />
                              <button onClick={()=>{ const arr=(subDati.attrezzatura||[]).filter((_,j)=>j!==ai); const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,attrezzatura:arr}}}}; setDizionario(n); salvaDizionario(n); }}
                                style={{background:"none",border:"none",color:theme.danger,cursor:"pointer",fontSize:12}}>✕</button>
                            </div>
                          ))}
                        </div>

                        {/* DPI sottocategoria */}
                        <div style={{ fontSize:10, color:theme.textDim, marginBottom:4 }}>⛑️ DPI specifici</div>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                          {TUTTI_DPI.map(dpi => {
                            const attivo = (subDati.dpi||[]).includes(dpi.id);
                            return (
                              <div key={dpi.id} onClick={()=>{ const nuoviDpi=attivo?(subDati.dpi||[]).filter(d=>d!==dpi.id):[...(subDati.dpi||[]),dpi.id]; const n={...dizionario,[chiave]:{...dati,sottocategorie:{...(dati.sottocategorie||{}),[subKey]:{...subDati,dpi:nuoviDpi}}}}; setDizionario(n); salvaDizionario(n); }}
                                style={{ display:"flex", alignItems:"center", gap:3, padding:"3px 8px", borderRadius:6, cursor:"pointer", fontSize:11, border:`1px solid ${attivo?theme.accent:theme.border}`, background:attivo?theme.accentSoft:theme.surface, color:attivo?theme.accent:theme.textMuted }}>
                                <span>{dpi.emoji}</span><span>{dpi.label}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* SCHEDE SICUREZZA collegate alla sottocategoria */}
                        <div style={{ marginTop:12, paddingTop:10, borderTop:`1px solid ${theme.border}` }}>
                          <SelettoreSchede
                            schedeSelezionate={subDati.schedeSicurezza || []}
                            contesto={`${dati.label||chiave} / ${subDati.label||subKey}`}
                            onChange={(nuoviCodici) => {
                              const n = {...dizionario, [chiave]: {...dati, sottocategorie: {...(dati.sottocategorie||{}), [subKey]: {...subDati, schedeSicurezza: nuoviCodici}}}};
                              setDizionario(n);
                              salvaDizionario(n);
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display:"flex", justifyContent:"flex-end" }}>
                    <button className="btn btn-ghost btn-sm" style={{color:theme.danger}} onClick={()=>{
                      if (!window.confirm(`Eliminare la categoria "${dati.label||chiave}" e tutte le sue sottocategorie?`)) return;
                      const n={...dizionario}; delete n[chiave];
                      setDizionario(n); salvaDizionario(n); setLavAperta(null);
                    }}>🗑️ Elimina categoria</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop:16, padding:"10px 14px", background:theme.bg, borderRadius:8, border:`1px solid ${theme.success}44`, fontSize:12, color:theme.success }}>
            {salvandoDiz ? "💾 Salvataggio in corso..." : "✅ Le modifiche vengono salvate automaticamente su Firebase e si applicano subito in tutta l'app."}
          </div>
        </div>
      )}

    </div>
  );
}

// ─── STATISTICHE ─────────────────────────────────────────────────────────────


// === FIX_PAGINA_SCADENZE_v1 - Scadenzario uscite ===
function Scadenze({ scadenzeDB = [] }) {
  const vuoto = { creditore:"", tipo:"", importo:"", scadenza:new Date().toISOString().slice(0,10), ricorrenza:"una_tantum", note:"" };
  const [form, setForm] = React.useState(vuoto);
  const [salvando, setSalvando] = React.useState(false);
  const [filtro, setFiltro] = React.useState("");
  const [ricerca, setRicerca] = React.useState(""); // FIX_RICERCA_SCAD_v1
  const [nascondiPagate, setNascondiPagate] = React.useState(true); // FIX_NASCONDI_PAGATE_v2
  const [personaSel, setPersonaSel] = React.useState(""); // SCADENZE_PERSONE_v1
  const [persCfg, setPersCfg] = React.useState(null);
  React.useEffect(() => { caricaPersone().then(setPersCfg).catch(() => {}); }, []);
  const _perOk = s => !personaSel || (personaSel === "_nessuna" ? !personaDi(s, persCfg) : personaDi(s, persCfg) === personaSel);
  const [showPiano, setShowPiano] = React.useState(false); // FIX_PIANO_RATE_v1
  const [piano, setPiano] = React.useState({ intestatario:"", creditore:"", tipo:"", etichetta:"", totaleAtteso:"", righe:[{data:"", importo:""}] });
  const [genRate, setGenRate] = React.useState(false);
  const setRiga = (i,k,v) => setPiano(p=>{ const r=[...p.righe]; r[i]={...r[i],[k]:v}; return {...p,righe:r}; });
  const addRiga = () => setPiano(p=>({...p,righe:[...p.righe,{data:"",importo:""}]}));
  const delRiga = (i) => setPiano(p=>({...p,righe:p.righe.filter((_,j)=>j!==i)}));
  const sommaRighe = () => piano.righe.reduce((s,r)=>s+(parseFloat(r.importo)||0),0);
  const generaPiano = async () => {
    const valide = piano.righe.filter(r=>r.data && (parseFloat(r.importo)||0)>=0);
    if(!piano.creditore.trim()){ alert("Metti il creditore."); return; }
    if(valide.length===0){ alert("Aggiungi almeno una rata con data e importo."); return; }
    setGenRate(true);
    const etich = piano.etichetta.trim() || piano.creditore.trim();
    const N = valide.length;
    try {
      for(let i=0;i<N;i++){
        const r=valide[i];
        await addDoc(collection(db,"scadenze"),{
          creditore:piano.creditore.trim(), tipo:piano.tipo.trim(),
          intestatario:piano.intestatario.trim(),
          importo:parseFloat(r.importo)||0, scadenza:r.data,
          ricorrenza:"rata", pagata:false,
          pratica:etich, rataNum:(i+1), rateTotali:N,
          note:etich+" — rata "+(i+1)+" di "+N,
          createdAt:new Date().toISOString(), updatedAt:new Date().toISOString()
        });
      }
      setShowPiano(false);
      setPiano({ intestatario:"", creditore:"", tipo:"", etichetta:"", totaleAtteso:"", righe:[{data:"", importo:""}] });
      alert("Piano creato: "+N+" rate inserite.");
    } catch(e){ alert("Errore: "+((e&&e.message)||e)); }
    setGenRate(false);
  };
  const [leggendoPdf, setLeggendoPdf] = React.useState(false); // FIX_LEGGI_PDF_UI_v1
  const leggiPdfPiano = async (file) => {
    if(!file) return;
    setLeggendoPdf(true);
    try {
      const b64 = await new Promise((resolve,reject)=>{ const r=new FileReader(); r.onload=()=>resolve(String(r.result).split(',')[1]); r.onerror=()=>reject(new Error('lettura file fallita')); r.readAsDataURL(file); });
      const jwt = localStorage.getItem('bertoc_jwt');
      const resp = await fetch('/api/scadenze/leggi-piano', { method:'POST', headers:{'Content-Type':'application/json','Authorization':'Bearer '+jwt}, body: JSON.stringify({ pdfBase64: b64 }) });
      const data = await resp.json();
      if(!resp.ok || !data.ok) throw new Error(data.error || 'lettura non riuscita');
      const righe = (data.righe||[]).map(r=>({ data: _isoDaIt(r.data), importo: String(r.importo!=null?r.importo:'') }));
      setPiano(p=>({ ...p,
        creditore: p.creditore || data.creditore || '',
        tipo: p.tipo || data.tipo || '',
        intestatario: p.intestatario || data.intestatario || '',
        totaleAtteso: data.totaleDichiarato ? String(data.totaleDichiarato) : p.totaleAtteso,
        righe: righe.length? righe : p.righe
      }));
      if(!righe.length) alert('Il documento e stato letto ma non ho trovato righe di rata. Puoi inserirle a mano.');
    } catch(e){ alert('Lettura PDF non riuscita: '+((e&&e.message)||e)); }
    setLeggendoPdf(false);
  };
  const _isoDaIt = (s) => { const m=String(s||'').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); if(m){ const g=m[1].padStart(2,'0'),me=m[2].padStart(2,'0'); return m[3]+'-'+me+'-'+g; } const i=String(s||'').match(/^(\d{4})-(\d{2})-(\d{2})/); return i? s : ''; };
  const SUGG = ["IRPEF","IVA","INPS","Cassa edile","F24","Buste paga/Stipendi","TFR","Affitto","Utenze","Artigiani/subappaltatori","Finanziamenti/banca","Leasing/mezzi","Assicurazioni","Commercialista","Fisco (cartelle/avvisi/definizioni)","Altro"];
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const parseD = s => { if(!s) return null; const m=String(s).match(/^(\d{4})-(\d{2})-(\d{2})/); if(m) return new Date(+m[1],+m[2]-1,+m[3]); const g=String(s).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); if(g) return new Date(+g[3],+g[2]-1,+g[1]); return null; };
  const giorni = s => { const d=parseD(s); if(!d) return null; d.setHours(0,0,0,0); return Math.round((d-oggi)/86400000); };
  const fmtEuro = v => "€ "+(parseFloat(v)||0).toLocaleString("it-IT",{minimumFractionDigits:2});
  const fmtData = s => { const d=parseD(s); return d? d.toLocaleDateString("it-IT") : (s||"—"); };
  const salva = async () => {
    if(!form.creditore.trim()){ alert("Metti almeno il creditore (a chi)."); return; }
    setSalvando(true);
    try { await addDoc(collection(db,"scadenze"), { creditore:form.creditore.trim(), tipo:form.tipo.trim(), importo:parseFloat(form.importo)||0, scadenza:form.scadenza, ricorrenza:form.ricorrenza, pagata:false, note:form.note||"", createdAt:new Date().toISOString(), updatedAt:new Date().toISOString() }); setForm(vuoto); }
    catch(e){ alert("Errore salvataggio: "+((e&&e.message)||e)); }
    setSalvando(false);
  };
  const togglePagata = async (s) => { try { await updateDoc(doc(db,"scadenze",s._id),{ pagata:!s.pagata, dataPagamento: !s.pagata? new Date().toISOString().slice(0,10):"", updatedAt:new Date().toISOString() }); } catch(e){ alert("Errore: "+((e&&e.message)||e)); } };
  const elimina = async (s) => { if(!window.confirm("Eliminare questa scadenza?")) return; try { await deleteDoc(doc(db,"scadenze",s._id)); } catch(e){ alert("Errore: "+((e&&e.message)||e)); } };
  const [editId, setEditId] = React.useState(null); // FIX_MODIFICA_SCAD_v2
  const [editForm, setEditForm] = React.useState({});
  const parseD2iso = s => { const m=String(s||'').match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/); if(m){return m[3]+'-'+m[2].padStart(2,'0')+'-'+m[1].padStart(2,'0');} const i=String(s||'').match(/^\d{4}-\d{2}-\d{2}/); return i? String(s).slice(0,10):''; };
  const apriModifica = (s) => { setEditId(s._id); setEditForm({ creditore:s.creditore||'', tipo:s.tipo||'', importo:String(s.importo!=null?s.importo:''), scadenza:parseD2iso(s.scadenza), intestatario:s.intestatario||'', note:s.note||'' }); };
  const salvaModifica = async (s) => { /* FIX_MODIFICA_PRATICA_v1 */
    try {
      const cred=editForm.creditore.trim(), tp=editForm.tipo.trim(), inte=(editForm.intestatario||'').trim();
      await updateDoc(doc(db,"scadenze",s._id), { creditore:cred, tipo:tp, importo:parseFloat(editForm.importo)||0, scadenza:editForm.scadenza, intestatario:inte, note:editForm.note||'', updatedAt:new Date().toISOString() });
      // Se fa parte di una pratica con piu rate, proponi di propagare i campi comuni
      const prat = s.pratica;
      if (prat) {
        const sorelle = scadenzeDB.filter(x => x.pratica===prat && (x._id||x.id)!==(s._id||s.id));
        if (sorelle.length > 0) {
          const cambiaTipo = tp !== (s.tipo||'');
          const cambiaCred = cred !== (s.creditore||'');
          const cambiaInte = inte !== (s.intestatario||'');
          if (cambiaTipo || cambiaCred || cambiaInte) {
            if (window.confirm('Questa fa parte della pratica "'+prat+'" con altre '+sorelle.length+' rate.\n\nVuoi applicare le stesse modifiche (creditore, tipo, intestatario) a TUTTE le rate della pratica?')) {
              const patch = { updatedAt:new Date().toISOString() };
              if (cambiaCred) patch.creditore = cred;
              if (cambiaTipo) patch.tipo = tp;
              if (cambiaInte) patch.intestatario = inte;
              for (const x of sorelle) { try { await updateDoc(doc(db,"scadenze",(x._id||x.id)), patch); } catch(e){ console.error(e); } }
            }
          }
        }
      }
      setEditId(null);
    } catch(e){ alert("Errore: "+((e&&e.message)||e)); }
  };
  const _campiScad = s => [s.creditore,s.tipo,s.intestatario,s.note,s.pratica].filter(Boolean).join(" ").toLowerCase();
  const _parole = ricerca.toLowerCase().split(/\s+/).filter(Boolean);
  const lista = [...scadenzeDB].filter(s=> _perOk(s) && (!filtro || (s.tipo||"")===filtro) && (_parole.length===0 || _parole.every(p=>_campiScad(s).includes(p))) && (!nascondiPagate || !s.pagata)).sort((a,b)=>{ const da=parseD(a.scadenza),db2=parseD(b.scadenza); if(!da) return 1; if(!db2) return -1; return da-db2; });
  const _baseFiltrata = [...scadenzeDB].filter(s=> _perOk(s) && (!filtro || (s.tipo||"")===filtro) && (_parole.length===0 || _parole.every(p=>_campiScad(s).includes(p)))); // FIX_TOT_PAGATO_v1
  const totPagato = _baseFiltrata.filter(s=>s.pagata).reduce((t,s)=>t+(parseFloat(s.importo)||0),0);
  const nonPagate = lista.filter(s=>!s.pagata);
  const totScaduto = nonPagate.filter(s=>{const g=giorni(s.scadenza); return g!=null&&g<0;}).reduce((t,s)=>t+(parseFloat(s.importo)||0),0);
  const meseCorr = oggi.getMonth(), annoCorr = oggi.getFullYear();
  const totMese = nonPagate.filter(s=>{const d=parseD(s.scadenza); return d&&d.getMonth()===meseCorr&&d.getFullYear()===annoCorr;}).reduce((t,s)=>t+(parseFloat(s.importo)||0),0);
  const totAperto = nonPagate.reduce((t,s)=>t+(parseFloat(s.importo)||0),0);
  const tipiUsati = Array.from(new Set(scadenzeDB.map(s=>s.tipo).filter(Boolean)));
  const card={background:theme.card,border:`1px solid ${theme.border}`,borderRadius:12,padding:16};
  const inp={padding:"9px 11px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:"100%"};
  const statoDi = s => { if(s.pagata) return {txt:"Pagata",col:"#3ecf8e"}; const g=giorni(s.scadenza); if(g==null) return {txt:"—",col:theme.textDim}; if(g<0) return {txt:"Scaduta da "+Math.abs(g)+"gg",col:"#f06060"}; if(g<=30) return {txt:"Tra "+g+"gg",col:"#f5a623"}; return {txt:fmtData(s.scadenza),col:theme.textDim}; };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {showPiano && (
        <div onClick={()=>!genRate&&setShowPiano(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",display:"flex",alignItems:"flex-start",justifyContent:"center",zIndex:9999,padding:"30px 16px",overflowY:"auto"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:theme.card,border:`1px solid ${theme.border}`,borderRadius:14,padding:20,maxWidth:640,width:"100%"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:16}}>📋 Nuovo piano a rate</div>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <label style={{padding:"7px 12px",borderRadius:8,border:`1px solid ${theme.accent}`,background:"transparent",color:theme.accent,cursor:leggendoPdf?"wait":"pointer",fontSize:12,fontWeight:600}}>{leggendoPdf?"Leggo...":"📄 Leggi da PDF"}<input type="file" accept="application/pdf" style={{display:"none"}} disabled={leggendoPdf} onChange={e=>{ const f=e.target.files&&e.target.files[0]; e.target.value=''; leggiPdfPiano(f); }} /></label>
                <button onClick={()=>setShowPiano(false)} style={{background:"none",border:"none",color:theme.textDim,fontSize:20,cursor:"pointer"}}>×</button>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Intestatario</div><input style={{padding:"9px 11px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:"100%"}} list="intest-opts" value={piano.intestatario} onChange={e=>setPiano({...piano,intestatario:e.target.value})} placeholder="Marco / Edoardo / Azienda / Ex soci" /><datalist id="intest-opts"><option value="Marco"/><option value="Edoardo"/><option value="Azienda"/><option value="Ex soci"/></datalist></div>
              <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Creditore</div><input style={{padding:"9px 11px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:"100%"}} value={piano.creditore} onChange={e=>setPiano({...piano,creditore:e.target.value})} placeholder="es. Agenzia Entrate Riscossione" /></div>
              <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Tipo</div><input style={{padding:"9px 11px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:"100%"}} list="tipi-scad" value={piano.tipo} onChange={e=>setPiano({...piano,tipo:e.target.value})} placeholder="es. Definizione agevolata" /></div>
              <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Etichetta pratica (facolt.)</div><input style={{padding:"9px 11px",borderRadius:8,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:"100%"}} value={piano.etichetta} onChange={e=>setPiano({...piano,etichetta:e.target.value})} placeholder="es. Rottamazione IRPEF 2021" /></div>
            </div>
            <div style={{fontSize:12,color:theme.textMuted,margin:"6px 0 6px"}}>Rate (una riga per rata)</div>
            <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:260,overflowY:"auto",paddingRight:4}}>
              {piano.righe.map((r,i)=>(
                <div key={i} style={{display:"grid",gridTemplateColumns:"30px 1fr 1fr 30px",gap:8,alignItems:"center"}}>
                  <div style={{fontSize:12,color:theme.textDim,textAlign:"center"}}>{i+1}</div>
                  <input type="date" value={r.data} onChange={e=>setRiga(i,"data",e.target.value)} style={{padding:"7px 9px",borderRadius:6,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:12}} />
                  <input type="number" value={r.importo} onChange={e=>setRiga(i,"importo",e.target.value)} placeholder="importo €" style={{padding:"7px 9px",borderRadius:6,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:12}} />
                  <button onClick={()=>delRiga(i)} style={{background:"none",border:"none",color:"#f06060",cursor:"pointer",fontSize:14}}>×</button>
                </div>
              ))}
            </div>
            <button onClick={addRiga} style={{marginTop:8,padding:"6px 12px",borderRadius:6,border:`1px dashed ${theme.border}`,background:"transparent",color:theme.textMuted,cursor:"pointer",fontSize:12}}>+ Aggiungi rata</button>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,gap:10,flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:16,alignItems:"center"}}>
                <div><div style={{fontSize:10,color:theme.textMuted,textTransform:"uppercase"}}>Somma rate</div><div style={{fontSize:15,fontWeight:800}}>€ {sommaRighe().toLocaleString("it-IT",{minimumFractionDigits:2})}</div></div>
                <div><div style={{fontSize:10,color:theme.textMuted,textTransform:"uppercase"}}>Totale atteso</div><input type="number" value={piano.totaleAtteso} onChange={e=>setPiano({...piano,totaleAtteso:e.target.value})} placeholder="dal documento" style={{padding:"6px 8px",borderRadius:6,border:`1px solid ${theme.border}`,background:theme.bg,color:theme.text,fontSize:13,width:120}} /></div>
                {piano.totaleAtteso && (Math.abs(sommaRighe()-(parseFloat(piano.totaleAtteso)||0))<0.05 ? <span style={{color:"#3ecf8e",fontSize:12,fontWeight:700}}>✓ torna</span> : <span style={{color:"#f5a623",fontSize:12,fontWeight:700}}>≠ controlla</span>)}
              </div>
              <button onClick={generaPiano} disabled={genRate} style={{padding:"10px 20px",borderRadius:8,border:"none",background:theme.accent,color:"#111",fontWeight:700,cursor:"pointer"}}>{genRate?"Genero...":"Genera "+piano.righe.filter(r=>r.data).length+" rate"}</button>
            </div>
          </div>
        </div>
      )}
      <ScadenzePersone scadenzeDB={scadenzeDB} cfg={persCfg} setCfg={setPersCfg} personaSel={personaSel} setPersonaSel={setPersonaSel} />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        <div style={{...card}}><div style={{fontSize:11,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1}}>Scaduto non pagato</div><div style={{fontSize:24,fontWeight:800,color:"#f06060",marginTop:4}}>{fmtEuro(totScaduto)}</div></div>
        <div style={{...card}}><div style={{fontSize:11,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1}}>In scadenza questo mese</div><div style={{fontSize:24,fontWeight:800,color:"#f5a623",marginTop:4}}>{fmtEuro(totMese)}</div></div>
        <div style={{...card}}><div style={{fontSize:11,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1}}>Totale da pagare</div><div style={{fontSize:24,fontWeight:800,color:theme.text,marginTop:4}}>{fmtEuro(totAperto)}</div></div>
        <div style={{...card}}><div style={{fontSize:11,color:theme.textMuted,textTransform:"uppercase",letterSpacing:1}}>Gia pagato</div><div style={{fontSize:24,fontWeight:800,color:"#3ecf8e",marginTop:4}}>{fmtEuro(totPagato)}</div></div>
      </div>
      <div style={{...card}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}><div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:14}}>➕ Nuova scadenza</div><button onClick={()=>setShowPiano(true)} style={{padding:"7px 14px",borderRadius:8,border:`1px solid ${theme.accent}`,background:"transparent",color:theme.accent,cursor:"pointer",fontSize:12,fontWeight:600}}>📋 Piano a rate</button></div>
        <div style={{display:"grid",gridTemplateColumns:"1.4fr 1.2fr 0.8fr 1fr 1fr",gap:10,alignItems:"end"}}>
          <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Creditore (a chi)</div><input style={inp} value={form.creditore} onChange={e=>setForm({...form,creditore:e.target.value})} placeholder="es. Agenzia Entrate, Enel, Rossi srl" /></div>
          <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Tipo</div><input style={inp} list="tipi-scad" value={form.tipo} onChange={e=>setForm({...form,tipo:e.target.value})} placeholder="scrivi o scegli" /><datalist id="tipi-scad">{SUGG.map((t,i)=><option key={i} value={t} />)}</datalist></div>
          <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Importo €</div><input style={inp} type="number" value={form.importo} onChange={e=>setForm({...form,importo:e.target.value})} placeholder="0" /></div>
          <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Scadenza</div><input style={inp} type="date" value={form.scadenza} onChange={e=>setForm({...form,scadenza:e.target.value})} /></div>
          <div><div style={{fontSize:11,color:theme.textMuted,marginBottom:4}}>Ricorrenza</div><select style={inp} value={form.ricorrenza} onChange={e=>setForm({...form,ricorrenza:e.target.value})}><option value="una_tantum">Una tantum</option><option value="mensile">Mensile</option><option value="trimestrale">Trimestrale</option><option value="annuale">Annuale</option></select></div>
        </div>
        <div style={{marginTop:12,display:"flex",gap:8,alignItems:"center"}}>
          <input style={{...inp,flex:1}} value={form.note} onChange={e=>setForm({...form,note:e.target.value})} placeholder="Note (facoltativo)" />
          <button onClick={salva} disabled={salvando} style={{padding:"9px 20px",borderRadius:8,border:"none",background:theme.accent,color:"#111",fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>{salvando?"Salvo...":"Salva scadenza"}</button>
        </div>
      </div>
      <div style={{...card}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
          <div style={{fontFamily:"'Syne',sans-serif",fontWeight:700,fontSize:14}}>🗓️ Scadenze ({nonPagate.length} da pagare)</div>
          <input style={{...inp,width:200}} value={ricerca} onChange={e=>setRicerca(e.target.value)} placeholder="🔍 Cerca (es. Marco rateizzazione)" />
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:theme.textMuted,cursor:"pointer",whiteSpace:"nowrap"}}><input type="checkbox" checked={nascondiPagate} onChange={e=>setNascondiPagate(e.target.checked)} /> nascondi pagate</label>
          <select style={{...inp,width:"auto"}} value={filtro} onChange={e=>setFiltro(e.target.value)}><option value="">Tutti i tipi</option>{tipiUsati.map((t,i)=><option key={i} value={t}>{t}</option>)}</select>
        </div>
        {lista.length===0 ? <div style={{color:theme.textDim,fontSize:13,padding:"12px 0"}}>Nessuna scadenza inserita. Aggiungine una qui sopra.</div> :
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {lista.map(s=>{ const st=statoDi(s); return (
              <div key={s._id}>
              <div style={{display:"grid",gridTemplateColumns:"10px 1.4fr 1fr 0.9fr 1.1fr auto",gap:10,alignItems:"center",padding:"10px 8px",borderBottom:`1px solid ${theme.border}44`,opacity:s.pagata?0.55:1}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:st.col}} />
                <div><div style={{fontSize:13,fontWeight:600,textDecoration:s.pagata?"line-through":"none"}}>{s.creditore}</div>{s.note?<div style={{fontSize:11,color:theme.textDim}}>{s.note}</div>:null}</div>
                <div style={{fontSize:12,color:theme.textMuted}}>{s.tipo||"—"}{s.ricorrenza&&s.ricorrenza!=="una_tantum"?<span style={{marginLeft:6,fontSize:10,background:theme.accentSoft,color:theme.accent,borderRadius:4,padding:"1px 5px"}}>{s.ricorrenza}</span>:null}</div>
                <div style={{fontSize:13,fontWeight:700}}>{fmtEuro(s.importo)}</div>
                <div style={{fontSize:12,color:st.col,fontWeight:600}}>{st.txt}</div>
                <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                  <button onClick={()=>apriModifica(s)} title="Modifica" style={{padding:"5px 9px",borderRadius:6,border:`1px solid ${theme.border}`,background:"transparent",color:theme.accent,cursor:"pointer",fontSize:12}}>✏️</button>
                  <button onClick={()=>togglePagata(s)} title="Segna pagata" style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${s.pagata?theme.border:"#3ecf8e"}`,background:"transparent",color:s.pagata?theme.textDim:"#3ecf8e",cursor:"pointer",fontSize:12}}>{s.pagata?"↺":"✓ Pagata"}</button>
                  <button onClick={()=>elimina(s)} title="Elimina" style={{padding:"5px 9px",borderRadius:6,border:`1px solid ${theme.border}`,background:"transparent",color:"#f06060",cursor:"pointer",fontSize:12}}>🗑</button>
                </div>
              </div>
              {editId===s._id && (
                <div style={{padding:"12px 8px 16px",borderBottom:`1px solid ${theme.border}44`,background:theme.bg}}>
                  <div style={{display:"grid",gridTemplateColumns:"1.3fr 1fr 0.8fr 1fr 1fr",gap:8,marginBottom:8}}>
                    <input style={inp} value={editForm.creditore} onChange={e=>setEditForm({...editForm,creditore:e.target.value})} placeholder="Creditore" />
                    <input style={inp} list="tipi-scad" value={editForm.tipo} onChange={e=>setEditForm({...editForm,tipo:e.target.value})} placeholder="Tipo" />
                    <input style={inp} type="number" value={editForm.importo} onChange={e=>setEditForm({...editForm,importo:e.target.value})} placeholder="Importo" />
                    <input style={inp} type="date" value={editForm.scadenza} onChange={e=>setEditForm({...editForm,scadenza:e.target.value})} />
                    <input style={inp} list="intest-opts" value={editForm.intestatario} onChange={e=>setEditForm({...editForm,intestatario:e.target.value})} placeholder="Intestatario" />
                  </div>
                  <div style={{display:"flex",gap:8,alignItems:"center"}}>
                    <input style={{...inp,flex:1}} value={editForm.note} onChange={e=>setEditForm({...editForm,note:e.target.value})} placeholder="Note" />
                    <button onClick={()=>salvaModifica(s)} style={{padding:"8px 16px",borderRadius:8,border:"none",background:theme.accent,color:"#111",fontWeight:700,cursor:"pointer"}}>Salva</button>
                    <button onClick={()=>setEditId(null)} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${theme.border}`,background:"transparent",color:theme.textMuted,cursor:"pointer"}}>Annulla</button>
                  </div>
                </div>
              )}
              </div>
            );})}
          </div>
        }
      </div>
    </div>
  );
}

function Statistiche({ cantieriDB = [], assenzeDB = [], bolleDB = [], preventiviDB = [], pagamentiDB = [] }) {
  const [tab, setTab] = useState("annuale");
  const [annoSel, setAnnoSel] = useState(String(new Date().getFullYear()));
  // FIX_STATISTICHE_DOPPIA_VISTA_v1: "lavorato" = ricavo da consuntivo (default); "incassato" = solo cantieri pagati
  const [modoVista, setModoVista] = useState("lavorato");

  const COSTO_ORA = COSTO_ORARIO_GLOBALE;
  const fmt = (n) => "€ " + Number(n||0).toLocaleString("it-IT", { minimumFractionDigits:0, maximumFractionDigits:0 });
  const fmtD = (n) => "€ " + Number(n||0).toLocaleString("it-IT", { minimumFractionDigits:2, maximumFractionDigits:2 });

  // Calcola totale incassato per cantiere
  const totIncassato = (cid) => pagamentiDB.filter(p=>p.cantiereId===cid).reduce((s,p)=>s+(parseFloat(p.importo)||0),0);

  // Cantieri completati (anche parzialmente pagati) — entrano nelle statistiche
  const cantieriChiusi = cantieriDB.filter(c => c.completato);
  const cantieriAttivi = cantieriDB.filter(c => !c.completato);

  // Anni disponibili — da cantieri completati (anche parzialmente pagati)
  const anni = [...new Set([
    ...cantieriChiusi.map(c => (c.dataCompletamento||c.dataSaldo||"").slice(-4)).filter(a=>a.match(/^\d{4}$/)),
    String(new Date().getFullYear())
  ])].sort().reverse();

  // Calcola dati per un cantiere
  const getDatiCantiere = (c, modo = "lavorato") => {
    // FIX_STATISTICHE_DOPPIA_VISTA_v1: modo "lavorato" = ricavo da consuntivo sempre; "incassato" = solo se pagato
    const cid = c._id||c.id;
    const consuntivo = c.consuntivo;
    const incassato = totIncassato(cid);
    const ricavoConsuntivo = consuntivo
      ? consuntivo.lavorazioni?.reduce((s,l)=>s+(parseFloat(l.totale)||0),0)||0
      : parseFloat(c.importo)||0;
    // Lavorato: usa sempre il consuntivo; Incassato: come prima (incassato>0 oppure saldato)
    const ricavo = modo === "lavorato"
      ? ricavoConsuntivo
      : (incassato > 0 ? incassato : (c.saldato ? ricavoConsuntivo : 0));
    const oreEff = assenzeDB.filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid||(a.cantiere||"")===(c.nome||""))).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
    const matReali = bolleDB.flatMap(b=>(b.righe||[]).filter(r=>r.cantiere===c.nome)).reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
    const costoMod = oreEff * COSTO_ORA;
    const costi = costoMod + matReali;
    const margine = ricavo - costi;
    const percMargine = ricavo>0 ? Math.round((margine/ricavo)*100) : 0;
    return { ricavo, ricavoConsuntivo, incassato, oreEff, matReali, costoMod, costi, margine, percMargine };
  };

  // ── TAB ANNUALE ──────────────────────────────────────────────────────────────
  const datiAnno = (anno, modo = "lavorato") => {
    const cant = cantieriChiusi.filter(c => {
      const data = c.dataCompletamento||c.dataSaldo||"";
      return data.includes(anno);
    });
    return cant.reduce((acc, c) => {
      const d = getDatiCantiere(c, modo);
      return {
        ricavo: acc.ricavo + d.ricavo,
        ore: acc.ore + d.oreEff,
        materiali: acc.materiali + d.matReali,
        margine: acc.margine + d.margine,
        cantieri: acc.cantieri + 1,
      };
    }, { ricavo:0, ore:0, materiali:0, margine:0, cantieri:0 });
  };

  // ── TAB PER LAVORAZIONE ──────────────────────────────────────────────────────
  const statsLavorazioni = () => {
    // FIX_STATISTICHE_ORE_PREV_DA_PREVENTIVO_v1
    // Scorriamo sempre le lavorazioni del PREVENTIVO del cantiere
    // (c.lavorazioni), dove ci sono minuti/categoria/sottocategoria.
    // Per il ricavo reale, cerchiamo la lavorazione gemella nel consuntivo
    // per id (o per desc come fallback).
    const map = {};
    cantieriChiusi.forEach(c => {
      const cid = c._id||c.id;
      const consuntivo = c.consuntivo;
      const lavsConsuntivo = consuntivo?.lavorazioni || [];
      const lavs = c.lavorazioni || [];
      lavs.forEach((l,i) => {
        const desc = (l.desc||"").trim();
        if (!desc) return;
        const catKey = l.categoria || getDatiLavorazione(desc)?.categoria || "altro";
        const subKey = l.sottocategoria || null;
        const groupKey = subKey ? `${catKey}__${subKey}` : catKey;
        const catLabel = dizionarioMateriali[catKey]?.label || catKey.charAt(0).toUpperCase()+catKey.slice(1);
        const subLabel = subKey ? (dizionarioMateriali[catKey]?.sottocategorie?.[subKey]?.label || subKey) : null;

        if (!map[groupKey]) map[groupKey] = { catKey, catLabel, subKey, subLabel, ricavoPrev:0, ricavoReale:0, orePrev:0, oreReali:0, matReali:0, count:0, voci:[] };
        const totP = parseFloat(l.totalePrev||l.totale||0) || (parseFloat(l.prezzoUn||0)*parseFloat(l.qta||0));
        map[groupKey].ricavoPrev += totP;
        // Cerca la gemella nel consuntivo: prima per id, poi per desc
        let gemella = null;
        if (consuntivo) {
          gemella = lavsConsuntivo.find(lc => lc.id && l.id && lc.id===l.id)
                 || lavsConsuntivo.find(lc => (lc.desc||"").trim()===desc);
        }
        // FIX_STATSLAVORAZIONI_RICAVO_ROBUSTO_v5: se gemella non trovata, fallback su totP
        const ricavoLav = consuntivo
          ? (gemella ? (parseFloat(gemella.totale)||totP) : totP)
          : totP;
        map[groupKey].ricavoReale += ricavoLav;
        const oreR = assenzeDB.filter(a=>a.tipo==="lavorato"&&(a.cantiereId===cid||(a.cantiere||"")===(c.nome||""))&&(trovaIdxLav(a, c.lavorazioni /* LAV_BY_ID_v8 */)===i)).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
        map[groupKey].oreReali += oreR;
        map[groupKey].orePrev += ((parseFloat(l.minuti||0)/60))*(parseFloat(l.qta||0));
        const matR = bolleDB.flatMap(b=>(b.righe||[]).filter(r=>r.cantiere===c.nome&&r.lavorazione===l.desc)).reduce((s,r)=>s+(parseFloat(r.prezzo)||0)*(parseFloat(r.qta)||1),0);
        map[groupKey].matReali += matR;
        map[groupKey].count++;
        map[groupKey].voci.push(desc);
      });
    });

    // Organizza in struttura gerarchica: categoria → sottocategorie
    const risultati = Object.values(map);
    const categoriePrincipali = risultati.filter(r => !r.subKey);
    const sottocategorie = risultati.filter(r => r.subKey);

    // Per ogni categoria principale, aggiungi le sue sottocategorie subito dopo
    const ordinati = [];
    // Prima raggruppa per catKey
    const tutteCategorie = [...new Set(risultati.map(r => r.catKey))];
    // FIX_STATSLAV_NO_DUPLICATI_v6
    // Per ogni categoria emette la sua intestazione (principale o aggregata)
    // seguita dalle sue sottocategorie. Una sola emissione per ciascuna.
    tutteCategorie.forEach(catKey => {
      const principale = categoriePrincipali.find(r => r.catKey === catKey);
      const subs = sottocategorie.filter(r => r.catKey === catKey).sort((a,b)=>b.ricavoReale-a.ricavoReale);
      if (principale) {
        ordinati.push({ ...principale, isCategoria:true });
        subs.forEach(s => ordinati.push({ ...s, isCategoria:false }));
      } else if (subs.length > 0) {
        const agg = subs.reduce((acc, s) => ({
          catKey, catLabel: s.catLabel, subKey:null, subLabel:null, isCategoria:true,
          ricavoPrev: acc.ricavoPrev+s.ricavoPrev,
          ricavoReale: acc.ricavoReale+s.ricavoReale,
          orePrev: acc.orePrev+s.orePrev,
          oreReali: acc.oreReali+s.oreReali,
          matReali: acc.matReali+s.matReali,
          count: acc.count+s.count, voci:[],
        }), { ricavoPrev:0,ricavoReale:0,orePrev:0,oreReali:0,matReali:0,count:0 });
        ordinati.push(agg);
        subs.forEach(s => ordinati.push({ ...s, isCategoria:false }));
      }
    });
    return ordinati;
  };

  const inputSt = { background:"#0f1117", border:"1px solid #2a2e45", borderRadius:8, color:"#e8eaf2", padding:"6px 12px", fontSize:13, fontFamily:"'DM Sans',sans-serif", outline:"none" };

  return (
    <div className="content">
      {/* FIX_STATISTICHE_DOPPIA_VISTA_v1: selettore vista Lavorato/Incassato */}
      <div style={{ display:(tab==="spese"||tab==="costo")?"none":"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
        <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginRight:4 }}>Vista:</div>
        {[
          { id:"lavorato",  label:"💼 Lavorato",  hint:"margine del lavoro fatto (consuntivo)" },
          { id:"incassato", label:"💰 Incassato", hint:"margine di cassa (solo cantieri pagati)" },
        ].map(o => (
          <div key={o.id} onClick={()=>setModoVista(o.id)} title={o.hint}
            style={{ padding:"6px 14px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600,
              border:`1px solid ${modoVista===o.id?theme.accent:theme.border}`,
              background:modoVista===o.id?theme.accentSoft:theme.bg,
              color:modoVista===o.id?theme.accent:theme.textMuted }}>
            {o.label}
          </div>
        ))}
        <div style={{ fontSize:11, color:theme.textDim, marginLeft:8 }}>
          {modoVista==="lavorato"
            ? "Ricavo = consuntivo di ogni cantiere completato"
            : "Ricavo = solo soldi effettivamente incassati"}
        </div>
      </div>
      <div className="tabs">
        {[["annuale","📅 Annuale"],["cantieri","🏗️ Per cantiere"],["lavorazioni","🔨 Per lavorazione"],["operai","👷 Per operaio"],["spese","💸 Spese azienda"],["costo","⏱️ Costo orario e pareggio"]].map(([id,label])=>(
          <div key={id} className={`tab ${tab===id?"active":""}`} onClick={()=>setTab(id)}>{label}</div>
        ))}
      </div>

      {(tab==="spese"||tab==="costo") && <StatisticheAzienda vista={tab} pagamentiDB={pagamentiDB} assenzeDB={assenzeDB} dipendenti={dipendenti} costoOrarioAttuale={COSTO_ORA} />}

      {/* ── TAB ANNUALE ── */}
      {tab==="annuale" && (
        <div>
          {/* Selettore anno */}
          <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
            {anni.map(a=>(
              <div key={a} onClick={()=>setAnnoSel(a)}
                style={{ padding:"6px 16px", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600,
                  border:`1px solid ${annoSel===a?theme.accent:theme.border}`,
                  background:annoSel===a?theme.accentSoft:theme.bg,
                  color:annoSel===a?theme.accent:theme.textMuted }}>
                {a}
              </div>
            ))}
          </div>

          {(() => {
            const d = datiAnno(annoSel, modoVista);
            const percMargine = d.ricavo>0 ? Math.round((d.margine/d.ricavo)*100) : 0;
            return (
              <>
                {/* KPI anno */}
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
                  {[
                    ["Fatturato", fmt(d.ricavo), "#4e9af5", "💰"],
                    ["Margine", fmt(d.margine)+" ("+percMargine+"%)", d.margine>0?"#3ecf8e":"#f06060", "📈"],
                    ["Ore lavorate", d.ore.toFixed(0)+"h", theme.accent, "⏱️"],
                    ["Cantieri", d.cantieri, theme.text, "🏗️"],
                  ].map(([label,val,color,icon])=>(
                    <div key={label} className="card" style={{ padding:"18px 20px" }}>
                      <div style={{ fontSize:22, marginBottom:8 }}>{icon}</div>
                      <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{label}</div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color }}>{val}</div>
                    </div>
                  ))}
                </div>

                {/* Confronto anni */}
                <div className="card" style={{ marginBottom:20 }}>
                  <div className="card-title" style={{ marginBottom:16 }}>📊 Confronto anni</div>
                  <div style={{ overflowX:"auto" }}>
                    <table>
                      <thead><tr><th>Anno</th><th style={{textAlign:"right"}}>Fatturato</th><th style={{textAlign:"right"}}>Costo manodopera</th><th style={{textAlign:"right"}}>Costo materiali</th><th style={{textAlign:"right"}}>Margine</th><th style={{textAlign:"right"}}>%</th><th style={{textAlign:"right"}}>Cantieri</th></tr></thead>
                      <tbody>
                        {anni.map(a => {
                          const da = datiAnno(a, modoVista);
                          const perc = da.ricavo>0 ? Math.round((da.margine/da.ricavo)*100) : 0;
                          return (
                            <tr key={a} style={{ background:a===annoSel?theme.accentSoft:"transparent", cursor:"pointer" }} onClick={()=>setAnnoSel(a)}>
                              <td style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, color:a===annoSel?theme.accent:theme.text }}>{a}</td>
                              <td style={{ textAlign:"right", fontWeight:600 }}>{fmt(da.ricavo)}</td>
                              <td style={{ textAlign:"right", color:"#f06060" }}>{fmt(da.ricavo>0?da.ore*COSTO_ORA:0)}</td>
                              <td style={{ textAlign:"right", color:"#f5a623" }}>{fmt(da.materiali)}</td>
                              <td style={{ textAlign:"right", fontWeight:700, color:da.margine>0?"#3ecf8e":"#f06060" }}>{fmt(da.margine)}</td>
                              <td style={{ textAlign:"right", color:perc>=30?"#3ecf8e":perc>=15?"#f5a623":"#f06060", fontWeight:700 }}>{perc}%</td>
                              <td style={{ textAlign:"right", color:theme.textMuted }}>{da.cantieri}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Grafico a barre fatturato vs margine per anno */}
                {anni.length > 0 && (() => {
                  const datiGrafico = anni.map(a => ({ anno:a, ...datiAnno(a, modoVista) })).filter(d=>d.ricavo>0);
                  if (!datiGrafico.length) return null;
                  const maxVal = Math.max(...datiGrafico.map(d=>d.ricavo));
                  const W = 700, H = 220, PAD = 48, BAR_W = Math.min(60, Math.floor((W - PAD*2) / datiGrafico.length / 2 - 8));
                  const slot = (W - PAD*2) / datiGrafico.length;
                  return (
                    <div className="card" style={{ marginBottom:20 }}>
                      <div className="card-title" style={{ marginBottom:16 }}>📈 Fatturato e margine per anno</div>
                      <svg viewBox={"0 0 "+W+" "+H} style={{ width:"100%", maxHeight:220 }}>
                        {/* Linee guida */}
                        {[0,25,50,75,100].map(p => {
                          const y = PAD + (H-PAD*2)*(1-p/100);
                          return <g key={p}>
                            <line x1={PAD} y1={y} x2={W-PAD} y2={y} stroke={theme.border} strokeWidth="1" strokeDasharray="3,3"/>
                            <text x={PAD-6} y={y+4} textAnchor="end" fontSize="9" fill={theme.textDim}>
                              {Math.round(maxVal*p/100/1000)}k
                            </text>
                          </g>;
                        })}
                        {/* Barre */}
                        {datiGrafico.map((d, i) => {
                          const cx = PAD + slot*i + slot/2;
                          const hFatt = maxVal>0 ? ((H-PAD*2)*d.ricavo/maxVal) : 0;
                          const hMarg = maxVal>0 ? ((H-PAD*2)*Math.max(0,d.margine)/maxVal) : 0;
                          const yFatt = H - PAD - hFatt;
                          const yMarg = H - PAD - hMarg;
                          const isSelected = d.anno === annoSel;
                          return (
                            <g key={d.anno} onClick={()=>setAnnoSel(d.anno)} style={{cursor:"pointer"}}>
                              {/* Barra fatturato */}
                              <rect x={cx-BAR_W-2} y={yFatt} width={BAR_W} height={hFatt}
                                fill={isSelected?"#4e9af5":"#4e9af533"} rx="3"
                                style={{transition:"all 0.3s"}}/>
                              {/* Barra margine */}
                              <rect x={cx+2} y={yMarg} width={BAR_W} height={hMarg}
                                fill={isSelected?"#3ecf8e":"#3ecf8e33"} rx="3"
                                style={{transition:"all 0.3s"}}/>
                              {/* Anno label */}
                              <text x={cx} y={H-PAD+14} textAnchor="middle" fontSize="11"
                                fill={isSelected?theme.accent:theme.textMuted} fontWeight={isSelected?"700":"400"}>
                                {d.anno}
                              </text>
                              {/* Valore fatturato sopra barra */}
                              {hFatt>20 && <text x={cx-BAR_W/2-2} y={yFatt-4} textAnchor="middle" fontSize="9" fill="#4e9af5">
                                {Math.round(d.ricavo/1000)}k
                              </text>}
                            </g>
                          );
                        })}
                        {/* Legenda */}
                        <rect x={W-PAD-120} y={8} width={10} height={10} fill="#4e9af5" rx="2"/>
                        <text x={W-PAD-106} y={17} fontSize="10" fill={theme.textMuted}>Fatturato</text>
                        <rect x={W-PAD-60} y={8} width={10} height={10} fill="#3ecf8e" rx="2"/>
                        <text x={W-PAD-46} y={17} fontSize="10" fill={theme.textMuted}>Margine</text>
                      </svg>
                    </div>
                  );
                })()}
              </>
            );
          })()}
        </div>
      )}

      {/* ── TAB PER CANTIERE ── */}
      {tab==="cantieri" && (
        <div>
          {/* FIX_INCASSATO_ACCONTI_v7: cantieri "in corso con acconti" nella vista Incassato */}
          {(() => {
            if (modoVista !== "incassato") return null;
            const inCorsoConAcconti = cantieriDB.filter(c => !c.completato && totIncassato(c._id||c.id) > 0);
            if (!inCorsoConAcconti.length) return null;
            const totAcc = inCorsoConAcconti.reduce((s,c)=>s+totIncassato(c._id||c.id),0);
            return (
              <div className="card" style={{ marginBottom:16, border:`1px solid #f5a62366`, background:"#f5a62311" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, fontSize:13 }}>
                  <span style={{ fontSize:18 }}>🟡</span>
                  <span style={{ color:theme.textMuted }}>
                    <strong style={{ color:"#f5a623", fontFamily:"'Syne',sans-serif", fontSize:16 }}>+{fmt(totAcc)}</strong>
                    {" "}acconti incassati su <strong>{inCorsoConAcconti.length}</strong> cantieri in corso (inclusi sotto con badge "In corso")
                  </span>
                </div>
              </div>
            );
          })()}
          {/* Grafico margine % per cantiere */}
          {(() => {
            const cantieriDaMostrare = modoVista === "incassato"
              ? [...cantieriChiusi, ...cantieriDB.filter(c => !c.completato && totIncassato(c._id||c.id) > 0)]
              : cantieriChiusi;
            const cant = cantieriDaMostrare.filter(c=>getDatiCantiere(c, modoVista).ricavo>0)
              .map(c=>({ nome:c.nome, ...getDatiCantiere(c, modoVista) }))
              .sort((a,b)=>b.percMargine-a.percMargine)
              .slice(0,10);
            if (!cant.length) return null;
            return (
              <div className="card" style={{ marginBottom:16 }}>
                <div className="card-title" style={{ marginBottom:14 }}>📊 Margine % per cantiere (top 10)</div>
                <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                  {cant.map(c => {
                    const perc = Math.max(0, Math.min(100, c.percMargine));
                    const color = perc>=30?"#3ecf8e":perc>=15?"#f5a623":"#f06060";
                    return (
                      <div key={c.nome}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12 }}>
                          <span style={{ color:theme.text, fontWeight:500, maxWidth:300, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.nome}</span>
                          <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, color, minWidth:48, textAlign:"right" }}>{c.percMargine}%</span>
                        </div>
                        <div style={{ height:8, background:theme.border, borderRadius:4, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:perc+"%", background:color, borderRadius:4, transition:"width 0.5s" }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display:"flex", gap:16, marginTop:12, fontSize:11, color:theme.textDim }}>
                  <span style={{color:"#3ecf8e"}}>■ ≥30% ottimo</span>
                  <span style={{color:"#f5a623"}}>■ 15-29% buono</span>
                  <span style={{color:"#f06060"}}>■ &lt;15% attenzione</span>
                </div>
              </div>
            );
          })()}

          <div className="card" style={{ marginBottom:20 }}>
            <div className="card-title" style={{ marginBottom:16 }}>🏗️ Tutti i cantieri — Preventivo vs Reale</div>
            <div style={{ overflowX:"auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Cantiere</th>
                    <th>Cliente</th>
                    <th style={{textAlign:"right"}}>Preventivo</th>
                    <th style={{textAlign:"right"}}>Consuntivo</th>
                    <th style={{textAlign:"right"}}>Ore reali</th>
                    <th style={{textAlign:"right"}}>Materiali</th>
                    <th style={{textAlign:"right"}}>Margine</th>
                    <th style={{textAlign:"right"}}>%</th>
                    <th>Stato</th>
                  </tr>
                </thead>
                <tbody>
                  {(modoVista === "incassato"
                    ? [...cantieriChiusi, ...cantieriDB.filter(c => !c.completato && totIncassato(c._id||c.id) > 0)]
                    : cantieriChiusi
                  ).map(c => {
                    const d = getDatiCantiere(c, modoVista);
                    const prev = parseFloat(c.importo)||0;
                    return (
                      <tr key={c._id||c.id}>
                        <td style={{ fontWeight:600 }}>{c.nome}</td>
                        <td style={{ color:theme.textMuted, fontSize:12 }}>{c.cliente||"—"}</td>
                        <td style={{ textAlign:"right", color:"#4e9af5" }}>{fmt(prev)}</td>
                        <td style={{ textAlign:"right", fontWeight:600 }}>{fmt(d.ricavo)}</td>
                        <td style={{ textAlign:"right", color:"#f5a623" }}>{d.oreEff.toFixed(1)}h</td>
                        <td style={{ textAlign:"right", color:"#f5a623" }}>{fmt(d.matReali)}</td>
                        <td style={{ textAlign:"right", fontWeight:700, color:d.margine>0?"#3ecf8e":"#f06060" }}>{fmt(d.margine)}</td>
                        <td style={{ textAlign:"right", fontWeight:700, color:d.percMargine>=30?"#3ecf8e":d.percMargine>=15?"#f5a623":"#f06060" }}>{d.percMargine}%</td>
                        <td><span className={`badge ${c.completato?"badge-success":"badge-warning"}`}>{c.completato?"✓":"In corso"}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB PER LAVORAZIONE ── */}
      {tab==="lavorazioni" && (
        <div>
          <div className="card">
            <div className="card-title" style={{ marginBottom:16 }}>🔨 Analisi per tipo di lavorazione</div>
            <div style={{ fontSize:12, color:theme.textMuted, marginBottom:14 }}>
              Confronto tra ore preventivate e reali, e margine per ogni tipo di lavoro su tutti i cantieri.
            </div>
            <div style={{ overflowX:"auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Lavorazione</th>
                    <th style={{textAlign:"right"}}>Cantieri</th>
                    <th style={{textAlign:"right"}}>Ricavo prev.</th>
                    <th style={{textAlign:"right"}}>Ricavo reale</th>
                    <th style={{textAlign:"right"}}>Ore prev.</th>
                    <th style={{textAlign:"right"}}>Ore reali</th>
                    <th style={{textAlign:"right"}}>Δ ore</th>
                    <th style={{textAlign:"right"}}>Mat. reali</th>
                    <th style={{textAlign:"right"}}>Margine est.</th>
                  </tr>
                </thead>
                <tbody>
                  {statsLavorazioni().map((l,i) => {
                    const deltaOre = l.oreReali - l.orePrev;
                    const costoMod = l.oreReali * COSTO_ORA;
                    const margine = l.ricavoReale - costoMod - l.matReali;
                    const percM = l.ricavoReale>0 ? Math.round((margine/l.ricavoReale)*100) : 0;
                    const isCategoria = l.isCategoria;
                    return (
                      <tr key={i} style={{ background: isCategoria ? theme.surface : "transparent" }}>
                        <td style={{ paddingLeft: isCategoria ? 10 : 24 }}>
                          {isCategoria ? (
                            <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:14 }}>{l.catLabel}</div>
                          ) : (
                            <div>
                              <span style={{ color:theme.textDim, marginRight:6 }}>↳</span>
                              <span style={{ fontWeight:600, color:theme.info }}>{l.subLabel}</span>
                            </div>
                          )}
                          {l.voci?.length>0 && isCategoria && <div style={{ fontSize:10, color:theme.textDim, marginTop:2 }}>es. {l.voci[0].slice(0,40)}{l.voci[0].length>40?"...":""}</div>}
                        </td>
                        <td style={{ textAlign:"right", color:theme.textMuted }}>{l.count}</td>
                        <td style={{ textAlign:"right", color:"#4e9af5" }}>{fmt(l.ricavoPrev)}</td>
                        <td style={{ textAlign:"right", fontWeight:isCategoria?700:400 }}>{fmt(l.ricavoReale)}</td>
                        <td style={{ textAlign:"right", color:theme.textMuted }}>{l.orePrev.toFixed(1)}h</td>
                        <td style={{ textAlign:"right" }}>{l.oreReali.toFixed(1)}h</td>
                        <td style={{ textAlign:"right", fontWeight:700, color:deltaOre>0?"#f06060":deltaOre<0?"#3ecf8e":theme.textDim }}>
                          {deltaOre!==0 ? (deltaOre>0?"+":"")+deltaOre.toFixed(1)+"h" : "—"}
                        </td>
                        <td style={{ textAlign:"right", color:"#f5a623" }}>{l.matReali>0?fmt(l.matReali):"—"}</td>
                        <td style={{ textAlign:"right", fontWeight:700, color:percM>=30?"#3ecf8e":percM>=15?"#f5a623":"#f06060" }}>
                          {fmt(margine)} <span style={{fontSize:11, fontWeight:400}}>({percM}%)</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB PER OPERAIO ── */}
      {tab==="operai" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 }}>
            {dipendenti.filter(d=>d.ruolo!=="Titolare").map(op => {
              const oreOp = assenzeDB.filter(a=>a.operaio===op.nome&&a.tipo==="lavorato");
              const anno = annoSel;
              const oreAnno = oreOp.filter(a=>(a.data||"").slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
              const oreMese = (() => {
                const now = new Date();
                const m = `${String(now.getMonth()+1).padStart(2,"0")}/${now.getFullYear()}`;
                return oreOp.filter(a=>(a.data||"").slice(3)===m).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
              })();
              const cantieriOp = [...new Set(oreOp.map(a=>a.cantiere).filter(Boolean))];
              const assenzeOp = assenzeDB.filter(a=>a.operaio===op.nome&&a.tipo!=="lavorato"&&a.tipo!=="foto"&&a.extra!==true);
              const ferieAnno = assenzeOp.filter(a=>a.tipo==="Ferie"&&(a.data||"").slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);
              const malattia = assenzeOp.filter(a=>(a.tipo==="Malattia"||a.tipo==="Infortunio")&&(a.data||"").slice(-4)===anno).reduce((s,a)=>s+(parseFloat(a.ore)||0)/8,0);

              // Ultimi 6 mesi
              const ultimiMesi = [];
              for(let i=5;i>=0;i--){
                const d=new Date(); d.setMonth(d.getMonth()-i);
                const m=`${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
                const ore=oreOp.filter(a=>(a.data||"").slice(3)===m).reduce((s,a)=>s+(parseFloat(a.ore)||0),0);
                ultimiMesi.push({m:m.slice(0,2)+"/"+m.slice(5), ore});
              }
              const maxOre = Math.max(...ultimiMesi.map(m=>m.ore), 1);

              return (
                <div key={op.nome} className="card">
                  <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16 }}>
                    <div style={{ width:44, height:44, borderRadius:"50%", background:op.colore||theme.accent, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color:"#111" }}>{op.nome[0]}</div>
                    <div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:16 }}>{op.nome}</div>
                      <div style={{ fontSize:12, color:theme.textMuted }}>{op.ruolo}</div>
                    </div>
                  </div>

                  {/* KPI */}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:16 }}>
                    {[
                      ["Ore questo mese", oreMese.toFixed(1)+"h", "#4e9af5"],
                      ["Ore anno "+anno, oreAnno.toFixed(1)+"h", theme.accent],
                      ["Cantieri", cantieriOp.length, theme.text],
                    ].map(([label,val,color])=>(
                      <div key={label} style={{ background:theme.bg, borderRadius:8, padding:"10px 12px", textAlign:"center", border:`1px solid ${theme.border}` }}>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, color }}>{val}</div>
                        <div style={{ fontSize:10, color:theme.textDim, marginTop:3 }}>{label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Grafico ore ultimi 6 mesi */}
                  <div style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, color:theme.textDim, textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Ore ultimi 6 mesi</div>
                    <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:60 }}>
                      {ultimiMesi.map(({m,ore})=>(
                        <div key={m} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                          <div style={{ width:"100%", background:ore>0?op.colore||theme.accent:theme.border, borderRadius:"4px 4px 0 0", height:`${Math.max(4,Math.round((ore/maxOre)*50))}px`, transition:"height 0.3s" }} />
                          <div style={{ fontSize:9, color:theme.textDim, whiteSpace:"nowrap" }}>{m}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Assenze anno */}
                  <div style={{ display:"flex", gap:10, fontSize:12, color:theme.textMuted }}>
                    <span>🏖️ Ferie: <strong style={{color:theme.text}}>{Math.round(ferieAnno)}gg</strong></span>
                    <span>🤒 Malattia: <strong style={{color:theme.text}}>{Math.round(malattia)}gg</strong></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// ScansionaBollaPage — wrapper per lo scanner bolle nel menu operaio
// Passa cantieri, dizionarioMateriali (per labels), nome operaio e callback
// ============================================================================
function ScansionaBollaPage({ nomeOperaio = "", cantieriDB = [], onFine }) {
  return (
    <ScansionaBolla
      cantieriDB={cantieriDB}
      dizionarioMateriali={dizionarioMateriali}
      nomeOperaio={nomeOperaio}
      onFine={onFine}
    />
  );
}
// ANAGRAFICA_CLIENTI_v1 - sezione Clienti
function Clienti({ clientiDB, cantieriDB, preventiviDB, sopralluoghiDB, onNavigate }) {
  const [cerca, setCerca] = useState("");
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState("");

  const nz = s => String(s == null ? "" : s).trim().toLowerCase();
  const VUOTO = { nome:"", tipo:"privato", piva:"", cf:"", indirizzo:"", citta:"", cap:"",
    telefono:"", email:"", pec:"", amministratore:"", amminTelefono:"", amminEmail:"",
    note:"", attivo:true };

  const inp = { width:"100%", padding:"8px 10px", borderRadius:8, border:"1px solid #2a2f45",
    background:"#151926", color:"#e8ebf5", fontSize:14, boxSizing:"border-box" };
  const lab = { fontSize:11, color:"#7b82a0", textTransform:"uppercase", letterSpacing:1, marginBottom:4 };

  const lavoriDi = (nome) => {
    const k = nz(nome);
    return {
      cantieri: (cantieriDB||[]).filter(c => nz(c.cliente) === k),
      preventivi: (preventiviDB||[]).filter(p => nz(p.cliente) === k),
      sopralluoghi: (sopralluoghiDB||[]).filter(s => nz(s.cliente) === k),
    };
  };

  const lista = (clientiDB||[])
    .filter(c => { const q = nz(cerca); return !q || nz(c.nome).includes(q) || nz(c.telefono).includes(q) || nz(c.citta).includes(q); })
    .sort((a,b) => String(a.nome||"").localeCompare(String(b.nome||"")));

  const apri  = (c) => { setSel(c._id); setForm(Object.assign({}, VUOTO, c)); setMsg(""); };
  const nuovo = ()  => { setSel("nuovo"); setForm(Object.assign({}, VUOTO)); setMsg(""); };
  const set   = (k,v) => setForm(f => Object.assign({}, f, { [k]: v }));

  const salva = async () => {
    const nome = String((form&&form.nome)||"").trim();
    if (!nome) { setMsg("Il nome e obbligatorio"); return; }
    setSalvando(true);
    try {
      const ora = new Date().toISOString();
      if (sel === "nuovo") await addDoc(collection(db,"clienti"), Object.assign({}, form, { nome, createdAt:ora, updatedAt:ora }));
      else await updateDoc(doc(db,"clienti",sel), Object.assign({}, form, { nome, updatedAt:ora }));
      setMsg("Salvato");
      setTimeout(() => { setSel(null); setForm(null); setMsg(""); }, 700);
    } catch(e) { setMsg("Errore: " + ((e&&e.message)||e)); }
    setSalvando(false);
  };

  const mappa = (indirizzo, citta) => {
    const q = [indirizzo, citta].filter(Boolean).join(" ");
    return q ? "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(q) : null;
  };

  if (form) {
    const l = sel === "nuovo" ? { cantieri:[], preventivi:[], sopralluoghi:[] } : lavoriDi(form.nome);
    const url = mappa(form.indirizzo, form.citta);
    return (
      <div>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:16 }}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{ setSel(null); setForm(null); }}>← Indietro</button>
          <div style={{ fontWeight:800, fontSize:18 }}>{sel === "nuovo" ? "Nuovo cliente" : form.nome}</div>
          {msg ? <div style={{ marginLeft:"auto", color: msg.indexOf("Errore")===0 ? "#f06060" : "#4ec97e", fontSize:13 }}>{msg}</div> : null}
        </div>
        <div className="card" style={{ padding:16, marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12 }}>
            <div><div style={lab}>Nome *</div><input style={inp} value={form.nome||""} onChange={e=>set("nome",e.target.value)} /></div>
            <div><div style={lab}>Tipo</div>
              <select style={inp} value={form.tipo||"privato"} onChange={e=>set("tipo",e.target.value)}>
                <option value="privato">Privato</option>
                <option value="condominio">Condominio</option>
                <option value="impresa">Impresa</option>
                <option value="pubblico">Ente pubblico</option>
              </select>
            </div>
            <div><div style={lab}>Telefono</div><input style={inp} value={form.telefono||""} onChange={e=>set("telefono",e.target.value)} /></div>
            <div><div style={lab}>Email</div><input style={inp} value={form.email||""} onChange={e=>set("email",e.target.value)} /></div>
            <div><div style={lab}>Indirizzo</div><input style={inp} value={form.indirizzo||""} onChange={e=>set("indirizzo",e.target.value)} /></div>
            <div><div style={lab}>Citta</div><input style={inp} value={form.citta||""} onChange={e=>set("citta",e.target.value)} /></div>
            <div><div style={lab}>CAP</div><input style={inp} value={form.cap||""} onChange={e=>set("cap",e.target.value)} /></div>
            <div><div style={lab}>Partita IVA</div><input style={inp} value={form.piva||""} onChange={e=>set("piva",e.target.value)} /></div>
            <div><div style={lab}>Codice fiscale</div><input style={inp} value={form.cf||""} onChange={e=>set("cf",e.target.value)} /></div>
            <div><div style={lab}>PEC</div><input style={inp} value={form.pec||""} onChange={e=>set("pec",e.target.value)} /></div>
          </div>
          {url ? <div style={{ marginTop:12 }}><a className="btn btn-ghost btn-sm" href={url} target="_blank" rel="noreferrer">📍 Apri la mappa</a></div> : null}
        </div>
        {form.tipo === "condominio" ? (
          <div className="card" style={{ padding:16, marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:10 }}>Amministratore</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12 }}>
              <div><div style={lab}>Nome</div><input style={inp} value={form.amministratore||""} onChange={e=>set("amministratore",e.target.value)} /></div>
              <div><div style={lab}>Telefono</div><input style={inp} value={form.amminTelefono||""} onChange={e=>set("amminTelefono",e.target.value)} /></div>
              <div><div style={lab}>Email</div><input style={inp} value={form.amminEmail||""} onChange={e=>set("amminEmail",e.target.value)} /></div>
            </div>
          </div>
        ) : null}
        <div className="card" style={{ padding:16, marginBottom:16 }}>
          <div style={lab}>Note</div>
          <textarea style={Object.assign({}, inp, { minHeight:70, resize:"vertical" })} value={form.note||""} onChange={e=>set("note",e.target.value)} />
          <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:12, fontSize:14 }}>
            <input type="checkbox" checked={form.attivo !== false} onChange={e=>set("attivo",e.target.checked)} />
            Cliente attivo
          </label>
        </div>
        {sel !== "nuovo" ? (
          <div className="card" style={{ padding:16, marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:10 }}>
              Lavori di questo cliente — {l.cantieri.length} cantieri, {l.preventivi.length} preventivi, {l.sopralluoghi.length} sopralluoghi
            </div>
            {l.cantieri.map(c => (
              <div key={c._id} style={{ display:"flex", gap:10, padding:"6px 0", borderTop:"1px solid #222738", fontSize:14, cursor:"pointer" }}
                   onClick={()=>onNavigate && onNavigate("cantieri", c._id)}>
                <span>🏗️</span><span style={{ flex:1 }}>{c.nome}</span>
                <span style={{ color: c.completato ? "#4ec97e" : "#f5a623" }}>{c.completato ? "chiuso" : "in corso"}</span> {/* ANAGRAFICA_CLIENTI_v2 */}
                <span>€ {(parseFloat(c.importo)||0).toLocaleString("it-IT")}</span>
              </div>
            ))}
            {l.preventivi.map(p => (
              <div key={p._id} style={{ display:"flex", gap:10, padding:"6px 0", borderTop:"1px solid #222738", fontSize:14 }}>
                <span>📄</span><span style={{ flex:1 }}>{p.nomeCantiere||p.nome_cantiere||"(senza nome)"}</span>
                <span style={{ color:"#7b82a0" }}>{p.stato||""}</span>
                <span>€ {(parseFloat(p.totaleFinale||p.totale_finale)||0).toLocaleString("it-IT")}</span>
              </div>
            ))}
            {l.sopralluoghi.map(s => (
              <div key={s._id} style={{ display:"flex", gap:10, padding:"6px 0", borderTop:"1px solid #222738", fontSize:14 }}>
                <span>📋</span><span style={{ flex:1 }}>{s.nomeCantiere||s.nome_cantiere||"(senza nome)"}</span>
                <span style={{ color:"#7b82a0" }}>{s.stato||""}</span>
              </div>
            ))}
          </div>
        ) : null}
        <button className="btn btn-primary" onClick={salva} disabled={salvando}>{salvando ? "Salvataggio..." : "Salva"}</button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <input style={Object.assign({}, inp, { maxWidth:320 })} placeholder="Cerca nome, telefono, citta..." value={cerca} onChange={e=>setCerca(e.target.value)} />
        <button className="btn btn-primary" onClick={nuovo}>+ Nuovo cliente</button>
        <div style={{ marginLeft:"auto", color:"#7b82a0", fontSize:13, alignSelf:"center" }}>{lista.length} clienti</div>
      </div>
      <div className="card" style={{ padding:0, overflow:"hidden" }}>
        {lista.map(c => {
          const l = lavoriDi(c.nome);
          return (
            <div key={c._id} onClick={()=>apri(c)}
                 style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px",
                          borderTop:"1px solid #222738", cursor:"pointer", opacity: c.attivo === false ? 0.45 : 1 }}>
              <span style={{ fontSize:16 }}>{c.tipo === "condominio" ? "🏢" : c.tipo === "impresa" ? "🏭" : "👤"}</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:600 }}>{c.nome}</div>
                <div style={{ fontSize:12, color:"#7b82a0" }}>
                  {[c.telefono, c.email, c.citta].filter(Boolean).join(" · ") || "nessun contatto"}
                </div>
              </div>
              <div style={{ fontSize:12, color:"#7b82a0", whiteSpace:"nowrap" }}>
                {l.cantieri.length ? l.cantieri.length + " cantieri" : ""}
                {l.preventivi.length ? "  " + l.preventivi.length + " prev." : ""}
              </div>
            </div>
          );
        })}
        {!lista.length ? <div style={{ padding:20, color:"#7b82a0" }}>Nessun cliente trovato.</div> : null}
      </div>
    </div>
  );
}

export default function App({ utente, onLogout }) {
  const [page, setPage] = useState("dashboard");
  const [pageParam, setPageParam] = useState(null);
  const [prevPageParam, setPrevPageParam] = useState(null);

  // ── DATI REALI DA FIREBASE ─────────────────────────────────────────────────
  const [cantieriDB, setCantieriDB] = useState([]);
  const [assenzeDB, setAssenzeDB] = useState([]);
  const [fattureDB, setFattureDB] = useState([]);
  const [preventiviDB, setPreventiviDB] = useState([]);
  const [sopralluoghiDB, setSopralluoghiDB] = useState([]);
  const [utentiDB, setUtentiDB] = useState([]);
  const [materialiDB, setMaterialiDB] = useState([]);
  const [bolleDB, setBolleDB] = useState([]);
  const [listinoMatDB, setListinoMatDB] = useState([]);
  const [famiglieProdottoDB, setFamiglieProdottoDB] = useState([]);
  const [pagamentiDB, setPagamentiDB] = useState([]);
  const [scadenzeDB, setScadenzeDB] = useState([]); // FIX_PAGINA_SCADENZE_v1
  const [clientiDB, setClientiDB] = useState([]); // ANAGRAFICA_CLIENTI_v1
  const [loadingDB, setLoadingDB] = useState(true);
  const [costoOrarioDB, setCostoOrarioDB] = useState(25);
  const [dizionarioVersion, setDizionarioVersion] = useState(0);

  useEffect(() => {
    // Cantieri in tempo reale
    const unsubCantieri = onSnapshot(
      query(collection(db, "cantieri"), orderBy("createdAt", "desc")),
      snap => {
        setCantieriDB(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
        setLoadingDB(false);
      },
      err => { console.error("Cantieri:", err); setLoadingDB(false); }
    );
    // Assenze in tempo reale
    const unsubAssenze = onSnapshot(
      collection(db, "assenze"),
      snap => setAssenzeDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Assenze:", err)
    );
    // Fatture in tempo reale
    const unsubFatture = onSnapshot(
      collection(db, "fatture"),
      snap => setFattureDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Fatture:", err)
    );
    // Preventivi in tempo reale
    const unsubPreventivi = onSnapshot(
      query(collection(db, "preventivi"), orderBy("createdAt", "desc")),
      snap => setPreventiviDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Preventivi:", err)
    );
    // Sopralluoghi in tempo reale
    const unsubSopralluoghi = onSnapshot(
      query(collection(db, "sopralluoghi"), orderBy("createdAt", "desc")),
      snap => setSopralluoghiDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Sopralluoghi:", err)
    );
    // Clienti in tempo reale - ANAGRAFICA_CLIENTI_v1
    const unsubClienti = onSnapshot(
      collection(db, "clienti"),
      snap => setClientiDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Clienti:", err)
    );
    // Utenti in tempo reale — aggiorna anche dipendenti globale
    const unsubUtenti = onSnapshot(collection(db, "utenti"), snap => {
      const lista = snap.docs.map(d => ({ ...d.data(), _id: d.id }));
      setUtentiDB(lista);
      // Aggiorna la variabile globale dipendenti usata da tutti i componenti
      if (lista.length > 0) {
        dipendenti = lista.map(u => ({
          nome:   u.nome,
          colore: u.colore || "#888",
          ruolo:  u.ruolo === "titolare" ? "Titolare" : "Operaio edile",
          _id:    u._id,
        }));
      }
    });
    const unsubMateriali = onSnapshot(collection(db, "materiali"), snap => {
      setMaterialiDB(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    }, () => {});
    const unsubBolle = onSnapshot(query(collection(db, "bolle"), orderBy("createdAt","desc")), snap => {
      setBolleDB(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    }, () => {});
    const unsubListinoMat = onSnapshot(collection(db, "listino_materiali"), snap => {
      setListinoMatDB(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    }, () => {});
    const unsubFamiglie = onSnapshot(doc(db, "impostazioni", "famiglie_prodotto"), snap => {
      const voci = snap.exists() ? (snap.data().voci || []) : [];
      setFamiglieProdottoDB(voci);
      window._famiglieProdotto = voci;
    }, () => {});
    const unsubPagamenti = onSnapshot(collection(db, "pagamenti"), snap => {
      setPagamentiDB(snap.docs.map(d => ({ ...d.data(), _id: d.id })));
    // Scadenze in tempo reale - FIX_PAGINA_SCADENZE_v1
    const unsubScadenze = onSnapshot(
      collection(db, "scadenze"),
      snap => setScadenzeDB(snap.docs.map(d => ({ ...d.data(), _id: d.id }))),
      err => console.error("Scadenze:", err)
    );

    }, () => {});
    // Carica dizionario lavorazioni da Firebase all'avvio
    const unsubDizionario = onSnapshot(doc(db, "impostazioni", "dizionario"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        const voci = data.voci || {};
        // Aggiorna la variabile globale usata da tutti i componenti
        Object.keys(dizionarioMateriali).forEach(k => delete dizionarioMateriali[k]);
        Object.assign(dizionarioMateriali, voci);
        setDizionarioVersion(v => v + 1); // forza re-render
      }
    }, () => {});
    // Carica costo orario globale da Firebase all'avvio
    const unsubParams = onSnapshot(doc(db, "impostazioni", "params"), snap => {
      if (snap.exists() && snap.data().voci?.costoOraio) {
        const val = parseFloat(snap.data().voci.costoOraio) || 25;
        COSTO_ORARIO_GLOBALE = val;
        setCostoOrarioDB(val);
      }
    }, () => {});
    return () => { unsubCantieri(); unsubAssenze(); unsubFatture(); unsubPreventivi(); unsubSopralluoghi(); unsubUtenti(); unsubMateriali(); unsubBolle(); unsubListinoMat(); unsubDizionario(); unsubPagamenti(); unsubParams(); unsubFamiglie(); unsubClienti(); };
  }, []);

  // Titolari: chiunque con ruolo "titolare" o i nomi fissi Marco/Edoardo
  const isTitolare = utente?.ruolo === "titolare" || utente?.nome === "Edoardo";

  // ── SISTEMA NOTIFICHE ─────────────────────────────────────────────────────
  const [showNotifiche, setShowNotifiche] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const parseDataIT = (s) => {
    if (!s) return null;
    const parts = s.split("/");
    if (parts.length === 3) return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    return null;
  };

  const oggiNotif = new Date();

  const notifFatture = fattureDB
    .filter(f => !f.pagata && f.scadenza)
    .map(f => {
      const d = parseDataIT(f.scadenza);
      const giorni = d ? Math.ceil((d - oggiNotif) / 86400000) : null;
      return { ...f, giorni };
    })
    .filter(f => f.giorni !== null && f.giorni <= 7)
    .map(f => ({
      tipo: f.giorni < 0 ? "danger" : "warning",
      icon: "🧾",
      testo: f.giorni < 0
        ? `Fattura scaduta ${Math.abs(f.giorni)}gg fa — ${f.cliente||"—"} (€ ${parseFloat(f.importo||0).toLocaleString("it-IT")})`
        : `Fattura in scadenza tra ${f.giorni}gg — ${f.cliente||"—"} (€ ${parseFloat(f.importo||0).toLocaleString("it-IT")})`,
    }));

  const notifCantieri = cantieriDB
    .filter(c => !c.completato && c.fine)
    .map(c => {
      const d = parseDataIT(c.fine);
      const giorni = d ? Math.ceil((d - oggiNotif) / 86400000) : null;
      return { ...c, giorni };
    })
    .filter(c => c.giorni !== null && c.giorni <= 5)
    .map(c => ({
      tipo: c.giorni <= 0 ? "danger" : "warning",
      icon: "🏗️",
      testo: c.giorni <= 0
        ? `Consegna scaduta: ${c.nome} (${Math.abs(c.giorni)}gg fa)`
        : `Consegna tra ${c.giorni} giorni: ${c.nome}`,
    }));

  // Notifiche sforamento budget bolle: scansiona cantieri con bolle che sforano
  // Le bolle sono in state locale, quindi questa è una notifica in-session
  const notifBolle = [];

  const tutteNotifiche = [...notifFatture, ...notifCantieri, ...notifBolle];
  const nNotifiche = tutteNotifiche.length;

  const navigate = (targetPage, param = null) => {
    setPage(targetPage);
    setPageParam(param);
    setShowNotifiche(false);
    setSidebarOpen(false);
  };

  // Menu differenziato: operai vedono solo Cantieri
  const pagesTitolare = [
    { id: "dashboard",     icon: "📊", label: "Dashboard" },
    { id: "clienti",       icon: "👥", label: "Clienti" },
    { id: "sopralluogo",   icon: "📋", label: "Sopralluogo" },
    { id: "preventivi",    icon: "📄", label: "Preventivi" },
    { id: "cantieri",      icon: "🏗️",  label: "Cantieri" },
    { id: "giornaliera",   icon: "⏱️",  label: "Giornaliera" },
    { id: "contabilita",   icon: "💰", label: "Contabilità" },
    { id: "cassetta",      icon: "📥", label: "Cassetta" },
    { id: "materiali",     icon: "📦", label: "Materiali" },
    { id: "pianificazione",icon: "📅", label: "Pianificazione" },
    { id: "scadenze",      icon: "🗓️",  label: "Scadenze" },
    { id: "archivio",      icon: "🗂️",  label: "Archivio" },
    { id: "statistiche",   icon: "📈", label: "Statistiche" },
    { id: "archivio_doc",  icon: "📁", label: "Archivio Doc." },
    { id: "sicurezza",     icon: "🛡️",  label: "Sicurezza" },
    { id: "impostazioni",  icon: "⚙️",  label: "Impostazioni" },
  ];
  const pagesOperaio = [
    { id: "dashboard_operaio", icon: "📊", label: "Dashboard" },
    { id: "cantieri",          icon: "🏗️", label: "I miei cantieri" },
    { id: "magazzino_operaio", icon: "📦", label: "Magazzino" },
    { id: "bolle_operaio",     icon: "📷", label: "Bolle" },
  ];
  const pages = isTitolare ? pagesTitolare : pagesOperaio;

  const titles = {
    dashboard: "Dashboard", sopralluogo: "Sopralluogo",
    clienti: "👥 Clienti",
    preventivi: "Preventivi", cantieri: isTitolare ? "Gestione Cantieri" : "I miei cantieri",
    giornaliera: "Giornaliera", contabilita: "Contabilità",
    materiali: "Materiali & Forniture",
    pianificazione: "Pianificazione", archivio: "Archivio", scadenze: "🗓️ Scadenze",
    statistiche: "Statistiche",
    impostazioni: "Impostazioni",
    archivio_doc: "📁 Archivio Documenti",
    cassetta: "📥 Cassetta documenti",
    sicurezza: "🛡️ Sicurezza Azienda",
    dashboard_operaio: "La mia Dashboard",
    magazzino_operaio: "📦 Magazzino",
    bolle_operaio: "📷 Bolle",
  };

  const renderPage = () => {
    if (!isTitolare) {
      if (page === "cantieri") return <Cantiere initialSelected={pageParam} vistaOperaio={true} nomeOperaio={utente?.nome} cantieriDB={cantieriDB} assenzeDB={assenzeDB} bolleDB={bolleDB} costoOrario={costoOrarioDB} preventiviDB={preventiviDB} />;
      if (page === "magazzino_operaio") return <ScaricoMagazzinoOperaio nomeOperaio={utente?.nome||""} cantieriDB={cantieriDB} bolleDB={bolleDB} />;
      if (page === "bolle_operaio") return <ScansionaBollaPage nomeOperaio={utente?.nome||""} cantieriDB={cantieriDB} onFine={() => navigate("dashboard_operaio")} />;
      return <DashboardOperaio nomeOperaio={utente?.nome||""} cantieriDB={cantieriDB} assenzeDB={assenzeDB} />;
    }
    switch (page) {
      case "dashboard":     return <Dashboard onNavigate={navigate} cantieriDB={cantieriDB} fattureDB={fattureDB} assenzeDB={assenzeDB} sopralluoghiDB={sopralluoghiDB} materialiDB={materialiDB} utente={utente} />;
      case "clienti":       return <Clienti clientiDB={clientiDB} cantieriDB={cantieriDB} preventiviDB={preventiviDB} sopralluoghiDB={sopralluoghiDB} onNavigate={navigate} />;
      case "sopralluogo":   return <Sopralluogo onNavigate={navigate} sopralluoghiDB={sopralluoghiDB} clientiDB={clientiDB} />;
      case "preventivi":    return <Preventivi key={dizionarioVersion} clientiDB={clientiDB} preventiviDB={preventiviDB} sopralluoghiDB={sopralluoghiDB} cantieriEsistenti={cantieriDB} initialSopralluogoId={pageParam} listinoMatDB={listinoMatDB} famiglieProdottoDB={famiglieProdottoDB} />;
      case "cantieri":      return <Cantiere initialSelected={pageParam} cantieriDB={cantieriDB} assenzeDB={assenzeDB} bolleDB={bolleDB} costoOrario={costoOrarioDB} preventiviDB={preventiviDB} />;
      case "giornaliera":   return <Giornaliera assenzeDB={assenzeDB} cantieriDB={cantieriDB} />; 
      case "contabilita":   return <Contabilita fattureDB={fattureDB} cantieriDB={cantieriDB} />;
      case "materiali":     return <Materiali cantieriDB={cantieriDB} famiglieProdottoDB={famiglieProdottoDB} bolleDB={bolleDB} utente={utente} />;
      case "pianificazione":return <Pianificazione cantieriDB={cantieriDB} assenzeDB={assenzeDB} />;
      case "scadenze":      return <Scadenze scadenzeDB={scadenzeDB} />;
      case "archivio":      return <Archivio cantieriDB={cantieriDB} preventiviDB={preventiviDB} sopralluoghiDB={sopralluoghiDB} assenzeDB={assenzeDB} bolleDB={bolleDB} />;
      case "statistiche":   return <Statistiche cantieriDB={cantieriDB} assenzeDB={assenzeDB} bolleDB={bolleDB} preventiviDB={preventiviDB} pagamentiDB={pagamentiDB} />;
      case "archivio_doc":  return <ArchivioDocumenti onIndietro={() => navigate("dashboard")} />;
      case "cassetta":      return <Cassetta cantieriDB={cantieriDB} />;
      case "sicurezza":     return <SicurezzaAzienda utentiDB={utentiDB} onIndietro={() => navigate("dashboard")} />;
      case "impostazioni":  return <Impostazioni utente={utente} famiglieProdottoDB={famiglieProdottoDB} listinoMatDB={listinoMatDB} />;
      default:              return <Dashboard onNavigate={navigate} cantieriDB={cantieriDB} fattureDB={fattureDB} assenzeDB={assenzeDB} />;
    }
  };

  if (loadingDB) {
    return (
      <div style={{ minHeight:"100vh", background:"#0f1117", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
        <style>{styles}</style>
        <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:22, color:"#f5a623" }}>BertocProject</div>
        <div style={{ color:"#7b82a0", fontSize:14 }}>Caricamento dati...</div>
      </div>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        {/* Overlay mobile — chiude sidebar toccando fuori */}
        <div className={`sidebar-overlay ${sidebarOpen?"open":""}`} onClick={()=>setSidebarOpen(false)} />

        <aside className={`sidebar ${sidebarOpen?"open":""}`}>
          <div className="sidebar-logo">
            <div className="logo-mark">BertocProject</div>
            <div className="logo-sub">Gestionale Edile</div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section-label">Navigazione</div>
            {pages.map(p => (
              <div key={p.id} className={`nav-item ${page === p.id ? "active" : ""}`} onClick={() => navigate(p.id)}>
                <span className="nav-icon">{p.icon}</span>
                {p.label}
              </div>
            ))}
          </nav>
          <div className="sidebar-footer">
            <strong>BertocProject Srl</strong><br />
            P.IVA 01747090478
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            {/* Hamburger — visibile solo su mobile */}
            <button className="hamburger" onClick={()=>setSidebarOpen(v=>!v)}>
              {sidebarOpen ? "✕" : "☰"}
            </button>
            <div className="topbar-title">{titles[page] || "Dashboard"}</div>
            <div className="topbar-actions">
              {isTitolare && (
                <div style={{ position:"relative" }}>
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => setShowNotifiche(v => !v)}
                    style={{ position:"relative", fontSize:16, padding:"6px 12px" }}>
                    🔔
                    {nNotifiche > 0 && (
                      <span style={{ position:"absolute", top:-4, right:-4, background:"#f06060",
                        color:"#fff", borderRadius:"50%", width:16, height:16, fontSize:9,
                        fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center" }}>
                        {nNotifiche}
                      </span>
                    )}
                  </button>
                  {showNotifiche && (
                    <div style={{ position:"absolute", right:0, top:"calc(100% + 8px)", width:370,
                      background:"#1a1d27", border:"1px solid #2a2e45", borderRadius:14,
                      boxShadow:"0 20px 60px #00000077", zIndex:200 }}
                      onClick={e => e.stopPropagation()}>
                      <div style={{ padding:"14px 18px", borderBottom:"1px solid #2a2e45",
                        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span style={{ fontFamily:"'Syne',sans-serif", fontWeight:700, fontSize:14 }}>
                          🔔 Notifiche {nNotifiche > 0 && <span style={{ background:"#f06060", color:"#fff", borderRadius:10, padding:"1px 7px", fontSize:11, marginLeft:6 }}>{nNotifiche}</span>}
                        </span>
                        <button style={{ background:"none", border:"none", color:"#4a5068", cursor:"pointer", fontSize:18 }}
                          onClick={() => setShowNotifiche(false)}>✕</button>
                      </div>
                      {tutteNotifiche.length === 0 ? (
                        <div style={{ padding:"28px 18px", textAlign:"center", color:"#4a5068", fontSize:13 }}>
                          ✅ Nessuna notifica in sospeso
                        </div>
                      ) : (
                        <div style={{ maxHeight:360, overflowY:"auto" }}>
                          {tutteNotifiche.map((n, i) => (
                            <div key={i} style={{ padding:"12px 18px", borderBottom:"1px solid #2a2e4544",
                              display:"flex", gap:12, alignItems:"flex-start",
                              background: n.tipo==="danger" ? "#f0606008" : "#f5a62308" }}>
                              <span style={{ fontSize:20, flexShrink:0 }}>{n.icon}</span>
                              <div style={{ flex:1, fontSize:13, fontWeight:500, lineHeight:1.4,
                                color: n.tipo==="danger" ? "#f06060" : "#f5a623" }}>
                                {n.testo}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ width:30, height:30, borderRadius:"50%", background: utente?.colore || "#f5a623",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:13, fontWeight:700, color:"#fff" }}>
                  {utente?.nome?.[0] || "?"}
                </div>
                <span style={{ fontSize:13, color:theme.textMuted }}>{utente?.nome}</span>
                {isTitolare && <span style={{ fontSize:10, background:"#f5a62322", color:"#f5a623", border:"1px solid #f5a62344", borderRadius:4, padding:"2px 6px", fontWeight:700 }}>TITOLARE</span>}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={onLogout}>Esci</button>
            </div>
          </div>
          {renderPage()}
        </main>
      </div>
    </>
  );
}


// FIX_GIORN_FOTO_v1

// FIX_DESC_ACAPO_v1

// FIX_VISTA_OP_SEMPL1_v1

// FIX_VISTA_OP_SEMPL2_v1
